import React, { useEffect, useState, useRef, useMemo, useCallback, useLayoutEffect } from "react";
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams, Navigate } from "react-router-dom";
import OrderDesign from './OrderDesign';
import ordersStyles from './OrdersStyles';
import "./BattleUI.css";
import './BattleUIExploreUnknownDarknessFlash.css';
import './OrderDesignMobile.css';
import BattlePlayerHudPanel from './components/battle/BattlePlayerHudPanel';
import BattleEmotePicker from './components/battle/BattleEmotePicker';
import BattlePlayerEmoteBubble from './components/battle/BattlePlayerEmoteBubble';
import BattleOpponentHudPanel from './components/battle/BattleOpponentHudPanel';
import GridOverlay from './GridOverlay';
import ExploreUnknownBossWeaponsRandomizerOverlay from './ExploreUnknownBossWeaponsRandomizerOverlay';
import ExploreUnknownCreationFireballOverlay from './ExploreUnknownCreationFireballOverlay';
import ExploreUnknownBossKangarooOverlay from './ExploreUnknownBossKangarooOverlay';
import { buildTurnStartOverlaySnapshot } from './utils/buildTurnStartOverlaySnapshot';
import { getStrategyHudTotal } from './utils/strategyHudTotal';
import {
  hideTurnStartOverlay,
  isTurnStartOverlayVisible,
  showTurnStartOverlay,
} from './utils/turnStartOverlayHost';
import { preloadImage } from './hooks/mediaPreload';
import DebugOverlay from './DebugOverlay';
import TargetedDebugOverlay from './TargetedDebugOverlay';
import SimpleElementSelector from './SimpleElementSelector';
import EditableElementOverlay from './EditableElementOverlay';
import { DeveloperConsole, DevBattleSetupDialog, useDeveloperMode, handleElementSelected, handleElementMoved, handleElementResized, handleSaveDesign } from './devmode';
import { getTokenExpiry, needsTokenRefresh, refreshToken } from './auth';
import { getAvatarImage, getAvatarDirection, shouldMirrorAvatar, getOpponentAvatarImage, shouldMirrorOpponentAvatar } from './avatars';
import { useTurnTimer, TurnTimerDisplay } from './timer';
import { useDamageFlash, getDamageFlashStyles, DamageAnimationProvider, useDamageAnimationContext, DamageAnimation, isSniperLethalKillOutcome, getActualUnitDamageFromOutcome, calculatePenetrateOverflowFromOutcome, useSlotDeathDetection, SlotDeathAnimations, useGameStartAnimations, getSlideInStyles, useUnitDeploymentAnimations, useDroneDeploymentAnimations, useStrategemSoundEffects, useDeployEffectSounds, useBattlefieldHealthCountdown, useUnitRepositionAnimations, RepositioningProvider, RepositioningOverlay, useStrategemGeneralDamage, useDecommissionAnimations, DecommissionOverlay, ShockwaveEffect, useStrategemShockwave, PulseShockwaveEffect, usePulseShockwave, NeurotoxPulseEffect, useNeurotoxPulse, HiveSurgePulseEffect, useHiveSurgePulse, QuantumSurgeRefreshEffect, useQuantumSurgeRefresh, ViralEchoHealPlusEffect, useViralEchoHealFx, VeinSeverSlashEffect, InfernoNapalmEffect, useInfernoDropNapalm, ExplosionEffect, useTacticalLoadoutFx, TacticalLoadoutFxOverlay, useWeaponEquipFlash, WeaponEquipFlashOverlay } from './effects';
import {
  enrichDeployAnimationDamageMeta,
  ENCRAZED_BOMBARDIER_FIRE_ARC_VARIANT,
  releasePendingProjectileHealthHold,
  resolveDeployHealthHoldTargets,
  trackPendingProjectileHealthHolds,
} from './effects/deployHealthDisplayHold';
import {
  filterHealthHoldTargetsExcludingBullyDamage,
  hasActiveStrategemAnimationsForInstance,
} from './effects/bullyDamageDisplaySync';
import {
  enrichStrategemUnitDamageMeta,
  isStrategemShieldDeflectedHit,
  resolveCountdownTargetHealth,
  applyStrategemCompleteHealthHolds,
  enrichStrategemAnimationDamageMeta,
  resolveStrategemGhostUnitFromPreEffectState,
  resolveStrategemHealthHoldTargets,
  resolveInstantExecuteStrategemHealthHolds,
  resolveInfernoNapalmHoldTargets,
  resolveInstantExecuteFlushKind,
  resolveSurvivorHealthAfter,
  shouldDeferInstantExecuteDamageToOverlay,
  INSTANT_EXECUTE_FLUSH_KIND,
  findCompletedStrategemEntry,
} from './effects/strategemHealthDisplayHold';
import {
  isInstanceIdInActiveTargetedAnimations,
  MAX_STALE_DAMAGE_TRIGGER_COMPLETION_MS,
  resolveBadgeHealth,
  shouldClearHealthDisplayOverride,
} from './effects/battlefieldHealthDisplay';
import {
  buildAttackerCounterDamageTrigger,
  holdScatterHealthForAttackOutcome,
  holdSurvivorHealthForAttackOutcome,
  resolveLiveUnitHealthByInstanceId,
  buildSurvivorCountdownSessionKey,
  resolvePrimaryScatterFollowUp,
  resolveScatterBystanderTargets,
  resolveSurvivorCountdownFromTrigger,
  resolveTargetHealthBeforeFromOutcome,
  resolveAttackTargetShieldDeflectedFromOutcome,
  shouldReholdScatterBeforeAttackCompleteState,
} from './effects/attackHealthDisplayHold';
import { HealthCountdownFlyoutFromInstance } from './effects/HealthCountdownFlyout';
import { REPOSITION_TOTAL_MS } from './effects/repositionAnimationTiming';
import {
  BattlebotPortalOverlay,
  FleshMimicHandPortalOverlay,
  PlagueBloomSlotPortalsOverlay,
  resolveBattlebot369ViewerSlots,
} from './components/BattlebotPortalOverlay';
import { CounterStrikeDialog, OpponentCounterStrikeDialog, useCounterStrike } from './counterstrike';
import { StrategicForecastDialog } from './strategicforecast';
import { MulliganDialog, useMulligan } from './mulligan';
import StateSyncMonitor from './components/StateSyncMonitor';
import StateSyncToggle from './components/StateSyncToggle';
import RevealedOrderModal from './RevealedOrderModal';
import { TargetingCancelDialog, useTargetingCancel } from './cancel';
import { useTranslation } from 'react-i18next';
import { updateDiscordPresence, clearDiscordPresence } from './hooks/useDiscordPresence';
import BattleActionLog from './BattleActionLog/BattleActionLog';
import { useUnitRegistry, updateUnitRegistry } from './UnitRegistry';
import { detectActions } from './actionDetection';
import { isFriendSpectatingSession } from './utils/battleSpectateSession';
import { useContainerScale } from './hooks/useViewportScale';
import {
  MobilePlayerAdvisor,
  MobilePlayerDrones,
  MobileOpponentDrones,
  MobileOpponentStats,
  MobileOrderCarousel
} from './components/mobile';
import {
  MOBILE_OPPONENT_AVATARS_TOP_PX,
} from './components/mobile/mobileBattleLayoutOffsets';
import useTouchDragDeploy from './hooks/useTouchDragDeploy';
import TouchDragOverlay from './components/TouchDragOverlay';
import useTouchDragAttack from './hooks/useTouchDragAttack';
import AttackDragOverlay from './components/AttackDragOverlay';
import { useAutobattlerMode } from './hooks/useAutobattlerMode';
import { useFriends } from './friends';
import {
  extractBattleGameIdFromPath,
  resolveExpectedSessionGameId,
  isWsMessageForDifferentSession as isWsMessageForDifferentSessionId,
  shouldDiscardStaleGameFetch,
} from './utils/battleWsSessionGuard';
import { useSpectateSpeedControl } from './hooks/useSpectateSpeedControl';
import { SpectateSpeedControl } from './components/SpectateSpeedControl';
import BattleDragFlashlight, { OpponentBattleFlashlight } from './components/BattleDragFlashlight';
import { useBattleFlashlightNetworkSync } from './hooks/useBattleFlashlightNetworkSync';
import { isValidFlashlightColorKey } from './constants/flashlightColors';
import {
  getTranslatedEmoteSpeech,
  formatEmoteBubbleTwoWords,
  resolveBattleEmoteBubbleDisplay,
} from './utils/emoteSpeechI18n';
import { EXPLORE_UNKNOWN_DARK_KNOWLEDGE_TARGET_PARTS } from './config/exploreUnknownDarkKnowledge';
import {
  EXPLORE_UNKNOWN_HEART_METERS,
  EXPLORE_UNKNOWN_HEART_TARGET_PARTS,
} from './config/exploreUnknownHearts';
import {
  EXPLORE_UNKNOWN_EP_METERS,
  EXPLORE_UNKNOWN_EP_TARGET_PARTS,
} from './config/exploreUnknownEmergencyProtocol';
import {
  EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_METER,
  EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_TARGET_PARTS,
} from './config/exploreUnknownRewriteExperience';
import { ExploreUnknownHeartProgressBar } from './components/ExploreUnknownHeartProgressBar';
import { VEIN_SEVER_SLASH_DURATION_MS } from './config/veinSeverEffect';
import { EXPLORE_UNKNOWN_PORTAL_LAYOUT } from './exploreUnknownPortalLayout';
import { unitCannotInitiateAttack } from './utils/unitCannotInitiateAttack';
import { convertPendingAnimationForViewer } from './utils/convertPendingAnimationForViewer';
import {
  shouldDeferStrategemAnimationComplete,
  tryFlushDeferredStrategemComplete,
  setPendingStrategemAnimInfo,
  getPendingStrategemAnimInfo,
  deletePendingStrategemAnimInfo,
  normalizeStrategemInstanceKey,
  resolveStrategemAnimationCompleteSend,
} from './utils/strategemTwoPhaseClient';
import {
  clearOverlayStrategemWatchdog,
  scheduleOverlayStrategemCompleteWatchdog,
} from './utils/overlayStrategemWatchdog';
import { passThroughExpandedBattlefieldPortalsForTargeting } from './utils/passThroughExpandedBattlefieldPortalsForTargeting';
import {
  getOverlappingBattlefieldSlotZIndex,
  OVERLAPPING_BATTLEFIELD_SLOT_COUNT,
} from './utils/battlefieldUnitExpandHoverHotZone';
import { isExploreUnknownEpTargetUnit } from './utils/exploreUnknownEpTargeting';
import { isPlayerGeneralAflameInMission68 } from './utils/exploreUnknownGeneralAflame';
import AbilityTooltip from './utils/AbilityTooltip';
import { buildDeployedDroneTooltipText } from './utils/deployedDroneTooltipText';
import { getTitleColorHex } from './constants/titleColors';
import {
  buildBattleTitleLookupFromProfileTitles,
  getLocalizedBattleTitle,
  normalizeTitlesApiLang,
} from './utils/battleTitleLocalization';
import { BATTLE_HEALTH_BARRIER_THRESHOLDS, BARRIER_SPRITE_DISMANTLE_DELAY_MS } from './constants/battleHealthHud';
import { getFactionIconPath } from './utils/factionHudDisplay';

/** Fullscreen battle-end art: win uses JPG per locale; default/defeat still use MP4 for loss in non-de/es locales. */
function getBattleEndOverlayVisuals(resolvedLang) {
  const raw = (resolvedLang || 'en-us').toLowerCase();
  const primary = raw.split('-')[0];
  if (primary === 'de') {
    return {
      winSrc: '/images/sieg.jpg',
      winIsVideo: false,
      lossSrc: '/images/niederlage.jpg',
      lossIsVideo: false,
    };
  }
  if (primary === 'es') {
    return {
      winSrc: '/images/victoria.jpg',
      winIsVideo: false,
      lossSrc: '/images/derrota.jpg',
      lossIsVideo: false,
    };
  }
  return {
    winSrc: '/images/victory.jpg',
    winIsVideo: false,
    lossSrc: '/images/defeat.mp4',
    lossIsVideo: true,
  };
}

// Faction colors for health orbs (matching Strategy.js)
const factionColors = {
  'Remnants of Humanity': '#556B2F',
  'Exolot Dominion': '#4B0082',
  'League of Shadows': '#1A1A1A',
  'Ra\'s Children': '#F4A460',
  'Asrah': '#8B0000',
  'The Sentients': '#ADD8E6',
  'Combined': '#FFA500'
};

/** Legendary-unique deploy ray with sparkling orbs (e.g. Samuel Johnson). Orbs randomly distributed throughout the ray; sibling so not blurred. */
const LegendaryUniqueDeployRay = () => (
  <>
    <div className="legendary-unique-deploy-ray" aria-hidden="true" />
    <div className="legendary-unique-deploy-ray-orbs" aria-hidden="true">
      {Array.from({ length: 126 }, (_, i) => (
        <span
          key={i}
          className="legendary-deploy-glow-orb"
          style={{
            left: `${12 + (i * 13) % 76}%`,
            top: `${4 + (i * 19) % 92}%`,
            animationDelay: `${(i * 0.07) % 2}s`,
          }}
        />
      ))}
    </div>
  </>
);

/** Legendary deploy glow with flickering orbs (non-unique legendaries e.g. t-rex). Orbs are a sibling layer so they are not blurred by the glow's filter. */
const LegendaryDeployGlow = () => (
  <>
    <div className="legendary-deploy-glow" aria-hidden="true" />
    <div className="legendary-deploy-glow-orbs" aria-hidden="true">
      {Array.from({ length: 42 }, (_, i) => (
        <span
          key={i}
          className="legendary-deploy-glow-orb"
          style={{
            left: `${8 + (i * 11) % 84}%`,
            top: `${8 + (i * 17) % 84}%`,
            animationDelay: `${(i * 0.08) % 2}s`,
          }}
        />
      ))}
    </div>
  </>
);

// Get faction orb background color/gradient (matching Strategy.js implementation)
const getFactionOrbBackground = (factionName) => {
  if (!factionName || factionName === 'Unknown') {
    return '#ccc';
  }

  // Check if it's a combined faction and return gradient with smooth blending
  // Using 45% and 55% stops creates a smooth transition zone in the middle
  if (factionName === 'The Conspiracy') {
    return 'linear-gradient(to right, #8B0000 45%, #1A1A1A 55%)';
  } else if (factionName === 'The Corrupted Core') {
    return 'linear-gradient(to right, #4B0082 45%, #8B0000 55%)';
  } else if (factionName === 'The Covenant') {
    return 'linear-gradient(to right, #F4A460 45%, #1A1A1A 55%)';
  } else if (factionName === 'The Divine Syndicate') {
    return 'linear-gradient(to right, #F4A460 45%, #4B0082 55%)';
  } else if (factionName === 'The Forsaken Pact') {
    return 'linear-gradient(to right, #556B2F 45%, #8B0000 55%)';
  } else if (factionName === 'The Hollow') {
    return 'linear-gradient(to right, #F4A460 45%, #8B0000 55%)';
  } else if (factionName === 'The Iron Alliance') {
    return 'linear-gradient(to right, #556B2F 45%, #4B0082 55%)';
  } else if (factionName === 'The Last Crusade') {
    return 'linear-gradient(to right, #F4A460 45%, #556B2F 55%)';
  } else if (factionName === 'The Shadow Consortium') {
    return 'linear-gradient(to right, #1A1A1A 45%, #4B0082 55%)';
  } else if (factionName === 'The Silent Accord') {
    return 'linear-gradient(to right, #556B2F 45%, #1A1A1A 55%)';
  }

  // Return base faction color
  return factionColors[factionName] || '#ccc';
};

/** Unit carousel: drag-to-deploy requires this much upward (negative Y) pointer travel from mousedown before a deploy is triggered. Horizontal / downward / tiny drags do not deploy. */
const CAROUSEL_DEPLOY_MIN_UPWARD_PX = 50;
const ENLARGED_OPPONENT_AVATAR_NAMES = new Set([
  'avatar_peacock',
  'avatar_peackock',
  'avatar_boss_1',
  'avatar_gallmuecke',
  'avatar_kraken',
  'kangaroo_profile',
  'mortality_profile',
  'serpent_profile',
  'boss_instinct__profile',
  'kinship_boss_profile',
  'sn_profile',
]);

function exploreUnknownDiplomaticAccordsActive(gameState) {
  if (gameState?.exploreUnknownMissionIndex == null) {
    return true;
  }
  const bonuses = gameState?.exploreUnknownLevelBonuses;
  return bonuses?.diplomaticAccords === true || bonuses?.reactiveDoctrine === true;
}

function normalizeDiplomaticAccordsList(value) {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }
  if (value) {
    return [value];
  }
  return [];
}

function getDiplomaticAccordTooltipText(accord, t) {
  if (accord === 'strategic_coordination') {
    return t('profile.diplomaticAccords.strategicCoordination');
  }
  if (accord === 'public_relations') {
    return t('profile.diplomaticAccords.publicRelations');
  }
  if (accord === 'emergency_protocol') {
    return t('profile.diplomaticAccords.emergencyProtocol');
  }
  if (accord === 'command_directive') {
    return t('profile.diplomaticAccords.commandDirective');
  }
  return '';
}

function playerAccordsInclude(gameState, playerId, accordKey) {
  const playerVal = gameState?.playerDiplomaticAccords?.[playerId];
  const opponentVal = gameState?.opponentDiplomaticAccords?.[playerId];
  return (
    normalizeDiplomaticAccordsList(playerVal).includes(accordKey) ||
    normalizeDiplomaticAccordsList(opponentVal).includes(accordKey)
  );
}

function exploreUnknownPreparationBonusActive(gameState) {
  if (gameState?.exploreUnknownMissionIndex == null) {
    return true;
  }
  return gameState?.exploreUnknownLevelBonuses?.preparationBonus === true;
}

function exploreUnknownBlackMarketMaxUses(gameState) {
  const n = Number(gameState?.exploreUnknownBlackMarketUsesPerBattle);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

function exploreUnknownPlanAheadMaxUses(gameState) {
  const n = Number(gameState?.exploreUnknownPlanAheadUsesPerBattle);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

/** Campaign tutorial missions 1–4 (chapter 1): UI hides player advisor portrait and opponent PvP rank banner. */
const EARLY_TUTORIAL_MISSION_IDS = Object.freeze([
  'chapter_1_mission_1',
  'chapter_1_mission_2',
  'chapter_1_mission_3',
  'chapter_1_mission_4',
]);

function shouldEnlargeOpponentAvatar(avatarName) {
  if (!avatarName || typeof avatarName !== 'string') {
    return false;
  }
  const normalizedAvatarName = avatarName
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/\.(png|jpg|jpeg|webp)$/i, '');
  return ENLARGED_OPPONENT_AVATAR_NAMES.has(normalizedAvatarName);
}

function getOpponentAvatarScale(avatarName) {
  return shouldEnlargeOpponentAvatar(avatarName) ? 1.65 : 1;
}

/** Extra scale for opponent portrait inside desktop `OpponentHUD` profile ring (+20% vs zone cards). */
const OPPONENT_HUD_PROFILE_RING_SCALE_MULTIPLIER = 1.2;

/** Explore Unknown mission 23 (Pride / Peacock boss): bot general portrait +38% in battle HUD only (20% then +15%). */
function getExploreUnknownPeacockBossOpponentHudProfileScaleBonus(missionIndex) {
  return Number(missionIndex) === 23 ? 1.38 : 1;
}

function normalizeEuAvatarNameForDedupe(avatarName) {
  if (!avatarName || typeof avatarName !== 'string') return '';
  return avatarName
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/\.(png|jpg|jpeg|webp)$/i, '');
}

/**
 * Boss missions should never show the same portrait twice. If general === advisor (stale
 * localStorage or legacy payloads), force the standard Explore Unknown advisor silhouette.
 */
function dedupeExploreUnknownBossOpponentAdvisorIfNeeded(missionIndex, general, advisor) {
  const idx = Number(missionIndex);
  if (!Number.isInteger(idx)) {
    return advisor;
  }
  const bossIndices = EXPLORE_UNKNOWN_PORTAL_LAYOUT.bossMissionIndices;
  if (!Array.isArray(bossIndices) || !bossIndices.includes(idx)) {
    return advisor;
  }
  const g = normalizeEuAvatarNameForDedupe(general);
  const a = normalizeEuAvatarNameForDedupe(advisor);
  if (!g || !a) {
    return advisor;
  }
  if (g === a && g !== 'avatar_shadows_1') {
    return 'avatar_shadows_2';
  }
  return advisor;
}

const FRONTAL_HARASSMENT_CROSS_VARIANT = 'frontal_harassment_blue_cross';

function convertPendingDeployAnimationForViewer(animationData, viewerUserId) {
  return convertPendingAnimationForViewer(animationData, viewerUserId);
}

/**
 * Delay barrier-cross VFX until the attack swing finishes (attack-complete arrives while anim may still run).
 */
function getBarrierCrossPlaybackDelayMs(attackId, attackAnimationsRef, animationSpeedMultiplier) {
  const speedFactor = animationSpeedMultiplier > 0 ? animationSpeedMultiplier : 1;
  const animDurationMs = Math.round(1500 / speedFactor);
  if (!attackId || !attackAnimationsRef?.current?.getPendingAttack) {
    return Math.min(animDurationMs, 400);
  }
  const pending = attackAnimationsRef.current.getPendingAttack(attackId);
  if (!pending) {
    return 200;
  }
  const elapsed = Date.now() - (pending.registeredAt || 0);
  return Math.max(200, Math.min(animDurationMs - elapsed + 120, 2200));
}

/**
 * Convert server pendingDeployAnimations to viewer-relative slots and register deploy VFX.
 * Removes gameStateFragment.pendingDeployAnimations after processing (same as main WS handler).
 */
function consumePendingDeployAnimationsForViewer(
  gameStateFragment,
  viewerUserId,
  deployHook,
  consumeOptions = {}
) {
  if (!gameStateFragment?.pendingDeployAnimations || !Array.isArray(gameStateFragment.pendingDeployAnimations)) {
    return;
  }
  if (!deployHook?.registerAnimation) {
    throw new Error('consumePendingDeployAnimationsForViewer: deployHook.registerAnimation is missing');
  }
  const pending = [...gameStateFragment.pendingDeployAnimations];
  delete gameStateFragment.pendingDeployAnimations;

  const { messageType, attackId, animationSpeedMultiplier = 1, attackAnimationsRef } = consumeOptions;

  const converted = pending.map((anim) => convertPendingDeployAnimationForViewer(anim, viewerUserId));
  const barrierCross = converted.filter(
    (a) => a.deployAnimationVariant === FRONTAL_HARASSMENT_CROSS_VARIANT
  );
  const immediate = converted.filter(
    (a) => a.deployAnimationVariant !== FRONTAL_HARASSMENT_CROSS_VARIANT
  );

  const { previousGameState = null } = consumeOptions;

  immediate.forEach((anim) => {
    let payload = anim;
    if (previousGameState && gameStateFragment) {
      payload = enrichDeployAnimationDamageMeta(
        anim,
        previousGameState,
        gameStateFragment,
        viewerUserId
      );
    }
    deployHook.registerAnimation(payload);
  });

  if (barrierCross.length === 0) {
    return;
  }

  if (!deployHook.enqueueBarrierCrossAnimations) {
    barrierCross.forEach((anim) => deployHook.registerAnimation(anim));
    return;
  }

  // Orphan crosses on turn-ended (should have played on attack-complete).
  if (messageType === 'turn-ended') {
    return;
  }

  // Stale payload on attack-initiated — never play before the attack animation.
  if (messageType === 'attack-initiated') {
    return;
  }

  if (messageType === 'attack-complete') {
    const delayMs = getBarrierCrossPlaybackDelayMs(
      attackId,
      attackAnimationsRef,
      animationSpeedMultiplier
    );
    deployHook.enqueueBarrierCrossAnimations(barrierCross, { delayMs });
    return;
  }

  // Strategem / deploy / game-update barrier breaks — play sequentially after a short DOM settle.
  deployHook.enqueueBarrierCrossAnimations(barrierCross, { delayMs: 80 });
}

/**
 * `pendingStrategemAnimations` from the server are caster-relative (`your_` / `opponent_` = caster's side vs enemy).
 * The watching client's board is viewer-relative, so when the **opponent** cast the strategem we must mirror zones
 * (same idea as `consumePendingDeployAnimationsForViewer`). The caster's own client does not use this path
 * (it registered the animation in targetStrategem.js).
 */
function mirrorStrategemToSlotForOpponentInitiatedViewer(toSlot) {
  if (!toSlot) return toSlot;
  const flipYourOpponent = (p) => {
    if (p === 'your' || p === 'opponent') return p === 'your' ? 'opponent' : 'your';
    const s = String(p);
    if (s === 'your' || s === 'opponent') return s === 'your' ? 'opponent' : 'your';
    throw new Error(`mirrorStrategemToSlotForOpponentInitiatedViewer: bad playerId ${String(p)}`);
  };
  if (toSlot.zone === 'general') {
    return {
      ...toSlot,
      playerId: flipYourOpponent(toSlot.playerId),
    };
  }
  const z = toSlot.zone;
  if (typeof z !== 'string') return toSlot;
  let newZone = z;
  if (z.startsWith('your_')) {
    newZone = `opponent_${z.slice('your_'.length)}`;
  } else if (z.startsWith('opponent_')) {
    newZone = `your_${z.slice('opponent_'.length)}`;
  }
  return {
    ...toSlot,
    zone: newZone,
    playerId: flipYourOpponent(toSlot.playerId),
  };
}

/**
 * Session-guarded consume for any handler path. Logs failures; does not throw (state can still be applied).
 */
function tryConsumePendingDeployAnimationsForViewer(
  gameStateFragment,
  viewerUserId,
  deployHook,
  msgGameId,
  isDifferentSession,
  consumeOptions = {}
) {
  if (!gameStateFragment?.pendingDeployAnimations?.length) {
    return;
  }
  if (typeof isDifferentSession === 'function' && msgGameId != null && isDifferentSession(msgGameId)) {
    return;
  }
  if (!deployHook?.registerAnimation) {
    console.error('🎬 tryConsumePendingDeployAnimationsForViewer: deployHook.registerAnimation is missing');
    return;
  }
  try {
    consumePendingDeployAnimationsForViewer(
      gameStateFragment,
      viewerUserId,
      deployHook,
      consumeOptions
    );
  } catch (err) {
    console.error('🎬 tryConsumePendingDeployAnimationsForViewer failed', err);
  }
}

/**
 * Server pendingRaidAnimations: red energy stream from raided general → raiding unit.
 */
function consumePendingRaidAnimationsForViewer(
  gameStateFragment,
  viewerUserId,
  raidHook,
  consumeOptions = {}
) {
  if (!gameStateFragment?.pendingRaidAnimations || !Array.isArray(gameStateFragment.pendingRaidAnimations)) {
    return;
  }
  if (!raidHook?.scheduleRaidAnimation) {
    throw new Error('consumePendingRaidAnimationsForViewer: raidHook.scheduleRaidAnimation is missing');
  }

  const pending = [...gameStateFragment.pendingRaidAnimations];
  delete gameStateFragment.pendingRaidAnimations;

  const {
    messageType,
    attackId,
    animationSpeedMultiplier = 1,
    attackAnimationsRef,
  } = consumeOptions;

  if (messageType === 'attack-initiated') {
    return;
  }

  const delayMs = messageType === 'attack-complete'
    ? getBarrierCrossPlaybackDelayMs(attackId, attackAnimationsRef, animationSpeedMultiplier)
    : 80;

  pending
    .map((anim) => convertPendingDeployAnimationForViewer(anim, viewerUserId))
    .forEach((anim) => {
      raidHook.scheduleRaidAnimation(anim, delayMs);
    });
}

function tryConsumePendingRaidAnimationsForViewer(
  gameStateFragment,
  viewerUserId,
  raidHook,
  msgGameId,
  isDifferentSession,
  consumeOptions = {}
) {
  if (!gameStateFragment?.pendingRaidAnimations?.length) {
    return;
  }
  if (typeof isDifferentSession === 'function' && msgGameId != null && isDifferentSession(msgGameId)) {
    return;
  }
  if (!raidHook?.scheduleRaidAnimation) {
    console.error('🎬 tryConsumePendingRaidAnimationsForViewer: raidHook.scheduleRaidAnimation is missing');
    return;
  }
  try {
    consumePendingRaidAnimationsForViewer(
      gameStateFragment,
      viewerUserId,
      raidHook,
      consumeOptions
    );
  } catch (err) {
    console.error('🎬 tryConsumePendingRaidAnimationsForViewer failed', err);
  }
}

function mergeDroneDamageHealthMetadataFromServer(serverAnim, viewerPayload) {
  if (!serverAnim) {
    return viewerPayload;
  }

  const merged = {
    ...viewerPayload,
    deployUnitId: serverAnim.deployUnitId ?? viewerPayload.deployUnitId,
    deployUnitName: serverAnim.deployUnitName ?? viewerPayload.deployUnitName,
    deployUnitInstanceId: serverAnim.deployUnitInstanceId ?? viewerPayload.deployUnitInstanceId,
    deployUnitAttackSoundType:
      serverAnim.deployUnitAttackSoundType ?? viewerPayload.deployUnitAttackSoundType,
    isRedeemDamageAnimation: serverAnim.isRedeemDamageAnimation === true,
    isDroneDamageAnimation: serverAnim.isDroneDamageAnimation === true,
    isGeneralTarget: serverAnim.isGeneralTarget === true,
    animationKey: serverAnim.animationKey ?? viewerPayload.animationKey,
  };

  if (serverAnim.isGeneralTarget === true) {
    if (
      typeof serverAnim.generalHealthBefore !== 'number'
      || typeof serverAnim.generalHealthAfter !== 'number'
      || typeof serverAnim.damageAmount !== 'number'
    ) {
      return merged;
    }
    return {
      ...merged,
      targetType: 'general',
      isGeneralTarget: true,
      damageAmount: serverAnim.damageAmount,
      generalHealthBefore: serverAnim.generalHealthBefore,
      generalHealthAfter: serverAnim.generalHealthAfter,
    };
  }

  if (
    typeof serverAnim.healthBefore !== 'number'
    || typeof serverAnim.healthAfter !== 'number'
    || typeof serverAnim.damageAmount !== 'number'
    || !serverAnim.targetInstanceId
  ) {
    return merged;
  }

  return {
    ...merged,
    targetInstanceId: serverAnim.targetInstanceId,
    healthBefore: serverAnim.healthBefore,
    healthAfter: serverAnim.healthAfter,
    damageAmount: serverAnim.damageAmount,
    ghostUnit: serverAnim.ghostUnit ?? viewerPayload.ghostUnit,
    targetZone: viewerPayload.toSlot?.zone,
    targetIndex: viewerPayload.toSlot?.index,
  };
}

/**
 * Drone charge unit damage (398, etc.) — reuse deploy projectile VFX + HP hold/countdown.
 */
function consumePendingDroneDamageAnimationsForViewer(
  gameStateFragment,
  viewerUserId,
  deployHook,
  consumeOptions = {}
) {
  if (!gameStateFragment?.pendingDroneDamageAnimations || !Array.isArray(gameStateFragment.pendingDroneDamageAnimations)) {
    return;
  }
  if (!deployHook?.registerAnimation) {
    throw new Error('consumePendingDroneDamageAnimationsForViewer: deployHook.registerAnimation is missing');
  }
  const pending = [...gameStateFragment.pendingDroneDamageAnimations];
  delete gameStateFragment.pendingDroneDamageAnimations;

  const { previousGameState = null } = consumeOptions;

  pending.forEach((anim) => {
    let payload = convertPendingDeployAnimationForViewer(anim, viewerUserId);
    payload = mergeDroneDamageHealthMetadataFromServer(anim, payload);
    if (previousGameState && gameStateFragment) {
      payload = enrichDeployAnimationDamageMeta(
        payload,
        previousGameState,
        gameStateFragment,
        viewerUserId
      );
      payload = mergeDroneDamageHealthMetadataFromServer(anim, payload);
    }
    deployHook.registerAnimation(payload);
  });
}

function tryConsumePendingDroneDamageAnimationsForViewer(
  gameStateFragment,
  viewerUserId,
  deployHook,
  msgGameId,
  isDifferentSession,
  consumeOptions = {}
) {
  if (!gameStateFragment?.pendingDroneDamageAnimations?.length) {
    return;
  }
  if (typeof isDifferentSession === 'function' && msgGameId != null && isDifferentSession(msgGameId)) {
    return;
  }
  if (!deployHook?.registerAnimation) {
    console.error('🎬 tryConsumePendingDroneDamageAnimationsForViewer: deployHook.registerAnimation is missing');
    return;
  }
  try {
    consumePendingDroneDamageAnimationsForViewer(
      gameStateFragment,
      viewerUserId,
      deployHook,
      consumeOptions
    );
  } catch (err) {
    console.error('🎬 tryConsumePendingDroneDamageAnimationsForViewer failed', err);
  }
}

function tryConsumePendingRedeemDeathSequencesForViewer(
  gameStateFragment,
  redeemDeathSequenceHook,
  msgGameId,
  isDifferentSession
) {
  if (!gameStateFragment?.pendingRedeemDeathSequences?.length) {
    return;
  }
  if (typeof isDifferentSession === 'function' && msgGameId != null && isDifferentSession(msgGameId)) {
    return;
  }
  if (!redeemDeathSequenceHook?.consumePendingRedeemDeathSequences) {
    throw new Error('tryConsumePendingRedeemDeathSequencesForViewer: consumePendingRedeemDeathSequences is missing');
  }
  redeemDeathSequenceHook.consumePendingRedeemDeathSequences(gameStateFragment);
}

// Calculate base factions and faction name from strategy orders (matching server logic)
// This is needed because getStrategyWithOrders() doesn't add faction_name to the strategy object
const getBaseFactionsFromStrategy = (strategy) => {
  if (!strategy || !strategy.orders || strategy.orders.length === 0) {
    return [];
  }

  const combinedFactions = {
    'The Conspiracy': ['League of Shadows', 'Asrah'],
    'The Corrupted Core': ['Exolot Dominion', 'Asrah'],
    'The Covenant': ['Ra\'s Children', 'League of Shadows'],
    'The Divine Syndicate': ['Ra\'s Children', 'Exolot Dominion'],
    'The Forsaken Pact': ['Remnants of Humanity', 'Asrah'],
    'The Hollow': ['Ra\'s Children', 'Asrah'],
    'The Iron Alliance': ['Remnants of Humanity', 'Exolot Dominion'],
    'The Last Crusade': ['Ra\'s Children', 'Remnants of Humanity'],
    'The Shadow Consortium': ['League of Shadows', 'Exolot Dominion'],
    'The Silent Accord': ['Remnants of Humanity', 'League of Shadows']
  };

  const baseFactionNames = ['Asrah', 'Exolot Dominion', 'League of Shadows', 'Ra\'s Children', 'Remnants of Humanity'];
  const combinedFactionNames = Object.keys(combinedFactions);

  // Get unique factions from orders
  const uniqueFactions = [...new Set(strategy.orders.map(order => order.faction_name).filter(Boolean))];

  // Get base factions (excluding The Sentients and combined faction names)
  const baseFactions = uniqueFactions
    .filter(f => f && f !== null && f.trim() !== '')
    .filter(f => !combinedFactionNames.includes(f)) // Remove combined faction names
    .filter(f => baseFactionNames.includes(f) || f === 'The Sentients'); // Only keep base factions (and Sentients)

  // Remove The Sentients (no dedicated background mapping)
  const baseFactionsOnly = baseFactions.filter(f => f !== 'The Sentients');

  return baseFactionsOnly;
};

const calculateFactionFromStrategy = (strategy) => {
  if (!strategy || !strategy.orders || strategy.orders.length === 0) {
    return null;
  }

  // If strategy already has faction_name, use it
  if (strategy.faction_name) {
    return strategy.faction_name;
  }

  const combinedFactions = {
    'The Conspiracy': ['League of Shadows', 'Asrah'],
    'The Corrupted Core': ['Exolot Dominion', 'Asrah'],
    'The Covenant': ['Ra\'s Children', 'League of Shadows'],
    'The Divine Syndicate': ['Ra\'s Children', 'Exolot Dominion'],
    'The Forsaken Pact': ['Remnants of Humanity', 'Asrah'],
    'The Hollow': ['Ra\'s Children', 'Asrah'],
    'The Iron Alliance': ['Remnants of Humanity', 'Exolot Dominion'],
    'The Last Crusade': ['Ra\'s Children', 'Remnants of Humanity'],
    'The Shadow Consortium': ['League of Shadows', 'Exolot Dominion'],
    'The Silent Accord': ['Remnants of Humanity', 'League of Shadows']
  };

  const baseFactionsOnly = getBaseFactionsFromStrategy(strategy);

  if (baseFactionsOnly.length === 0) {
    return 'Unknown';
  } else if (baseFactionsOnly.length === 1) {
    // Single base faction
    return baseFactionsOnly[0];
  } else {
    // Check if it matches a combined faction
    const sortedBaseFactions = [...baseFactionsOnly].sort();

    // Try to find a matching combined faction
    let matchedFaction = null;
    for (const [combinedFactionName, requiredFactions] of Object.entries(combinedFactions)) {
      const sortedRequired = [...requiredFactions].sort();
      const baseSet = new Set(sortedBaseFactions);
      const requiredSet = new Set(sortedRequired);

      if (baseSet.size === requiredSet.size &&
          sortedBaseFactions.every(f => requiredSet.has(f)) &&
          sortedRequired.every(f => baseSet.has(f))) {
        matchedFaction = combinedFactionName;
        break;
      }
    }

    if (matchedFaction) {
      return matchedFaction;
    }

    // No combined faction match; use the first base faction
    return baseFactionsOnly[0];
  }
};

// Map base factions to background images for BattleUI
const getBackgroundForBaseFactions = (baseFactions) => {
  if (!Array.isArray(baseFactions) || baseFactions.length === 0) {
    throw new Error('BattleUI background selection failed: no base factions provided.');
  }

  const factionBackgrounds = {
    'League of Shadows': ['alps.jpg'],
    Asrah: ['ganges.jpg'],
    "Ra's Children": ['pyramid.jpg'],
    'Exolot Dominion': ['lavender.jpg'],
    'Remnants of Humanity': ['citycenter.jpg', 'refinery.jpg', 'snow.jpg'],
  };

  const pool = baseFactions.reduce((acc, faction) => {
    const backgrounds = factionBackgrounds[faction];
    if (backgrounds && backgrounds.length > 0) {
      acc.push(...backgrounds);
    }
    return acc;
  }, []);

  if (pool.length === 0) {
    throw new Error(`BattleUI background selection failed: no background mapping for base factions: ${baseFactions.join(', ')}`);
  }

  const randomIndex = Math.floor(Math.random() * pool.length);
  return pool[randomIndex];
};
import CompletedOrdersModal from './CompletedOrdersModal';
import { FriendsIcon } from './friends';
import { SettingsMenu } from './settings';
import { getEffectiveSoundOutputVolume } from './hooks/soundEffectsManager';
import { getWebSocketAuthMessage, API_URL, WS_URL } from './config/apiConfig';
import {
  tryGetExploreUnknownBattleBackgroundPath,
  resolveBattleBackgroundCssUrl,
  EXPLORE_UNKNOWN_BOSS_LOSS_DARKNESS_INCREMENT_PERCENT,
  EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_LOSS_DARKNESS_INCREMENT_PERCENT,
} from './config/exploreUnknownMissionBattle';
import { computeExploreUnknownDarknessLossFillFromPct } from './utils/exploreUnknownDarknessLossTransition';
import {
  bumpExploreUnknownBossAttemptForRetry,
  clearExploreUnknownBossAttemptNumber,
  getExploreUnknownBossRetryAttemptTranslationKey,
  readExploreUnknownBossAttemptNumber,
} from './utils/exploreUnknownBossRetryAttempt';
import ExploreUnknownDarknessMeter from './components/ExploreUnknownDarknessMeter';
import { isTrainPvEBattle } from './utils/isTrainPvEBattle';
import { didHumanPlayerWinGame } from './utils/exploreUnknownBattleOutcome';
import ExploreUnknownDarkKnowledgeRewardModal from './exploreUnknownRewardPopups/ExploreUnknownDarkKnowledgeRewardModal';
import ExploreUnknownHeartSetCompleteModal from './exploreUnknownRewardPopups/ExploreUnknownHeartSetCompleteModal';
import ExploreUnknownEpSetCompleteModal from './exploreUnknownRewardPopups/ExploreUnknownEpSetCompleteModal';
import ExploreUnknownRewriteSetCompleteModal from './exploreUnknownRewardPopups/ExploreUnknownRewriteSetCompleteModal';
import {
  DUPLICATE_TARGET_IMAGE_PATH,
  DUPLICATE_TARGET_CENTERED_IMAGE_STYLE,
  DUPLICATE_HAND_OVERLAY_MATCH_ORDER_DESIGN_TRANSFORM,
  DUPLICATE_SOUND_PATH,
} from './config/duplicateStrategemAnimation';

// Action history limit - maximum number of actions to display
// Storage limit: Store up to 50 actions for swipe navigation
// Action log: server sends entries; BattleActionLog shows the 3 most recent order-card thumbnails inside the open button (no entry-count badge) and the full list in a drawer
import StrategyModal from './StrategyModal';
import MobileTooltipLayer from './components/mobile/MobileTooltipLayer';
import { useAttackAnimations } from './hooks/useAttackAnimations';
import { useGameStateSync } from './hooks/useGameStateSync';
import AttackAnimation from './components/AttackAnimation';
import { getDeployedUnitInfo } from './utils/deploySpawnUnitInfo';
import { buildOrderDetailLangQuery } from './utils/buildOrderDetailLangQuery';
import { DeployTargetAnimation } from './components/DeployTargetAnimation';
import { useDeployTargetAnimations } from './hooks/useDeployTargetAnimations';
import { useRaidAnimations } from './hooks/useRaidAnimations';
import { RaidEnergyStreamEffect } from './effects/RaidEnergyStreamEffect';
import './effects/RaidEnergyStreamEffect.css';
import { useRedeemDeathSequence } from './effects/useRedeemDeathSequence';
import './effects/RedeemDeathDissipation.css';
import { useSkyfireStrikeAnimations } from './hooks/useSkyfireStrikeAnimations';
import { useExploreUnknownKinshipGeneralDamageAnimations } from './hooks/useExploreUnknownKinshipGeneralDamageAnimations';
import { useExploreUnknownNegaraBossMimicToast } from './hooks/useExploreUnknownNegaraBossMimicToast';
import ExploreUnknownNegaraBossMimicIntro from './ExploreUnknownNegaraBossMimicIntro';
import ExploreUnknownNegaraOutro from './ExploreUnknownNegaraOutro';
import { StrategemTargetAnimation } from './components/StrategemTargetAnimation';
import { doesStrategemDealDamageById, doesStrategemDestroyUnitsById, doesStrategemShrinkUnitsById, getStrategemDamageAmountById, getDeployedUnitHealthByInstanceId } from './utils/strategemUtils';
import {
  BATTLEFIELD_SLOT_ZONES,
  getBattlefieldUnitAtZoneIndex,
  getSlotDamageAnchorFromDom,
  isValidDamageAnimationAnchor,
  isBattlefieldUnitLethalHit,
  postDamageHealthFromSnapshot,
  resolveDamageTriggerIsGeneralAnimation,
} from './utils/battleLethalDeathFx';
import {
  getMobileGeneralDamageAnchorFromDom,
  getMobileSlotDamageAnchorFromDom,
  isValidMobileDamageAnimationAnchor,
  resolveMobileDamageAnimationContainerSizePx,
} from './utils/mobileBattleDamageFx';
import decommissionAnimationDetector from './effects/decommissionAnimationDetector';

const { isDecommissionedUnitInHand: isDecommissionedUnitInHandForLethalFx } = decommissionAnimationDetector;
import {
  isInvalidStrategemTargetPixel,
  isUnusableStrategemTargetPixel,
  slotHasVisibleDeployedUnit,
} from './utils/strategemTargetPosition';
import { getFractureMindsOutcomeForPlayer, FRACTURE_MINDS_STRATEGEM_ID } from './utils/fractureMindsOutcome';
import { useStrategemTargetAnimations } from './hooks/useStrategemTargetAnimations';
import { useOpponentStrategemAnimations } from './hooks/useOpponentStrategemAnimations';
import {
  usesCasterGeneralProjectileOrigin,
  getStrategemCasterGeneralPixelPosition,
} from './config/casterGeneralStrategemProjectile';
import { useBattleMusic } from './hooks/useBattleMusic';
import { useExploreUnknownMission46CelloAudio } from './hooks/useExploreUnknownMission46CelloAudio';
import { isExploreUnknownKendrexColonyMissionIndex } from './config/exploreUnknownKendrexColony';
import { getExploreUnknownBossIntroTranslationKey } from './config/exploreUnknownBossIntro';
import { setExploreUnknownKendrexColonyBattleAudioSuppressed } from './utils/exploreUnknownKendrexColonyAudio';
import { playSoundEffect } from './hooks/useSoundEffects';
import { 
  needsTargetingOnDeploy, 
  getAvailableTargets, 
  targetDeploy, 
  useTargetingState,
  getTargetingMode,
  getTargetingBorderStyle,
  getTargetingImageBorderStyle,
  getTargetingMessage,
  handleTargetingClick,
  isTarget,
  attachWeapon,
  weaponDeployTarget,
  targetDrone,
  targetImmobilize,
  targetConfuse,
  targetStrategem,
  targetSpecial,
  getZoneTargetingStyles,
  getExploreUnknownEpZoneTargetingStyles,
  hasActiveZoneTargetingHighlight,
  handleZoneClick,
  shouldHighlightZone,
  getZoneTargetingMessage,
  getStrategemTargetingPrompt,
  droneNeedsTargeting,
  droneCanTargetOpponents,
  droneNoTargeting,
  unitNeedsTargetingOnDeploy,
  strategemNeedsTargeting,
  weaponNeedsTargeting,
  specialUnitNeedsTargeting,
  getDeployTargetingRules,
  getStrategemTargetingRules,
  getValidTargets,
  canTargetUnit,
  TargetingCursor
} from './targeting';
import { getTargetingBorderStyles, getTargetingBorderClassName } from './targeting/TargetingBorder';

function isSurvivorHealthCountdown(trigger) {
  return resolveSurvivorCountdownFromTrigger(trigger) != null;
}

/** When tutorial overlay restricts strategem to specific units (data-tutorial-overlay-strategem-restrict), return true if this unit is in the allow list so we can show the yellow target border. */
function isTutorialOverlayStrategemTarget(unit, selectedStrategemId) {
  if (!unit || selectedStrategemId == null || typeof document === 'undefined' || !document.body) return false;
  try {
    const attr = document.body.getAttribute('data-tutorial-overlay-strategem-restrict');
    if (!attr) return false;
    const overlay = JSON.parse(attr);
    const sid = Number(selectedStrategemId) || selectedStrategemId;
    const allowed = overlay[String(sid)] ?? overlay[sid];
    if (!Array.isArray(allowed) || allowed.length === 0) return false;
    const orderId = unit.id ?? unit.orderId ?? unit.order_id;
    if (orderId == null) return false;
    return new Set(allowed.map(a => Number(a))).has(Number(orderId));
  } catch (_) { return false; }
}

// WebSocket URL - imported from apiConfig (uses environment variable, falls back to localhost for development)
const DEBUG = false;

// ✅ FIX: Wrapper component for attack animations
// NOTE: Not memoized because internal state (damageAnimationTrigger) needs to trigger re-renders
/**
 * Drag-to-deploy/attack line (origin → cursor). Tracks the pointer imperatively via refs so that
 * dragging an order does NOT re-render BattleUI on every mousemove (was 170+ renders/sec → flicker).
 * Origin is static for the duration of a drag; only the cursor end is updated, on the DOM directly.
 */
function DeployDragLine({ originX, originY, initialX, initialY }) {
  const glowRef = useRef(null);
  const mainRef = useRef(null);
  const targetRef = useRef(null);
  const posRef = useRef({
    x: Number.isFinite(initialX) ? initialX : originX,
    y: Number.isFinite(initialY) ? initialY : originY,
  });

  const apply = (x, y) => {
    posRef.current = { x, y };
    const glow = glowRef.current;
    const main = mainRef.current;
    const target = targetRef.current;
    if (glow) { glow.setAttribute('x2', x); glow.setAttribute('y2', y); }
    if (main) { main.setAttribute('x2', x); main.setAttribute('y2', y); }
    if (target) { target.setAttribute('cx', x); target.setAttribute('cy', y); }
  };

  // Re-apply the latest cursor position after every (incidental) re-render so React never
  // resets the line endpoints back to their initial values mid-drag.
  useLayoutEffect(() => {
    apply(posRef.current.x, posRef.current.y);
  });

  useEffect(() => {
    const onMove = (e) => apply(e.clientX, e.clientY);
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => window.removeEventListener('pointermove', onMove);
  }, []);

  const startX = posRef.current.x;
  const startY = posRef.current.y;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 1080;

  return (
    <svg
      className="deploy-drag-line"
      viewBox={`0 0 ${vw} ${vh}`}
      preserveAspectRatio="none"
      style={{ pointerEvents: 'none' }}
    >
      <defs>
        <filter id="deploy-drag-glow">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id="deploy-drag-glow-soft">
          <feGaussianBlur stdDeviation="6" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
          </feMerge>
        </filter>
        <filter id="deploy-drag-target-glow">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      {/* Soft glow layer (drawn first, behind main line) */}
      <line ref={glowRef} className="deploy-drag-line-glow" x1={originX} y1={originY} x2={startX} y2={startY} />
      <line ref={mainRef} x1={originX} y1={originY} x2={startX} y2={startY} />
      {/* Lit target at cursor */}
      <circle ref={targetRef} className="deploy-drag-line-target" cx={startX} cy={startY} r={10} />
    </svg>
  );
}

/**
 * Background for the Mission-6 darkness "flashlight" overlay. With finite coords it cuts a
 * soft light cone at the pointer; otherwise it is fully dark. Kept module-scope so the initial
 * render and the imperative (ref-based) pointer updates stay in sync.
 */
function buildMission6DarknessBackground(x, y) {
  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    return 'rgba(0, 0, 0, 0.99)';
  }
  return `radial-gradient(circle at ${x}px ${y}px, rgba(0, 0, 0, 0.00) 0px, rgba(0, 0, 0, 0.22) 75px, rgba(0, 0, 0, 0.82) 150px, rgba(0, 0, 0, 0.99) 280px)`;
}

const LETHAL_ATTACK_FADE_BASE_MS = 1200;

const MemoizedAttackAnimation = ({ 
  attackId, 
  attacker, 
  outcome,
  attackerPlayerId,
  targetPlayerId,
  attackAnimations,
  attackInProgressRef,
  setDamageAnimationTrigger, // ✅ FIX: Pass parent's state setter
  damageAnimationTriggeredRef, // ✅ BUG FIX: Pass ref to track triggered animations
  onUnitLethalDeathFromAttack,
  onAttackHitOutcomePreview,
  onQueueScatterAtHit,
  animationSpeedMultiplier = 1, // ✅ SPEED CONTROL: Animation speed multiplier
  mobileLayout = false,
  resolveSlotDamageAnchor = getSlotDamageAnchorFromDom,
  validateDamageAnchor = isValidDamageAnimationAnchor,
}) => {
  // Memoize slot objects to prevent recreation on every render
  const fromSlot = useMemo(() => ({
    zone: outcome.attackerZone,
    index: outcome.attackerIndex,
    playerId: attackerPlayerId
  }), [outcome.attackerZone, outcome.attackerIndex, attackerPlayerId]);
  
  const toSlot = useMemo(() => ({
    zone: outcome.targetZone,
    index: outcome.targetIndex,
    playerId: targetPlayerId
  }), [outcome.targetZone, outcome.targetIndex, targetPlayerId]);
  
  // Memoize callbacks to prevent recreation on every render
  const onComplete = useCallback(() => {
    attackAnimations.completeAnimation(attackId);
    attackInProgressRef.current = false;
  }, [attackId, attackAnimations, attackInProgressRef]);
  
  // ✅ FIX: onDiveComplete callback that stores damage animation in parent state
  // This ensures the animation persists even if this component unmounts
  const onDiveComplete = useCallback((targetPosition) => {
    // ✅ FIX: Trigger damage animation at exact target position
    // This should ALWAYS fire, even if attacker dies (animation completes before unit removal)
    // ✅ BUG FIX: Only trigger ONE damage animation at the TARGET position, never at attacker position
    // ✅ BUG FIX: Validate that this is actually the target position, not the attacker position
    // If targetZone/attackerZone are stored, verify we're using the target
    if (targetPosition?.targetZone && targetPosition?.attackerZone) {
      if (targetPosition.targetZone === targetPosition.attackerZone && 
          targetPosition.targetIndex === targetPosition.attackerIndex) {
        console.error(`❌ BUG: onDiveComplete received position where target === attacker! This is wrong!`, {
          attackId,
          targetPosition
        });
        return; // Don't trigger animation at wrong position
      }
    }
    
    // ✅ CRITICAL FIX: Validate targetPosition more strictly
    // Must have valid x and y coordinates (not 0, not NaN, not undefined)
    if (!targetPosition) {
      console.error(`❌ onDiveComplete: targetPosition is null/undefined for attack ${attackId}`);
      return;
    }
    
    const x = targetPosition.x;
    const y = targetPosition.y;
    const isGeneralAttack = outcome.isGeneralAttack || outcome.targetZone === 'general' || targetPosition?.targetZone === 'general';

    let anchorX = x;
    let anchorY = y;
    let slotElement = targetPosition.slotElement;
    let rect = targetPosition.rect;

    if (mobileLayout && isGeneralAttack) {
      const generalAnchor = getMobileGeneralDamageAnchorFromDom(targetPlayerId);
      if (generalAnchor && validateDamageAnchor(generalAnchor, { isGeneralAnimation: true })) {
        anchorX = generalAnchor.x;
        anchorY = generalAnchor.y;
        slotElement = generalAnchor.slotElement;
        rect = generalAnchor.rect;
      }
    } else if (mobileLayout && targetPosition.targetZone != null && targetPosition.targetIndex != null) {
      const cardAnchor = resolveSlotDamageAnchor(
        targetPosition.targetZone,
        targetPosition.targetIndex,
        outcome?.targetInstanceId
      );
      if (cardAnchor && validateDamageAnchor(cardAnchor, { isGeneralAnimation: isGeneralAttack })) {
        anchorX = cardAnchor.x;
        anchorY = cardAnchor.y;
        slotElement = cardAnchor.slotElement;
        rect = cardAnchor.rect;
      }
    } else if (!validateDamageAnchor({ x, y }, { isGeneralAnimation: isGeneralAttack })) {
      const zone = targetPosition.targetZone;
      const index = targetPosition.targetIndex;
      if (zone != null && index != null) {
        const fallbackAnchor = resolveSlotDamageAnchor(zone, index);
        if (fallbackAnchor && validateDamageAnchor(fallbackAnchor, { isGeneralAnimation: isGeneralAttack })) {
          anchorX = fallbackAnchor.x;
          anchorY = fallbackAnchor.y;
          slotElement = fallbackAnchor.slotElement;
          rect = fallbackAnchor.rect;
        }
      }
    }

    if (!validateDamageAnchor({ x: anchorX, y: anchorY }, { isGeneralAnimation: isGeneralAttack })) {
      console.error(`❌ onDiveComplete: Invalid targetPosition coordinates for attack ${attackId}:`, {
        x: anchorX,
        y: anchorY,
        targetPosition,
      });
      return;
    }
    
    const attackerCounter = buildAttackerCounterDamageTrigger({
      attackId,
      outcome,
      attackerZone: targetPosition.attackerZone,
      attackerIndex: targetPosition.attackerIndex,
      anchor: resolveSlotDamageAnchor(targetPosition.attackerZone, targetPosition.attackerIndex),
      isGeneralAttack,
    });

    // Store in parent's damageAnimationTriggers Map (passed via props)
    if (setDamageAnimationTrigger) {
      // ✅ BUG FIX: Check if we've already triggered a damage animation for this attack
      // This prevents duplicate animations when both units die
      if (damageAnimationTriggeredRef && damageAnimationTriggeredRef.current && damageAnimationTriggeredRef.current.has(attackId)) {
        console.error(`❌ BUG: onDiveComplete called multiple times for attack ${attackId}! Only ONE damage animation should be triggered per attack.`, {
          attackId,
          position: { x, y },
          targetZone: targetPosition?.targetZone,
          attackerZone: targetPosition?.attackerZone
        });
        return; // Don't trigger second animation
      }

      if (damageAnimationTriggeredRef && damageAnimationTriggeredRef.current) {
        damageAnimationTriggeredRef.current.add(attackId);
      }

      const actualUnitDamage = isGeneralAttack
        ? (outcome.damageToTarget || 0)
        : getActualUnitDamageFromOutcome(outcome);
      const penetrateOverflow = !isGeneralAttack
        ? calculatePenetrateOverflowFromOutcome(outcome)
        : 0;
      const targetShieldDeflected = resolveAttackTargetShieldDeflectedFromOutcome(outcome);

      setDamageAnimationTrigger(attackId, {
        attackId,
        x: anchorX,
        y: anchorY,
        slotElement,
        rect,
        timestamp: Date.now(),
        targetZone: targetPosition.targetZone,
        targetIndex: targetPosition.targetIndex,
        attackerZone: targetPosition.attackerZone,
        attackerIndex: targetPosition.attackerIndex,
        isGeneralAnimation: isGeneralAttack,
        damageAmount: targetShieldDeflected ? undefined : actualUnitDamage,
        shieldDeflected: targetShieldDeflected,
        sniperLethalKill: isSniperLethalKillOutcome(outcome, isGeneralAttack),
        targetInstanceId: !isGeneralAttack ? outcome.targetInstanceId : undefined,
        healthBefore: resolveTargetHealthBeforeFromOutcome(outcome, actualUnitDamage, isGeneralAttack),
        healthAfter: !isGeneralAttack && typeof outcome.targetHealthAfter === 'number'
          ? outcome.targetHealthAfter
          : undefined,
      });

      if (attackerCounter) {
        setDamageAnimationTrigger(attackerCounter.triggerKey, attackerCounter.payload);
      }

      if (penetrateOverflow > 0) {
        const generalZone = targetPlayerId === 'opponent' ? 'opponent_general' : 'player_general';
        const generalElement = document.querySelector(`[data-zone="${generalZone}"]`);
        if (!generalElement) {
          console.error(`❌ onDiveComplete: Penetrate overflow ${penetrateOverflow} but general element missing (${generalZone})`);
        } else {
          const generalRect = generalElement.getBoundingClientRect();
          const generalKey = `${attackId}-penetrate-general`;
          setDamageAnimationTrigger(generalKey, {
            attackId: generalKey,
            x: generalRect.left + generalRect.width / 2,
            y: generalRect.top + generalRect.height / 2,
            slotElement: generalElement,
            rect: generalRect,
            timestamp: Date.now(),
            isGeneralAnimation: true,
            damageAmount: penetrateOverflow,
          });
        }
      }
    } else {
      console.error(`❌ onDiveComplete: setDamageAnimationTrigger is not available for attack ${attackId}`);
    }

    if (onAttackHitOutcomePreview) {
      onAttackHitOutcomePreview(attackId, outcome);
    }

    if (onQueueScatterAtHit) {
      onQueueScatterAtHit(attackId, outcome);
    }

    if (
      outcome.targetDestroyed &&
      !isGeneralAttack &&
      onUnitLethalDeathFromAttack &&
      targetPosition.targetZone &&
      targetPosition.targetIndex != null &&
      BATTLEFIELD_SLOT_ZONES.has(targetPosition.targetZone)
    ) {
      onUnitLethalDeathFromAttack({
        zoneName: targetPosition.targetZone,
        index: targetPosition.targetIndex,
        unitSnapshot: outcome.target,
        damageAmount: getActualUnitDamageFromOutcome(outcome) || undefined,
        skipDamageQueue: true,
      });
    }

    if (
      outcome.attackerDestroyed
      && !isGeneralAttack
      && onUnitLethalDeathFromAttack
      && targetPosition.attackerZone
      && targetPosition.attackerIndex != null
      && BATTLEFIELD_SLOT_ZONES.has(targetPosition.attackerZone)
    ) {
      onUnitLethalDeathFromAttack({
        zoneName: targetPosition.attackerZone,
        index: targetPosition.attackerIndex,
        unitSnapshot: outcome.attacker,
        damageAmount: typeof outcome.damageToAttacker === 'number' ? outcome.damageToAttacker : undefined,
        skipDamageQueue: Boolean(attackerCounter),
      });
    }
  }, [attackId, setDamageAnimationTrigger, damageAnimationTriggeredRef, outcome, onUnitLethalDeathFromAttack, onAttackHitOutcomePreview, onQueueScatterAtHit, targetPlayerId, resolveSlotDamageAnchor, validateDamageAnchor, mobileLayout]);
  
  return (
    <>
      <AttackAnimation
        key={attackId}
        order={attacker}
        fromSlot={fromSlot}
        toSlot={toSlot}
        outcome={outcome}
        attackerSurvived={!outcome.attackerDestroyed}
        onComplete={onComplete}
        onDiveComplete={onDiveComplete}
        animationSpeedMultiplier={animationSpeedMultiplier}
        mobileLayout={mobileLayout}
      />
    </>
  );
};

// === Ability-aware helpers ===

// Token management functions moved to ./auth/tokenManager.js


// ✅ REFACTORED: validateGameStateInstanceIds moved to useGameStateSync hook

const EXPLORE_UNKNOWN_BATTLE_DARKNESS_FLASH_CELL_PX = 20;
/** 1-based battle layout grid: row center for vertical anchoring (20px cells on logical stage). */
const EXPLORE_UNKNOWN_BATTLE_DARKNESS_FLASH_CELL_ROW = 33;
const FIXED_ORDER_HOVER_CELL_PX = 20;
// Desktop expanded hand card anchor:
// move from center field to the old player-advisor area (right side, above player drones).
const FIXED_ORDER_HOVER_CELL_COL = 88;
const FIXED_ORDER_HOVER_CELL_ROW = 37;
const FIXED_ORDER_HOVER_VERTICAL_OFFSET_PX = 24;

function getExploreUnknownBattleDarknessFlashAnchorLogicalPx(baseWidth, baseHeight) {
  const cell = EXPLORE_UNKNOWN_BATTLE_DARKNESS_FLASH_CELL_PX;
  const x = baseWidth / 2;
  const yRaw = (EXPLORE_UNKNOWN_BATTLE_DARKNESS_FLASH_CELL_ROW - 0.5) * cell;
  const margin = 16;
  const yMax = Math.max(margin, baseHeight - margin);
  const xMax = Math.max(margin, baseWidth - margin);
  return {
    x: Math.min(Math.max(x, margin), xMax),
    y: Math.min(Math.max(yRaw, margin), yMax),
  };
}

function getFixedOrderHoverAnchorLogicalPx(baseWidth, baseHeight) {
  const xRaw = (FIXED_ORDER_HOVER_CELL_COL - 0.5) * FIXED_ORDER_HOVER_CELL_PX;
  const yRaw = (FIXED_ORDER_HOVER_CELL_ROW - 0.5) * FIXED_ORDER_HOVER_CELL_PX;
  const margin = 16;
  const xMax = Math.max(margin, baseWidth - margin);
  const yMax = Math.max(margin, baseHeight - margin);
  return {
    x: Math.min(Math.max(xRaw, margin), xMax),
    y: Math.min(Math.max(yRaw, margin), yMax),
  };
}

/** Must match `EXPANDED_CARD_UNIFORM_SCALE` in `OrderDesign.js` (portal uses translate(-50%,-50%) scale(scale * this)). */
const HAND_PREVIEW_UNIFORM_SCALE = 0.81;
const HAND_DEPLOY_SPAWN_PREVIEW_GAP_PX = 20;

/**
 * Second hand preview (e.g. spawned unit) sits to the left of the main expanded card, same vertical center.
 * Both use center-anchored fixed portals; `mainCenter` is the shared anchor (top/left = center in viewport px).
 */
function getHandDeployedSpawnPreviewCenter(mainCenter, viewportScale) {
  if (!mainCenter || !Number.isFinite(mainCenter.top) || !Number.isFinite(mainCenter.left)) {
    return null;
  }
  const u = HAND_PREVIEW_UNIFORM_SCALE;
  const wMain = 390 * viewportScale * u;
  const wSpawn = 390 * (viewportScale * 0.8) * u;
  return {
    top: mainCenter.top,
    left: mainCenter.left - (wMain / 2 + HAND_DEPLOY_SPAWN_PREVIEW_GAP_PX + wSpawn / 2),
  };
}

/** Brief map-style darkness meter when Memory lowers pending run darkness (Explore Unknown). */
function ExploreUnknownBattleDarknessFlash({ fromPct, toPct, regionLabel, tooltipTitle }) {
  return (
    <div
      className="battle-eu-darkness-flash-host"
      role="status"
      aria-live="polite"
      aria-label={regionLabel}
      tabIndex={0}
    >
      <ExploreUnknownDarknessMeter
        fromPercent={fromPct}
        toPercent={toPct}
        tooltipTitle={tooltipTitle}
        regionLabel={regionLabel}
      />
    </div>
  );
}

/** Brief Dark Knowledge fragment meter when `exploreUnknownDarkKnowledgePartsCollected` changes (Explore Unknown). */
function ExploreUnknownBattleDarkKnowledgeFlash({
  fromParts,
  toParts,
  targetParts,
  regionLabel,
  tooltipTitle,
  tooltipBody,
}) {
  const [fillParts, setFillParts] = useState(fromParts);
  useLayoutEffect(() => {
    const id = requestAnimationFrame(() => setFillParts(toParts));
    return () => cancelAnimationFrame(id);
  }, [fromParts, toParts]);
  const pct =
    (Math.min(targetParts, Math.max(0, fillParts)) / targetParts) * 100;
  return (
    <div
      className="battle-eu-dark-knowledge-flash-host"
      role="status"
      aria-live="polite"
      aria-label={regionLabel}
      tabIndex={0}
    >
      <div className="battle-eu-dark-knowledge-tooltip" role="tooltip" aria-hidden="true">
        <p className="battle-eu-dark-knowledge-tooltip-title">{tooltipTitle}</p>
        <p className="battle-eu-dark-knowledge-tooltip-body">{tooltipBody}</p>
      </div>
      <img src="/images/dark_knowledge.jpg" alt="" className="battle-eu-dark-knowledge-bar-icon" />
      <div className="battle-eu-dark-knowledge-track">
        <div className="battle-eu-dark-knowledge-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="battle-eu-dark-knowledge-count-slot" aria-hidden="true">
        <span className="battle-eu-dark-knowledge-count">
          {Math.round(toParts)}/{targetParts}
        </span>
      </span>
    </div>
  );
}

/** Shown on units that just lost Protect to Demoralizing Warcry (strategem 113) */
function DemoralizingBrokenShieldOverlay({ visible }) {
  if (!visible) return null;
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '76px',
        height: '76px',
        zIndex: 7,
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <img
        src="/images/broken_shield.png"
        alt=""
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.55))',
        }}
      />
    </div>
  );
}

function BattleUI({ user, userId, token, onLogout, initialGameState, initialGameId, autobattlerWs, onGameStateChange, blockedOrderIds = [] }) {

  const { t, i18n: i18nInstance } = useTranslation();
  
  // Viewport scaling for responsive design
  // Scales the game container to fit any screen size
  // Desktop: 1920x1080 base, Mobile: 844x390 base
  const { 
    scale: viewportScale, 
    offsetX: viewportOffsetX, 
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile
  } = useContainerScale();

  const resolveSlotDamageAnchor = useCallback((zoneName, index, instanceIdFallback = null) => {
    if (isContainerMobile) {
      return getMobileSlotDamageAnchorFromDom(zoneName, index, instanceIdFallback);
    }
    return getSlotDamageAnchorFromDom(zoneName, index, instanceIdFallback);
  }, [isContainerMobile]);

  const validateDamageAnchor = useCallback((anchor, options = {}) => {
    if (isContainerMobile) {
      return isValidMobileDamageAnimationAnchor(anchor, options);
    }
    return isValidDamageAnimationAnchor(anchor, options);
  }, [isContainerMobile]);

  const fixedDesktopExpandedPosition = useMemo(() => {
    if (isContainerMobile) return null;
    const anchor = getFixedOrderHoverAnchorLogicalPx(containerBaseWidth, containerBaseHeight);
    return {
      left: viewportOffsetX + (anchor.x * viewportScale),
      top: viewportOffsetY + (anchor.y * viewportScale) + FIXED_ORDER_HOVER_VERTICAL_OFFSET_PX,
    };
  }, [isContainerMobile, containerBaseWidth, containerBaseHeight, viewportOffsetX, viewportOffsetY, viewportScale]);

  const viewportWrapperRef = useRef(null);
  const [battleFlashlightColor, setBattleFlashlightColor] = useState('white');
  /** Map of server battle title string (en-us inject) → localized display name for current UI language. */
  const [battleTitleLookupMap, setBattleTitleLookupMap] = useState(null);

  useEffect(() => {
    if (!token) {
      setBattleTitleLookupMap(null);
      return;
    }
    let cancelled = false;
    const fromStorage =
      typeof localStorage !== 'undefined' ? localStorage.getItem('i18nextLng') : null;
    const apiLang = normalizeTitlesApiLang(
      fromStorage || i18nInstance.resolvedLanguage || i18nInstance.language || 'en-us'
    );
    fetch(`${API_URL}/profile/titles?lang=${encodeURIComponent(apiLang)}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Accept-Language': apiLang,
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`profile/titles failed: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        setBattleTitleLookupMap(buildBattleTitleLookupFromProfileTitles(data.titles || []));
      })
      .catch((err) => {
        console.error('BattleUI: profile/titles for battle title localization:', err);
        if (!cancelled) setBattleTitleLookupMap(null);
      });
    return () => {
      cancelled = true;
    };
  }, [token, i18nInstance.language, i18nInstance.resolvedLanguage]);

  const resolveBattleTitleDisplay = useCallback(
    (raw) => getLocalizedBattleTitle(raw, battleTitleLookupMap, t),
    [battleTitleLookupMap, t]
  );

  /** Explore Unknown Memory token: brief fade-out overlay when slot clears (no damage death VFX). */
  const [euMemoryFadeOverlays, setEuMemoryFadeOverlays] = useState({});
  /** Lethal attack: unit art washes to white before slot clears. */
  const [lethalAttackFadeOverlays, setLethalAttackFadeOverlays] = useState({});
  /** Keeps removed units visible on-card during lethal damage pop + white fade (deploy/shrink/strategem kills). */
  const lethalDeathGhostsRef = useRef(new Map());
  const [lethalDeathGhostTick, setLethalDeathGhostTick] = useState(0);
  const lethalDeathSequenceSlotsRef = useRef(new Set());
  const redeemMarkedByInstanceIdRef = useRef(new Map());
  const [redeemMarkedGhostTick, setRedeemMarkedGhostTick] = useState(0);
  // Units spawned by a redeemer (e.g. 138 → 149) that must stay hidden until that redeemer's death
  // sequence finishes. Keyed by the spawned unit's instanceId; checked in getVisibleUnit.
  const redeemSpawnHeldInstanceIdsRef = useRef(new Set());
  const suppressRedeemSlotDeathInstanceIdsRef = useRef(new Set());
  /** Redeem deaths that finished staged VFX — never replay slot-keyed lethal death for these instanceIds. */
  const completedRedeemDeathInstanceIdsRef = useRef(new Set());
  /** Lethal slot deaths that already played VFX — suppress stale deploy/state-diff replays. */
  const completedLethalDeathInstanceIdsRef = useRef(new Set());
  const playRedeemGrowPulseRef = useRef(null);
  const playRedeemGeneralHealRef = useRef(null);
  const clearSlotPreviousStateRef = useRef(null);
  const [redeemFinishingByInstanceId, setRedeemFinishingByInstanceId] = useState({});
  const onExploreUnknownMemoryFade = useCallback(({ zoneName, index, image }) => {
    const key = `${zoneName}_${index}`;
    if (!image || typeof image !== 'string') {
      throw new Error('Explore Unknown memory fade requires image path');
    }
    setEuMemoryFadeOverlays((prev) => ({ ...prev, [key]: { image, opacity: 1 } }));
    requestAnimationFrame(() => {
      setEuMemoryFadeOverlays((prev) => {
        if (!prev[key]) return prev;
        return { ...prev, [key]: { ...prev[key], opacity: 0 } };
      });
    });
    window.setTimeout(() => {
      setEuMemoryFadeOverlays((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }, 520);
  }, []);

  const [euDarknessMeterFlash, setEuDarknessMeterFlash] = useState(null);
  const euDarknessFlashGameIdRef = useRef(null);
  const euMemoryReductionPrevForFlashRef = useRef(0);
  const euRunDarknessPrevForFlashRef = useRef(null);
  const [euDarkKnowledgeMeterFlash, setEuDarkKnowledgeMeterFlash] = useState(null);
  const euDarkKnowledgeFlashGameIdRef = useRef(null);
  const euDarkKnowledgePartsPrevFlashRef = useRef(null);
  const [euHeartMeterFlash, setEuHeartMeterFlash] = useState(null);
  const euHeartFlashGameIdRef = useRef(null);
  const euHeartPartsPrevFlashRef = useRef({});
  const [euDarkKnowledgeRewardModal, setEuDarkKnowledgeRewardModal] = useState(null);
  const euDismissedDarkKnowledgeRewardPopupsRef = useRef(new Set());
  const [euHeartSetCompleteModal, setEuHeartSetCompleteModal] = useState(null);
  const euDismissedHeartSetCompletePopupsRef = useRef(new Set());
  const [euEpMeterFlash, setEuEpMeterFlash] = useState(null);
  const euEpFlashGameIdRef = useRef(null);
  const euEpPartsPrevFlashRef = useRef({});
  const [euEpSetCompleteModal, setEuEpSetCompleteModal] = useState(null);
  const euDismissedEpSetCompletePopupsRef = useRef(new Set());
  const [euRewriteMeterFlash, setEuRewriteMeterFlash] = useState(null);
  const euRewriteFlashGameIdRef = useRef(null);
  const euRewritePartsPrevFlashRef = useRef(null);
  const [euRewriteSetCompleteModal, setEuRewriteSetCompleteModal] = useState(null);
  const euDismissedRewriteSetCompletePopupsRef = useRef(new Set());
  const [euEpZoneTargetMode, setEuEpZoneTargetMode] = useState(false);
  const [euEpTargetMode, setEuEpTargetMode] = useState(false);
  const [euEpUnifiedSpendPickerOpen, setEuEpUnifiedSpendPickerOpen] = useState(false);

  // Spectator mode support
  const { 
    isSpectating, 
    spectatingGameState, 
    spectatingPlayerId, 
    pendingAttackInitiated, // ✅ SPECTATOR FIX: Get pending attack-initiated messages
    setPendingAttackInitiated, // ✅ SPECTATOR FIX: Clear after processing
    pendingEmote, // ✅ SPECTATOR FIX: Get pending emote messages
    setPendingEmote, // ✅ SPECTATOR FIX: Clear after processing
    stopSpectating,
    friends,
    spectators, // Users watching you
    handleSpectatorJoined, // ✅ FIX: Function to handle spectator-joined from game WebSocket
    handleSpectatorLeft // ✅ FIX: Function to handle spectator-left from game WebSocket
  } = useFriends();
  
  // Get friend's name for spectator banner
  const spectatingFriend = isSpectating ? friends.find(f => f.id === spectatingPlayerId) : null;
  const spectatingFriendName = spectatingFriend?.username || 'Friend';
  
  // Helper function to translate backend error messages
  const translateBackendError = (message) => {
    if (!message) return message;
    
    // Map common backend error messages to translation keys
    if (message.includes("Not your turn") || message.includes("not your turn")) {
      return t('battleUI.notYourTurn');
    }
    if (message.includes("Not enough intelligence")) {
      // Extract numbers if present (e.g., "Need 5, have 3")
      const needMatch = message.match(/Need (\d+)/);
      const haveMatch = message.match(/have (\d+)/);
      if (needMatch && haveMatch) {
        return `${t('battleUI.notEnoughIntelligence')}! ${t('battleUI.need')} ${needMatch[1]}, ${t('battleUI.have')} ${haveMatch[1]}`;
      }
      return t('battleUI.notEnoughIntelligence');
    }
    
    // Return original message if no translation found
    return message;
  };
  
  const location = useLocation();

  // Background selection:
  // - Mission 5 uses bg_mission5.jpg exclusively
  // - Mission 6 uses scorpion_barn.jpg exclusively
  // - Tutorial missions 1–4 use background_tutorial_1.jpg
  // - Other games use a background derived from the opponent's base factions:
  //   League of Shadows → alps.jpg
  //   Asrah → ganges.jpg
  //   Ra's Children → pyramid.jpg
  //   Exolot Dominion → lavender.jpg
  //   Remnants of Humanity → citycenter.jpg / refinery.jpg / snow.jpg
  //   Combined factions (e.g. The Conspiracy) pick randomly from all involved base factions.
  const [backgroundImage, setBackgroundImage] = useState(() => {
    let initialBgState = initialGameState || location.state?.gameState || null;
    let backgroundPovUserId = userId;

    const tutorialMissionId = initialBgState?.missionId;
    if (tutorialMissionId === 'chapter_1_mission_5') {
      return 'bg_mission5.jpg';
    }
    if (tutorialMissionId === 'chapter_1_mission_6') {
      return 'scorpion_barn.jpg';
    }
    if (EARLY_TUTORIAL_MISSION_IDS.includes(tutorialMissionId)) {
      return 'background_tutorial_1.jpg';
    }

    try {
      if (!initialBgState && typeof sessionStorage !== 'undefined') {
        const raw = sessionStorage.getItem('spectating');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed?.isSpectating && parsed?.gameState) {
            initialBgState = parsed.gameState;
            if (parsed.watchingPlayerId != null) {
              backgroundPovUserId = parsed.watchingPlayerId;
            }
          }
        }
      }
    } catch (e) {
      console.error('BattleUI: failed to read spectating session for initial background', e);
    }

    const euBg = tryGetExploreUnknownBattleBackgroundPath(initialBgState?.exploreUnknownMissionIndex);
    if (euBg != null) {
      return euBg;
    }

    // If we don't have an initial game state yet (e.g. matchmaking/spectator paths),
    // use a neutral default; it will be updated once gameState/strategies are available.
    if (!initialBgState || !initialBgState.players || !initialBgState.state) {
      return 'snow.jpg';
    }

    try {
      const opponentId = initialBgState.players.find((id) => String(id) !== String(backgroundPovUserId));
      const opponentState = opponentId ? initialBgState.state[opponentId] : null;
      const opponentStrategy = opponentState?.strategy;
      const baseFactions = getBaseFactionsFromStrategy(opponentStrategy);
      return getBackgroundForBaseFactions(baseFactions);
    } catch (error) {
      console.error('BattleUI background initialization failed:', error);
      // Keep neutral default; error is logged for debugging.
      return 'snow.jpg';
    }
  });
  const isVideoBackground = typeof backgroundImage === 'string' && backgroundImage.toLowerCase().endsWith('.mp4');

  // Memoized so the full-screen background element keeps a stable React identity and is NOT
  // reconciled on every game-state re-render. Combined with its own GPU layer (CSS), this stops
  // the background from flashing when the board updates.
  const battleBackgroundElement = useMemo(() => {
    if (typeof backgroundImage !== 'string' || backgroundImage.length === 0) {
      return null;
    }
    const resolvedUrl = resolveBattleBackgroundCssUrl(backgroundImage);
    if (isVideoBackground) {
      return (
        <video
          className="fullscreen-background fullscreen-background-video"
          autoPlay
          muted
          loop
          playsInline
          aria-hidden
        >
          <source src={resolvedUrl} type="video/mp4" />
        </video>
      );
    }
    return (
      <div
        className="fullscreen-background"
        style={{ backgroundImage: `url(${resolvedUrl})` }}
      />
    );
  }, [backgroundImage, isVideoBackground]);

  // Mobile landscape detection
  const [isMobile, setIsMobile] = useState(() => {
    return window.matchMedia('(max-height: 768px)').matches || window.innerHeight <= 768;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-height: 768px)');
    const handleChange = (e) => {
      setIsMobile(e.matches || window.innerHeight <= 768);
    };
    mediaQuery.addEventListener('change', handleChange);
    // Also check on resize
    const handleResize = () => {
      setIsMobile(window.innerHeight <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const navigate = useNavigate();
  const { strategyId } = location.state ?? {};

  const { gameId: urlGameId } = useParams();
  // Use initialGameId prop if provided (for campaign), otherwise use URL param
  const gameId = initialGameId || urlGameId;

  const isFriendSpectating = isFriendSpectatingSession(isSpectating, gameId);

  // ✅ DEV AUTOBATTLER: Support autobattlerWs prop (for dev autobattler and regular autobattler)
  const passedWs = location.state?.ws || autobattlerWs;
  const [ws, setWs] = useState(passedWs ?? null);

  // ✅ FIX: Initialize gameState from props, navigation state, or null (avoids unnecessary reconnect).
  // Declared before any useEffect that depends on gameState to avoid "Cannot access before initialization".
  const [gameState, setGameState] = useState(initialGameState || (location.state?.gameState ?? null));
  // Mission-6 darkness "flashlight" overlay is positioned imperatively via this ref so that
  // moving the held beam does NOT re-render all of BattleUI on every pointer sample (flicker fix).
  const mission6DarknessOverlayRef = useRef(null);
  const [mission6LightsRestored, setMission6LightsRestored] = useState(false);
  const isMission6DarknessMode =
    location.state?.missionId === 'chapter_1_mission_6' ||
    gameState?.missionId === 'chapter_1_mission_6';

  // Set background once per game and never switch for the entire session.
  const backgroundSetForGameRef = useRef(false);
  useEffect(() => {
    if (backgroundSetForGameRef.current) return;

    const stateForBg = isFriendSpectating ? spectatingGameState : gameState;
    const userIdForBg = isFriendSpectating ? spectatingPlayerId : userId;

    const tutorialMissionId = stateForBg?.missionId;
    if (tutorialMissionId === 'chapter_1_mission_5') {
      setBackgroundImage('bg_mission5.jpg');
      backgroundSetForGameRef.current = true;
      return;
    }
    if (tutorialMissionId === 'chapter_1_mission_6') {
      setBackgroundImage('scorpion_barn.jpg');
      backgroundSetForGameRef.current = true;
      return;
    }
    if (EARLY_TUTORIAL_MISSION_IDS.includes(tutorialMissionId)) {
      setBackgroundImage('background_tutorial_1.jpg');
      backgroundSetForGameRef.current = true;
      return;
    }

    const euBg = tryGetExploreUnknownBattleBackgroundPath(stateForBg?.exploreUnknownMissionIndex);
    if (euBg != null) {
      setBackgroundImage(euBg);
      backgroundSetForGameRef.current = true;
      return;
    }

    if (!stateForBg || !stateForBg.players || !stateForBg.state || !userIdForBg) {
      return;
    }

    try {
      const opponentId = stateForBg.players.find((id) => String(id) !== String(userIdForBg));
      const opponentState = opponentId ? stateForBg.state[opponentId] : null;
      const opponentStrategy = opponentState?.strategy;
      const baseFactions = getBaseFactionsFromStrategy(opponentStrategy);
      const background = getBackgroundForBaseFactions(baseFactions);
      setBackgroundImage(background);
      backgroundSetForGameRef.current = true;
    } catch (error) {
      console.error('BattleUI background update failed:', error);
    }
  }, [gameState, userId, isFriendSpectating, spectatingGameState, spectatingPlayerId]);

  useEffect(() => {
    if (!isMission6DarknessMode) {
      setMission6LightsRestored(false);
    }
  }, [isMission6DarknessMode, gameId]);

  const isBotGame = location.state?.isBotGame;

  // Capture initialGameState at mount time so the autobattler check in the game-fetch
  // effect doesn't re-trigger when the parent passes a new reference.
  const initialGameStateRef = useRef(initialGameState);
  
  // CAMPAIGN: Propagate game state to parent so "Out of reach" (queued by backend when bot deploys) can show
  useEffect(() => {
    if (gameState && onGameStateChange) onGameStateChange(gameState);
  }, [gameState, onGameStateChange]);
  
  // When spectating, use the spectating game state from context
  // Use useMemo to ensure we get a new reference when spectatingGameState changes
  const effectiveGameState = useMemo(() => {
    return isFriendSpectating ? spectatingGameState : gameState;
  }, [isFriendSpectating, spectatingGameState, gameState]);
  const effectiveUserId = isFriendSpectating ? spectatingPlayerId : userId;
  /**
   * Board POV for remapping deploy VFX. Sync to activeUserId after that memo is defined
   * (autobattler uses playerBotId as the in-state player key; human userId would mismatch pendingDeployAnimations).
   */
  const deployAnimationViewerUserIdRef = useRef(null);
  const attackHealthFxViewerUserIdRef = useRef(userId);
  
  // Autobattler mode support (must be after effectiveGameState is defined)
  const { isAutobattler, isSpectator: isAutobattlerSpectator } = useAutobattlerMode(effectiveGameState, userId);
  
  // ✅ SPEED CONTROL: Initialize speed control for autobattler spectators
  // Pass ws and gameId for backend pause/resume communication
  const speedControl = useSpectateSpeedControl(
    isAutobattlerSpectator, // Only enable for autobattler spectators
    100, // Initial speed: 100% (real-time)
    ws,  // WebSocket for pause/resume messages
    gameId // Game ID for pause/resume messages
  );
  
  // ✅ FIX: Store speedControl.queueUpdate and isAutobattlerSpectator in refs to avoid stale closures
  const speedControlQueueUpdateRef = useRef(speedControl.queueUpdate);
  const animationSpeedMultiplierRef = useRef(speedControl.animationSpeedMultiplier);
  const isAutobattlerSpectatorRef = useRef(isAutobattlerSpectator);
  useEffect(() => {
    if (speedControl.queueUpdate) {
      speedControlQueueUpdateRef.current = speedControl.queueUpdate;
    }
    animationSpeedMultiplierRef.current = speedControl.animationSpeedMultiplier;
    isAutobattlerSpectatorRef.current = isAutobattlerSpectator;
  }, [speedControl.queueUpdate, isAutobattlerSpectator]);
  
  // ✅ FIX: Initialize gameStateRef and store opponent strategy when gameState comes from navigation (only once on mount)
  const initializedFromNavigationRef = useRef(false);
  useEffect(() => {
    if (gameState && location.state?.gameState && !initializedFromNavigationRef.current) {
      // GameState came from navigation state - initialize refs and store opponent strategy (only once)
      gameStateRefRef.current.current = gameState;
      storeOpponentStrategyId(gameState);
      // Validate state
      if (!validateStateRef.current(gameState)) {
        console.error('🚨 STATE VALIDATION FAILED in navigation state: InstanceIds missing!');
      }
      initializedFromNavigationRef.current = true;
    }
  }, []); // Only run once on mount
  
  // ✅ CAMPAIGN TUTORIAL: Dispatch when Opportunist (302) deployed to flank (for tutorial auto-advance)
  useEffect(() => {
    if (!gameState?.campaignMode || isSpectating || !userId) return;
    const flankUnits = gameState.state?.[userId]?.playedOrders?.flank || [];
    if (flankUnits.some(u => u && u.id === 302)) {
      const backendQueue = gameState.scriptState?.tutorialQueue || [];
      const nextTutorial = backendQueue.find(t => (t.title || t.id) === "Special Units");
      window.dispatchEvent(new CustomEvent('opportunist-deployed-to-flank', {
        detail: { gameState, nextTutorial }
      }));
    }
  }, [gameState, userId, isSpectating]);
  
  // Initialize attack animations hook
  // ✅ SPECTATOR FIX: Pass isSpectating flag so friend-spectators don't send animation-complete messages
  // ✅ DISPATCH ASSET: Autobattler spectators DO send animation-complete (they are the dispatch owner / proxy player).
  // Without this, backend has to wait its 5s timeout before applying bot-vs-bot damage, which makes
  // damage feel deducted with a huge delay compared to PvE. Sending animation-complete from the
  // dispatch-owner spectator makes timing match PvE (~1.5s after attack-initiated).
  // ✅ SPEED CONTROL: Pass animation speed multiplier for slower/faster animations
  const attackAnimations = useAttackAnimations(ws, gameId, isFriendSpectating, speedControl.animationSpeedMultiplier);
  const battlefieldHealthCountdown = useBattlefieldHealthCountdown(speedControl.animationSpeedMultiplier);
  const countdownHealthLookupRef = useRef(null);

  const triggerLethalAttackFade = useCallback(({ zoneName, index }) => {
    if (!zoneName || index == null) {
      throw new Error('Lethal attack fade requires zoneName and index');
    }
    const key = `${zoneName}_${index}`;
    const speedFactor = speedControl.animationSpeedMultiplier > 0 ? speedControl.animationSpeedMultiplier : 1;
    setLethalAttackFadeOverlays((prev) => ({
      ...prev,
      [key]: {
        durationMs: Math.round(LETHAL_ATTACK_FADE_BASE_MS / speedFactor),
      },
    }));
  }, [speedControl.animationSpeedMultiplier]);

  const isSlotLethalFading = useCallback(
    (zoneName, idx, instanceId = null) => {
      void redeemMarkedGhostTick;
      if (instanceId && suppressRedeemSlotDeathInstanceIdsRef.current.has(instanceId)) {
        return false;
      }
      if (instanceId && completedRedeemDeathInstanceIdsRef.current.has(instanceId)) {
        return false;
      }
      if (instanceId && redeemMarkedByInstanceIdRef.current.has(instanceId)) {
        return false;
      }
      const slotKey = `${zoneName}_${idx}`;
      if (!lethalAttackFadeOverlays[slotKey]) {
        return false;
      }
      if (instanceId) {
        const ghost = lethalDeathGhostsRef.current.get(slotKey);
        if (ghost?.instanceId && ghost.instanceId !== instanceId) {
          return false;
        }
      }
      return true;
    },
    [lethalAttackFadeOverlays, redeemMarkedGhostTick]
  );

  const completeLethalAttackFade = useCallback((zoneName, idx) => {
    const key = `${zoneName}_${idx}`;
    if (lethalDeathGhostsRef.current.has(key)) {
      lethalDeathGhostsRef.current.delete(key);
      setLethalDeathGhostTick((tick) => tick + 1);
    }
    setLethalAttackFadeOverlays((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const getLethalFadeDurationMs = useCallback(
    (zoneName, idx) =>
      lethalAttackFadeOverlays[`${zoneName}_${idx}`]?.durationMs ?? LETHAL_ATTACK_FADE_BASE_MS,
    [lethalAttackFadeOverlays]
  );
  
  // ✅ CRITICAL FIX: Store attackAnimations in ref for passedWs handler
  // This ensures the handler always has access to the latest attackAnimations (avoids stale closure)
  const attackAnimationsRef = useRef(attackAnimations);
  attackAnimationsRef.current = attackAnimations;
  
  // ✅ FIX: Store damage animation triggers OUTSIDE MemoizedAttackAnimation
  // This ensures they persist even after the attack component unmounts
  const [damageAnimationTriggers, setDamageAnimationTriggers] = useState(new Map());
  const damageAnimationTriggersRef = useRef(new Map());
  useEffect(() => {
    damageAnimationTriggersRef.current = damageAnimationTriggers;
  }, [damageAnimationTriggers]);
  const [explosionEffects, setExplosionEffects] = useState(new Map());
  const [battlebotPortalEffects, setBattlebotPortalEffects] = useState(new Map());
  const battlebotPortalGameIdRef = useRef(null);
  const battlebotPortalInitializedRef = useRef(false);
  const battlebotPortalSeenInstanceIdsRef = useRef(new Set());
  const [fleshMimicPortalEffects, setFleshMimicPortalEffects] = useState(new Map());
  const fleshMimicPortalGameIdRef = useRef(null);
  const fleshMimicPortalInitializedRef = useRef(false);
  const fleshMimicPortalSeenInstanceIdsRef = useRef(new Set());
  const [plagueBloomPortalEffects, setPlagueBloomPortalEffects] = useState(new Map());
  const plagueBloomPortalGameIdRef = useRef(null);
  const plagueBloomPortalInitializedRef = useRef(false);
  const plagueBloomPortalSeenInstanceIdsRef = useRef(new Set());
  
  // ✅ BUG FIX: Track which attackIds have already triggered damage animations
  // This prevents duplicate damage animations for the same attack
  const damageAnimationTriggeredRef = useRef(new Set());
  /** Prevents duplicate HP countdown when damage-animation complete fires more than once per trigger. */
  const completedDamageAnimationTriggerKeysRef = useRef(new Set());
  /** Prevents replaying the same survivor step-down after turn boundaries / stale triggers. */
  const attackSurvivorCountdownPlayedRef = useRef(new Set());
  /** Slots that already have a queued unit damage FX (attack/strategem) — suppress slot death duplicate. */
  const pendingUnitDamageFxSlotsRef = useRef(new Set());
  /** Primary-target scatter chip queued after main attack countdown completes. */
  const pendingPrimaryScatterDamageRef = useRef(new Map());
  /** Scatter bystander holds keyed by attackId until hit FX runs. */
  const pendingAttackScatterHoldsRef = useRef(new Map());
  /**
   * Deploy/strategem pre-hit HP holds (sync Set) — survives turn-played cleanup before
   * useDeployTargetAnimations flushes activeAnimations to React state.
   */
  const pendingProjectileHealthHoldRef = useRef(new Set());
  const queueBroadcastStrategemDamageTriggersRef = useRef(null);

  const queueDamageAnimationTrigger = useCallback((triggerKey, trigger) => {
    const now = Date.now();
    const timestamp = trigger?.timestamp ?? now;
    const prev = damageAnimationTriggersRef.current;

    if (trigger?.targetZone != null && trigger?.targetIndex != null) {
      for (const [, existing] of prev) {
        if (
          existing.targetZone === trigger.targetZone
          && existing.targetIndex === trigger.targetIndex
          && now - (existing.timestamp || 0) < 1000
        ) {
          return;
        }
      }
    } else if (trigger?.isGeneralAnimation) {
      for (const [, existing] of prev) {
        if (
          existing.isGeneralAnimation
          && now - (existing.timestamp || 0) < 1000
          && Math.abs((existing.x || 0) - (trigger.x || 0)) < 24
          && Math.abs((existing.y || 0) - (trigger.y || 0)) < 24
        ) {
          return;
        }
      }
    }

    const survivorCountdown = resolveSurvivorCountdownFromTrigger(trigger);
    if (survivorCountdown) {
      battlefieldHealthCountdown.setHealthHold(
        survivorCountdown.instanceId,
        survivorCountdown.healthBefore,
        survivorCountdown.toHealth,
        {
          zoneName: survivorCountdown.zoneName,
          slotIndex: survivorCountdown.slotIndex,
        }
      );
    }

    if (trigger?.targetZone != null && trigger?.targetIndex != null) {
      const slotKey = `${trigger.targetZone}_${trigger.targetIndex}`;
      pendingUnitDamageFxSlotsRef.current.add(slotKey);
      window.setTimeout(() => {
        pendingUnitDamageFxSlotsRef.current.delete(slotKey);
      }, 4000);
    }

    setDamageAnimationTriggers((prevTriggers) => {
      const next = new Map(prevTriggers);
      next.set(triggerKey, { ...trigger, timestamp });
      damageAnimationTriggersRef.current = next;
      return next;
    });
  }, [battlefieldHealthCountdown.setHealthHold]);

  const cancelUnitLethalDeathFx = useCallback((instanceId, zoneName, index) => {
    if (zoneName != null && index != null) {
      const slotKey = `${zoneName}_${index}`;
      lethalDeathSequenceSlotsRef.current.delete(slotKey);
      lethalDeathGhostsRef.current.delete(slotKey);
      pendingUnitDamageFxSlotsRef.current.delete(slotKey);
      setLethalAttackFadeOverlays((prev) => {
        if (!prev[slotKey]) {
          return prev;
        }
        const next = { ...prev };
        delete next[slotKey];
        return next;
      });
      setDamageAnimationTriggers((prevTriggers) => {
        let changed = false;
        const next = new Map(prevTriggers);
        for (const [key, trigger] of next) {
          if (trigger.targetZone !== zoneName || trigger.targetIndex !== index) {
            continue;
          }
          if (instanceId && trigger.targetInstanceId && trigger.targetInstanceId !== instanceId) {
            continue;
          }
          next.delete(key);
          changed = true;
        }
        if (!changed) {
          return prevTriggers;
        }
        damageAnimationTriggersRef.current = next;
        return next;
      });
    }
    if (instanceId) {
      releasePendingProjectileHealthHold(pendingProjectileHealthHoldRef.current, instanceId);
      battlefieldHealthCountdown.clearCountdown(instanceId);
      setLethalDeathGhostTick((tick) => tick + 1);
    }
  }, [battlefieldHealthCountdown.clearCountdown]);

  const handleDamageAnimationComplete = useCallback((attackId, trigger) => {
    if (attackId != null && completedDamageAnimationTriggerKeysRef.current.has(attackId)) {
      return;
    }

    const triggerAgeMs = typeof trigger?.timestamp === 'number'
      ? Date.now() - trigger.timestamp
      : 0;
    if (triggerAgeMs > MAX_STALE_DAMAGE_TRIGGER_COMPLETION_MS) {
      if (attackId != null) {
        completedDamageAnimationTriggerKeysRef.current.add(attackId);
      }
      if (trigger?.targetInstanceId) {
        releasePendingProjectileHealthHold(
          pendingProjectileHealthHoldRef.current,
          trigger.targetInstanceId
        );
        battlefieldHealthCountdown.clearCountdown(trigger.targetInstanceId);
      }
      setDamageAnimationTriggers((prev) => {
        if (!prev.has(attackId)) {
          return prev;
        }
        const next = new Map(prev);
        next.delete(attackId);
        damageAnimationTriggersRef.current = next;
        return next;
      });
      damageAnimationTriggeredRef.current.delete(attackId);
      return;
    }

    if (attackId != null) {
      completedDamageAnimationTriggerKeysRef.current.add(attackId);
    }

    const survivorCountdown = resolveSurvivorCountdownFromTrigger(
      trigger,
      countdownHealthLookupRef.current
    );
    if (survivorCountdown) {
      const sessionKey = buildSurvivorCountdownSessionKey(
        survivorCountdown.instanceId,
        survivorCountdown.healthBefore,
        survivorCountdown.toHealth
      );
      if (sessionKey && attackSurvivorCountdownPlayedRef.current.has(sessionKey)) {
        releasePendingProjectileHealthHold(
          pendingProjectileHealthHoldRef.current,
          survivorCountdown.instanceId
        );
        battlefieldHealthCountdown.clearCountdown(survivorCountdown.instanceId);
      } else {
        if (sessionKey) {
          attackSurvivorCountdownPlayedRef.current.add(sessionKey);
        }
        releasePendingProjectileHealthHold(
          pendingProjectileHealthHoldRef.current,
          survivorCountdown.instanceId
        );
        battlefieldHealthCountdown.startCountdown(
          survivorCountdown.instanceId,
          survivorCountdown.healthBefore,
          survivorCountdown.toHealth,
          {
            zoneName: survivorCountdown.zoneName,
            slotIndex: survivorCountdown.slotIndex,
          }
        );
      }
    } else if (trigger?.targetInstanceId) {
      releasePendingProjectileHealthHold(
        pendingProjectileHealthHoldRef.current,
        trigger.targetInstanceId
      );
      battlefieldHealthCountdown.clearCountdown(trigger.targetInstanceId);
    }

    const scatterFollowUp = pendingPrimaryScatterDamageRef.current.get(attackId);
    if (scatterFollowUp) {
      pendingPrimaryScatterDamageRef.current.delete(attackId);
      battlefieldHealthCountdown.setHealthHold(
        scatterFollowUp.targetInstanceId,
        scatterFollowUp.healthBefore,
        scatterFollowUp.healthAfter,
        {
          zoneName: scatterFollowUp.targetZone,
          slotIndex: scatterFollowUp.targetIndex,
        }
      );
      if (typeof queueBroadcastStrategemDamageTriggersRef.current !== 'function') {
        throw new Error('handleDamageAnimationComplete: queueBroadcastStrategemDamageTriggers is not ready');
      }
      queueBroadcastStrategemDamageTriggersRef.current([scatterFollowUp]);
    }

    setDamageAnimationTriggers((prev) => {
      const next = new Map(prev);
      next.delete(attackId);
      damageAnimationTriggersRef.current = next;
      return next;
    });
    damageAnimationTriggeredRef.current.delete(attackId);
  }, [battlefieldHealthCountdown.startCountdown, battlefieldHealthCountdown.clearCountdown]);

  const holdDeployZoneHealthBeforeDamage = useCallback((targets) => {
    if (!Array.isArray(targets)) {
      return;
    }
    targets.forEach((target) => {
      if (
        !target?.targetInstanceId
        || typeof target.healthBefore !== 'number'
        || typeof target.healthAfter !== 'number'
        || target.healthBefore <= target.healthAfter
      ) {
        return;
      }
      trackPendingProjectileHealthHolds(pendingProjectileHealthHoldRef.current, [target]);
      battlefieldHealthCountdown.setHealthHold(
        target.targetInstanceId,
        target.healthBefore,
        target.healthAfter,
        {
          zoneName: target.targetZone,
          slotIndex: target.targetIndex,
        }
      );
    });
  }, [battlefieldHealthCountdown.setHealthHold]);

  const holdDeployZoneHealthBeforeDamageRef = useRef(holdDeployZoneHealthBeforeDamage);
  holdDeployZoneHealthBeforeDamageRef.current = holdDeployZoneHealthBeforeDamage;

  const startDeployZoneHealthCountdowns = useCallback((targets) => {
    if (!Array.isArray(targets)) {
      return;
    }
    targets.forEach((target) => {
      if (
        !target?.targetInstanceId
        || typeof target.healthBefore !== 'number'
        || typeof target.healthAfter !== 'number'
      ) {
        return;
      }
      if (target.healthAfter <= 0 || target.healthBefore <= target.healthAfter) {
        if (target.healthAfter <= 0) {
          releasePendingProjectileHealthHold(
            pendingProjectileHealthHoldRef.current,
            target.targetInstanceId
          );
          battlefieldHealthCountdown.clearCountdown(target.targetInstanceId);
        }
        return;
      }
      releasePendingProjectileHealthHold(
        pendingProjectileHealthHoldRef.current,
        target.targetInstanceId
      );
      battlefieldHealthCountdown.startCountdown(
        target.targetInstanceId,
        target.healthBefore,
        target.healthAfter,
        {
          zoneName: target.targetZone,
          slotIndex: target.targetIndex,
        }
      );
    });
  }, [battlefieldHealthCountdown.startCountdown, battlefieldHealthCountdown.clearCountdown]);

  const triggerUnitLethalDeathSequence = useCallback(({
    zoneName,
    index,
    unitSnapshot = null,
    damageAmount = null,
    skipDamageQueue = false,
  }) => {
    if (!zoneName || index == null || !BATTLEFIELD_SLOT_ZONES.has(zoneName)) {
      return;
    }
    if (unitSnapshot?.instanceId) {
      if (suppressRedeemSlotDeathInstanceIdsRef.current.has(unitSnapshot.instanceId)) {
        return;
      }
      if (completedRedeemDeathInstanceIdsRef.current.has(unitSnapshot.instanceId)) {
        return;
      }
      if (completedLethalDeathInstanceIdsRef.current.has(unitSnapshot.instanceId)) {
        return;
      }
    }
    const slotKey = `${zoneName}_${index}`;
    if (lethalDeathSequenceSlotsRef.current.has(slotKey)) {
      return;
    }
    lethalDeathSequenceSlotsRef.current.add(slotKey);
    if (unitSnapshot?.instanceId) {
      completedLethalDeathInstanceIdsRef.current.add(unitSnapshot.instanceId);
    }
    window.setTimeout(() => {
      lethalDeathSequenceSlotsRef.current.delete(slotKey);
    }, 4000);

    if (unitSnapshot && unitSnapshot.instanceId) {
      const postDamageHealth = postDamageHealthFromSnapshot(unitSnapshot, damageAmount);
      lethalDeathGhostsRef.current.set(slotKey, {
        ...unitSnapshot,
        health: postDamageHealth,
        _authoritativeHealth: postDamageHealth,
      });
      setLethalDeathGhostTick((tick) => tick + 1);
    }

    if (!skipDamageQueue) {
      const anchor = resolveSlotDamageAnchor(zoneName, index);
      if (anchor) {
        const animationKey = `lethal-death-${slotKey}-${Date.now()}`;
        queueDamageAnimationTrigger(animationKey, {
          attackId: animationKey,
          x: anchor.x,
          y: anchor.y,
          slotElement: anchor.slotElement,
          rect: anchor.rect,
          timestamp: Date.now(),
          targetZone: zoneName,
          targetIndex: index,
          damageAmount: typeof damageAmount === 'number' && damageAmount > 0 ? damageAmount : undefined,
        });
      }
    }

    triggerLethalAttackFade({ zoneName, index });
  }, [queueDamageAnimationTrigger, triggerLethalAttackFade, resolveSlotDamageAnchor, validateDamageAnchor]);

  const queueBroadcastStrategemDamageTriggers = useCallback((holdTargets) => {
    if (!Array.isArray(holdTargets)) {
      return;
    }
    holdTargets.forEach((target) => {
      if (
        !target?.targetInstanceId
        || target.targetZone == null
        || target.targetIndex == null
      ) {
        return;
      }

      const isLethal = typeof target.healthAfter === 'number' && target.healthAfter <= 0;
      if (isLethal) {
        triggerUnitLethalDeathSequence({
          zoneName: target.targetZone,
          index: target.targetIndex,
          unitSnapshot: target.ghostUnit,
          damageAmount: target.damageAmount,
        });
        return;
      }

      const anchor = resolveSlotDamageAnchor(target.targetZone, target.targetIndex);
      if (anchor && validateDamageAnchor(anchor)) {
        const animationKey = `broadcast-strategem-damage-${target.targetInstanceId}-${Date.now()}`;
        queueDamageAnimationTrigger(animationKey, {
          attackId: animationKey,
          x: anchor.x,
          y: anchor.y,
          slotElement: anchor.slotElement,
          rect: anchor.rect,
          timestamp: Date.now(),
          targetZone: target.targetZone,
          targetIndex: target.targetIndex,
          damageAmount: target.damageAmount,
          targetInstanceId: target.targetInstanceId,
          healthBefore: target.healthBefore,
          healthAfter: target.healthAfter,
        });
        return;
      }

      if (
        typeof target.healthBefore === 'number'
        && typeof target.healthAfter === 'number'
        && target.healthAfter > 0
        && target.healthBefore > target.healthAfter
      ) {
        battlefieldHealthCountdown.startCountdown(
          target.targetInstanceId,
          target.healthBefore,
          target.healthAfter,
          {
            zoneName: target.targetZone,
            slotIndex: target.targetIndex,
          }
        );
      }
    });
  }, [
    queueDamageAnimationTrigger,
    triggerUnitLethalDeathSequence,
    battlefieldHealthCountdown.startCountdown,
  ]);

  queueBroadcastStrategemDamageTriggersRef.current = queueBroadcastStrategemDamageTriggers;

  const registerAttackScatterHolds = useCallback((attackId, outcome) => {
    if (!outcome?.scatterDamageTargets?.length) {
      return;
    }
    const viewerUserId = attackHealthFxViewerUserIdRef.current;
    holdScatterHealthForAttackOutcome(
      outcome,
      battlefieldHealthCountdown.setHealthHold,
      viewerUserId
    );
    const bystanders = resolveScatterBystanderTargets(outcome, viewerUserId);
    if (attackId && bystanders.length > 0) {
      pendingAttackScatterHoldsRef.current.set(attackId, bystanders);
    }
    const primaryScatter = resolvePrimaryScatterFollowUp(outcome, viewerUserId);
    if (attackId && primaryScatter) {
      pendingPrimaryScatterDamageRef.current.set(attackId, primaryScatter);
    }
  }, [battlefieldHealthCountdown.setHealthHold]);

  const registerAttackScatterHoldsRef = useRef(registerAttackScatterHolds);
  registerAttackScatterHoldsRef.current = registerAttackScatterHolds;

  const applyAttackInitiatedHealthFx = useCallback((attackId, outcome) => {
    if (!outcome) {
      return;
    }
    holdSurvivorHealthForAttackOutcome(
      outcome,
      battlefieldHealthCountdown.setHealthHold,
      { getActualUnitDamage: getActualUnitDamageFromOutcome }
    );
    registerAttackScatterHolds(attackId, outcome);
  }, [battlefieldHealthCountdown.setHealthHold, registerAttackScatterHolds]);

  const applyAttackInitiatedHealthFxRef = useRef(applyAttackInitiatedHealthFx);
  applyAttackInitiatedHealthFxRef.current = applyAttackInitiatedHealthFx;

  const applyAttackCompleteHealthFx = useCallback((attackId, outcome) => {
    if (!outcome) {
      return;
    }
    const scatterFxNotStartedYet = shouldReholdScatterBeforeAttackCompleteState(
      pendingAttackScatterHoldsRef.current,
      attackId
    );
    const previewNotAppliedYet = !attackId || !attackPreviewAppliedRef.current.has(attackId);
    const resolveLiveHealthByInstanceId = (instanceId) => (
      resolveLiveUnitHealthByInstanceId(gameStateRefRef.current?.current, instanceId)
    );

    if (scatterFxNotStartedYet) {
      registerAttackScatterHolds(attackId, outcome);
    }
    if (previewNotAppliedYet) {
      holdSurvivorHealthForAttackOutcome(
        outcome,
        battlefieldHealthCountdown.setHealthHold,
        {
          getActualUnitDamage: getActualUnitDamageFromOutcome,
          resolveLiveHealthByInstanceId,
        }
      );
    }
  }, [
    battlefieldHealthCountdown.setHealthHold,
    registerAttackScatterHolds,
  ]);

  const applyAttackCompleteHealthFxRef = useRef(applyAttackCompleteHealthFx);
  applyAttackCompleteHealthFxRef.current = applyAttackCompleteHealthFx;

  const queueAttackScatterAtHit = useCallback((attackId, outcome) => {
    if (!outcome?.scatterDamageTargets?.length) {
      return;
    }
    const viewerUserId = attackHealthFxViewerUserIdRef.current;
    const bystanders = resolveScatterBystanderTargets(outcome, viewerUserId);
    if (bystanders.length > 0) {
      queueBroadcastStrategemDamageTriggers(bystanders);
      if (attackId) {
        pendingAttackScatterHoldsRef.current.delete(attackId);
      }
    }
  }, [queueBroadcastStrategemDamageTriggers]);

  const queueAttackScatterAtHitRef = useRef(queueAttackScatterAtHit);
  queueAttackScatterAtHitRef.current = queueAttackScatterAtHit;

  const handleDamageAnimationCompleteRef = useRef(handleDamageAnimationComplete);
  handleDamageAnimationCompleteRef.current = handleDamageAnimationComplete;

  useEffect(() => {
    damageAnimationTriggers.forEach((trigger, attackId) => {
      if (completedDamageAnimationTriggerKeysRef.current.has(attackId)) {
        return;
      }
      const isGeneralAnimation = resolveDamageTriggerIsGeneralAnimation(attackId, trigger);
      if (validateDamageAnchor(trigger, { isGeneralAnimation })) {
        return;
      }
      handleDamageAnimationCompleteRef.current(attackId, trigger);
    });
  }, [damageAnimationTriggers, validateDamageAnchor]);

  // ✅ ORDERING GUARD: Track completed attackIds so stale attack-initiated
  // messages cannot re-apply pre-damage state after attack-complete.
  const completedAttackIdsRef = useRef(new Set());
  // ✅ SPECTATOR DAMAGE PREVIEW: apply outcome locally at hit timing to avoid
  // visible late HP updates in dispatch spectating.
  const attackPreviewAppliedRef = useRef(new Set());
  const attackPreviewTimeoutsRef = useRef(new Map());

  const applyAttackOutcomePreviewState = useCallback((sourceState, outcome) => {
    if (!sourceState || !outcome) {
      throw new Error('applyAttackOutcomePreviewState: sourceState and outcome are required');
    }
    const next = JSON.parse(JSON.stringify(sourceState));
    const nextState = next.state;
    if (!nextState || typeof nextState !== 'object') {
      throw new Error('applyAttackOutcomePreviewState: next.state missing');
    }

    const attackerPlayer = nextState[outcome.attackerPlayerId];
    const targetPlayer = nextState[outcome.targetPlayerId];
    if (!attackerPlayer || !targetPlayer) {
      throw new Error('applyAttackOutcomePreviewState: attacker/target player missing');
    }

    const resolveUnit = (playerObj, zone, instanceId, index) => {
      const zoneArr = playerObj?.playedOrders?.[zone];
      if (!Array.isArray(zoneArr)) return null;
      if (instanceId) {
        const byId = zoneArr.find(u => u && u.instanceId === instanceId);
        if (byId) return byId;
      }
      if (typeof index === 'number' && index >= 0 && index < zoneArr.length) {
        return zoneArr[index] || null;
      }
      return null;
    };

    // General attack preview
    if (outcome.isGeneralAttack || outcome.targetZone === 'general') {
      if (typeof outcome.targetHealthAfter === 'number') {
        targetPlayer.health = Math.max(0, outcome.targetHealthAfter);
      }
      return next;
    }

    const attackerUnit = resolveUnit(
      attackerPlayer,
      outcome.attackerZone,
      outcome.attackerInstanceId,
      outcome.attackerIndex
    );
    const targetUnit = resolveUnit(
      targetPlayer,
      outcome.targetZone,
      outcome.targetInstanceId,
      outcome.targetIndex
    );

    if (attackerUnit && typeof outcome.attackerHealthAfter === 'number') {
      attackerUnit.health = Math.max(0, outcome.attackerHealthAfter);
    }
    if (targetUnit && typeof outcome.targetHealthAfter === 'number') {
      targetUnit.health = Math.max(0, outcome.targetHealthAfter);
    }

    return next;
  }, []);

  const applyAttackHitOutcomePreview = useCallback((attackId, outcome) => {
    if (!attackId || !outcome) {
      return;
    }
    if (attackPreviewAppliedRef.current.has(attackId)) {
      return;
    }
    if (completedAttackIdsRef.current.has(attackId)) {
      return;
    }
    holdSurvivorHealthForAttackOutcome(
      outcome,
      battlefieldHealthCountdown.setHealthHold,
      { getActualUnitDamage: getActualUnitDamageFromOutcome }
    );
    holdScatterHealthForAttackOutcome(
      outcome,
      battlefieldHealthCountdown.setHealthHold,
      attackHealthFxViewerUserIdRef.current
    );
    setGameState((prevState) => applyAttackOutcomePreviewState(prevState, outcome));
    attackPreviewAppliedRef.current.add(attackId);
  }, [
    applyAttackOutcomePreviewState,
    battlefieldHealthCountdown.setHealthHold,
  ]);
  
  // Initialize deploy target animations hook
  const deployTargetAnimations = useDeployTargetAnimations();
  const deployTargetAnimationsRef = useRef(deployTargetAnimations);
  const raidAnimations = useRaidAnimations();
  const raidAnimationsRef = useRef(raidAnimations);
  const registeredDeployVfxKeysRef = useRef(new Set());

  // Initialize strategem target animations hook
  const strategemTargetAnimations = useStrategemTargetAnimations();
  const {
    registerSlotAnimation: registerEpSlotDeathAnimation,
    getSlotAnimations: getEpSlotDeathAnimations,
    unregisterSlotAnimation: unregisterEpSlotDeathAnimation,
  } = useDamageAnimationContext();

  /**
   * Instance IDs for strategems we registered locally (targetStrategem). Used so strategem-initiated
   * can skip mirroring for the caster when strategemCasterPlayerId is missing (e.g. counterstrike on our turn).
   */
  const selfInitiatedStrategemInstanceIdsRef = useRef(new Set());
  /** Pre-hit hold targets keyed by strategem instanceId — applied again on strategem-complete. */
  const pendingStrategemHealthHoldsRef = useRef(new Map());
  /** Hold targets for Inferno Drop (319) napalm completion, keyed by strategem instanceId. */
  const pendingBroadcastStrategemDamageRef = useRef(new Map());
  /** Instant-execute strategem hold targets — flushed when that strategem's VFX pipeline completes. */
  const pendingInstantExecuteHealthHoldsRef = useRef(new Map());
  const flushPendingInstantExecuteHealthHoldsRef = useRef(null);

  const getPendingInfernoBroadcastDamage = useCallback((instanceId) => {
    if (instanceId == null) {
      return null;
    }
    return pendingBroadcastStrategemDamageRef.current.get(String(instanceId)) ?? null;
  }, []);

  const registerStrategemTargetAnimation = useCallback(
    (anim, stateForEnrichment = null) => {
      const stateSource = stateForEnrichment ?? gameState;
      const damageAmount =
        anim?.strategemId != null
          ? getStrategemDamageAmountById(anim.strategemId, stateSource)
          : null;
      const enriched = enrichStrategemUnitDamageMeta(anim, damageAmount);
      const healthHoldTargets = resolveStrategemHealthHoldTargets(enriched);
      if (healthHoldTargets.length > 0 && anim?.strategemInstanceId != null) {
        pendingStrategemHealthHoldsRef.current.set(
          String(anim.strategemInstanceId),
          healthHoldTargets
        );
      }
      strategemTargetAnimations.registerAnimation(enriched);
    },
    [strategemTargetAnimations, gameState]
  );

  const registerStrategemTargetAnimationRef = useRef(registerStrategemTargetAnimation);
  registerStrategemTargetAnimationRef.current = registerStrategemTargetAnimation;

  const registerLocalStrategemTargetAnimation = useCallback(
    (anim) => {
      if (anim?.strategemInstanceId != null) {
        const sid = String(anim.strategemInstanceId);
        selfInitiatedStrategemInstanceIdsRef.current.add(sid);
        window.setTimeout(() => {
          selfInitiatedStrategemInstanceIdsRef.current.delete(sid);
        }, 120000);
      }
      registerStrategemTargetAnimation(anim);
    },
    [registerStrategemTargetAnimation]
  );

  const registerDeployTargetAnimation = useCallback(
    (anim) => {
      if (anim?.isRedeemDamageAnimation === true && !anim?.deployUnitAttackSoundType) {
        throw new Error(
          `registerDeployTargetAnimation: redeem damage animation missing deployUnitAttackSoundType for unit ${anim.deployUnitId}`
        );
      }
      const dedupeKey = (anim?.isDroneDamageAnimation || anim?.isRedeemDamageAnimation)
        ? anim?.animationKey
        : (anim?.instanceIdKeyForDedupe || anim?.animationKey);
      if (dedupeKey && registeredDeployVfxKeysRef.current.has(dedupeKey)) {
        return;
      }
      if (dedupeKey) {
        registeredDeployVfxKeysRef.current.add(dedupeKey);
      }
      const healthHoldTargets = resolveDeployHealthHoldTargets(anim);
      if (healthHoldTargets.length > 0) {
        holdDeployZoneHealthBeforeDamage(healthHoldTargets);
      }
      deployTargetAnimations.registerAnimation(anim);
    },
    [deployTargetAnimations, holdDeployZoneHealthBeforeDamage]
  );

  // Server-authoritative redeem: pull the redeem projectiles out of the broadcast's drone-damage
  // queue so they DON'T auto-play together. We hold each target's HP at its pre-damage value up
  // front (so the already-applied authoritative state doesn't flash all targets to post-damage),
  // then return them grouped by redeemer instanceId for the death pump to fire one unit at a time.
  const prepareRedeemProjectiles = useCallback((gameStateFragment) => {
    const grouped = new Map();
    const anims = gameStateFragment?.pendingDroneDamageAnimations;
    if (!Array.isArray(anims) || anims.length === 0) {
      return grouped;
    }
    const viewerUserId = deployAnimationViewerUserIdRef.current;
    if (!viewerUserId) {
      return grouped;
    }
    const previousGameState = previousGameStateRef.current;
    const remaining = [];
    anims.forEach((anim) => {
      if (anim?.isRedeemDamageAnimation !== true) {
        remaining.push(anim);
        return;
      }
      let payload = convertPendingDeployAnimationForViewer(anim, viewerUserId);
      payload = mergeDroneDamageHealthMetadataFromServer(anim, payload);
      if (previousGameState && gameStateFragment) {
        payload = enrichDeployAnimationDamageMeta(payload, previousGameState, gameStateFragment, viewerUserId);
        payload = mergeDroneDamageHealthMetadataFromServer(anim, payload);
      }
      const healthHoldTargets = resolveDeployHealthHoldTargets(payload);
      if (healthHoldTargets.length > 0) {
        holdDeployZoneHealthBeforeDamage(healthHoldTargets);
      }
      const instanceId = payload.deployUnitInstanceId;
      if (!instanceId) {
        throw new Error('prepareRedeemProjectiles: redeem damage animation missing deployUnitInstanceId');
      }
      const list = grouped.get(instanceId) || [];
      list.push(payload);
      grouped.set(instanceId, list);
    });
    // Leave the non-redeem drone animations for the normal drone-damage consumer.
    if (remaining.length > 0) {
      gameStateFragment.pendingDroneDamageAnimations = remaining;
    } else {
      delete gameStateFragment.pendingDroneDamageAnimations;
    }
    return grouped;
  }, [holdDeployZoneHealthBeforeDamage]);

  deployTargetAnimationsRef.current = {
    ...deployTargetAnimations,
    registerAnimation: registerDeployTargetAnimation,
  };
  raidAnimationsRef.current = raidAnimations;

  const strategemActiveAnimationsRef = useRef(strategemTargetAnimations.activeAnimations);
  strategemActiveAnimationsRef.current = strategemTargetAnimations.activeAnimations;

  const flushPendingInstantExecuteHealthHolds = useCallback((
    strategemInstanceId,
    filterTargetInstanceId = null
  ) => {
    if (strategemInstanceId == null) {
      return;
    }
    const key = String(strategemInstanceId);
    const pending = pendingInstantExecuteHealthHoldsRef.current.get(key);
    if (!pending || !Array.isArray(pending.holdTargets) || pending.holdTargets.length === 0) {
      return;
    }

    let holdTargets = pending.holdTargets;
    if (filterTargetInstanceId != null) {
      holdTargets = holdTargets.filter(
        (target) => target.targetInstanceId === filterTargetInstanceId
      );
      if (holdTargets.length === 0) {
        return;
      }
      const remaining = pending.holdTargets.filter(
        (target) => target.targetInstanceId !== filterTargetInstanceId
      );
      if (remaining.length === 0) {
        pendingInstantExecuteHealthHoldsRef.current.delete(key);
      } else {
        pendingInstantExecuteHealthHoldsRef.current.set(key, {
          ...pending,
          holdTargets: remaining,
        });
      }
    } else {
      pendingInstantExecuteHealthHoldsRef.current.delete(key);
    }

    queueBroadcastStrategemDamageTriggers(holdTargets);
  }, [queueBroadcastStrategemDamageTriggers]);

  flushPendingInstantExecuteHealthHoldsRef.current = flushPendingInstantExecuteHealthHolds;

  const releaseInstantExecuteHoldForTarget = useCallback((strategemInstanceId, targetInstanceId) => {
    if (strategemInstanceId == null || targetInstanceId == null) {
      return;
    }
    const key = String(strategemInstanceId);
    const pending = pendingInstantExecuteHealthHoldsRef.current.get(key);
    if (!pending?.holdTargets?.length) {
      return;
    }
    const remaining = pending.holdTargets.filter(
      (target) => target.targetInstanceId !== targetInstanceId
    );
    if (remaining.length === pending.holdTargets.length) {
      return;
    }
    if (remaining.length === 0) {
      pendingInstantExecuteHealthHoldsRef.current.delete(key);
      return;
    }
    pendingInstantExecuteHealthHoldsRef.current.set(key, {
      ...pending,
      holdTargets: remaining,
    });
  }, []);

  const scheduleInstantExecuteHoldFlushIfNoActiveAnimation = useCallback((strategemInstanceId) => {
    if (strategemInstanceId == null) {
      return;
    }
    const key = String(strategemInstanceId);
    if (!pendingInstantExecuteHealthHoldsRef.current.has(key)) {
      return;
    }
    window.requestAnimationFrame(() => {
      const stillPending = pendingInstantExecuteHealthHoldsRef.current.has(key);
      if (!stillPending) {
        return;
      }
      const hasActive = Array.from(strategemActiveAnimationsRef.current.values()).some(
        (anim) => String(anim.strategemInstanceId) === key
      );
      if (!hasActive) {
        flushPendingInstantExecuteHealthHolds(strategemInstanceId);
      }
    });
  }, [flushPendingInstantExecuteHealthHolds]);

  const deployActiveAnimationsRef = useRef(deployTargetAnimations.activeAnimations);
  deployActiveAnimationsRef.current = deployTargetAnimations.activeAnimations;

  const hasPendingDamageTriggerForInstance = useCallback((instanceId) => {
    if (!instanceId) {
      return false;
    }
    for (const [, trigger] of damageAnimationTriggersRef.current) {
      if (trigger?.targetInstanceId === instanceId) {
        return true;
      }
    }
    return false;
  }, []);

  const hasActiveProjectileAnimationForInstance = useCallback((instanceId) => {
    if (pendingProjectileHealthHoldRef.current.has(instanceId)) {
      return true;
    }
    if (isInstanceIdInActiveTargetedAnimations(instanceId, strategemActiveAnimationsRef.current)
      || isInstanceIdInActiveTargetedAnimations(instanceId, deployActiveAnimationsRef.current)) {
      return true;
    }
    for (const [, pending] of pendingBroadcastStrategemDamageRef.current) {
      if (pending.holdTargets?.some((target) => target.targetInstanceId === instanceId)) {
        return true;
      }
    }
    for (const [, bystanders] of pendingAttackScatterHoldsRef.current) {
      if (bystanders?.some((target) => target.targetInstanceId === instanceId)) {
        return true;
      }
    }
    for (const [, primaryScatter] of pendingPrimaryScatterDamageRef.current) {
      if (primaryScatter?.targetInstanceId === instanceId) {
        return true;
      }
    }
    return false;
  }, []);

  const releaseStaleAttackDamageFxOnTurnBoundary = useCallback(() => {
    const pendingAttacks = attackAnimationsRef.current?.pendingAttacks ?? [];
    const hasUnfinishedAttack = pendingAttacks.some(
      ([attackId]) => !completedAttackIdsRef.current.has(attackId)
    );
    if (hasUnfinishedAttack || attackInProgressRef.current) {
      return;
    }

    setDamageAnimationTriggers(new Map());
    damageAnimationTriggersRef.current = new Map();
    pendingPrimaryScatterDamageRef.current.clear();
    pendingAttackScatterHoldsRef.current.clear();
    attackSurvivorCountdownPlayedRef.current.clear();

    battlefieldHealthCountdown.releaseStaleAttackDamageFxExceptInstances(
      hasActiveProjectileAnimationForInstance
    );
  }, [
    battlefieldHealthCountdown.releaseStaleAttackDamageFxExceptInstances,
    hasActiveProjectileAnimationForInstance,
  ]);

  const releaseStaleAttackDamageFxOnTurnBoundaryRef = useRef(releaseStaleAttackDamageFxOnTurnBoundary);
  releaseStaleAttackDamageFxOnTurnBoundaryRef.current = releaseStaleAttackDamageFxOnTurnBoundary;
  
  // Track which zones have had strategem 219 (Plague of Darkness) animations complete
  // This delays showing immobilize indicators until the attack animation arrives
  const strategem219CompletedZonesRef = useRef(new Set());
  /** Map instanceId -> immobilized; null until first snapshot (skip sound on initial load). */
  const prevImmobilizedByInstanceRef = useRef(null);
  /** Map playerId -> Set(barrier threshold); null until first snapshot (skip sound on initial load). */
  const prevBarrierDismantledByPlayerRef = useRef(null);
  /** Opponent counterstrike unit instanceIds; null until first snapshot (skip sound on initial load). */
  const prevOpponentCounterStrikeUnitsRef = useRef(null);
  const counterstrikeSoundGameIdRef = useRef(null);
  
  // ✅ TWO-PHASE STRATEGEM SYSTEM: Track mapping from strategemInstanceId to backend's strategemAnimationId
  // This allows the onComplete callback to send the correct animation ID back to the backend
  const pendingStrategemAnimationIdsRef = useRef(new Map());
  /**
   * Instance IDs whose local animation already finished before `strategem-initiated` arrived (delayed play-turn + RTT).
   * When initiated arrives, flush `strategem-animation-complete` immediately so the server does not hit the fallback timeout.
   */
  const strategemAnimationCompleteDeferredRef = useRef(new Set());
  /** Overlay strategems (60/313/314/315/341): one-shot completion if onComplete never fires. */
  const overlayStrategemWatchdogTimeoutsRef = useRef(new Map());

  /** Strategem 326 (Momentum Shift): play cast SFX when completed orders gains this strategem */
  const momentumShiftSfxPrevLenByPlayerRef = useRef({});
  useEffect(() => {
    if (!gameState?.state) return;
    for (const playerId of Object.keys(gameState.state)) {
      const completed = gameState.state[playerId]?.completedOrders;
      const len = Array.isArray(completed) ? completed.length : 0;
      const prevLen = momentumShiftSfxPrevLenByPlayerRef.current[playerId];
      if (prevLen === undefined) {
        momentumShiftSfxPrevLenByPlayerRef.current[playerId] = len;
        continue;
      }
      if (len > prevLen) {
        const last = completed[len - 1];
        if (last && last.id === 326) {
          if (getEffectiveSoundOutputVolume(1) === null) {
            momentumShiftSfxPrevLenByPlayerRef.current[playerId] = len;
            continue;
          }
          try {
            const audio = new Audio('/sounds/momentum_shift.mp3');
            audio.play().catch(() => {});
          } catch (_) {}
        }
      }
      momentumShiftSfxPrevLenByPlayerRef.current[playerId] = len;
    }
  }, [gameState]);

  /** Strategem 323 (Strategic Forecast): play when completed orders gains this strategem */
  const strategicForecastSfxPrevLenByPlayerRef = useRef({});
  useEffect(() => {
    if (!gameState?.state) return;
    for (const playerId of Object.keys(gameState.state)) {
      const completed = gameState.state[playerId]?.completedOrders;
      const len = Array.isArray(completed) ? completed.length : 0;
      const prevLen = strategicForecastSfxPrevLenByPlayerRef.current[playerId];
      if (prevLen === undefined) {
        strategicForecastSfxPrevLenByPlayerRef.current[playerId] = len;
        continue;
      }
      if (len > prevLen) {
        const last = completed[len - 1];
        if (last && last.id === 323) {
          if (getEffectiveSoundOutputVolume(1) !== null) {
            try {
              const audio = new Audio('/sounds/strategic_forecast.mp3');
              audio.play().catch(() => {});
            } catch (_) {}
          }
        }
      }
      strategicForecastSfxPrevLenByPlayerRef.current[playerId] = len;
    }
  }, [gameState]);

  // Bully damage ghost system: holds pre-damage unit snapshots so health drop can be revealed one-by-one
  const bullyDamageGhostsRef = useRef(new Map());
  const [bullyGhostTick, setBullyGhostTick] = useState(0);    // eslint-disable-line no-unused-vars
  const bullyDamageStagingTimersRef = useRef([]);
  const bullyFlashInstanceIdsRef = useRef(new Set());
  const bullyFistElementsRef = useRef([]);
  const bullyAnimatedCountRef = useRef(0);
  const bullyDamagePendingInstanceIdsRef = useRef(new Set());
  /** Fist playback deferred until Oblivion Pulse / zone strategem projectiles finish. */
  const pendingBullyFistPlaybackRef = useRef(null);
  /** Pre-effect snapshot for bully ghost staging when a follow-up game-update arrives. */
  const bullySequenceAnchorPrevStateRef = useRef(null);

  // NOTE: useOpponentStrategemAnimations is called after actionLog is defined (see below)

  // Faction translations for tooltips
  const [factionTranslations, setFactionTranslations] = useState({});

  // Fetch faction translations
  useEffect(() => {
    const fetchFactionTranslations = async () => {
      try {
        const currentLanguage = i18nInstance.language || 'en-us';
        const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
        
        const response = await fetch(`${API_URL}/factions?lang=${encodeURIComponent(normalizedLanguage)}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const factions = await response.json();
          const translationMap = {};
          factions.forEach(faction => {
            translationMap[faction.english_name] = faction.name;
          });
          setFactionTranslations(translationMap);
        }
      } catch (err) {
        console.error('Failed to fetch faction translations:', err);
        setFactionTranslations({});
      }
    };
    
    fetchFactionTranslations();
  }, [i18nInstance.language, token]);

  // Helper to get translated faction name
  const getTranslatedFactionName = (factionName) => {
    if (!factionName) return null;
    return factionTranslations[factionName] || factionName;
  };
  
  // ✅ FIX: Initialize yourTurn from navigation state if available
  const [yourTurn, setYourTurn] = useState(location.state?.yourTurn ?? false);
  const [error, setError] = useState("");

  // Discord Rich Presence: Update when battle starts
  const battleStartTimeRef = useRef(null);
  const lastPresenceUpdateRef = useRef(null);
  
  useEffect(() => {
    let interval = null;
    
    // Only update if we have effectiveGameState and we're not spectating
    // Use effectiveGameState for consistency with rest of component
    if (effectiveGameState && !isSpectating) {
      // Battle has started - update Discord presence with elapsed time
      // Only set start time once when battle first starts
      if (!battleStartTimeRef.current) {
        battleStartTimeRef.current = Math.floor(Date.now() / 1000);
      }
      
      // Update presence with the fixed start time
      const updatePresence = () => {
        const startTimestamp = battleStartTimeRef.current;
        
        // Check if Electron and Discord API are available
        // Use direct API call since we need to pass a custom start timestamp
        if (window && window.electron && window.electron.discord) {
          try {
            window.electron.discord.updatePresence('In Battle', null, startTimestamp);
            lastPresenceUpdateRef.current = Date.now();
          } catch (error) {
            console.error('❌ Error updating Discord presence:', error);
          }
        } else {
          console.warn('⚠️ Discord API not available in BattleUI', {
            window: typeof window !== 'undefined',
            electron: !!(window && window.electron),
            discord: !!(window && window.electron && window.electron.discord)
          });
        }
      };
      
      // Update immediately
      updatePresence();
      
      // Also update periodically (every 20 seconds) to keep it fresh
      // Discord rate limit is 15 seconds, so 20 seconds is safe
      interval = setInterval(() => {
        if (effectiveGameState && !isSpectating && battleStartTimeRef.current) {
          updatePresence();
        }
      }, 20000);
    } else if (!effectiveGameState && battleStartTimeRef.current) {
      // Battle ended - reset start time
      battleStartTimeRef.current = null;
      lastPresenceUpdateRef.current = null;
    }
    
    // Cleanup: Clear presence when component unmounts (battle ends)
    return () => {
      if (interval) {
        clearInterval(interval);
      }
      if (!isSpectating) {
        battleStartTimeRef.current = null;
        lastPresenceUpdateRef.current = null;
        clearDiscordPresence();
      }
    };
  }, [effectiveGameState, isSpectating]);
  // Store opponent strategy ID for potential subscription after loss
  const opponentStrategyIdRef = useRef(null);
  // Track if turn state has been initialized (to avoid showing image on initial load)
  const turnStateInitializedRef = useRef(false);
  // Track previous turn state to detect turn changes (initialize with current state)
  const previousYourTurnRef = useRef(location.state?.yourTurn ?? false);

  // Campaign/initial state: when we receive initialGameState (e.g. from REST start-mission), set turn state
  // so the first WebSocket update (e.g. deploy response) is not mistaken for "turn just switched to player"
  // which would show the "TIME TO ACT" overlay incorrectly after the first deploy in tutorial.
  const initialTurnStateFromGameStateRef = useRef(false);
  useEffect(() => {
    if (initialTurnStateFromGameStateRef.current || !initialGameState || !userId) return;
    const isMyTurn = initialGameState.turn === userId ||
      initialGameState.turn === userId.toString() ||
      String(initialGameState.turn) === String(userId);
    if (isMyTurn) {
      previousYourTurnRef.current = true;
      setYourTurn(true);
      turnStateInitializedRef.current = true;
      initialTurnStateFromGameStateRef.current = true;
    }
  }, [initialGameState, userId]);

  // Helper function to store opponent strategy ID from gameState
  const storeOpponentStrategyId = (gameState) => {
    if (gameState && gameState.players && gameState.state) {
      const opponentId = gameState.players.find(id => id !== userId);
      if (opponentId && opponentId !== 'bot') {
        const opponentState = gameState.state[opponentId];
        if (opponentState?.strategy) {
          const strategy = opponentState.strategy;
          const strategyId = strategy.id;
          if (strategyId && typeof strategyId === 'number' && strategyId !== 'bot-hardcoded') {
            opponentStrategyIdRef.current = strategyId;
          }
        }
      }
    }
  };
  
  const openTurnStartOverlayRef = useRef(() => {});

  // Helper function to update yourTurn and detect turn changes
  const updateYourTurn = (newYourTurn, source = 'unknown') => {
    const wasYourTurn = previousYourTurnRef.current;
    previousYourTurnRef.current = newYourTurn;
    setYourTurn(newYourTurn);
    
    // Check for turn change to player (only after turn state is initialized)
    if (turnStateInitializedRef.current && wasYourTurn !== null && !wasYourTurn && newYourTurn) {
      openTurnStartOverlayRef.current();
    }
  };
  
  // ✅ REFACTORED: Use custom hook for state synchronization
  const stateSync = useGameStateSync(userId, (newState) => {
    // Callback when state is updated - update React state
    setGameState(newState);
  });
  
  // Extract state sync utilities
  const {
    gameStateRef,
    isStateStabilizing,
    isStateUpdating,
    showLoadingOverlay,
    updateState,
    validateState,
    lockUI,
    unlockUI,
    cleanup: cleanupStateSync,
    getCurrentState,
    getStateVersion,
    lastStateVersionRef
  } = stateSync;
  
  // ✅ FIX: Store hook functions in refs so WebSocket handler always has latest versions
  const unlockUIRef = useRef(unlockUI);
  const updateStateRef = useRef(updateState);
  const validateStateRef = useRef(validateState);
  const checkStateVersionRef = useRef(stateSync.checkStateVersion);
  const gameStateRefRef = useRef(gameStateRef);
  const wsConnectingRef = useRef(false); // ✅ FIX: Track if we're already connecting to prevent loops
  const currentSocketRef = useRef(null); // ✅ FIX: Track current socket for reconnect verification
  // Hide units a dying redeemer spawned (already present in the incoming authoritative state) until
  // that redeemer's death sequence finishes. Returns the held spawned instanceIds for later release.
  const holdRedeemSpawnedUnits = useCallback((sourceInstanceId, gameStateFragment, redeemSourceSlot) => {
    if (!sourceInstanceId || !gameStateFragment?.state) {
      return [];
    }
    const held = [];
    const holdInstanceId = (instanceId) => {
      if (!instanceId || instanceId === sourceInstanceId || held.includes(instanceId)) {
        return;
      }
      redeemSpawnHeldInstanceIdsRef.current.add(instanceId);
      held.push(instanceId);
    };
    for (const pid of Object.keys(gameStateFragment.state)) {
      const orders = gameStateFragment.state[pid]?.playedOrders;
      if (!orders) {
        continue;
      }
      for (const zone of ['front', 'flank']) {
        const lane = orders[zone];
        if (!Array.isArray(lane)) {
          continue;
        }
        for (const unit of lane) {
          if (
            unit
            && unit.spawnedByRedeem === true
            && unit.spawnedRedeemSourceInstanceId === sourceInstanceId
            && unit.instanceId
          ) {
            holdInstanceId(unit.instanceId);
          }
        }
      }
    }
    if (
      redeemSourceSlot?.playerId != null
      && redeemSourceSlot?.zone
      && typeof redeemSourceSlot.index === 'number'
    ) {
      const occupant = gameStateFragment.state[redeemSourceSlot.playerId]
        ?.playedOrders?.[redeemSourceSlot.zone]?.[redeemSourceSlot.index];
      if (occupant?.instanceId) {
        holdInstanceId(occupant.instanceId);
      }
    }
    if (held.length > 0) {
      setRedeemMarkedGhostTick((tick) => tick + 1);
    }
    return held;
  }, []);

  const releaseRedeemSpawnedUnits = useCallback((spawnedInstanceIds) => {
    if (!Array.isArray(spawnedInstanceIds) || spawnedInstanceIds.length === 0) {
      return;
    }
    let changed = false;
    spawnedInstanceIds.forEach((instanceId) => {
      if (redeemSpawnHeldInstanceIdsRef.current.delete(instanceId)) {
        changed = true;
      }
    });
    if (changed) {
      setRedeemMarkedGhostTick((tick) => tick + 1);
    }
  }, []);

  const redeemDeathSequence = useRedeemDeathSequence({
    viewerUserIdRef: deployAnimationViewerUserIdRef,
    redeemMarkedByInstanceIdRef,
    setRedeemMarkedGhostTick,
    suppressSlotDeathInstanceIdsRef: suppressRedeemSlotDeathInstanceIdsRef,
    completedRedeemDeathInstanceIdsRef,
    clearSlotPreviousStateRef,
    pendingUnitDamageFxSlotsRef,
    queueDamageAnimationTrigger,
    resolveSlotDamageAnchor,
    setRedeemFinishingByInstanceId,
    cancelUnitLethalDeathFx,
    prepareRedeemProjectiles,
    playRedeemProjectile: registerDeployTargetAnimation,
    holdRedeemSpawnedUnits,
    releaseRedeemSpawnedUnits,
    playRedeemGrowPulse: (fx) => {
      const trigger = playRedeemGrowPulseRef.current;
      if (typeof trigger !== 'function') {
        throw new Error('playRedeemGrowPulse: triggerGrowPulseFromFx is not wired');
      }
      trigger(fx);
    },
    playRedeemGeneralHeal: (fx) => {
      const trigger = playRedeemGeneralHealRef.current;
      if (typeof trigger !== 'function') {
        throw new Error('playRedeemGeneralHeal: triggerViralEchoHealFromFx is not wired');
      }
      trigger(fx);
    },
  });
  const redeemDeathSequenceRef = useRef(redeemDeathSequence);
  redeemDeathSequenceRef.current = redeemDeathSequence;

  const isRedeemMarkedForDeathForUnit = useCallback((instanceId) => {
    void redeemMarkedGhostTick;
    if (!instanceId) {
      return false;
    }
    return redeemMarkedByInstanceIdRef.current.has(instanceId);
  }, [redeemMarkedGhostTick]);

  const isRedeemDeathFinishingForUnit = useCallback((instanceId) => {
    if (!instanceId) {
      return false;
    }
    return !!redeemFinishingByInstanceId[instanceId];
  }, [redeemFinishingByInstanceId]);

  const gameSocketReconnectSentRef = useRef(false); // ✅ FIX: Skip closing socket in effect cleanup when reconnect already sent (avoids close on Strict Mode / effect re-run)
  // ✅ FIX: Token ref so WebSocket effect does NOT depend on token - prevents "connection lost" when
  // War Effort or other routes trigger App re-renders / token updates (effect would re-run and close socket)
  const tokenRef = useRef(token);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  // ✅ CRITICAL: Ref for current gameId so WebSocket onmessage always sees the latest game.
  const gameIdRef = useRef(gameId);
  // Keep ref in sync with URL on every render.
  gameIdRef.current = gameId;

  // Source of truth for "which game is the user viewing" when handling WS messages.
  // Reading from URL at handler execution time avoids stale ref when navigating A→B before next render.
  // Must use React Router pathname (works with HashRouter / Electron). window.location.pathname
  // is often "/" or "/index.html" while the real route lives in the hash — wrong window path broke
  // session guards and winner/after-battle checks after Escape → new PvE.
  const getCurrentGameIdFromUrl = useCallback(() => {
    return extractBattleGameIdFromPath(location.pathname || '');
  }, [location.pathname]);

  // WebSocket session key (e.g. matchmaking `4_1_...`) must match what the user is actually viewing.
  // Prefer URL at handler time, then ref, then prop — avoids soft-lock when gameIdRef lags behind /battle/:id (reconnect, Strict Mode, etc.).
  const resolveWsSessionGameIdRef = useRef(() => null);
  const isWsMessageForDifferentSessionRef = useRef(() => false);
  resolveWsSessionGameIdRef.current = () =>
    resolveExpectedSessionGameId(getCurrentGameIdFromUrl, gameIdRef.current, gameId);
  isWsMessageForDifferentSessionRef.current = (msgGameId) =>
    isWsMessageForDifferentSessionId(msgGameId, resolveWsSessionGameIdRef.current());

  const getGameSocketForFlashlight = useCallback(() => ws || currentSocketRef.current, [ws]);

  const {
    opponentNormalized: opponentBattleFlashlight,
    setOpponentNormalized: setOpponentBattleFlashlight,
    onLocalBeamSample: onLocalBeamSampleNetworkSync,
  } = useBattleFlashlightNetworkSync({
    enabled: Boolean(gameId) && !isSpectating && !isAutobattlerSpectator,
    gameId,
    viewportWrapperRef,
    getSocket: getGameSocketForFlashlight,
    flashlightColor: battleFlashlightColor,
  });

  const onLocalBeamSample = useCallback((sample) => {
    onLocalBeamSampleNetworkSync(sample);
    // Position the darkness overlay imperatively (no setState → no BattleUI re-render storm).
    const overlayEl = mission6DarknessOverlayRef.current;
    if (!overlayEl) return;
    if (!sample || sample.active !== true) {
      overlayEl.style.background = buildMission6DarknessBackground(null, null);
      return;
    }
    if (!Number.isFinite(sample.clientX) || !Number.isFinite(sample.clientY)) {
      throw new Error('BattleUI: local flashlight sample requires finite client coordinates');
    }
    overlayEl.style.background = buildMission6DarknessBackground(sample.clientX, sample.clientY);
  }, [onLocalBeamSampleNetworkSync]);

  useEffect(() => {
    setOpponentBattleFlashlight(null);
  }, [gameId, setOpponentBattleFlashlight]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !userId) return undefined;
    let cancelled = false;
    fetch(`${API_URL}/profile/flashlight-color`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) {
          throw new Error(`flashlight-color GET ${r.status}`);
        }
        return r.json();
      })
      .then((d) => {
        if (cancelled || d == null || typeof d.color !== 'string') {
          throw new Error('flashlight-color: invalid response body');
        }
        if (!isValidFlashlightColorKey(d.color)) {
          throw new Error(`flashlight-color: server returned invalid key "${d.color}"`);
        }
        setBattleFlashlightColor(d.color.toLowerCase());
      })
      .catch((err) => {
        console.error('[BattleUI] Failed to load flashlight color:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Campaign: sync currentAllowedActions to server when tutorial step changes (so backend can enforce continueOnly etc.)
  useEffect(() => {
    if (!gameId || !gameId.startsWith('campaign_')) return;
    const handler = (e) => {
      const { gameId: detailGameId, currentAllowedActions } = e.detail || {};
      if (detailGameId !== gameId) return;
      const activeSocket = ws || currentSocketRef.current;
      if (!activeSocket || activeSocket.readyState !== 1) return;
      activeSocket.send(JSON.stringify({
        type: 'campaign-sync-allowed-actions',
        gameId: detailGameId,
        currentAllowedActions
      }));
    };
    window.addEventListener('campaign-sync-allowed-actions', handler);
    return () => window.removeEventListener('campaign-sync-allowed-actions', handler);
  }, [gameId, ws]);

  // Update refs when functions change
  useEffect(() => {
    unlockUIRef.current = unlockUI;
    updateStateRef.current = updateState;
    validateStateRef.current = validateState;
    checkStateVersionRef.current = stateSync.checkStateVersion;
    gameStateRefRef.current = gameStateRef;
  }, [unlockUI, updateState, validateState, stateSync.checkStateVersion, gameStateRef]);
  
  const lastActionTimestamp = useRef(0);
  const ACTION_COOLDOWN_MS = 150; // Minimum time between actions
  const ACTION_QUEUE_MAX = 8;
  const STATE_BUSY_POLL_MS = 50;
  const STATE_BUSY_MAX_POLLS = 30;
  const isStateUpdatingRef = useRef(false);
  const isStateStabilizingRef = useRef(false);
  const lastSentPlayOrderRef = useRef(null);
  const playOrderRef = useRef(null);
  useEffect(() => {
    isStateUpdatingRef.current = isStateUpdating;
    isStateStabilizingRef.current = isStateStabilizing;
  }, [isStateUpdating, isStateStabilizing]);
  const [playerGoesFirst, setPlayerGoesFirst] = useState(null); // null = unknown, true = player goes first, false = player goes second
  const [showFirstTurnMessage, setShowFirstTurnMessage] = useState(false); // Controls visibility of first turn message
  const isContainerMobileRef = useRef(false);
  const turnStartOverlayCopyRef = useRef({ turnLabel: null, timeToActText: '' });
  const [showMulliganIntroOverlay, setShowMulliganIntroOverlay] = useState(false);
  const [showMulliganIntroPlayerCircle, setShowMulliganIntroPlayerCircle] = useState(false);
  const [showMulliganIntroOpponentCircle, setShowMulliganIntroOpponentCircle] = useState(false);
  const [showMulliganIntroVs, setShowMulliganIntroVs] = useState(false);
  const [showMulliganIntroBossBar, setShowMulliganIntroBossBar] = useState(false);
  const [isMulliganIntroExiting, setIsMulliganIntroExiting] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedOrderInstanceId, setSelectedOrderInstanceId] = useState(null);
  
  // Avatar state
  const [avatars, setAvatars] = useState([]);
  const [selectedGeneral, setSelectedGeneral] = useState(location.state?.selectedGeneral || '');
  const [selectedAdvisor, setSelectedAdvisor] = useState(location.state?.selectedAdvisor || '');
  
  // Opponent avatar state
  const [opponentGeneral, setOpponentGeneral] = useState('');
  const [opponentAdvisor, setOpponentAdvisor] = useState('');
  const [opponentUsername, setOpponentUsername] = useState(null);
  
  // Preparation bonus state
  const [preparationBonus, setPreparationBonus] = useState(null);
  const [preparationBonusUsed, setPreparationBonusUsed] = useState(false);
  const preparationBonusPendingRef = useRef(false); // Track if activation is pending
  
  // Opponent preparation bonus state
  const [opponentPreparationBonus, setOpponentPreparationBonus] = useState(null);
  const [opponentPreparationBonusUsed, setOpponentPreparationBonusUsed] = useState(false);
  
  // Diplomatic accord state
  const [diplomaticAccord, setDiplomaticAccord] = useState(null);
  const [opponentDiplomaticAccord, setOpponentDiplomaticAccord] = useState(null);
  
  // Player ranks state (leaderboard #) + PvP ladder row from /api/player-ranks for insignia vs # display
  const [playerRank, setPlayerRank] = useState(null);
  const [playerPvpLadder, setPlayerPvpLadder] = useState(null);
  const [opponentRank, setOpponentRank] = useState(null);
  const [opponentPvpLadder, setOpponentPvpLadder] = useState(null);
  /** Dev-only: when set, battle rank badges use these instead of /api/player-ranks */
  const [devRankMock, setDevRankMock] = useState(null);
  /** Dev-only: hides developer console + Autowin until page refresh (screenshot-friendly UI) */
  const [hideDevElementsForScreenshot, setHideDevElementsForScreenshot] = useState(false);
  /** Dev-only: scenario setup overrides (opponent display + dev UI visibility) */
  const [devBattleSetupUi, setDevBattleSetupUi] = useState(null);
  const [showDevBattleSetupDialog, setShowDevBattleSetupDialog] = useState(false);

  const showDevAutowinButton =
    devBattleSetupUi?.showAutowin !== false && !hideDevElementsForScreenshot;
  const showDevConsolePanel =
    devBattleSetupUi?.showDevConsole !== false && !hideDevElementsForScreenshot;

  const effectiveOpponentUsername = useMemo(() => {
    if (devBattleSetupUi?.opponentName != null && devBattleSetupUi.opponentName !== '') {
      return devBattleSetupUi.opponentName;
    }
    return opponentUsername;
  }, [devBattleSetupUi, opponentUsername]);

  const effectiveOpponentTitleRaw = useMemo(() => {
    if (devBattleSetupUi != null) {
      if (devBattleSetupUi.opponentTitleId == null) {
        return null;
      }
      if (devBattleSetupUi.opponentTitleInjected != null && devBattleSetupUi.opponentTitleInjected !== '') {
        return devBattleSetupUi.opponentTitleInjected;
      }
    }
    const effGs = isSpectating && spectatingGameState ? spectatingGameState : gameState;
    const effUid = isSpectating && spectatingPlayerId != null ? spectatingPlayerId : userId;
    return effGs?.opponentTitles?.[effUid] ?? null;
  }, [devBattleSetupUi, gameState, spectatingGameState, userId, spectatingPlayerId, isSpectating]);

  const effectiveOpponentTitleColorKey = useMemo(() => {
    if (devBattleSetupUi != null && devBattleSetupUi.opponentTitleId != null) {
      return devBattleSetupUi.opponentTitleColor ?? 'white';
    }
    const effGs = isSpectating && spectatingGameState ? spectatingGameState : gameState;
    const effUid = isSpectating && spectatingPlayerId != null ? spectatingPlayerId : userId;
    return effGs?.opponentTitleColors?.[effUid] ?? 'white';
  }, [devBattleSetupUi, gameState, spectatingGameState, userId, spectatingPlayerId, isSpectating]);

  const effectivePlayerRank = useMemo(() => {
    if (devRankMock?.player != null) {
      const h = devRankMock.player.honorRank;
      if (h === '' || h === undefined) return null;
      const n = Number(h);
      return Number.isFinite(n) ? n : null;
    }
    return playerRank;
  }, [devRankMock, playerRank]);

  const effectivePlayerPvpLadder = useMemo(() => {
    if (devRankMock?.player != null) {
      const p = devRankMock.player;
      return {
        pvp_ladder_rank_index: p.pvp_ladder_rank_index,
        pvp_ladder_stars: 0,
        pvp_in_league: p.pvp_in_league === true,
      };
    }
    return playerPvpLadder;
  }, [devRankMock, playerPvpLadder]);

  const effectiveOpponentRank = useMemo(() => {
    if (devRankMock?.opponent != null) {
      const h = devRankMock.opponent.honorRank;
      if (h === '' || h === undefined) return null;
      const n = Number(h);
      return Number.isFinite(n) ? n : null;
    }
    return opponentRank;
  }, [devRankMock, opponentRank]);

  const effectiveOpponentPvpLadder = useMemo(() => {
    if (devRankMock?.opponent != null) {
      const p = devRankMock.opponent;
      return {
        pvp_ladder_rank_index: p.pvp_ladder_rank_index,
        pvp_ladder_stars: 0,
        pvp_in_league: p.pvp_in_league === true,
      };
    }
    return opponentPvpLadder;
  }, [devRankMock, opponentPvpLadder]);

  const desktopOpponentHudRankProps = useMemo(() => {
    const effGs = isSpectating && spectatingGameState ? spectatingGameState : gameState;
    const effUid = isSpectating && spectatingPlayerId != null ? spectatingPlayerId : userId;
    const oppId = effGs?.players?.find((id) => String(id) !== String(effUid));
    const isBotNoRankUi = String(oppId) === 'bot' && devRankMock == null;
    const hideOpponentRankBannerEarlyTutorial =
      effGs?.campaignMode === true && EARLY_TUTORIAL_MISSION_IDS.includes(effGs?.missionId);
    if (isBotNoRankUi || hideOpponentRankBannerEarlyTutorial) {
      return {
        opponentRank: null,
        opponentPvpLadderRow: null,
        showOpponentBanner: false,
      };
    }
    return {
      opponentRank: effectiveOpponentRank,
      opponentPvpLadderRow: effectiveOpponentPvpLadder,
      showOpponentBanner: true,
    };
  }, [
    isSpectating,
    spectatingGameState,
    spectatingPlayerId,
    userId,
    gameState,
    devRankMock,
    effectiveOpponentRank,
    effectiveOpponentPvpLadder,
  ]);

  // Battle end transition state
  const [battleEndTransition, setBattleEndTransition] = useState({ isTransitioning: false, type: null }); // type: 'win' | 'loss' | null
  const [showVictoryImage, setShowVictoryImage] = useState(false);
  const [showNegaraPeacefulOutro, setShowNegaraPeacefulOutro] = useState(false);
  const pendingNegaraAfterBattleNavRef = useRef(null);
  /** Blocks Navigate/duplicate battle-end while Negara slideshow plays (sync, before React state commits). */
  const negaraPeacefulOutroActiveRef = useRef(false);
  const negaraPeacefulBattleEndHandledRef = useRef(false);
  const cancelAllDeploySoundsRef = useRef(() => {});
  const [showDefeatImage, setShowDefeatImage] = useState(false);
  const [victoryImageWhite, setVictoryImageWhite] = useState('rgba(255, 255, 255, 1)'); // Default to pure white, will be sampled from image
  const [defeatImageGray, setDefeatImageGray] = useState('rgba(0, 0, 0, 1)'); // Default to pure black, will be sampled from image
  const battleEndOverlayVisuals = useMemo(
    () =>
      getBattleEndOverlayVisuals(
        i18nInstance.resolvedLanguage || i18nInstance.language || 'en-us'
      ),
    [i18nInstance.resolvedLanguage, i18nInstance.language]
  );
  const battleEndSoundRef = useRef(null);
  const isTransitioningRef = useRef(false); // Ref to track transition state synchronously
  
  // Function to handle battle end transition
  // triggerSource: optional string for diagnostics (e.g. 'game-update-winner', 'player-resigned', 'attack-complete')
  const handleBattleEnd = useCallback((didYouWin, gameId, socketToClose = null, isFriendlyMatch = false, triggerSource = 'unknown', authoritativeGameState = null) => {
    // Prefer the gameState from the WebSocket message that ended the match (React state can lag one render).
    const currentGameState = authoritativeGameState ?? gameState ?? location.state?.gameState;
    const isPeacefulNegaraDeployWin =
      Number(currentGameState?.exploreUnknownMissionIndex) === 75 &&
      currentGameState?.exploreUnknownNegaraPeacefulWin === true;
    if (isPeacefulNegaraDeployWin) {
      negaraPeacefulOutroActiveRef.current = true;
      if (negaraPeacefulBattleEndHandledRef.current) {
        return;
      }
      negaraPeacefulBattleEndHandledRef.current = true;
    }
    // Prevent multiple calls
    if (isTransitioningRef.current) {
      return;
    }
    // Set refs immediately to prevent race conditions
    isTransitioningRef.current = true;
    exitingRef.current = true;

    const runBattleEnd = async () => {
      const humanWon = didHumanPlayerWinGame(currentGameState, userId);
      const resolvedDidYouWin = humanWon || didYouWin;

      // Boss tactical loss: run stays open when darkness < 100% — retry the same boss instantly (no map).
      // When darkness reaches 100%, fall through to the loss screen + run rewards summary.
      let bossLossEclipseEndedRun = false;
      if (
        !resolvedDidYouWin &&
        currentGameState?.exploreUnknownMissionIndex != null &&
        currentGameState?.exploreUnknownRunId != null &&
        Array.isArray(EXPLORE_UNKNOWN_PORTAL_LAYOUT.bossMissionIndices) &&
        EXPLORE_UNKNOWN_PORTAL_LAYOUT.bossMissionIndices.includes(
          Number(currentGameState.exploreUnknownMissionIndex)
        )
      ) {
        const bossNoDarknessRetry =
          currentGameState.exploreUnknownRewriteBossRetryNoDarkness === true ||
          currentGameState.exploreUnknownEndgameFreeRetryNoDarkness === true;
        let runStateDataFromFetch = null;
        let continueRun = false;

        if (currentGameState.exploreUnknownDarknessLoss === true) {
          bossLossEclipseEndedRun = true;
        } else {
          try {
            const rawToken = localStorage.getItem('token');
            if (!rawToken) {
              throw new Error('BattleUI: token is required to verify Explore Unknown boss-loss outcome');
            }
            let tokenForRunCheck = rawToken;
            if (needsTokenRefresh(rawToken)) {
              const refreshTokenForRunCheck = localStorage.getItem('refreshToken');
              if (!refreshTokenForRunCheck) {
                throw new Error('BattleUI: refresh token is required when auth token needs refresh');
              }
              const refreshedToken = await refreshToken(refreshTokenForRunCheck);
              if (!refreshedToken) {
                throw new Error('BattleUI: refreshToken returned an empty token');
              }
              tokenForRunCheck = refreshedToken;
              localStorage.setItem('token', refreshedToken);
            }
            const runStateResponse = await fetch(`${API_URL}/explore-unknown/active-run`, {
              headers: { Authorization: `Bearer ${tokenForRunCheck}` },
            });
            runStateDataFromFetch = await runStateResponse.json().catch(() => ({}));
            if (!runStateResponse.ok) {
              throw new Error(
                runStateDataFromFetch?.error ||
                  `BattleUI: active-run check failed (${runStateResponse.status})`
              );
            }
            const runStillOpen =
              runStateDataFromFetch != null &&
              runStateDataFromFetch.runId != null &&
              Number(runStateDataFromFetch.runId) === Number(currentGameState.exploreUnknownRunId);
            if (!runStillOpen) {
              bossLossEclipseEndedRun = true;
            } else if (
              bossNoDarknessRetry ||
              currentGameState.exploreUnknownBossLossRetry === true
            ) {
              continueRun = true;
            } else {
              const gsDark = currentGameState.exploreUnknownRunDarknessPercent;
              const postBattleDark = runStateDataFromFetch.darknessPercent;
              if (
                typeof postBattleDark === 'number' &&
                !Number.isNaN(postBattleDark) &&
                typeof gsDark === 'number' &&
                !Number.isNaN(gsDark) &&
                postBattleDark > gsDark
              ) {
                continueRun = true;
              } else if (
                typeof postBattleDark === 'number' &&
                !Number.isNaN(postBattleDark) &&
                (typeof gsDark !== 'number' || Number.isNaN(gsDark))
              ) {
                continueRun = true;
              } else {
                continueRun = false;
                console.error(
                  'BattleUI: Explore Unknown boss-loss retry blocked — run is open but darkness did not increase',
                  { gsDark, postBattleDark, missionIndex: currentGameState.exploreUnknownMissionIndex }
                );
              }
            }
          } catch (err) {
            console.error('BattleUI: failed to verify Explore Unknown boss-loss outcome', err);
            if (currentGameState.exploreUnknownBossLossRetry === true) {
              continueRun = true;
            }
          }
        }
        if (continueRun) {
          for (const tid of bullyDamageStagingTimersRef.current) clearTimeout(tid);
          bullyDamageStagingTimersRef.current = [];
          bullyDamageGhostsRef.current.clear();
          bullyFlashInstanceIdsRef.current.clear();
          for (const el of bullyFistElementsRef.current) {
            if (el.parentNode) el.parentNode.removeChild(el);
          }
          bullyFistElementsRef.current = [];
          bullyAnimatedCountRef.current = 0;
          bullyDamagePendingInstanceIdsRef.current.clear();
          pendingBullyFistPlaybackRef.current = null;
          bullySequenceAnchorPrevStateRef.current = null;
          if (attackAnimationsRef.current?.reset) {
            attackAnimationsRef.current.reset();
          }
          if (deployTargetAnimationsRef.current?.reset) {
            deployTargetAnimationsRef.current.reset();
            registeredDeployVfxKeysRef.current.clear();
          }
          if (raidAnimationsRef.current?.reset) {
            raidAnimationsRef.current.reset();
          }
          setDamageAnimationTriggers(new Map());
          setLethalAttackFadeOverlays({});
          lethalDeathGhostsRef.current.clear();
          lethalDeathSequenceSlotsRef.current.clear();
          suppressRedeemSlotDeathInstanceIdsRef.current.clear();
          completedRedeemDeathInstanceIdsRef.current.clear();
          completedLethalDeathInstanceIdsRef.current.clear();
          redeemMarkedByInstanceIdRef.current.clear();
          redeemSpawnHeldInstanceIdsRef.current.clear();
          redeemDeathSequenceRef.current?.reset?.();
          battlefieldHealthCountdown.resetAll();
          pendingProjectileHealthHoldRef.current.clear();
          if (damageAnimationTriggeredRef.current) {
            damageAnimationTriggeredRef.current.clear();
          }
          completedDamageAnimationTriggerKeysRef.current.clear();
          attackSurvivorCountdownPlayedRef.current.clear();
          setExplosionEffects(new Map());
          setShowVictoryImage(false);
          setShowDefeatImage(false);
          setBattleEndTransition({ isTransitioning: false, type: null });
          const socket = socketToClose || ws;
          try {
            if (socket) socket.close();
          } catch {}
          const mem = currentGameState.exploreUnknownMemoryDarknessReductionTotal;
          if (typeof mem !== 'number' || Number.isNaN(mem)) {
            throw new Error(
              'BattleUI: exploreUnknownMemoryDarknessReductionTotal must be a number for EU boss-loss retry'
            );
          }
          let newDark = null;
          if (
            runStateDataFromFetch != null &&
            typeof runStateDataFromFetch.darknessPercent === 'number' &&
            !Number.isNaN(runStateDataFromFetch.darknessPercent)
          ) {
            newDark = runStateDataFromFetch.darknessPercent;
          } else if (
            typeof currentGameState.exploreUnknownRunDarknessPercent === 'number' &&
            !Number.isNaN(currentGameState.exploreUnknownRunDarknessPercent)
          ) {
            newDark = currentGameState.exploreUnknownRunDarknessPercent;
          } else {
            throw new Error(
              'BattleUI: exploreUnknownRunDarknessPercent (or active-run darknessPercent) required for EU boss-loss retry'
            );
          }
          let bossLossDarknessIncrement = EXPLORE_UNKNOWN_BOSS_LOSS_DARKNESS_INCREMENT_PERCENT;
          if (
            typeof currentGameState.exploreUnknownBossLossDarknessIncrementApplied === 'number' &&
            !Number.isNaN(currentGameState.exploreUnknownBossLossDarknessIncrementApplied)
          ) {
            bossLossDarknessIncrement = currentGameState.exploreUnknownBossLossDarknessIncrementApplied;
          } else {
            const altReduction =
              Number(currentGameState.exploreUnknownAltPathBossRetryDarknessReductionPercent) || 0;
            if (altReduction > 0) {
              bossLossDarknessIncrement =
                EXPLORE_UNKNOWN_BOSS_LOSS_DARKNESS_INCREMENT_PERCENT * (1 - altReduction / 100);
            }
          }
          const oldRun = bossNoDarknessRetry
            ? newDark
            : Math.max(0, newDark - bossLossDarknessIncrement);
          const fromPct = computeExploreUnknownDarknessLossFillFromPct(oldRun, mem);
          const toPct = computeExploreUnknownDarknessLossFillFromPct(newDark, mem);
          const bossLossMeterMs = 2200;
          if (!bossNoDarknessRetry) {
            setEuDarknessMeterFlash({ fromPct, toPct, key: Date.now(), durationMs: bossLossMeterMs });
          }
          const uid = userId;
          const numUid = uid != null ? parseInt(String(uid), 10) : NaN;
          const strategyFromState =
            (uid && currentGameState?.state?.[uid]?.strategy?.id) ??
            (!Number.isNaN(numUid) && currentGameState?.state?.[numUid]?.strategy?.id) ??
            (uid && currentGameState?.state?.[String(uid)]?.strategy?.id);
          if (strategyFromState == null || strategyFromState === '') {
            throw new Error('BattleUI: Explore Unknown boss-loss retry missing player strategy id on game state');
          }
          const retryAttempt = bumpExploreUnknownBossAttemptForRetry(
            Number(currentGameState.exploreUnknownRunId),
            Number(currentGameState.exploreUnknownMissionIndex)
          );
          const bossRetryNavState = {
            exploreUnknownBossRetryMissionIndex: Number(currentGameState.exploreUnknownMissionIndex),
            exploreUnknownBossRetryRunId: Number(currentGameState.exploreUnknownRunId),
            exploreUnknownBossRetryStrategyId: String(strategyFromState),
            exploreUnknownBossRetryAttempt: retryAttempt,
          };
          if (currentGameState.exploreUnknownDevDirectBossMission === true) {
            bossRetryNavState.exploreUnknownDevDirectBossMission = true;
          }
          window.setTimeout(() => {
            navigate('/explore-unknown/boss-retry', {
              replace: true,
              state: bossRetryNavState,
            });
          }, bossNoDarknessRetry ? 0 : bossLossMeterMs);
          return;
        }
      }

      // Rewrite Experience: non-boss tactical loss with a charge — retry same mission at +10% darkness.
      if (
        !resolvedDidYouWin &&
        currentGameState?.exploreUnknownMissionIndex != null &&
        currentGameState?.exploreUnknownRunId != null &&
        Array.isArray(EXPLORE_UNKNOWN_PORTAL_LAYOUT.bossMissionIndices) &&
        !EXPLORE_UNKNOWN_PORTAL_LAYOUT.bossMissionIndices.includes(
          Number(currentGameState.exploreUnknownMissionIndex)
        )
      ) {
        let continueRun = currentGameState.exploreUnknownRewriteExperienceLossRetry === true;
        let runStateDataFromFetch = null;
        if (!continueRun) {
          try {
            const rawToken = localStorage.getItem('token');
            if (!rawToken) {
              throw new Error('BattleUI: token is required to verify Explore Unknown rewrite-loss retry');
            }
            let tokenForRunCheck = rawToken;
            if (needsTokenRefresh(rawToken)) {
              const refreshTokenForRunCheck = localStorage.getItem('refreshToken');
              if (!refreshTokenForRunCheck) {
                throw new Error('BattleUI: refresh token is required when auth token needs refresh');
              }
              const refreshedToken = await refreshToken(refreshTokenForRunCheck);
              if (!refreshedToken) {
                throw new Error('BattleUI: refreshToken returned an empty token');
              }
              tokenForRunCheck = refreshedToken;
              localStorage.setItem('token', refreshedToken);
            }
            const runStateResponse = await fetch(`${API_URL}/explore-unknown/active-run`, {
              headers: { Authorization: `Bearer ${tokenForRunCheck}` },
            });
            runStateDataFromFetch = await runStateResponse.json().catch(() => ({}));
            if (!runStateResponse.ok) {
              throw new Error(
                runStateDataFromFetch?.error ||
                  `BattleUI: active-run check failed (${runStateResponse.status})`
              );
            }
            continueRun =
              runStateDataFromFetch != null &&
              runStateDataFromFetch.runId != null &&
              Number(runStateDataFromFetch.runId) === Number(currentGameState.exploreUnknownRunId);
          } catch (err) {
            console.error('BattleUI: failed to verify Explore Unknown rewrite-loss retry', err);
          }
        }
        if (continueRun) {
          for (const tid of bullyDamageStagingTimersRef.current) clearTimeout(tid);
          bullyDamageStagingTimersRef.current = [];
          bullyDamageGhostsRef.current.clear();
          bullyFlashInstanceIdsRef.current.clear();
          for (const el of bullyFistElementsRef.current) {
            if (el.parentNode) el.parentNode.removeChild(el);
          }
          bullyFistElementsRef.current = [];
          bullyAnimatedCountRef.current = 0;
          bullyDamagePendingInstanceIdsRef.current.clear();
          pendingBullyFistPlaybackRef.current = null;
          bullySequenceAnchorPrevStateRef.current = null;
          if (attackAnimationsRef.current?.reset) {
            attackAnimationsRef.current.reset();
          }
          if (deployTargetAnimationsRef.current?.reset) {
            deployTargetAnimationsRef.current.reset();
            registeredDeployVfxKeysRef.current.clear();
          }
          if (raidAnimationsRef.current?.reset) {
            raidAnimationsRef.current.reset();
          }
          setDamageAnimationTriggers(new Map());
          setLethalAttackFadeOverlays({});
          lethalDeathGhostsRef.current.clear();
          lethalDeathSequenceSlotsRef.current.clear();
          suppressRedeemSlotDeathInstanceIdsRef.current.clear();
          completedRedeemDeathInstanceIdsRef.current.clear();
          completedLethalDeathInstanceIdsRef.current.clear();
          redeemMarkedByInstanceIdRef.current.clear();
          redeemSpawnHeldInstanceIdsRef.current.clear();
          redeemDeathSequenceRef.current?.reset?.();
          battlefieldHealthCountdown.resetAll();
          pendingProjectileHealthHoldRef.current.clear();
          if (damageAnimationTriggeredRef.current) {
            damageAnimationTriggeredRef.current.clear();
          }
          completedDamageAnimationTriggerKeysRef.current.clear();
          attackSurvivorCountdownPlayedRef.current.clear();
          setExplosionEffects(new Map());
          setShowVictoryImage(false);
          setShowDefeatImage(false);
          const socket = socketToClose || ws;
          try {
            if (socket) socket.close();
          } catch {}
          const mem = currentGameState.exploreUnknownMemoryDarknessReductionTotal;
          if (typeof mem !== 'number' || Number.isNaN(mem)) {
            throw new Error(
              'BattleUI: exploreUnknownMemoryDarknessReductionTotal must be a number for EU rewrite-loss retry'
            );
          }
          let newDark = currentGameState.exploreUnknownRunDarknessPercent;
          if (typeof newDark !== 'number' || Number.isNaN(newDark)) {
            if (
              runStateDataFromFetch != null &&
              typeof runStateDataFromFetch.darknessPercent === 'number' &&
              !Number.isNaN(runStateDataFromFetch.darknessPercent)
            ) {
              newDark = runStateDataFromFetch.darknessPercent;
            } else {
              throw new Error(
                'BattleUI: exploreUnknownRunDarknessPercent (or active-run darknessPercent) required for EU rewrite-loss retry'
              );
            }
          }
          const oldRun = Math.max(
            0,
            newDark - EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_LOSS_DARKNESS_INCREMENT_PERCENT
          );
          const fromPct = computeExploreUnknownDarknessLossFillFromPct(oldRun, mem);
          const toPct = computeExploreUnknownDarknessLossFillFromPct(newDark, mem);
          setEuDarknessMeterFlash({ fromPct, toPct, key: Date.now(), durationMs: 2200 });
          const uid = userId;
          const numUid = uid != null ? parseInt(String(uid), 10) : NaN;
          const strategyFromState =
            (uid && currentGameState?.state?.[uid]?.strategy?.id) ??
            (!Number.isNaN(numUid) && currentGameState?.state?.[numUid]?.strategy?.id) ??
            (uid && currentGameState?.state?.[String(uid)]?.strategy?.id);
          if (strategyFromState == null || strategyFromState === '') {
            throw new Error('BattleUI: Explore Unknown rewrite-loss retry missing player strategy id on game state');
          }
          window.setTimeout(() => {
            isTransitioningRef.current = false;
            exitingRef.current = false;
            navigate('/explore-unknown', {
              replace: true,
              state: {
                resumeArrival: true,
                exploreUnknownCommittedStrategyId: String(strategyFromState),
                exploreUnknownRunId: currentGameState.exploreUnknownRunId,
                exploreUnknownMapRefresh: true,
              },
            });
          }, 2200);
          return;
        }
      }

      let exploreUnknownDarknessLoss =
        currentGameState?.exploreUnknownDarknessLoss === true || bossLossEclipseEndedRun;

      // Explore Unknown: a tactical win can still end the run as a loss when darkness reaches 100%.
      const skipActiveRunDarknessCheck =
        resolvedDidYouWin && Number(currentGameState?.exploreUnknownMissionIndex) === 75;

      if (
        !exploreUnknownDarknessLoss &&
        resolvedDidYouWin &&
        currentGameState?.exploreUnknownMissionIndex != null &&
        !skipActiveRunDarknessCheck
      ) {
        try {
          const rawToken = localStorage.getItem('token');
          if (!rawToken) {
            throw new Error('BattleUI: token is required to verify Explore Unknown run outcome');
          }

          let tokenForRunCheck = rawToken;
          if (needsTokenRefresh(rawToken)) {
            const refreshTokenForRunCheck = localStorage.getItem('refreshToken');
            if (!refreshTokenForRunCheck) {
              throw new Error('BattleUI: refresh token is required when auth token needs refresh');
            }
            const refreshedToken = await refreshToken(refreshTokenForRunCheck);
            if (!refreshedToken) {
              throw new Error('BattleUI: refreshToken returned an empty token');
            }
            tokenForRunCheck = refreshedToken;
            localStorage.setItem('token', refreshedToken);
          }

          const runStateResponse = await fetch(`${API_URL}/explore-unknown/active-run`, {
            headers: { Authorization: `Bearer ${tokenForRunCheck}` },
          });
          const runStateData = await runStateResponse.json().catch(() => ({}));
          if (!runStateResponse.ok) {
            throw new Error(
              runStateData?.error ||
                `BattleUI: active-run check failed (${runStateResponse.status})`
            );
          }
          exploreUnknownDarknessLoss = runStateData == null || runStateData.runId == null;
        } catch (err) {
          console.error('BattleUI: failed to verify Explore Unknown darkness-loss outcome', err);
        }
      }

      const effectiveWin = resolvedDidYouWin && !exploreUnknownDarknessLoss;
      if (
        effectiveWin &&
        currentGameState?.exploreUnknownRunId != null &&
        currentGameState?.exploreUnknownMissionIndex != null &&
        getExploreUnknownBossIntroTranslationKey(currentGameState.exploreUnknownMissionIndex) != null
      ) {
        clearExploreUnknownBossAttemptNumber(
          Number(currentGameState.exploreUnknownRunId),
          Number(currentGameState.exploreUnknownMissionIndex)
        );
      }
      const transitionType = effectiveWin ? 'win' : 'loss';

      for (const tid of bullyDamageStagingTimersRef.current) clearTimeout(tid);
      bullyDamageStagingTimersRef.current = [];
      bullyDamageGhostsRef.current.clear();
      bullyFlashInstanceIdsRef.current.clear();
      for (const el of bullyFistElementsRef.current) { if (el.parentNode) el.parentNode.removeChild(el); }
      bullyFistElementsRef.current = [];
      bullyAnimatedCountRef.current = 0;
      bullyDamagePendingInstanceIdsRef.current.clear();
      pendingBullyFistPlaybackRef.current = null;
      bullySequenceAnchorPrevStateRef.current = null;

      if (attackAnimationsRef.current?.reset) {
        attackAnimationsRef.current.reset();
      }
      if (deployTargetAnimationsRef.current?.reset) {
        deployTargetAnimationsRef.current.reset();
        registeredDeployVfxKeysRef.current.clear();
      }
      if (raidAnimationsRef.current?.reset) {
        raidAnimationsRef.current.reset();
      }

      setDamageAnimationTriggers(new Map());
      setLethalAttackFadeOverlays({});
      lethalDeathGhostsRef.current.clear();
      lethalDeathSequenceSlotsRef.current.clear();
      suppressRedeemSlotDeathInstanceIdsRef.current.clear();
      completedRedeemDeathInstanceIdsRef.current.clear();
      completedLethalDeathInstanceIdsRef.current.clear();
      redeemMarkedByInstanceIdRef.current.clear();
      redeemSpawnHeldInstanceIdsRef.current.clear();
      redeemDeathSequenceRef.current?.reset?.();
      battlefieldHealthCountdown.resetAll();
      pendingProjectileHealthHoldRef.current.clear();

      // Clear damage animation triggered tracking ref
      if (damageAnimationTriggeredRef.current) {
        damageAnimationTriggeredRef.current.clear();
      }
      completedDamageAnimationTriggerKeysRef.current.clear();
      attackSurvivorCountdownPlayedRef.current.clear();

      // Clear explosion effects
      setExplosionEffects(new Map());

      // Reset image visibility for a fresh transition
      setShowVictoryImage(false);
      setShowDefeatImage(false);

      const playBattleEndSound = (soundPath) => {
        if (isExploreUnknownKendrexColonyMissionIndex(currentGameState?.exploreUnknownMissionIndex)) {
          return;
        }
        const v = getEffectiveSoundOutputVolume(1);
        if (v === null) return;
        const audio = new Audio(soundPath);
        audio.volume = v;
        audio.load();
        battleEndSoundRef.current = audio;
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          playPromise.catch(err => {
            console.error('Error playing battle end sound:', err);
          });
        }
      };

      // Close socket (use provided socket or ws from state)
      const socket = socketToClose || ws;
      try {
        if (socket) socket.close();
      } catch {}

      // Prepare navigation state (include _triggerSource for redirect diagnostics)
      const navigationState = { result: transitionType, gameId, _triggerSource: triggerSource };
      if (isFriendlyMatch) {
        navigationState.isFriendlyMatch = true;
      }
      // Check if this is a tournament match from gameState
      if (currentGameState?.isTournamentMatch) {
        navigationState.isTournamentMatch = true;
        navigationState.gameState = currentGameState; // Pass gameState so AfterBattle can check it
      }
      // Check if this is an autobattler match
      if (currentGameState?.isAutobattler) {
        navigationState.isAutobattler = true;
        navigationState.gameState = currentGameState; // Pass gameState so AfterBattle can check it
      }
      // Check if this is a war effort battle (redirect back to war effort page)
      if (currentGameState?.warEffortMissionId || currentGameState?.isWarEffort) {
        navigationState.isWarEffort = true;
        navigationState.gameState = currentGameState;
      }
      // Check if this is a campaign battle (for tutorial reward choice)
      if (currentGameState?.campaignMode && currentGameState?.missionId) {
        navigationState.isCampaign = true;
        navigationState.missionId = currentGameState.missionId;
        navigationState.gameState = currentGameState;
      }
      if (currentGameState?.isEngageEnemyQueue === true) {
        navigationState.isEngageEnemyQueue = true;
      }
      if (currentGameState?.exploreUnknownMissionIndex != null) {
        navigationState.exploreUnknownRun = true;
        navigationState.gameState =
          exploreUnknownDarknessLoss && currentGameState.exploreUnknownDarknessLoss !== true
            ? { ...currentGameState, exploreUnknownDarknessLoss: true }
            : currentGameState;
        if (currentGameState.exploreUnknownXpGrant) {
          navigationState.exploreUnknownXpGrant = currentGameState.exploreUnknownXpGrant;
        }
        if (currentGameState.exploreUnknownRefinedErrayBossDrop) {
          navigationState.exploreUnknownRefinedErrayBossDrop =
            currentGameState.exploreUnknownRefinedErrayBossDrop;
        }
        const isNegaraHumanityCombatWin =
          effectiveWin &&
          Number(currentGameState.exploreUnknownMissionIndex) === 75 &&
          currentGameState.exploreUnknownNegaraPeacefulWin !== true;
        if (
          isNegaraHumanityCombatWin ||
          currentGameState.exploreUnknownReturnToExploreEnd === true
        ) {
          navigationState.exploreUnknownReturnToExploreEnd = true;
          navigationState.gameState = {
            ...currentGameState,
            exploreUnknownReturnToExploreEnd: true,
          };
        }
      }
      if (currentGameState && isTrainPvEBattle(currentGameState)) {
        navigationState.trainPvE = true;
        navigationState.gameState = currentGameState;
      }

      const isNegaraPeacefulWin =
        Number(currentGameState?.exploreUnknownMissionIndex) === 75 &&
        currentGameState?.exploreUnknownNegaraPeacefulWin === true;

      if (isNegaraPeacefulWin) {
        navigationState.exploreUnknownRunHomecoming = true;
        cancelAllDeploySoundsRef.current();
        pendingNegaraAfterBattleNavRef.current = navigationState;
        setShowNegaraPeacefulOutro(true);
        // Keep isTransitioningRef true so Navigate and duplicate handleBattleEnd do not fire.
        return;
      }

      const battleEndVideoRevealDelayMs = 2000;
      const battleEndVictorySectionTotalMs = 3000;
      const battleEndDefeatVideoOnScreenMs = 5000;

      const startTransition = () => {
        setBattleEndTransition({ isTransitioning: true, type: transitionType });
        if (effectiveWin) {
          // WIN: victory.mp3 + victory JPG (locale-specific) after white fade
          playBattleEndSound('/sounds/victory.mp3');
          setTimeout(() => {
            setShowVictoryImage(true);
          }, battleEndVideoRevealDelayMs);

          setTimeout(() => {
            navigate('/after-battle', { replace: true, state: navigationState });
          }, battleEndVictorySectionTotalMs);
        } else {
          // LOSS (or Explore Unknown darkness loss after a tactical win): loss mp3 + muted defeat video
          playBattleEndSound('/sounds/loss.mp3');
          setTimeout(() => {
            setShowDefeatImage(true);
          }, battleEndVideoRevealDelayMs);

          setTimeout(() => {
            navigate('/after-battle', { replace: true, state: navigationState });
          }, battleEndVideoRevealDelayMs + battleEndDefeatVideoOnScreenMs);
        }
      };

      if (exploreUnknownDarknessLoss && currentGameState?.exploreUnknownMissionIndex != null) {
        const runDarkness = currentGameState?.exploreUnknownRunDarknessPercent;
        const memoryReduction = currentGameState?.exploreUnknownMemoryDarknessReductionTotal;
        const fromPct = computeExploreUnknownDarknessLossFillFromPct(runDarkness, memoryReduction);
        setEuDarknessMeterFlash({ fromPct, toPct: 100, key: Date.now(), durationMs: 2300 });
        window.setTimeout(startTransition, 2300);
        return;
      }

      startTransition();
    };

    void runBattleEnd().catch((err) => {
      console.error('BattleUI: handleBattleEnd failed', err);
      isTransitioningRef.current = false;
      exitingRef.current = false;
    });
  }, [navigate, ws, gameState, location, userId]);

  // Sample fade color from the same battle-end asset shown for the current UI language
  useEffect(() => {
    const { winSrc, winIsVideo } = battleEndOverlayVisuals;

    const applySample = (ctx, w, h) => {
      try {
        const samplePoints = [
          [0, 0],
          [w - 1, 0],
          [0, h - 1],
          [w - 1, h - 1],
          [w / 2, 0],
          [w / 2, h - 1],
        ];
        let totalR = 0, totalG = 0, totalB = 0;
        for (const [x, y] of samplePoints) {
          const pixelData = ctx.getImageData(Math.floor(x), Math.floor(y), 1, 1).data;
          totalR += pixelData[0];
          totalG += pixelData[1];
          totalB += pixelData[2];
        }
        const avgR = Math.round(totalR / samplePoints.length);
        const avgG = Math.round(totalG / samplePoints.length);
        const avgB = Math.round(totalB / samplePoints.length);
        setVictoryImageWhite(`rgba(${avgR}, ${avgG}, ${avgB}, 1)`);
      } catch (error) {
        console.warn('⚠️ Could not sample victory overlay color, using default white:', error);
      }
    };

    if (!winIsVideo) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          applySample(ctx, img.width, img.height);
        } catch (error) {
          console.warn('⚠️ Could not sample victory image color, using default white:', error);
        }
      };
      img.onerror = () => {
        console.warn('⚠️ Could not load victory overlay image for color sampling, using default white');
      };
      img.src = winSrc;
      return () => {
        img.onload = null;
        img.onerror = null;
        img.src = '';
      };
    }

    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';

    const sampleFrame = () => {
      try {
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (!w || !h) return;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        canvas.width = w;
        canvas.height = h;
        ctx.drawImage(video, 0, 0);
        applySample(ctx, w, h);
      } catch (error) {
        console.warn('⚠️ Could not sample victory video color, using default white:', error);
      }
    };

    const onSeeked = () => {
      sampleFrame();
    };

    video.addEventListener('seeked', onSeeked);

    const onLoadedData = () => {
      const t = video.duration && !Number.isNaN(video.duration)
        ? Math.min(0.05, Math.max(0.001, video.duration * 0.001))
        : 0.05;
      video.currentTime = t;
    };

    video.addEventListener('loadeddata', onLoadedData);

    video.onerror = () => {
      console.warn('⚠️ Could not load victory video for color sampling, using default white');
    };

    video.src = winSrc;
    video.load();

    return () => {
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('loadeddata', onLoadedData);
      video.removeAttribute('src');
      video.load();
    };
  }, [battleEndOverlayVisuals.winSrc, battleEndOverlayVisuals.winIsVideo]);

  useEffect(() => {
    const { lossSrc, lossIsVideo } = battleEndOverlayVisuals;

    const applySample = (ctx, w, h) => {
      try {
        const samplePoints = [
          [0, 0],
          [w - 1, 0],
          [0, h - 1],
          [w - 1, h - 1],
          [w / 2, 0],
          [w / 2, h - 1],
        ];
        let totalR = 0, totalG = 0, totalB = 0;
        for (const [x, y] of samplePoints) {
          const pixelData = ctx.getImageData(Math.floor(x), Math.floor(y), 1, 1).data;
          totalR += pixelData[0];
          totalG += pixelData[1];
          totalB += pixelData[2];
        }
        const avgR = Math.round(totalR / samplePoints.length);
        const avgG = Math.round(totalG / samplePoints.length);
        const avgB = Math.round(totalB / samplePoints.length);
        setDefeatImageGray(`rgba(${avgR}, ${avgG}, ${avgB}, 1)`);
      } catch (error) {
        console.warn('⚠️ Could not sample defeat overlay color, using default black:', error);
      }
    };

    if (!lossIsVideo) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          applySample(ctx, img.width, img.height);
        } catch (error) {
          console.warn('⚠️ Could not sample defeat image color, using default black:', error);
        }
      };
      img.onerror = () => {
        console.warn('⚠️ Could not load defeat overlay image for color sampling, using default black');
      };
      img.src = lossSrc;
      return () => {
        img.onload = null;
        img.onerror = null;
        img.src = '';
      };
    }

    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';

    const sampleFrame = () => {
      try {
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (!w || !h) return;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        canvas.width = w;
        canvas.height = h;
        ctx.drawImage(video, 0, 0);
        applySample(ctx, w, h);
      } catch (error) {
        console.warn('⚠️ Could not sample defeat video color, using default black:', error);
      }
    };

    const onSeeked = () => {
      sampleFrame();
    };

    video.addEventListener('seeked', onSeeked);

    const onLoadedData = () => {
      const t = video.duration && !Number.isNaN(video.duration)
        ? Math.min(0.05, Math.max(0.001, video.duration * 0.001))
        : 0.05;
      video.currentTime = t;
    };

    video.addEventListener('loadeddata', onLoadedData);

    video.onerror = () => {
      console.warn('⚠️ Could not load defeat video for color sampling, using default black');
    };

    video.src = lossSrc;
    video.load();

    return () => {
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('loadeddata', onLoadedData);
      video.removeAttribute('src');
      video.load();
    };
  }, [battleEndOverlayVisuals.lossSrc, battleEndOverlayVisuals.lossIsVideo]);

  // Load player avatars from game state when available
  useEffect(() => {
    // Use activeUserId for spectator mode (watched player) or userId for normal mode
    const effectiveUserIdForAvatars = isSpectating && spectatingPlayerId ? spectatingPlayerId : userId;
    const effectiveGameStateForAvatars = isSpectating && spectatingGameState ? spectatingGameState : gameState;
    
    if (effectiveGameStateForAvatars && effectiveUserIdForAvatars) {
      // In spectator mode, use the watched player's avatars
      const playerAvatars = effectiveGameStateForAvatars.playerAvatars?.[effectiveUserIdForAvatars] || {};
      if (playerAvatars.general || playerAvatars.advisor) {
        if (playerAvatars.general) setSelectedGeneral(playerAvatars.general);
        if (playerAvatars.advisor) setSelectedAdvisor(playerAvatars.advisor);
      }
      
      // Track preparation bonus usage from game state
      const player = effectiveGameStateForAvatars.state?.[effectiveUserIdForAvatars];
      if (player) {
        let newPreparationBonusUsed = player.preparationBonusUsed || false;
        if (preparationBonus === 'black_market') {
          const maxUses = exploreUnknownBlackMarketMaxUses(effectiveGameStateForAvatars);
          const used = Number(player.blackMarketUsesThisBattle) || 0;
          newPreparationBonusUsed = used >= maxUses;
        } else if (preparationBonus === 'plan_ahead') {
          const maxUses = exploreUnknownPlanAheadMaxUses(effectiveGameStateForAvatars);
          const used = Number(player.planAheadUsesThisBattle) || 0;
          newPreparationBonusUsed = used >= maxUses;
        }
        if (newPreparationBonusUsed !== preparationBonusUsed) {
          setPreparationBonusUsed(newPreparationBonusUsed);
        }
        if (preparationBonusPendingRef.current) {
          if (preparationBonus === 'black_market') {
            const maxUses = exploreUnknownBlackMarketMaxUses(effectiveGameStateForAvatars);
            const used = Number(player.blackMarketUsesThisBattle) || 0;
            if (used > 0) {
              preparationBonusPendingRef.current = false;
            }
          } else if (preparationBonus === 'plan_ahead') {
            const maxUses = exploreUnknownPlanAheadMaxUses(effectiveGameStateForAvatars);
            const used = Number(player.planAheadUsesThisBattle) || 0;
            if (used > 0) {
              preparationBonusPendingRef.current = false;
            }
          } else if (newPreparationBonusUsed) {
            preparationBonusPendingRef.current = false;
          }
        }
      }
      
      // Load opponent's preparation bonus from game state
      const opponentId = effectiveGameStateForAvatars.players?.find(id => String(id) !== String(effectiveUserIdForAvatars));
      if (opponentId) {
        // In spectator mode, opponent prep bonus is keyed by the watched player's ID
        const opponentPrepBonus = effectiveGameStateForAvatars.opponentPreparationBonuses?.[effectiveUserIdForAvatars];
        if (opponentPrepBonus !== opponentPreparationBonus) {
          setOpponentPreparationBonus(opponentPrepBonus || null);
        }
        
        // Track opponent's preparation bonus usage
        const opponent = effectiveGameStateForAvatars.state?.[opponentId];
        if (opponent) {
          const newOpponentPrepBonusUsed = opponent.preparationBonusUsed || false;
          setOpponentPreparationBonusUsed(newOpponentPrepBonusUsed);
        } else {
          // If opponent state doesn't exist, reset to false
          setOpponentPreparationBonusUsed(false);
        }
        
        // Load opponent's diplomatic accord from game state
        const opponentDiplomaticAccordValue = effectiveGameStateForAvatars.opponentDiplomaticAccords?.[effectiveUserIdForAvatars];
        if (opponentDiplomaticAccordValue !== opponentDiplomaticAccord) {
          setOpponentDiplomaticAccord(opponentDiplomaticAccordValue || null);
        }
      }
      
      const isExploreUnknownBattle = effectiveGameStateForAvatars.exploreUnknownMissionIndex != null;
      const playerPrepBonusValue = effectiveGameStateForAvatars.playerPreparationBonuses?.[effectiveUserIdForAvatars];
      const normalizedPrepBonus =
        playerPrepBonusValue === 'team_leader_meeting' ? 'team_leader' : playerPrepBonusValue;
      if (isExploreUnknownBattle) {
        if (normalizedPrepBonus !== preparationBonus) {
          setPreparationBonus(normalizedPrepBonus ?? null);
        }
      } else if (playerPrepBonusValue !== undefined) {
        if (normalizedPrepBonus !== preparationBonus) {
          setPreparationBonus(normalizedPrepBonus || null);
        }
      }

      const playerDiplomaticAccordValue = effectiveGameStateForAvatars.playerDiplomaticAccords?.[effectiveUserIdForAvatars];
      const normalizedAccords = normalizeDiplomaticAccordsList(playerDiplomaticAccordValue);
      const accordForCompare = Array.isArray(playerDiplomaticAccordValue)
        ? normalizedAccords
        : (playerDiplomaticAccordValue ?? null);
      if (isExploreUnknownBattle) {
        const currentCompare = Array.isArray(diplomaticAccord)
          ? diplomaticAccord
          : (diplomaticAccord ?? null);
        if (JSON.stringify(accordForCompare) !== JSON.stringify(currentCompare)) {
          setDiplomaticAccord(accordForCompare);
        }
      } else if (playerDiplomaticAccordValue !== undefined && playerDiplomaticAccordValue !== diplomaticAccord) {
        setDiplomaticAccord(playerDiplomaticAccordValue || null);
      }
    }
  }, [gameState, spectatingGameState, userId, spectatingPlayerId, isSpectating, preparationBonusUsed, opponentPreparationBonus, opponentDiplomaticAccord, preparationBonus]);

  // Fetch player ranks when game state is available
  useEffect(() => {
    const fetchPlayerRanks = async () => {
      if (!gameState || !userId) return;
      
      const effectiveUserId = isSpectating && spectatingPlayerId ? spectatingPlayerId : userId;
      const effectiveGameState = isSpectating && spectatingGameState ? spectatingGameState : gameState;
      
      const opponentId = effectiveGameState.players?.find(id => id !== effectiveUserId);
      if (!opponentId) return;
      
      // Don't fetch ranks if opponent is a bot
      const isBot = opponentId === 'bot';
      
      const userIdsToFetch = [effectiveUserId];
      if (!isBot) {
        userIdsToFetch.push(opponentId);
      }
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/player-ranks`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ userIds: userIdsToFetch })
        });
        
        if (response.ok) {
          const ranks = await response.json();
          
          // Compare IDs as strings to handle both string and number formats
          const playerRankData = ranks.find(r => String(r.id) === String(effectiveUserId));
          const opponentRankData = !isBot ? ranks.find(r => String(r.id) === String(opponentId)) : null;
          
          // Convert rank to number if it exists
          const playerRankNum = playerRankData?.rank ? Number(playerRankData.rank) : null;
          const opponentRankNum = opponentRankData?.rank ? Number(opponentRankData.rank) : null;
          
          setPlayerRank(playerRankNum);
          setPlayerPvpLadder(
            playerRankData
              ? {
                  pvp_ladder_rank_index: playerRankData.pvp_ladder_rank_index,
                  pvp_ladder_stars: playerRankData.pvp_ladder_stars,
                  pvp_in_league: playerRankData.pvp_in_league
                }
              : null
          );
          setOpponentRank(opponentRankNum);
          setOpponentPvpLadder(
            opponentRankData
              ? {
                  pvp_ladder_rank_index: opponentRankData.pvp_ladder_rank_index,
                  pvp_ladder_stars: opponentRankData.pvp_ladder_stars,
                  pvp_in_league: opponentRankData.pvp_in_league,
                }
              : null
          );
        } else {
          console.error('🏆 Failed to fetch ranks:', response.status, response.statusText);
          const errorText = await response.text();
          console.error('🏆 Error response:', errorText);
          setPlayerPvpLadder(null);
          setOpponentPvpLadder(null);
        }
      } catch (error) {
        console.error('🏆 Error fetching player ranks:', error);
        setPlayerPvpLadder(null);
        setOpponentPvpLadder(null);
      }
    };
    
    fetchPlayerRanks();
  }, [gameState, spectatingGameState, userId, spectatingPlayerId, isSpectating]);

  // Fetch opponent username
  useEffect(() => {
    const fetchOpponentUsername = async () => {
      const effectiveUserId = isSpectating && spectatingPlayerId ? spectatingPlayerId : userId;
      const effectiveGameState = isSpectating && spectatingGameState ? spectatingGameState : gameState;
      
      if (!effectiveGameState || !effectiveUserId) {
        setOpponentUsername(null);
        return;
      }
      
      const opponentId = effectiveGameState.players?.find(id => String(id) !== String(effectiveUserId));
      if (!opponentId) {
        setOpponentUsername(null);
        return;
      }
      
      // Check if opponent is a bot
      const isBot = opponentId === 'bot' || String(opponentId).includes('bot');
      
      if (isBot) {
        setOpponentUsername('Bot');
        return;
      }
      
      // Fetch username from player-ranks endpoint (which also returns usernames)
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/player-ranks`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ userIds: [opponentId] })
        });
        
        if (response.ok) {
          const ranks = await response.json();
          const opponentData = ranks.find(r => String(r.id) === String(opponentId));
          if (opponentData && opponentData.username) {
            setOpponentUsername(opponentData.username);
          } else {
            setOpponentUsername('Opponent');
          }
        } else {
          setOpponentUsername('Opponent');
        }
      } catch (error) {
        console.error('Error fetching opponent username:', error);
        setOpponentUsername('Opponent');
      }
    };

    fetchOpponentUsername();
  }, [gameState, userId, isSpectating, spectatingPlayerId, spectatingGameState]);

  // Avatar helper functions moved to ./avatars/avatarManager.js
  
  // Helper function to get rank styling based on rank tier
  const getRankStyle = (rank) => {
    if (!rank) return {};
    
    if (rank <= 10) {
      return {
        color: '#FFD700',
        textShadow: '0 0 10px #FFD700, 0 0 20px #FFD700, 0 0 30px #FFD700',
        fontWeight: 'bold'
      };
    } else if (rank <= 50) {
      return {
        color: '#C0C0C0',
        textShadow: '0 0 10px #C0C0C0, 0 0 20px #C0C0C0, 0 0 30px #C0C0C0',
        fontWeight: 'bold'
      };
    } else if (rank <= 100) {
      return {
        color: '#CD7F32',
        textShadow: '0 0 10px #CD7F32, 0 0 20px #CD7F32, 0 0 30px #CD7F32',
        fontWeight: 'bold'
      };
    } else if (rank <= 500) {
      return {
        color: '#FFFFFF',
        textShadow: '0 0 10px #FFFFFF, 0 0 20px #FFFFFF',
        fontWeight: 'bold'
      };
    } else {
      return {
        color: '#FFFFFF',
        fontWeight: 'bold'
      };
    }
  };
  
  // NEW: For attack selection
  const [selectedAttacker, setSelectedAttacker] = useState(null); // {zone, idx}
  const [attackMode, setAttackMode] = useState(false);
  const [lastAttackedTarget, setLastAttackedTarget] = useState(null); // Track last attacked target {zone, idx} to keep expand disabled until mouse leaves
  const attackInProgressRef = useRef(false); // Prevent double-click attacks (using ref for synchronous access)
  const pendingAttackInfoRef = useRef(null); // Store pending attack info for animation (only set if attack succeeds)
  const battleKeyboardShortcutsRef = useRef(null); // Space / G / P / R + resign dialog: listener reads this each keypress
  // NEW: For drag-and-drop from carousel
  const [draggedOrderId, setDraggedOrderId] = useState(null);
  const [draggedOrderInstanceId, setDraggedOrderInstanceId] = useState(null);
  // Desktop drag-to-deploy: custom mouse drag (no HTML5 DnD), red line only, drop by half-screen
  const [dragDeployState, setDragDeployState] = useState(null); // { order, originX, originY, currentX, currentY }
  const mouseDragPendingRef = useRef(null); // { order, startX, startY } while tracking
  const clickHandledByMouseupRef = useRef(false); // so card onClick doesn't double-fire
  const dragStartedRef = useRef(false); // true once we've shown the line (moved past threshold)
  // Strategem drag-and-drop: store intended target when we play strategem via drag so we can send it when backend sets needsStrategemTarget
  const pendingStrategemDropTargetRef = useRef(null); // { targetType, targetZone, targetIndex, targetUnitId, targetInstanceId, generalPlayer? }
  const [strategemDragState, setStrategemDragState] = useState(null); // { order, originX, originY, currentX, currentY } during strategem drag
  const strategemDragStartedRef = useRef(false);
  const strategemMouseDragPendingRef = useRef(null); // { order, startX, startY } while tracking strategem drag
  const [weaponDragState, setWeaponDragState] = useState(null); // { order, originX, originY, currentX, currentY } during weapon drag
  const weaponDragStartedRef = useRef(false);
  const weaponMouseDragPendingRef = useRef(null); // { order, startX, startY } while tracking weapon drag
  const [droneDragState, setDroneDragState] = useState(null); // { order, originX, originY, currentX, currentY } during drone drag
  const droneDragStartedRef = useRef(false);
  const droneMouseDragPendingRef = useRef(null); // { order, startX, startY } while tracking drone drag
  const [pendingDroneDropCoords, setPendingDroneDropCoords] = useState(null); // { clientX, clientY, orderId } after drop to call playDrone
  const [pendingDeployFullZoneDrop, setPendingDeployFullZoneDrop] = useState(null); // { clientX, clientY } when deploy drop on full zone → synthetic click to enter replace mode
  const [attackDragState, setAttackDragState] = useState(null); // { attacker: { zone, idx }, originX, originY, currentX, currentY } during attack drag from friendly unit
  const attackDragStartedRef = useRef(false);
  const attackMouseDragPendingRef = useRef(null); // { attacker: { zone, idx }, startX, startY } while tracking attack drag
  const [pendingAttackDropCoords, setPendingAttackDropCoords] = useState(null); // { clientX, clientY } after drop → set attacker + synthetic click on target
  const [droneTargetDragState, setDroneTargetDragState] = useState(null); // { drone: { id, instanceId, index }, originX, originY, currentX, currentY } when dragging deployed drone (with charges) to target
  const droneTargetDragStartedRef = useRef(false);
  const droneTargetMouseDragPendingRef = useRef(null); // { drone, startX, startY } while tracking drone target drag
  const [pendingDroneTargetDropCoords, setPendingDroneTargetDropCoords] = useState(null); // { clientX, clientY } after drop → set drone + drone target mode + synthetic click on target
  const [reconnecting, setReconnecting] = useState(false);
  const reconnectingRef = useRef(false); // Track reconnecting state to avoid stale closures
  const reconnectingTimeoutRef = useRef(null); // Track timeout for clearing stuck reconnecting state
  // Temporary message state for "feeling tired" message
  const [temporaryMessage, setTemporaryMessage] = useState(null);
  const [stateSyncMonitorEnabled, setStateSyncMonitorEnabled] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  // Cache for orders fetched from database for tooltips
  const [orderDBCache, setOrderDBCache] = useState({});
  // ✅ Action queuing — buffer deploy intents while state is applying (no "please wait" errors)
  const actionQueueRef = useRef([]);
  const isProcessingActionRef = useRef(false);

  const isBattleActionBusy = () =>
    isStateUpdatingRef.current || isStateStabilizingRef.current;

  const isActionLockErrorMessage = (message) => {
    if (!message) return false;
    const lower = message.toLowerCase();
    return lower.includes('another action is in progress') || lower.includes('action in progress');
  };

  const orderMatchesPlayIntent = (order, orderId, instanceId) => {
    if (!order) return false;
    return instanceId ? order.instanceId === instanceId : order.id === orderId;
  };

  const findPlayOrderLocation = (player, orderId, instanceId) => {
    if (!player) return 'not_found';
    if (player.hand?.some((o) => orderMatchesPlayIntent(o, orderId, instanceId))) return 'hand';
    if (player.deck?.some((o) => orderMatchesPlayIntent(o, orderId, instanceId))) return 'deck';
    if (player.completedOrders?.some((o) => orderMatchesPlayIntent(o, orderId, instanceId))) return 'completedOrders';
    if (player.discard?.some((o) => orderMatchesPlayIntent(o, orderId, instanceId))) return 'discard';
    for (const zone of ['front', 'flank']) {
      if (player.playedOrders?.[zone]?.some((o) => orderMatchesPlayIntent(o, orderId, instanceId))) {
        return 'battlefield';
      }
    }
    if (player.drones?.some((o) => orderMatchesPlayIntent(o, orderId, instanceId))) return 'drones';
    return 'not_found';
  };

  const isStaleQueuedPlayOrderIntent = (intent, player) => {
    if (!intent || intent.type !== 'playOrder' || !player) return false;
    const location = findPlayOrderLocation(player, intent.orderId, intent.instanceId);
    return location === 'battlefield' || location === 'drones' || location === 'completedOrders';
  };

  const pruneStalePlayOrderIntents = (player) => {
    if (!player || actionQueueRef.current.length === 0) return;
    actionQueueRef.current = actionQueueRef.current.filter(
      (intent) => !isStaleQueuedPlayOrderIntent(intent, player)
    );
  };

  const pruneQueuedPlayOrderForInstance = (instanceId) => {
    if (!instanceId) return;
    actionQueueRef.current = actionQueueRef.current.filter(
      (intent) => !(intent.type === 'playOrder' && intent.instanceId === instanceId)
    );
  };

  const enqueuePlayOrderIntent = ({ orderId, zone, targetInfo = null, instanceId = null }) => {
    if (instanceId) {
      pruneQueuedPlayOrderForInstance(instanceId);
    }
    if (actionQueueRef.current.length >= ACTION_QUEUE_MAX) {
      actionQueueRef.current.shift();
    }
    actionQueueRef.current.push({ type: 'playOrder', orderId, zone, targetInfo, instanceId });
  };

  const drainActionQueue = () => {
    if (isProcessingActionRef.current || actionQueueRef.current.length === 0) {
      return;
    }
    if (isBattleActionBusy()) {
      return;
    }
    const currentState = getCurrentState() || gameState;
    const player = currentState?.state?.[userId];
    pruneStalePlayOrderIntents(player);
    if (actionQueueRef.current.length === 0) {
      return;
    }
    const nextAction = actionQueueRef.current.shift();
    if (isStaleQueuedPlayOrderIntent(nextAction, player)) {
      drainActionQueue();
      return;
    }
    isProcessingActionRef.current = true;
    try {
      if (nextAction.type === 'playOrder' && playOrderRef.current) {
        playOrderRef.current(
          nextAction.orderId,
          nextAction.zone,
          nextAction.targetInfo ?? null,
          nextAction.instanceId ?? null
        );
      }
    } finally {
      isProcessingActionRef.current = false;
      if (actionQueueRef.current.length > 0 && !isBattleActionBusy()) {
        drainActionQueue();
      }
    }
  };

  const scheduleDrainActionQueue = () => {
    let polls = 0;
    const tryDrain = () => {
      if (actionQueueRef.current.length === 0) {
        return;
      }
      if (isBattleActionBusy()) {
        polls += 1;
        if (polls < STATE_BUSY_MAX_POLLS) {
          setTimeout(tryDrain, STATE_BUSY_POLL_MS);
        }
        return;
      }
      drainActionQueue();
    };
    tryDrain();
  };
  
  // Fetch orders from database for tooltips when hovering (same method as deployment)
  useEffect(() => {
    if (hoveredIndex !== null && gameState?.state?.[userId]?.hand) {
      const hand = gameState.state[userId].hand;
      const hoveredOrder = hand[hoveredIndex];
      if (hoveredOrder) {
        const deployedUnitInfo = getDeployedUnitInfo(hoveredOrder.id);
        if (deployedUnitInfo && deployedUnitInfo.id && !orderDBCache[deployedUnitInfo.id]) {
          const fetchOrder = async () => {
            try {
              // Use the same endpoint as the backend uses for fetching orders (same as deployment)
              const response = await fetch(`${API_URL}/order/${deployedUnitInfo.id}`, {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              });
              if (response.ok) {
                const orderData = await response.json();
                // Use the order data directly from DB (includes type, faction_name, description)
                setOrderDBCache(prev => ({ ...prev, [deployedUnitInfo.id]: orderData }));
              }
            } catch (error) {
              console.warn(`Failed to fetch order ${deployedUnitInfo.id} from database:`, error);
            }
          };
          fetchOrder();
        }
      }
    }
  }, [hoveredIndex, token, orderDBCache, gameState, userId]);
  // ✅ REFACTORED: lastStateVersionRef now comes from useGameStateSync hook
  const [hoveredPlayed, setHoveredPlayed] = useState({ zone: null, idx: null });
  /** Pinned expanded view for an opponent unit (orange border); no gameplay action. Cleared when any targeting/attack mode is active. */
  const [opponentInspectSelection, setOpponentInspectSelection] = useState(null);
  const lastPointerPosRef = useRef({ x: null, y: null });
  const [opponentLookingAtInstanceId, setOpponentLookingAtInstanceId] = useState(null);
  const isProtectUnit = useCallback((order) => {
    if (!order) return false;
    const orderType = order.type;
    if (orderType === 'Weapon' || orderType === 'Drone' || orderType === 'Strategem' || orderType === 'Stratagem') {
      return false;
    }
    const abilities = order.hasInstanceModifications && Array.isArray(order.instanceAbilities)
      ? order.instanceAbilities
      : Array.isArray(order.abilities)
        ? order.abilities
        : typeof order.ability === 'string'
          ? [order.ability]
          : [];
    return abilities.includes('Protect') || order.displayAsProtectUnit === true;
  }, []);
  // ✅ FIXED: Use useRef for timeout to prevent race conditions with state updates
  const hoverTimeoutRef = useRef(null);
  // Completed orders modal state
  const [showCompletedOrders, setShowCompletedOrders] = useState(false);
  const [hoveredCompletedOrder, setHoveredCompletedOrder] = useState(null);
  
  // Emote selection state
  const [showEmoteButtons, setShowEmoteButtons] = useState(false);
  const [playerEmotes, setPlayerEmotes] = useState([]);
  const [playerEmoteSelections, setPlayerEmoteSelections] = useState({});
  const [emotesLoading, setEmotesLoading] = useState(true); // ✅ FIX: Track loading state
  const [emotesError, setEmotesError] = useState(null); // ✅ FIX: Track error state
  const [currentEmoteBubble, setCurrentEmoteBubble] = useState(null); // { emoteKey?: string, text?: string, timestamp: number }
  const [opponentEmoteBubble, setOpponentEmoteBubble] = useState(null); // { emoteKey?: string, text?: string, timestamp: number }
  const [advisorEmoteBubble, setAdvisorEmoteBubble] = useState(null); // { text: string, timestamp: number }
  const mission5IntroEmoteShownForGameRef = useRef(null);
  const mission5DialogueTimersRef = useRef([]);
  const mission6IntroEmoteShownForGameRef = useRef(null);
  const mission6DialogueTimersRef = useRef([]);

  // Safety net: never let any dialogue bubble stay forever.
  useEffect(() => {
    const timeoutIds = [];
    if (currentEmoteBubble?.timestamp) {
      const ts = currentEmoteBubble.timestamp;
      timeoutIds.push(setTimeout(() => {
        setCurrentEmoteBubble((prev) => (prev?.timestamp === ts ? null : prev));
      }, 10000));
    }
    if (advisorEmoteBubble?.timestamp) {
      const ts = advisorEmoteBubble.timestamp;
      timeoutIds.push(setTimeout(() => {
        setAdvisorEmoteBubble((prev) => (prev?.timestamp === ts ? null : prev));
      }, 10000));
    }
    if (opponentEmoteBubble?.timestamp) {
      const ts = opponentEmoteBubble.timestamp;
      timeoutIds.push(setTimeout(() => {
        setOpponentEmoteBubble((prev) => (prev?.timestamp === ts ? null : prev));
      }, 10000));
    }

    return () => {
      for (const id of timeoutIds) clearTimeout(id);
    };
  }, [currentEmoteBubble, advisorEmoteBubble, opponentEmoteBubble]);
  
  // Notify opponent when player hovers over an order (for "looking at" glow on their view)
  const sendOrderHover = useCallback((instanceId) => {
    const s = ws || currentSocketRef.current;
    if (s && gameId && s.readyState === 1) {
      s.send(JSON.stringify({ type: 'order-hover', gameId, instanceId: instanceId || null }));
    }
  }, [ws, gameId]);

  const clearHoveredPlayedSlot = useCallback((zone, idx) => {
    setHoveredPlayed((prev) =>
      prev.zone === zone && prev.idx === idx ? { zone: null, idx: null } : prev
    );
    sendOrderHover(null);
  }, [sendOrderHover]);

  // Desktop safety net: if pointer is no longer over a real source order, clear hover state
  // even when no additional mousemove events arrive (prevents stuck expanded cards).
  useEffect(() => {
    if (isContainerMobile) return undefined;
    if (opponentInspectSelection) return undefined;

    const hasAnyHover = hoveredIndex !== null || hoveredPlayed?.zone !== null;
    if (!hasAnyHover) return undefined;

    const clearHover = () => {
      setHoveredIndex(null);
      setHoveredPlayed({ zone: null, idx: null });
      sendOrderHover(null);
    };

    const isPointerOnHoverSource = (x, y) => {
      // Most reliable: actual CSS hover state on source elements (slots/hand only — not the
      // fixed lower-right preview portal, which is a consequence of hover/selection).
      const hoveredSource = document.querySelector(
        '.carousel-order-card:hover, [data-slot-zone][data-unit-key]:hover, [data-coda-ability-tooltip-root]:hover'
      );
      if (hoveredSource) return true;

      // Fallback: pointer-based hit test.
      if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
      const el = document.elementFromPoint(x, y);
      if (!el) return false;

      // Hand carousel, battlefield slots, ability tooltips on those slots.
      return Boolean(
        el.closest('.carousel-order-card') ||
        el.closest('[data-slot-zone][data-unit-key]') ||
        el.closest('[data-coda-ability-tooltip-root]')
      );
    };

    const hoverStartedAt = Date.now();

    const evaluateAtLastPointer = () => {
      if (isTurnStartOverlayVisible()) return;
      // Grace period prevents false clears during rapid enter/leave transitions.
      if (Date.now() - hoverStartedAt < 180) return;
      const { x, y } = lastPointerPosRef.current || {};
      if (!isPointerOnHoverSource(x, y)) {
        clearHover();
      }
    };

    const onPointerMove = (ev) => {
      if (isTurnStartOverlayVisible()) return;
      lastPointerPosRef.current = { x: ev.clientX, y: ev.clientY };
    };

    const onWindowBlur = () => {
      clearHover();
    };

    // Catch stale state immediately and then keep sampling while hover is active.
    evaluateAtLastPointer();
    const watchdogId = window.setInterval(evaluateAtLastPointer, 120);

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('blur', onWindowBlur);
    return () => {
      window.clearInterval(watchdogId);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('blur', onWindowBlur);
    };
  }, [hoveredIndex, hoveredPlayed, isContainerMobile, sendOrderHover, opponentInspectSelection]);

  // Close emote buttons when clicking outside
  useEffect(() => {
    if (!showEmoteButtons) return;
    
    const handleClickOutside = (e) => {
      // Check if click is outside the emote buttons container
      const emoteContainer = document.querySelector('[data-emote-buttons]');
      if (emoteContainer && !emoteContainer.contains(e.target)) {
        // Also check if click is not on the general image
        const generalImage = document.querySelector('[data-zone="player_general"]');
        if (generalImage && !generalImage.contains(e.target)) {
          setShowEmoteButtons(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showEmoteButtons]);

  // Mission 5 dialogue via emote bubble system (show at start of player's second turn).
  useEffect(() => {
    if (gameState?.missionId !== 'chapter_1_mission_5' || !gameId || !userId) {
      return;
    }
    const isPlayersTurn = String(gameState?.turn) === String(userId);
    const turnsTaken = Number(gameState?.state?.[userId]?.turnsTaken || 0);
    if (!isPlayersTurn || turnsTaken !== 2) {
      return;
    }
    if (mission5IntroEmoteShownForGameRef.current === gameId) {
      return;
    }
    mission5IntroEmoteShownForGameRef.current = gameId;

    const gapMs = 250;
    const dialogue = [
      { speaker: 'general', text: "I shouldn't go too deep into the cornfield-it'll be harder to see anything, and fighting would be impossible.", sound: '/sounds/david_m5_1.mp3', durationMs: 6200 },
      { speaker: 'advisor', text: 'Maybe you should take your shirt off to fight them even harder', sound: '/sounds/isabela_m5_1.mp3', durationMs: 3200 },
      { speaker: 'general', text: 'Isabela! This is serious.', sound: '/sounds/david_m5_2.mp3', durationMs: 2800 },
      { speaker: 'advisor', text: 'I am serious', sound: '/sounds/isabela_m5_2.mp3', durationMs: 2600 },
      { speaker: 'general', text: 'Well then. You first', sound: '/sounds/david_m5_3.mp3', durationMs: 2800 },
      { speaker: 'advisor', text: 'Then you actually would fight harder', sound: '/sounds/isabela_m5_3.mp3', durationMs: 3200 }
    ];

    // Clear any old timers before scheduling.
    for (const timerId of mission5DialogueTimersRef.current) {
      clearTimeout(timerId);
    }
    mission5DialogueTimersRef.current = [];

    let cursorMs = 0;
    dialogue.forEach((line) => {
      const lineDuration = Number(line.durationMs) > 0 ? Number(line.durationMs) : 3200;
      const startAt = cursorMs;
      const showTimer = setTimeout(() => {
        setCurrentEmoteBubble(null);
        setAdvisorEmoteBubble(null);
        playSoundEffect(line.sound, 0.85);
        const payload = { text: line.text, timestamp: Date.now() };
        if (line.speaker === 'general') {
          setCurrentEmoteBubble(payload);
        } else {
          setAdvisorEmoteBubble(payload);
        }
      }, startAt);

      const hideTimer = setTimeout(() => {
        if (line.speaker === 'general') {
          setCurrentEmoteBubble(null);
        } else {
          setAdvisorEmoteBubble(null);
        }
      }, startAt + lineDuration);

      mission5DialogueTimersRef.current.push(showTimer, hideTimer);
      cursorMs += lineDuration + gapMs;
    });

    return () => {
      for (const timerId of mission5DialogueTimersRef.current) {
        clearTimeout(timerId);
      }
      mission5DialogueTimersRef.current = [];
    };
  }, [gameState?.missionId, gameState?.turn, gameId, userId]);

  // Mission 6 dialogue via emote bubble system (show directly on player's first turn).
  useEffect(() => {
    if (gameState?.missionId !== 'chapter_1_mission_6' || !gameId || !userId) {
      return;
    }
    const isPlayersTurn = String(gameState?.turn) === String(userId);
    const turnsTaken = Number(gameState?.state?.[userId]?.turnsTaken || 0);
    if (!isPlayersTurn || turnsTaken !== 1) {
      return;
    }
    if (mission6IntroEmoteShownForGameRef.current === gameId) {
      return;
    }
    mission6IntroEmoteShownForGameRef.current = gameId;

    const gapMs = 250;
    const dialogue = [
      { speaker: 'advisor', text: "It's so dark in here.", sound: '/sounds/isabela_m6_1.mp3', durationMs: 3200 },
      { speaker: 'general', text: "Yeah. Does not look like your typical Sentients location. Do we know that your scanner thingy works?", sound: '/sounds/david_m6_1.mp3', durationMs: 5200 },
      { speaker: 'advisor', text: "No, I don't know if my \"thingy\" works. But we are here now, so lets check it out.", sound: '/sounds/isabela_m6_2.mp3', durationMs: 5200 },
      { speaker: 'general', text: 'In any case, lets be careful.', sound: '/sounds/david_m6_2.mp3', durationMs: 3200 }
    ];

    for (const timerId of mission6DialogueTimersRef.current) {
      clearTimeout(timerId);
    }
    mission6DialogueTimersRef.current = [];

    let cursorMs = 0;
    dialogue.forEach((line) => {
      const lineDuration = Number(line.durationMs) > 0 ? Number(line.durationMs) : 3200;
      const startAt = cursorMs;
      const showTimer = setTimeout(() => {
        setCurrentEmoteBubble(null);
        setAdvisorEmoteBubble(null);
        playSoundEffect(line.sound, 0.85);
        const payload = { text: line.text, timestamp: Date.now() };
        if (line.speaker === 'general') {
          setCurrentEmoteBubble(payload);
        } else {
          setAdvisorEmoteBubble(payload);
        }
      }, startAt);

      const hideTimer = setTimeout(() => {
        if (line.speaker === 'general') {
          setCurrentEmoteBubble(null);
        } else {
          setAdvisorEmoteBubble(null);
        }
      }, startAt + lineDuration);

      mission6DialogueTimersRef.current.push(showTimer, hideTimer);
      cursorMs += lineDuration + gapMs;
    });
  }, [gameState?.missionId, gameState?.turn, gameState?.state, gameId, userId]);

  // Mission 6 timer cleanup only when leaving mission/game (not on normal turn changes).
  useEffect(() => {
    if (gameState?.missionId === 'chapter_1_mission_6') {
      return;
    }
    for (const timerId of mission6DialogueTimersRef.current) {
      clearTimeout(timerId);
    }
    mission6DialogueTimersRef.current = [];
  }, [gameState?.missionId, gameId]);
  // Strategy modal state
  const [showStrategy, setShowStrategy] = useState(false);
  const [hoveredStrategyOrder, setHoveredStrategyOrder] = useState(null);
  // Force re-render when dev targeting override toggles
  const [targetingDevTick, setTargetingDevTick] = useState(0);
  // Token management state
  const [refreshTokenValue, setRefreshTokenValue] = useState(localStorage.getItem('refreshToken'));
  // All targeting state managed by useTargetingState hook
  const {
    // Database targeting data
    targetingData,
    targetingDataLoaded,
    targetingDataError,
    
    // Deploy targeting state
    deployTargetMode,
    selectedDeployOrderId,
    selectedDeployInstanceId,
    selectedDeployZone,
    deployTargetInfo,
    setDeployTargetMode,
    setSelectedDeployOrderId,
    setSelectedDeployInstanceId,
    setSelectedDeployZone,
    setDeployTargetInfo,
    
    // Weapon targeting state
    weaponTargetMode,
    selectedWeaponId,
    selectedWeaponInstanceId,
    setWeaponTargetMode,
    setSelectedWeaponId,
    setSelectedWeaponInstanceId,
    
    // Weapon deploy targeting state
    weaponDeployTargetMode,
    weaponDeployTargetInfo,
    setWeaponDeployTargetMode,
    setWeaponDeployTargetInfo,
    
    // Drone targeting state
    droneTargetMode,
    selectedDroneId,
    selectedDroneInstanceId,
    setDroneTargetMode,
    setSelectedDroneId,
    setSelectedDroneInstanceId,
    
    // Immobilize targeting state
    immobilizeTargetMode,
    selectedImmobilizeWeaponId,
    setImmobilizeTargetMode,
    setSelectedImmobilizeWeaponId,
    
    // Confuse targeting state
    confuseTargetMode,
    selectedConfuseWeaponId,
    setConfuseTargetMode,
    setSelectedConfuseWeaponId,

    // Strategem targeting state
    strategemTargetMode,
    selectedStrategemId,
    strategemTargetInfo,
    setStrategemTargetMode,
    setSelectedStrategemId,
    setStrategemTargetInfo,
    
    // Special unit targeting state
    specialTargetMode,
    selectedSpecialUnitId,
    setSpecialTargetMode,
    setSelectedSpecialUnitId,
    
    // Replacement targeting state
    replaceTargetMode,
    selectedReplaceUnitId,
    selectedReplaceInstanceId,
    replaceTargetZone,
    clickedUnitForReplace,
    setReplaceTargetMode,
    setSelectedReplaceUnitId,
    setSelectedReplaceInstanceId,
    setReplaceTargetZone,
    setClickedUnitForReplace,
    
    // Drone replacement targeting state
    replaceDroneTargetMode,
    selectedReplaceDroneId,
    selectedReplaceDroneInstanceId,
    clickedDroneForReplace,
    setReplaceDroneTargetMode,
    setSelectedReplaceDroneId,
    setSelectedReplaceDroneInstanceId,
    setClickedDroneForReplace
  } = useTargetingState();

  /** orderId → localized name for cancel-targeting bar (GET /order/:id?lang=…) */
  const localizedTargetingOrderNamesRef = useRef(new Set());
  const [localizedTargetingOrderNames, setLocalizedTargetingOrderNames] = useState({});

  useEffect(() => {
    localizedTargetingOrderNamesRef.current = new Set();
    setLocalizedTargetingOrderNames({});
  }, [i18nInstance.language]);

  useEffect(() => {
    const lang = i18nInstance.language;
    const ids = [];
    if (deployTargetMode && selectedDeployOrderId != null) ids.push(Number(selectedDeployOrderId));
    if (strategemTargetMode && selectedStrategemId != null) ids.push(Number(selectedStrategemId));
    if (weaponTargetMode && selectedWeaponId != null) ids.push(Number(selectedWeaponId));
    if (droneTargetMode && selectedDroneId != null) ids.push(Number(selectedDroneId));
    if (weaponDeployTargetMode && weaponDeployTargetInfo?.weaponId != null) {
      ids.push(Number(weaponDeployTargetInfo.weaponId));
    }
    const uniqueIds = [...new Set(ids.filter((n) => Number.isFinite(n)))];
    if (uniqueIds.length === 0) return undefined;

    const ac = new AbortController();
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    uniqueIds.forEach((orderId) => {
      const refKey = `${orderId}:${lang}`;
      if (localizedTargetingOrderNamesRef.current.has(refKey)) return;
      localizedTargetingOrderNamesRef.current.add(refKey);

      const qs = buildOrderDetailLangQuery(lang);
      fetch(`${API_URL}/order/${orderId}?${qs}`, { headers, signal: ac.signal })
        .then((res) => {
          if (!res.ok) throw new Error(`order ${orderId} ${res.status}`);
          return res.json();
        })
        .then((data) => {
          const nm = typeof data?.name === 'string' ? data.name : '';
          setLocalizedTargetingOrderNames((prev) => (prev[orderId] === nm ? prev : { ...prev, [orderId]: nm }));
        })
        .catch((err) => {
          if (err.name === 'AbortError') return;
          localizedTargetingOrderNamesRef.current.delete(refKey);
        });
    });

    return () => ac.abort();
  }, [
    deployTargetMode,
    selectedDeployOrderId,
    strategemTargetMode,
    selectedStrategemId,
    weaponTargetMode,
    selectedWeaponId,
    droneTargetMode,
    selectedDroneId,
    weaponDeployTargetMode,
    weaponDeployTargetInfo?.weaponId,
    i18nInstance.language,
    token,
  ]);

  // Developer mode state
  const {
    devUnitSearch,
    setDevUnitSearch,
    devSelectedUnit,
    setDevSelectedUnit,
    showGridOverlay,
    setShowGridOverlay,
    showDebugOverlay,
    setShowDebugOverlay,
    showTargetedDebug,
    setShowTargetedDebug,
    showElementSelector,
    setShowElementSelector,
    showEditableOverlay,
    setShowEditableOverlay,
    gridSize,
    setGridSize,
    savedDesign,
    setSavedDesign,
    isConsoleMinimized,
    setIsConsoleMinimized,
    targetElements,
    setTargetElements
  } = useDeveloperMode();

  // Turn timer hook
  const { turnTimeLeft, isWarning, shouldShowTimer } = useTurnTimer(yourTurn, gameState);

  // Counter Strike hook
  const { counterStrikeOrders, opponentHasCounterStrike, updateCounterStrikeOrders, playCounterStrike, keepCounterStrike, processingCounterStrikeId } = useCounterStrike(ws, gameId, userId);

  /** Open game WebSocket: ref is updated on connect before React state; state can be null briefly during reconnect. */
  const getActiveGameWebSocket = useCallback(() => {
    const fromRef = currentSocketRef.current;
    if (fromRef && fromRef.readyState === WebSocket.OPEN) return fromRef;
    if (ws && ws.readyState === WebSocket.OPEN) return ws;
    return null;
  }, [ws]);
  
  // Mulligan hook
  const { mulliganOrders, needsMulligan, mulliganTimeLeft, updateMulliganOrders, submitMulligan } = useMulligan(ws, gameId, userId, getActiveGameWebSocket);

  // Touch drag deployment hook for mobile
  const {
    isDragging: isTouchDragging,
    draggedOrder: touchDraggedOrder,
    dragPosition: touchDragPosition,
    targetZone: touchTargetZone,
    handleTouchStart: handleTouchDragStart,
    handleTouchMove: handleTouchDragMove,
    handleTouchEnd: handleTouchDragEnd,
    cancelDrag: cancelTouchDrag,
  } = useTouchDragDeploy({ isMobile, yourTurn });
  
  // Touch drag attack hook for mobile
  const {
    isDragging: isAttackDragging,
    attackerInfo: attackDragAttacker,
    dragPosition: attackDragPosition,
    startPosition: attackDragStartPosition,
    hoveredTarget: attackDragTarget,
    handleTouchStart: handleAttackTouchStart,
    handleTouchMove: handleAttackTouchMove,
    handleTouchEnd: handleAttackTouchEnd,
    cancelDrag: cancelAttackDrag,
  } = useTouchDragAttack({ isMobile, yourTurn, gameState, userId });
  
  // ✅ Auto-hide first turn message after 5 seconds
  useEffect(() => {
    if (showFirstTurnMessage) {
      const timer = setTimeout(() => {
        setShowFirstTurnMessage(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showFirstTurnMessage]);
  
  // Targeting Cancel hook
  const { createCancelDeployHandler, createCancelStrategemHandler, createCancelWeaponHandler, createCancelWeaponDeployHandler, createCancelDroneHandler } = useTargetingCancel(ws, gameId, setError);

  // Helper function to cancel all active targeting modes
  const cancelAllTargeting = useCallback(() => {
    // Cancel deploy targeting
    if (deployTargetMode && selectedDeployOrderId) {
      const cancelDeploy = createCancelDeployHandler(
        setDeployTargetMode,
        setSelectedDeployOrderId,
        setSelectedDeployInstanceId,
        setSelectedDeployZone,
        setDeployTargetInfo,
        selectedDeployOrderId,
        selectedDeployInstanceId,
        selectedDeployZone
      );
      cancelDeploy();
    }
    
    // Cancel strategem targeting
    if (strategemTargetMode && selectedStrategemId) {
      const cancelStrategem = createCancelStrategemHandler(
        setStrategemTargetMode,
        setSelectedStrategemId,
        setStrategemTargetInfo
      );
      cancelStrategem();
    }
    
    // Cancel weapon targeting
    if (weaponTargetMode && selectedWeaponId) {
      const cancelWeapon = createCancelWeaponHandler(
        setWeaponTargetMode,
        setSelectedWeaponId,
        setSelectedWeaponInstanceId
      );
      cancelWeapon();
    }
    
    // Cancel weapon deploy targeting
    if (weaponDeployTargetMode && weaponDeployTargetInfo) {
      const cancelWeaponDeploy = createCancelWeaponDeployHandler(
        setWeaponDeployTargetMode,
        setWeaponDeployTargetInfo,
        weaponDeployTargetInfo
      );
      cancelWeaponDeploy();
    }
    
    // Cancel drone targeting
    if (droneTargetMode && selectedDroneId) {
      const cancelDrone = createCancelDroneHandler(
        setDroneTargetMode,
        setSelectedDroneId,
        setSelectedDroneInstanceId
      );
      cancelDrone();
    }
    
    // Cancel immobilize targeting
    if (immobilizeTargetMode && selectedImmobilizeWeaponId) {
      setImmobilizeTargetMode(false);
      setSelectedImmobilizeWeaponId(null);
    }
    
    // Cancel confuse targeting
    if (confuseTargetMode && selectedConfuseWeaponId) {
      setConfuseTargetMode(false);
      setSelectedConfuseWeaponId(null);
    }

    if (euEpZoneTargetMode) {
      setEuEpZoneTargetMode(false);
    }
    if (euEpTargetMode) {
      setEuEpTargetMode(false);
    }
  }, [
    deployTargetMode,
    selectedDeployOrderId,
    selectedDeployInstanceId,
    selectedDeployZone,
    strategemTargetMode,
    selectedStrategemId,
    weaponTargetMode,
    selectedWeaponId,
    weaponDeployTargetMode,
    weaponDeployTargetInfo,
    droneTargetMode,
    selectedDroneId,
    immobilizeTargetMode,
    selectedImmobilizeWeaponId,
    confuseTargetMode,
    selectedConfuseWeaponId,
    euEpZoneTargetMode,
    euEpTargetMode,
    createCancelDeployHandler,
    createCancelStrategemHandler,
    createCancelWeaponHandler,
    createCancelWeaponDeployHandler,
    createCancelDroneHandler,
    setDeployTargetMode,
    setSelectedDeployOrderId,
    setSelectedDeployInstanceId,
    setSelectedDeployZone,
    setDeployTargetInfo,
    setStrategemTargetMode,
    setSelectedStrategemId,
    setStrategemTargetInfo,
    setWeaponTargetMode,
    setSelectedWeaponId,
    setWeaponDeployTargetMode,
    setWeaponDeployTargetInfo,
    setDroneTargetMode,
    setSelectedDroneId,
    setSelectedDroneInstanceId,
    setImmobilizeTargetMode,
    setSelectedImmobilizeWeaponId,
    setConfuseTargetMode,
    setSelectedConfuseWeaponId
  ]);

  // Cancel targeting when user selects a different unit in hand
  const prevSelectedOrderInstanceIdRef = useRef(selectedOrderInstanceId);
  useEffect(() => {
    // Only cancel if a new unit is being selected (not when clearing selection)
    if (selectedOrderInstanceId && 
        selectedOrderInstanceId !== prevSelectedOrderInstanceIdRef.current &&
        prevSelectedOrderInstanceIdRef.current !== null) {
      // User clicked a different unit in hand - cancel any active targeting
      cancelAllTargeting();
    }
    prevSelectedOrderInstanceIdRef.current = selectedOrderInstanceId;
  }, [selectedOrderInstanceId, cancelAllTargeting]);
  
  // Counter Strike state
  const [selectedCounterStrikeOrder, setSelectedCounterStrikeOrder] = useState(null);
  const [counterStrikeReplaceMode, setCounterStrikeReplaceMode] = useState(false);

  // Revealed enemy order state (Ghost Operative - Unit 295)
  const [revealedEnemyOrder, setRevealedEnemyOrder] = useState(null);

  // Action log: server-authoritative; client only displays (seed from navigation/initial state for refresh/F5 consistency)
  const [actionLog, setActionLog] = useState(() => {
    const gs = initialGameState ?? location.state?.gameState ?? null;
    return Array.isArray(gs?.actionLog) ? gs.actionLog : [];
  });
  // Deduplicate opponent weapon-equip SFX playback across incremental actionLog updates.
  const heardWeaponEquipActionKeysRef = useRef(new Set());
  
  // Highlighted units from action log hover (optional, for future use)
  const [highlightedInstanceIds, setHighlightedInstanceIds] = useState([]);
  
  // Detect and animate opponent strategem plays (works for human and bot opponents)
  // Uses actionLog (server entries with type 'strategem-requested') from opponent
  useOpponentStrategemAnimations(
    effectiveGameState,
    effectiveUserId,
    registerStrategemTargetAnimation,
    strategemTargetAnimations.ghostUnitsRef,
    actionLog
  );

  // Friend spectate: game WebSocket is closed — mirror server actionLog from friends context.
  useEffect(() => {
    if (!isFriendSpectating) return;
    if (!spectatingGameState) return;
    const log = spectatingGameState.actionLog;
    setActionLog(Array.isArray(log) ? log : []);
  }, [isFriendSpectating, spectatingGameState]);

  // Play equip sound when an opponent equips a weapon (server-authoritative action log).
  useEffect(() => {
    if (!Array.isArray(actionLog) || actionLog.length === 0) return;

    const povId = isFriendSpectating ? spectatingPlayerId : userId;
    if (povId == null) return;
    const selfId = String(povId);
    let shouldPlayEquipSound = false;

    for (const entry of actionLog) {
      if (!entry || entry.type !== 'weapon-equipped') continue;
      // Only play for opponent actions; local equips already play on weapon-attached/deploy-completed.
      if (String(entry.playerId) === selfId) continue;

      const key = `${entry.sequence ?? 'no-seq'}:${entry.timestamp ?? 'no-ts'}:${entry.weapon?.instanceId ?? entry.unit?.instanceId ?? 'no-instance'}`;
      if (heardWeaponEquipActionKeysRef.current.has(key)) continue;

      heardWeaponEquipActionKeysRef.current.add(key);
      shouldPlayEquipSound = true;
    }

    if (shouldPlayEquipSound) {
      playSoundEffect('/sounds/equip.mp3', 0.5);
    }

    // Prevent unbounded growth for very long matches.
    if (heardWeaponEquipActionKeysRef.current.size > 200) {
      const recentKeys = actionLog
        .filter(entry => entry && entry.type === 'weapon-equipped')
        .slice(-80)
        .map(entry => `${entry.sequence ?? 'no-seq'}:${entry.timestamp ?? 'no-ts'}:${entry.weapon?.instanceId ?? entry.unit?.instanceId ?? 'no-instance'}`);
      heardWeaponEquipActionKeysRef.current = new Set(recentKeys);
    }
  }, [actionLog, userId, isFriendSpectating, spectatingPlayerId]);
  
  // Unit Registry: Persistent snapshot of all units by instanceId
  // This allows us to reference units even after they're removed from the battlefield
  const unitRegistryRef = useUnitRegistry();

const [strategyDetails, setStrategyDetails] = useState(null);
const [showResignDialog, setShowResignDialog] = useState(false);
/** Which resign dialog action has keyboard focus: 'confirm' = Yes resign (left), 'cancel' = Cancel (right) */
const [resignDialogFocus, setResignDialogFocus] = useState('cancel');
const resignDialogConfirmRef = useRef(null);
const resignDialogCancelRef = useRef(null);
const exitingRef = useRef(false);
  
  // Battle music starts as soon as battle gameState exists (including mulligan).
  // Stable placeholder so useBattleMusic effect doesn't re-run every render (which would restart music)
  const tutorialMusicPlaceholder = useMemo(() => ({ orders: [] }), []);
  const isTutorialMission =
    gameState?.campaignMode === true && EARLY_TUTORIAL_MISSION_IDS.includes(gameState?.missionId);
  const showPlayerAdvisor =
    !!(gameState?.campaignMode || location.state?.isCampaign) && !isTutorialMission;

  const activatePreparationBonus = useCallback((clickEvent = null) => {
    if (isSpectating || isAutobattler || isAutobattlerSpectator) return false;
    if (isTutorialMission) return false;
    if (playerGoesFirst !== false) return false;
    if (preparationBonusPendingRef.current || !yourTurn) return false;
    if (
      preparationBonus !== 'plan_ahead' &&
      preparationBonus !== 'black_market' &&
      preparationBonus !== 'team_leader'
    ) {
      return false;
    }
    if (preparationBonus === 'black_market') {
      const maxUses = exploreUnknownBlackMarketMaxUses(gameState);
      const used = Number(gameState?.state?.[userId]?.blackMarketUsesThisBattle) || 0;
      if (used >= maxUses) return false;
    } else if (preparationBonus === 'plan_ahead') {
      const maxUses = exploreUnknownPlanAheadMaxUses(gameState);
      const used = Number(gameState?.state?.[userId]?.planAheadUsesThisBattle) || 0;
      if (used >= maxUses) return false;
    } else if (preparationBonusUsed) {
      return false;
    }

    const activeSocket = ws || currentSocketRef.current;
    if (!gameId || !activeSocket || activeSocket.readyState !== WebSocket.OPEN) {
      setError(t('battleUI.websocketNotConnected') || 'WebSocket not connected');
      return false;
    }

    if (clickEvent && clickEvent.currentTarget) {
      const buttonElement = clickEvent.currentTarget;
      const blackMarketHasAnotherChargeAfterThis =
        preparationBonus === 'black_market' &&
        (() => {
          const maxUses = exploreUnknownBlackMarketMaxUses(gameState);
          const used = Number(gameState?.state?.[userId]?.blackMarketUsesThisBattle) || 0;
          return used + 1 < maxUses;
        })();
      const planAheadHasAnotherChargeAfterThis =
        preparationBonus === 'plan_ahead' &&
        (() => {
          const maxUses = exploreUnknownPlanAheadMaxUses(gameState);
          const used = Number(gameState?.state?.[userId]?.planAheadUsesThisBattle) || 0;
          return used + 1 < maxUses;
        })();
      if (!blackMarketHasAnotherChargeAfterThis && !planAheadHasAnotherChargeAfterThis) {
        buttonElement.style.filter = 'grayscale(100%)';
        buttonElement.style.cursor = 'not-allowed';
        buttonElement.style.pointerEvents = 'none';
        buttonElement.style.background = 'rgba(30, 30, 30, 0.7)';
        buttonElement.disabled = true;
      }
    }

    preparationBonusPendingRef.current = true;
    if (preparationBonus !== 'black_market' && preparationBonus !== 'plan_ahead') {
      setPreparationBonusUsed(true);
    }

    const bonusTypeForBackend = preparationBonus === 'team_leader' ? 'team_leader_meeting' : preparationBonus;
    activeSocket.send(
      JSON.stringify({
        type: 'play-turn',
        gameId,
        action: {
          type: 'activatePreparationBonus',
          bonusType: bonusTypeForBackend,
        },
        stateVersion: lastStateVersionRef.current,
      })
    );
    return true;
  }, [
    isSpectating,
    isAutobattler,
    isAutobattlerSpectator,
    isTutorialMission,
    playerGoesFirst,
    preparationBonusUsed,
    yourTurn,
    preparationBonus,
    gameState,
    userId,
    ws,
    gameId,
    setError,
    t,
    lastStateVersionRef,
  ]);

  // Battle music follows the opponent's faction (authoritative gameState strategy), not the player's.
  const musicViewerUserId = useMemo(() => {
    if (effectiveGameState?.isAutobattler && effectiveGameState?.playerBotId) {
      return effectiveGameState.playerBotId;
    }
    return effectiveUserId;
  }, [effectiveGameState?.isAutobattler, effectiveGameState?.playerBotId, effectiveUserId]);
  const musicStrategyDetails = useMemo(() => {
    if (!gameState) return null;
    if (isTutorialMission) return tutorialMusicPlaceholder;
    const opponentId = effectiveGameState?.players?.find(
      (id) => String(id) !== String(musicViewerUserId)
    );
    const opponentStrategyFromGameState = opponentId
      ? effectiveGameState?.state?.[opponentId]?.strategy
      : null;
    if (opponentStrategyFromGameState?.orders?.length) return opponentStrategyFromGameState;
    return null;
  }, [
    gameState,
    isTutorialMission,
    tutorialMusicPlaceholder,
    effectiveGameState,
    musicViewerUserId,
  ]);
  useBattleMusic(musicStrategyDetails, 2000, {
    missionId: gameState?.missionId,
    exploreUnknownMissionIndex: gameState?.exploreUnknownMissionIndex,
  });
  useExploreUnknownMission46CelloAudio(gameState, gameId);

  useEffect(() => {
    const suppressed = isExploreUnknownKendrexColonyMissionIndex(gameState?.exploreUnknownMissionIndex);
    setExploreUnknownKendrexColonyBattleAudioSuppressed(suppressed);
    return () => setExploreUnknownKendrexColonyBattleAudioSuppressed(false);
  }, [gameState?.exploreUnknownMissionIndex]);

  // Check if developer mode is enabled (only for username/ID "player1")
  // Player1 should ALWAYS see it, regardless of battle position
  const isPlayer1 = userId === 'player1' || user?.username === 'player1' || user?.id === 'player1';
  const isDeveloperMode = Boolean(location.state?.developerMode && isPlayer1);
  

  // ✅ FIX: Fetch player's emotes with retry logic (moved outside useEffect for retry button access).
  // On 403 (invalid/expired token), refresh token and retry once—same pattern as fetchGameWithRetry.
  const fetchEmotes = useCallback(async (retryCount = 0, tokenOverride = null) => {
    const tokenToUse = tokenOverride !== null && tokenOverride !== undefined ? tokenOverride : token;
    if (!tokenToUse) {
      setEmotesLoading(false);
      setEmotesError('No authentication token');
      return;
    }

    const maxRetries = 3;
    const retryDelay = Math.min(1000 * Math.pow(2, retryCount), 5000); // Exponential backoff, max 5s

    try {
      setEmotesLoading(true);
      setEmotesError(null);

      const res = await fetch(`${API_URL}/profile/emotes`, {
        headers: {
          Authorization: `Bearer ${tokenToUse}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        const emotes = data.emotes || [];
        setPlayerEmotes(emotes);
        setPlayerEmoteSelections(data.selections || {});
        setEmotesLoading(false);
        setEmotesError(null);
      } else {
        // ✅ FIX: On 403 (invalid/expired token), refresh and retry once
        if (res.status === 403 && tokenOverride == null) {
          try {
            const refreshTokenValue = localStorage.getItem('refreshToken');
            if (refreshTokenValue) {
              const newToken = await refreshToken(refreshTokenValue);
              localStorage.setItem('token', newToken);
              return fetchEmotes(0, newToken);
            }
          } catch (refreshErr) {
            console.warn('Emote fetch: token refresh failed', refreshErr);
          }
        }
        // Retry on server errors (5xx) or rate limits (429)
        if ((res.status >= 500 || res.status === 429) && retryCount < maxRetries) {
          console.warn(`⚠️ Emote fetch failed (${res.status}), retrying in ${retryDelay}ms... (attempt ${retryCount + 1}/${maxRetries})`);
          setTimeout(() => fetchEmotes(retryCount + 1), retryDelay);
          return;
        }

        const errorMsg = `Failed to fetch emotes: ${res.status} ${res.statusText}`;
        console.error(errorMsg);
        setEmotesError(errorMsg);
        setEmotesLoading(false);
      }
    } catch (err) {
      // ✅ FIX: Retry on network errors
      if (retryCount < maxRetries) {
        console.warn(`⚠️ Emote fetch error (network), retrying in ${retryDelay}ms... (attempt ${retryCount + 1}/${maxRetries}):`, err.message);
        setTimeout(() => fetchEmotes(retryCount + 1), retryDelay);
        return;
      }

      const errorMsg = `Failed to fetch emotes: ${err.message}`;
      console.error(errorMsg, err);
      setEmotesError(errorMsg);
      setEmotesLoading(false);
    }
  }, [token]);

  // Periodic token refresh check
  // Fetch avatars and preparation bonus on component mount
  useEffect(() => {
    const fetchAvatars = async () => {
      try {
        const res = await fetch(`${API_URL}/get-avatar`);
        const data = await res.json();
        if (Array.isArray(data)) {
          setAvatars(data);
        } else {
          console.error('Unexpected avatar response format:', data);
          setAvatars([]);
        }
      } catch (err) {
        console.error('Failed to fetch avatars:', err);
        setAvatars([]);
      }
    };

    const isExploreUnknownBattle =
      initialGameState?.exploreUnknownMissionIndex != null ||
      location.state?.gameState?.exploreUnknownMissionIndex != null;

    const fetchPreparationBonus = async () => {
      if (isExploreUnknownBattle) {
        return;
      }
      try {
        const res = await fetch(`${API_URL}/profile/features`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (res.ok) {
          const data = await res.json();
          setPreparationBonus(data.selections?.preparation_bonus || null);
          const accordValue = data.selections?.diplomatic_accords || null;
          setDiplomaticAccord(accordValue);
        }
      } catch (err) {
        console.error('Failed to fetch preparation bonus:', err);
      }
    };

    fetchAvatars();
    fetchPreparationBonus();
    fetchEmotes();
  }, [token, fetchEmotes]);

  // Clean up all bot avatar entries when starting any bot game
  useEffect(() => {
    if (isBotGame) {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('botAvatars_')) {
          localStorage.removeItem(key);
        }
      });
    }
  }, [isBotGame]);

  // Listen for dev targeting override changes to re-render highlights
  useEffect(() => {
    const handler = () => setTargetingDevTick(prev => prev + 1);
    window.addEventListener('force-highlight-change', handler);
    return () => window.removeEventListener('force-highlight-change', handler);
  }, []);

  // Listen for campaign auto end-turn (e.g. "Deploy both to front" tutorial)
  useEffect(() => {
    const handler = () => {
      endTurn();
    };
    window.addEventListener('campaign-auto-end-turn', handler);
    return () => window.removeEventListener('campaign-auto-end-turn', handler);
  }, []);

  // Log intelligence changes for player
  useEffect(() => {
    if (gameState?.state?.[userId]) {
      const player = gameState.state[userId];
    }
  }, [gameState?.state?.[userId]?.intelligence, gameState?.state?.[userId]?.maxIntelligence, userId]);

  // Log intelligence changes for opponent
  useEffect(() => {
    if (gameState?.state && gameState.players) {
      const opponentId = gameState.players.find(id => id !== userId);
      if (opponentId && gameState.state[opponentId]) {
        const opponent = gameState.state[opponentId];
      }
    }
  }, [gameState?.state, gameState?.players, userId]);


  useEffect(() => {
    if (!token || !refreshTokenValue) {
      return;
    }

    const checkTokenRefresh = async () => {
      if (needsTokenRefresh(token)) {
        try {
          const newToken = await refreshToken(refreshTokenValue);
          // Store new token in localStorage for next session
          localStorage.setItem('token', newToken);
        } catch (error) {
          // Don't show error to user yet, let it fail naturally on next request
        }
      }
    };

    // Check every 2 minutes
    const interval = setInterval(checkTokenRefresh, 2 * 60 * 1000);
    
    // Check immediately
    checkTokenRefresh();

    return () => clearInterval(interval);
  }, [token, refreshTokenValue]);
  
  // Target elements are now managed by useDeveloperMode hook

  // Load saved design changes on component mount
  useEffect(() => {
    const savedDesign = localStorage.getItem('battleUI_design');
    if (savedDesign) {
      try {
        const designChanges = JSON.parse(savedDesign);
        setSavedDesign(designChanges);
        
        // Apply saved changes to DOM elements after a short delay to ensure elements are rendered
        setTimeout(() => {
          Object.keys(designChanges).forEach(elementId => {
            const changes = designChanges[elementId];
            const elementIndex = parseInt(elementId.replace('editable-', ''));
            const targetElement = targetElements[elementIndex];
            
            if (targetElement) {
              // Find the actual DOM element
              let domElements = [];
              if (targetElement.className) {
                domElements = document.querySelectorAll(`.${targetElement.className.split(' ').join('.')}`);
              } else if (targetElement.selector) {
                domElements = document.querySelectorAll(targetElement.selector);
              }
              
              // Apply changes to all matching elements
              domElements.forEach((el, elIndex) => {
                if (changes.x !== undefined && changes.y !== undefined) {
                  el.style.position = 'absolute';
                  el.style.left = changes.x + 'px';
                  el.style.top = changes.y + 'px';
                }
                if (changes.width !== undefined) {
                  el.style.width = changes.width + 'px';
                }
                if (changes.height !== undefined) {
                  el.style.height = changes.height + 'px';
                }
              });
            }
          });
        }, 100);
      } catch (error) {
        console.error('Error loading saved design:', error);
      }
    }
  }, [targetElements]);

  // Developer mode functions are now imported from devmode folder

useEffect(() => {
  if (passedWs) {
    const socket = passedWs;
    currentSocketRef.current = socket;
    setWs(socket);

    socket.onmessage = event => {
      if (exitingRef.current) return;
      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch (parseErr) {
        console.error('[passedWs] Failed to parse WebSocket message:', parseErr);
        return;
      }

      // ✅ Same as main game socket: consume deploy VFX before any handler returns early (spectate, errors, etc.)
      tryConsumePendingDeployAnimationsForViewer(
        msg.gameState,
        deployAnimationViewerUserIdRef.current,
        deployTargetAnimationsRef.current,
        msg.gameId,
        isWsMessageForDifferentSessionRef.current,
        {
          messageType: msg.type,
          attackId: msg.attackId,
          animationSpeedMultiplier: animationSpeedMultiplierRef.current ?? 1,
          attackAnimationsRef,
          previousGameState: previousGameStateRef.current,
        }
      );
      tryConsumePendingRaidAnimationsForViewer(
        msg.gameState,
        deployAnimationViewerUserIdRef.current,
        raidAnimationsRef.current,
        msg.gameId,
        isWsMessageForDifferentSessionRef.current,
        {
          messageType: msg.type,
          attackId: msg.attackId,
          animationSpeedMultiplier: animationSpeedMultiplierRef.current ?? 1,
          attackAnimationsRef,
        }
      );
      // Redeem extraction must run BEFORE the drone-damage consumer so it can pull the redeem
      // projectiles out (and hold their target HP) before they would otherwise auto-play together.
      tryConsumePendingRedeemDeathSequencesForViewer(
        msg.gameState,
        redeemDeathSequenceRef.current,
        msg.gameId,
        isWsMessageForDifferentSessionRef.current
      );
      tryConsumePendingDroneDamageAnimationsForViewer(
        msg.gameState,
        deployAnimationViewerUserIdRef.current,
        deployTargetAnimationsRef.current,
        msg.gameId,
        isWsMessageForDifferentSessionRef.current,
        {
          previousGameState: previousGameStateRef.current,
        }
      );
      
      if (msg.type === "match-found") {
        navigate(`/battle/${msg.gameId}`, {
          state: {
            strategyId,
            ws: socket,
            token,
            userId,
          },
        });
      }
      
      // ✅ CRITICAL FIX: Handle attack-initiated for passedWs (bot attacks)
      // This was missing, causing bot attacks to never complete when using passedWs
      if (msg.type === "attack-initiated") {
        const passedAiGameId = msg.gameId;
        if (isWsMessageForDifferentSessionRef.current(passedAiGameId)) return;
        if (msg.attackId && completedAttackIdsRef.current.has(msg.attackId)) {
          return;
        }
        // ✅ CRITICAL: For autobattler spectators, apply attack-initiated immediately.
        // Queuing pre-damage attack states can race with immediate attack-complete
        // and visually re-introduce "late damage" (old HP shown again first).
        if (isAutobattlerSpectatorRef.current) {
          const applyUpdate = () => {
            // Update game state
            if (msg.gameState) {
              gameStateRefRef.current.current = msg.gameState;
              setGameState(msg.gameState);
              setYourTurn(msg.yourTurn || false);
            }
            
            // Register attack for animation (use ref to avoid stale closure)
            const currentAttackAnimations = attackAnimationsRef.current;
            if (msg.attackId && msg.attackOutcome && currentAttackAnimations) {
              try {
                currentAttackAnimations.registerAttack(msg.attackId, msg.attackOutcome, msg.isBotAttack || false);
                applyAttackInitiatedHealthFxRef.current(msg.attackId, msg.attackOutcome);
              } catch (registerError) {
                console.error(`❌ [passedWs] Error calling registerAttack:`, registerError);
              }
            } else {
              console.error(`❌ [passedWs] Cannot register attack: missing data`, {
                hasAttackId: !!msg.attackId,
                hasAttackOutcome: !!msg.attackOutcome,
                hasAttackAnimations: !!currentAttackAnimations
              });
            }

            // Apply local damage preview at hit timing for spectators.
            if (msg.attackId && msg.attackOutcome && !attackPreviewAppliedRef.current.has(msg.attackId)) {
              const previewTimeout = setTimeout(() => {
                if (completedAttackIdsRef.current.has(msg.attackId)) {
                  return;
                }
                try {
                  applyAttackHitOutcomePreview(msg.attackId, msg.attackOutcome);
                } catch (previewError) {
                  console.error('Attack preview apply failed (passedWs):', previewError);
                } finally {
                  attackPreviewTimeoutsRef.current.delete(msg.attackId);
                }
              }, 420);
              attackPreviewTimeoutsRef.current.set(msg.attackId, previewTimeout);
            }
          };

          applyUpdate();
          return; // Don't process immediately for autobattler spectators
        }
        
        // Update game state (non-spectator path)
        if (msg.gameState) {
          gameStateRefRef.current.current = msg.gameState;
          setGameState(msg.gameState);
          setYourTurn(msg.yourTurn || false);
        }
        
        // Register attack for animation (use ref to avoid stale closure)
        const currentAttackAnimations = attackAnimationsRef.current;
        if (msg.attackId && msg.attackOutcome && currentAttackAnimations) {
          try {
            currentAttackAnimations.registerAttack(msg.attackId, msg.attackOutcome, msg.isBotAttack || false);
            applyAttackInitiatedHealthFxRef.current(msg.attackId, msg.attackOutcome);
          } catch (registerError) {
            console.error(`❌ [passedWs] Error calling registerAttack:`, registerError);
          }
        } else {
          console.error(`❌ [passedWs] Cannot register attack: missing data`, {
            hasAttackId: !!msg.attackId,
            hasAttackOutcome: !!msg.attackOutcome,
            hasAttackAnimations: !!currentAttackAnimations
          });
        }
        return; // Don't fall through
      }
      
      // ✅ CRITICAL FIX: Handle attack-complete for passedWs
      if (msg.type === "attack-complete") {
        const passedAcGameId = msg.gameId;
        if (isWsMessageForDifferentSessionRef.current(passedAcGameId)) return;
        attackInProgressRef.current = false;
        if (msg.attackId) {
          const pendingPreviewTimeout = attackPreviewTimeoutsRef.current.get(msg.attackId);
          if (pendingPreviewTimeout) {
            clearTimeout(pendingPreviewTimeout);
            attackPreviewTimeoutsRef.current.delete(msg.attackId);
          }
          completedAttackIdsRef.current.add(msg.attackId);
          if (completedAttackIdsRef.current.size > 2000) {
            completedAttackIdsRef.current.clear();
            attackPreviewAppliedRef.current.clear();
          }
        }

        if (msg.gameState) {
          updateStateRef.current(msg.gameState, { skipVersionCheck: true });
          setActionLog(msg.gameState.actionLog ?? []);
          setYourTurn(msg.yourTurn || false, 'attack-complete');
          if (window.syncDeployTargeting) {
            window.syncDeployTargeting(msg.gameState, userId);
          }
          if (window.syncStrategemTargeting) {
            window.syncStrategemTargeting(msg.gameState, userId);
          }
        }

        if (msg.attackOutcome) {
          applyAttackCompleteHealthFxRef.current(msg.attackId, msg.attackOutcome);
        }

        if (msg.gameState) {
          previousGameStateRef.current = JSON.parse(JSON.stringify(msg.gameState));
        }

        return; // Don't fall through
      }
      
      // ✅ State sync for passedWs — MUST mirror main socket message types that carry gameState.
      // targetDeploy (e.g. Encrazed Bombardier) sends turn-played, not game-update; we only handled game-update before,
      // so deploy VFX + state never applied for clients using the passed-in match socket.
      if (
        msg.type === 'game-update' ||
        msg.type === 'turn-played' ||
        msg.type === 'turn-ended' ||
        msg.type === 'turn-timeout' ||
        msg.type === 'player-disconnected' ||
        msg.type === 'player-abandoned' ||
        msg.type === 'counter-strike-played'
      ) {
        const passedSyncGameId = msg.gameId;
        if (isWsMessageForDifferentSessionRef.current(passedSyncGameId)) return;
        // ✅ FIX: Clear reconnecting state when gameState is received
        if (reconnectingRef.current && msg.gameState) {
          reconnectingRef.current = false;
          setReconnecting(false);
          wsConnectingRef.current = false;
          if (reconnectingTimeoutRef.current) {
            clearTimeout(reconnectingTimeoutRef.current);
            reconnectingTimeoutRef.current = null;
          }
        }

        const applyPassedWsState = () => {
          if (!msg.gameState) return;
          // Deploy VFX consumed at start of passedWs onmessage (tryConsumePendingDeployAnimationsForViewer)
          const prevState = previousGameStateRef.current;
          if (prevState && msg.gameState) {
            const players = msg.gameState.players || Object.keys(msg.gameState.state || {});
            for (const playerId of players) {
              const prevDrones = prevState.state?.[playerId]?.drones?.length ?? 0;
              const currDrones = msg.gameState.state?.[playerId]?.drones?.length ?? 0;
              if (prevDrones > currDrones) {
                playSoundEffect('/sounds/out_of_charges.mp3', 0.6);
                break;
              }
            }
          }
          gameStateRefRef.current.current = msg.gameState;
          setGameState(msg.gameState);
          setActionLog(msg.gameState.actionLog ?? []);
          setYourTurn(msg.yourTurn || false);
          if (msg.type === 'turn-played' || msg.type === 'turn-ended') {
            releaseStaleAttackDamageFxOnTurnBoundaryRef.current();
          }
          previousGameStateRef.current = JSON.parse(JSON.stringify(msg.gameState));
        };

        if (isAutobattlerSpectatorRef.current && msg.gameState) {
          speedControlQueueUpdateRef.current(msg, applyPassedWsState);
          return;
        }

        applyPassedWsState();
        return;
      }
      
      // ✅ AUTOBATTLER FIX: Handle spectate-update messages for autobattler spectators
      if (msg.type === "spectate-update") {
        const isAutobattlerGame = msg.gameState?.isAutobattler || msg.gameId?.includes('_autobattler_');
        if (isAutobattlerGame && msg.gameState) {
          // ✅ FIX: Clear reconnecting state when gameState is received
          // This prevents getting stuck in "Reconnecting..." state
          if (reconnectingRef.current) {
            reconnectingRef.current = false;
            setReconnecting(false);
            wsConnectingRef.current = false;
            if (reconnectingTimeoutRef.current) {
              clearTimeout(reconnectingTimeoutRef.current);
              reconnectingTimeoutRef.current = null;
            }
          }
          
          // ✅ SPEED CONTROL: Queue update instead of applying immediately
          const applyUpdate = () => {
            gameStateRefRef.current.current = msg.gameState;
            setGameState(msg.gameState);
            setYourTurn(false); // Spectators never have their turn
            
            if (msg.gameState) {
              setActionLog(msg.gameState.actionLog ?? []);
            }
            previousGameStateRef.current = JSON.parse(JSON.stringify(msg.gameState));
          };
          
          // Queue the update (will apply immediately if speed control disabled)
          // Only use speed control for autobattler spectators
          if (isAutobattlerSpectatorRef.current && speedControlQueueUpdateRef.current) {
            speedControlQueueUpdateRef.current(msg, applyUpdate);
          } else {
            // Normal spectating or speed control not available - apply immediately
            applyUpdate();
          }
        }
        return; // Don't fall through
      }
      
      // ✅ DEV AUTOBATTLER: Handle dev-autobattler messages
      if (msg.type === "dev-autobattler-started" || msg.type === "dev-autobattler-turn-complete" || msg.type === "dev-autobattler-game-ended") {
        if (msg.gameState) {
          if (msg.gameState) {
            setActionLog(msg.gameState.actionLog ?? []);
          }
          previousGameStateRef.current = JSON.parse(JSON.stringify(msg.gameState));
          
          gameStateRefRef.current.current = msg.gameState;
          setGameState(msg.gameState);
          setYourTurn(false); // Player is spectator in dev autobattler
        }
        return; // Don't fall through
      }
      
      // If server ever includes winner in pushed state, start transition
      // ✅ FIX: Only accept winner when message is for the game currently in the URL (avoids stale ref / delayed message from previous game).
      const msgGameId = msg.gameId;
      const expectedSid = resolveWsSessionGameIdRef.current();
      if (msg.gameState?.winner && !isSpectating && msgGameId != null && expectedSid != null && String(msgGameId) === String(expectedSid)) {
        const didYouWin = didHumanPlayerWinGame(msg.gameState, userId);
        const isFriendlyMatch = msg.gameState.isFriendlyMatch || false;
        handleBattleEnd(didYouWin, gameIdRef.current, socket, isFriendlyMatch, 'passedWs-winner', msg.gameState);
      }
    };

    socket.onclose = () => {
      if (currentSocketRef.current === socket) {
        currentSocketRef.current = null;
      }
      setWs(null);
    };
    
    socket.onerror = (error) => {
      console.error(`🔌 [passedWs] WebSocket ERROR:`, error);
    };

    return () => {
      if (currentSocketRef.current === socket) {
        currentSocketRef.current = null;
      }
      // Don't close autobattlerWs - it's managed by parent component
      if (!autobattlerWs) {
        socket.close();
      }
    };
  }
}, [passedWs, autobattlerWs, navigate, strategyId, token, userId, getCurrentGameIdFromUrl]);

useEffect(() => {
  if (exitingRef.current) return;
  if (!gameId || !token) return;
  
  // Skip game fetch for friend spectators — they receive state via Friends spectate WebSocket, not REST.
  if (isFriendSpectating) {
    return;
  }
  
  // ✅ AUTOBATTLER: Skip game fetch if initialGameState was provided at mount (autobattler/dev autobattler).
  // Uses ref so this check doesn't cause re-fetches when parent passes a new initialGameState reference.
  const mountInitial = initialGameStateRef.current;
  if (mountInitial && (mountInitial.isAutobattler || mountInitial.devAutobattlerMode)) {
    return;
  }

  // ✅ FIX: Helper function to fetch game with token refresh retry
  const fetchGameWithRetry = async (currentToken, retryOn403 = true) => {
    const response = await fetch(`${API_URL}/game/${gameId}`, {
      headers: {
        Authorization: `Bearer ${currentToken}`,
      },
    });

    // If 403 and we haven't retried yet, try refreshing token
    if (!response.ok && response.status === 403 && retryOn403) {
      try {
        const refreshTokenValue = localStorage.getItem('refreshToken');
        if (refreshTokenValue) {
          // refreshToken is already imported at the top of the file
          const newToken = await refreshToken(refreshTokenValue);
          localStorage.setItem('token', newToken);
          // Retry with new token (only once)
          return fetchGameWithRetry(newToken, false);
        }
      } catch (refreshError) {
        console.error('❌ Token refresh failed:', refreshError);
        // Fall through to error handling
      }
    }

    if (!response.ok) {
      throw { status: response.status, response };
    }

    return response.json();
  };

  // ✅ FIX: First verify the gameId is actually active for this user
  // This prevents loading old games from stale URLs
  const requestedGameId = String(gameId);
  let fetchCancelled = false;

  fetchGameWithRetry(token)
    .then(data => {
      if (fetchCancelled) return;
      // ✅ CRITICAL: Ignore responses from a previous /game/:id fetch after navigating to a new battle
      // (otherwise an abandoned/completed game's state can overwrite the new session and trigger reward UI).
      if (shouldDiscardStaleGameFetch(gameIdRef.current, requestedGameId)) {
        return;
      }
      if (!data) return; // Error case handled below
      
      // ✅ Additional validation: Check if user is actually part of this game
      // In autobattler mode, user is a spectator (spectatorUserId), not a player
      // ✅ FIX: Handle both string and number types for player IDs (war effort battles use strings, regular battles may use numbers)
      const isPlayer = data.gameState?.players?.includes(userId) || 
                      data.gameState?.players?.includes(userId.toString()) || 
                      data.gameState?.players?.includes(Number(userId));
      const isAutobattlerSpectator =
        data.gameState?.isAutobattler === true &&
        data.gameState?.spectatorUserId != null &&
        String(data.gameState.spectatorUserId) === String(userId);
      
      if (!data.gameState || !data.gameState.players || (!isPlayer && !isAutobattlerSpectator)) {
        console.warn(`⚠️ User ${userId} is not part of game ${gameId}. Redirecting.`, {
          isPlayer,
          isAutobattlerSpectator,
          players: data.gameState?.players,
          spectatorUserId: data.gameState?.spectatorUserId,
          isAutobattler: data.gameState?.isAutobattler
        });
        setError(t('battleUI.gameNotFound'));
        setTimeout(() => {
          navigate('/battleoverview', { replace: true });
        }, 2000);
        return;
      }
      
      setGameState(data.gameState);
      setActionLog(data.gameState.actionLog ?? []);
      // ✅ FIX: Handle both string and number types for turn check
      const isMyTurn = data.gameState.turn === userId || 
                      data.gameState.turn === userId.toString() || 
                      String(data.gameState.turn) === String(userId);
      setYourTurn(isMyTurn);
      storeOpponentStrategyId(data.gameState);
      
      // ✅ FIX: Initialize previousGameStateRef with initial state so first action can be detected
      // Create a baseline state for comparison (deep copy to avoid reference issues)
      if (!previousGameStateRef.current) {
        previousGameStateRef.current = JSON.parse(JSON.stringify(data.gameState));
      }
      
      // ✅ Determine if player goes first (after both players have mulliganed)
      const playerState = data.gameState.state?.[userId];
      const opponentId = data.gameState.players?.find(p => p !== userId);
      const opponentState = opponentId ? data.gameState.state?.[opponentId] : null;
      
      // Check if both players have completed mulligan
      const playerMulliganed = playerState && !playerState.needsMulligan && playerState.hasMulliganed;
      const opponentMulliganed = opponentState && !opponentState.needsMulligan && opponentState.hasMulliganed;
      
      if (playerMulliganed && opponentMulliganed) {
        // Both players have mulliganed - determine who goes first (only set once)
        // ✅ FIX: Use players[0] to determine who goes first, not current turn
        setPlayerGoesFirst(prev => {
          if (prev === null) {
            const firstPlayerId = data.gameState.players?.[0];
            // ✅ FIX: Handle both string and number types for player ID comparison
            const goesFirst = firstPlayerId === userId || 
                             firstPlayerId === userId.toString() || 
                             String(firstPlayerId) === String(userId);
            // Show message (will auto-hide after 5 seconds via useEffect)
            setShowFirstTurnMessage(true);
            return goesFirst;
          }
          return prev; // Keep existing value
        });
      }
      
      // ✅ REFACTORED: Use hook's validateState method
      validateState(data.gameState);
    })
    .catch(err => {
      if (fetchCancelled) return;
      if (shouldDiscardStaleGameFetch(gameIdRef.current, requestedGameId)) {
        return;
      }
      console.error("❌ Failed to fetch game state:", err);
      const status = err.status || (err.response?.status);
      
      // Game not found or not accessible - likely an old gameId or auth issue
      if (status === 404 || status === 403) {
        console.warn(`⚠️ Game ${gameId} not found or not accessible (status: ${status}). Redirecting to battle overview.`);
        setError(t('battleUI.gameNotFound'));
      } else {
        setError(t('battleUI.connectionLost'));
      }
      
      // Redirect to battle overview after a short delay
      setTimeout(() => {
        if (shouldDiscardStaleGameFetch(gameIdRef.current, requestedGameId)) {
          return;
        }
        navigate('/battleoverview', { replace: true });
      }, 2000);
    });

  return () => {
    fetchCancelled = true;
  };
}, [gameId, token, userId, location.state?.developerMode, t, navigate, isFriendSpectating]);

// === Handle temporary messages (e.g., "feeling tired") ===
useEffect(() => {
  if (!gameState || !gameState.temporaryMessages || !userId) return;
  
  const playerMessage = gameState.temporaryMessages[userId];
  if (playerMessage && playerMessage.message === 'feelingTired') {
    // Show the message
    setTemporaryMessage('feelingTired');
    
    // Auto-hide after 3 seconds
    const timer = setTimeout(() => {
      setTemporaryMessage(null);
    }, 3000);
    
    return () => clearTimeout(timer);
  }
}, [gameState, userId]);

// Play immobilize.mp3 when any battlefield unit transitions to immobilized (uses effectiveGameState for spectators)
useEffect(() => {
  if (!effectiveGameState?.state) return;

  const isImmobilizedOrder = (order) =>
    !!order &&
    ((order.isImmobilized === true) || (order.immobilized === true) || (order.status === 'immobilized'));

  const current = new Map();
  for (const playerId of Object.keys(effectiveGameState.state)) {
    const played = effectiveGameState.state[playerId]?.playedOrders;
    if (!played) continue;
    for (const zone of [played.front, played.flank]) {
      if (!Array.isArray(zone)) continue;
      for (const u of zone) {
        if (!u?.instanceId) continue;
        current.set(String(u.instanceId), isImmobilizedOrder(u) === true);
      }
    }
  }

  const prev = prevImmobilizedByInstanceRef.current;
  if (prev !== null) {
    let newlyImmobilized = false;
    for (const [id, nowImm] of current) {
      if (nowImm === true && prev.get(id) !== true) {
        newlyImmobilized = true;
        break;
      }
    }
    if (newlyImmobilized) {
      playSoundEffect('/sounds/immobilize.mp3', 0.6);
    }
  }
  prevImmobilizedByInstanceRef.current = current;
}, [effectiveGameState]);

// Play barrier_dismanteled.mp3 when any general's barrier becomes dismantled (HUD logic).
// useLayoutEffect (not useEffect): runs after commit but before browser paint, so audio starts before
// the barrier art updates—useEffect ran after paint and felt visually "late."
// One clip per state update even if multiple thresholds cross at once (uses effectiveGameState for spectators).
useLayoutEffect(() => {
  if (!effectiveGameState?.state) return;

  const nextByPlayer = new Map();
  for (const playerId of Object.keys(effectiveGameState.state)) {
    const p = effectiveGameState.state[playerId];
    const healthRaw = p?.health;
    const healthNum = Number(healthRaw);
    const health = Number.isFinite(healthNum) ? healthNum : 0;
    const removed = Array.isArray(p?.barriersRemoved) ? p.barriersRemoved : [];
    const dismantled = new Set();
    for (const threshold of BATTLE_HEALTH_BARRIER_THRESHOLDS) {
      if (health <= threshold || removed.includes(threshold)) {
        dismantled.add(threshold);
      }
    }
    nextByPlayer.set(String(playerId), dismantled);
  }

  const prev = prevBarrierDismantledByPlayerRef.current;
  if (prev !== null) {
    let anyNewDismantle = false;
    for (const [playerId, nextSet] of nextByPlayer) {
      const prevSet = prev.get(playerId);
      if (!prevSet) continue;
      for (const threshold of nextSet) {
        if (!prevSet.has(threshold)) {
          anyNewDismantle = true;
          break;
        }
      }
      if (anyNewDismantle) break;
    }
    if (anyNewDismantle) {
      playSoundEffect('/sounds/barrier_dismanteled.mp3', 0.65);
    }
  }
  prevBarrierDismantledByPlayerRef.current = nextByPlayer;
}, [effectiveGameState]);

// Play counterstrike.mp3 when the opponent strikes back with a counterstrike unit (STRIKE! + zone deploy).
useLayoutEffect(() => {
  if (!effectiveGameState?.state || effectiveUserId == null) return;

  if (counterstrikeSoundGameIdRef.current !== gameId) {
    counterstrikeSoundGameIdRef.current = gameId;
    prevOpponentCounterStrikeUnitsRef.current = null;
  }

  const opponentId = effectiveGameState.players?.find((id) => String(id) !== String(effectiveUserId));
  if (!opponentId) return;

  const opponentState = effectiveGameState.state[opponentId];
  const current = new Set();
  for (const zone of ['front', 'flank']) {
    const units = opponentState?.playedOrders?.[zone];
    if (!Array.isArray(units)) continue;
    for (const u of units) {
      if (u?.isCounterStrike && u.instanceId) {
        current.add(String(u.instanceId));
      }
    }
  }

  const prev = prevOpponentCounterStrikeUnitsRef.current;
  if (prev !== null) {
    for (const id of current) {
      if (!prev.has(id)) {
        playSoundEffect('/sounds/counterstrike.mp3', 0.7);
        break;
      }
    }
  }
  prevOpponentCounterStrikeUnitsRef.current = current;
}, [effectiveGameState, effectiveUserId, gameId]);


useEffect(() => {
  if (strategyDetails?.orders) {
    // Check if orders already have .id property to avoid infinite loop
    const needsNormalization = strategyDetails.orders.some(order => !order.id && order.order_id);
    if (needsNormalization) {
      const normalizedOrders = strategyDetails.orders.map((order) => ({
        ...order,
        id: order.order_id, // ✅ Ensure .id is available
      }));
      // ✅ FIX: Use functional update to avoid infinite loop
      // Only update if the normalized orders are actually different
      setStrategyDetails((prev) => {
        // Check if normalization is still needed (avoid infinite loop)
        const stillNeedsNormalization = prev?.orders?.some(order => !order.id && order.order_id);
        if (!stillNeedsNormalization) {
          return prev; // Already normalized, don't update
        }
        return {
          ...prev,
          orders: normalizedOrders,
        };
      });
    }
  }
}, [strategyDetails]);



useEffect(() => {
  if (exitingRef.current) return;
  if (!strategyId || !token) return;

  // ✅ CRITICAL: Do NOT pass language parameter for game logic
  // Strategy orders must use English faction names for game logic to work correctly
  // Translations are only for display, not for matching/filtering in game logic
  fetch(`${API_URL}/strategy/${strategyId}`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  })
    .then(res => res.json())
    .then(data => {
      setStrategyDetails(data);
    })
    .catch(err => {
      console.error("❌ Failed to fetch strategy:", err);
    });
}, [strategyId, token]);

// Update counter strike orders when game state changes
// ✅ FIX: updateCounterStrikeOrders and updateMulliganOrders are useCallback functions
// They are stable and won't cause infinite loops, so we can safely include them in deps
useEffect(() => {
  if (gameState) {
    updateCounterStrikeOrders(gameState);
    updateMulliganOrders(gameState);
  }
}, [gameState, userId, updateCounterStrikeOrders, updateMulliganOrders]);

// EU boss instant retry replaces /battle/:id on the same BattleUI instance — reset end-of-battle locks.
const previousBattleSessionGameIdRef = useRef(null);
useLayoutEffect(() => {
  if (gameId == null || gameId === '') {
    return;
  }
  if (previousBattleSessionGameIdRef.current === gameId) {
    return;
  }
  const isSessionSwap = previousBattleSessionGameIdRef.current != null;
  previousBattleSessionGameIdRef.current = gameId;

  if (!isSessionSwap) {
    return;
  }

  isTransitioningRef.current = false;
  exitingRef.current = false;
  setBattleEndTransition({ isTransitioning: false, type: null });
  setShowVictoryImage(false);
  setShowDefeatImage(false);
  setEuDarknessMeterFlash(null);
  setShowMulliganIntroOverlay(false);
  setShowMulliganIntroBossBar(false);
  setShowMulliganIntroPlayerCircle(false);
  setShowMulliganIntroOpponentCircle(false);
  setShowMulliganIntroVs(false);
  setIsMulliganIntroExiting(false);
  setPlayerGoesFirst(null);
  setShowFirstTurnMessage(false);
  setWs(null);
  currentSocketRef.current = null;
  wsConnectingRef.current = false;
  initializedFromNavigationRef.current = false;

  const navState = location.state;
  if (navState?.gameState) {
    setGameState(navState.gameState);
    setYourTurn(navState.yourTurn ?? false);
    gameStateRefRef.current.current = navState.gameState;
    storeOpponentStrategyId(navState.gameState);
  }
}, [gameId, location.state]);

// ✅ FIX: Clear selectedCounterStrikeOrder if it's no longer in the pending counterstrike array
// This handles the case where a counterstrike was played and removed from the array
useEffect(() => {
  if (selectedCounterStrikeOrder && counterStrikeOrders.length > 0) {
    // Check if the selected order is still in the pending counterstrike array
    const stillPending = counterStrikeOrders.find(
      order => order.instanceId === selectedCounterStrikeOrder.instanceId
    );
    if (!stillPending) {
      setSelectedCounterStrikeOrder(null);
    }
  } else if (selectedCounterStrikeOrder && counterStrikeOrders.length === 0) {
    // If there are no pending counterstrikes, clear the selected order
    setSelectedCounterStrikeOrder(null);
  }
}, [counterStrikeOrders, selectedCounterStrikeOrder]);

// Ghost Operative (295): stable signature so we only open the modal once per distinct reveal
// and do not re-apply state on every gameState tick (which also re-showed after local-only dismiss).
const lastGhostRevealSigRef = useRef(null);

// Track hand size to detect when new cards are drawn
const previousHandSizeRef = useRef(0);
useEffect(() => {
  if (gameState && gameState.state && gameState.state[userId]) {
    const playerState = gameState.state[userId];
    const currentHandSize = playerState.hand?.length || 0;
    const previousHandSize = previousHandSizeRef.current;
    
    // If hand size increased, play draw sound
    if (currentHandSize > previousHandSize && previousHandSize > 0) {
      const cardsDrawn = currentHandSize - previousHandSize;
      // Play draw sound for each card drawn
      for (let i = 0; i < cardsDrawn; i++) {
        setTimeout(() => {
          playSoundEffect('/sounds/draw.mp3');
        }, i * 100); // Stagger sounds slightly if multiple cards drawn
      }
    }
    
    // Update previous hand size
    previousHandSizeRef.current = currentHandSize;
  }
}, [gameState, userId]);

// Update revealed enemy order when game state changes (once per distinct reveal from server)
useEffect(() => {
  if (gameState && gameState.state && gameState.state[userId]) {
    const playerState = gameState.state[userId];
    const newRevealedOrder = playerState.revealedEnemyOrder || null;
    const sig =
      newRevealedOrder && newRevealedOrder.order
        ? `${newRevealedOrder.enemyId}:${newRevealedOrder.order.instanceId ?? ''}:${newRevealedOrder.order.id}`
        : null;
    if (sig === null) {
      lastGhostRevealSigRef.current = null;
      setRevealedEnemyOrder(null);
      return;
    }
    if (sig !== lastGhostRevealSigRef.current) {
      lastGhostRevealSigRef.current = sig;
      setRevealedEnemyOrder(newRevealedOrder);
    }
  } else {
    lastGhostRevealSigRef.current = null;
    setRevealedEnemyOrder(null);
  }
}, [gameState, userId]);

const acknowledgeGhostRevealedOrder = useCallback(() => {
  setRevealedEnemyOrder(null);
  const gid = gameIdRef.current;
  if (!gid) {
    console.error('Ghost Operative acknowledge: gameId is missing');
    return;
  }
  const socket =
    (currentSocketRef.current && currentSocketRef.current.readyState === WebSocket.OPEN && currentSocketRef.current) ||
    (ws && ws.readyState === WebSocket.OPEN ? ws : null);
  if (!socket) {
    console.error('Ghost Operative acknowledge: WebSocket not connected; modal closed locally only');
    return;
  }
  try {
    socket.send(JSON.stringify({ action: 'acknowledge-revealed-order', gameId: gid }));
  } catch (err) {
    console.error('Ghost Operative acknowledge: WebSocket send failed', err);
  }
}, [ws]);

useEffect(() => {
  if (error) {
    const timeout = setTimeout(() => {
      setError("");
    }, 3000); // 3 seconds

    return () => clearTimeout(timeout);
  }
}, [error]);

// Turn timer logic moved to ./timer/useTurnTimer.js


  const handlePrev = () => {
    if (!you || !you.hand.length) return;
    setCarouselIndex(prev => (prev === 0 ? you.hand.length - 1 : prev - 1));
  };
  const handleNext = () => {
    if (!you || !you.hand.length) return;
    setCarouselIndex(prev => (prev === you.hand.length - 1 ? 0 : prev + 1));
  };

useEffect(() => {
  let socket;

  // ✅ CAMPAIGN MODE: Skip WebSocket reconnect for campaign games if gameState is already provided
  // Campaign games use scripted actions and don't need real-time sync
  const isCampaignFromState = location.state?.isCampaign || gameState?.campaignMode;
  const hasGameStateFromNavigation = location.state?.gameState && gameState;
  const isReliveCampaign = ['chapter_1_mission_5', 'chapter_1_mission_6'].includes(location.state?.missionId) ||
    ['chapter_1_mission_5', 'chapter_1_mission_6'].includes(gameState?.missionId);
  
  if (isCampaignFromState && hasGameStateFromNavigation && !isReliveCampaign) {
    return; // Don't establish WebSocket for campaign games with gameState from navigation
  }
  if (isReliveCampaign) {
    console.log('🧪 M5 TRACE: WebSocket gate check', {
      isCampaignFromState,
      hasGameStateFromNavigation,
      isReliveCampaign,
      gameId
    });
  }

  // ✅ FIX: Always establish WebSocket connection if we have gameId and don't have a connection yet
  // WebSocket is needed for sending actions (deploy, attack, etc.) regardless of whether we have gameState
  // Use ref to prevent multiple connection attempts
  // SKIP for friend spectators - they get game state through friends WebSocket, not game WebSocket
  // ✅ CRITICAL FIX: Allow WebSocket creation for autobattler spectators - they need game WebSocket to receive updates
  // SKIP for campaign games with gameState from navigation (they'll connect when needed)
  const isAutobattlerGame = gameId?.includes('_autobattler_') || initialGameState?.isAutobattler || gameState?.isAutobattler;
  const shouldCreateWebSocket = !isSpectating || isAutobattlerGame; // Allow for autobattler even if isSpectating is true

  if (
    shouldCreateWebSocket &&
    !exitingRef.current &&
    gameId &&
    !ws &&
    !wsConnectingRef.current &&
    !(isCampaignFromState && hasGameStateFromNavigation && !isReliveCampaign)
  ) {
    wsConnectingRef.current = true; // Mark that we're connecting
    // If we don't have gameState, we need to reconnect to get it
    const needsReconnect = !gameState;
    if (needsReconnect) {
      reconnectingRef.current = true;
      setReconnecting(true);
      // ✅ FIX: Set timeout to clear reconnecting state if no response received
      // This prevents getting stuck in "Reconnecting..." state
      if (reconnectingTimeoutRef.current) {
        clearTimeout(reconnectingTimeoutRef.current);
      }
      reconnectingTimeoutRef.current = setTimeout(() => {
        console.warn('⚠️ [Reconnect] Timeout waiting for gameState - clearing reconnecting state');
        reconnectingRef.current = false;
        setReconnecting(false);
        wsConnectingRef.current = false;
        reconnectingTimeoutRef.current = null;
      }, 10000); // 10 second timeout
    }
    
    // Check if token needs refresh before connecting (use tokenRef so we don't depend on token in effect deps)
    const refreshTokenIfNeeded = async () => {
      const currentToken = tokenRef.current;
      if (needsTokenRefresh(currentToken) && refreshTokenValue) {
        try {
          const newToken = await refreshToken(refreshTokenValue);
          tokenRef.current = newToken;
          return newToken;
        } catch (error) {
          return currentToken;
        }
      }
      return currentToken;
    };

    // Start WebSocket connection (different URL/port than CRA/webpack HMR on the dev server page)
    socket = new window.WebSocket(WS_URL);
    // ✅ CRITICAL: Set both state and ref immediately when socket is created
    // This ensures endTurn can find the socket even if state update is delayed
    currentSocketRef.current = socket;
    setWs(socket);

  socket.onerror = () => {
    console.error(
      '[CODA] Game WebSocket error — connection failed or dropped. Is the game server listening? URL:',
      WS_URL
    );
  };
  
    socket.onopen = async () => {
      if (isReliveCampaign) {
        console.log('🧪 M5 TRACE: WebSocket opened', { gameId });
      }
    const tokenToUse = await refreshTokenIfNeeded();
    // Align with i18n + DB translation codes (en-us / de-de / es-es). localStorage alone can
    // drift from the active UI language and skew server-side translateGameState on attack-initiated.
    const rawLng =
      i18nInstance.resolvedLanguage ||
      i18nInstance.language ||
      (typeof localStorage !== 'undefined' ? localStorage.getItem('i18nextLng') : null) ||
      'en-us';
    const normalizedLanguage = normalizeTitlesApiLang(rawLng);
    // Use centralized auth message with API key
    socket.send(JSON.stringify(getWebSocketAuthMessage(tokenToUse, normalizedLanguage)));
  };
  socket.onmessage = event => {
    if (exitingRef.current) return;
    let msg;
    try {
      msg = JSON.parse(event.data);
    } catch (err) {
      console.error("Failed to parse WebSocket message:", err);
      return;
    }

    // ✅ Deploy VFX (e.g. Encrazed Bombardier fire arc): consume before early version rejection.
    // Otherwise turn-played can be dropped as "stale" while still carrying the only copy of
    // pendingDeployAnimations — opponent never registers the animation.
    tryConsumePendingDeployAnimationsForViewer(
      msg.gameState,
      deployAnimationViewerUserIdRef.current,
      deployTargetAnimationsRef.current,
      msg.gameId,
      isWsMessageForDifferentSessionRef.current,
      {
        messageType: msg.type,
        attackId: msg.attackId,
        animationSpeedMultiplier: animationSpeedMultiplierRef.current ?? 1,
        attackAnimationsRef,
        previousGameState: previousGameStateRef.current,
      }
    );
    tryConsumePendingRaidAnimationsForViewer(
      msg.gameState,
      deployAnimationViewerUserIdRef.current,
      raidAnimationsRef.current,
      msg.gameId,
      isWsMessageForDifferentSessionRef.current,
      {
        messageType: msg.type,
        attackId: msg.attackId,
        animationSpeedMultiplier: animationSpeedMultiplierRef.current ?? 1,
        attackAnimationsRef,
      }
    );
    // Redeem extraction must run BEFORE the drone-damage consumer so it can pull the redeem
    // projectiles out (and hold their target HP) before they would otherwise auto-play together.
    tryConsumePendingRedeemDeathSequencesForViewer(
      msg.gameState,
      redeemDeathSequenceRef.current,
      msg.gameId,
      isWsMessageForDifferentSessionRef.current
    );
    tryConsumePendingDroneDamageAnimationsForViewer(
      msg.gameState,
      deployAnimationViewerUserIdRef.current,
      deployTargetAnimationsRef.current,
      msg.gameId,
      isWsMessageForDifferentSessionRef.current,
      {
        previousGameState: previousGameStateRef.current,
      }
    );
    
    // ✅ IMPROVEMENT #1: Early state version check - reject stale state before any processing
    // This prevents unnecessary work if the state is stale
    // ✅ FIX: Skip version check for spectate-update - autobattler spectators should always receive updates
    // They're passive observers, so version conflicts don't matter (no actions to desync)
    if (msg.gameState && msg.type !== "state-desync-error" && msg.type !== "reconnect-success" && msg.type !== "spectate-update" && msg.type !== "weapon-attached" && msg.type !== "weapon-deploy-completed") {
      // Skip version check for desync recovery, reconnect, and spectate updates (they need to force update)
      // Skip for weapon-attached / weapon-deploy-completed: must always apply deploy-targeting sync even if stateVersion races with turn-played
      const versionAcceptable = checkStateVersionRef.current(msg.gameState);
      if (!versionAcceptable) {
        const currentVersion = lastStateVersionRef.current || (gameStateRefRef.current?.current?.stateVersion || 0);
        const incomingVersion = msg.gameState.stateVersion || 0;
        console.warn(`⚠️ Early rejection: Stale state update detected before processing. incoming=${incomingVersion}, current=${currentVersion}. Requesting fresh state...`);
        // Request fresh state instead of processing stale one
        if (socket && socket.readyState === 1) {
          socket.send(JSON.stringify({ type: 'request-state-refresh', gameId }));
        }
        return; // Don't process stale state
      }
    }
    
    if (msg.type === "auth-success") {
      // ✅ CRITICAL FIX: Include gameId for autobattler spectators
      // Use tokenRef.current so we use latest token without making effect depend on token
      gameSocketReconnectSentRef.current = true; // So effect cleanup won't close this socket on re-run (e.g. Strict Mode)
      socket.send(JSON.stringify({
        type: "reconnect",
        token: tokenRef.current,
        gameId // Include gameId for autobattler spectating
      }));
      // Campaign: ask wrapper to re-sync allowed actions so server has them after connect
      if (gameId && String(gameId).startsWith('campaign_')) {
        window.dispatchEvent(new CustomEvent('campaign-request-sync', { detail: { gameId } }));
      }
    }
    if (msg.type === "reconnect-success") {
      // ✅ FIX: Never apply completed game state from reconnect - prevents infinite redirect to after-battle
      if (msg.gameState?.winner != null && msg.gameState?.winner !== undefined) {
        if (isWsMessageForDifferentSessionRef.current(msg.gameId)) {
          return;
        }
        console.warn(`⚠️ Reconnect sent completed game (winner: ${msg.gameState.winner}). Redirecting to battle overview.`);
        reconnectingRef.current = false;
        setReconnecting(false);
        wsConnectingRef.current = false;
        if (reconnectingTimeoutRef.current) {
          clearTimeout(reconnectingTimeoutRef.current);
          reconnectingTimeoutRef.current = null;
        }
        navigate('/battleoverview', { replace: true });
        return;
      }
      // ✅ FIX: Validate that the reconnected gameId matches what the user is viewing (URL first, then ref).
      // If the server omits gameId, do not apply payload — avoids poisoning a new session after leaving a prior battle.
      const reconnectExpectedSid = resolveWsSessionGameIdRef.current();
      if (reconnectExpectedSid != null) {
        if (!msg.gameId || String(msg.gameId) !== String(reconnectExpectedSid)) {
          if (msg.gameId) {
            console.warn(`⚠️ Reconnected gameId (${msg.gameId}) doesn't match resolved session gameId (${reconnectExpectedSid}, ref=${gameIdRef.current}). Redirecting to correct game.`);
            navigate(`/battle/${msg.gameId}`, { replace: true });
          } else {
            console.warn('⚠️ reconnect-success missing gameId; refusing to apply state for this session.');
            reconnectingRef.current = false;
            setReconnecting(false);
            wsConnectingRef.current = false;
            if (reconnectingTimeoutRef.current) {
              clearTimeout(reconnectingTimeoutRef.current);
              reconnectingTimeoutRef.current = null;
            }
          }
          return;
        }
      }
      
      // ✅ REFACTORED: Use hook's unlockUI method (via ref to avoid stale closure)
      unlockUIRef.current();
      
      // ✅ REFACTORED: Use hook's validateState method (via ref to avoid stale closure)
      if (!validateStateRef.current(msg.gameState)) {
        console.error('🚨 STATE VALIDATION FAILED in reconnect: InstanceIds missing!');
      }
      
      // ✅ CAMPAIGN MODE: Preserve tutorial queue when reconnecting campaign games
      // Merge scriptState from existing gameState if it exists
      const existingScriptState = gameState?.scriptState;
      const reconnectedScriptState = msg.gameState?.scriptState;
      
      if (gameState?.campaignMode && existingScriptState && reconnectedScriptState) {
        // Preserve tutorial queue from existing state if reconnected state doesn't have it
        if (existingScriptState.tutorialQueue && (!reconnectedScriptState.tutorialQueue || reconnectedScriptState.tutorialQueue.length === 0)) {
          msg.gameState.scriptState.tutorialQueue = existingScriptState.tutorialQueue;
        }
        // Merge other scriptState properties
        msg.gameState.scriptState = {
          ...reconnectedScriptState,
          tutorialQueue: reconnectedScriptState.tutorialQueue || existingScriptState.tutorialQueue || []
        };
      }
      
      // ✅ ENHANCED DESYNC FIX: Update ref first, then state
      // ✅ FIX: Always update gameState from server (it's the source of truth)
      // Even if we had gameState from navigation, server state is authoritative
      gameStateRefRef.current.current = msg.gameState;
      setGameState(msg.gameState);
      setActionLog(msg.gameState.actionLog ?? []);
      setYourTurn(msg.yourTurn);
      storeOpponentStrategyId(msg.gameState);
      
      // ✅ Determine if player goes first (after both players have mulliganed)
      const playerState = msg.gameState.state?.[userId];
      const opponentId = msg.gameState.players?.find(p => p !== userId);
      const opponentState = opponentId ? msg.gameState.state?.[opponentId] : null;
      
      // Check if both players have completed mulligan
      const playerMulliganed = playerState && !playerState.needsMulligan && playerState.hasMulliganed;
      const opponentMulliganed = opponentState && !opponentState.needsMulligan && opponentState.hasMulliganed;
      
      if (playerMulliganed && opponentMulliganed) {
        // Both players have mulliganed - determine who goes first (only set once)
        // ✅ FIX: Use players[0] to determine who goes first, not current turn
        setPlayerGoesFirst(prev => {
          if (prev === null) {
            const firstPlayerId = msg.gameState.players?.[0];
            const goesFirst = firstPlayerId === userId;
            // Show message (will auto-hide after 5 seconds via useEffect)
            setShowFirstTurnMessage(true);
            return goesFirst;
          }
          return prev; // Keep existing value
        });
      }
      
      setSelectedOrderId(null);
      setSelectedAttacker(null);
      setAttackMode(false);
      attackInProgressRef.current = false; // Reset attack in progress flag
      reconnectingRef.current = false;
      setReconnecting(false);
      wsConnectingRef.current = false; // ✅ FIX: Reset connection flag after successful reconnect
      // ✅ FIX: Clear timeout since we received reconnect-success
      if (reconnectingTimeoutRef.current) {
        clearTimeout(reconnectingTimeoutRef.current);
        reconnectingTimeoutRef.current = null;
      }
      
      // ✅ CRITICAL FIX: Find the best available socket
      // The socket from closure might be stale, so check all possible sources
      let bestSocket = socket; // Start with closure socket
      
      // Check if ref has a better socket (more recent, or actually OPEN)
      if (currentSocketRef.current) {
        if (currentSocketRef.current.readyState === WebSocket.OPEN) {
          bestSocket = currentSocketRef.current;
        } else if (!bestSocket || bestSocket.readyState !== WebSocket.OPEN) {
          // Ref socket might be better even if not OPEN (at least it exists)
          if (currentSocketRef.current.readyState === WebSocket.CONNECTING) {
            bestSocket = currentSocketRef.current;
          }
        }
      }
      
      // Check if ws state has a better socket
      if (ws && ws.readyState === WebSocket.OPEN) {
        bestSocket = ws;
      }
      
      // Verify we have a socket
      if (!bestSocket) {
        console.error('❌ No socket available after reconnect-success');
        wsConnectingRef.current = false;
        setWs(null);
        currentSocketRef.current = null;
        return;
      }
      
      // ✅ CRITICAL: Always update both ref and state with the best socket
      // This ensures endTurn can find the socket even if state is stale
      currentSocketRef.current = bestSocket;
      setWs(bestSocket);
      
      // ✅ CRITICAL: Verify the socket is actually OPEN and ready
      if (bestSocket.readyState !== WebSocket.OPEN) {
        console.warn('⚠️ WARNING: Socket not OPEN after reconnect-success', {
          readyState: bestSocket.readyState,
          readyStateName: bestSocket.readyState === WebSocket.OPEN ? 'OPEN' : 
                         bestSocket.readyState === WebSocket.CONNECTING ? 'CONNECTING' :
                         bestSocket.readyState === WebSocket.CLOSING ? 'CLOSING' : 'CLOSED',
          expected: WebSocket.OPEN
        });
      }
      
      
      // Sync strategem targeting state with game state
      if (window.syncStrategemTargeting) {
        window.syncStrategemTargeting(msg.gameState, userId);
      }
      // ✅ FIX: Skip for spectators - they should be handled by friendsContext
      // ✅ FIX: Ignore winner from another game (stale/delayed message) to prevent redirect loop
      if (msg.gameState?.winner && !isSpectating && !isWsMessageForDifferentSessionRef.current(msg.gameId)) {
        const didYouWin = msg.gameState.winner === userId;
        const isFriendlyMatch = msg.gameState.isFriendlyMatch || false;
        handleBattleEnd(didYouWin, gameIdRef.current, socket, isFriendlyMatch, 'reconnect-success-winner', msg.gameState);
        return;
      }
    }

    // ✅ AUTOBATTLER PAUSE/RESUME: Handle backend confirmation of pause/resume
    if (msg.type === "autobattler-paused" || msg.type === "autobattler-resumed") {
      // Notify speed control hook about backend confirmation
      if (speedControl.handleBackendPauseConfirmation) {
        speedControl.handleBackendPauseConfirmation(msg.success, msg.isPaused);
      }
      return; // Don't process further - this is just a confirmation message
    }
    
    // Handle weapon responses
    if (msg.type === "weapon-attached") {
      if (msg.success) {
        if (msg.needsDeployTargeting) {
          // Step 2: Weapon needs deploy targeting
          // ✅ CRITICAL: Clear weapon targeting mode before entering deploy targeting
          setWeaponTargetMode(false);
          setSelectedWeaponId(null);
          setSelectedWeaponInstanceId(null);
          
          setWeaponDeployTargetMode(true);
          setWeaponDeployTargetInfo({
            weaponInstanceId: msg.weapon.instanceId,
            weaponId: msg.weapon.id, // Include weapon ID for message lookup
            weaponName: msg.weapon.name, // Include weapon name for debugging
            targetUnitInstanceId: msg.targetUnit.instanceId
          });
          setError(t('battleUI.selectTargetWeapon'));
        } else {
          // Weapon attached successfully, no deploy targeting needed
          // ✅ Play equip sound when weapon is successfully equipped
          playSoundEffect('/sounds/equip.mp3', 0.5); // Play at 50% volume
          
          setWeaponTargetMode(false);
          setSelectedWeaponId(null);
          setSelectedWeaponInstanceId(null);
          setError("");
        }
      } else {
        setError(t('battleUI.connectionLost'));
      }
      // Don't return early - let it fall through to update gameState
    }

    if (msg.type === "weapon-deploy-completed") {
      // Backend sends two messages: (1) { success: true } then (2) { gameState } from sendGameStateToPlayers.
      // Treat as success when we have success: true OR gameState (second message has no success field).
      const isSuccess = msg.success === true || (msg.gameState != null);
      if (isSuccess) {
        // Weapon deploy effect completed successfully
        if (msg.success === true) {
          playSoundEffect('/sounds/equip.mp3', 0.5); // Play at 50% volume
        }
        setWeaponDeployTargetMode(false);
        setWeaponDeployTargetInfo(null);
        setWeaponTargetMode(false);
        setSelectedWeaponId(null);
        setSelectedWeaponInstanceId(null);
        setError("");
      } else {
        setError(t('battleUI.connectionLost'));
      }
      // Don't return early when we have gameState - let it fall through to update game state below
      if (!msg.gameState) return;
    }

    if (msg.type === "weapon-deploy-cancelled") {
      if (msg.success) {
        setWeaponDeployTargetMode(false);
        setWeaponDeployTargetInfo(null);
        setWeaponTargetMode(false);
        setSelectedWeaponId(null);
        setSelectedWeaponInstanceId(null);
        setError("");
      }
      if (!msg.gameState) return;
    }

    const showIncomingEmoteBubble = (emoteKey, durationMs = 3000) => {
      if (typeof emoteKey !== 'string' || !emoteKey.trim()) {
        throw new Error('showIncomingEmoteBubble: emoteKey is required');
      }
      setOpponentEmoteBubble({
        emoteKey,
        timestamp: Date.now()
      });
      setTimeout(() => {
        setOpponentEmoteBubble(null);
      }, durationMs);
    };

    // ✅ Handle emote message from opponent
    if (msg.type === "emote") {
      if (!msg.emoteKey || typeof msg.emoteKey !== 'string') {
        throw new Error('emote WebSocket message missing emoteKey');
      }
      const durationMs = Number.isFinite(Number(msg.durationMs)) ? Number(msg.durationMs) : 3000;
      showIncomingEmoteBubble(msg.emoteKey, durationMs);
      return; // Don't fall through to other handlers
    }

    // Opponent is hovering over an order; show subtle glow on that order for this player
    if (msg.type === 'opponent-looking-at-order') {
      setOpponentLookingAtInstanceId(msg.instanceId ?? null);
      return;
    }

    if (msg.type === 'opponent-battle-flashlight') {
      if (msg.gameId == null || String(msg.gameId).trim() === '') {
        throw new Error('opponent-battle-flashlight: gameId is required');
      }
      if (isWsMessageForDifferentSessionRef.current(msg.gameId)) {
        return;
      }
      if (msg.active === false) {
        setOpponentBattleFlashlight(null);
        return;
      }
      const nx = Number(msg.nx);
      const ny = Number(msg.ny);
      if (!Number.isFinite(nx) || !Number.isFinite(ny)) {
        throw new Error('opponent-battle-flashlight: active message requires finite nx and ny');
      }
      if (!isValidFlashlightColorKey(msg.color)) {
        throw new Error('opponent-battle-flashlight: active message requires a valid color');
      }
      setOpponentBattleFlashlight({ nx, ny, color: String(msg.color).toLowerCase() });
      return;
    }

    // ✅ FIX: Handle spectator-joined message from game WebSocket
    if (msg.type === "spectator-joined") {
      if (handleSpectatorJoined && msg.spectatorId && msg.username) {
        handleSpectatorJoined({
          spectatorId: msg.spectatorId,
          username: msg.username
        });
      }
      return; // Don't fall through to other handlers
    }

    // ✅ FIX: Handle spectator-left message from game WebSocket
    if (msg.type === "spectator-left") {
      if (handleSpectatorLeft && msg.spectatorId) {
        handleSpectatorLeft({
          spectatorId: msg.spectatorId,
          username: msg.username
        });
      }
      return; // Don't fall through to other handlers
    }

    // ✅ Handle attack-initiated message (damage delayed until animation completes)
    if (msg.type === "attack-initiated") {
      // ✅ FIX: Ignore messages for a different game (stale/delayed).
      // Use only msg.gameId (WebSocket session / activeGames key). gameState.gameId is internal and mismatches session id.
      const aiMsgGameId = msg.gameId;
      if (isWsMessageForDifferentSessionRef.current(aiMsgGameId)) {
        return;
      }
      if (msg.attackId && completedAttackIdsRef.current.has(msg.attackId)) {
        return;
      }
      // ✅ CRITICAL: For autobattler spectators, apply attack-initiated immediately.
      // Queuing pre-damage attack states can race with immediate attack-complete
      // and visually re-introduce "late damage" (old HP shown again first).
      if (isAutobattlerSpectator) {
        const applyUpdate = () => {
          // ✅ LOG ATTACK INSTANTLY: Log attack action immediately when message arrives
          if (msg.attackOutcome) {
            const outcome = msg.attackOutcome;
            
            // Check if this is a general attack
            if (outcome.isGeneralAttack) {
              // General attack - log as general-interaction
              const attackerUnit = outcome.attacker;
              if (attackerUnit && attackerUnit.name) {
                const newAction = {
                  type: 'general-interaction',
                  playerId: outcome.targetPlayerId,
                  timestamp: Date.now(),
                  source: {
                    id: attackerUnit.id,
                    name: attackerUnit.name,
                    instanceId: attackerUnit.instanceId,
                    type: 'unit'
                  },
                  effectType: 'attack',
                  amount: outcome.damageToTarget || 0,
                  attackerDamageTaken: outcome.damageToAttacker ?? 0,
                  message: `${attackerUnit.name} dealt ${outcome.damageToTarget || 0} damage`,
                  general: {
                    playerId: outcome.targetPlayerId,
                    healthBefore: outcome.targetHealthBefore || 0,
                    damageTaken: outcome.damageToTarget || 0
                  }
                };
              }
            } else {
              // Unit vs unit attack - server logs via actionLog; no client-side add
            }
          }
          
          // Update game state (units still alive, damage not applied yet)
          if (msg.gameState) {
            gameStateRefRef.current.current = msg.gameState;
            setGameState(msg.gameState);
            updateYourTurn(msg.yourTurn || false, 'attack-initiated');
          }
          // Play destroy sound when a drone is destroyed by attack (destroy effect)
          if (msg.attackOutcome?.targetDestroyed && msg.attackOutcome?.targetZone === 'opponent_drone') {
            playSoundEffect('/sounds/destroy.mp3', 0.6);
          }
          // Register attack for animation
          if (msg.attackId && msg.attackOutcome) {
            attackAnimations.registerAttack(msg.attackId, msg.attackOutcome, msg.isBotAttack || false);
            applyAttackInitiatedHealthFxRef.current(msg.attackId, msg.attackOutcome);
          } else {
            console.error(`❌ Cannot register attack: missing attackId or attackOutcome`, {
              hasAttackId: !!msg.attackId,
              hasAttackOutcome: !!msg.attackOutcome,
              msgType: msg.type
            });
          }

          // Apply local damage preview at hit timing for spectators.
          if (msg.attackId && msg.attackOutcome && !attackPreviewAppliedRef.current.has(msg.attackId)) {
            const previewTimeout = setTimeout(() => {
              if (completedAttackIdsRef.current.has(msg.attackId)) {
                return;
              }
              try {
                applyAttackHitOutcomePreview(msg.attackId, msg.attackOutcome);
              } catch (previewError) {
                console.error('Attack preview apply failed:', previewError);
              } finally {
                attackPreviewTimeoutsRef.current.delete(msg.attackId);
              }
            }, 420);
            attackPreviewTimeoutsRef.current.set(msg.attackId, previewTimeout);
          }
        };

        applyUpdate();
        return; // Don't process immediately for autobattler spectators
      }
      
      // ✅ LOG ATTACK INSTANTLY: Log attack action immediately when message arrives
      if (msg.attackOutcome) {
        const outcome = msg.attackOutcome;
        
        // Check if this is a general attack
        if (outcome.isGeneralAttack) {
          // General attack - log as general-interaction
          const attackerUnit = outcome.attacker;
          if (attackerUnit && attackerUnit.name) {
            const newAction = {
              type: 'general-interaction',
              playerId: outcome.targetPlayerId, // The player whose general was attacked
              timestamp: Date.now(),
              source: {
                id: attackerUnit.id,
                name: attackerUnit.name,
                instanceId: attackerUnit.instanceId,
                type: 'unit'
              },
              effectType: 'attack',
              amount: outcome.damageToTarget || 0,
              attackerDamageTaken: outcome.damageToAttacker ?? 0,
              message: `${attackerUnit.name} dealt ${outcome.damageToTarget || 0} damage`,
              general: {
                playerId: outcome.targetPlayerId,
                healthBefore: outcome.targetHealthBefore || 0,
                damageTaken: outcome.damageToTarget || 0
              }
            };
          }
        } else {
          // Unit vs unit attack - log as unit-attacked
          const attackerUnit = outcome.attacker;
          const targetUnit = outcome.target;
          const damageToTarget = outcome.damageToTarget || 0;
          if (attackerUnit && targetUnit && attackerUnit.name && targetUnit.name) {
            const newAction = {
              type: 'unit-attacked',
              playerId: outcome.attackerPlayerId,
              timestamp: Date.now(),
              amount: damageToTarget,
              attacker: {
                id: attackerUnit.id,
                name: attackerUnit.name,
                instanceId: attackerUnit.instanceId,
                died: outcome.attackerDestroyed || false,
                healthBefore: outcome.attackerHealthBefore || attackerUnit.health || 0,
                damageTaken: outcome.damageToAttacker || 0
              },
              target: {
                id: targetUnit.id,
                name: targetUnit.name,
                instanceId: targetUnit.instanceId,
                died: outcome.targetDestroyed || false,
                healthBefore: outcome.targetHealthBefore || targetUnit.health || 0,
                damageTaken: damageToTarget
              },
              message: `${attackerUnit.name} dealt ${damageToTarget} damage to ${targetUnit.name}`
            };
          }
        }
      }
      
      // Update game state (units still alive, damage not applied yet)
      if (msg.gameState) {
        gameStateRefRef.current.current = msg.gameState;
        setGameState(msg.gameState);
        updateYourTurn(msg.yourTurn || false, 'attack-initiated');
      }
      // Play destroy sound when a drone is destroyed by attack (destroy effect)
      if (msg.attackOutcome?.targetDestroyed && msg.attackOutcome?.targetZone === 'opponent_drone') {
        playSoundEffect('/sounds/destroy.mp3', 0.6);
      }
      // Register attack for animation
      // ✅ BOT ANIMATION FIX: Pass isBotAttack flag to auto-complete bot attacks
      // ✅ CRITICAL: Bot attacks should animate even when it's not the player's turn
      if (msg.attackId && msg.attackOutcome) {
        attackAnimations.registerAttack(msg.attackId, msg.attackOutcome, msg.isBotAttack || false);
        applyAttackInitiatedHealthFxRef.current(msg.attackId, msg.attackOutcome);
      } else {
        console.error(`❌ Cannot register attack: missing attackId or attackOutcome`, {
          hasAttackId: !!msg.attackId,
          hasAttackOutcome: !!msg.attackOutcome
        });
      }
      return; // Don't fall through to game-update handler
    }

    // ✅ Handle attack-complete message (damage applied, final state)
    if (msg.type === "attack-complete") {
      // ✅ FIX: Ignore messages for a different game (stale/delayed) - do not apply state or redirect.
      // Use only msg.gameId (session key); gameState.gameId is internal.
      const acMsgGameId = msg.gameId;
      if (isWsMessageForDifferentSessionRef.current(acMsgGameId)) {
        return;
      }
      if (msg.attackId) {
        const pendingPreviewTimeout = attackPreviewTimeoutsRef.current.get(msg.attackId);
        if (pendingPreviewTimeout) {
          clearTimeout(pendingPreviewTimeout);
          attackPreviewTimeoutsRef.current.delete(msg.attackId);
        }
        completedAttackIdsRef.current.add(msg.attackId);
        if (completedAttackIdsRef.current.size > 2000) {
          completedAttackIdsRef.current.clear();
          attackPreviewAppliedRef.current.clear();
        }
      }
      // ✅ FIX: Clear attack in progress flag when backend confirms attack is complete
      attackInProgressRef.current = false;

      if (msg.gameState) {
        updateStateRef.current(msg.gameState);
        if (window.syncDeployTargeting) {
          window.syncDeployTargeting(msg.gameState, userId);
        }
        if (window.syncStrategemTargeting) {
          window.syncStrategemTargeting(msg.gameState, userId);
        }
      }

      if (msg.attackOutcome) {
        applyAttackCompleteHealthFxRef.current(msg.attackId, msg.attackOutcome);
      }
      
      // ✅ CRITICAL FIX: Update dead targets/attackers tracking with corrected outcome from backend
      // The backend sends the corrected outcome with actual destruction status after protection effects
      // This is the AUTHORITATIVE source - always trust the backend's final determination
      if (msg.attackOutcome) {
        const outcome = msg.attackOutcome;
        
        // HP hold/countdown FX lifecycle is owned by handleDamageAnimationComplete + reconcile.
        // Do not clear here — attack-complete can arrive while countdown flyout is still running.

        // Update dead targets tracking based on corrected outcome from backend
        if (outcome.targetInstanceId) {
          if (outcome.targetDestroyed) {
            // Target was actually destroyed - add to dead targets
            attackAnimations.deadTargetsRef.current.add(outcome.targetInstanceId);
            // Clean up after delay to prevent interference with strategem animations
            if (attackAnimations.cleanupDeadTarget) {
              attackAnimations.cleanupDeadTarget(outcome.targetInstanceId);
            }
          } else {
            // Target survived (e.g., Isabela with protection) - remove from dead targets
            const wasInDeadTargets = attackAnimations.deadTargetsRef.current.has(outcome.targetInstanceId);
            attackAnimations.deadTargetsRef.current.delete(outcome.targetInstanceId);
          }
        }
        
        // Update dead attackers tracking based on corrected outcome from backend
        if (outcome.attackerInstanceId) {
          if (outcome.attackerDestroyed) {
            // Attacker was actually destroyed
            attackAnimations.deadAttackersRef.current.add(outcome.attackerInstanceId);
          } else {
            // Attacker survived - remove from dead attackers
            attackAnimations.deadAttackersRef.current.delete(outcome.attackerInstanceId);
          }
          
          // ✅ CRITICAL FIX: Show attacker if they survived (e.g., Isabela with protection)
          // Remove from hiddenUnits so the unit displays correctly
          // ✅ ELECTRON FIX: Always remove from hiddenUnits when attack-complete arrives
          // This ensures units are shown even if they were hidden during animation
          if (outcome.attackerInstanceId) {
            attackAnimations.setHiddenUnits(prev => {
              const next = new Set(prev);
              if (next.has(outcome.attackerInstanceId)) {
                next.delete(outcome.attackerInstanceId);
              }
              return next;
            });
          }
          
          // ✅ ELECTRON FIX: Also show target if it survived (in case it was hidden)
          if (outcome.targetInstanceId && !outcome.targetDestroyed) {
            attackAnimations.setHiddenUnits(prev => {
              const next = new Set(prev);
              if (next.has(outcome.targetInstanceId)) {
                next.delete(outcome.targetInstanceId);
              }
              return next;
            });
          }
        }
      }
      
      // ✅ FIX: Clean up dead attacker tracking after delay (allows slot death detection to check first)
      if (msg.attackOutcome?.attackerDestroyed && msg.attackOutcome?.attackerInstanceId) {
        attackAnimations.cleanupDeadAttacker(msg.attackOutcome.attackerInstanceId);
      }
      
      // ✅ CAMPAIGN TUTORIAL: Dispatch custom event for attack-complete (for tutorial progression)
      if (msg.attackOutcome) {
        window.dispatchEvent(new CustomEvent('attack-complete', {
          detail: {
            attackOutcome: msg.attackOutcome,
            gameState: msg.gameState
          }
        }));
      }
      
      // ✅ Unit-vs-unit: do not log a separate "unit1 destroyed unit2" entry here.
      // The damage entry ("dealt X damage" / "suffered X damage from") already shows the outcome.
      // For other killers (strategem, drone, etc.) the "destroyed" entry is still added from
      // actionHistoryManager when it detects completedOrders with destructionType !== 'combat'.
      
      // Update game state with final state (damage applied)
      // ✅ SPEED CONTROL: Only update state directly if NOT an autobattler spectator
      // (autobattler spectators already queued the update above)
      if (!isAutobattlerSpectator && msg.gameState) {
        // Authoritative post-combat state — use state sync so version tracking rejects stale game-update
        updateStateRef.current(msg.gameState, { skipVersionCheck: true });
        setActionLog(msg.gameState.actionLog ?? []);
        updateYourTurn(msg.yourTurn || false, 'attack-complete');
        storeOpponentStrategyId(msg.gameState);
        previousGameStateRef.current = JSON.parse(JSON.stringify(msg.gameState));
      }
      // Check for winner
      // ✅ FIX: Skip for spectators - they should be handled by friendsContext
      // ✅ FIX: Ignore winner from another game (stale/delayed message) to prevent redirect loop.
      // Use only msg.gameId (session key); gameState.gameId is internal.
      const attackCompleteMsgGameId = msg.gameId;
      if (msg.gameState?.winner && !isSpectating && !isWsMessageForDifferentSessionRef.current(attackCompleteMsgGameId)) {
        const didYouWin = msg.gameState.winner === userId;
        const isFriendlyMatch = msg.gameState.isFriendlyMatch || false;
        handleBattleEnd(didYouWin, gameIdRef.current, null, isFriendlyMatch, 'attack-complete-winner', msg.gameState);
        return;
      }
      return; // Don't fall through to game-update handler
    }

    // ✅ Handle strategem-initiated message (two-phase strategem system - effect deferred until animation completes)
    if (msg.type === "strategem-initiated") {
      // ✅ TWO-PHASE STRATEGEM: Store mapping from strategemInstanceId to backend's strategemAnimationId
      // This allows the onComplete callback to send the correct animation ID back
      if (msg.strategemInstanceId && msg.strategemAnimationId) {
        setPendingStrategemAnimInfo(pendingStrategemAnimationIdsRef.current, msg.strategemInstanceId, {
          strategemAnimationId: msg.strategemAnimationId,
          strategemName: msg.strategemName,
          strategemId: msg.strategemId,
          timestamp: Date.now()
        });
      }

      // Animation finished before this message (overlay ~530ms can beat strategem-initiated RTT).
      const flushResult = tryFlushDeferredStrategemComplete({
        deferredSet: strategemAnimationCompleteDeferredRef.current,
        pendingMap: pendingStrategemAnimationIdsRef.current,
        strategemInstanceId: msg.strategemInstanceId,
        wsReady: !!(ws && ws.readyState === 1),
        isSpectating,
        isAutobattlerSpectator,
      });
      if (flushResult.ok && ws && ws.readyState === 1) {
        ws.send(
          JSON.stringify({
            type: 'strategem-animation-complete',
            strategemAnimationId: flushResult.strategemAnimationId,
            strategemInstanceId: flushResult.strategemInstanceId,
            gameId: gameId,
          })
        );
        clearOverlayStrategemWatchdog(
          overlayStrategemWatchdogTimeoutsRef.current,
          msg.strategemInstanceId
        );
      }

      // ✅ OPPONENT STRATEGEM HANDLING: pendingAnimations use caster-relative your_/opponent_ (see mirrorStrategemToSlotForOpponentInitiatedViewer).
      // Mirror into viewer-relative slots whenever the local POV is not the caster's (including counter-strikes during our turn: yourTurn may be true).
      // Use deployAnimationViewerUserIdRef (activeUserId incl. autobattler playerBotId) so caster id matches gameState keys.
      // For YOUR strategem, the animation was already registered locally in targetStrategem.js
      const strategemCasterPlayerId =
        msg.strategemCasterPlayerId ??
        msg.gameState?.pendingStrategemEffects?.[msg.strategemAnimationId]?.playerId;
      const viewerPovIdForStrategemMirror =
        deployAnimationViewerUserIdRef.current != null
          ? deployAnimationViewerUserIdRef.current
          : effectiveUserId;
      const viewerSid =
        viewerPovIdForStrategemMirror != null ? String(viewerPovIdForStrategemMirror) : null;
      const casterSid =
        strategemCasterPlayerId != null ? String(strategemCasterPlayerId) : null;
      const knownCasterSameAsViewer =
        casterSid != null && viewerSid != null && casterSid === viewerSid;
      const likelyLocalCasterViaRef =
        casterSid == null &&
        msg.strategemInstanceId != null &&
        selfInitiatedStrategemInstanceIdsRef.current.has(String(msg.strategemInstanceId));
      const shouldMirrorPendingStrategemAnimations =
        msg.pendingAnimations &&
        msg.pendingAnimations.length > 0 &&
        !knownCasterSameAsViewer &&
        !likelyLocalCasterViaRef;
      if (shouldMirrorPendingStrategemAnimations && strategemTargetAnimations && strategemTargetAnimations.registerAnimation) {
        const viewerPovForFrom =
          deployAnimationViewerUserIdRef.current != null
            ? deployAnimationViewerUserIdRef.current
            : effectiveUserId;
        const resolveOpponentInitiatedFromPosition = (strategemIdForAnim) => {
          const sid = strategemIdForAnim != null ? Number(strategemIdForAnim) : NaN;
          if (usesCasterGeneralProjectileOrigin(sid)) {
            const fromGeneral = getStrategemCasterGeneralPixelPosition(
              viewerPovForFrom,
              strategemCasterPlayerId,
              true
            );
            if (fromGeneral) return fromGeneral;
          }
          const generalElement = document.querySelector('[data-zone="opponent_general"]');
          if (generalElement) {
            const rect = generalElement.getBoundingClientRect();
            return {
              x: rect.left + rect.width / 2,
              y: rect.top + rect.height / 2,
            };
          }
          return { x: window.innerWidth - 200, y: 100 };
        };
        const fromPosition = resolveOpponentInitiatedFromPosition(msg.strategemId);
        
        msg.pendingAnimations.forEach((anim, idx) => {
          const animationKey = `opponent-${msg.strategemAnimationId}-${idx}-${Date.now()}`;
          const toSlot = mirrorStrategemToSlotForOpponentInitiatedViewer(anim.toSlot);
          const ghostUnit = anim.targetType === 'unit'
            ? (
              resolveStrategemGhostUnitFromPreEffectState(
                msg.gameState,
                viewerPovForFrom,
                toSlot,
                anim.targetInstanceId
              )
              ?? anim.ghostUnit
              ?? null
            )
            : null;
          registerStrategemTargetAnimationRef.current({
            fromHand: { 
              instanceId: msg.strategemInstanceId, 
              position: fromPosition 
            },
            toSlot,
            targetType: anim.targetType,
            strategemId: msg.strategemId,
            strategemInstanceId: msg.strategemInstanceId,
            targetInstanceId: anim.targetInstanceId,
            targetGeneralPlayerId: anim.targetGeneralPlayerId,
            strategemCasterPlayerId: msg.strategemCasterPlayerId,
            effectType: anim.effectType ?? 'damage',
            ghostUnit,
            animationKey,
            isOpponentStrategem: true
          }, msg.gameState);
        });
      }
      
      // Update game state (effect NOT yet applied - this is the pre-effect state)
      if (msg.gameState) {
        gameStateRefRef.current.current = msg.gameState;
        setGameState(msg.gameState);
        updateYourTurn(msg.yourTurn || false, 'strategem-initiated');
      }

      const overlayWatchdogTargetType =
        msg.pendingAnimations?.[0]?.targetType ?? 'unit';
      const scheduleLocalOverlayWatchdog =
        (knownCasterSameAsViewer || likelyLocalCasterViaRef) && msg.strategemInstanceId;
      if (scheduleLocalOverlayWatchdog) {
        clearOverlayStrategemWatchdog(
          overlayStrategemWatchdogTimeoutsRef.current,
          msg.strategemInstanceId
        );
        const watchdogTimeoutId = scheduleOverlayStrategemCompleteWatchdog({
          strategemId: msg.strategemId,
          targetType: overlayWatchdogTargetType,
          strategemInstanceId: msg.strategemInstanceId,
          pendingMap: pendingStrategemAnimationIdsRef.current,
          deferredSet: strategemAnimationCompleteDeferredRef.current,
          wsReady: !!(ws && ws.readyState === 1),
          isSpectating,
          isAutobattlerSpectator,
          gameId,
          sendComplete: (payload) => {
            if (ws && ws.readyState === 1) {
              ws.send(
                JSON.stringify({
                  type: 'strategem-animation-complete',
                  ...payload,
                })
              );
            }
          },
        });
        if (watchdogTimeoutId != null) {
          const watchKey = normalizeStrategemInstanceKey(msg.strategemInstanceId);
          if (watchKey) {
            overlayStrategemWatchdogTimeoutsRef.current.set(watchKey, watchdogTimeoutId);
          }
        }
      }

      // For YOUR strategem: Animation is already registered by targetStrategem.js before WebSocket message was sent
      // For OPPONENT strategem: Animation was just registered above
      // When animation completes, onComplete callback will send strategem-animation-complete
      
      return; // Don't fall through to game-update handler
    }

    // ✅ Handle strategem-complete message (two-phase strategem system - effect has been applied)
    if (msg.type === "strategem-complete") {
      // ✅ FIX: Ignore messages for a different game (stale/delayed) - do not apply state or redirect
      const scMsgGameId = msg.gameId;
      if (isWsMessageForDifferentSessionRef.current(scMsgGameId)) {
        return;
      }

      // Clean up any pending animation ID mapping (in case timeout applied it)
      // Find and remove by strategemAnimationId
      if (msg.strategemAnimationId) {
        for (const [instanceId, info] of pendingStrategemAnimationIdsRef.current.entries()) {
          if (info.strategemAnimationId === msg.strategemAnimationId) {
            deletePendingStrategemAnimInfo(pendingStrategemAnimationIdsRef.current, instanceId);
            strategemAnimationCompleteDeferredRef.current.delete(normalizeStrategemInstanceKey(instanceId));
            clearOverlayStrategemWatchdog(overlayStrategemWatchdogTimeoutsRef.current, instanceId);
            break;
          }
        }
      }
      
      // Update game state with final state (effect applied)
      if (msg.gameState) {
        // ✅ FIX: Update previousGameStateRef BEFORE detecting actions
        // This ensures we can detect targets by comparing pre-effect and post-effect states
        const prevStateForStrategemDetection = previousGameStateRef.current;
        const viewerPovForStrategemHold =
          deployAnimationViewerUserIdRef.current != null
            ? deployAnimationViewerUserIdRef.current
            : userId;
        const strategemInstanceKey = msg.strategemInstanceId != null
          ? String(msg.strategemInstanceId)
          : null;
        const cachedStrategemHolds = strategemInstanceKey
          ? (pendingStrategemHealthHoldsRef.current.get(strategemInstanceKey) ?? [])
          : [];
        const strategemCompleteHealthHolds = applyStrategemCompleteHealthHolds({
          prevState: prevStateForStrategemDetection,
          nextState: msg.gameState,
          viewerUserId: viewerPovForStrategemHold,
          strategemInstanceId: msg.strategemInstanceId,
          activeAnimations: strategemActiveAnimationsRef.current,
          cachedHoldTargets: cachedStrategemHolds,
          resolveDamageAmount: (anim) => (
            anim?.strategemId != null
              ? getStrategemDamageAmountById(anim.strategemId, msg.gameState)
              : null
          ),
        });
        const bullyDamageIds = msg.gameState.bullyDamageInstanceIds;
        const filteredStrategemCompleteHolds = filterHealthHoldTargetsExcludingBullyDamage(
          strategemCompleteHealthHolds,
          bullyDamageIds
        );
        if (Array.isArray(bullyDamageIds)) {
          bullyDamageIds.forEach((instanceId) => {
            if (instanceId) {
              releasePendingProjectileHealthHold(pendingProjectileHealthHoldRef.current, instanceId);
              battlefieldHealthCountdown.clearCountdown(instanceId);
            }
          });
        }
        if (filteredStrategemCompleteHolds.length > 0) {
          holdDeployZoneHealthBeforeDamageRef.current(filteredStrategemCompleteHolds);
        }
        if (strategemInstanceKey) {
          pendingStrategemHealthHoldsRef.current.delete(strategemInstanceKey);
        }

        previousGameStateRef.current = JSON.parse(JSON.stringify(msg.gameState));
        
        const deferBullyFists = hasActiveStrategemAnimationsForInstance(
          strategemActiveAnimationsRef.current,
          msg.strategemInstanceId
        );
        console.log(`👊 BullyGhost CALL from strategem-complete, ids=${bullyDamageIds?.length}, deferFists=${deferBullyFists}`);
        startBullyDamageGhostSequence(prevStateForStrategemDetection, msg.gameState, {
          deferFistPlayback: deferBullyFists,
          strategemInstanceId: msg.strategemInstanceId,
        });
        delete msg.gameState.bullyDamageInstanceIds;

        gameStateRefRef.current.current = msg.gameState;
        setGameState(msg.gameState);
        setActionLog(msg.gameState.actionLog ?? []);
        updateYourTurn(msg.yourTurn || false, 'strategem-complete');
        storeOpponentStrategyId(msg.gameState);
        
        // Strategem targets: server sends full actionLog; no client-side target detection for log
        if (prevStateForStrategemDetection && msg.strategemAnimationId && false) {
          const strategemActionIndex = -1;
          if (strategemActionIndex !== -1) {
            const strategemAction = null;
            const strategemInstanceId = strategemAction.order?.instanceId;
            const strategemId = strategemAction.order?.id;
            
            if (strategemInstanceId && strategemId) {
              // Manually detect targets by comparing states
              const targets = [];
              const allPlayerIds = msg.gameState.players || Object.keys(msg.gameState.state);
              
              // ✅ FIX: Try to find the actual target from strategem animation metadata first
              // This is the most reliable way to identify which unit was targeted
              let actualTargetInstanceId = null;
              if (msg.gameState.pendingStrategemAnimations && Array.isArray(msg.gameState.pendingStrategemAnimations)) {
                const strategemAnimation = msg.gameState.pendingStrategemAnimations.find(
                  anim => anim.strategemInstanceId === strategemInstanceId || 
                         anim.strategemAnimationId === msg.strategemAnimationId
                );
                if (strategemAnimation && strategemAnimation.targetInstanceId) {
                  actualTargetInstanceId = strategemAnimation.targetInstanceId;
                }
              }
              
              // 1. Check for units killed by this strategem
              allPlayerIds.forEach(targetPlayerId => {
                const prevTargetPlayer = prevStateForStrategemDetection.state?.[targetPlayerId];
                const currTargetPlayer = msg.gameState.state?.[targetPlayerId];
                if (!prevTargetPlayer || !currTargetPlayer) return;
                
                const prevCompleted = prevTargetPlayer.completedOrders || [];
                const currCompleted = currTargetPlayer.completedOrders || [];
                
                currCompleted.forEach(destroyedUnit => {
                  if (!destroyedUnit || !destroyedUnit.instanceId) return;
                  
                  // Check if this unit was destroyed this turn by this strategem
                  const wasDestroyedThisTurn = !prevCompleted.some(
                    o => o && o.instanceId === destroyedUnit.instanceId
                  );
                  
                  if (wasDestroyedThisTurn && 
                      (destroyedUnit.destructionType === 'strategem' || 
                       (destroyedUnit.destroyedBy && destroyedUnit.destroyedBy.startsWith('Strategem ')))) {
                    // Extract strategem ID from destroyedBy (format: "Strategem 317 (Guided Missile)")
                    const strategemMatch = destroyedUnit.destroyedBy?.match(/Strategem (\d+)/);
                    if (strategemMatch && parseInt(strategemMatch[1]) === strategemId) {
                      // ✅ FIX: For single-target strategems, verify this is the actual target
                      // If we have actualTargetInstanceId from animation metadata, only add if it matches
                      if (actualTargetInstanceId && destroyedUnit.instanceId !== actualTargetInstanceId) {
                        return; // Skip - not the actual target
                      }
                      
                      // ✅ FIX: Find the unit in previous state to get healthBefore and calculate damageTaken
                      let healthBefore = 0;
                      let damageTaken = 0;
                      
                      // Try to find the unit in previous state to get accurate healthBefore
                      let foundPrevUnit = false;
                      ['front', 'flank'].forEach(zone => {
                        if (foundPrevUnit) return; // Already found
                        
                        const prevUnit = prevTargetPlayer.playedOrders?.[zone]?.find(
                          u => u && u.instanceId === destroyedUnit.instanceId
                        );
                        if (prevUnit) {
                          foundPrevUnit = true;
                          healthBefore = prevUnit.health || prevUnit.maxHealth || 0;
                          const finalHealth = destroyedUnit.finalHealth !== undefined ? destroyedUnit.finalHealth : 0;
                          damageTaken = healthBefore - finalHealth;
                          
                          // If we couldn't calculate damageTaken from finalHealth, use healthBefore as fallback
                          // (unit was killed, so it took at least that much damage)
                          if (damageTaken <= 0 && healthBefore > 0) {
                            damageTaken = healthBefore;
                          }
                        }
                      });
                      
                      // Fallback: if we couldn't find the unit in previous state, use destroyedUnit data
                      if (!foundPrevUnit) {
                        healthBefore = destroyedUnit.health || destroyedUnit.maxHealth || 0;
                        // If unit was killed, it took at least its full health as damage
                        damageTaken = healthBefore;
                      }
                      
                      targets.push({
                        id: destroyedUnit.id,
                        name: destroyedUnit.name,
                        instanceId: destroyedUnit.instanceId,
                        died: true,
                        healthBefore: healthBefore,
                        damageTaken: damageTaken
                      });
                    }
                  }
                });
              });
              
              // 1.5. Kinetron Blast (111): unit decommissioned - disappeared from battlefield and added to hand
              if (strategemId === 111 && targets.length === 0) {
                allPlayerIds.forEach(targetPlayerId => {
                  const prevTargetPlayer = prevStateForStrategemDetection.state?.[targetPlayerId];
                  const currTargetPlayer = msg.gameState.state?.[targetPlayerId];
                  if (!prevTargetPlayer || !currTargetPlayer) return;
                  ['front', 'flank'].forEach(zone => {
                    if (!prevTargetPlayer.playedOrders?.[zone]) return;
                    prevTargetPlayer.playedOrders[zone].forEach((prevUnit, index) => {
                      if (!prevUnit || !prevUnit.instanceId) return;
                      const currUnitOnBattlefield = currTargetPlayer.playedOrders?.[zone]?.[index];
                      const unitDisappeared = !currUnitOnBattlefield || currUnitOnBattlefield.instanceId !== prevUnit.instanceId;
                      if (!unitDisappeared) return;
                      const wasInHandBefore = prevTargetPlayer.hand?.some(handUnit => handUnit && handUnit.id === prevUnit.id);
                      const wasAddedToHand = currTargetPlayer.hand?.some(handUnit => handUnit && handUnit.id === prevUnit.id);
                      const wasDecommissioned = !wasInHandBefore && wasAddedToHand;
                      if (wasDecommissioned) {
                        const alreadyAdded = targets.some(t => t.instanceId === prevUnit.instanceId);
                        if (!alreadyAdded) {
                          targets.push({
                            id: prevUnit.id,
                            name: prevUnit.name,
                            instanceId: prevUnit.instanceId,
                            decommissioned: true
                          });
                        }
                      }
                    });
                  });
                });
              }
              
              // 2. Check for units that took damage from this strategem (but didn't die)
              // ✅ FIX: For single-target strategems like Guided Missile, only find ONE target
              // Check if this strategem targets a single unit (most strategems do)
              const isSingleTargetStrategem = strategemId === 317 || // Guided Missile
                                             strategemId === 55 ||  // Vein Sever
                                             strategemId === 56 ||  // Viral Echo
                                             strategemId === 57 ||  // etc. (add more as needed)
                                             true; // Default to single-target for safety
              
              // Only proceed if we haven't found a target yet (for single-target strategems)
              if (!isSingleTargetStrategem || targets.length === 0) {
                allPlayerIds.forEach(targetPlayerId => {
                  const prevTargetPlayer = prevStateForStrategemDetection.state?.[targetPlayerId];
                  const currTargetPlayer = msg.gameState.state?.[targetPlayerId];
                  if (!prevTargetPlayer || !currTargetPlayer) return;
                  
                  ['front', 'flank'].forEach(zone => {
                    if (!currTargetPlayer.playedOrders?.[zone]) return;
                    
                    currTargetPlayer.playedOrders[zone].forEach((currUnit, idx) => {
                      // ✅ FIX: For single-target strategems, stop if we already found a target
                      if (isSingleTargetStrategem && targets.length > 0) return;
                      
                      if (!currUnit || !currUnit.instanceId) return;
                      
                      // Find the same unit in previous state
                      const prevUnit = prevTargetPlayer.playedOrders?.[zone]?.[idx];
                      if (!prevUnit || prevUnit.instanceId !== currUnit.instanceId) return;
                      
                      // Check if unit took damage this turn
                      const prevHealth = prevUnit.health || prevUnit.maxHealth || 0;
                      const currHealth = currUnit.health || currUnit.maxHealth || 0;
                      const prevDamageTaken = prevUnit.damageTaken || 0;
                      const currDamageTaken = currUnit.damageTaken || 0;
                      
                      const healthDecreased = currHealth < prevHealth;
                      const damageTakenIncreased = currDamageTaken > prevDamageTaken;
                      
                      // ✅ FIX: For single-target strategems, only add if this is the actual target
                      // Check if we have actualTargetInstanceId from animation metadata
                      if (isSingleTargetStrategem && actualTargetInstanceId) {
                        // Only add if this is the actual target
                        if (currUnit.instanceId !== actualTargetInstanceId) {
                          return; // Skip - not the actual target
                        }
                      }
                      
                      // If unit took damage and is still alive, it might be a target
                      if ((healthDecreased || damageTakenIncreased) && currUnit.health > 0) {
                        // Check if we already added this target (might have been added as killed)
                        const alreadyAdded = targets.some(t => t.instanceId === currUnit.instanceId);
                        if (!alreadyAdded) {
                          // For single-target strategems, only add if we haven't found a target yet
                          // or if this is the actual target from animation metadata
                          const shouldAdd = !isSingleTargetStrategem || 
                                           targets.length === 0 || 
                                           (actualTargetInstanceId && currUnit.instanceId === actualTargetInstanceId);
                          
                          if (shouldAdd) {
                            // ✅ FIX: Include health information for damaged units
                            const healthBefore = prevHealth;
                            const damageTaken = healthDecreased ? (prevHealth - currHealth) : (damageTakenIncreased ? (currDamageTaken - prevDamageTaken) : 0);
                            
                            targets.push({
                              id: currUnit.id,
                              name: currUnit.name,
                              instanceId: currUnit.instanceId,
                              damaged: true,
                              healthBefore: healthBefore,
                              damageTaken: damageTaken > 0 ? damageTaken : undefined
                            });
                          }
                        }
                      }
                    });
                  });
                });
              }
              
              // ✅ FIX: Check for units spawned/deployed by this strategem
              // This handles strategems like Forbidden Clone Protocol (324) that deploy new units
              allPlayerIds.forEach(targetPlayerId => {
                const prevTargetPlayer = prevStateForStrategemDetection.state?.[targetPlayerId];
                const currTargetPlayer = msg.gameState.state?.[targetPlayerId];
                if (!prevTargetPlayer || !currTargetPlayer) return;
                
                ['front', 'flank'].forEach(zone => {
                  const prevZone = prevTargetPlayer.playedOrders?.[zone] || [];
                  const currZone = currTargetPlayer.playedOrders?.[zone] || [];
                  
                  // Check for new units that appeared in this zone
                  currZone.forEach((currUnit, idx) => {
                    if (!currUnit || !currUnit.instanceId) return;
                    
                    // Check if this unit is new (wasn't in previous state at this position)
                    const prevUnit = prevZone[idx];
                    const isNewUnit = !prevUnit || prevUnit.instanceId !== currUnit.instanceId;
                    
                    if (isNewUnit) {
                      let isSpawnedByStrategem = false;
                      
                      // Method 1: Check if unit has deployedBy metadata linking to this strategem
                      if (currUnit.deployedBy && 
                          currUnit.deployedBy.type === 'strategem' &&
                          (currUnit.deployedBy.instanceId === strategemInstanceId || 
                           currUnit.deployedBy.id === strategemId)) {
                        isSpawnedByStrategem = true;
                      }
                      
                      // Method 2: Fallback - check for strategem-specific spawned units
                      // Forbidden Clone Protocol (324) spawns Animated Fleshsacks (ID 328)
                      if (!isSpawnedByStrategem && strategemId === 324 && currUnit.id === 328) {
                        // Check if this unit appeared in the same turn as the strategem
                        // and wasn't in the previous state
                        const unitDeployedThisTurn = currUnit.deployedTurn === msg.gameState.turnNumber ||
                                                     currUnit.playedTurn === msg.gameState.turnNumber;
                        if (unitDeployedThisTurn) {
                          isSpawnedByStrategem = true;
                        }
                      }
                      
                      if (isSpawnedByStrategem) {
                        // This unit was spawned by this strategem
                        const alreadyAdded = targets.some(t => t.instanceId === currUnit.instanceId);
                        if (!alreadyAdded) {
                          targets.push({
                            id: currUnit.id,
                            name: currUnit.name,
                            instanceId: currUnit.instanceId,
                            spawned: true,
                            deployed: true
                          });
                        }
                      }
                    }
                  });
                });
              });
              
              // ✅ FIX: Check for general damage/healing from this strategem
              // This handles strategems like Kamikaze (314) that deal damage to generals
              allPlayerIds.forEach(targetPlayerId => {
                const prevTargetPlayer = prevStateForStrategemDetection.state?.[targetPlayerId];
                const currTargetPlayer = msg.gameState.state?.[targetPlayerId];
                if (!prevTargetPlayer || !currTargetPlayer) return;
                
                // Check if general health changed
                const prevGeneralHealth = prevTargetPlayer.health || 0;
                const currGeneralHealth = currTargetPlayer.health || 0;
                const generalHealthDecreased = currGeneralHealth < prevGeneralHealth;
                const generalHealthIncreased = currGeneralHealth > prevGeneralHealth;
                
                if (generalHealthDecreased || generalHealthIncreased) {
                  // Check if this strategem caused the general interaction
                  const generalInteractions = currTargetPlayer.generalInteractions || [];
                  const strategemInteraction = generalInteractions.find(
                    interaction => interaction.sourceType === 'strategem' &&
                                  (interaction.sourceInstanceId === strategemInstanceId || 
                                   interaction.sourceId === strategemId) &&
                                  interaction.interactionTurn === msg.gameState.turnNumber
                  );
                  
                  if (strategemInteraction) {
                    // This strategem affected the general
                    const generalTarget = {
                      id: null,
                      name: 'General',
                      instanceId: null,
                      type: 'general',
                      playerId: targetPlayerId,
                      died: false,
                      healthBefore: prevGeneralHealth,
                      damageTaken: generalHealthDecreased ? (prevGeneralHealth - currGeneralHealth) : undefined,
                      healed: generalHealthIncreased,
                      healAmount: generalHealthIncreased ? (currGeneralHealth - prevGeneralHealth) : undefined
                    };
                    
                    // Check if we already added this general (shouldn't happen, but safety check)
                    const alreadyAdded = targets.some(t => t.type === 'general' && t.playerId === targetPlayerId);
                    if (!alreadyAdded) {
                      targets.push(generalTarget);
                    }
                  }
                }
              });
              
              // 6. Phase Traverse (186): detect unit repositioned from one zone to the other (front <-> flank)
              if (strategemId === 186 && targets.length === 0) {
                const playerIdWhoPlayed = strategemAction.playerId;
                const prevPlayer = prevStateForStrategemDetection.state?.[playerIdWhoPlayed];
                const currPlayer = msg.gameState.state?.[playerIdWhoPlayed];
                if (prevPlayer?.playedOrders && currPlayer?.playedOrders) {
                  ['front', 'flank'].forEach((fromZone) => {
                    const toZone = fromZone === 'front' ? 'flank' : 'front';
                    if (!prevPlayer.playedOrders[fromZone]) return;
                    prevPlayer.playedOrders[fromZone].forEach((prevUnit) => {
                      if (!prevUnit || !prevUnit.instanceId) return;
                      const stillInFrom = currPlayer.playedOrders[fromZone]?.some(u => u?.instanceId === prevUnit.instanceId);
                      if (stillInFrom) return;
                      const nowInTo = currPlayer.playedOrders[toZone]?.some(u => u?.instanceId === prevUnit.instanceId);
                      if (!nowInTo) return;
                      targets.push({
                        id: prevUnit.id,
                        name: prevUnit.name,
                        instanceId: prevUnit.instanceId,
                        repositioned: true,
                        fromZone,
                        toZone
                      });
                    });
                  });
                }
              }
              
              // Update the strategem action with targets
              if (targets.length > 0) {
                // ✅ FIX: Generate appropriate message based on target types
                // Separate targets by type: killed, damaged, spawned, general, repositioned (Phase Traverse)
                const killedTargets = targets.filter(tgt => tgt.died);
                const damagedTargets = targets.filter(tgt => tgt.damaged && !tgt.died);
                const spawnedTargets = targets.filter(tgt => tgt.spawned || tgt.deployed);
                const decommissionedTargets = targets.filter(tgt => tgt.decommissioned);
                const generalTargets = targets.filter(tgt => tgt.type === 'general');
                const repositionedTargets = targets.filter(tgt => tgt.repositioned);
                
                let message = '';
                if (repositionedTargets.length === 1) {
                  const target = repositionedTargets[0];
                  message = target.fromZone === 'front'
                    ? t('battleUI.actionHistory.movedFromFrontToFlank', { unitName: target.name, defaultValue: `Moved ${target.name} from front to flank.` })
                    : t('battleUI.actionHistory.movedFromFlankToFront', { unitName: target.name, defaultValue: `Moved ${target.name} from flank to front.` });
                } else if (targets.length === 1) {
                  const target = targets[0];
                  const strategemName = strategemAction.order.name;
                  if (target.died) {
                    message = t('battleUI.actionHistory.strategemHasDestroyed', { strategemName, targetName: target.name, defaultValue: `${strategemName} has destroyed ${target.name}` });
                  } else if (target.spawned || target.deployed) {
                    message = t('battleUI.actionHistory.strategemHasSpawned', { strategemName, targetName: target.name, defaultValue: `${strategemName} has spawned ${target.name}` });
                  } else if (target.decommissioned) {
                    message = t('battleUI.actionHistory.strategemHasDecommissioned', { strategemName, targetName: target.name, defaultValue: `${strategemName} has decommissioned ${target.name}` });
                  } else if (target.type === 'general') {
                    message = t('battleUI.actionHistory.strategemRequestedOn', { strategemName, targetName: target.name, defaultValue: `${strategemName} was requested on ${target.name}` });
                  } else {
                    message = t('battleUI.actionHistory.strategemRequestedOn', { strategemName, targetName: target.name, defaultValue: `${strategemName} was requested on ${target.name}` });
                  }
                } else {
                  // Multiple targets - describe what happened (localized)
                  const parts = [];
                  if (killedTargets.length > 0) {
                    parts.push(t('battleUI.actionHistory.strategemDestroyedUnits', { count: killedTargets.length, defaultValue: `destroyed ${killedTargets.length} unit(s)` }));
                  }
                  if (damagedTargets.length > 0) {
                    parts.push(t('battleUI.actionHistory.strategemDamagedUnits', { count: damagedTargets.length, defaultValue: `damaged ${damagedTargets.length} unit(s)` }));
                  }
                  if (decommissionedTargets.length > 0) {
                    parts.push(t('battleUI.actionHistory.strategemDecommissionedUnits', { count: decommissionedTargets.length, defaultValue: `decommissioned ${decommissionedTargets.length} unit(s)` }));
                  }
                  if (spawnedTargets.length > 0) {
                    parts.push(t('battleUI.actionHistory.strategemSpawnedUnits', { count: spawnedTargets.length, defaultValue: `spawned ${spawnedTargets.length} unit(s)` }));
                  }
                  if (generalTargets.length > 0) {
                    parts.push(t('battleUI.actionHistory.strategemAffectedGeneral', { defaultValue: 'affected general' }));
                  }
                  
                  if (parts.length > 0) {
                    message = `${strategemAction.order.name} has ${parts.join(', ')}`;
                  } else {
                    message = t('battleUI.actionHistory.strategemRequestedOnTargets', { strategemName: strategemAction.order.name, count: targets.length, defaultValue: `${strategemAction.order.name} was requested on ${targets.length} target(s)` });
                  }
                }
                
              }
            }
          }
        }
      }
      
      // Check for winner
      // ✅ FIX: Ignore winner from another game (stale/delayed message) to prevent redirect loop
      if (msg.gameState?.winner && !isSpectating && !isWsMessageForDifferentSessionRef.current(msg.gameId)) {
        const didYouWin = msg.gameState.winner === userId;
        const isFriendlyMatch = msg.gameState.isFriendlyMatch || false;
        handleBattleEnd(didYouWin, gameIdRef.current, null, isFriendlyMatch, 'strategem-complete-winner', msg.gameState);
        return;
      }
      
      return; // Don't fall through to game-update handler
    }

    // ✅ Handle state desync errors - automatically recover by updating game state
    if (msg.type === "state-desync-error") {
      if (isWsMessageForDifferentSessionRef.current(msg.gameId)) {
        return;
      }
      console.warn('🔄 State desync detected, recovering with backend state:', msg.message);
      
      // ✅ REFACTORED: Use hook's unlockUI method (via ref to avoid stale closure)
      unlockUIRef.current();
      
      if (msg.gameState) {
        // Force complete state update - backend state is authoritative
        // ✅ FIX: Only set previousGameStateRef if not already initialized (preserve baseline for action detection)
        if (!previousGameStateRef.current) {
          previousGameStateRef.current = JSON.parse(JSON.stringify(msg.gameState));
        }
        // ✅ REFACTORED: Use hook's updateState (via ref to avoid stale closure)
        updateStateRef.current(msg.gameState, { skipVersionCheck: true, skipStabilization: true });
        setYourTurn(msg.yourTurn || false);
        storeOpponentStrategyId(msg.gameState);
        // Clear action queue on desync - backend state is authoritative
        actionQueueRef.current = [];
        isProcessingActionRef.current = false;
        lastSentPlayOrderRef.current = null;
        lastStateVersionRef.current = msg.gameState.stateVersion || null;
        // Clear selections that might be invalid
        setSelectedOrderId(null);
        setSelectedOrderInstanceId(null);
        setError(t('battleUI.gameStateOutOfSync'));
        setTimeout(() => setError(''), 3000);
        // Force UI update by updating unit registry
        if (msg.gameState) {
          updateUnitRegistry(msg.gameState, unitRegistryRef);
        }
      }
      return;
    }

    // ✅ AUTOBATTLER FIX: Handle spectate-update messages for autobattler spectators
    // Check if this is an autobattler game by looking at the gameState or message properties
    if (msg.type === "spectate-update") {
      const isAutobattlerGame = msg.gameState?.isAutobattler || msg.gameId?.includes('_autobattler_');
      const hasGameState = !!msg.gameState;
      const willProcess = isAutobattlerGame && hasGameState;

      if (willProcess) {
        // ✅ FIX: Clear reconnecting state when gameState is received
        // This prevents getting stuck in "Reconnecting..." state
        if (reconnectingRef.current) {
          reconnectingRef.current = false;
          setReconnecting(false);
          wsConnectingRef.current = false;
          if (reconnectingTimeoutRef.current) {
            clearTimeout(reconnectingTimeoutRef.current);
            reconnectingTimeoutRef.current = null;
          }
        }
        
        // ✅ SPEED CONTROL: Queue update instead of applying immediately
        const applyUpdate = () => {
          gameStateRefRef.current.current = msg.gameState;
          setGameState(msg.gameState);
          setYourTurn(false); // Spectators never have their turn
          
          if (msg.gameState) {
            setActionLog(msg.gameState.actionLog ?? []);
          }
          previousGameStateRef.current = JSON.parse(JSON.stringify(msg.gameState));
        };

        // Queue the update (will apply immediately if speed control disabled)
        // Only use speed control for autobattler spectators
        if (isAutobattlerSpectatorRef.current && speedControlQueueUpdateRef.current) {
          speedControlQueueUpdateRef.current(msg, applyUpdate);
        } else {
          // Normal spectating or speed control not available - apply immediately
          applyUpdate();
        }
      }
      return;
    }

    if (
      msg.type === "game-update" ||
      msg.type === "turn-played" ||
      msg.type === "turn-ended" ||
      msg.type === "turn-timeout" ||
      msg.type === "player-disconnected" ||
      msg.type === "player-abandoned" ||
      msg.type === "player-resigned" ||
      msg.type === "counter-strike-played" ||
      msg.type === "weapon-attached" ||
      msg.type === "weapon-deploy-completed"
    ) {
      // ✅ FIX: Ignore messages for a different game (stale/delayed) - prevents redirect to reward screen mid-game
      // Use only msg.gameId (WebSocket session / activeGames key). gameState.gameId is internal and mismatches session id.
      const rawMsgGameId = msg.gameId;
      if (isWsMessageForDifferentSessionRef.current(rawMsgGameId)) {
        return;
      }

      // ✅ REFACTORED: Use hook's unlockUI method (via ref to avoid stale closure)
      unlockUIRef.current();

      // ✅ FIX: Clear reconnecting state when gameState is received in game-update
      // This prevents getting stuck in "Reconnecting..." state for autobattler spectators
      // who might receive game-update instead of reconnect-success
      if (reconnectingRef.current && msg.gameState) {
        reconnectingRef.current = false;
        setReconnecting(false);
        wsConnectingRef.current = false;
        if (reconnectingTimeoutRef.current) {
          clearTimeout(reconnectingTimeoutRef.current);
          reconnectingTimeoutRef.current = null;
        }
      }

      // Deploy VFX consumed at top of onmessage (tryConsumePendingDeployAnimationsForViewer)

      // ✅ FIX: Handle player-resigned message - start transition if winner is set
      // ✅ FIX: Skip for spectators - they should be handled by friendsContext
      // ✅ FIX: Ignore resignation from another game (stale/delayed message) to prevent redirect loop
      if (msg.type === "player-resigned" && msg.gameState?.winner && !isSpectating && !isWsMessageForDifferentSessionRef.current(msg.gameId)) {
        if (msg.gameState) {
          storeOpponentStrategyId(msg.gameState);
        }
        const didYouWin = String(msg.gameState.winner) === String(userId);
        const isFriendlyMatch = msg.gameState?.isFriendlyMatch || false;
        handleBattleEnd(didYouWin, gameIdRef.current, ws, isFriendlyMatch, 'player-resigned-winner', msg.gameState);
        return;
      }
      
      if (msg.gameState) {
        // OLD ATTACK DETECTION CODE REMOVED - Now using attack-initiated message flow
        // This old code was detecting attacks by comparing gameState, but the new flow
        // uses attack-initiated message which is sent BEFORE damage is applied
        const prevState = previousGameStateRef.current;
        if (false && prevState && !activeAttack) { // DISABLED - using new attack-initiated flow
          // Check for attacks (both player and bot units that just attacked)
          const allPlayerIds = msg.gameState.players || Object.keys(msg.gameState.state);
          const currentTurn = msg.gameState.turnNumber || msg.gameState.turn;
          
          allPlayerIds.forEach(playerId => {
            const prevPlayer = prevState.state?.[playerId];
            const currPlayer = msg.gameState.state?.[playerId];
            
            if (!prevPlayer || !currPlayer || !prevPlayer.playedOrders || !currPlayer.playedOrders) return;
            
            ['front', 'flank'].forEach(zone => {
              if (!prevPlayer.playedOrders[zone] || !currPlayer.playedOrders[zone]) return;
              
              prevPlayer.playedOrders[zone].forEach((prevUnit, idx) => {
                if (!prevUnit) return;
                
                const currUnit = currPlayer.playedOrders[zone]?.[idx];
                if (!currUnit || currUnit.instanceId !== prevUnit.instanceId) return;
                
                // Check if unit just attacked (lastAttackTurn changed)
                const prevLastAttack = prevUnit.lastAttackTurn;
                const currLastAttack = currUnit.lastAttackTurn;
                
                if (prevLastAttack !== currLastAttack && currLastAttack === currentTurn) {
                  // Unit just attacked - trigger animation
                  // For player attacks, use stored pending attack info if available
                  const isPlayerAttack = playerId === userId;
                  let attackInfo = null;
                  
                  if (isPlayerAttack && pendingAttackInfoRef.current) {
                    // Use stored pending attack info
                    attackInfo = pendingAttackInfoRef.current;
                    // Clear pending attack info after using it
                    pendingAttackInfoRef.current = null;
                  } else {
                    // Bot attack or no stored info - find actual target by checking opponent units that took damage
                    const opponentId = allPlayerIds.find(id => id !== playerId);
                    let foundTarget = null;
                    
                    if (opponentId) {
                      const opponent = msg.gameState.state[opponentId];
                      const prevOpponent = prevState.state?.[opponentId];
                      
                      if (opponent && prevOpponent) {
                        // Prioritize unit attacks over general attacks (more visually interesting)
                        // Check opponent units for damage or death first
                        ['front', 'flank'].forEach(oppZone => {
                          if (foundTarget) return; // Already found target
                          
                          if (prevOpponent.playedOrders?.[oppZone] && opponent.playedOrders?.[oppZone]) {
                            prevOpponent.playedOrders[oppZone].forEach((prevTarget, targetIdx) => {
                              if (foundTarget || !prevTarget) return;
                              
                              const currTarget = opponent.playedOrders[oppZone]?.find(t => t && t.instanceId === prevTarget.instanceId);
                              
                              // Check if target died or took damage
                              const died = !currTarget && prevTarget;
                              const tookDamage = currTarget && currTarget.instanceId === prevTarget.instanceId && 
                                                 currTarget.health < prevTarget.health;
                              
                              if (died || tookDamage) {
                                foundTarget = {
                                  type: 'unit',
                                  targetZone: oppZone,
                                  targetIndex: targetIdx,
                                  targetPlayerId: opponentId,
                                };
                              }
                            });
                          }
                        });
                        
                        // If no unit was attacked, check if general was attacked (health decreased)
                        if (!foundTarget && opponent.health < prevOpponent.health) {
                          foundTarget = {
                            type: 'general',
                            targetZone: 'general',
                            targetIndex: null,
                            targetPlayerId: opponentId,
                          };
                        }
                      }
                    }
                    
                    // Use found target or fallback to general attack
                    if (foundTarget) {
                      attackInfo = {
                        attackerZone: zone,
                        attackerIndex: idx,
                        attackerPlayerId: playerId,
                        targetZone: foundTarget.targetZone === 'general' ? 'general' : `opponent_${foundTarget.targetZone}`,
                        targetIndex: foundTarget.targetIndex,
                        targetPlayerId: foundTarget.targetPlayerId,
                      };
                    } else {
                      // Fallback: assume general attack if no target found
                      attackInfo = {
                        attackerZone: zone,
                        attackerIndex: idx,
                        attackerPlayerId: playerId,
                        targetZone: 'general',
                        targetIndex: null,
                        targetPlayerId: playerId === userId ? opponentId : userId,
                      };
                    }
                  }
                  
                  setActiveAttack({
                    attacker: currUnit,
                    ...attackInfo,
                    startTime: Date.now(),
                  });
                  
                  // Clear after animation completes (~600ms total - must complete before damage animations)
                  setTimeout(() => {
                    setActiveAttack(null);
                  }, 600);
                }
              });
            });
          });
        }
        
        // Update unit registry with PREVIOUS state BEFORE detecting actions
        // This ensures we have a snapshot of all units that existed before state changes
        // We update with previous state so we can reference units even after they're removed
        const prevStateForActions = previousGameStateRef.current;
        if (prevStateForActions) {
          updateUnitRegistry(prevStateForActions, unitRegistryRef);
        }
        
        // ALSO update registry with CURRENT state BEFORE detecting actions
        // This ensures we have the latest data (like lastAttackTurn) when checking current state
        // The registry will have both old and new data, with new data taking precedence
        if (msg.gameState) {
          updateUnitRegistry(msg.gameState, unitRegistryRef);
        }
        
        // Detect actions by comparing previous and current game state
        // ✅ FIX: Always run detection on every state update to catch kills instantly
        if (prevStateForActions && msg.gameState) {
          // Play out-of-charges sound when a drone was removed after spending its last charge; skip attack-complete (destroy sound plays on attack-initiated)
          if (msg.type !== 'attack-complete') {
            const players = msg.gameState.players || Object.keys(msg.gameState.state || {});
            for (const playerId of players) {
              const prevDrones = prevStateForActions.state?.[playerId]?.drones?.length ?? 0;
              const currDrones = msg.gameState.state?.[playerId]?.drones?.length ?? 0;
              if (prevDrones > currDrones) {
                playSoundEffect('/sounds/out_of_charges.mp3', 0.6);
                break;
              }
            }
          }
          // ✅ DEBUG: Log state comparison for bot turns
          if (msg.type === 'turn-played') {
            const botId = msg.gameState.players?.find(p => p === 'bot' || String(p).includes('bot'));
            // Debug logging disabled for bot turn tracking
          }
        }
        // ✅ FIX: Update previousGameStateRef with deep copy to avoid reference issues
        // This ensures the next comparison will detect new actions correctly
        // ✅ CRITICAL: Only update AFTER action detection, so we compare the correct states
        // ✅ CRITICAL: For 'turn-ended', this sets the baseline for bot turn detection
        // ✅ CRITICAL: For 'turn-played', this sets the baseline for next turn detection
        if (msg.gameState) {
          if (prevStateForActions) {
            const viewerPovForInstantStrategem =
              deployAnimationViewerUserIdRef.current != null
                ? deployAnimationViewerUserIdRef.current
                : userId;
            const instantStrategemHolds = resolveInstantExecuteStrategemHealthHolds(
              prevStateForActions,
              msg.gameState,
              viewerPovForInstantStrategem
            );
            const filteredInstantHolds = filterHealthHoldTargetsExcludingBullyDamage(
              instantStrategemHolds.holdTargets,
              msg.gameState.bullyDamageInstanceIds
            );
            if (filteredInstantHolds.length > 0) {
              holdDeployZoneHealthBeforeDamageRef.current(filteredInstantHolds);
              instantStrategemHolds.newStrategems.forEach(({
                strategemInstanceId,
                strategemId,
                playerId,
                strategem,
              }) => {
                if (strategemInstanceId == null) {
                  return;
                }
                const completedEntry = strategem
                  ?? findCompletedStrategemEntry(msg.gameState, playerId, strategemInstanceId);
                const flushKind = resolveInstantExecuteFlushKind(strategemId, completedEntry);
                if (shouldDeferInstantExecuteDamageToOverlay(strategemId)) {
                  pendingBroadcastStrategemDamageRef.current.set(String(strategemInstanceId), {
                    strategemId,
                    holdTargets: filteredInstantHolds,
                  });
                  return;
                }
                pendingInstantExecuteHealthHoldsRef.current.set(String(strategemInstanceId), {
                  strategemId,
                  flushKind,
                  holdTargets: filteredInstantHolds,
                });
                if (
                  flushKind === INSTANT_EXECUTE_FLUSH_KIND.STRATEGEM_ANIMATION
                  || flushKind === INSTANT_EXECUTE_FLUSH_KIND.PER_UNIT_MISSILE
                ) {
                  scheduleInstantExecuteHoldFlushIfNoActiveAnimation(strategemInstanceId);
                }
              });
            }
          }

          if (msg.gameState.bullyDamageInstanceIds?.length >= 1) {
            const prevForBully = bullySequenceAnchorPrevStateRef.current ?? prevStateForActions;
            const deferBullyFists = hasActiveStrategemAnimationsForInstance(
              strategemActiveAnimationsRef.current,
              pendingBullyFistPlaybackRef.current?.strategemInstanceId
            ) || (Array.isArray(strategemActiveAnimationsRef.current)
              && strategemActiveAnimationsRef.current.length > 0);
            console.log(`👊 BullyGhost CALL from game-update (${msg.type}), ids=${msg.gameState.bullyDamageInstanceIds?.length}`);
            startBullyDamageGhostSequence(prevForBully, msg.gameState, {
              deferFistPlayback: deferBullyFists,
              strategemInstanceId: pendingBullyFistPlaybackRef.current?.strategemInstanceId,
            });
          }
          delete msg.gameState.bullyDamageInstanceIds;

          previousGameStateRef.current = JSON.parse(JSON.stringify(msg.gameState));
        }
        
        // ✅ REFACTORED: Use hook's updateState method (via ref to avoid stale closure)
        const stateUpdated = updateStateRef.current(msg.gameState);
        
        if (!stateUpdated) {
          // State was rejected (stale version) - request fresh state
          // ✅ FIX: Check both ws state and socket ref for browser refresh scenarios
          const activeSocket = ws || currentSocketRef.current;
          if (activeSocket && activeSocket.readyState === 1) {
            activeSocket.send(JSON.stringify({ type: 'request-state-refresh', gameId }));
          }
          return; // Don't process rejected state
        }
        
        // State was updated successfully - continue with game logic
        setActionLog(msg.gameState.actionLog ?? []);
        storeOpponentStrategyId(msg.gameState);
        const wasYourTurn = previousYourTurnRef.current;
        // ✅ FIX: Handle both string and number types for turn check
        const isYourTurn = msg.gameState.turn === userId || 
                          msg.gameState.turn === userId.toString() || 
                          String(msg.gameState.turn) === String(userId);
        
        // Mark turn state as initialized after first game state update
        if (!turnStateInitializedRef.current) {
          turnStateInitializedRef.current = true;
        }

        // Update turn state using helper function
        updateYourTurn(isYourTurn, 'game-update');

        if (msg.type === 'turn-played' || msg.type === 'turn-ended') {
          releaseStaleAttackDamageFxOnTurnBoundaryRef.current();
        }
        
        // ✅ Determine if player goes first (after both players have mulliganed)
        const playerState = msg.gameState.state?.[userId];
        const opponentId = msg.gameState.players?.find(p => p !== userId);
        const opponentState = opponentId ? msg.gameState.state?.[opponentId] : null;
        
        // Check if both players have completed mulligan
        const playerMulliganed = playerState && !playerState.needsMulligan && playerState.hasMulliganed;
        const opponentMulliganed = opponentState && !opponentState.needsMulligan && opponentState.hasMulliganed;
        
        if (playerMulliganed && opponentMulliganed) {
          // Both players have mulliganed - determine who goes first (only set once)
          // ✅ FIX: Use players[0] to determine who goes first, not current turn
          // (current turn might have changed if bot already took their turn)
          setPlayerGoesFirst(prev => {
            if (prev === null) {
              const firstPlayerId = msg.gameState.players?.[0];
              const goesFirst = firstPlayerId === userId;
              // Show message (will auto-hide after 5 seconds via useEffect)
              setShowFirstTurnMessage(true);
              return goesFirst;
            }
            return prev; // Keep existing value
          });
        }
        
        lastSentPlayOrderRef.current = null;
        scheduleDrainActionQueue();
        
        // Reset attack ref when receiving game state update (allows multiple actions per turn)
        // This was previously only reset when turn changed, blocking multiple attacks per turn
        attackInProgressRef.current = false;
        
        // Keep turn change logic for other cleanup
        if (wasYourTurn && !isYourTurn) {
          // Turn changed to opponent
        }
        
        // Show turn start image when player's turn begins (only after turn state is initialized)
        if (turnStateInitializedRef.current && wasYourTurn !== null && !wasYourTurn && isYourTurn) {
          openTurnStartOverlayRef.current();
        }
        
        // Clear selected attacker if it no longer exists (unit was killed)
        if (selectedAttacker && msg.gameState.state?.[userId]?.playedOrders?.[selectedAttacker.zone]) {
          const attackerStillExists = msg.gameState.state[userId].playedOrders[selectedAttacker.zone][selectedAttacker.idx];
          if (!attackerStillExists) {
            setSelectedAttacker(null);
            setAttackMode(false);
          }
        }
      }
      
      // Only clear selection state for certain message types
      if (msg.type === "turn-ended" || msg.type === "turn-timeout" || 
          msg.type === "player-disconnected" || msg.type === "player-abandoned" || 
          msg.type === "player-resigned") {
        setSelectedOrderId(null);
        setSelectedOrderInstanceId(null);
        setSelectedAttacker(null);
        setAttackMode(false);
        attackInProgressRef.current = false; // Reset attack in progress flag when turn ends
      }
      
      // Sync deploy targeting state with game state
      if (window.syncDeployTargeting) {
        window.syncDeployTargeting(msg.gameState, userId);
      }
      
      // Sync strategem targeting state with game state
      if (window.syncStrategemTargeting) {
        window.syncStrategemTargeting(msg.gameState, userId);
      }
      
      // ✅ REFACTORED: Use hook's validateState method (via ref to avoid stale closure)
      validateStateRef.current(msg.gameState);
      
      
      // DEBUG: Check Unit 1 attack ratings in WebSocket update
      if (msg.gameState?.state?.[userId]?.playedOrders) {
        ['front', 'flank'].forEach(zone => {
          if (msg.gameState.state[userId].playedOrders[zone]) {
            msg.gameState.state[userId].playedOrders[zone].forEach((unit, index) => {
              if (unit && unit.id === 1) {
              }
            });
          }
        });
      }
      
      // Check for weapons that need immobilize targeting (resolve player key: id type mismatches skip whole block otherwise)
      const playerForTargeting =
        msg.gameState?.state?.[userId] ??
        msg.gameState?.state?.[String(userId)] ??
        (typeof userId === 'string' && !Number.isNaN(Number(userId)) ? msg.gameState?.state?.[Number(userId)] : null);
      if (playerForTargeting) {
        const player = playerForTargeting;
        let needsImmobilizeTarget = false;
        let needsConfuseTarget = false;
        let immobilizeWeaponId = null;
        let confuseWeaponId = null;
        
        ['front', 'flank'].forEach(zone => {
          if (player.playedOrders[zone]) {
            player.playedOrders[zone].forEach(unit => {
              if (unit && unit.attachedWeapons) {
                unit.attachedWeapons.forEach(weapon => {
                  if (weapon.needsImmobilizeTarget) {
                    needsImmobilizeTarget = true;
                    immobilizeWeaponId = weapon.id;
                  }
                  if (weapon.needsConfuseTarget) {
                    needsConfuseTarget = true;
                    confuseWeaponId = weapon.id;
                  }
                });
              }
            });
          }
        });
        
        if (needsImmobilizeTarget) {
          setImmobilizeTargetMode(true);
          setSelectedImmobilizeWeaponId(immobilizeWeaponId);
          setError(t('battleUI.selectTargetImmobilize'));
        } else if (needsConfuseTarget) {
          setConfuseTargetMode(true);
          setSelectedConfuseWeaponId(confuseWeaponId);
          setError(t('battleUI.selectTargetConfuse'));
        } else if (player.needsStrategemTarget) {
          setStrategemTargetMode(true);
          setSelectedStrategemId(player.needsStrategemTarget.strategemId);
          setStrategemTargetInfo(player.needsStrategemTarget);
          // Don't show "Select a target" when we have a pending drag-drop target (we'll auto-send it)
          if (pendingStrategemDropTargetRef.current?.strategemInstanceId !== player.needsStrategemTarget.strategemInstanceId) {
            setError(getStrategemTargetingPrompt(player.needsStrategemTarget));
          }
        }

        // Sandra Lehnsherr (395): server keeps weapon deploy pending in player state; enrich now includes it for turn-played sync
        const isYourTurnFor395 =
          msg.gameState.turn === userId ||
          msg.gameState.turn === userId?.toString?.() ||
          String(msg.gameState.turn) === String(userId);
        if (
          isYourTurnFor395 &&
          player.unit395WaitingForDeployTargeting &&
          player.unit395CurrentWeapon &&
          player.unit395CurrentUnit
        ) {
          const w = player.unit395CurrentWeapon;
          const u = player.unit395CurrentUnit;
          setWeaponTargetMode(false);
          setSelectedWeaponId(null);
          setSelectedWeaponInstanceId(null);
          setWeaponDeployTargetMode(true);
          setWeaponDeployTargetInfo({
            weaponInstanceId: w.instanceId,
            weaponId: w.id,
            weaponName: w.name,
            targetUnitInstanceId: u.instanceId
          });
          setError(t('battleUI.selectTargetWeapon'));
        }

        const isYourTurnForBossRandomizer =
          msg.gameState.turn === userId ||
          msg.gameState.turn === userId?.toString?.() ||
          String(msg.gameState.turn) === String(userId);
        if (
          isYourTurnForBossRandomizer &&
          player.bossRandomizerWaitingForDeployTargeting &&
          player.bossRandomizerCurrentWeapon &&
          player.bossRandomizerCurrentUnit
        ) {
          const randomizerWeapon = player.bossRandomizerCurrentWeapon;
          const randomizerUnit = player.bossRandomizerCurrentUnit;
          setWeaponTargetMode(false);
          setSelectedWeaponId(null);
          setSelectedWeaponInstanceId(null);
          setWeaponDeployTargetMode(true);
          setWeaponDeployTargetInfo({
            weaponInstanceId: randomizerWeapon.instanceId,
            weaponId: randomizerWeapon.id,
            weaponName: randomizerWeapon.name,
            targetUnitInstanceId: randomizerUnit.instanceId
          });
          setError(t('battleUI.selectTargetWeapon'));
        }

        const isYourTurnForHarralSpawnblade =
          msg.gameState.turn === userId ||
          msg.gameState.turn === userId?.toString?.() ||
          String(msg.gameState.turn) === String(userId);
        if (
          isYourTurnForHarralSpawnblade &&
          player.harralSpawnbladeWaitingForDeployTargeting &&
          player.harralSpawnbladeCurrentWeapon &&
          player.harralSpawnbladeCurrentUnit
        ) {
          const hsWeapon = player.harralSpawnbladeCurrentWeapon;
          const hsUnit = player.harralSpawnbladeCurrentUnit;
          setWeaponTargetMode(false);
          setSelectedWeaponId(null);
          setSelectedWeaponInstanceId(null);
          setWeaponDeployTargetMode(true);
          setWeaponDeployTargetInfo({
            weaponInstanceId: hsWeapon.instanceId,
            weaponId: hsWeapon.id,
            weaponName: hsWeapon.name,
            targetUnitInstanceId: hsUnit.instanceId
          });
          setError(t('battleUI.selectTargetWeapon'));
        }

        const isYourTurnForWeaponDeploy =
          msg.gameState.turn === userId ||
          msg.gameState.turn === userId?.toString?.() ||
          String(msg.gameState.turn) === String(userId);
        if (
          isYourTurnForWeaponDeploy &&
          player.weaponDeployWaitingForTargeting &&
          player.weaponDeployCurrentWeapon &&
          player.weaponDeployCurrentUnit
        ) {
          const wdWeapon = player.weaponDeployCurrentWeapon;
          const wdUnit = player.weaponDeployCurrentUnit;
          setWeaponTargetMode(false);
          setSelectedWeaponId(null);
          setSelectedWeaponInstanceId(null);
          setWeaponDeployTargetMode(true);
          setWeaponDeployTargetInfo({
            weaponInstanceId: wdWeapon.instanceId,
            weaponId: wdWeapon.id,
            weaponName: wdWeapon.name,
            targetUnitInstanceId: wdUnit.instanceId
          });
          setError(t('battleUI.selectTargetWeapon'));
        }
        
        // ✅ CRITICAL: Only check for targeting mode if there's no error
        // This prevents targeting mode activation when intelligence check fails
        if (!error) {
          // Check for units that need deploy targeting
          let needsDeployTarget = false;
          let deployOrderId = null;
          let deployInstanceId = null;
          let deployZone = null;
          
          // ✅ CRITICAL FIX: Always check backend's selectedDeployInstanceId FIRST (authoritative source)
          // Even if deployTargetMode is false, the backend may have set selectedDeployInstanceId
          // This prevents race conditions where backend sets instanceId but gameState update hasn't arrived
          if (player.selectedDeployInstanceId && player.selectedDeployOrderId && player.selectedDeployZone) {
            // Validate that the instanceId from backend actually exists in the zone
            const zoneUnits = player.playedOrders[player.selectedDeployZone] || [];
            const unitExists = zoneUnits.some(u => u && u.instanceId === player.selectedDeployInstanceId);
            
            if (unitExists) {
              // Backend has authoritative instanceId and unit exists - use it
              needsDeployTarget = true;
              deployOrderId = player.selectedDeployOrderId;
              deployInstanceId = player.selectedDeployInstanceId;
              deployZone = player.selectedDeployZone;
            } else {
              // Backend instanceId doesn't exist in zone - this is a sync issue
              console.warn('⚠️ DEPLOY TARGETING: Backend instanceId not found in zone, falling back to scan', {
                backendInstanceId: player.selectedDeployInstanceId,
                backendOrderId: player.selectedDeployOrderId,
                backendZone: player.selectedDeployZone,
                zoneUnits: zoneUnits.map(u => u ? { id: u.id, name: u.name, instanceId: u.instanceId } : null)
              });
            }
          }
          
          // If backend didn't provide instanceId or it doesn't exist, check if deployTargetMode is active
          if (!needsDeployTarget && player.deployTargetMode && player.selectedDeployOrderId) {
            needsDeployTarget = true;
            deployOrderId = player.selectedDeployOrderId;
            deployZone = player.selectedDeployZone;
            // ✅ CRITICAL: Always use authoritative instanceId from server (backend is source of truth)
            // The backend sets selectedDeployInstanceId when the unit is deployed
            // If it's not set, that means the unit deployment failed or there's a backend bug
            if (!player.selectedDeployInstanceId) {
              console.error(`❌ ROOT CAUSE: Backend deployTargetMode is active but selectedDeployInstanceId is not set!`);
              console.error(`   This indicates the unit deployment didn't properly set selectedDeployInstanceId.`);
              console.error(`   deployOrderId: ${deployOrderId}, deployZone: ${deployZone}`);
              console.error(`   Zone ${deployZone} units:`, player.playedOrders[deployZone]?.map(u => u ? { id: u.id, name: u.name, instanceId: u.instanceId } : null));
              // This is a backend bug - the unit was deployed but selectedDeployInstanceId wasn't set
              throw new Error(`Backend error: deployTargetMode active but selectedDeployInstanceId not set. Check DeployOrders.js line 202.`);
            }
            deployInstanceId = player.selectedDeployInstanceId;
          }
          
          // Fallback: Look for units that need targeting (only if backend didn't provide instanceId)
          if (!needsDeployTarget) {
            ['front', 'flank'].forEach(zone => {
              if (player.playedOrders[zone]) {
                player.playedOrders[zone].forEach(unit => {
                  if (unit && (unit.needsDeployTarget || (targetingData && unitNeedsTargetingOnDeploy(unit.id, targetingData)))) {
                    needsDeployTarget = true;
                    deployOrderId = unit.id;
                    deployInstanceId = unit.instanceId;
                    deployZone = zone;
                  }
                });
              }
            });
          }
          
          if (needsDeployTarget) {
            // ✅ CRITICAL: Only activate targeting mode if there's no error
            // This prevents yellow borders when intelligence check fails
            if (!error) {
              // Find the unit object to get its name for the targeting message
              let unitObject = null;
              if (deployInstanceId && deployZone) {
                const zoneUnits = player.playedOrders[deployZone] || [];
                unitObject = zoneUnits.find(u => u && u.instanceId === deployInstanceId);
              }
              
              setDeployTargetMode(true);
              setSelectedDeployOrderId(deployOrderId);
              setSelectedDeployInstanceId(deployInstanceId);
              setSelectedDeployZone(deployZone);
              setDeployTargetInfo(unitObject ? { order: unitObject } : null);
              setError(t('battleUI.selectTargetDeploy'));
            }
          }
        }
      }
      
      // ✅ FIX: Only accept winner when message matches resolved session (URL first, then ref) — avoids stale ref vs /battle/:id.
      const updateMsgGameId = msg.gameId;
      const updateExpectedSid = resolveWsSessionGameIdRef.current();
      if (msg.gameState?.winner && !isSpectating) {
        if (updateMsgGameId != null && updateExpectedSid != null && String(updateMsgGameId) === String(updateExpectedSid)) {
          const didYouWin = String(msg.gameState.winner) === String(userId);
          const isFriendlyMatch = msg.gameState.isFriendlyMatch || false;
          handleBattleEnd(didYouWin, gameIdRef.current, socket, isFriendlyMatch, `${msg.type}-winner`, msg.gameState);
          return;
        }
      }
    }
    if (msg.type === "error") {
      // ✅ FIX: Handle reconnect errors - if no active game, game ended, or stale state, redirect to battle overview
      const redirectToOverview = msg.message && (
        msg.message.includes('No active game found') ||
        msg.message.includes('Game state not found') ||
        msg.message.includes('Game already ended') ||
        msg.message.includes('Stale game state detected')
      );
      if (redirectToOverview) {
        console.warn(`⚠️ Reconnect failed: ${msg.message}. Redirecting to battle overview.`);
        setError(t('battleUI.gameNotFound'));
        reconnectingRef.current = false;
        setReconnecting(false);
        wsConnectingRef.current = false;
        // ✅ FIX: Clear timeout on error
        if (reconnectingTimeoutRef.current) {
          clearTimeout(reconnectingTimeoutRef.current);
          reconnectingTimeoutRef.current = null;
        }
        setTimeout(() => {
          navigate('/battleoverview', { replace: true });
        }, 2000);
        return;
      }
      // ✅ REFACTORED: Use hook's unlockUI method (via ref to avoid stale closure)
      unlockUIRef.current();

      if (msg.retryable && isActionLockErrorMessage(msg.message)) {
        if (lastSentPlayOrderRef.current) {
          enqueuePlayOrderIntent(lastSentPlayOrderRef.current);
          lastSentPlayOrderRef.current = null;
        }
        scheduleDrainActionQueue();
        return;
      }

      lastSentPlayOrderRef.current = null;
      setError(translateBackendError(msg.message));
      
      // ✅ FIX: Handle preparation bonus errors
      if (msg.message && msg.message.includes('Preparation bonus')) {
        if (msg.message.includes('has already been used')) {
          // Backend confirms it's already used - keep it as true (optimistic update was correct)
          setPreparationBonusUsed(true);
          preparationBonusPendingRef.current = false; // Clear pending since backend confirmed
        } else {
          // Other preparation bonus errors (e.g., "not yet implemented", network issues)
          // Revert optimistic update to allow retry
          setPreparationBonusUsed(false);
          preparationBonusPendingRef.current = false; // Clear pending to allow retry
        }
      }
      
      // If attack failed, clear pending attack info and reset attack state
      // This prevents the unit from becoming inactive after an illegal attack
      if (pendingAttackInfoRef.current) {
        pendingAttackInfoRef.current = null;
        attackInProgressRef.current = false;
        // Don't clear selectedAttacker or attackMode - let user try again or cancel manually
      }
      
      // If we're in targeting mode and get an error, keep targeting mode active
      // Don't reset targeting mode on error - let user try again or cancel
    }
  };
  socket.onclose = () => {
    // ✅ FIX: Don't clear socket reference on close if we're in the middle of reconnecting
    // The socket might close temporarily during reconnect, but we need to keep the ref
    // Only clear if we're definitely not reconnecting
    if (!wsConnectingRef.current) {
      setWs(null);
      // Only clear ref if it's the same socket that closed
      if (currentSocketRef.current === socket) {
        currentSocketRef.current = null;
      }
    } else {
      // If we're connecting, keep the ref but mark ws as null
      // This allows the reconnect flow to complete
      setWs(null);
      // Don't clear currentSocketRef.current - keep it for reconnect
    }
    wsConnectingRef.current = false; // Reset connection flag when socket closes
  };
  return () => {
    if (socket) {
      // Always close on cleanup (Escape → leave battle, or /battle/A → /battle/B remount).
      // Leaving the socket open leaked a second connection after new PvE and let abandoned-game
      // traffic keep hitting a stale handler. Strict Mode remount opens a fresh socket on the next run.
      wsConnectingRef.current = false;
      currentSocketRef.current = null;
      gameSocketReconnectSentRef.current = false;
      if (reconnectingTimeoutRef.current) {
        clearTimeout(reconnectingTimeoutRef.current);
        reconnectingTimeoutRef.current = null;
      }
      try {
        if (socket.readyState === WebSocket.CONNECTING || socket.readyState === WebSocket.OPEN) {
          socket.close();
        }
      } catch (err) {
        // Ignore errors when closing socket
      }
    }
  };
}

}, [userId, gameId, navigate, getCurrentGameIdFromUrl]); // ✅ FIX: Removed token so War Effort / App re-renders don't tear down socket. Token is read via tokenRef.current. Removed ws and gameState to prevent connection loops.




const playOrder = async (orderId, zone, targetInfo = null, instanceId = null) => {
  // Queue deploy intents while the previous action's state is still applying (no error toast)
  if (isStateUpdating || isStateStabilizing) {
    enqueuePlayOrderIntent({ orderId, zone, targetInfo, instanceId });
    return;
  }

  // ✅ DESYNC FIX: Enforce cooldown between actions (prevent spam clicking)
  const now = Date.now();
  const timeSinceLastAction = now - lastActionTimestamp.current;
  if (timeSinceLastAction < ACTION_COOLDOWN_MS) {
    return;
  }
  lastActionTimestamp.current = now;
  
  if (!yourTurn || !gameId || !ws || ws.readyState !== 1) {
      setError(t('battleUI.connectionLost'));
      return;
    }

  // ✅ REFACTORED: Use hook's getCurrentState method
  const currentState = getCurrentState() || gameState;
  
  // ✅ CRITICAL: Validate card is actually in hand before playing (prevent state desync errors)
  if (!currentState?.state?.[userId]) {
    setError(t('battleUI.gameStateNotAvailable'));
    console.error('🚨 STATE DESYNC: Game state not available for player', { userId, hasGameState: !!currentState });
    return;
  }

  const player = currentState.state[userId];
  
  // ✅ CRITICAL: Check if order is in deck FIRST (before checking hand)
  // This catches the case where frontend shows card in hand but backend has it in deck
  const orderInDeck = instanceId 
    ? player.deck?.find(o => o && o.instanceId === instanceId)
    : player.deck?.find(o => o && o.id === orderId);
  
  if (orderInDeck) {
    console.error('🚨 STATE DESYNC: Order is in deck, not in hand. Card not drawn yet.', {
      orderId,
      instanceId,
      orderName: orderInDeck.name,
      deckPosition: player.deck?.indexOf(orderInDeck),
      handSize: player.hand?.length,
      deckSize: player.deck?.length,
      stateVersion: currentState.stateVersion,
      turnNumber: currentState.turnNumber
    });
    setError(t('battleUI.cardNotAvailable'));
    // Force state refresh
    // ✅ FIX: Check both ws state and socket ref for browser refresh scenarios
    const activeSocket = ws || currentSocketRef.current;
    if (activeSocket && activeSocket.readyState === 1) {
      activeSocket.send(JSON.stringify({ type: 'request-state-refresh', gameId }));
    }
    return;
  }
  
  // ✅ CRITICAL: Check if order is in completedOrders BEFORE checking hand
  const orderInCompleted = player.completedOrders?.find(o => 
    (instanceId && o.instanceId === instanceId) || 
    (!instanceId && o.id === orderId)
  );
  
  if (orderInCompleted) {
    console.error('🚨 STATE DESYNC: Order is in completedOrders (already played).', { 
      orderId, 
      instanceId,
      orderName: orderInCompleted.name 
    });
    setError(t('battleUI.cardAlreadyPlayed'));
    // Force state refresh by requesting it
    // ✅ FIX: Check both ws state and socket ref for browser refresh scenarios
    const activeSocket = ws || currentSocketRef.current;
    if (activeSocket && activeSocket.readyState === 1) {
      activeSocket.send(JSON.stringify({ type: 'request-state-refresh', gameId }));
    }
    return;
  }
  
  // ✅ IMPROVED: Use exact instanceId matching first, then fall back to orderId
  const order = instanceId 
    ? player.hand.find(o => o.instanceId === instanceId)
    : player.hand.find(o => o.id === orderId);
  
  // ✅ CRITICAL: Check if order is actually in hand (not in deck, discard, etc.)
  if (!order) {
    const orderLocation = findPlayOrderLocation(player, orderId, instanceId);

    // Stale queued intent after a successful manual retry — card already left hand for the battlefield
    if (orderLocation === 'battlefield' || orderLocation === 'drones') {
      pruneQueuedPlayOrderForInstance(instanceId);
      return;
    }

    if (orderLocation === 'discard') {
      console.error('🚨 STATE DESYNC: Order is in discard pile.', { orderId, instanceId });
      setError(t('battleUI.cardAlreadyDiscarded'));
      return;
    }

    console.error('🚨 STATE DESYNC: Order not found anywhere in player state.', {
      orderId,
      instanceId,
      orderLocation,
    });
    setError(t('battleUI.cardNotFound'));
    // Force state refresh
    // ✅ FIX: Check both ws state and socket ref for browser refresh scenarios
    const activeSocket = ws || currentSocketRef.current;
    if (activeSocket && activeSocket.readyState === 1) {
      activeSocket.send(JSON.stringify({ type: 'request-state-refresh', gameId }));
    }
    return;
  }

  // Unit 6 (Exolot Husk; DE/ES UI: Roolac Husk) — server also enforces; block early so intel/targeting are not consumed
  if (Number(order.id) === 6 && zone !== 'drone' && zone !== 'strategem') {
    const frontCount = (player.playedOrders?.front || []).filter((u) => u != null).length;
    const flankCount = (player.playedOrders?.flank || []).filter((u) => u != null).length;
    if (frontCount < 1 || flankCount < 1) {
      setError(t('battleUI.exolotHuskNeedsFrontAndFlank'));
      return;
    }
  }

  // ✅ CRITICAL: Check intelligence BEFORE sending action to prevent targeting mode activation
  // Use Ability Array System to get correct cost
  const orderCost = order.hasInstanceModifications && order.instanceCost !== undefined ? order.instanceCost : order.cost;
  if (player.intelligence < orderCost) {
    setError(`${t('battleUI.notEnoughIntelligence')} ${order.name}! ${t('battleUI.need')} ${orderCost}, ${t('battleUI.have')} ${player.intelligence}`);
    return;
  }

  // Check if token needs refresh before sending
  if (needsTokenRefresh(token) && refreshTokenValue) {
    try {
      const newToken = await refreshToken(refreshTokenValue);
      // Note: We can't update the token prop here, but the server will handle auth
    } catch (error) {
      setError(t('battleUI.connectionLost'));
      return;
    }
  } else if (needsTokenRefresh(token) && !refreshTokenValue) {
    setError(t('battleUI.connectionLost'));
    return;
  }

  try {
    // Clear any error message
    setError("");

    // ✅ CRITICAL: Check for targeting BEFORE locking UI
    // If targeting is needed, enter targeting mode without locking
    // Handle deploy orders with targeting
    if (needsTargetingOnDeploy(orderId) && targetInfo === null) {
      setDeployMode(true);
      setSelectedDeployOrderId(orderId);
      setSelectedDeployInstanceId(instanceId);
      setSelectedOrderId(orderId);
      setSelectedOrderInstanceId(instanceId);
      return;
    }
    
    // Handle special unit deploy targeting
    if (specialUnitNeedsTargeting(orderId) && targetInfo === null) {
      setDeployMode(true);
      setSelectedDeployOrderId(orderId);
      setSelectedDeployInstanceId(instanceId);
      setSelectedOrderId(orderId);
      setSelectedOrderInstanceId(instanceId);
      setIsSpecialDeploy(true);
      return;
    }

    // ✅ REFACTORED: Use hook's lockUI method with timeout callbacks
    lockUI(
      () => setError(t('battleUI.actionTimeout')), // onTimeout callback
      () => { // onTimeoutRequestRefresh callback
        // ✅ FIX: Check both ws state and socket ref for browser refresh scenarios
        const activeSocket = ws || currentSocketRef.current;
        if (activeSocket && activeSocket.readyState === 1) {
          activeSocket.send(JSON.stringify({ type: 'request-state-refresh', gameId }));
        }
      }
    );

    const action = { type: "playOrder", orderId, zone };
    if (instanceId) {
      action.instanceId = instanceId;
    }
    
    // Add targeting information if provided
    if (targetInfo) {
      action.targetUnitId = targetInfo.targetUnitId;
      action.targetZone = targetInfo.targetZone;
      action.targetIndex = targetInfo.targetIndex;
    }

    // Send the action to backend
    // ✅ FIX: Check both ws state and socket ref for browser refresh scenarios
    const activeSocket = ws || currentSocketRef.current;
    if (!activeSocket || activeSocket.readyState !== 1) {
      console.error('❌ playOrder blocked: WebSocket not ready', { hasWs: !!ws, hasRef: !!currentSocketRef.current, wsReady: ws?.readyState, refReady: currentSocketRef.current?.readyState });
      setError(t('battleUI.connectionLost'));
      unlockUI();
      return;
    }
    lastSentPlayOrderRef.current = { orderId, zone, targetInfo, instanceId };
    pruneQueuedPlayOrderForInstance(instanceId);
    activeSocket.send(
      JSON.stringify({
        type: "play-turn",
        gameId,
        action,
        stateVersion: lastStateVersionRef.current, // Send current state version for optimistic locking
      })
    );
    
    
    // UI will be unlocked when we receive state update from backend
    
  } catch (error) {
    console.error('❌ Error in playOrder:', error);
    setError(`${t('battleUI.failedToPlayCard')}: ${error.message}`);
    lastSentPlayOrderRef.current = null;
    // ✅ REFACTORED: Use hook's unlockUI method
    unlockUI();
  }
};
playOrderRef.current = playOrder;

const playDrone = (orderId) => {
  // ✅ FIX: Check both ws state and socket ref for browser refresh scenarios
  const activeSocket = ws || currentSocketRef.current;
  if (!yourTurn || !gameId || !activeSocket || activeSocket.readyState !== 1) {
    console.error('❌ playDrone blocked:', { yourTurn, gameId, hasWs: !!ws, hasRef: !!currentSocketRef.current, wsReady: ws?.readyState, refReady: currentSocketRef.current?.readyState });
    setError(t('battleUI.connectionLost'));
    return;
  }

  // ✅ CRITICAL: Check intelligence BEFORE sending action to prevent targeting mode activation
  if (gameState?.state?.[userId]) {
    const player = gameState.state[userId];
    const order = player.hand.find(o => o.id === orderId);
    
    if (!order) {
      setError(t('battleUI.cardNotFound'));
      return;
    }
    
    // ✅ FIX: Extract instanceId from order (drones should have instanceIds like any other order)
    if (!order.instanceId) {
      console.error(`🚨 CRITICAL: Drone ${order.name} (ID: ${order.id}) missing instanceId in hand`);
      setError('Card missing instanceId. This indicates a backend data integrity issue.');
      return;
    }
    
    // Use Ability Array System to get correct cost
    const orderCost = order.hasInstanceModifications && order.instanceCost !== undefined ? order.instanceCost : order.cost;
    if (player.intelligence < orderCost) {
      setError(`${t('battleUI.notEnoughIntelligence')} ${order.name}! ${t('battleUI.need')} ${orderCost}, ${t('battleUI.have')} ${player.intelligence}`);
      return;
    }

    // ✅ FIX: Use activeSocket instead of ws to handle browser refresh scenarios
    activeSocket.send(
      JSON.stringify({
        type: "play-turn",
        gameId,
        action: { type: "playOrder", orderId, zone: "drone", instanceId: order.instanceId },
      })
    );
    // Clear hand-selection targeting state immediately after dispatching drone deploy
    // so we do not fall through into unit-zone targeting visuals/actions.
    setSelectedOrderId(null);
    setSelectedOrderInstanceId(null);
  }
};

const playStrategem = (orderId, instanceId = null) => {
  // ✅ FIX: Check both ws state and socket ref for browser refresh scenarios
  const activeSocket = ws || currentSocketRef.current;
  if (!yourTurn || !gameId || !activeSocket || activeSocket.readyState !== 1) {
    console.error('❌ playStrategem blocked:', { yourTurn, gameId, hasWs: !!ws, hasRef: !!currentSocketRef.current, wsReady: ws?.readyState, refReady: currentSocketRef.current?.readyState });
    setError(t('battleUI.connectionLost'));
    return;
  }

  // ✅ CRITICAL: Check intelligence BEFORE sending action to prevent targeting mode activation
  if (gameState?.state?.[userId]) {
    const player = gameState.state[userId];
    // ✅ FIX: Find by instanceId if provided, otherwise fall back to orderId
    const order = instanceId 
      ? player.hand.find(o => o.instanceId === instanceId)
      : player.hand.find(o => o.id === orderId);
    
    if (!order) {
      setError(t('battleUI.cardNotFound'));
      return;
    }
    
    // ✅ FIX: Extract instanceId from order (strategems should have instanceIds like any other order)
    if (!order.instanceId) {
      console.error(`🚨 CRITICAL: Strategem ${order.name} (ID: ${order.id}) missing instanceId in hand`);
      setError('Card missing instanceId. This indicates a backend data integrity issue.');
      return;
    }
    
    // Use Ability Array System to get correct cost
    const orderCost = order.hasInstanceModifications && order.instanceCost !== undefined ? order.instanceCost : order.cost;
    if (player.intelligence < orderCost) {
      setError(`${t('battleUI.notEnoughIntelligence')} ${order.name}! ${t('battleUI.need')} ${orderCost}, ${t('battleUI.have')} ${player.intelligence}`);
      return;
    }

    // ✅ Strategem 114 (Tactical Loadout), 115 (Stinger Unit Alpha), 221, and 225: Trigger explosion effect when requested
    if (orderId === 114 || orderId === 115 || orderId === 221 || orderId === 225) {
      // Get card position from DOM
      const cardElement = document.querySelector(`[data-instance-id="${order.instanceId}"]`);
      let explosionPosition;
      
      if (cardElement) {
        const rect = cardElement.getBoundingClientRect();
        explosionPosition = {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2
        };
      } else {
        // Fallback to approximate hand position
        explosionPosition = {
          x: window.innerWidth / 2,
          y: window.innerHeight - 100
        };
      }
      
      // Determine color: black for 221 and 225, purple for 115, orange for 114
      const explosionColor = (orderId === 221 || orderId === 225) ? 'black' : orderId === 115 ? 'purple' : 'orange';
      
      // Trigger explosion effect
      const explosionKey = `explosion-${orderId}-${order.instanceId}-${Date.now()}`;
      setExplosionEffects(prev => {
        const next = new Map(prev);
        next.set(explosionKey, {
          key: explosionKey,
          x: explosionPosition.x,
          y: explosionPosition.y,
          strategemInstanceId: order.instanceId,
          color: explosionColor
        });
        return next;
      });
      
      // Clean up after animation completes
      setTimeout(() => {
        setExplosionEffects(prev => {
          const next = new Map(prev);
          next.delete(explosionKey);
          return next;
        });
      }, 700); // Slightly longer than animation duration
    }

    // Strategem 369 portals: only after state sync (deployed id 374) — see useEffect + inferBattlebotSlotsFromPlayedOrders

    // ✅ FIX: Use activeSocket instead of ws to handle browser refresh scenarios
    activeSocket.send(
      JSON.stringify({
        type: "play-turn",
        gameId,
        action: { type: "playOrder", orderId, zone: "strategem", instanceId: order.instanceId },
      })
    );
  }
};

const activateDrone = (droneId) => {
  // Campaign tutorial: block drone activation when step forbids it (e.g. "Your Drones" – deploy only)
  if (typeof document !== 'undefined' && document.body?.getAttribute('data-tutorial-block-drone-activation') === 'true') {
    setError(t('battleUI.deployYourDroneFirst') || 'Deploy your drone first, then pass the turn.');
    return;
  }

  // ✅ FIX: Check both ws state and socket ref for browser refresh scenarios
  const activeSocket = ws || currentSocketRef.current;
  if (!yourTurn || !gameId || !activeSocket || activeSocket.readyState !== 1) {
    console.error('❌ activateDrone blocked:', { yourTurn, gameId, hasWs: !!ws, hasRef: !!currentSocketRef.current, wsReady: ws?.readyState, refReady: currentSocketRef.current?.readyState });
    setError(t('battleUI.connectionLost'));
    return;
  }

  // ✅ BUG FIX: Find drone by instanceId (unique per deployed instance)
  const drone = you?.drones?.find(d => d.instanceId === droneId);
  if (!drone) {
    console.error(`❌ Drone with instanceId ${droneId} not found in player drones:`, you?.drones);
    return;
  }

  // ✅ BUG FIX: Check if drone has already been used this turn
  if (drone.usedThisTurn) {
    setError(t('battleUI.droneUsedThisTurn'));
    return;
  }

  // ✅ BUG FIX: Check if drone has charges remaining
  if (drone.charges !== undefined && drone.charges <= 0) {
    setError(t('battleUI.droneNoCharges'));
    return;
  }

  // ✅ Unit 502 (Disruptor): Check if any Disruptor is deployed (deactivates all drones)
  const allPlayerIds = gameState?.players || (gameState?.state ? Object.keys(gameState.state) : []);
  let disruptorDeployed = false;
  for (const pid of allPlayerIds) {
    const p = gameState?.state?.[pid];
    if (p && p.playedOrders) {
      for (const zone of ['front', 'flank']) {
        if (p.playedOrders[zone]) {
          for (const unit of p.playedOrders[zone]) {
            if (unit && unit.id === 502 && unit.health > 0) {
              disruptorDeployed = true;
              break;
            }
          }
          if (disruptorDeployed) break;
        }
      }
      if (disruptorDeployed) break;
    }
  }
  
  if (disruptorDeployed) {
    setError('Drones are inactive while Disruptor is deployed');
    return;
  }

  // Check if drone needs targeting using extracted function
  const needsTargeting = droneNeedsTargeting(drone.id, targetingData);
  
  // Check if drone can target opponent units using extracted function
  const canTargetOpponents = droneCanTargetOpponents(drone.id, targetingData);
  
  if (needsTargeting) {
    setSelectedDroneId(drone.id);              // Order ID for reference
    setSelectedDroneInstanceId(drone.instanceId); // ✅ Unique instance ID
    setDroneTargetMode(true);
    setError(t('battleUI.selectTargetDrone'));
    return;
  }

  // Drones that don't need targeting: 20, 21, 275, 277, 278
  // ✅ FIX: Use activeSocket instead of ws to handle browser refresh scenarios
  activeSocket.send(
    JSON.stringify({
      type: "play-turn",
      gameId,
      action: { 
        type: "activateDrone", 
        droneId: drone.id,            // Order ID
        droneInstanceId: drone.instanceId, // ✅ REQUIRED: Unique instance ID
        targetUnitId: null,
        targetZone: null,
        targetIndex: null
      },
    })
  );
};


// attachWeapon function moved to targeting folder

// targetDrone function moved to targeting folder

// targetImmobilize function moved to targeting folder

// targetConfuse function moved to targeting folder

// targetStrategem function moved to targeting folder

// Handle deploy targeting
const handleTargetDeploy = (targetZone, targetIndex, targetUnitId) => {
  targetDeploy(
    targetZone,
    targetIndex,
    targetUnitId,
    yourTurn,
    gameId,
    ws,
    setError,
    selectedDeployOrderId,
    selectedDeployInstanceId,
    selectedDeployZone,
    setDeployTargetMode,
    setSelectedDeployOrderId,
    setSelectedDeployInstanceId,
    setSelectedDeployZone,
    setDeployTargetInfo,
    gameState, // ✅ FIX: Pass gameState for validation
    userId // ✅ FIX: Pass playerId for validation
  );
};

const getMission5CornGroupInfo = (zone, index) => {
  const player = gameState?.state?.[userId];
  const rows = player?.playedOrders;
  if (!rows || !['front', 'flank'].includes(zone)) return { size: 0, cost: 1, members: [] };
  const start = rows?.[zone]?.[index];
  if (!start || start.isMission5Corn !== true) return { size: 0, cost: 1, members: [] };

  const toKey = (z, i) => `${z}:${i}`;
  const neighbors = (z, i) => {
    // Mission 5 adjacency is linear left/right across player slots:
    // front[0..4] then flank[0..4], with a bridge between front[4] <-> flank[0].
    if (z === 'front') {
      if (i === 0) return [{ zone: 'front', index: 1 }];
      if (i === 4) return [{ zone: 'front', index: 3 }, { zone: 'flank', index: 0 }];
      return [{ zone: 'front', index: i - 1 }, { zone: 'front', index: i + 1 }];
    }
    // z === 'flank'
    if (i === 0) return [{ zone: 'front', index: 4 }, { zone: 'flank', index: 1 }];
    if (i === 4) return [{ zone: 'flank', index: 3 }];
    return [{ zone: 'flank', index: i - 1 }, { zone: 'flank', index: i + 1 }];
  };

  const visited = new Set();
  const queue = [{ zone, index }];
  const members = [];
  while (queue.length > 0) {
    const current = queue.shift();
    const key = toKey(current.zone, current.index);
    if (visited.has(key)) continue;
    visited.add(key);
    const unit = rows?.[current.zone]?.[current.index];
    if (!unit || unit.isMission5Corn !== true) continue;
    members.push({ ...current, instanceId: unit.instanceId });
    for (const n of neighbors(current.zone, current.index)) {
      if (!visited.has(toKey(n.zone, n.index))) queue.push(n);
    }
  }

  const size = members.length;
  const cost = size >= 3 ? 6 : (size === 2 ? 3 : 1);
  return { size, cost, members };
};

const removeMission5CornGroup = (zone, index, cornUnit) => {
  const activeSocket = ws || currentSocketRef.current;
  if (!yourTurn || !gameId || !activeSocket || activeSocket.readyState !== 1) {
    setError(t('battleUI.connectionLost'));
    return;
  }

  if (!cornUnit?.instanceId) {
    setError('Corn removal failed: missing instanceId.');
    return;
  }

  const groupInfo = getMission5CornGroupInfo(zone, index);
  const intelligence = gameState?.state?.[userId]?.intelligence ?? 0;
  if (intelligence < groupInfo.cost) {
    setError(`${t('battleUI.notEnoughIntelligence')} Corn! ${t('battleUI.need')} ${groupInfo.cost}, ${t('battleUI.have')} ${intelligence}`);
    return;
  }

  // Suppress corn removal death/damage animations by marking all group members
  // as strategem-targeted for one update cycle.
  for (const member of groupInfo.members) {
    if (member.instanceId) {
      strategemTargetAnimations.targetedUnitInstanceIdsRef.current.add(member.instanceId);
    }
  }
  setTimeout(() => {
    for (const member of groupInfo.members) {
      if (member.instanceId) {
        strategemTargetAnimations.targetedUnitInstanceIdsRef.current.delete(member.instanceId);
      }
    }
  }, 1500);

  setError('');
  playSoundEffect('/sounds/cut_corn.mp3', 0.7);
  activeSocket.send(JSON.stringify({
    type: 'play-turn',
    gameId,
    action: {
      type: 'removeMission5CornGroup',
      zone,
      index,
      instanceId: cornUnit.instanceId,
    },
    stateVersion: lastStateVersionRef.current,
  }));
};

const endTurn = () => {
  playSoundEffect('/sounds/click.mp3');
  
  // Cancel any active targeting when ending turn
  cancelAllTargeting();
  
  if (!gameId) {
    setError(t('battleUI.connectionLost'));
    return;
  }

  // Campaign/WS desync hardening: use authoritative gameState.turn, not only stale yourTurn UI flag.
  const isMyTurnNow = String(gameState?.turn) === String(userId);
  if (!isMyTurnNow) {
    console.error('❌ endTurn blocked: not your turn by gameState.turn', {
      gameTurn: gameState?.turn,
      userId,
      yourTurnFlag: yourTurn
    });
    setError(translateBackendError('Not your turn'));
    return;
  }

  if (opponentHasCounterStrike) {
    setError(translateBackendError('Wait for opponent to resolve Counter Strike before continuing your turn'));
    return;
  }

  const isMission5 = gameState?.campaignMode && ['chapter_1_mission_5', 'chapter_1_mission_6'].includes(gameState?.missionId);
  const message = JSON.stringify({ type: "end-turn", gameId });

  const trySendEndTurn = (retriesLeft = 10) => {
    const socketCandidate = currentSocketRef.current || ws;
    if (socketCandidate && socketCandidate.readyState === WebSocket.OPEN) {
      try {
        if (isMission5) {
          console.log('🧪 M5 TRACE: Sending end turn', {
            gameId,
            messageType: 'end-turn',
            retriesLeft,
            readyState: socketCandidate.readyState,
            yourTurn,
            gameTurn: gameState?.turn,
            userId
          });
        }
        socketCandidate.send(message);
        return;
      } catch (error) {
        console.error('❌ Error sending end-turn message:', error);
      }
    }

    if (retriesLeft > 0) {
      if (isMission5) {
        console.log('🧪 M5 TRACE: End turn retry scheduled', {
          gameId,
          retriesLeft,
          refReady: currentSocketRef.current?.readyState,
          wsReady: ws?.readyState
        });
      }
      setTimeout(() => trySendEndTurn(retriesLeft - 1), 300);
      return;
    }

    console.error('❌ endTurn failed: socket never became OPEN', {
      hasRef: !!currentSocketRef.current,
      hasWs: !!ws,
      refReady: currentSocketRef.current?.readyState,
      wsReady: ws?.readyState
    });
    setError(t('battleUI.connectionLost'));
  };

  trySendEndTurn();
};

const handleResign = () => {
  playSoundEffect('/sounds/click.mp3');
  setResignDialogFocus('cancel');
  setShowResignDialog(true);
};

const confirmResign = () => {
  playSoundEffect('/sounds/click.mp3');
  // ✅ FIX: Check both ws state and socket ref for browser refresh scenarios
  const activeSocket = ws || currentSocketRef.current;
  if (!gameId || !activeSocket || activeSocket.readyState !== 1) {
    console.error('❌ confirmResign blocked:', { gameId, hasWs: !!ws, hasRef: !!currentSocketRef.current, wsReady: ws?.readyState, refReady: currentSocketRef.current?.readyState });
    setError(t('battleUI.connectionLost'));
    return;
  }

  activeSocket.send(JSON.stringify({ type: "resign", gameId }));
  setShowResignDialog(false);
};

const cancelResign = () => {
  playSoundEffect('/sounds/click.mp3');
  setShowResignDialog(false);
};

const handleDevAutowin = () => {
  playSoundEffect('/sounds/click.mp3');
  const activeSocket = ws || currentSocketRef.current;
  if (!gameId || !activeSocket || activeSocket.readyState !== 1) {
    setError(t('battleUI.connectionLost'));
    return;
  }
  activeSocket.send(JSON.stringify({ type: 'dev-autowin', gameId }));
};

const handleDevBattleSetupApply = useCallback(
  async ({ serverAction, uiPatch }) => {
    const activeSocket = ws || currentSocketRef.current;
    if (!gameId || !activeSocket || activeSocket.readyState !== 1) {
      throw new Error(t('battleUI.connectionLost'));
    }
    activeSocket.send(
      JSON.stringify({
        type: 'play-turn',
        gameId,
        action: serverAction,
        stateVersion: lastStateVersionRef.current,
      })
    );
    setDevBattleSetupUi(uiPatch);
  },
  [ws, gameId, t]
);

  const sendExploreUnknownDevSpawn = (spawnActionType) => {
    playSoundEffect('/sounds/click.mp3');
    const activeSocket = ws || currentSocketRef.current;
    if (!gameId || !activeSocket || activeSocket.readyState !== 1) {
      setError(t('battleUI.connectionLost'));
      return;
    }
    activeSocket.send(
      JSON.stringify({
        type: 'play-turn',
        gameId,
        action: { type: spawnActionType },
        stateVersion: lastStateVersionRef.current,
      })
    );
  };

  const registerExploreUnknownEpKillExplosion = (zoneWithPrefix, index, variant, damageAmount) => {
    const existing = getEpSlotDeathAnimations(zoneWithPrefix, index);
    if (existing && existing.length > 0) {
      return;
    }
    const animationKey = `eu_ep_${variant}_${zoneWithPrefix}_${index}_${Date.now()}`;
    registerEpSlotDeathAnimation(zoneWithPrefix, index, [
      {
        key: animationKey,
        startDelay: 0,
        damageAmount: damageAmount > 0 ? damageAmount : undefined,
        onComplete: () => {
          unregisterEpSlotDeathAnimation(zoneWithPrefix, index);
        },
      },
    ]);
  };

  const registerExploreUnknownEpLocalAnimations = (actionType, extra = {}) => {
    const state = activeGameState?.state;
    if (!state || typeof state !== 'object') return;
    const selfId = String(activeUserId);
    const allPlayerIds = Object.keys(state);

    const variant =
      actionType === 'useExploreUnknownEpGlobal'
        ? 'global'
        : actionType === 'useExploreUnknownEpZone'
          ? 'zone'
          : actionType === 'useExploreUnknownEpTarget'
            ? 'target'
            : null;
    if (!variant) return;

    const hasNoFriendlyDestroy =
      activeGameState?.exploreUnknownLevelBonuses?.epNoFriendlyDestroy === true ||
      (Array.isArray(activeGameState?.exploreUnknownLevelBonuses?.activeEffects) &&
        activeGameState.exploreUnknownLevelBonuses.activeEffects.some(
          (fx) => fx && fx.kind === 'endgame' && fx.effect === 'epNoFriendlyDestroy'
        ));

    const registerKillAt = (ownerPlayerId, zone, index) => {
      const isSelf = String(ownerPlayerId) === selfId;
      const zoneWithPrefix = `${isSelf ? 'your' : 'opponent'}_${zone}`;
      const unit = state[ownerPlayerId]?.playedOrders?.[zone]?.[index];
      const damageAmount = unit && typeof unit.health === 'number' ? unit.health : undefined;
      registerExploreUnknownEpKillExplosion(zoneWithPrefix, index, variant, damageAmount);
    };

    const queuedTargets = [];

    if (actionType === 'useExploreUnknownEpTarget') {
      const targetInstanceId = extra?.targetInstanceId;
      if (!targetInstanceId) return;
      let foundTarget = false;
      for (const pid of allPlayerIds) {
        for (const zone of ['front', 'flank']) {
          const lane = state[pid]?.playedOrders?.[zone];
          if (!Array.isArray(lane)) continue;
          const idx = lane.findIndex((unit) => unit && String(unit.instanceId) === String(targetInstanceId));
          if (idx >= 0) {
            queuedTargets.push({ ownerPlayerId: pid, zone, index: idx });
            foundTarget = true;
            break;
          }
        }
        if (foundTarget) break;
      }
      if (!foundTarget) return;
    }

    if (actionType === 'useExploreUnknownEpZone') {
      const normalizedZone = String(extra?.zone || '').replace(/^opponent_/, '').replace(/^your_/, '');
      if (normalizedZone !== 'front' && normalizedZone !== 'flank') return;
      for (const pid of allPlayerIds) {
        if (hasNoFriendlyDestroy && String(pid) === selfId) continue;
        const lane = state[pid]?.playedOrders?.[normalizedZone];
        if (!Array.isArray(lane)) continue;
        lane.forEach((unit, idx) => {
          if (!unit) return;
          queuedTargets.push({ ownerPlayerId: pid, zone: normalizedZone, index: idx });
        });
      }
    }

    if (actionType === 'useExploreUnknownEpGlobal') {
      for (const pid of allPlayerIds) {
        if (hasNoFriendlyDestroy && String(pid) === selfId) continue;
        for (const zone of ['front', 'flank']) {
          const lane = state[pid]?.playedOrders?.[zone];
          if (!Array.isArray(lane)) continue;
          lane.forEach((unit, idx) => {
            if (!unit) return;
            queuedTargets.push({ ownerPlayerId: pid, zone, index: idx });
          });
        }
      }
    }

    if (queuedTargets.length === 0) return;

    // Instant explosion per killed unit (Repurposed Warhead / slot-death pattern), not staggered strategem VFX.
    queuedTargets.forEach(({ ownerPlayerId, zone, index }) => {
      registerKillAt(ownerPlayerId, zone, index);
    });
  };

  const sendExploreUnknownEpSpend = (actionType, extra = {}) => {
    playSoundEffect('/sounds/click.mp3');
    const activeSocket = ws || currentSocketRef.current;
    if (!gameId || !activeSocket || activeSocket.readyState !== 1) {
      setError(t('battleUI.connectionLost'));
      return;
    }
    if (!yourTurn) {
      setError(t('battleUI.notYourTurn', "It's not your turn"));
      return;
    }
    registerExploreUnknownEpLocalAnimations(actionType, extra);
    setEuEpZoneTargetMode(false);
    setEuEpTargetMode(false);
    activeSocket.send(
      JSON.stringify({
        type: 'play-turn',
        gameId,
        action: { type: actionType, ...extra },
        stateVersion: lastStateVersionRef.current,
      })
    );
  };

  const attackOrder = (attackerZone, attackerIdx, targetZone, targetIdx) => {
    // Client-side protect validation removed - server handles all protect logic validation
    if (!yourTurn || !gameId) {
      console.error('❌ ATTACK ORDER BLOCKED: Not your turn or no gameId', { yourTurn, gameId });
      return;
    }
    if (!ws || ws.readyState !== 1) {
      console.error('❌ ATTACK ORDER BLOCKED: WebSocket not ready', { hasWs: !!ws, readyState: ws?.readyState });
      setError(t('battleUI.connectionLost'));
      return;
    }
    
    // Prevent double-click attacks (using ref for synchronous check)
    if (attackInProgressRef.current) {
      // ✅ SAFETY: If attack has been in progress for more than 10 seconds, reset it
      // This handles cases where attack-complete message was lost or animation never completed
      const attackTimeout = 10000; // 10 seconds
      const lastAttackTime = pendingAttackInfoRef.current?.timestamp || 0;
      const timeSinceAttack = Date.now() - lastAttackTime;
      
      if (timeSinceAttack > attackTimeout && lastAttackTime > 0) {
        attackInProgressRef.current = false;
        pendingAttackInfoRef.current = null;
        // Continue with the attack
      } else {
        return;
      }
    }

    // Get the actual unit instances
    const attacker = you?.playedOrders?.[attackerZone]?.[attackerIdx];
    if (!attacker) {
      console.error('❌ ATTACK ORDER BLOCKED: No attacker found', { attackerZone, attackerIdx, playedOrders: you?.playedOrders });
      setError(t('battleUI.connectionLost'));
      return;
    }
    
    
    // ✅ VALIDATION: Check if attacker has 0 attack rating - cannot attack
    const attackRating = attacker.attack_rating ?? 0;
    if (attackRating === 0) {
      setError('This unit has 0 attack rating and cannot attack');
      attackInProgressRef.current = false;
      return;
    }
    
    // Determine target based on targetZone
    let target = null;
    let actualTargetZone = targetZone;

    if (targetZone === 'general') {
      // Attacking opponent general
      target = null;
      actualTargetZone = 'general';
    } else if (targetZone === 'opponent_drone' || targetZone === 'drones') {
      // Attacking opponent drone
      const opponentId = gameState.players?.find(id => String(id) !== String(userId));
      const opponentState = opponentId ? gameState.state?.[opponentId] : null;
      const drone = opponentState?.drones?.[targetIdx];
      
      if (!drone) {
        console.error('No drone found', { targetZone, targetIdx });
        setError(t('battleUI.connectionLost'));
        return;
      }
      
      target = drone;
      actualTargetZone = 'drone'; // Special zone identifier for drones
    } else {
      // Determine if the target zone belongs to opponent or player
      // Zone names starting with 'opponent_' are opponent zones
      const isOpponentZone = targetZone.startsWith('opponent_');
      const normalizedZone = targetZone.replace('opponent_', '');
      
      if (isOpponentZone) {
        // Attacking opponent unit
        target = opponent?.playedOrders?.[normalizedZone]?.[targetIdx];
        actualTargetZone = normalizedZone;
      } else {
        // Attacking friendly unit (rare, but possible with confuse)
        target = you?.playedOrders?.[normalizedZone]?.[targetIdx];
        actualTargetZone = normalizedZone;
      }
      
      if (!target) {
        console.error('No target found', { targetZone, normalizedZone, targetIdx, isOpponentZone });
        setError(t('battleUI.connectionLost'));
        return;
      }
    }

    // ✅ OOR VALIDATION: Check if target has Out of Reach status
    // CRITICAL: OOR units cannot be attacked by ANYONE, regardless of Blitz
    // Blitz only allows attacking the same turn you're deployed, NOT bypassing OOR
    if (target && target.isOOR) {
      setError(`Cannot attack ${target.name} - target has Out of Reach status. OOR units cannot be attacked.`);
      return; // Block the attack
    }

    // Send attack action with instanceIds
    // NOTE: Animation will be triggered only after backend confirms successful attack
    // This prevents animations for illegal attacks (e.g., attacking protected units)
    const attackMessage = {
      type: "play-turn",
      gameId,
      action: {
        type: "attackOrder",
        attackerInstanceId: attacker.instanceId,
        targetInstanceId: target?.instanceId,
        targetZone: actualTargetZone,
      },
    };

    // ✅ FIX: Check both ws state and socket ref for browser refresh scenarios
    const activeSocket = ws || currentSocketRef.current;
    if (!activeSocket || activeSocket.readyState !== 1) {
      console.error('❌ attackOrder blocked: WebSocket not ready', { hasWs: !!ws, hasRef: !!currentSocketRef.current, wsReady: ws?.readyState, refReady: currentSocketRef.current?.readyState });
      setError(t('battleUI.connectionLost'));
      attackInProgressRef.current = false;
      return;
    }
    try {
      activeSocket.send(JSON.stringify(attackMessage));
    } catch (error) {
      console.error('❌ ATTACK ORDER SEND FAILED:', error);
      setError('Failed to send attack order');
      attackInProgressRef.current = false;
      return;
    }
    
    // Mark attack as in progress (using ref for synchronous update)
    attackInProgressRef.current = true;
    setSelectedAttacker(null);
    setAttackMode(false);
    
    // Store the target that was just attacked - expand will stay disabled for this target until mouse leaves
    if (targetIdx !== null) {
      // Unit attack - store target info
      const targetZoneForTracking = targetZone.startsWith('opponent_') ? targetZone : `opponent_${targetZone}`;
      setLastAttackedTarget({ zone: targetZoneForTracking, idx: targetIdx });
    } else if (targetZone === 'general') {
      // General attack - clear last attacked target (general doesn't have expand anyway)
      setLastAttackedTarget(null);
    }
    
    // Store pending attack info for animation (will be used if attack succeeds)
    // This allows us to trigger animation only after backend confirms success
    pendingAttackInfoRef.current = {
      attacker,
      attackerZone,
      attackerIndex: attackerIdx,
      attackerPlayerId: userId,
      targetZone: actualTargetZone,
      targetIndex: targetIdx,
      targetPlayerId: targetZone === 'general' ? opponentId : (targetZone.startsWith('opponent_') ? opponentId : userId),
      timestamp: Date.now(), // Store timestamp for timeout check
    };
  };

  const attackGeneral = (attackerZone, attackerIdx) => {
    attackOrder(attackerZone, attackerIdx, 'general', null);
  };

  // Unit replacement handlers (for full zone replacement)
  const clearReplaceTargetingState = () => {
    setReplaceTargetMode(false);
    setCounterStrikeReplaceMode(false);
    setSelectedReplaceUnitId(null);
    setSelectedReplaceInstanceId(null);
    setReplaceTargetZone(null);
    setClickedUnitForReplace(null);
    setHoveredPlayed({ zone: null, idx: null });
    setSelectedAttacker(null);
    setAttackMode(false);
    setError('');
  };

  const enterCounterStrikeReplaceMode = (zone) => {
    if (!selectedCounterStrikeOrder) {
      return;
    }
    setReplaceTargetMode(true);
    setCounterStrikeReplaceMode(true);
    setSelectedReplaceUnitId(selectedCounterStrikeOrder.id);
    setSelectedReplaceInstanceId(selectedCounterStrikeOrder.instanceId);
    setReplaceTargetZone(zone);
    setError(t('battleUI.zoneFull'));
  };

  const resolveReplaceTargetFromGameState = (targetUnit) => {
    const zone = targetUnit?.zone;
    if (!zone || targetUnit?.instanceId == null) {
      return null;
    }

    const state = activeGameState?.state;
    if (!state) {
      return null;
    }

    const viewerId = activeUserId != null ? String(activeUserId) : null;
    const playerState =
      (viewerId && state[viewerId]) ||
      (viewerId && state[activeUserId]) ||
      (userId != null && state[String(userId)]) ||
      (userId != null && state[userId]);
    const playedOrders = playerState?.playedOrders?.[zone];
    if (!Array.isArray(playedOrders)) {
      return null;
    }

    const targetInstanceId = String(targetUnit.instanceId);
    let targetIndex = playedOrders.findIndex(
      (unit) => unit && String(unit.instanceId) === targetInstanceId
    );

    if (targetIndex < 0 && targetUnit.index != null) {
      const unitAtIndex = playedOrders[targetUnit.index];
      if (unitAtIndex && String(unitAtIndex.instanceId) === targetInstanceId) {
        targetIndex = targetUnit.index;
      }
    }

    if (targetIndex < 0) {
      return null;
    }

    const resolvedUnit = playedOrders[targetIndex];
    return {
      unit: resolvedUnit,
      zone,
      index: targetIndex,
      instanceId: resolvedUnit.instanceId,
    };
  };

  const handleReplaceUnit = (targetUnit) => {
    if (!yourTurn || !ws || ws.readyState !== 1 || !gameId || !selectedReplaceInstanceId) {
      setError(t('battleUI.connectionLost'));
      return;
    }

    const resolvedTarget = resolveReplaceTargetFromGameState(targetUnit);
    if (!resolvedTarget) {
      setError(t('battleUI.replaceTargetNotFound') || 'Target unit not found — try clicking the unit again');
      setTimeout(() => setError(''), 2500);
      return;
    }

    ws.send(JSON.stringify({
      type: 'play-turn',
      gameId,
      action: {
        type: 'replaceUnit',
        selectedUnitId: selectedReplaceUnitId,
        selectedInstanceId: selectedReplaceInstanceId,
        targetUnitId: resolvedTarget.unit.id,
        targetInstanceId: resolvedTarget.instanceId,
        targetZone: resolvedTarget.zone,
        targetIndex: resolvedTarget.index
      }
    }));

    clearReplaceTargetingState();
    setSelectedOrderId(null);
    setSelectedOrderInstanceId(null);
  };

  const handleReplaceCounterStrike = (targetUnit) => {
    if (!ws || ws.readyState !== 1 || !gameId || !selectedReplaceInstanceId) {
      setError(t('battleUI.connectionLost'));
      return;
    }

    const resolvedTarget = resolveReplaceTargetFromGameState(targetUnit);
    if (!resolvedTarget) {
      setError(t('battleUI.replaceTargetNotFound') || 'Target unit not found — try clicking the unit again');
      setTimeout(() => setError(''), 2500);
      return;
    }

    ws.send(JSON.stringify({
      type: 'play-turn',
      gameId,
      action: {
        type: 'replaceCounterStrike',
        orderId: selectedReplaceUnitId,
        instanceId: selectedReplaceInstanceId,
        targetZone: resolvedTarget.zone,
        targetIndex: resolvedTarget.index,
        targetInstanceId: resolvedTarget.instanceId
      }
    }));

    clearReplaceTargetingState();
    setSelectedCounterStrikeOrder(null);
  };

  const handleCancelReplace = () => {
    const wasCounterStrikeReplace = counterStrikeReplaceMode;
    clearReplaceTargetingState();
    setSelectedOrderId(null);
    setSelectedOrderInstanceId(null);
    if (!wasCounterStrikeReplace) {
      setSelectedCounterStrikeOrder(null);
    }
  };

  // Portal displacement (War Effort): use one charge to move unit to the other zone
  const handlePortalDisplacement = (unit, targetZone) => {
    if (!yourTurn || !ws || ws.readyState !== 1 || !gameId) {
      setError(t('battleUI.connectionLost'));
      return;
    }
    if (!unit?.instanceId || (targetZone !== 'front' && targetZone !== 'flank')) return;
    ws.send(JSON.stringify({
      type: 'play-turn',
      gameId,
      action: { type: 'portalDisplacement', instanceId: unit.instanceId, targetZone }
    }));
    setError('');
  };

  // Drone replacement handlers (for full drone zone replacement)
  const handleReplaceDrone = (targetDrone) => {
    if (!yourTurn || !ws || ws.readyState !== 1 || !gameId || !selectedReplaceDroneInstanceId) {
      setError(t('battleUI.connectionLost'));
      return;
    }

    // Send replace action to backend
    ws.send(JSON.stringify({
      type: 'play-turn',
      gameId,
      action: {
        type: 'replaceDrone',
        selectedDroneId: selectedReplaceDroneId,
        selectedInstanceId: selectedReplaceDroneInstanceId,
        targetDroneId: targetDrone.id,
        targetInstanceId: targetDrone.instanceId,
        targetIndex: targetDrone.index
      }
    }));

    // Clear replacement state
    setReplaceDroneTargetMode(false);
    setSelectedReplaceDroneId(null);
    setSelectedReplaceDroneInstanceId(null);
    setClickedDroneForReplace(null);
    
    // Clear hand selection
    setSelectedOrderId(null);
    setSelectedOrderInstanceId(null);
    
    setError('');
  };

  const handleCancelReplaceDrone = () => {
    // Clear replacement state
    setReplaceDroneTargetMode(false);
    setSelectedReplaceDroneId(null);
    setSelectedReplaceDroneInstanceId(null);
    setClickedDroneForReplace(null);
    
    // Clear hand selection
    setSelectedOrderId(null);
    setSelectedOrderInstanceId(null);
    
    setError('');
  };

  // Counter Strike mode handler - don't use carousel mechanism
  const handleCounterStrikeModeActivated = (order) => {
    
    // Check if this is a strategem - strategems should be played immediately
    if (order.type === 'Strategem') {
      // For strategems, play immediately with a dummy zone (backend will handle it correctly)
      // The backend will either play it immediately or enter targeting mode
      playCounterStrike(order.id, 'front', order.instanceId);
      // Clear any existing selected counter strike order to avoid confusion
      setSelectedCounterStrikeOrder(null);
      return;
    }
    
    // For units and other orders, store for zone selection
    setSelectedCounterStrikeOrder(order);
  };


  // Strategem Counter Strike functions
  const executeStrategemCounterStrike = async (strategemIndex) => {
    if (!gameId) {
      setError(t('battleUI.connectionLost'));
      return;
    }

    try {
      const response = await fetch(`${API_URL}/execute-strategem-counter-strike`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          gameId,
          strategemIndex
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to execute strategem Counter Strike');
      }

      const result = await response.json();
      
      // The strategem will now enter targeting mode
      // The game state will be updated via WebSocket
    } catch (error) {
      console.error('Error executing strategem Counter Strike:', error);
      setError(translateBackendError(error.message));
    }
  };

  const keepStrategemCounterStrike = async (strategemIndex) => {
    if (!gameId) {
      setError(t('battleUI.connectionLost'));
      return;
    }

    try {
      const response = await fetch(`${API_URL}/keep-strategem-counter-strike`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          gameId,
          strategemIndex
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to keep strategem Counter Strike');
      }

      const result = await response.json();
      
      // The strategem will be added to hand
      // The game state will be updated via WebSocket
    } catch (error) {
      console.error('Error keeping strategem Counter Strike:', error);
      setError(translateBackendError(error.message));
    }
  };

  // Use effective state/userId for spectator mode support
  // ✅ CRITICAL: Memoize activeGameState so it only changes when gameState/effectiveGameState actually changes
  // This ensures 'you' and 'opponent' memos update properly when state changes
  const activeGameState = useMemo(() => {
    return isFriendSpectating ? effectiveGameState : gameState;
  }, [isFriendSpectating, effectiveGameState, gameState]);
  // ✅ FIX: For autobattler mode, use playerBotId instead of userId
  // In autobattler mode, the actual players are bot IDs, not the user ID
  // Priority: autobattler mode > friend spectating > normal player
  const activeUserId = useMemo(() => {
    if (activeGameState?.isAutobattler && activeGameState?.playerBotId) {
      return activeGameState.playerBotId;
    }
    if (isFriendSpectating) {
      return effectiveUserId;
    }
    return userId;
  }, [activeGameState?.isAutobattler, activeGameState?.playerBotId, isFriendSpectating, effectiveUserId, userId]);

  const activeUserIdRef = useRef(activeUserId);
  activeUserIdRef.current = activeUserId;
  attackHealthFxViewerUserIdRef.current = deployAnimationViewerUserIdRef.current ?? activeUserId;
  const activeOpponentId = activeGameState?.players?.find(
    (id) => String(id) !== String(activeUserId)
  );
  countdownHealthLookupRef.current = (trigger) => resolveCountdownTargetHealth(
    trigger,
    activeGameState,
    activeUserId,
    activeOpponentId
  );

  const healthFxReconcileContextRef = useRef(null);
  healthFxReconcileContextRef.current = {
    hasPendingDamageTrigger: hasPendingDamageTriggerForInstance,
    hasActiveProjectileAnimation: hasActiveProjectileAnimationForInstance,
  };

  useEffect(() => {
    if (!activeGameState) {
      return;
    }
    battlefieldHealthCountdown.reconcileFromGameState(
      activeGameState,
      healthFxReconcileContextRef.current
    );
  }, [activeGameState, battlefieldHealthCountdown.reconcileFromGameState]);
  isContainerMobileRef.current = isContainerMobile;
  turnStartOverlayCopyRef.current = {
    turnLabel:
      typeof activeGameState?.turnNumber === 'number' && !Number.isNaN(activeGameState.turnNumber)
        ? t('battleUI.turnStartTurnLabel', { turn: activeGameState.turnNumber })
        : null,
    timeToActText: t('battleUI.timeToAct'),
  };
  openTurnStartOverlayRef.current = () => {
    const gs = gameStateRef.current;
    if (!gs) return;
    const turnNumber =
      typeof gs.turnNumber === 'number' && !Number.isNaN(gs.turnNumber) ? gs.turnNumber : null;
    const copy = turnStartOverlayCopyRef.current;
    const snapshot = buildTurnStartOverlaySnapshot(
      gs,
      activeUserIdRef.current,
      isContainerMobileRef.current,
      {
        turnLabel:
          turnNumber != null
            ? t('battleUI.turnStartTurnLabel', { turn: turnNumber })
            : copy.turnLabel,
        timeToActText: copy.timeToActText || t('battleUI.timeToAct'),
      }
    );
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        showTurnStartOverlay(snapshot);
      });
    });
  };

  useEffect(() => {
    preloadImage('/images/turnstart2.png').catch(() => {});
    return () => {
      hideTurnStartOverlay();
    };
  }, []);

  deployAnimationViewerUserIdRef.current = activeUserId;

  useEffect(() => {
    const gs = activeGameState;
    if (!gs) return;
    const gid = gs.gameId ?? null;
    if (gid != null && battlebotPortalGameIdRef.current !== gid) {
      battlebotPortalGameIdRef.current = gid;
      battlebotPortalInitializedRef.current = false;
      battlebotPortalSeenInstanceIdsRef.current = new Set();
      setBattlebotPortalEffects(new Map());
    }
    if (!gs.state) return;
    if (!battlebotPortalInitializedRef.current) {
      for (const pid of Object.keys(gs.state)) {
        const completed = gs.state[pid]?.completedOrders || [];
        for (const o of completed) {
          if (o && o.type === 'Strategem' && o.id === 369 && o.instanceId) {
            battlebotPortalSeenInstanceIdsRef.current.add(o.instanceId);
          }
        }
      }
      battlebotPortalInitializedRef.current = true;
      return;
    }
    if (activeUserId == null) return;
    const viewerId = activeUserId;
    const preferTurn =
      typeof gs.turnNumber === 'number' && !Number.isNaN(gs.turnNumber) ? gs.turnNumber : undefined;
    for (const pid of Object.keys(gs.state)) {
      const completed = gs.state[pid]?.completedOrders || [];
      for (const o of completed) {
        if (!o || o.type !== 'Strategem' || o.id !== 369 || !o.instanceId) continue;
        if (battlebotPortalSeenInstanceIdsRef.current.has(o.instanceId)) continue;
        const isCasterViewer = String(pid) === String(viewerId);
        const viewerSlots = resolveBattlebot369ViewerSlots(
          gs.state[pid]?.playedOrders,
          isCasterViewer,
          { preferTurnNumber: preferTurn }
        );
        if (!viewerSlots) continue;
        battlebotPortalSeenInstanceIdsRef.current.add(o.instanceId);
        const portalKey = `bb369-${o.instanceId}-${Date.now()}`;
        setBattlebotPortalEffects((prev) => {
          const next = new Map(prev);
          next.set(portalKey, {
            key: portalKey,
            slots: viewerSlots,
          });
          return next;
        });
      }
    }
  }, [activeGameState, activeUserId]);

  useEffect(() => {
    const gs = activeGameState;
    if (!gs?.state) return;
    const gid = gs.gameId ?? null;
    if (gid != null && fleshMimicPortalGameIdRef.current !== gid) {
      fleshMimicPortalGameIdRef.current = gid;
      fleshMimicPortalInitializedRef.current = false;
      fleshMimicPortalSeenInstanceIdsRef.current = new Set();
      setFleshMimicPortalEffects(new Map());
    }
    if (!fleshMimicPortalInitializedRef.current) {
      for (const pid of Object.keys(gs.state)) {
        const completed = gs.state[pid]?.completedOrders || [];
        for (const o of completed) {
          if (o && o.type === 'Strategem' && Number(o.id) === 52 && o.instanceId) {
            fleshMimicPortalSeenInstanceIdsRef.current.add(o.instanceId);
          }
        }
      }
      fleshMimicPortalInitializedRef.current = true;
      return;
    }
    if (activeUserId == null) return;
    const viewerId = activeUserId;
    for (const pid of Object.keys(gs.state)) {
      if (String(pid) !== String(viewerId)) continue;
      const completed = gs.state[pid]?.completedOrders || [];
      for (const o of completed) {
        if (!o || o.type !== 'Strategem' || Number(o.id) !== 52 || !o.instanceId) continue;
        if (fleshMimicPortalSeenInstanceIdsRef.current.has(o.instanceId)) continue;
        const fx = o.fleshMimicFx;
        if (!fx || fx.copiedOrderInstanceId == null) continue;
        fleshMimicPortalSeenInstanceIdsRef.current.add(o.instanceId);
        const portalKey = `fm52-${o.instanceId}-${Date.now()}`;
        setFleshMimicPortalEffects((prev) => {
          const next = new Map(prev);
          next.set(portalKey, {
            key: portalKey,
            copiedOrderInstanceId: fx.copiedOrderInstanceId,
          });
          return next;
        });
      }
    }
  }, [activeGameState, activeUserId]);

  useEffect(() => {
    const gs = activeGameState;
    if (!gs) return;
    const gid = gs.gameId ?? null;
    if (gid != null && plagueBloomPortalGameIdRef.current !== gid) {
      plagueBloomPortalGameIdRef.current = gid;
      plagueBloomPortalInitializedRef.current = false;
      plagueBloomPortalSeenInstanceIdsRef.current = new Set();
      setPlagueBloomPortalEffects(new Map());
    }
    if (!gs.state) return;
    if (!plagueBloomPortalInitializedRef.current) {
      for (const pid of Object.keys(gs.state)) {
        const completed = gs.state[pid]?.completedOrders || [];
        for (const o of completed) {
          if (
            o &&
            o.type === 'Strategem' &&
            (Number(o.id) === 58 || Number(o.id) === 61 || Number(o.id) === 184) &&
            o.instanceId
          ) {
            plagueBloomPortalSeenInstanceIdsRef.current.add(o.instanceId);
          }
        }
      }
      plagueBloomPortalInitializedRef.current = true;
      return;
    }
    if (activeUserId == null) return;
    const viewerId = activeUserId;
    for (const pid of Object.keys(gs.state)) {
      const completed = gs.state[pid]?.completedOrders || [];
      for (const o of completed) {
        if (
          !o ||
          o.type !== 'Strategem' ||
          (Number(o.id) !== 58 && Number(o.id) !== 61 && Number(o.id) !== 184) ||
          !o.instanceId
        ) {
          continue;
        }
        if (plagueBloomPortalSeenInstanceIdsRef.current.has(o.instanceId)) continue;
        const fx = o.plagueBloomFx;
        if (!fx || !Array.isArray(fx.deploys) || fx.deploys.length === 0) continue;
        const ownerId = fx.targetPlayerId != null ? String(fx.targetPlayerId) : String(pid);
        const isOwnerViewer = ownerId === String(viewerId);
        const prefix = isOwnerViewer ? 'your' : 'opponent';
        const slots = fx.deploys.map((d) => ({
          zone: `${prefix}_${d.zone}`,
          index: Number(d.slotIndex),
        }));
        const portalVariant = fx.portalVariant === 'void' ? 'void' : 'crimson';
        plagueBloomPortalSeenInstanceIdsRef.current.add(o.instanceId);
        const portalKey = `pb${o.id}-${o.instanceId}-${Date.now()}`;
        setPlagueBloomPortalEffects((prev) => {
          const next = new Map(prev);
          next.set(portalKey, { key: portalKey, slots, portalVariant });
          return next;
        });
      }
    }
  }, [activeGameState, activeUserId]);

  // Explore Unknown: flash map-style darkness meter when run darkness rises or Memory pending reduction increases.
  useEffect(() => {
    const gs = activeGameState;
    if (!gs || gs.exploreUnknownMissionIndex == null) {
      euDarknessFlashGameIdRef.current = null;
      euMemoryReductionPrevForFlashRef.current = 0;
      euRunDarknessPrevForFlashRef.current = null;
      setEuDarknessMeterFlash(null);
      return;
    }
    const gid = gs.gameId ?? null;
    if (gid != null && euDarknessFlashGameIdRef.current !== gid) {
      euDarknessFlashGameIdRef.current = gid;
      euMemoryReductionPrevForFlashRef.current =
        typeof gs.exploreUnknownMemoryDarknessReductionTotal === 'number' &&
        !Number.isNaN(gs.exploreUnknownMemoryDarknessReductionTotal)
          ? gs.exploreUnknownMemoryDarknessReductionTotal
          : 0;
      const base0 = gs.exploreUnknownRunDarknessPercent;
      euRunDarknessPrevForFlashRef.current =
        typeof base0 === 'number' && !Number.isNaN(base0) ? base0 : null;
      return;
    }
    const base = gs.exploreUnknownRunDarknessPercent;
    const red = gs.exploreUnknownMemoryDarknessReductionTotal;
    if (typeof red !== 'number' || Number.isNaN(red)) return;
    if (typeof base !== 'number' || Number.isNaN(base)) return;

    const effectivePct = Math.max(0, Math.min(100, base - red));
    const prevRed = euMemoryReductionPrevForFlashRef.current;
    const prevBase = euRunDarknessPrevForFlashRef.current;

    let fromPct = null;
    if (prevBase != null && base > prevBase) {
      fromPct = Math.max(0, Math.min(100, prevBase - prevRed));
    } else if (red > prevRed) {
      fromPct = Math.max(0, Math.min(100, base - prevRed));
    }

    if (fromPct != null && effectivePct !== fromPct) {
      setEuDarknessMeterFlash({ fromPct, toPct: effectivePct, key: Date.now() });
    }

    euMemoryReductionPrevForFlashRef.current = red;
    euRunDarknessPrevForFlashRef.current = base;
  }, [
    activeGameState,
    activeGameState?.gameId,
    activeGameState?.exploreUnknownMissionIndex,
    activeGameState?.exploreUnknownRunDarknessPercent,
    activeGameState?.exploreUnknownMemoryDarknessReductionTotal,
    activeGameState?.stateVersion,
  ]);

  useEffect(() => {
    if (!euDarknessMeterFlash) return undefined;
    const durationMs =
      typeof euDarknessMeterFlash.durationMs === 'number' && euDarknessMeterFlash.durationMs > 0
        ? euDarknessMeterFlash.durationMs
        : 2650;
    const t = window.setTimeout(() => setEuDarknessMeterFlash(null), durationMs);
    return () => window.clearTimeout(t);
  }, [euDarknessMeterFlash?.key]);

  useEffect(() => {
    const gs = activeGameState;
    if (!gs || gs.exploreUnknownMissionIndex == null) {
      euDarkKnowledgeFlashGameIdRef.current = null;
      euDarkKnowledgePartsPrevFlashRef.current = null;
      setEuDarkKnowledgeMeterFlash(null);
      return;
    }
    const partsRaw = gs.exploreUnknownDarkKnowledgePartsCollected;
    if (typeof partsRaw !== 'number' || Number.isNaN(partsRaw)) {
      throw new Error(
        'Explore Unknown: exploreUnknownDarkKnowledgePartsCollected must be a finite number on game state'
      );
    }
    const parts = Math.round(partsRaw);
    if (!Number.isInteger(parts) || parts < 0 || parts > EXPLORE_UNKNOWN_DARK_KNOWLEDGE_TARGET_PARTS) {
      throw new Error(
        `Explore Unknown: exploreUnknownDarkKnowledgePartsCollected out of range (${String(partsRaw)})`
      );
    }
    const gid = gs.gameId ?? null;
    if (gid != null && euDarkKnowledgeFlashGameIdRef.current !== gid) {
      euDarkKnowledgeFlashGameIdRef.current = gid;
      euDarkKnowledgePartsPrevFlashRef.current = parts;
      return;
    }
    const prev = euDarkKnowledgePartsPrevFlashRef.current;
    if (prev === null || prev === undefined) {
      euDarkKnowledgePartsPrevFlashRef.current = parts;
      return;
    }
    if (parts !== prev) {
      euDarkKnowledgePartsPrevFlashRef.current = parts;
      setEuDarkKnowledgeMeterFlash({ fromParts: prev, toParts: parts, key: Date.now() });
    }
  }, [
    activeGameState,
    activeGameState?.gameId,
    activeGameState?.exploreUnknownMissionIndex,
    activeGameState?.exploreUnknownDarkKnowledgePartsCollected,
    activeGameState?.stateVersion,
  ]);

  useEffect(() => {
    if (!euDarkKnowledgeMeterFlash) return undefined;
    const timerId = window.setTimeout(() => setEuDarkKnowledgeMeterFlash(null), 2650);
    return () => window.clearTimeout(timerId);
  }, [euDarkKnowledgeMeterFlash?.key]);

  useEffect(() => {
    const gs = activeGameState;
    if (!gs || gs.exploreUnknownMissionIndex == null) {
      euHeartFlashGameIdRef.current = null;
      euHeartPartsPrevFlashRef.current = {};
      setEuHeartMeterFlash(null);
      return;
    }
    const gid = gs.gameId ?? null;
    if (gid != null && euHeartFlashGameIdRef.current !== gid) {
      euHeartFlashGameIdRef.current = gid;
      const init = {};
      for (const meter of EXPLORE_UNKNOWN_HEART_METERS) {
        const partsRaw = gs[meter.gameStateKey];
        if (typeof partsRaw !== 'number' || Number.isNaN(partsRaw)) {
          throw new Error(
            `Explore Unknown: ${meter.gameStateKey} must be a finite number on game state`
          );
        }
        const parts = Math.round(partsRaw);
        if (!Number.isInteger(parts) || parts < 0 || parts > EXPLORE_UNKNOWN_HEART_TARGET_PARTS) {
          throw new Error(
            `Explore Unknown: ${meter.gameStateKey} out of range (${String(partsRaw)})`
          );
        }
        init[meter.id] = parts;
      }
      euHeartPartsPrevFlashRef.current = init;
      return;
    }
    for (const meter of EXPLORE_UNKNOWN_HEART_METERS) {
      const partsRaw = gs[meter.gameStateKey];
      if (typeof partsRaw !== 'number' || Number.isNaN(partsRaw)) {
        throw new Error(
          `Explore Unknown: ${meter.gameStateKey} must be a finite number on game state`
        );
      }
      const parts = Math.round(partsRaw);
      if (!Number.isInteger(parts) || parts < 0 || parts > EXPLORE_UNKNOWN_HEART_TARGET_PARTS) {
        throw new Error(
          `Explore Unknown: ${meter.gameStateKey} out of range (${String(partsRaw)})`
        );
      }
      const prev = euHeartPartsPrevFlashRef.current[meter.id];
      if (prev === undefined) {
        euHeartPartsPrevFlashRef.current[meter.id] = parts;
        continue;
      }
      if (parts !== prev) {
        euHeartPartsPrevFlashRef.current[meter.id] = parts;
        setEuHeartMeterFlash({
          heartId: meter.id,
          fromParts: prev,
          toParts: parts,
          key: Date.now(),
        });
        break;
      }
    }
  }, [
    activeGameState,
    activeGameState?.gameId,
    activeGameState?.exploreUnknownMissionIndex,
    activeGameState?.exploreUnknownWeakHeartPartsCollected,
    activeGameState?.exploreUnknownHeartPartsCollected,
    activeGameState?.exploreUnknownStrongHeartPartsCollected,
    activeGameState?.stateVersion,
  ]);

  useEffect(() => {
    if (!euHeartMeterFlash) return undefined;
    const timerId = window.setTimeout(() => setEuHeartMeterFlash(null), 2650);
    return () => window.clearTimeout(timerId);
  }, [euHeartMeterFlash?.key]);

  useEffect(() => {
    const gs = activeGameState;
    if (!gs || gs.exploreUnknownMissionIndex == null) {
      euEpFlashGameIdRef.current = null;
      euEpPartsPrevFlashRef.current = {};
      setEuEpMeterFlash(null);
      return;
    }
    const gid = gs.gameId ?? null;
    if (gid != null && euEpFlashGameIdRef.current !== gid) {
      euEpFlashGameIdRef.current = gid;
      const init = {};
      for (const meter of EXPLORE_UNKNOWN_EP_METERS) {
        const partsRaw = gs[meter.gameStatePartsKey];
        if (typeof partsRaw !== 'number' || Number.isNaN(partsRaw)) {
          throw new Error(
            `Explore Unknown: ${meter.gameStatePartsKey} must be a finite number on game state`
          );
        }
        const parts = Math.round(partsRaw);
        if (!Number.isInteger(parts) || parts < 0 || parts > EXPLORE_UNKNOWN_EP_TARGET_PARTS) {
          throw new Error(
            `Explore Unknown: ${meter.gameStatePartsKey} out of range (${String(partsRaw)})`
          );
        }
        init[meter.id] = parts;
      }
      euEpPartsPrevFlashRef.current = init;
      return;
    }
    for (const meter of EXPLORE_UNKNOWN_EP_METERS) {
      const partsRaw = gs[meter.gameStatePartsKey];
      if (typeof partsRaw !== 'number' || Number.isNaN(partsRaw)) {
        throw new Error(
          `Explore Unknown: ${meter.gameStatePartsKey} must be a finite number on game state`
        );
      }
      const parts = Math.round(partsRaw);
      if (!Number.isInteger(parts) || parts < 0 || parts > EXPLORE_UNKNOWN_EP_TARGET_PARTS) {
        throw new Error(
          `Explore Unknown: ${meter.gameStatePartsKey} out of range (${String(partsRaw)})`
        );
      }
      const prev = euEpPartsPrevFlashRef.current[meter.id];
      if (prev === undefined) {
        euEpPartsPrevFlashRef.current[meter.id] = parts;
        continue;
      }
      if (parts !== prev) {
        euEpPartsPrevFlashRef.current[meter.id] = parts;
        setEuEpMeterFlash({
          epId: meter.id,
          fromParts: prev,
          toParts: parts,
          key: Date.now(),
        });
        break;
      }
    }
  }, [
    activeGameState,
    activeGameState?.gameId,
    activeGameState?.exploreUnknownMissionIndex,
    activeGameState?.exploreUnknownEpGlobalPartsCollected,
    activeGameState?.exploreUnknownEpZonePartsCollected,
    activeGameState?.exploreUnknownEpTargetPartsCollected,
    activeGameState?.stateVersion,
  ]);

  useEffect(() => {
    if (!euEpMeterFlash) return undefined;
    const timerId = window.setTimeout(() => setEuEpMeterFlash(null), 2650);
    return () => window.clearTimeout(timerId);
  }, [euEpMeterFlash?.key]);

  useEffect(() => {
    const popup = activeGameState?.exploreUnknownEpSetCompletePopup;
    if (!popup || !popup.popupId) return;
    if (euDismissedEpSetCompletePopupsRef.current.has(popup.popupId)) return;
    const epId = popup.epId;
    if (typeof epId !== 'string') {
      throw new Error('Explore Unknown EP set complete popup missing epId');
    }
    const upgradedFromTarget = popup.upgradedFromTarget === true;
    const displayEpId =
      upgradedFromTarget && typeof popup.chargeBonusId === 'string'
        ? popup.chargeBonusId
        : epId;
    const epMeter = EXPLORE_UNKNOWN_EP_METERS.find((m) => m.id === displayEpId);
    if (!epMeter) {
      throw new Error(
        `Explore Unknown EP set complete popup has unknown epId "${String(displayEpId)}"`
      );
    }
    const newCharges = Number(popup.newCharges);
    if (!Number.isFinite(newCharges) || newCharges < 1) {
      throw new Error('Explore Unknown EP set complete popup missing valid newCharges');
    }
    setEuEpSetCompleteModal({
      popupId: popup.popupId,
      epId,
      imageSrc: epMeter.image,
      variant: epMeter.variant,
      i18nKey: epMeter.i18nKey,
      newCharges,
      upgradedFromTarget,
    });
    playSoundEffect('/sounds/legendary_reveal.mp3');
  }, [
    activeGameState?.exploreUnknownEpSetCompletePopup?.popupId,
    activeGameState?.exploreUnknownEpSetCompletePopup?.epId,
    activeGameState?.exploreUnknownEpSetCompletePopup?.newCharges,
    activeGameState?.exploreUnknownEpSetCompletePopup?.upgradedFromTarget,
    activeGameState?.exploreUnknownEpSetCompletePopup?.chargeBonusId,
  ]);

  useEffect(() => {
    const gs = activeGameState;
    const partsKey = EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_METER.gameStatePartsKey;
    if (!gs || gs.exploreUnknownMissionIndex == null) {
      euRewriteFlashGameIdRef.current = null;
      euRewritePartsPrevFlashRef.current = null;
      setEuRewriteMeterFlash(null);
      return;
    }
    const partsRaw = gs[partsKey];
    if (typeof partsRaw !== 'number' || Number.isNaN(partsRaw)) {
      throw new Error(`Explore Unknown: ${partsKey} must be a finite number on game state`);
    }
    const parts = Math.round(partsRaw);
    if (!Number.isInteger(parts) || parts < 0 || parts > EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_TARGET_PARTS) {
      throw new Error(`Explore Unknown: ${partsKey} out of range (${String(partsRaw)})`);
    }
    const gid = gs.gameId ?? null;
    if (gid != null && euRewriteFlashGameIdRef.current !== gid) {
      euRewriteFlashGameIdRef.current = gid;
      euRewritePartsPrevFlashRef.current = parts;
      return;
    }
    const prev = euRewritePartsPrevFlashRef.current;
    if (prev === null || prev === undefined) {
      euRewritePartsPrevFlashRef.current = parts;
      return;
    }
    if (parts !== prev) {
      euRewritePartsPrevFlashRef.current = parts;
      setEuRewriteMeterFlash({ fromParts: prev, toParts: parts, key: Date.now() });
    }
  }, [
    activeGameState,
    activeGameState?.gameId,
    activeGameState?.exploreUnknownMissionIndex,
    activeGameState?.exploreUnknownRewriteExperiencePartsCollected,
    activeGameState?.stateVersion,
  ]);

  useEffect(() => {
    if (!euRewriteMeterFlash) return undefined;
    const timerId = window.setTimeout(() => setEuRewriteMeterFlash(null), 2650);
    return () => window.clearTimeout(timerId);
  }, [euRewriteMeterFlash?.key]);

  useEffect(() => {
    const popup = activeGameState?.exploreUnknownRewriteExperienceSetCompletePopup;
    if (!popup || !popup.popupId) return;
    if (euDismissedRewriteSetCompletePopupsRef.current.has(popup.popupId)) return;
    const newCharges = Number(popup.newCharges);
    if (!Number.isFinite(newCharges) || newCharges < 1) {
      throw new Error('Explore Unknown Rewrite Experience set complete popup missing valid newCharges');
    }
    setEuRewriteSetCompleteModal({
      popupId: popup.popupId,
      newCharges,
    });
    playSoundEffect('/sounds/legendary_reveal.mp3');
  }, [
    activeGameState?.exploreUnknownRewriteExperienceSetCompletePopup?.popupId,
    activeGameState?.exploreUnknownRewriteExperienceSetCompletePopup?.newCharges,
  ]);

  useEffect(() => {
    const popup = activeGameState?.exploreUnknownDarkKnowledgeRewardPopup;
    if (!popup || !popup.popupId) return;
    if (euDismissedDarkKnowledgeRewardPopupsRef.current.has(popup.popupId)) return;
    const rewardType = popup.rewardType || 'order';
    if (rewardType === 'billy_bucks') {
      const amount = Number(popup.amount);
      if (!Number.isFinite(amount) || amount <= 0) {
        throw new Error('Explore Unknown Dark Knowledge Billy Bucks popup missing valid amount');
      }
      setEuDarkKnowledgeRewardModal({
        popupId: popup.popupId,
        rewardType: 'billy_bucks',
        amount,
      });
      return;
    }
    if (rewardType !== 'order') {
      throw new Error(`Explore Unknown Dark Knowledge reward popup has unsupported rewardType: ${String(rewardType)}`);
    }
    const popupOrderId = Number(popup.orderId);
    if (Number.isNaN(popupOrderId)) {
      throw new Error('Explore Unknown Dark Knowledge reward popup missing valid orderId');
    }
    setEuDarkKnowledgeRewardModal({
      popupId: popup.popupId,
      rewardType: 'order',
      orderId: popupOrderId,
    });
  }, [
    activeGameState?.exploreUnknownDarkKnowledgeRewardPopup?.popupId,
    activeGameState?.exploreUnknownDarkKnowledgeRewardPopup?.orderId,
    activeGameState?.exploreUnknownDarkKnowledgeRewardPopup?.rewardType,
    activeGameState?.exploreUnknownDarkKnowledgeRewardPopup?.amount,
  ]);

  useEffect(() => {
    const popup = activeGameState?.exploreUnknownHeartSetCompletePopup;
    if (!popup || !popup.popupId) return;
    if (euDismissedHeartSetCompletePopupsRef.current.has(popup.popupId)) return;
    const heartId = popup.heartId;
    if (typeof heartId !== 'string') {
      throw new Error('Explore Unknown heart set complete popup missing heartId');
    }
    const heartMeter = EXPLORE_UNKNOWN_HEART_METERS.find((m) => m.id === heartId);
    if (!heartMeter) {
      throw new Error(`Explore Unknown heart set complete popup has unknown heartId "${String(heartId)}"`);
    }
    const healthBonus = Number(popup.healthBonus);
    if (!Number.isFinite(healthBonus) || healthBonus <= 0) {
      throw new Error('Explore Unknown heart set complete popup missing valid healthBonus');
    }
    setEuHeartSetCompleteModal({
      popupId: popup.popupId,
      heartId,
      imageSrc: heartMeter.image,
      variant: heartMeter.variant,
      i18nKey: heartMeter.i18nKey,
      healthBonus,
      darknessReductionPercent: Number(popup.darknessReductionPercent) || 0,
    });
    playSoundEffect('/sounds/legendary_reveal.mp3');
  }, [
    activeGameState?.exploreUnknownHeartSetCompletePopup?.popupId,
    activeGameState?.exploreUnknownHeartSetCompletePopup?.heartId,
    activeGameState?.exploreUnknownHeartSetCompletePopup?.healthBonus,
    activeGameState?.exploreUnknownHeartSetCompletePopup?.darknessReductionPercent,
  ]);
  
  // Debug spectator mode - log when state changes
  useEffect(() => {
    if (isSpectating && spectatingGameState) {
    }
  }, [isSpectating, spectatingGameState, effectiveGameState, activeGameState, activeUserId, spectatingPlayerId]);
  
  // ✅ FIX: Clean up invalid pending attacks that can't be rendered
  // This prevents ghost animations from staying on screen when attacker is not found
  useEffect(() => {
    if (!attackAnimations?.pendingAttacks?.length || !activeGameState?.state) {
      return;
    }
    
    // Check each pending attack and clean up any that can't be rendered
    attackAnimations.pendingAttacks.forEach(([attackId, pendingAttack]) => {
      const outcome = pendingAttack?.outcome;
      if (!outcome) {
        attackAnimations.completeAnimation(attackId);
        return;
      }
      
      const attackerPlayer = activeGameState.state[outcome.attackerPlayerId];
      if (!attackerPlayer) {
        attackAnimations.completeAnimation(attackId);
        return;
      }
      
      const targetPlayer = activeGameState.state[outcome.targetPlayerId];
      if (!targetPlayer) {
        attackAnimations.completeAnimation(attackId);
        return;
      }
      
      // Find attacker by instanceId or index
      let attacker = null;
      if (attackerPlayer.playedOrders?.[outcome.attackerZone]) {
        if (outcome.attackerInstanceId) {
          attacker = attackerPlayer.playedOrders[outcome.attackerZone].find(
            u => u && u.instanceId === outcome.attackerInstanceId
          );
        }
        if (!attacker && outcome.attackerIndex >= 0) {
          attacker = attackerPlayer.playedOrders[outcome.attackerZone][outcome.attackerIndex];
        }
      }
      
      if (!attacker) {
        attackAnimations.completeAnimation(attackId);
        return;
      }
    });
  }, [attackAnimations?.pendingAttacks, activeGameState?.state, attackAnimations]);
  
  // ✅ SPECTATOR FIX: Handle attack-initiated messages for spectators
  useEffect(() => {
    // Only log when there's actually a pending attack (not on every render)
    if (process.env.NODE_ENV === 'development' && pendingAttackInitiated) {
    }
    
    if (isSpectating && pendingAttackInitiated && attackAnimations) {
      
      // Play destroy sound when a drone is destroyed by attack
      if (pendingAttackInitiated.attackOutcome?.targetDestroyed && pendingAttackInitiated.attackOutcome?.targetZone === 'opponent_drone') {
        playSoundEffect('/sounds/destroy.mp3', 0.6);
      }
      // Register attack for animation (same as regular players)
      if (pendingAttackInitiated.attackId && pendingAttackInitiated.attackOutcome) {
        try {
          attackAnimations.registerAttack(
            pendingAttackInitiated.attackId,
            pendingAttackInitiated.attackOutcome,
            pendingAttackInitiated.isBotAttack || false
          );
        } catch (error) {
          console.error('❌ Error registering spectator attack animation:', error);
        }
      } else {
        console.warn('⚠️ Cannot register spectator attack: missing attackId or attackOutcome', {
          hasAttackId: !!pendingAttackInitiated.attackId,
          hasAttackOutcome: !!pendingAttackInitiated.attackOutcome
        });
      }
      
      // Clear the pending attack-initiated message after processing
      setPendingAttackInitiated(null);
    }
  }, [isSpectating, pendingAttackInitiated, attackAnimations, setPendingAttackInitiated]);
  
  // ✅ SPECTATOR FIX: Handle emote messages for spectators
  useEffect(() => {
    if (isSpectating && pendingEmote && pendingEmote.emoteKey) {
      
      // ✅ FIX: Normalize types for comparison (handle number/string mismatch)
      const normalizedFromPlayerId = String(pendingEmote.fromPlayerId);
      const normalizedActiveUserId = String(activeUserId);
      
      // Determine if emote is from the player being watched or their opponent
      const isFromWatchedPlayer = normalizedFromPlayerId === normalizedActiveUserId;
      
      
      if (isFromWatchedPlayer) {
        // Emote from the player being watched - show as "your" emote
        setCurrentEmoteBubble({
          emoteKey: pendingEmote.emoteKey,
          timestamp: pendingEmote.timestamp
        });
        setTimeout(() => {
          setCurrentEmoteBubble(null);
        }, 3000);
      } else {
        // Emote from opponent - show as opponent emote
        setOpponentEmoteBubble({
          emoteKey: pendingEmote.emoteKey,
          timestamp: pendingEmote.timestamp
        });
        setTimeout(() => {
          setOpponentEmoteBubble(null);
        }, 3000);
      }
      
      // Clear the pending emote after processing
      setPendingEmote(null);
    }
  }, [isSpectating, pendingEmote, activeUserId, spectatingPlayerId, setPendingEmote, t]);
  
  // Memoize 'you' and 'opponent' to ensure they update when activeGameState changes
  // This is critical for spectator mode to see real-time health updates
  const you = useMemo(() => {
    const st = activeGameState?.state;
    if (!st || activeUserId == null) return undefined;
    return st[activeUserId] ?? st[String(activeUserId)];
  }, [activeGameState, activeUserId]);

  const isPlayerGeneralAflame = useMemo(
    () => isPlayerGeneralAflameInMission68(activeGameState, activeUserId, you),
    [you, activeGameState, activeUserId, activeGameState?.actionLog, activeGameState?.stateVersion]
  );
  
  const opponentId = useMemo(() => {
    return activeGameState?.players?.find(id => id !== activeUserId);
  }, [activeGameState, activeUserId]);
  
  const opponent = useMemo(() => {
    return opponentId ? activeGameState?.state?.[opponentId] : null;
  }, [activeGameState, opponentId]);
  const introFirstPlayerId = useMemo(() => activeGameState?.players?.[0], [activeGameState]);
  const introPlayerIsFirst = useMemo(() => {
    if (introFirstPlayerId == null) return null;
    return String(introFirstPlayerId) === String(activeUserId);
  }, [introFirstPlayerId, activeUserId]);
  const playerFactionName = useMemo(() => {
    if (!you?.strategy) return null;
    return calculateFactionFromStrategy(you.strategy);
  }, [you]);
  const opponentFactionName = useMemo(() => {
    if (!opponent?.strategy) return null;
    return calculateFactionFromStrategy(opponent.strategy);
  }, [opponent]);
  const playerFactionIconPath = useMemo(() => getFactionIconPath(playerFactionName), [playerFactionName]);
  const opponentFactionIconPath = useMemo(() => getFactionIconPath(opponentFactionName), [opponentFactionName]);
  const exploreUnknownBossIntroTranslationKey = useMemo(
    () => getExploreUnknownBossIntroTranslationKey(gameState?.exploreUnknownMissionIndex),
    [gameState?.exploreUnknownMissionIndex]
  );
  const isExploreUnknownBossMulliganIntro = Boolean(exploreUnknownBossIntroTranslationKey);
  const exploreUnknownBossRetryAttemptNumber = useMemo(() => {
    const fromGameState = activeGameState?.exploreUnknownBossRetryAttempt;
    if (fromGameState != null) {
      const n = Number(fromGameState);
      if (!Number.isInteger(n) || n < 1) {
        throw new Error(
          `BattleUI: invalid exploreUnknownBossRetryAttempt on game state (${String(fromGameState)})`
        );
      }
      return n;
    }
    const fromNav = location.state?.exploreUnknownBossRetryAttempt;
    if (fromNav != null) {
      const n = Number(fromNav);
      if (!Number.isInteger(n) || n < 1) {
        throw new Error(
          `BattleUI: invalid exploreUnknownBossRetryAttempt on navigation state (${String(fromNav)})`
        );
      }
      return n;
    }
    const runId = activeGameState?.exploreUnknownRunId;
    const missionIndex = activeGameState?.exploreUnknownMissionIndex;
    if (runId == null || missionIndex == null) {
      return 1;
    }
    return readExploreUnknownBossAttemptNumber(Number(runId), Number(missionIndex));
  }, [
    activeGameState?.exploreUnknownBossRetryAttempt,
    activeGameState?.exploreUnknownMissionIndex,
    activeGameState?.exploreUnknownRunId,
    location.state?.exploreUnknownBossRetryAttempt,
  ]);
  const exploreUnknownBossRetryAttemptLabelKey = useMemo(
    () => getExploreUnknownBossRetryAttemptTranslationKey(exploreUnknownBossRetryAttemptNumber),
    [exploreUnknownBossRetryAttemptNumber]
  );
  const playMulliganIntroSound = useCallback((soundPath) => {
    const volume = getEffectiveSoundOutputVolume(0.75);
    if (volume === null) return;

    const audio = new Audio(soundPath);
    audio.volume = volume;

    let cleanedUp = false;
    const interactionEvents = ['click', 'touchstart', 'keydown', 'pointerdown'];

    const cleanup = () => {
      if (cleanedUp) return;
      cleanedUp = true;
      interactionEvents.forEach((eventName) => {
        document.removeEventListener(eventName, replayOnInteraction, true);
      });
    };

    const replayOnInteraction = () => {
      audio.play().then(cleanup).catch(() => {});
    };

    audio.play().catch((err) => {
      if (!err || err.name !== 'NotAllowedError') {
        return;
      }
      interactionEvents.forEach((eventName) => {
        document.addEventListener(eventName, replayOnInteraction, { once: true, capture: true });
      });
      window.setTimeout(cleanup, 3000);
    });
  }, []);

  const playFactionGreetSound = useCallback(() => {
    if (isExploreUnknownKendrexColonyMissionIndex(gameState?.exploreUnknownMissionIndex)) {
      return;
    }
    playMulliganIntroSound('/sounds/faction_greet.mp3');
  }, [gameState?.exploreUnknownMissionIndex, playMulliganIntroSound]);

  const playBossIntroSound = useCallback(() => {
    playMulliganIntroSound('/sounds/ahhh.mp3');
  }, [playMulliganIntroSound]);

  useEffect(() => {
    const hasIntroData = isExploreUnknownBossMulliganIntro
      ? Boolean(exploreUnknownBossIntroTranslationKey)
      : Boolean(playerFactionName) &&
        Boolean(opponentFactionName) &&
        Boolean(user?.username) &&
        Boolean(opponentUsername);

    if (!gameId || !needsMulligan || isSpectating || isDeveloperMode || !hasIntroData) {
      return;
    }

    const introAttemptSuffix =
      exploreUnknownBossRetryAttemptNumber >= 2
        ? `:attempt:${exploreUnknownBossRetryAttemptNumber}`
        : '';
    const storageKey = `battleMulliganIntroSeen:${gameId}${introAttemptSuffix}`;
    try {
      if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(storageKey)) {
        return;
      }
    } catch (_) {
      // Private mode / blocked storage: continue without persistence
    }

    let introAnimationCompleted = false;

    const persistIntroSeen = () => {
      try {
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem(storageKey, '1');
        }
      } catch (_) {}
    };

    const handleUnload = () => {
      if (introAnimationCompleted) return;
      persistIntroSeen();
    };
    window.addEventListener('unload', handleUnload);

    const resetIntroUi = () => {
      setShowMulliganIntroOverlay(false);
      setShowMulliganIntroPlayerCircle(false);
      setShowMulliganIntroOpponentCircle(false);
      setShowMulliganIntroVs(false);
      setShowMulliganIntroBossBar(false);
      setIsMulliganIntroExiting(false);
    };

    setIsMulliganIntroExiting(false);
    setShowMulliganIntroOverlay(true);

    let secondCircleTimer;
    let vsTimer;

    if (isExploreUnknownBossMulliganIntro) {
      setShowMulliganIntroBossBar(true);
      playBossIntroSound();
    } else {
      setShowMulliganIntroPlayerCircle(true);
      playFactionGreetSound();

      secondCircleTimer = setTimeout(() => {
        setShowMulliganIntroOpponentCircle(true);
        playFactionGreetSound();
      }, 650);
      vsTimer = setTimeout(() => {
        setShowMulliganIntroVs(true);
        playFactionGreetSound();
      }, 1300);
    }

    const startExitTimer = setTimeout(() => {
      setIsMulliganIntroExiting(true);
    }, 5000);
    const closeTimer = setTimeout(() => {
      introAnimationCompleted = true;
      persistIntroSeen();
      resetIntroUi();
    }, 5600);

    return () => {
      window.removeEventListener('unload', handleUnload);
      if (secondCircleTimer) clearTimeout(secondCircleTimer);
      if (vsTimer) clearTimeout(vsTimer);
      clearTimeout(startExitTimer);
      clearTimeout(closeTimer);
      if (!introAnimationCompleted) {
        resetIntroUi();
      }
    };
  }, [
    exploreUnknownBossIntroTranslationKey,
    exploreUnknownBossRetryAttemptNumber,
    gameId,
    isDeveloperMode,
    isExploreUnknownBossMulliganIntro,
    isSpectating,
    needsMulligan,
    opponentFactionName,
    opponentUsername,
    playBossIntroSound,
    playFactionGreetSound,
    playerFactionName,
    user?.username,
  ]);

  const isExploreUnknownMission33 = Number(activeGameState?.exploreUnknownMissionIndex) === 33;
  const opponentHealthDisplayText = useMemo(() => {
    if (!isExploreUnknownMission33) return null;
    const clawsRemaining = Number(activeGameState?.exploreUnknownMission33KrakenClawsRemaining);
    if (Number.isInteger(clawsRemaining) && clawsRemaining >= 0 && clawsRemaining <= 8) {
      return `${clawsRemaining}/8`;
    }
    const spawnedTotal = Number(activeGameState?.exploreUnknownMission33KrakenClawsSpawnedTotal || 0);
    if (Number.isInteger(spawnedTotal) && spawnedTotal >= 0 && spawnedTotal <= 8) {
      return `${Math.max(0, Math.min(8, 8 - spawnedTotal))}/8`;
    }
    return '8/8';
  }, [activeGameState, isExploreUnknownMission33]);

  const isDisorientationHandLocked = useMemo(() => {
    const fortTypes = Array.isArray(activeGameState?.warEffortFortificationTypes)
      ? activeGameState.warEffortFortificationTypes
      : (activeGameState?.warEffortFortificationType ? [activeGameState.warEffortFortificationType] : []);
    if (!fortTypes.includes('disorientation')) return false;

    const defenderId = activeGameState?.warEffortDefenderPlayerId;
    if (!defenderId) return false;
    const players = activeGameState?.players;
    if (!Array.isArray(players) || players.length < 2) return false;

    const attackerId = players.find((id) => String(id) !== String(defenderId));
    if (!attackerId || String(attackerId) !== String(activeUserId)) return false;

    const turnsTaken = activeGameState?.state?.[activeUserId]?.turnsTaken;
    if (typeof turnsTaken !== 'number') return false;
    return turnsTaken <= 2;
  }, [activeGameState, activeUserId]);

  // Mobile-friendly card click handler (extracted for reuse)
  const handleCardClick = useCallback((order, e) => {
    // Allow during counter strike deploy targeting (e.g. selecting Bulwark target from hand context)
    const isCounterStrikeDeployTargeting = deployTargetMode && selectedDeployOrderId && !yourTurn;
    if (!yourTurn && !isCounterStrikeDeployTargeting) return;
    if (isDisorientationHandLocked) {
      setError(t('battleUI.disorientationLock') || 'Disorientation active: your hand is deactivated for your first 2 turns');
      return;
    }

    setOpponentInspectSelection(null);

    // Handle deploy targeting first
    if (deployTargetMode && selectedDeployOrderId) {
      const isEnemyUnit = order.zone && order.zone.startsWith('opponent_');
      const targetUnitId = isEnemyUnit ? 'opponent' : 'friendly';
      const targetZone = isEnemyUnit ? order.zone : order.zone;
      handleTargetDeploy(targetZone, order.idx, targetUnitId);
      return;
    }
    
    if (order.type === 'Weapon') {
      // For weapons, enter targeting mode
      if (selectedWeaponInstanceId === order.instanceId && weaponTargetMode) {
        // Deselect if already in targeting mode
        setWeaponTargetMode(false);
        setSelectedWeaponId(null);
        setSelectedWeaponInstanceId(null);
        setSelectedOrderId(null);
        setSelectedOrderInstanceId(null);
      } else {
        const orderCost = order.cost !== undefined ? order.cost : (order.hasInstanceModifications && order.instanceCost !== undefined ? order.instanceCost : order.originalCost || 0);
        if (gameState?.state?.[userId] && gameState.state[userId].intelligence >= orderCost) {
          setWeaponTargetMode(true);
          setSelectedWeaponId(order.id);
          setSelectedWeaponInstanceId(order.instanceId);
          setSelectedOrderId(order.id);
          setSelectedOrderInstanceId(order.instanceId);
          // Show message on mobile
          if (isMobile) {
            setError(t('battleUI.selectTargetWeapon') || 'Select a unit to attach this weapon to');
            setTimeout(() => setError(''), 3000);
          }
        } else {
          setError(`${t('battleUI.notEnoughIntelligence')} ${order.name}! ${t('battleUI.need')} ${orderCost}, ${t('battleUI.have')} ${gameState?.state?.[userId]?.intelligence || 0}`);
        }
      }
    } else if (order.type === 'Strategem') {
      // For strategems, check if already in targeting mode (backend has set needsStrategemTarget)
      if (selectedStrategemId === order.id && strategemTargetMode) {
        // Deselect if already in targeting mode
        setStrategemTargetMode(false);
        setSelectedStrategemId(null);
        setStrategemTargetInfo(null);
        setSelectedOrderId(null);
        setSelectedOrderInstanceId(null);
      } else {
        // ✅ FIX: Always call playStrategem() first - the backend will set needsStrategemTarget
        // if targeting is needed, and the game state sync will enter targeting mode
        const orderCost = order.hasInstanceModifications && order.instanceCost !== undefined ? order.instanceCost : order.cost;
        if (gameState?.state?.[userId] && gameState.state[userId].intelligence < orderCost) {
          setError(`${t('battleUI.notEnoughIntelligence')} ${order.name}! ${t('battleUI.need')} ${orderCost}, ${t('battleUI.have')} ${gameState.state[userId].intelligence}`);
        } else {
          setSelectedOrderId(order.id);
          setSelectedOrderInstanceId(order.instanceId);
          playStrategem(order.id, order.instanceId);
        }
      }
    } else if (order.type === 'Drone') {
      // Drones from hand use click-to-select and slot-click deploy.
      const isSelectedDroneFromHand = selectedOrderInstanceId === order.instanceId && selectedOrderId === order.id;
      if (isSelectedDroneFromHand) {
        setSelectedOrderId(null);
        setSelectedOrderInstanceId(null);
        return;
      }
      setSelectedOrderId(order.id);
      setSelectedOrderInstanceId(order.instanceId);
      return;
    } else {
      // ✅ FIX: Cancel weapon targeting when selecting a unit from hand
      if (weaponTargetMode && selectedWeaponId) {
        setWeaponTargetMode(false);
        setSelectedWeaponId(null);
        setSelectedWeaponInstanceId(null);
      }
      
      // ✅ FIX: Cancel strategem targeting when selecting a unit from hand
      if (strategemTargetMode && selectedStrategemId) {
        setStrategemTargetMode(false);
        setSelectedStrategemId(null);
        setStrategemTargetInfo(null);
      }
      
      // ✅ FIX: Cancel drone targeting when selecting a unit from hand
      if (droneTargetMode && selectedDroneId) {
        setDroneTargetMode(false);
        setSelectedDroneId(null);
        setSelectedDroneInstanceId(null);
      }
      
      // For other orders, toggle selection
      const newInstanceId = order.instanceId === selectedOrderInstanceId ? null : order.instanceId;
      const newOrderId = order.id === selectedOrderId ? null : order.id;
      if (order) {
        const orderCost = order.hasInstanceModifications && order.instanceCost !== undefined ? order.instanceCost : order.cost;
        if (newInstanceId && gameState?.state?.[userId] && gameState.state[userId].intelligence < orderCost) {
          setError(`${t('battleUI.notEnoughIntelligence')} ${order.name}! ${t('battleUI.need')} ${orderCost}, ${t('battleUI.have')} ${gameState.state[userId].intelligence}`);
        } else {
          setSelectedOrderInstanceId(newInstanceId);
          setSelectedOrderId(newOrderId);
        }
      }
    }
  }, [yourTurn, deployTargetMode, selectedDeployOrderId, selectedWeaponInstanceId, weaponTargetMode, selectedWeaponId, selectedStrategemId, strategemTargetMode, strategemTargetInfo, droneTargetMode, selectedDroneId, selectedDroneInstanceId, selectedOrderInstanceId, selectedOrderId, gameState, userId, t, isMobile, targetingData, handleTargetDeploy, setWeaponTargetMode, setSelectedWeaponId, setSelectedWeaponInstanceId, setStrategemTargetMode, setSelectedStrategemId, setStrategemTargetInfo, setDroneTargetMode, setSelectedDroneId, setSelectedDroneInstanceId, setSelectedOrderInstanceId, setSelectedOrderId, playStrategem, getStrategemTargetingRules, strategemNeedsTargeting, setError, isDisorientationHandLocked, setOpponentInspectSelection]);

  // Mobile touch handler (prevents double-firing)
  const handleCardTouch = useCallback((order, e) => {
    // Use the same logic as click handler
    handleCardClick(order, e);
  }, [handleCardClick]);

  // Resolve drop target for strategem drag: elementUnderPoint -> general | zone | unit | battlefield
  // Use elementsFromPoint and check every element in the stack (top to bottom); the topmost is often
  // a container (#root, etc.) with no data attrs, so we must try each until we find a battlefield element.
  const resolveStrategemDropTarget = useCallback((clientX, clientY) => {
    if (typeof document === 'undefined') return null;
    const elements = typeof document.elementsFromPoint === 'function'
      ? document.elementsFromPoint(clientX, clientY)
      : [document.elementFromPoint(clientX, clientY)].filter(Boolean);
    if (!elements.length) {
      return { type: 'battlefield' };
    }
    let fallbackZone = null; // Prefer unit over zone: only return zone if no element resolved to unit
    for (let i = 0; i < elements.length; i++) {
      const top = elements[i];
      if (top.closest?.('.deploy-drag-overlay')) continue;
      let el = top;
      let steps = 0;
      while (el && el !== document.body) {
        steps++;
        const zoneAttr = el.getAttribute('data-zone');
        if (zoneAttr === 'player_general') {
          return { type: 'general', generalPlayer: 'friendly' };
        }
        if (zoneAttr === 'opponent_general') {
          return { type: 'general', generalPlayer: 'opponent' };
        }
        const slotZone = el.getAttribute('data-slot-zone');
        const slotIndex = el.getAttribute('data-slot-index');
        const unitKey = el.getAttribute('data-unit-key');
        if (slotZone != null && slotIndex != null && unitKey) {
          const idx = parseInt(slotIndex, 10);
          if (!Number.isNaN(idx)) {
            const opponentId = Object.keys(gameState?.state || {}).find(
              (id) => String(id) !== String(userId)
            );
            const isYour = slotZone === 'your_front' || slotZone === 'your_flank';
            const playerId = isYour ? userId : opponentId;
            const zoneKey = slotZone === 'your_front' || slotZone === 'opponent_front' ? 'front' : 'flank';
            const zoneUnits = gameState?.state?.[playerId]?.playedOrders?.[zoneKey] || [];
            const unit = zoneUnits[idx];
            if (unit && unit.instanceId) {
              return { type: 'unit', zone: slotZone, index: idx, unitId: unit.id, instanceId: unit.instanceId };
            }
          }
        }
        const zoneKeyAttr = el.getAttribute('data-zone-key');
        if (zoneKeyAttr) {
          if (!fallbackZone) fallbackZone = { type: 'zone', zone: zoneKeyAttr };
        }
        el = el.parentElement;
      }
    }
    if (fallbackZone) {
      return fallbackZone;
    }
    return { type: 'battlefield' };
  }, [gameState, userId]);

  // Desktop: custom mouse-based drag (no HTML5 DnD) so drag-to-deploy works reliably
  const startCarouselMouseDrag = useCallback((order, clientX, clientY) => {
    document.body.classList.add('deploy-drag-tracking');
    if (window.getSelection) window.getSelection().removeAllRanges();
    mouseDragPendingRef.current = { order, startX: clientX, startY: clientY };
    clickHandledByMouseupRef.current = false;
    dragStartedRef.current = false;
    let isClosed = false;
    let cleanupTimeout = null;
    let maxUpwardPx = 0; // peak (startY - clientY); deploy only if >= CAROUSEL_DEPLOY_MIN_UPWARD_PX
    const closeSession = () => {
      if (isClosed) return;
      isClosed = true;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('contextmenu', onContextMenu);
      window.removeEventListener('blur', onWindowBlur);
      if (cleanupTimeout) clearTimeout(cleanupTimeout);
      document.body.classList.remove('deploy-drag-tracking');
      if (window.getSelection) window.getSelection().removeAllRanges();
    };
    const onMove = (e) => {
      const pending = mouseDragPendingRef.current;
      if (!pending) return;
      const upwardPx = pending.startY - e.clientY;
      if (upwardPx > maxUpwardPx) maxUpwardPx = upwardPx;
      // Unit hand→board: arm drag UI only after meaningful upward motion (not horizontal-only jitter).
      if (!dragStartedRef.current && maxUpwardPx >= CAROUSEL_DEPLOY_MIN_UPWARD_PX) {
        dragStartedRef.current = true;
        setDragDeployState({
          order: pending.order,
          originX: pending.startX,
          originY: pending.startY,
          currentX: e.clientX,
          currentY: e.clientY,
        });
      }
      // Cursor end of the drag line is tracked imperatively by DeployDragLine (no per-move setState).
    };
    const onContextMenu = (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
    };
    const onWindowBlur = () => {
      closeSession();
      mouseDragPendingRef.current = null;
      setDragDeployState(null);
      setDraggedOrderId(null);
      setDraggedOrderInstanceId(null);
      dragStartedRef.current = false;
    };
    const onUp = (e) => {
      closeSession();
      const pending = mouseDragPendingRef.current;
      mouseDragPendingRef.current = null;
      if (e.button === 2) {
        e.preventDefault();
        e.stopPropagation();
        setDragDeployState(null);
        setDraggedOrderId(null);
        setDraggedOrderInstanceId(null);
        dragStartedRef.current = false;
        const preventNextContextMenu = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          document.removeEventListener('contextmenu', preventNextContextMenu, true);
        };
        document.addEventListener('contextmenu', preventNextContextMenu, true);
        return;
      }
      if (dragStartedRef.current && pending) {
        const order = pending.order;
        const isDeployable = order?.type !== 'Drone' && order?.type !== 'Strategem' && order?.type !== 'Weapon';
        const upwardAtRelease = pending.startY - e.clientY;
        if (upwardAtRelease > maxUpwardPx) maxUpwardPx = upwardAtRelease;
        const enoughUpwardToDeploy = maxUpwardPx >= CAROUSEL_DEPLOY_MIN_UPWARD_PX;
        if (order && isDeployable && enoughUpwardToDeploy) {
          const zone = e.clientX < window.innerWidth / 2 ? 'front' : 'flank';
          const zoneCount = you?.playedOrders?.[zone]?.filter(u => u !== null).length ?? 0;
          if (zoneCount >= 5) {
            // Same as click: select unit from hand then synthetic click on slot → replace mode (Replace/Cancel)
            setSelectedOrderInstanceId(order.instanceId);
            setSelectedOrderId(order.id);
            setPendingDeployFullZoneDrop({ clientX: e.clientX, clientY: e.clientY });
          } else {
            playOrder(order.id, zone, null, order.instanceId);
            setSelectedOrderId(null);
            setSelectedOrderInstanceId(null);
          }
        }
        setDragDeployState(null);
        setDraggedOrderId(null);
        setDraggedOrderInstanceId(null);
        dragStartedRef.current = false;
      } else if (pending) {
        clickHandledByMouseupRef.current = true;
        handleCardClick(pending.order, e);
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('contextmenu', onContextMenu);
    window.addEventListener('blur', onWindowBlur);
    cleanupTimeout = setTimeout(onWindowBlur, 8000);
  }, [you?.playedOrders, playOrder, setError, t, setDragDeployState, setSelectedOrderId, setSelectedOrderInstanceId, setDraggedOrderId, setDraggedOrderInstanceId, handleCardClick]);

  // Desktop: strategem drag-and-drop — drop on exact target (general, zone, unit, or anywhere for battlefield)
  const startStrategemMouseDrag = useCallback((order, clientX, clientY) => {
    document.body.classList.add('deploy-drag-tracking');
    if (window.getSelection) window.getSelection().removeAllRanges();
    strategemMouseDragPendingRef.current = { order, startX: clientX, startY: clientY };
    clickHandledByMouseupRef.current = false;
    strategemDragStartedRef.current = false;
    let isClosed = false;
    let cleanupTimeout = null;
    const DRAG_THRESHOLD_PX = 5;
    const closeSession = () => {
      if (isClosed) return;
      isClosed = true;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('contextmenu', onContextMenu);
      window.removeEventListener('blur', onWindowBlur);
      if (cleanupTimeout) clearTimeout(cleanupTimeout);
      document.body.classList.remove('deploy-drag-tracking');
      if (window.getSelection) window.getSelection().removeAllRanges();
    };
    const onMove = (e) => {
      const pending = strategemMouseDragPendingRef.current;
      if (!pending) return;
      const dx = e.clientX - pending.startX;
      const dy = e.clientY - pending.startY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (!strategemDragStartedRef.current && dist > DRAG_THRESHOLD_PX) {
        strategemDragStartedRef.current = true;
        setStrategemDragState({
          order: pending.order,
          originX: pending.startX,
          originY: pending.startY,
          currentX: e.clientX,
          currentY: e.clientY,
        });
      }
      // Cursor end of the drag line is tracked imperatively by DeployDragLine (no per-move setState).
    };
    const onContextMenu = (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
    };
    const onWindowBlur = () => {
      closeSession();
      strategemMouseDragPendingRef.current = null;
      setStrategemDragState(null);
      strategemDragStartedRef.current = false;
    };
    const onUp = (e) => {
      closeSession();
      const pending = strategemMouseDragPendingRef.current;
      strategemMouseDragPendingRef.current = null;
      if (e.button === 2) {
        e.preventDefault();
        e.stopPropagation();
        setStrategemDragState(null);
        strategemDragStartedRef.current = false;
        const preventNextContextMenu = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          document.removeEventListener('contextmenu', preventNextContextMenu, true);
        };
        document.addEventListener('contextmenu', preventNextContextMenu, true);
        return;
      }
      if (strategemDragStartedRef.current && pending) {
        const order = pending.order;
        const rules = getStrategemTargetingRules(order.id, targetingData);
        const needsTarget = strategemNeedsTargeting(order.id, targetingData);
        // Hide overlay first so next-tick elementFromPoint hits the battlefield, not the overlay
        setStrategemDragState(null);
        strategemDragStartedRef.current = false;
        if (!needsTarget || (rules && rules.targeting === 'none')) {
          playStrategem(order.id, order.instanceId);
          return;
        }
        if (!rules) {
          setError(t('battleUI.selectTargetStrategem') || 'Select a target');
          return;
        }
        // Reuse the same code path as click: store drop coordinates and dispatch a synthetic click
        // after backend sets needsStrategemTarget, so the unit/zone/general onClick runs and calls
        // handleTargetingClick / handleZoneClick / general onClick -> targetStrategem.
        pendingStrategemDropTargetRef.current = {
          type: 'click',
          clientX: e.clientX,
          clientY: e.clientY,
          strategemInstanceId: order.instanceId
        };
        playStrategem(order.id, order.instanceId);
      } else if (pending) {
        clickHandledByMouseupRef.current = true;
        handleCardClick(pending.order, e);
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('contextmenu', onContextMenu);
    window.addEventListener('blur', onWindowBlur);
    cleanupTimeout = setTimeout(onWindowBlur, 8000);
  }, [getStrategemTargetingRules, strategemNeedsTargeting, playStrategem, setError, t, handleCardClick, targetingData]);

  // Desktop: weapon drag-and-drop — drop on a unit to attach; reuse click path via synthetic click
  const startWeaponMouseDrag = useCallback((order, clientX, clientY) => {
    document.body.classList.add('deploy-drag-tracking');
    if (window.getSelection) window.getSelection().removeAllRanges();
    weaponMouseDragPendingRef.current = { order, startX: clientX, startY: clientY };
    clickHandledByMouseupRef.current = false;
    weaponDragStartedRef.current = false;
    let isClosed = false;
    let cleanupTimeout = null;
    const DRAG_THRESHOLD_PX = 5;
    const closeSession = () => {
      if (isClosed) return;
      isClosed = true;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('contextmenu', onContextMenu);
      window.removeEventListener('blur', onWindowBlur);
      if (cleanupTimeout) clearTimeout(cleanupTimeout);
      document.body.classList.remove('deploy-drag-tracking');
      if (window.getSelection) window.getSelection().removeAllRanges();
    };
    const onMove = (e) => {
      const pending = weaponMouseDragPendingRef.current;
      if (!pending) return;
      const dx = e.clientX - pending.startX;
      const dy = e.clientY - pending.startY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (!weaponDragStartedRef.current && dist > DRAG_THRESHOLD_PX) {
        weaponDragStartedRef.current = true;
        setWeaponDragState({
          order: pending.order,
          originX: pending.startX,
          originY: pending.startY,
          currentX: e.clientX,
          currentY: e.clientY,
        });
      }
      // Cursor end of the drag line is tracked imperatively by DeployDragLine (no per-move setState).
    };
    const onContextMenu = (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
    };
    const onWindowBlur = () => {
      closeSession();
      weaponMouseDragPendingRef.current = null;
      setWeaponDragState(null);
      weaponDragStartedRef.current = false;
    };
    const onUp = (e) => {
      closeSession();
      const pending = weaponMouseDragPendingRef.current;
      weaponMouseDragPendingRef.current = null;
      if (e.button === 2) {
        e.preventDefault();
        e.stopPropagation();
        setWeaponDragState(null);
        weaponDragStartedRef.current = false;
        const preventNextContextMenu = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          document.removeEventListener('contextmenu', preventNextContextMenu, true);
        };
        document.addEventListener('contextmenu', preventNextContextMenu, true);
        return;
      }
      if (weaponDragStartedRef.current && pending && pending.order?.type === 'Weapon') {
        setWeaponDragState(null);
        weaponDragStartedRef.current = false;
        const order = pending.order;
        const orderCost = order.cost !== undefined ? order.cost : (order.hasInstanceModifications && order.instanceCost !== undefined ? order.instanceCost : order.originalCost || 0);
        if (gameState?.state?.[userId] && gameState.state[userId].intelligence >= orderCost) {
          setWeaponTargetMode(true);
          setSelectedWeaponId(order.id);
          setSelectedWeaponInstanceId(order.instanceId);

          const resolved = resolveStrategemDropTarget(e.clientX, e.clientY);
          if (!resolved || resolved.type !== 'unit') {
            setWeaponTargetMode(false);
            setSelectedWeaponId(null);
            setSelectedWeaponInstanceId(null);
            setError(t('battleUI.selectTargetWeapon') || 'Select a unit to attach this weapon to');
            setTimeout(() => setError(''), 3000);
          } else {
            const slotZone = resolved.zone;
            const idx = resolved.index;
            const zoneKey = slotZone === 'your_front' || slotZone === 'opponent_front' ? 'front' : 'flank';
            const isYour = slotZone === 'your_front' || slotZone === 'your_flank';
            const opponentKey = Object.keys(gameState?.state || {}).find((pid) => String(pid) !== String(userId));
            const playerId = isYour ? userId : opponentKey;
            if (playerId == null) {
              setWeaponTargetMode(false);
              setSelectedWeaponId(null);
              setSelectedWeaponInstanceId(null);
              setError(t('battleUI.selectTargetWeapon') || 'Select a unit to attach this weapon to');
              setTimeout(() => setError(''), 3000);
            } else {
              const unit = gameState?.state?.[playerId]?.playedOrders?.[zoneKey]?.[idx];
              if (!unit || !unit.instanceId) {
                setWeaponTargetMode(false);
                setSelectedWeaponId(null);
                setSelectedWeaponInstanceId(null);
                setError(t('battleUI.selectTargetWeapon') || 'Select a unit to attach this weapon to');
                setTimeout(() => setError(''), 3000);
              } else {
                let attachZone;
                if (slotZone === 'your_front') attachZone = 'front';
                else if (slotZone === 'your_flank') attachZone = 'flank';
                else if (slotZone === 'opponent_front') attachZone = 'opponent_front';
                else if (slotZone === 'opponent_flank') attachZone = 'opponent_flank';
                else {
                  setWeaponTargetMode(false);
                  setSelectedWeaponId(null);
                  setSelectedWeaponInstanceId(null);
                  setError(t('battleUI.selectTargetWeapon') || 'Select a unit to attach this weapon to');
                  setTimeout(() => setError(''), 3000);
                  return;
                }
                const stopGhostClick = (clickEv) => {
                  clickEv.preventDefault();
                  clickEv.stopPropagation();
                  clickEv.stopImmediatePropagation();
                };
                document.addEventListener('click', stopGhostClick, { capture: true, once: true });
                const targetUnit = { ...unit, zone: attachZone, index: idx };
                attachWeapon(
                  targetUnit,
                  ws || currentSocketRef.current,
                  setError,
                  setWeaponTargetMode,
                  order.id,
                  setSelectedWeaponId,
                  order.instanceId,
                  gameId,
                  userId,
                  gameState,
                  setWeaponDeployTargetMode,
                  setWeaponDeployTargetInfo
                );
              }
            }
          }
        } else {
          setError(`${t('battleUI.notEnoughIntelligence')} ${order.name}! ${t('battleUI.need')} ${orderCost}, ${t('battleUI.have')} ${gameState?.state?.[userId]?.intelligence || 0}`);
        }
      } else if (pending) {
        clickHandledByMouseupRef.current = true;
        handleCardClick(pending.order, e);
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('contextmenu', onContextMenu);
    window.addEventListener('blur', onWindowBlur);
    cleanupTimeout = setTimeout(onWindowBlur, 8000);
  }, [
    gameState,
    userId,
    setError,
    t,
    handleCardClick,
    resolveStrategemDropTarget,
    ws,
    gameId,
    setWeaponTargetMode,
    setSelectedWeaponId,
    setSelectedWeaponInstanceId,
    setWeaponDeployTargetMode,
    setWeaponDeployTargetInfo,
  ]);

  // Desktop: drone drag-and-drop — drop on player drone zone (empty slot) to deploy; same path as Deploy button (playDrone).
  const startDroneMouseDrag = useCallback((order, clientX, clientY) => {
    document.body.classList.add('deploy-drag-tracking');
    if (window.getSelection) window.getSelection().removeAllRanges();
    droneMouseDragPendingRef.current = { order, startX: clientX, startY: clientY };
    clickHandledByMouseupRef.current = false;
    droneDragStartedRef.current = false;
    let isClosed = false;
    let cleanupTimeout = null;
    const DRAG_THRESHOLD_PX = 5;
    const closeSession = () => {
      if (isClosed) return;
      isClosed = true;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('contextmenu', onContextMenu);
      window.removeEventListener('blur', onWindowBlur);
      if (cleanupTimeout) clearTimeout(cleanupTimeout);
      document.body.classList.remove('deploy-drag-tracking');
      if (window.getSelection) window.getSelection().removeAllRanges();
    };
    const onMove = (e) => {
      const pending = droneMouseDragPendingRef.current;
      if (!pending) return;
      const dx = e.clientX - pending.startX;
      const dy = e.clientY - pending.startY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (!droneDragStartedRef.current && dist > DRAG_THRESHOLD_PX) {
        droneDragStartedRef.current = true;
        setDroneDragState({
          order: pending.order,
          originX: pending.startX,
          originY: pending.startY,
          currentX: e.clientX,
          currentY: e.clientY,
        });
      }
      // Cursor end of the drag line is tracked imperatively by DeployDragLine (no per-move setState).
    };
    const onContextMenu = (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
    };
    const onWindowBlur = () => {
      closeSession();
      droneMouseDragPendingRef.current = null;
      setDroneDragState(null);
      droneDragStartedRef.current = false;
    };
    const onUp = (e) => {
      closeSession();
      const pending = droneMouseDragPendingRef.current;
      droneMouseDragPendingRef.current = null;
      if (e.button === 2) {
        e.preventDefault();
        e.stopPropagation();
        setDroneDragState(null);
        droneDragStartedRef.current = false;
        const preventNextContextMenu = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          document.removeEventListener('contextmenu', preventNextContextMenu, true);
        };
        document.addEventListener('contextmenu', preventNextContextMenu, true);
        return;
      }
      if (droneDragStartedRef.current && pending && pending.order?.type === 'Drone') {
        setDroneDragState(null);
        droneDragStartedRef.current = false;
        const order = pending.order;
        const orderCost = order.cost !== undefined ? order.cost : (order.hasInstanceModifications && order.instanceCost !== undefined ? order.instanceCost : order.originalCost || 0);
        if (gameState?.state?.[userId] && gameState.state[userId].intelligence >= orderCost) {
          setPendingDroneDropCoords({
            clientX: e.clientX,
            clientY: e.clientY,
            orderId: order.id,
            orderInstanceId: order.instanceId,
          });
        } else {
          setError(`${t('battleUI.notEnoughIntelligence')} ${order.name}! ${t('battleUI.need')} ${orderCost}, ${t('battleUI.have')} ${gameState?.state?.[userId]?.intelligence || 0}`);
        }
      } else if (pending) {
        clickHandledByMouseupRef.current = true;
        handleCardClick(pending.order, e);
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('contextmenu', onContextMenu);
    window.addEventListener('blur', onWindowBlur);
    cleanupTimeout = setTimeout(onWindowBlur, 8000);
  }, [gameState, userId, setError, t, handleCardClick]);

  // Desktop: attack drag-and-drop — drag friendly unit (front/flank), drop on opponent unit or general; reuse click path via synthetic click
  const startAttackMouseDrag = useCallback((zone, idx, clientX, clientY) => {
    document.body.classList.add('deploy-drag-tracking');
    if (window.getSelection) window.getSelection().removeAllRanges();
    const attacker = { zone, idx };
    attackMouseDragPendingRef.current = { attacker, startX: clientX, startY: clientY };
    attackDragStartedRef.current = false;
    let isClosed = false;
    let cleanupTimeout = null;
    const DRAG_THRESHOLD_PX = 5;
    const closeSession = () => {
      if (isClosed) return;
      isClosed = true;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('contextmenu', onContextMenu);
      window.removeEventListener('blur', onWindowBlur);
      if (cleanupTimeout) clearTimeout(cleanupTimeout);
      document.body.classList.remove('deploy-drag-tracking');
      if (window.getSelection) window.getSelection().removeAllRanges();
    };
    const onMove = (e) => {
      const pending = attackMouseDragPendingRef.current;
      if (!pending) return;
      const dx = e.clientX - pending.startX;
      const dy = e.clientY - pending.startY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (!attackDragStartedRef.current && dist > DRAG_THRESHOLD_PX) {
        attackDragStartedRef.current = true;
        setAttackDragState({
          attacker: pending.attacker,
          originX: pending.startX,
          originY: pending.startY,
          currentX: e.clientX,
          currentY: e.clientY,
        });
      }
      // Cursor end of the drag line is tracked imperatively by DeployDragLine (no per-move setState).
    };
    const onContextMenu = (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
    };
    const onWindowBlur = () => {
      closeSession();
      attackMouseDragPendingRef.current = null;
      setAttackDragState(null);
      attackDragStartedRef.current = false;
    };
    const onUp = (e) => {
      closeSession();
      const pending = attackMouseDragPendingRef.current;
      attackMouseDragPendingRef.current = null;
      if (e.button === 2) {
        e.preventDefault();
        e.stopPropagation();
        setAttackDragState(null);
        attackDragStartedRef.current = false;
        const preventNextContextMenu = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          document.removeEventListener('contextmenu', preventNextContextMenu, true);
        };
        document.addEventListener('contextmenu', preventNextContextMenu, true);
        return;
      }
      if (attackDragStartedRef.current && pending?.attacker) {
        setAttackDragState(null);
        attackDragStartedRef.current = false;
        setSelectedAttacker({ zone: pending.attacker.zone, idx: pending.attacker.idx });
        setAttackMode(true);
        setPendingAttackDropCoords({
          clientX: e.clientX,
          clientY: e.clientY,
        });
      }
      // If we didn't drag, no pendingAttackDropCoords; the same mouseup will trigger click on the unit slot (select attacker)
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('contextmenu', onContextMenu);
    window.addEventListener('blur', onWindowBlur);
    cleanupTimeout = setTimeout(onWindowBlur, 8000);
  }, []);

  // Desktop: drone-with-charges targeting drag — drag deployed drone (that needs targeting), drop on yellow-bordered target to execute effect
  const startDroneTargetMouseDrag = useCallback((drone, clientX, clientY) => {
    document.body.classList.add('deploy-drag-tracking');
    if (window.getSelection) window.getSelection().removeAllRanges();
    droneTargetMouseDragPendingRef.current = { drone, startX: clientX, startY: clientY };
    droneTargetDragStartedRef.current = false;
    let isClosed = false;
    let cleanupTimeout = null;
    const DRAG_THRESHOLD_PX = 5;
    const closeSession = () => {
      if (isClosed) return;
      isClosed = true;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('contextmenu', onContextMenu);
      window.removeEventListener('blur', onWindowBlur);
      if (cleanupTimeout) clearTimeout(cleanupTimeout);
      document.body.classList.remove('deploy-drag-tracking');
      if (window.getSelection) window.getSelection().removeAllRanges();
    };
    const onMove = (e) => {
      const pending = droneTargetMouseDragPendingRef.current;
      if (!pending) return;
      const dx = e.clientX - pending.startX;
      const dy = e.clientY - pending.startY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (!droneTargetDragStartedRef.current && dist > DRAG_THRESHOLD_PX) {
        droneTargetDragStartedRef.current = true;
        setDroneTargetDragState({
          drone: pending.drone,
          originX: pending.startX,
          originY: pending.startY,
          currentX: e.clientX,
          currentY: e.clientY,
        });
      }
      // Cursor end of the drag line is tracked imperatively by DeployDragLine (no per-move setState).
    };
    const onContextMenu = (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
    };
    const onWindowBlur = () => {
      closeSession();
      droneTargetMouseDragPendingRef.current = null;
      setDroneTargetDragState(null);
      droneTargetDragStartedRef.current = false;
    };
    const onUp = (e) => {
      closeSession();
      const pending = droneTargetMouseDragPendingRef.current;
      droneTargetMouseDragPendingRef.current = null;
      if (e.button === 2) {
        e.preventDefault();
        e.stopPropagation();
        setDroneTargetDragState(null);
        droneTargetDragStartedRef.current = false;
        const preventNextContextMenu = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          document.removeEventListener('contextmenu', preventNextContextMenu, true);
        };
        document.addEventListener('contextmenu', preventNextContextMenu, true);
        return;
      }
      if (droneTargetDragStartedRef.current && pending?.drone) {
        setDroneTargetDragState(null);
        droneTargetDragStartedRef.current = false;
        setSelectedDroneId(pending.drone.id);
        setSelectedDroneInstanceId(pending.drone.instanceId);
        setDroneTargetMode(true);
        setPendingDroneTargetDropCoords({
          clientX: e.clientX,
          clientY: e.clientY,
        });
      }
      // If we didn't drag, no pendingDroneTargetDropCoords; the same mouseup will trigger click on the drone (activateDrone → may enter target mode)
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('contextmenu', onContextMenu);
    window.addEventListener('blur', onWindowBlur);
    cleanupTimeout = setTimeout(onWindowBlur, 8000);
  }, []);

  // Effective targeting state for highlights: show yellow targeting boxes during drag as well as click
  const isDeployDragActive = !isContainerMobile && dragDeployState?.order && dragDeployState.order.type !== 'Strategem' && dragDeployState.order.type !== 'Drone' && dragDeployState.order.type !== 'Weapon';
  const deployPointerSourceOrder =
    isDeployDragActive && dragDeployState?.order
      ? dragDeployState.order
      : selectedOrderInstanceId && you?.hand
        ? you.hand.find((o) => o.instanceId === selectedOrderInstanceId)
        : null;
  const deployPointerTypeLo = (deployPointerSourceOrder?.type || '').toLowerCase();
  const isDeployHandPointerActive = Boolean(
    yourTurn &&
      deployPointerSourceOrder &&
      deployPointerTypeLo !== 'strategem' &&
      deployPointerTypeLo !== 'stratagem' &&
      deployPointerTypeLo !== 'drone' &&
      deployPointerTypeLo !== 'weapon'
  );
  const isDroneDragActive = !isContainerMobile && droneDragState?.order?.type === 'Drone';
  const isDroneTargetDragActive = !isContainerMobile && droneTargetDragState?.drone;
  const isWeaponDragActive = !isContainerMobile && weaponDragState?.order?.type === 'Weapon';
  const isAttackDragActive = !isContainerMobile && attackDragState?.attacker;
  const effectiveDroneTargetMode = (droneTargetMode && selectedDroneId) || isDroneTargetDragActive;
  const effectiveSelectedDroneId = (droneTargetMode && selectedDroneId) ? selectedDroneId : (isDroneTargetDragActive && droneTargetDragState?.drone ? droneTargetDragState.drone.id : null);
  const effectiveWeaponTargetMode = (weaponTargetMode && selectedWeaponId) || isWeaponDragActive;
  const effectiveAttackMode = (attackMode && selectedAttacker) || isAttackDragActive;
  const effectiveSelectedAttacker = (attackMode && selectedAttacker) ? selectedAttacker : (isAttackDragActive && attackDragState?.attacker ? { zone: attackDragState.attacker.zone, idx: attackDragState.attacker.idx } : null);
  const attackerOrderForBattlePointer =
    effectiveAttackMode && effectiveSelectedAttacker && gameState?.state?.[userId]?.playedOrders
      ? gameState.state[userId].playedOrders[effectiveSelectedAttacker.zone]?.[effectiveSelectedAttacker.idx]
      : null;
  const attackPointerZoneUi =
    effectiveSelectedAttacker && (effectiveSelectedAttacker.zone === 'front' || effectiveSelectedAttacker.zone === 'flank')
      ? effectiveSelectedAttacker.zone === 'front'
        ? 'your_front'
        : 'your_flank'
      : null;
  const isAttackPointerActive = Boolean(
    yourTurn &&
      effectiveAttackMode &&
      effectiveSelectedAttacker &&
      attackerOrderForBattlePointer &&
      attackPointerZoneUi &&
      !unitCannotInitiateAttack(attackerOrderForBattlePointer, gameState, attackPointerZoneUi, {
        activePlayerId: activeUserId,
        opponentPlayerId: opponentId,
      }) &&
      (attackerOrderForBattlePointer.attack_rating ?? 0) > 0
  );
  const isStrategemDragActive = !isContainerMobile && strategemDragState?.order && strategemNeedsTargeting(strategemDragState.order.id, targetingData);
  const effectiveStrategemTargetMode = (strategemTargetMode && selectedStrategemId) || isStrategemDragActive;
  const effectiveSelectedStrategemId = (strategemTargetMode && selectedStrategemId) ? selectedStrategemId : (isStrategemDragActive ? strategemDragState?.order?.id : null);
  const effectiveStrategemTargetInfo = (strategemTargetMode && strategemTargetInfo) ? strategemTargetInfo : (isStrategemDragActive && strategemDragState?.order ? (() => {
    const dragOrder = strategemDragState.order;
    const rules = getStrategemTargetingRules(dragOrder.id, targetingData);
    if (!rules) return null;
    const synthetic = {
      type: rules.targeting === 'zone' ? 'zone' : 'unit',
      targetingOptions: [rules],
      strategemInstanceId: dragOrder.instanceId,
      strategemId: dragOrder.id,
      strategemName: dragOrder.name,
    };
    if (Number(dragOrder.id) === FRACTURE_MINDS_STRATEGEM_ID && gameState?.state?.[userId]) {
      synthetic.fractureMindsOutcome = getFractureMindsOutcomeForPlayer(gameState.state[userId]);
    }
    return synthetic;
  })() : null);

  const canPinOpponentUnitInspect =
    yourTurn &&
    !effectiveAttackMode &&
    !effectiveStrategemTargetMode &&
    !immobilizeTargetMode &&
    !confuseTargetMode &&
    !effectiveWeaponTargetMode &&
    !(weaponDeployTargetMode && weaponDeployTargetInfo) &&
    !(deployTargetMode && selectedDeployOrderId) &&
    !effectiveDroneTargetMode &&
    !(replaceTargetMode && selectedReplaceUnitId) &&
    !(specialTargetMode && selectedSpecialUnitId) &&
    !(replaceDroneTargetMode && selectedReplaceDroneId);

  useEffect(() => {
    if (!canPinOpponentUnitInspect) {
      setOpponentInspectSelection(null);
    }
  }, [canPinOpponentUnitInspect]);

  // More spectator debug
  if (isSpectating && activeGameState) {
  }
  
  // Auto-open completed orders modal when Essia Luhr needs selection (skip in spectator mode)
  useEffect(() => {
    if (isSpectating) return;
    const playerState = gameState?.state?.[userId];
    if (playerState?.essiaLuhrNeedsSelection && !playerState?.essiaLuhrSelected) {
      setShowCompletedOrders(true);
    }
  }, [gameState?.state?.[userId]?.essiaLuhrNeedsSelection, gameState?.state?.[userId]?.essiaLuhrSelected, userId, isSpectating]);

  // When backend sets needsStrategemTarget after a drag-drop play, dispatch a synthetic click at the drop
  // coordinates so the same handler as "click strategem then click target" runs (handleTargetingClick etc).
  useEffect(() => {
    const needs = gameState?.state?.[userId]?.needsStrategemTarget;
    const pending = pendingStrategemDropTargetRef.current;
    if (needs) {
    }
    if (!needs || !pending) return;
    if (pending.strategemInstanceId !== needs.strategemInstanceId) {
      pendingStrategemDropTargetRef.current = null;
      return;
    }
    pendingStrategemDropTargetRef.current = null;
    setError('');
    if (pending.type === 'click' && typeof pending.clientX === 'number' && typeof pending.clientY === 'number') {
      const el = document.elementFromPoint(pending.clientX, pending.clientY);
      if (el) {
        el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window, clientX: pending.clientX, clientY: pending.clientY }));
      }
      return;
    }
    // Legacy payload path (kept for any non-click pending shape)
    if (pending.targetType) {
      const animConfig = {
        registerAnimation: registerLocalStrategemTargetAnimation,
        strategemInstanceId: needs.strategemInstanceId,
        strategemId: needs.strategemId,
        gameState,
        userId
      };
      targetStrategem(
        pending.targetType,
        pending.targetZone,
        pending.targetIndex,
        pending.targetUnitId,
        pending.targetInstanceId,
        ws || currentSocketRef.current,
        gameId,
        setError,
        setStrategemTargetMode,
        setSelectedStrategemId,
        setStrategemTargetInfo,
        animConfig
      );
    }
  }, [gameState?.state?.[userId]?.needsStrategemTarget, gameState, userId, ws, gameId, setError, setStrategemTargetMode, setSelectedStrategemId, setStrategemTargetInfo, strategemTargetAnimations.registerAnimation, targetStrategem]);

  // Attack drop: we set selectedAttacker and attackMode in onUp; dispatch synthetic click at drop coords so target's onClick runs (attackOrder).
  // Resolve clickable target: opponent general has a sibling overlay (targeting border) with pointer-events:none on top of the img;
  // elementFromPoint can return that overlay, so the img's onClick would never run. Use elementsFromPoint and pick the element
  // with data-zone="opponent_general" so we always hit the general's click handler when dropping on it.
  // Clear pendingAttackDropCoords only inside rAF so React Strict Mode cleanup (which cancels rAF) doesn't prevent the click:
  // without this, effect runs -> clear state -> schedule rAF -> cleanup cancels rAF -> effect re-runs with null -> click never fires.
  useEffect(() => {
    if (!pendingAttackDropCoords) return;
    const { clientX, clientY } = pendingAttackDropCoords;
    const id = requestAnimationFrame(() => {
      setPendingAttackDropCoords(null);
      const elements = typeof document.elementsFromPoint === 'function'
        ? document.elementsFromPoint(clientX, clientY)
        : [document.elementFromPoint(clientX, clientY)].filter(Boolean);
      let el = elements[0] || null;
      for (let i = 0; i < elements.length; i++) {
        const candidate = elements[i];
        if (candidate?.getAttribute?.('data-zone') === 'opponent_general') {
          el = candidate;
          break;
        }
      }
      if (el) {
        el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window, clientX, clientY }));
      }
    });
    return () => cancelAnimationFrame(id);
  }, [pendingAttackDropCoords]);

  // Drone target drop: we set selectedDroneId, selectedDroneInstanceId, droneTargetMode in onUp; dispatch synthetic click at drop coords so target's onClick runs (targetDrone).
  useEffect(() => {
    if (!pendingDroneTargetDropCoords) return;
    const { clientX, clientY } = pendingDroneTargetDropCoords;
    setPendingDroneTargetDropCoords(null);
    const id = requestAnimationFrame(() => {
      const el = document.elementFromPoint(clientX, clientY);
      if (el) {
        el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window, clientX, clientY }));
      }
    });
    return () => cancelAnimationFrame(id);
  }, [pendingDroneTargetDropCoords]);

  // Deploy drop on full zone: we already set selectedOrderInstanceId/selectedOrderId in onUp; dispatch synthetic
  // click at drop coords so the unit slot's onClick runs and enters replace mode (Replace/Cancel) like the click path.
  useEffect(() => {
    if (!pendingDeployFullZoneDrop) return;
    const { clientX, clientY } = pendingDeployFullZoneDrop;
    setPendingDeployFullZoneDrop(null);
    const id = requestAnimationFrame(() => {
      const el = document.elementFromPoint(clientX, clientY);
      if (el) {
        el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window, clientX, clientY }));
      }
    });
    return () => cancelAnimationFrame(id);
  }, [pendingDeployFullZoneDrop]);

  // Drone drop: resolve drop on player drone zone. If zone has room → playDrone. If full → drop on a deployed
  // drone slot enters replace mode directly (Replace/Cancel on that drone); no hand selection step.
  useEffect(() => {
    if (!pendingDroneDropCoords) return;
    const { clientX, clientY, orderId, orderInstanceId } = pendingDroneDropCoords;
    setPendingDroneDropCoords(null);
    const id = requestAnimationFrame(() => {
      const elements = typeof document.elementsFromPoint === 'function'
        ? document.elementsFromPoint(clientX, clientY)
        : [document.elementFromPoint(clientX, clientY)].filter(Boolean);
      let onPlayerDroneZone = false;
      for (let i = 0; i < elements.length; i++) {
        let el = elements[i];
        while (el) {
          if (el.getAttribute && el.getAttribute('data-player-drone-zone') === 'true') {
            onPlayerDroneZone = true;
            break;
          }
          el = el.parentElement;
        }
        if (onPlayerDroneZone) break;
      }
      if (!onPlayerDroneZone) {
        setError(t('battleUI.selectTargetDrone') || 'Drop on your drone zone to deploy');
        setTimeout(() => setError(''), 3000);
        return;
      }
      const dronesFiltered = you?.drones?.filter(d => !d.markedForRemoval) || [];
      const dronesCount = dronesFiltered.length;
      if (dronesCount >= 4) {
        // Full zone: resolve which deployed drone slot they dropped on and show Replace/Cancel on it directly.
        let slotEl = null;
        for (let i = 0; i < elements.length; i++) {
          let el = elements[i];
          while (el) {
            if (el.getAttribute && el.getAttribute('data-drone-zone') === 'your_drone') {
              const idxAttr = el.getAttribute('data-drone-index');
              if (idxAttr != null) {
                slotEl = el;
                break;
              }
            }
            el = el.parentElement;
          }
          if (slotEl) break;
        }
        if (slotEl) {
          const slotIndex = parseInt(slotEl.getAttribute('data-drone-index'), 10);
          const drone = dronesFiltered[slotIndex];
          if (drone != null) {
            setReplaceDroneTargetMode(true);
            setSelectedReplaceDroneId(orderId);
            setSelectedReplaceDroneInstanceId(orderInstanceId);
            setClickedDroneForReplace({ ...drone, index: slotIndex });
            return;
          }
        }
        setError(t('battleUI.droneZoneFull') || 'Drop on a drone to replace it');
        setTimeout(() => setError(''), 3000);
        return;
      }
      playDrone(orderId);
    });
    return () => cancelAnimationFrame(id);
  }, [pendingDroneDropCoords, you?.drones, playDrone, setError, t, setReplaceDroneTargetMode, setSelectedReplaceDroneId, setSelectedReplaceDroneInstanceId, setClickedDroneForReplace]);

  // Debug Strategic Forecast state
  if (you?.needsStrategicForecastSelection) {
  }
  
  // Developer mode intelligence handling removed - use dev commands instead
  
  // Only log error if we've attempted to load gameState but still don't have it
  if (!activeGameState && gameId && token && !reconnecting) {
  }

  // opponentId and opponent are now memoized above to ensure real-time updates in spectator mode
  
  
  // ✅ FIXED: Only validate when game state is fully loaded and structured
  if (gameState && you && opponent) {
    if (!opponent?.playedOrders?.front || !Array.isArray(opponent.playedOrders.front)) {
      throw new Error('Invalid opponent front zone: expected array');
    }
    if (!opponent?.playedOrders?.flank || !Array.isArray(opponent.playedOrders.flank)) {
      throw new Error('Invalid opponent flank zone: expected array');
    }
    if (!you?.playedOrders?.front || !Array.isArray(you.playedOrders.front)) {
      throw new Error('Invalid player front zone: expected array');
    }
    if (!you?.playedOrders?.flank || !Array.isArray(you.playedOrders.flank)) {
      throw new Error('Invalid player flank zone: expected array');
    }
  }
  
  // ✅ REMOVED: hasAnimationsPlaying - no longer needed with two-phase attack system

  // Helper to keep destroyed units visible if animations are playing
  const getVisibleUnits = (zoneArray, playerId) => {
    return zoneArray.map((order, idx) => {
      // If unit is null but has animations playing, keep it visible temporarily
      if (!order && idx < zoneArray.length) {
        // Try to find the unit by checking previous state or context
        // For now, we'll handle this in the rendering logic below
        return null;
      }
      return order;
    });
  };

  // Track previous units to maintain visibility during animations
  // ✅ REMOVED: delayedRemovals - no longer needed with two-phase attack system
  
  // Track repositioning units - keep unit visible at old position during animation
  
  // ✅ REMOVED: Delayed removal logic for old damage animation system
  // Two-phase attack system handles attack timing - units are destroyed after animations complete
  // Slot death animations are handled separately by useSlotDeathDetection

  // Memoize unit arrays to ensure React detects health changes in spectator mode
  // Use stateVersion or deep comparison to force updates when units change
  const opponentFront = useMemo(() => {
    // Force new array reference by spreading - ensures React detects changes
    return opponent?.playedOrders?.front ? [...opponent.playedOrders.front] : [];
  }, [opponent, activeGameState?.stateVersion]);
  
  const opponentFlank = useMemo(() => {
    return opponent?.playedOrders?.flank ? [...opponent.playedOrders.flank] : [];
  }, [opponent, activeGameState?.stateVersion]);

  // Left-slot-on-top stacking makes slot 0 clickable under overlaps, but toggling it while
  // hovering causes adjacent slots to steal pointer events — use DOM order while any slot is active.
  const opponentFrontPreferLeftSlotStack =
    hoveredPlayed.zone !== 'opponent_front' &&
    opponentInspectSelection?.zone !== 'opponent_front';
  const opponentFlankPreferLeftSlotStack =
    hoveredPlayed.zone !== 'opponent_flank' &&
    opponentInspectSelection?.zone !== 'opponent_flank';
  const yourFrontPreferLeftSlotStack = hoveredPlayed.zone !== 'front';
  const yourFlankPreferLeftSlotStack = hoveredPlayed.zone !== 'flank';
  
  const yourFront = useMemo(() => {
    return you?.playedOrders?.front ? [...you.playedOrders.front] : [];
  }, [you, activeGameState?.stateVersion]);
  
  const yourFlank = useMemo(() => {
    return you?.playedOrders?.flank ? [...you.playedOrders.flank] : [];
  }, [you, activeGameState?.stateVersion]);

  // A weapon in hand is only playable when there is at least one unit on the battlefield to attach it to.
  const hasWeaponAttachTargetAvailable = useMemo(() => {
    const battlefieldUnits = [...yourFront, ...yourFlank, ...opponentFront, ...opponentFlank];
    return battlefieldUnits.some(unit => unit && unit.instanceId);
  }, [yourFront, yourFlank, opponentFront, opponentFlank]);

  // True when the player could legally take this hand action (used for End Turn “nothing to do” highlight).
  // Includes disorientation lock; hand card *glow* intentionally does not (matches pre-change afford-only UX).
  const isHandOrderActuallyPlayable = useCallback((order) => {
    if (!yourTurn || !order || !you) return false;
    if (isDisorientationHandLocked) return false;

    // Match OrderDesign getDisplayCost: missing cost must compare as 0, not undefined (>= undefined is always false).
    const orderCost =
      order.hasInstanceModifications && order.instanceCost !== undefined
        ? order.instanceCost
        : (order.cost || 0);
    const intelNum = Number(you.intelligence);
    const costNum = Number(orderCost);
    if (!Number.isFinite(intelNum) || !Number.isFinite(costNum)) return false;
    if (intelNum < costNum) return false;

    if (order.type === 'Weapon' && !hasWeaponAttachTargetAvailable) {
      return false;
    }

    return true;
  }, [yourTurn, isDisorientationHandLocked, you, hasWeaponAttachTargetAvailable]);

  const hasAffordablePlayableHandOrder = useMemo(() => {
    if (!you || !Array.isArray(you.hand) || you.hand.length === 0) return false;
    return you.hand.some(order => isHandOrderActuallyPlayable(order));
  }, [you, isHandOrderActuallyPlayable]);

  // Hand cards shown in the desktop/mobile carousel (matches order-carousel filter).
  const carouselHandOrders = useMemo(() => {
    if (needsMulligan) return [];
    return (you?.hand || []).filter((order) => {
      if (!order?.instanceId) return false;
      if (you.completedOrders?.some((co) => co?.instanceId === order.instanceId)) return false;
      if (you.deck?.some((d) => d?.instanceId === order.instanceId)) return false;
      return true;
    });
  }, [you?.hand, you?.completedOrders, you?.deck, needsMulligan]);

  // When hovering an affordable hand order, preview remaining intel in PlayerHUD.
  const handIntelHoverPreview = useMemo(() => {
    if (hoveredIndex == null || !yourTurn || !you) return null;

    const order = carouselHandOrders[hoveredIndex];
    if (!order) return null;

    const isBlockedByOrderRule = Array.isArray(blockedOrderIds) && blockedOrderIds.includes(order.id);
    if (isBlockedByOrderRule || isDisorientationHandLocked) return null;

    const orderCost =
      order.hasInstanceModifications && order.instanceCost !== undefined
        ? order.instanceCost
        : (order.cost || 0);
    const intelNum = Number(you.intelligence);
    const costNum = Number(orderCost);
    if (!Number.isFinite(intelNum) || !Number.isFinite(costNum)) return null;
    if (intelNum < costNum) return null;
    if (order.type === 'Weapon' && !hasWeaponAttachTargetAvailable) return null;

    return {
      remainingIntel: intelNum - costNum,
      intelCost: costNum,
    };
  }, [
    hoveredIndex,
    carouselHandOrders,
    yourTurn,
    you,
    hasWeaponAttachTargetAvailable,
    blockedOrderIds,
    isDisorientationHandLocked,
  ]);

  // ========== DEBUGGING: SLOT VISIBILITY AND NUMBERING ==========
  // TODO: Remove all debugging code marked with "DEBUGGING:" comments
  // Helper function to get global slot number (1-20) for debugging
  // Opponent Front: 1-5, Opponent Flank: 6-10, Your Front: 11-15, Your Flank: 16-20
  // COMMENTED OUT - no longer needed
  /*
  const getGlobalSlotNumber = (zone, idx) => {
    if (zone === 'opponent_front') return idx + 1;        // 1-5
    if (zone === 'opponent_flank') return idx + 6;        // 6-10
    if (zone === 'front') return idx + 11;               // 11-15
    if (zone === 'flank') return idx + 16;                // 16-20
    return idx + 1; // fallback
  };
  */
  // ========== END DEBUGGING ==========
  
  const handleUnitLethalDeath = useCallback(({ zoneName, index, unitSnapshot, damageAmount }) => {
    triggerUnitLethalDeathSequence({
      zoneName,
      index,
      unitSnapshot,
      damageAmount,
    });
  }, [triggerUnitLethalDeathSequence]);

  // Detect unit deaths by tracking slot states
  // ✅ FIX: Pass deadAttackersRef and deadTargetsRef directly for synchronous checking (no callback timing issues)
  // Use activeGameState/activeUserId for spectator support
  // Also pass strategemTargetedUnitsRef to suppress death animations for strategem targets (handled by onDamageAtTarget)
  useSlotDeathDetection(
    opponentFront,
    opponentFlank,
    yourFront,
    yourFlank,
    activeGameState,
    activeUserId,
    attackAnimations.deadAttackersRef,
    attackAnimations.deadTargetsRef,
    strategemTargetAnimations.targetedUnitInstanceIdsRef,
    pendingUnitDamageFxSlotsRef,
    bullyDamagePendingInstanceIdsRef,
    suppressRedeemSlotDeathInstanceIdsRef,
    completedRedeemDeathInstanceIdsRef,
    clearSlotPreviousStateRef,
    handleUnitLethalDeath,
    onExploreUnknownMemoryFade
  );

  battleKeyboardShortcutsRef.current = {
    endTurn,
    attackGeneral,
    activatePreparationBonus,
    yourTurn,
    userId,
    activeUserId,
    opponentId,
    gameState,
    activeGameState,
    isSpectating,
    isAutobattler,
    isAutobattlerSpectator,
    attackAnimations,
    attackInProgressRef,
    yourFront,
    yourFlank,
    needsMulligan,
    showResignDialog,
    deployTargetMode,
    selectedDeployOrderId,
    strategemTargetMode,
    selectedStrategemId,
    weaponTargetMode,
    selectedWeaponId,
    weaponDeployTargetMode,
    weaponDeployTargetInfo,
    droneTargetMode,
    selectedDroneId,
    immobilizeTargetMode,
    selectedImmobilizeWeaponId,
    confuseTargetMode,
    selectedConfuseWeaponId,
    specialTargetMode,
    selectedSpecialUnitId,
    replaceTargetMode,
    selectedReplaceUnitId,
    replaceDroneTargetMode,
    selectedReplaceDroneId,
    isAttackPointerActive,
    isDeployHandPointerActive,
    counterStrikeOrders,
    opponentHasCounterStrike,
    dragDeployState,
    strategemDragState,
    weaponDragState,
    droneDragState,
    attackDragState,
    droneTargetDragState,
    you,
    opponent,
    targetingDataLoaded,
    targetingDataError,
    targetingData,
    handleResign,
    confirmResign,
    cancelResign,
    resignDialogFocus,
    setResignDialogFocus,
    showStrategy,
    setShowStrategy,
    showCompletedOrders,
    setShowCompletedOrders,
    selectedAttacker,
    attackMode,
    setSelectedAttacker,
    setAttackMode,
    setLastAttackedTarget,
    opponentInspectSelection,
    setOpponentInspectSelection,
    selectedOrderInstanceId,
    setSelectedOrderInstanceId,
    selectedOrderId,
    setSelectedOrderId,
    cancelAllTargeting,
    deployTargetMode,
    strategemTargetMode,
    weaponTargetMode,
    weaponDeployTargetMode,
    droneTargetMode,
    immobilizeTargetMode,
    confuseTargetMode,
    specialTargetMode,
    replaceTargetMode,
    replaceDroneTargetMode,
    euEpZoneTargetMode,
    euEpTargetMode,
  };

  const cancelEuEpZoneTargeting = useCallback(() => {
    setEuEpZoneTargetMode(false);
    setError('');
  }, []);

  const cancelEuEpTargetTargeting = useCallback(() => {
    setEuEpTargetMode(false);
    setError('');
  }, []);

  useLayoutEffect(() => {
    if (!showResignDialog) return;
    const node =
      resignDialogFocus === 'confirm'
        ? resignDialogConfirmRef.current
        : resignDialogCancelRef.current;
    if (node && typeof node.focus === 'function') {
      node.focus();
    }
  }, [showResignDialog, resignDialogFocus]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.defaultPrevented) return;
      if (e.repeat) return;
      const el = e.target;
      if (el && typeof el.closest === 'function' && el.closest('[data-battle-keyboard-shortcuts-ignore]')) {
        return;
      }
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable) {
        return;
      }

      const key = e.key;
      const isSpace = key === ' ' || key === 'Spacebar';
      const isG = key === 'g' || key === 'G' || e.code === 'KeyG';
      const isP = key === 'p' || key === 'P' || e.code === 'KeyP';
      const isR = key === 'r' || key === 'R' || e.code === 'KeyR';

      const ctx = battleKeyboardShortcutsRef.current;
      if (!ctx) return;

      if (ctx.showResignDialog) {
        if (key === 'Escape') {
          e.preventDefault();
          ctx.cancelResign();
          return;
        }
        if (key === 'Enter') {
          e.preventDefault();
          if (ctx.resignDialogFocus === 'confirm') {
            ctx.confirmResign();
          } else {
            ctx.cancelResign();
          }
          return;
        }
        if (key === 'Tab') {
          e.preventDefault();
          ctx.setResignDialogFocus((prev) => (prev === 'confirm' ? 'cancel' : 'confirm'));
          return;
        }
        if (key === 'ArrowLeft') {
          e.preventDefault();
          ctx.setResignDialogFocus('confirm');
          return;
        }
        if (key === 'ArrowRight') {
          e.preventDefault();
          ctx.setResignDialogFocus('cancel');
          return;
        }
        return;
      }

      if (key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();

        if (ctx.showStrategy) {
          ctx.setShowStrategy(false);
          return;
        }
        if (ctx.showCompletedOrders) {
          ctx.setShowCompletedOrders(false);
          return;
        }
        if (ctx.attackMode || ctx.selectedAttacker) {
          ctx.setSelectedAttacker(null);
          ctx.setAttackMode(false);
          ctx.setLastAttackedTarget(null);
          return;
        }
        if (ctx.opponentInspectSelection) {
          ctx.setOpponentInspectSelection(null);
          return;
        }
        if (ctx.selectedOrderInstanceId || ctx.selectedOrderId) {
          ctx.setSelectedOrderInstanceId(null);
          ctx.setSelectedOrderId(null);
          return;
        }
        const hasActiveTargeting =
          ctx.deployTargetMode ||
          ctx.strategemTargetMode ||
          ctx.weaponTargetMode ||
          ctx.weaponDeployTargetMode ||
          ctx.droneTargetMode ||
          ctx.immobilizeTargetMode ||
          ctx.confuseTargetMode ||
          ctx.specialTargetMode ||
          ctx.replaceTargetMode ||
          ctx.replaceDroneTargetMode ||
          ctx.euEpZoneTargetMode ||
          ctx.euEpTargetMode;
        if (hasActiveTargeting && ctx.cancelAllTargeting) {
          ctx.cancelAllTargeting();
        }
        return;
      }

      if (isR) {
        if (ctx.isSpectating || ctx.isAutobattler || ctx.isAutobattlerSpectator) return;
        if (!ctx.targetingDataLoaded || ctx.targetingDataError || !ctx.targetingData) return;
        if (!ctx.activeGameState || !ctx.you || !ctx.opponent) return;
        if (ctx.needsMulligan) return;
        if (ctx.activeGameState.winner) return;
        if (ctx.counterStrikeOrders && ctx.counterStrikeOrders.length > 0) return;
        if (ctx.opponentHasCounterStrike) return;
        e.preventDefault();
        ctx.handleResign();
        return;
      }

      if (!isSpace && !isG && !isP) return;

      if (ctx.isSpectating || ctx.isAutobattler || ctx.isAutobattlerSpectator) return;
      if (!ctx.targetingDataLoaded || ctx.targetingDataError || !ctx.targetingData) return;
      if (!ctx.activeGameState || !ctx.you || !ctx.opponent) return;
      if (ctx.needsMulligan) return;
      if (ctx.activeGameState.winner) return;

      const isTargetingCursorActive =
        (ctx.deployTargetMode && ctx.selectedDeployOrderId) ||
        (ctx.strategemTargetMode && ctx.selectedStrategemId) ||
        (ctx.weaponTargetMode && ctx.selectedWeaponId) ||
        (ctx.weaponDeployTargetMode && ctx.weaponDeployTargetInfo) ||
        (ctx.droneTargetMode && ctx.selectedDroneId) ||
        (ctx.immobilizeTargetMode && ctx.selectedImmobilizeWeaponId) ||
        (ctx.confuseTargetMode && ctx.selectedConfuseWeaponId) ||
        (ctx.specialTargetMode && ctx.selectedSpecialUnitId) ||
        (ctx.replaceTargetMode && ctx.selectedReplaceUnitId) ||
        (ctx.replaceDroneTargetMode && ctx.selectedReplaceDroneId) ||
        ctx.euEpZoneTargetMode ||
        ctx.euEpTargetMode ||
        ctx.isAttackPointerActive ||
        ctx.isDeployHandPointerActive;
      if (isTargetingCursorActive) return;

      if (ctx.counterStrikeOrders && ctx.counterStrikeOrders.length > 0) return;
      if (ctx.opponentHasCounterStrike) return;

      if (
        ctx.dragDeployState ||
        ctx.strategemDragState ||
        ctx.weaponDragState ||
        ctx.droneDragState ||
        ctx.attackDragState ||
        ctx.droneTargetDragState
      ) {
        return;
      }

      const turnVal = ctx.gameState?.turn;
      const isBotTurn =
        turnVal &&
        (turnVal === 'bot' ||
          turnVal === 'campaign_bot' ||
          (typeof turnVal === 'string' && turnVal.startsWith('bot')));
      const hasPendingAnimations =
        ctx.attackAnimations &&
        ctx.attackAnimations.pendingAttacks &&
        ctx.attackAnimations.pendingAttacks.length > 0;
      const hasAttackInProgress = ctx.attackInProgressRef.current;
      const hasRedeemDeathSequence = redeemDeathSequenceRef.current?.isSequenceActive === true;

      if (isSpace) {
        if (!ctx.yourTurn || isBotTurn || hasPendingAnimations || hasAttackInProgress || hasRedeemDeathSequence) return;
        e.preventDefault();
        ctx.endTurn();
        return;
      }

      if (isP) {
        if (!ctx.yourTurn || isBotTurn || hasPendingAnimations || hasAttackInProgress || hasRedeemDeathSequence) return;
        const activated = ctx.activatePreparationBonus();
        if (activated) {
          e.preventDefault();
        }
        return;
      }

      if (isG) {
        if (!ctx.yourTurn || isBotTurn || hasPendingAnimations || hasAttackInProgress || hasRedeemDeathSequence) return;
        e.preventDefault();
        const front = ctx.yourFront || [];
        const flank = ctx.yourFlank || [];
        const pickAttackerSlot = (zoneKey, arr) => {
          const zoneUiName = zoneKey === 'front' ? 'your_front' : 'your_flank';
          for (let idx = 0; idx < arr.length; idx += 1) {
            const unit = arr[idx];
            if (!unit) continue;
            if (
              unitCannotInitiateAttack(unit, ctx.gameState, zoneUiName, {
                activePlayerId: ctx.activeUserId ?? ctx.userId,
                opponentPlayerId: ctx.opponentId,
              })
            ) {
              continue;
            }
            const attackRating = unit.attack_rating ?? 0;
            if (attackRating <= 0) continue;
            return { zone: zoneKey, idx };
          }
          return null;
        };
        const slot = pickAttackerSlot('front', front) || pickAttackerSlot('flank', flank);
        if (!slot) return;
        ctx.attackGeneral(slot.zone, slot.idx);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
  
  // Reconcile ghost units with authoritative state: clear ghosts for slots that are empty in state
  // and not targeted by an active strategem animation (fixes ghost units persisting after destruction)
  useEffect(() => {
    const emptySlotKeys = [];
    const pushEmpty = (zoneName, arr) => {
      if (!Array.isArray(arr)) return;
      for (let i = 0; i < arr.length; i += 1) {
        if (arr[i] == null) emptySlotKeys.push(`${zoneName}_${i}`);
      }
    };
    pushEmpty('opponent_front', opponentFront);
    pushEmpty('opponent_flank', opponentFlank);
    pushEmpty('your_front', yourFront);
    pushEmpty('your_flank', yourFlank);
    if (emptySlotKeys.length > 0 && strategemTargetAnimations.reconcileGhostUnitsForEmptySlots) {
      strategemTargetAnimations.reconcileGhostUnitsForEmptySlots(emptySlotKeys);
    }
  }, [opponentFront, opponentFlank, yourFront, yourFlank, activeGameState?.stateVersion, strategemTargetAnimations.reconcileGhostUnitsForEmptySlots]);
  
  // Game start entrance animations - use activeGameState for spectator support
  const { animationsStarted } = useGameStartAnimations(activeGameState);
  
  // Unit deployment animations - use activeGameState for spectator support
  const {
    getDeploymentAnimationStyles,
    isNewlyDeployed,
    isNewlyDeployedLegendaryUnique,
    isNewlyDeployedLegendary,
    cancelAllDeploySounds,
  } = useUnitDeploymentAnimations(activeGameState, activeUserId);
  cancelAllDeploySoundsRef.current = cancelAllDeploySounds;

  useEffect(() => {
    if (!showNegaraPeacefulOutro) return;
    cancelAllDeploySoundsRef.current();
  }, [showNegaraPeacefulOutro]);
  
  // Drone deployment animations - use activeGameState for spectator support
  useDroneDeploymentAnimations(activeGameState, activeUserId);

  // Skyfire Strike (275): light-blue stick from drone to opponent general on charge use
  useSkyfireStrikeAnimations(
    activeGameState,
    activeUserId,
    deployTargetAnimations.registerAnimation
  );

  useExploreUnknownKinshipGeneralDamageAnimations(
    activeGameState,
    activeUserId,
    deployTargetAnimations.registerAnimation
  );

  const exploreUnknownNegaraMimicToast = useExploreUnknownNegaraBossMimicToast(activeGameState);
  
  
  // Strategem sound effects - use activeGameState for spectator support
  useStrategemSoundEffects(activeGameState, activeUserId);
  const { fxSession: tacticalLoadoutFxSession, completeFxSession: completeTacticalLoadoutFx } =
    useTacticalLoadoutFx(activeGameState, activeUserId);
  /** While Tactical Loadout (114) fly-in FX runs for your POV, hide the real hand slots so only the flying thumbnails show. */
  const tacticalLoadoutHiddenHandInstanceIds = useMemo(() => {
    const s = tacticalLoadoutFxSession;
    if (!s?.isSelf || !Array.isArray(s.weapons)) return null;
    const next = new Set();
    for (const w of s.weapons) {
      if (w?.instanceId != null) next.add(String(w.instanceId));
    }
    return next.size > 0 ? next : null;
  }, [tacticalLoadoutFxSession]);
  useDeployEffectSounds(activeGameState, activeUserId);
  
  // Strategem 113 shockwave effect - use activeGameState for spectator support
  const strategemShockwave = useStrategemShockwave(activeGameState, activeUserId);
  const pulseShockwave = usePulseShockwave(activeGameState, activeUserId, {
    onShockwaveComplete: (strategemInstanceId) => {
      flushPendingInstantExecuteHealthHoldsRef.current?.(strategemInstanceId);
    },
  });
  const neurotoxPulse = useNeurotoxPulse(activeGameState, activeUserId);
  const hiveSurgePulse = useHiveSurgePulse(activeGameState, activeUserId);
  playRedeemGrowPulseRef.current = hiveSurgePulse.triggerGrowPulseFromFx;
  const quantumSurgeRefresh = useQuantumSurgeRefresh(activeGameState, activeUserId);
  const weaponEquipFlash = useWeaponEquipFlash(activeGameState, activeUserId);
  const viralEchoHealFx = useViralEchoHealFx(activeGameState, activeUserId);
  playRedeemGeneralHealRef.current = viralEchoHealFx.triggerViralEchoHealFromFx;

  /** Vein Sever (55): slash FX anchored to strategem hit coords (same timing as damage Lottie) */
  const [veinSeverSlashPoints, setVeinSeverSlashPoints] = useState([]);

  const [demoralizingBrokenShieldUntilByInstance, setDemoralizingBrokenShieldUntilByInstance] = useState({});
  const processedDemoralizingStripAtRef = useRef(null);

  useEffect(() => {
    const ev = activeGameState?.demoralizingWarcryStripEvent;
    if (!ev?.at || !Array.isArray(ev.units) || ev.units.length === 0) return;
    const staleMs = Date.now() - ev.at;
    if (staleMs > 8000) return;
    if (processedDemoralizingStripAtRef.current === ev.at) return;
    processedDemoralizingStripAtRef.current = ev.at;

    const DISPLAY_MS = 2800;
    const nextExpiry = Date.now() + DISPLAY_MS;
    setDemoralizingBrokenShieldUntilByInstance(prev => {
      const next = { ...prev };
      for (const u of ev.units) {
        if (u?.instanceId != null) next[String(u.instanceId)] = nextExpiry;
      }
      return next;
    });

    const timeouts = ev.units.map(u => {
      if (u?.instanceId == null) return null;
      const sid = String(u.instanceId);
      return window.setTimeout(() => {
        setDemoralizingBrokenShieldUntilByInstance(p => {
          if (!p[sid]) return p;
          const q = { ...p };
          delete q[sid];
          return q;
        });
      }, DISPLAY_MS);
    });

    return () => {
      timeouts.forEach(t => {
        if (t != null) window.clearTimeout(t);
      });
    };
  }, [activeGameState]);

  const showDemoralizingBrokenShieldFor = useCallback(
    (instanceId) => {
      if (instanceId == null || instanceId === '') return false;
      const until = demoralizingBrokenShieldUntilByInstance[String(instanceId)];
      return typeof until === 'number' && until > Date.now();
    },
    [demoralizingBrokenShieldUntilByInstance]
  );

  const duplicateHandRevealSeenRef = useRef(new Set());
  const duplicateHandRevealHydratedRef = useRef(false);
  const [duplicateHandPulseUntil, setDuplicateHandPulseUntil] = useState({});

  useEffect(() => {
    const hand = you?.hand;
    if (!hand || !Array.isArray(hand)) return;

    if (!duplicateHandRevealHydratedRef.current) {
      duplicateHandRevealHydratedRef.current = true;
      for (const o of hand) {
        if (o?.duplicatedFrom && o?.instanceId != null) {
          duplicateHandRevealSeenRef.current.add(String(o.instanceId));
        }
      }
      return;
    }

    const DURATION_MS = 2000;
    const timeouts = [];
    for (const order of hand) {
      if (!order?.duplicatedFrom || order.instanceId == null) continue;
      const sid = String(order.instanceId);
      if (duplicateHandRevealSeenRef.current.has(sid)) continue;
      duplicateHandRevealSeenRef.current.add(sid);

      if (getEffectiveSoundOutputVolume(1) !== null) {
        try {
          const audio = new Audio(DUPLICATE_SOUND_PATH);
          audio.play().catch(() => {});
        } catch (_) {}
      }

      setDuplicateHandPulseUntil(prev => ({ ...prev, [sid]: Date.now() + DURATION_MS }));
      const tid = window.setTimeout(() => {
        setDuplicateHandPulseUntil(prev => {
          if (!prev[sid]) return prev;
          const next = { ...prev };
          delete next[sid];
          return next;
        });
      }, DURATION_MS);
      timeouts.push(tid);
    }

    return () => {
      timeouts.forEach(t => window.clearTimeout(t));
    };
  }, [you?.hand]);

  const showDuplicateHandOverlayFor = useCallback(
    (order) => {
      if (!order?.duplicatedFrom || order.instanceId == null) return false;
      const until = duplicateHandPulseUntil[String(order.instanceId)];
      return typeof until === 'number' && until > Date.now();
    },
    [duplicateHandPulseUntil]
  );

  // Inferno Drop (strategem 319) napalm effect over all 4 zones - detected from completedOrders (no-target strategem)
  const infernoNapalm = useInfernoDropNapalm(
    activeGameState,
    activeUserId,
    getPendingInfernoBroadcastDamage
  );
  
  // Unit repositioning animations - use activeGameState for spectator support
  const { isUnitRepositioning, repositioningUnits: repositioningUnitsFromHook } = useUnitRepositionAnimations(activeGameState, activeUserId);
  
  // Track active repositioning overlays (copies of units for animation)
  const [activeRepositioningOverlays, setActiveRepositioningOverlays] = useState(new Map());
  const activeGameStateForRepositionRef = useRef(activeGameState);
  activeGameStateForRepositionRef.current = activeGameState;
  
  // Decommission animations - detect units decommissioned by strategem 111
  const { decommissionedUnits: decommissionedUnitsFromHook } = useDecommissionAnimations(activeGameState, activeUserId);
  
  // Track active decommission overlays (copies of units for animation)
  const [activeDecommissionOverlays, setActiveDecommissionOverlays] = useState(new Map());
  
  // Attack animations - REMOVED (useAttackAnimations hook deleted)
  
  // Create overlays for repositioning units (do not recreate on unrelated gameState updates e.g. Raid +0/+2)
  useEffect(() => {
    if (!repositioningUnitsFromHook || repositioningUnitsFromHook.size === 0) {
      setActiveRepositioningOverlays((prev) => (prev.size === 0 ? prev : new Map()));
      return;
    }

    setActiveRepositioningOverlays((prev) => {
      const next = new Map(prev);
      let changed = false;

      repositioningUnitsFromHook.forEach((reposition, key) => {
        if (next.has(key)) {
          return;
        }
        const playerId = reposition.playerId;
        const instanceId = reposition.instanceId;
        const player = activeGameStateForRepositionRef.current?.state?.[playerId];
        if (!player?.playedOrders) return;

        const unitAtNewPos = player.playedOrders[reposition.toZone]?.[reposition.toIndex];
        if (unitAtNewPos && unitAtNewPos.instanceId === instanceId) {
          next.set(key, {
            reposition,
            unit: { ...unitAtNewPos },
            gameStateSnapshot: activeGameStateForRepositionRef.current,
          });
          changed = true;
        }
      });

      next.forEach((_, key) => {
        if (!repositioningUnitsFromHook.has(key)) {
          next.delete(key);
          changed = true;
        }
      });

      return changed ? next : prev;
    });

    const cleanupTimeout = setTimeout(() => {
      setActiveRepositioningOverlays((prev) => {
        const next = new Map(prev);
        let changed = false;
        next.forEach((_, key) => {
          if (!repositioningUnitsFromHook.has(key)) {
            next.delete(key);
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }, REPOSITION_TOTAL_MS + 50);

    return () => clearTimeout(cleanupTimeout);
  }, [repositioningUnitsFromHook]);
  
  // Create overlays for decommissioned units
  useEffect(() => {
    if (!decommissionedUnitsFromHook || decommissionedUnitsFromHook.size === 0) {
      setActiveDecommissionOverlays(new Map());
      return;
    }
    
    // For each decommissioned unit, create an overlay
    const newOverlays = new Map();
    decommissionedUnitsFromHook.forEach((decommission, key) => {
      // Get unit from decommission data (includes full unitData)
      let unit = decommission.unitData;
      
      // Try to get from ghost units if unitData not available
      if (!unit) {
        const slotKey = `${decommission.zone}_${decommission.index}`;
        const ghostData = strategemTargetAnimations.ghostUnitsRef?.current?.get(slotKey);
        if (ghostData && ghostData.unit) {
          unit = ghostData.unit;
        } else {
          // Fallback: create unit object from decommission data
          unit = {
            id: decommission.id,
            name: decommission.name,
            instanceId: decommission.instanceId
          };
        }
      }
      
      if (unit) {
        newOverlays.set(key, {
          decommission: decommission,
          unit: { ...unit },
          startTime: Date.now()
        });
      }
    });
    
    setActiveDecommissionOverlays(newOverlays);
    
    // Clean up old overlays after animation (handled by hook timeout, but cleanup here too)
    const cleanupTimeout = setTimeout(() => {
      setActiveDecommissionOverlays(prev => {
        const next = new Map(prev);
        let changed = false;
        next.forEach((data, key) => {
          if (!decommissionedUnitsFromHook.has(key)) {
            next.delete(key);
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }, 2100);
    
    return () => clearTimeout(cleanupTimeout);
  }, [decommissionedUnitsFromHook, strategemTargetAnimations.ghostUnitsRef]);

  // Kinetron Blast (111): suppress slot-death VFX for healthy units removed from the board (same instanceId as decommission overlay)
  useEffect(() => {
    const ref = strategemTargetAnimations.targetedUnitInstanceIdsRef;
    if (!ref?.current || !decommissionedUnitsFromHook?.size) return undefined;
    const added = [];
    decommissionedUnitsFromHook.forEach((d) => {
      if (d.instanceId) {
        ref.current.add(d.instanceId);
        added.push(d.instanceId);
      }
    });
    const t = window.setTimeout(() => {
      added.forEach((id) => ref.current.delete(id));
    }, 3000);
    return () => {
      window.clearTimeout(t);
      added.forEach((id) => ref.current.delete(id));
    };
  }, [decommissionedUnitsFromHook, strategemTargetAnimations.targetedUnitInstanceIdsRef]);
  
  // Attack overlays - REMOVED (useAttackAnimations hook deleted)
  
  // OLD Attack animations state - REMOVED, now using useAttackAnimations hook
  // const [activeAttack, setActiveAttack] = useState(null); // DISABLED - using new attack-initiated flow
  const unitRefs = useRef(new Map()); // Store refs to unit containers for position calculation
  const previousGameStateRef = useRef(null); // Track previous game state for bot attack detection

  const BULLY_FIST_TRAVEL_MS = 200;
  const BULLY_FIST_BASE_DELAY_MS = 200;
  const BULLY_PUNCH_INTERVAL_MS = 400;

  /**
   * Animate bully_fist.png flying from source to target, rotating to point at the target
   * (same rotation math as Guided Missile). Fire-and-forget visual only.
   */
  const animateBullyFist = (fromPos, toPos) => {
    const dx = toPos.x - fromPos.x;
    const dy = toPos.y - fromPos.y;
    const angleRad = Math.atan2(dy, dx);
    const rotationDeg = (angleRad * 180 / Math.PI) + 90;

    const img = document.createElement('img');
    img.src = '/images/bully_fist.png';
    img.alt = '';
    Object.assign(img.style, {
      position: 'fixed',
      left: `${fromPos.x}px`,
      top: `${fromPos.y}px`,
      transform: `translate(-50%, -50%) rotate(${rotationDeg}deg)`,
      width: '48px',
      height: 'auto',
      pointerEvents: 'none',
      zIndex: '10000',
      transition: `left ${BULLY_FIST_TRAVEL_MS}ms ease-in, top ${BULLY_FIST_TRAVEL_MS}ms ease-in`,
    });
    document.body.appendChild(img);
    bullyFistElementsRef.current.push(img);

    void img.offsetWidth;
    img.style.left = `${toPos.x}px`;
    img.style.top = `${toPos.y}px`;

    const cleanTid = setTimeout(() => {
      if (img.parentNode) img.parentNode.removeChild(img);
      bullyFistElementsRef.current = bullyFistElementsRef.current.filter(el => el !== img);
    }, BULLY_FIST_TRAVEL_MS + 100);
    bullyDamageStagingTimersRef.current.push(cleanTid);
  };

  const scheduleBullyFistPlayback = useCallback((ghosts, bullySlotZone, bullySlotIdx) => {
    if (!Array.isArray(ghosts) || ghosts.length === 0) {
      return;
    }

    const getCenter = (el) => {
      if (!el) return null;
      const r = el.getBoundingClientRect();
      return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    };

    const timers = [];
    ghosts.forEach((entry, i) => {
      const hitMs = BULLY_FIST_BASE_DELAY_MS + i * BULLY_PUNCH_INTERVAL_MS;

      const tid = setTimeout(() => {
        const bullyEl = bullySlotZone != null
          ? document.querySelector(`[data-slot-zone="${bullySlotZone}"][data-slot-index="${bullySlotIdx}"]`)
          : null;
        const targetEl = document.querySelector(`[data-slot-zone="${entry.viewerZone}"][data-slot-index="${entry.idx}"]`);
        const bullyPos = getCenter(bullyEl);
        const targetPos = getCenter(targetEl);
        if (bullyPos && targetPos) {
          animateBullyFist(bullyPos, targetPos);
        }

        playSoundEffect('/sounds/punch.mp3', 0.6);
        bullyDamageGhostsRef.current.delete(entry.slotKey);
        bullyFlashInstanceIdsRef.current.add(entry.instanceId);
        bullyDamagePendingInstanceIdsRef.current.delete(entry.instanceId);
        setBullyGhostTick(t => t + 1);

        if (entry.wasDestroyed) {
          triggerUnitLethalDeathSequence({
            zoneName: entry.viewerZone,
            index: entry.idx,
            unitSnapshot: entry.unitSnapshot,
            damageAmount: entry.damageAmount,
            skipDamageQueue: true,
          });
        }

        const flashTid = setTimeout(() => {
          bullyFlashInstanceIdsRef.current.delete(entry.instanceId);
          setBullyGhostTick(t => t + 1);
        }, 300);
        bullyDamageStagingTimersRef.current.push(flashTid);
      }, hitMs);

      timers.push(tid);
    });

    bullyDamageStagingTimersRef.current = timers;
  }, [triggerUnitLethalDeathSequence]);

  const flushPendingBullyFistPlayback = useCallback((strategemInstanceId = null) => {
    const pending = pendingBullyFistPlaybackRef.current;
    if (!pending) {
      return;
    }
    if (
      strategemInstanceId != null
      && pending.strategemInstanceId != null
      && String(pending.strategemInstanceId) !== String(strategemInstanceId)
    ) {
      return;
    }
    pendingBullyFistPlaybackRef.current = null;
    scheduleBullyFistPlayback(pending.ghosts, pending.bullySlotZone, pending.bullySlotIdx);
  }, [scheduleBullyFistPlayback]);

  /**
   * Start a bully-damage ghost sequence: for each unit hit by Professional Bully,
   * store a pre-damage ghost so getVisibleUnit returns the old (higher) health.
   * Ghosts are removed one-by-one with a fist animation + punch sound for each target.
   * @param {{ deferFistPlayback?: boolean, strategemInstanceId?: string|number }} [options]
   * @returns {boolean} true if staging was activated (1+ targets)
   */
  const startBullyDamageGhostSequence = (prevState, finalState, options = {}) => {
    const { deferFistPlayback = false, strategemInstanceId = null } = options;
    const allIds = finalState.bullyDamageInstanceIds;
    if (!allIds || !prevState?.state) {
      console.log(`👊 BullyGhost: SKIP (no allIds or no prevState). allIds=${!!allIds}`);
      return false;
    }

    const instanceIds = allIds.slice(bullyAnimatedCountRef.current);
    console.log(`👊 BullyGhost: allIds.length=${allIds.length}, count=${bullyAnimatedCountRef.current}, newIds=${instanceIds.length}, existingTimers=${bullyDamageStagingTimersRef.current.length}, deferFists=${deferFistPlayback}`);
    if (instanceIds.length < 1) return false;

    bullyAnimatedCountRef.current = allIds.length;
    bullySequenceAnchorPrevStateRef.current = prevState;

    // Cancel any in-flight fist timers (not ghost staging) and remove lingering fist elements
    for (const tid of bullyDamageStagingTimersRef.current) clearTimeout(tid);
    bullyDamageStagingTimersRef.current = [];
    bullyDamageGhostsRef.current.clear();
    bullyFlashInstanceIdsRef.current.clear();
    bullyDamagePendingInstanceIdsRef.current.clear();
    for (const el of bullyFistElementsRef.current) { if (el.parentNode) el.parentNode.removeChild(el); }
    bullyFistElementsRef.current = [];
    pendingBullyFistPlaybackRef.current = null;

    const findUnitInState = (state, instanceId) => {
      const playerIds = state.players || Object.keys(state.state || {});
      for (const pid of playerIds) {
        const player = state.state?.[pid];
        if (!player?.playedOrders) continue;
        for (const zone of ['front', 'flank']) {
          const units = player.playedOrders[zone];
          if (!units) continue;
          for (const candidate of units) {
            if (candidate && candidate.instanceId === instanceId) {
              return candidate;
            }
          }
        }
      }
      return null;
    };

    // Preload punch sound so the first play doesn't have a fetch/decode delay
    const preload = new Audio('/sounds/punch.mp3');
    preload.volume = 0;
    preload.play().then(() => preload.pause()).catch(() => {});

    const viewerUserId = activeUserId;
    const allPlayerIds = prevState.players || Object.keys(prevState.state || {});
    const turnNumber = finalState.turnNumber || finalState.turn;
    const ghosts = [];

    for (const targetInstanceId of instanceIds) {
      let found = false;
      for (const pid of allPlayerIds) {
        if (found) break;
        const player = prevState.state[pid];
        if (!player?.playedOrders) continue;
        for (const zone of ['front', 'flank']) {
          if (found) break;
          const units = player.playedOrders[zone];
          if (!units) continue;
          for (let idx = 0; idx < units.length; idx++) {
            const unit = units[idx];
            if (!unit || unit.instanceId !== targetInstanceId) continue;

            const viewerZone = String(pid) === String(viewerUserId) ? `your_${zone}` : `opponent_${zone}`;
            const slotKey = `${viewerZone}_${idx}`;
            const ghost = {
              ...unit,
              isImmobilized: true,
              immobilized: true,
              immobilizedTurn: turnNumber,
              immobilizedUntilTurn: (turnNumber || 0) + 2
            };
            bullyDamageGhostsRef.current.set(slotKey, ghost);
            const finalUnit = findUnitInState(finalState, targetInstanceId);
            const preHealth = typeof unit.health === 'number' ? unit.health : 0;
            const wasDestroyed = !finalUnit || finalUnit.health <= 0;
            const damageAmount = wasDestroyed
              ? preHealth
              : Math.max(0, preHealth - finalUnit.health);
            bullyDamagePendingInstanceIdsRef.current.add(targetInstanceId);
            ghosts.push({
              slotKey,
              instanceId: targetInstanceId,
              viewerZone,
              idx,
              wasDestroyed,
              damageAmount,
              unitSnapshot: { ...unit },
            });
            found = true;
            break;
          }
        }
      }
    }

    if (ghosts.length < 1) {
      bullyDamageGhostsRef.current.clear();
      bullyDamagePendingInstanceIdsRef.current.clear();
      bullySequenceAnchorPrevStateRef.current = null;
      return false;
    }

    // Find the Professional Bully (Unit 291) slot for the fist origin
    let bullySlotZone = null;
    let bullySlotIdx = null;
    const finalPlayerIds = finalState.players || Object.keys(finalState.state || {});
    for (const pid of finalPlayerIds) {
      if (bullySlotZone) break;
      const player = finalState.state?.[pid];
      if (!player?.playedOrders) continue;
      for (const zone of ['front', 'flank']) {
        if (bullySlotZone) break;
        const units = player.playedOrders[zone];
        if (!units) continue;
        for (let idx = 0; idx < units.length; idx++) {
          if (units[idx] && units[idx].id === 291) {
            bullySlotZone = String(pid) === String(viewerUserId) ? `your_${zone}` : `opponent_${zone}`;
            bullySlotIdx = idx;
            break;
          }
        }
      }
    }

    if (deferFistPlayback) {
      pendingBullyFistPlaybackRef.current = {
        ghosts,
        bullySlotZone,
        bullySlotIdx,
        strategemInstanceId,
      };
      console.log(`👊 BullyGhost: staged ${ghosts.length} ghost(s); fist playback deferred until strategem VFX completes`);
      return true;
    }

    scheduleBullyFistPlayback(ghosts, bullySlotZone, bullySlotIdx);
    return true;
  };

  // Helper function to get visible unit
  // ✅ SIMPLIFIED: Removed delayed removal logic - two-phase attack system handles timing
  // Slot death animations are handled separately by useSlotDeathDetection
  const applyHealthDisplayOverride = useCallback((unit) => {
    if (!unit?.instanceId || typeof unit.health !== 'number') {
      return unit;
    }

    const fxEntry = battlefieldHealthCountdown.getHealthFxEntry(unit.instanceId);
    if (!fxEntry) {
      return unit;
    }

    const fxContext = {
      hasPendingDamageTrigger: hasPendingDamageTriggerForInstance,
      hasActiveProjectileAnimation: hasActiveProjectileAnimationForInstance,
    };

    const badgeHealth = resolveBadgeHealth(
      unit.health,
      fxEntry,
      fxContext,
      unit.instanceId
    );

    if (shouldClearHealthDisplayOverride(unit, fxEntry, badgeHealth, fxContext)) {
      battlefieldHealthCountdown.clearCountdown(unit.instanceId);
      return unit;
    }

    return {
      ...unit,
      badgeHealth,
      healthCountdownPulse: fxEntry.pulsing,
    };
  }, [
    battlefieldHealthCountdown.getHealthFxEntry,
    battlefieldHealthCountdown.clearCountdown,
    hasPendingDamageTriggerForInstance,
    hasActiveProjectileAnimationForInstance,
  ]);

  const getVisibleUnit = (zoneName, idx, actualOrder) => {
    void lethalDeathGhostTick;
    void redeemMarkedGhostTick;
    // A unit spawned by a redeemer stays hidden until that redeemer's death sequence finishes; treat
    // its slot as empty so the dying unit's ghost can play first, then the new unit appears on release.
    if (actualOrder?.instanceId && redeemSpawnHeldInstanceIdsRef.current.has(actualOrder.instanceId)) {
      actualOrder = null;
    }
    const bullyGhostKey = `${zoneName}_${idx}`;
    const bullyGhost = bullyDamageGhostsRef.current.get(bullyGhostKey);
    if (bullyGhost) {
      return bullyGhost;
    }

    if (actualOrder && bullyFlashInstanceIdsRef.current.has(actualOrder.instanceId)) {
      return applyHealthDisplayOverride({ ...actualOrder, bullyDamageFlash: true });
    }

    // Redeem death owns this slot while marked (e.g. Forbidden Clone Protocol deploys a Fleshsack
    // into the sacrificed unit's index before the ghost/dissipation plays).
    for (const markedEntry of redeemMarkedByInstanceIdRef.current.values()) {
      if (markedEntry.viewerZoneName === zoneName && markedEntry.viewerIndex === idx) {
        return applyHealthDisplayOverride(markedEntry.ghostUnit);
      }
    }

    if (actualOrder) {
      // Unit still exists in game state
      
      // Extract playerId from zoneName for repositioning check.
      let playerId = null;
      if (zoneName?.startsWith('your_')) {
        playerId = activeUserId || null;
      } else if (zoneName?.startsWith('opponent_')) {
        playerId = opponentId || null;
      }
      
      // Check if this unit is being repositioned (hide it during animation - overlay shows the copy)
      if (playerId && actualOrder.instanceId && isUnitRepositioning && isUnitRepositioning(playerId, actualOrder.instanceId)) {
        // Unit is being animated in overlay - hide it at both old and new positions
        return null; // Will be shown after animation completes
      }

      if (actualOrder.instanceId && redeemMarkedByInstanceIdRef.current.has(actualOrder.instanceId)) {
        const markedEntry = redeemMarkedByInstanceIdRef.current.get(actualOrder.instanceId);
        return applyHealthDisplayOverride(markedEntry.ghostUnit);
      }

      const displayUnit = actualOrder;
      return applyHealthDisplayOverride(displayUnit);
    }

    for (const markedEntry of redeemMarkedByInstanceIdRef.current.values()) {
      if (markedEntry.viewerZoneName === zoneName && markedEntry.viewerIndex === idx) {
        return applyHealthDisplayOverride(markedEntry.ghostUnit);
      }
    }

    // Unit was removed from game state — keep on-card during lethal damage pop + white fade
    const lethalGhost = lethalDeathGhostsRef.current.get(`${zoneName}_${idx}`);
    if (lethalGhost) {
      return applyHealthDisplayOverride(lethalGhost);
    }

    // Ghost units keep targets visible while strategem animation plays
    const ghostUnit = strategemTargetAnimations.getGhostUnit(zoneName, idx);
    if (ghostUnit) {
      return applyHealthDisplayOverride(ghostUnit);
    }
    
    return null;
  };

  // Helper: UI immobilize flag
  // For strategem 219 (Plague of Darkness), only show immobilize indicator after animation completes
  const isImmobilizedUI = (u, zoneName = null) => {
    if (!u) return false;
    const isImmobilized = (u.isImmobilized === true) || (u.immobilized === true) || (u.status === 'immobilized');
    if (!isImmobilized) return false;
    
    // Check if this unit is in a zone where strategem 219 animation hasn't completed yet
    // We need to check if the unit's zone matches a pending strategem 219 animation
    if (zoneName) {
      // Normalize zone name (remove player prefix for comparison)
      const normalizedZone = zoneName.replace('your_', '').replace('opponent_', '');
      const zoneKey = normalizedZone; // e.g., 'front' or 'flank'
      
      // Check if this zone has a pending strategem 219 animation
      // If the zone is NOT in the completed set, delay showing the indicator
      if (!strategem219CompletedZonesRef.current.has(zoneKey)) {
        // Check if there's an active strategem 219 animation for this zone
        const hasActive219Animation = strategemTargetAnimations.activeAnimations.some(anim => {
          if (anim.strategemId !== 219) return false;
          // Check if animation targets this zone
          const animZone = anim.toSlot?.zone?.replace('your_', '').replace('opponent_', '');
          return animZone === zoneKey;
        });
        
        // If there's an active animation, delay showing the indicator
        if (hasActive219Animation) {
          return false;
        }
      }
    }
    
    return true;
  };

  // Damage flash hook (after you and opponent are defined)
  const { playerDamageFlash, opponentDamageFlash } = useDamageFlash(you, opponent);

  const opponentHudPanelProps = useMemo(() => {
    const currentOpponentIdForHud = gameState?.players?.find((id) => id !== userId);
    const opponentGoesSecondForHud = Boolean(
      currentOpponentIdForHud && gameState?.players?.[1] === currentOpponentIdForHud
    );
    const showOpponentPrepBonusHud =
      opponentGoesSecondForHud &&
      opponentPreparationBonus &&
      (opponentPreparationBonus === 'plan_ahead' ||
        opponentPreparationBonus === 'black_market' ||
        opponentPreparationBonus === 'team_leader');
    const opponentPrepTooltipForHud =
      opponent.preparationBonusUsed === true
        ? (t('battleUI.preparationBonusUsed') || 'Preparation bonus already used')
        : opponentPreparationBonus === 'plan_ahead'
          ? t('profile.preparationBonus.planAhead')
          : opponentPreparationBonus === 'black_market'
            ? t('profile.preparationBonus.blackMarketDossier')
            : t('profile.preparationBonus.teamLeaderMeeting');
    const opponentDiplomaticTooltipForHud = opponentDiplomaticAccord
      ? getDiplomaticAccordTooltipText(opponentDiplomaticAccord, t)
      : '';

    const hudGeneralPseudoUnit = { isGeneral: true, type: 'general' };
    const isOpponentGeneralTarget = isTarget({
      strategemTargetMode: effectiveStrategemTargetMode,
      selectedStrategemId: effectiveSelectedStrategemId,
      strategemTargetInfo: effectiveStrategemTargetInfo,
      immobilizeTargetMode,
      confuseTargetMode,
      weaponTargetMode: effectiveWeaponTargetMode,
      selectedWeaponId,
      weaponDeployTargetMode,
      deployTargetMode,
      selectedDeployOrderId,
      selectedDeployInstanceId,
      selectedDeployZone,
      zone: 'opponent_general',
      targetingData,
      gameState,
      playerId: userId,
      attackMode: effectiveAttackMode,
      selectedAttacker: effectiveSelectedAttacker,
      droneTargetMode: effectiveDroneTargetMode,
      selectedDroneId: effectiveSelectedDroneId,
      unit: hudGeneralPseudoUnit,
    });

    const generalPortraitPointer =
      (yourTurn && attackMode && selectedAttacker) ||
      (deployTargetMode && selectedDeployOrderId) ||
      (effectiveStrategemTargetMode &&
        effectiveSelectedStrategemId &&
        getStrategemTargetingRules(effectiveSelectedStrategemId, targetingData)?.general === true);

    const handleOpponentGeneralPortraitClick = () => {
      if (yourTurn && attackMode && selectedAttacker) {
        attackGeneral(selectedAttacker.zone, selectedAttacker.idx);
      } else if (deployTargetMode && selectedDeployOrderId) {
        handleTargetDeploy('general', null, 'opponent');
      } else if (strategemTargetMode && selectedStrategemId) {
        const targetingRules = getStrategemTargetingRules(selectedStrategemId, targetingData);
        if (targetingRules?.general === true) {
          targetStrategem(
            'general',
            null,
            null,
            'opponent',
            null,
            ws,
            gameId,
            setError,
            setStrategemTargetMode,
            setSelectedStrategemId,
            setStrategemTargetInfo,
            {
              registerAnimation: registerLocalStrategemTargetAnimation,
              strategemInstanceId: strategemTargetInfo?.strategemInstanceId,
              strategemId: selectedStrategemId,
              gameState,
              userId,
            }
          );
        }
      }
    };

    const profileImageStyle = {
      ...getDamageFlashStyles(opponentDamageFlash),
      transform: (() => {
        const hudProfileScale =
          getOpponentAvatarScale(opponentGeneral) *
          OPPONENT_HUD_PROFILE_RING_SCALE_MULTIPLIER *
          getExploreUnknownPeacockBossOpponentHudProfileScaleBonus(
            activeGameState?.exploreUnknownMissionIndex
          );
        return shouldMirrorOpponentAvatar(opponentGeneral, 'upper_middle', 'general', avatars)
          ? `scale(${hudProfileScale}) scaleX(-1)`
          : `scale(${hudProfileScale})`;
      })(),
    };

    return {
      opponentEmoteBubble,
      emoteDisplayText: opponentEmoteBubble
        ? formatEmoteBubbleTwoWords(resolveBattleEmoteBubbleDisplay(opponentEmoteBubble, t))
        : '',
      opponent,
      opponentHealthDisplayText,
      opponentGeneral,
      effectiveOpponentUsername,
      effectiveOpponentTitleRaw,
      effectiveOpponentTitleColorKey,
      opponentDamageFlash,
      opponentFactionName,
      getTranslatedFactionName,
      resolveBattleTitleDisplay,
      opponentPreparationBonus,
      opponentDiplomaticAccord,
      showOpponentPrepBonusHud,
      opponentPrepTooltipForHud,
      opponentDiplomaticTooltipForHud,
      isTutorialMission,
      yourTurn,
      gameId,
      opponentRank: desktopOpponentHudRankProps.opponentRank,
      opponentPvpLadderRow: desktopOpponentHudRankProps.opponentPvpLadderRow,
      showOpponentBanner: desktopOpponentHudRankProps.showOpponentBanner,
      getRankStyle,
      profileImage: getOpponentAvatarImage(opponentGeneral),
      profileImageStyle,
      isGeneralTarget: isOpponentGeneralTarget,
      generalPortraitPointer,
      onGeneralPortraitClick: handleOpponentGeneralPortraitClick,
    };
  }, [
    gameState,
    userId,
    opponentPreparationBonus,
    opponentDiplomaticAccord,
    opponent,
    opponentEmoteBubble,
    opponentHealthDisplayText,
    opponentGeneral,
    effectiveOpponentUsername,
    effectiveOpponentTitleRaw,
    effectiveOpponentTitleColorKey,
    opponentDamageFlash,
    opponentFactionName,
    getTranslatedFactionName,
    resolveBattleTitleDisplay,
    isTutorialMission,
    yourTurn,
    gameId,
    desktopOpponentHudRankProps,
    getRankStyle,
    avatars,
    activeGameState?.exploreUnknownMissionIndex,
    effectiveStrategemTargetMode,
    effectiveSelectedStrategemId,
    effectiveStrategemTargetInfo,
    immobilizeTargetMode,
    confuseTargetMode,
    effectiveWeaponTargetMode,
    selectedWeaponId,
    weaponDeployTargetMode,
    deployTargetMode,
    selectedDeployOrderId,
    selectedDeployInstanceId,
    selectedDeployZone,
    targetingData,
    effectiveAttackMode,
    effectiveSelectedAttacker,
    effectiveDroneTargetMode,
    effectiveSelectedDroneId,
    attackMode,
    selectedAttacker,
    strategemTargetMode,
    selectedStrategemId,
    strategemTargetInfo,
    ws,
    setError,
    setStrategemTargetMode,
    setSelectedStrategemId,
    setStrategemTargetInfo,
    registerLocalStrategemTargetAnimation,
    attackGeneral,
    handleTargetDeploy,
    targetStrategem,
    t,
  ]);

  const handlePlayerHudAvatarClick = useCallback((e) => {
    e.stopPropagation();
    if (isSpectating) {
      return;
    }
    if (strategemTargetMode && selectedStrategemId) {
      const targetingRules = getStrategemTargetingRules(selectedStrategemId, targetingData);
      if (targetingRules?.general === true) {
        const playerRestriction = targetingRules?.player || 'opponent';
        if (playerRestriction === 'friendly' || playerRestriction === 'both') {
          targetStrategem(
            'general',
            null,
            null,
            'friendly',
            null,
            ws,
            gameId,
            setError,
            setStrategemTargetMode,
            setSelectedStrategemId,
            setStrategemTargetInfo,
            {
              registerAnimation: registerLocalStrategemTargetAnimation,
              strategemInstanceId: strategemTargetInfo?.strategemInstanceId,
              strategemId: selectedStrategemId,
              gameState,
              userId,
            }
          );
          return;
        }
      }
    }
    if (!deployTargetMode && !weaponTargetMode && !strategemTargetMode && !attackMode && !emotesLoading) {
      setShowEmoteButtons((prev) => !prev);
    }
  }, [
    isSpectating,
    strategemTargetMode,
    selectedStrategemId,
    targetingData,
    deployTargetMode,
    weaponTargetMode,
    attackMode,
    emotesLoading,
    ws,
    gameId,
    setError,
    setStrategemTargetMode,
    setSelectedStrategemId,
    setStrategemTargetInfo,
    registerLocalStrategemTargetAnimation,
    strategemTargetInfo?.strategemInstanceId,
    gameState,
    userId,
    targetStrategem,
  ]);

  const playerHudPanelProps = useMemo(() => {
    const hudGeneralPseudoUnit = { isGeneral: true, type: 'general' };
    const isPlayerGeneralTarget = isTarget({
      strategemTargetMode: effectiveStrategemTargetMode,
      selectedStrategemId: effectiveSelectedStrategemId,
      strategemTargetInfo: effectiveStrategemTargetInfo,
      immobilizeTargetMode,
      confuseTargetMode,
      weaponTargetMode: effectiveWeaponTargetMode,
      selectedWeaponId,
      weaponDeployTargetMode,
      deployTargetMode,
      selectedDeployOrderId,
      selectedDeployInstanceId,
      selectedDeployZone,
      zone: 'player_general',
      targetingData,
      gameState,
      playerId: userId,
      attackMode: effectiveAttackMode,
      selectedAttacker: effectiveSelectedAttacker,
      droneTargetMode: effectiveDroneTargetMode,
      selectedDroneId: effectiveSelectedDroneId,
      unit: hudGeneralPseudoUnit,
    });

    const euDiplomaticAccordsUnlocked = exploreUnknownDiplomaticAccordsActive(gameState);
    const effectiveDiplomaticAccords = euDiplomaticAccordsUnlocked
      ? normalizeDiplomaticAccordsList(diplomaticAccord)
      : normalizeDiplomaticAccordsList(diplomaticAccord);
    const effectiveDiplomaticAccord = effectiveDiplomaticAccords[0] || null;
    const blackMarketMaxUses = exploreUnknownBlackMarketMaxUses(gameState);
    const blackMarketUses = Number(you?.blackMarketUsesThisBattle) || 0;
    const blackMarketChargesRemaining = Math.max(0, blackMarketMaxUses - blackMarketUses);
    const planAheadMaxUses = exploreUnknownPlanAheadMaxUses(gameState);
    const planAheadUses = Number(you?.planAheadUsesThisBattle) || 0;
    const planAheadChargesRemaining = Math.max(0, planAheadMaxUses - planAheadUses);

    const canOpenEmotesOrTarget =
      (!isSpectating && !deployTargetMode && !weaponTargetMode && !strategemTargetMode && !attackMode) ||
      (strategemTargetMode && selectedStrategemId);

    return {
      isExploreUnknownAflame: isPlayerGeneralAflame,
      health: you?.health,
      intelligenceLeft: you?.intelligence,
      intelligenceTotal: you?.maxIntelligence,
      barriersRemoved: you?.barriersRemoved ?? [],
      barrierSpriteDelayMs: BARRIER_SPRITE_DISMANTLE_DELAY_MS,
      barrierVisualResetKey: gameId ?? '',
      damageFlash: playerDamageFlash,
      factionName: playerFactionName,
      translatedFactionName: getTranslatedFactionName(playerFactionName),
      isActiveTurn: yourTurn,
      selectedGeneral,
      avatars,
      onAvatarClick: canOpenEmotesOrTarget ? handlePlayerHudAvatarClick : undefined,
      isGeneralTarget: isPlayerGeneralTarget,
      yourTurn,
      playerTitle: resolveBattleTitleDisplay(activeGameState?.playerTitles?.[activeUserId]),
      playerTitleColor: activeGameState?.playerTitleColors?.[activeUserId],
      playerRank: effectivePlayerRank,
      playerPvpLadderRow: effectivePlayerPvpLadder,
      getRankStyle,
      notYourTurnLabel: t('battleUI.notYourTurn') || "It's not your turn",
      ordersReady: needsMulligan ? 0 : (you?.hand?.length ?? 0),
      strategySize: getStrategyHudTotal(you),
      completedOrders: you?.completedOrders?.length || 0,
      onOpenStrategy: () => {
        playSoundEffect('/sounds/click.mp3');
        setShowStrategy(true);
      },
      onOpenCompletedOrders: () => {
        playSoundEffect('/sounds/click.mp3');
        setShowCompletedOrders(true);
      },
      ordersReadyLabel: t('battleUI.ordersReady'),
      strategySizeLabel: t('battleUI.strategySize'),
      completedOrdersLabel: t('battleUI.completedOrders'),
      clickToViewStrategyLabel: t('battleUI.clickToViewStrategy') || 'Click to view strategy',
      clickToViewCompletedOrdersLabel: t('battleUI.clickToViewCompletedOrders') || 'Click to view completed orders',
      isCommandDirectivePending:
        euDiplomaticAccordsUnlocked &&
        effectiveDiplomaticAccords.includes('command_directive') &&
        !(you?.commandDirectiveSelected),
      preparationBonus,
      preparationBonusUsed,
      preparationBonusPending: preparationBonusPendingRef.current,
      blackMarketMaxUses,
      blackMarketUsesThisBattle: blackMarketUses,
      planAheadMaxUses,
      planAheadUsesThisBattle: planAheadUses,
      yourTurnForPrepBonus: yourTurn,
      canShowPreparationBonus:
        !isTutorialMission &&
        (preparationBonus === 'plan_ahead' ||
          preparationBonus === 'black_market' ||
          preparationBonus === 'team_leader') &&
        playerGoesFirst === false &&
        exploreUnknownPreparationBonusActive(gameState),
      onActivatePreparationBonus: (e) => {
        const ok = activatePreparationBonus(e);
        if (!ok && e) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      preparationBonusTooltip:
        preparationBonus === 'black_market'
          ? blackMarketChargesRemaining <= 0
            ? t('battleUI.preparationBonusUsed') || 'Preparation bonus already used'
            : !yourTurn
              ? t('battleUI.notYourTurn') || 'Not your turn'
              : blackMarketMaxUses >= 2
                ? t('profile.preparationBonus.blackMarketDossierTwoCharges', {
                    remaining: blackMarketChargesRemaining,
                    total: blackMarketMaxUses,
                  })
                : t('profile.preparationBonus.blackMarketDossier')
          : preparationBonusUsed
            ? t('battleUI.preparationBonusUsed') || 'Preparation bonus already used'
            : !yourTurn
              ? t('battleUI.notYourTurn') || 'Not your turn'
              : preparationBonus === 'plan_ahead'
                ? planAheadMaxUses >= 2
                  ? t('profile.preparationBonus.planAheadTwoCharges', {
                      remaining: planAheadChargesRemaining,
                      total: planAheadMaxUses,
                    })
                  : t('profile.preparationBonus.planAhead')
                : preparationBonus === 'black_market'
                  ? blackMarketMaxUses >= 2
                    ? t('profile.preparationBonus.blackMarketDossierTwoCharges', {
                        remaining: blackMarketChargesRemaining,
                        total: blackMarketMaxUses,
                      })
                    : t('profile.preparationBonus.blackMarketDossier')
                  : t('profile.preparationBonus.teamLeaderMeeting'),
      diplomaticAccord: !isTutorialMission ? effectiveDiplomaticAccord : null,
      diplomaticAccords: !isTutorialMission ? effectiveDiplomaticAccords : [],
      getDiplomaticAccordTooltip: (accord) => getDiplomaticAccordTooltipText(accord, t),
      intelHoverPreview: handIntelHoverPreview,
    };
  }, [
    effectiveStrategemTargetMode,
    effectiveSelectedStrategemId,
    effectiveStrategemTargetInfo,
    immobilizeTargetMode,
    confuseTargetMode,
    effectiveWeaponTargetMode,
    selectedWeaponId,
    weaponDeployTargetMode,
    deployTargetMode,
    selectedDeployOrderId,
    selectedDeployInstanceId,
    selectedDeployZone,
    targetingData,
    gameState,
    userId,
    effectiveAttackMode,
    effectiveSelectedAttacker,
    effectiveDroneTargetMode,
    effectiveSelectedDroneId,
    isPlayerGeneralAflame,
    you,
    gameId,
    playerDamageFlash,
    playerFactionName,
    getTranslatedFactionName,
    yourTurn,
    selectedGeneral,
    avatars,
    handlePlayerHudAvatarClick,
    isSpectating,
    strategemTargetMode,
    selectedStrategemId,
    attackMode,
    activeGameState?.playerTitles,
    activeGameState?.playerTitleColors,
    activeUserId,
    resolveBattleTitleDisplay,
    effectivePlayerRank,
    effectivePlayerPvpLadder,
    getRankStyle,
    t,
    needsMulligan,
    setShowStrategy,
    setShowCompletedOrders,
    diplomaticAccord,
    preparationBonus,
    preparationBonusUsed,
    isTutorialMission,
    playerGoesFirst,
    activatePreparationBonus,
    handIntelHoverPreview,
  ]);

  // Strategem general damage animations - triggers explosion when strategem damages general
  // Pass getAnimatedStrategemInstanceIds to suppress duplicates when targeting animation already handles it
  useStrategemGeneralDamage(
    you,
    opponent,
    queueDamageAnimationTrigger,
    strategemTargetAnimations.getAnimatedStrategemInstanceIds,
    strategemTargetAnimations.isAnimatingToGeneral
  );

  // Fetch avatars from server
  useEffect(() => {
    const fetchAvatars = async () => {
      try {
        const res = await fetch(`${API_URL}/get-avatar`);
        const data = await res.json();
        if (Array.isArray(data)) {
          setAvatars(data);
        } else {
          console.error('Unexpected avatar response format:', data);
          setAvatars([]);
        }
      } catch (err) {
        console.error('Failed to fetch avatars:', err);
        setAvatars([]);
      }
    };

    fetchAvatars();
  }, []);

  // Extract opponent avatar selections from game state
  useEffect(() => {
    // Use effectiveUserId for spectator mode (watched player) or userId for normal mode
    // For autobattler, use the playerBotId as the effective user (the bot the spectator is watching)
    const effectiveUserIdForAvatars = isAutobattlerSpectator && gameState?.playerBotId 
      ? gameState.playerBotId 
      : (isSpectating && spectatingPlayerId ? spectatingPlayerId : userId);
    // For autobattler, always use gameState (not spectatingGameState) since autobattler doesn't use friends context
    const effectiveGameStateForAvatars = isAutobattlerSpectator 
      ? gameState 
      : (isSpectating && spectatingGameState ? spectatingGameState : gameState);
    
    if (effectiveGameStateForAvatars) {
      // Get opponent's actual player ID
      const opponentId = effectiveGameStateForAvatars.players?.find(id => String(id) !== String(effectiveUserIdForAvatars));
      
      if (opponentId) {
        // In spectator mode OR autobattler mode, get opponent avatars from the opponent's playerAvatars
        // In normal mode, get from opponentAvatars keyed by userId
        let opponentAvatars = {};
        if (isSpectating || isAutobattlerSpectator) {
          // In spectator mode or autobattler mode, opponent avatars are in playerAvatars keyed by opponent's ID
          opponentAvatars = effectiveGameStateForAvatars.playerAvatars?.[opponentId] || {};
        } else {
          // In normal mode, opponent avatars are keyed by userId
          opponentAvatars = effectiveGameStateForAvatars.opponentAvatars?.[effectiveUserIdForAvatars] || {};
        }
        
        const opponentState = effectiveGameStateForAvatars.state?.[opponentId];
        let opponentGeneral = opponentAvatars.general || opponentState?.selectedGeneral || effectiveGameStateForAvatars.opponentGeneral || '';
        let opponentAdvisor = opponentAvatars.advisor || opponentState?.selectedAdvisor || effectiveGameStateForAvatars.opponentAdvisor || '';
        
        // For bot games, avatars should already be in game state from server
        // Fallback to localStorage only if game state doesn't have them (for old games)
        if (isBotGame && (!opponentGeneral || !opponentAdvisor)) {
          const gameId = effectiveGameStateForAvatars.gameId || effectiveGameStateForAvatars.id;
          const storedBotAvatars = localStorage.getItem(`botAvatars_${gameId}`);
          
          if (storedBotAvatars) {
            // Use stored avatars from localStorage (fallback for old games)
            const parsedAvatars = JSON.parse(storedBotAvatars);
            opponentGeneral = parsedAvatars.general || opponentGeneral;
            opponentAdvisor = parsedAvatars.advisor || opponentAdvisor;
          }
        }

        opponentAdvisor = dedupeExploreUnknownBossOpponentAdvisorIfNeeded(
          effectiveGameStateForAvatars.exploreUnknownMissionIndex,
          opponentGeneral,
          opponentAdvisor
        );
        
        setOpponentGeneral(opponentGeneral);
        setOpponentAdvisor(opponentAdvisor);
      }
    }
  }, [gameState, spectatingGameState, opponent, isBotGame, userId, spectatingPlayerId, isSpectating, isAutobattlerSpectator]);

  // ✅ REFACTORED: Cleanup state sync timers on unmount
  useEffect(() => {
    return () => {
      cleanupStateSync();
    };
  }, [cleanupStateSync]);

  // ✅ FIXED: Cleanup hover timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
    };
  }, []);

  // Damage flash logic moved to ./effects/useDamageFlash.js

// Navigate back after game ends (skip for spectators)
// ✅ FIX: Use handleBattleEnd to ensure consistent transition behavior
useEffect(() => {
  if (isSpectating) return; // Spectators don't navigate on game end
  if (!gameState?.winner) return;
  if (battleEndTransition.isTransitioning || isTransitioningRef.current) return; // Already transitioning

  const urlSid = getCurrentGameIdFromUrl();
  if (urlSid != null && String(urlSid) !== String(gameId)) {
    return;
  }

  // gameState.gameId is the engine internal id (e.g. game_*); URL / gameIdRef use the WebSocket session key (e.g. 4_1_*).
  // Comparing them always failed for PvP and blocked after-battle navigation. Wrong-game filtering is done on incoming WS messages via msg.gameId.

  if (negaraPeacefulOutroActiveRef.current || gameState?.exploreUnknownNegaraPeacefulWin === true) {
    return;
  }
  const didYouWin = didHumanPlayerWinGame(gameState, userId);
  handleBattleEnd(
    didYouWin,
    gameId,
    ws,
    gameState?.isFriendlyMatch || false,
    'useEffect-gameState.winner',
    gameState
  );
}, [gameState, gameId, getCurrentGameIdFromUrl, ws, userId, battleEndTransition.isTransitioning, handleBattleEnd]);

// ✅ AUTOBATTLER SPECTATOR: Navigate back to dispatch-asset when spectated game ends
useEffect(() => {
  // Only handle autobattler spectators
  if (!isAutobattlerSpectator) return;
  
  // Check if game has ended (winner set)
  const hasWinner = activeGameState?.winner;
  if (!hasWinner) return;
  
  // Prevent multiple navigations
  if (isTransitioningRef.current || exitingRef.current) return;
  
  
  // ✅ SPEED CONTROL: Clear queue when game ends
  speedControl.clearQueue();
  
  // Clear all animation states to prevent ghost images persisting after game ends
  for (const tid of bullyDamageStagingTimersRef.current) clearTimeout(tid);
  bullyDamageStagingTimersRef.current = [];
  bullyDamageGhostsRef.current.clear();
  bullyFlashInstanceIdsRef.current.clear();
  for (const el of bullyFistElementsRef.current) { if (el.parentNode) el.parentNode.removeChild(el); }
  bullyFistElementsRef.current = [];
  bullyAnimatedCountRef.current = 0;
  bullyDamagePendingInstanceIdsRef.current.clear();
  pendingBullyFistPlaybackRef.current = null;
  bullySequenceAnchorPrevStateRef.current = null;

  if (attackAnimationsRef.current?.reset) {
    attackAnimationsRef.current.reset();
  }
  if (deployTargetAnimationsRef.current?.reset) {
    deployTargetAnimationsRef.current.reset();
    registeredDeployVfxKeysRef.current.clear();
  }
  if (raidAnimationsRef.current?.reset) {
    raidAnimationsRef.current.reset();
  }
  
  setDamageAnimationTriggers(new Map());
  setLethalAttackFadeOverlays({});
  lethalDeathGhostsRef.current.clear();
  lethalDeathSequenceSlotsRef.current.clear();
  suppressRedeemSlotDeathInstanceIdsRef.current.clear();
  completedRedeemDeathInstanceIdsRef.current.clear();
  completedLethalDeathInstanceIdsRef.current.clear();
  redeemMarkedByInstanceIdRef.current.clear();
  redeemSpawnHeldInstanceIdsRef.current.clear();
  redeemDeathSequenceRef.current?.reset?.();
  battlefieldHealthCountdown.resetAll();
  pendingProjectileHealthHoldRef.current.clear();
  
  // Clear damage animation triggered tracking ref
  if (damageAnimationTriggeredRef.current) {
    damageAnimationTriggeredRef.current.clear();
  }
  completedDamageAnimationTriggerKeysRef.current.clear();
  attackSurvivorCountdownPlayedRef.current.clear();
  
  // Clear explosion effects
  setExplosionEffects(new Map());
  
  // Mark as transitioning
  isTransitioningRef.current = true;
  exitingRef.current = true;
  
  // Determine if the player's bot won
  const playerBotId = activeGameState?.playerBotId;
  const didPlayerWin = playerBotId && activeGameState.winner === playerBotId;
  
  // Short delay to let the final state render, then navigate back
  const navTimeout = setTimeout(() => {
    navigate('/dispatch-asset', { 
      replace: true, 
      state: { 
        spectateEnded: true,
        result: didPlayerWin ? 'win' : 'loss',
        gameId: gameId
      } 
    });
  }, 2000); // 2 second delay so user can see the final result
  
  return () => {
    clearTimeout(navTimeout);
  };
}, [isAutobattlerSpectator, activeGameState?.winner, activeGameState?.playerBotId, navigate, gameId, speedControl]);

// ✅ SPEED CONTROL: Cleanup on unmount or when no longer spectating
useEffect(() => {
  if (!isAutobattlerSpectator) {
    speedControl.clearQueue();
  }
  
  return () => {
    speedControl.clearQueue();
  };
}, [isAutobattlerSpectator, speedControl]);

// ✅ FIX: Show reconnecting message for autobattler spectators too
// But only if we're actually reconnecting and don't have gameState yet
const isAutobattlerGame = gameId?.includes('_autobattler_') || initialGameState?.isAutobattler || gameState?.isAutobattler;
const shouldShowReconnecting = reconnecting && (!isFriendSpectating || (isAutobattlerGame && !gameState));

if (shouldShowReconnecting) {
  return <div style={{ padding: 24 }}>{t('battleUI.reconnecting')}</div>;
}

  // For friend spectators, wait for game state from spectate-started / Friends WS
  if (isFriendSpectating && (!activeGameState || !you || !opponent)) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <div style={{ fontSize: '24px', marginBottom: '10px' }}>👁️</div>
        <div>Connecting to spectator view...</div>
        <div style={{ fontSize: '12px', color: '#888', marginTop: '10px' }}>
          Waiting for game state from {spectatingFriendName}'s game
        </div>
      </div>
    );
  }

  if (!activeGameState || !you || !opponent) {
    return <div style={{ padding: 24 }}>{t('battleUI.loadingBattle')}</div>;
  }

  // Show error if targeting data failed to load
  if (targetingDataError) {
    return <div style={{ padding: 24, color: 'red' }}>Error loading targeting data: {targetingDataError}</div>;
  }

  // Show loading if targeting data is not yet loaded
  if (!targetingDataLoaded || !targetingData) {
    return <div style={{ padding: 24 }}>Loading targeting data...</div>;
  }

// Use activeGameState for spectator support
// ✅ FIX: Don't exit immediately if we're transitioning - let the transition animation play
const peacefulOutroBlocksExit =
  showNegaraPeacefulOutro ||
  negaraPeacefulOutroActiveRef.current ||
  activeGameState?.exploreUnknownNegaraPeacefulWin === true;
const shouldExit =
  Boolean(activeGameState?.winner) &&
  !battleEndTransition.isTransitioning &&
  !isTransitioningRef.current &&
  !peacefulOutroBlocksExit;
const resultForExit = shouldExit
  ? (activeGameState.winner === activeUserId && activeGameState.exploreUnknownDarknessLoss !== true ? 'win' : 'loss')
  : null;

// Glowing cursor overlay: standard targeting (yellow), attack mode with eligible attacker (red), deployable unit from hand (green)
const baseBattleTargetingCursorActive =
  (deployTargetMode && selectedDeployOrderId) ||
  (strategemTargetMode && selectedStrategemId) ||
  (weaponTargetMode && selectedWeaponId) ||
  (weaponDeployTargetMode && weaponDeployTargetInfo) ||
  (droneTargetMode && selectedDroneId) ||
  (immobilizeTargetMode && selectedImmobilizeWeaponId) ||
  (confuseTargetMode && selectedConfuseWeaponId) ||
  euEpTargetMode ||
  euEpZoneTargetMode ||
  (specialTargetMode && selectedSpecialUnitId) ||
  (replaceTargetMode && selectedReplaceUnitId) ||
  (replaceDroneTargetMode && selectedReplaceDroneId);
const isTargetingCursorActive =
  baseBattleTargetingCursorActive ||
  isAttackPointerActive ||
  isDeployHandPointerActive;
const battleTargetingCursorVariant = baseBattleTargetingCursorActive
  ? 'target'
  : isAttackPointerActive
    ? 'attack'
    : isDeployHandPointerActive
      ? 'deploy'
      : 'target';

// Suppress drag-to-illuminate while picking targets (weapon deploy, strategems, attacks, etc.)
const battleFlashlightDisabled =
  isTargetingCursorActive ||
  effectiveWeaponTargetMode ||
  effectiveStrategemTargetMode ||
  effectiveDroneTargetMode ||
  euEpTargetMode ||
  euEpZoneTargetMode;

const passThroughExpandedBattlefieldPortals = passThroughExpandedBattlefieldPortalsForTargeting({
  deployTargetMode,
  selectedDeployOrderId,
  strategemTargetMode,
  selectedStrategemId,
  weaponTargetMode,
  selectedWeaponId,
  confuseTargetMode,
  selectedConfuseWeaponId,
  immobilizeTargetMode,
  selectedImmobilizeWeaponId,
  droneTargetMode,
  selectedDroneId,
  weaponDeployTargetMode,
  weaponDeployTargetInfo,
  replaceTargetMode,
  selectedReplaceUnitId,
  replaceDroneTargetMode,
  selectedReplaceDroneId,
  effectiveAttackMode,
  specialTargetMode,
  selectedSpecialUnitId,
  euEpZoneTargetMode,
  euEpTargetMode,
});

const IDLE_ZONE_OUTLINE_STYLES = { outline: 'none', cursor: 'default' };

const resolveBattlefieldZoneHighlightStyles = (zoneKey) => {
  const epStyles = getExploreUnknownEpZoneTargetingStyles(euEpZoneTargetMode);
  if (epStyles) return epStyles;
  const zoneStyles = getZoneTargetingStyles(
    effectiveStrategemTargetMode,
    effectiveStrategemTargetInfo,
    'zone',
    zoneKey
  );
  if (hasActiveZoneTargetingHighlight(zoneStyles)) return zoneStyles;
  // Suppress the default opponent-zone red outline unless yellow targeting is active.
  return IDLE_ZONE_OUTLINE_STYLES;
};

const isBattlefieldZoneHighlightActive = (zoneKey) => {
  if (euEpZoneTargetMode) return true;
  const zoneStyles = getZoneTargetingStyles(
    effectiveStrategemTargetMode,
    effectiveStrategemTargetInfo,
    'zone',
    zoneKey
  );
  return hasActiveZoneTargetingHighlight(zoneStyles);
};

const isExploreUnknownEpTargetHighlight = (unit, zone) => {
  if (!euEpTargetMode || !unit) return false;
  return isExploreUnknownEpTargetUnit({
    unit,
    zone,
    gameState: activeGameState,
    playerId: userId,
    targetingData,
  });
};

return (
  <>
    {/* Viewport Wrapper - creates the scaling container for responsive design */}
    <div className="viewport-wrapper" ref={viewportWrapperRef}>
      <TargetingCursor active={isTargetingCursorActive} variant={battleTargetingCursorVariant} />
      {/* Full-screen background - outside viewport-container to avoid letterboxing */}
      {/* Uses object-fit: cover to fill entire screen regardless of aspect ratio. */}
      {/* Memoized element + own GPU layer (CSS) prevents re-raster flashes on state updates. */}
      {battleBackgroundElement}
      <div 
        className="viewport-container"
        style={{
          width: `${containerBaseWidth}px`,
          height: `${containerBaseHeight}px`,
          transformOrigin: '0 0',
          transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
        }}
      >
    {/* Boss-mission Weapons Randomizer overlay (Explore Unknown mission 10) — renders nothing
        unless gameState.bossWeaponsRandomizerActive is true. Drives the rotate-and-spit
        animation from `eu-boss-weapons-randomizer-fired` action-log entries. */}
    <ExploreUnknownBossWeaponsRandomizerOverlay
      gameState={activeGameState}
      userId={activeUserId}
    />
    <ExploreUnknownCreationFireballOverlay gameState={activeGameState} userId={activeUserId} />
    <ExploreUnknownBossKangarooOverlay gameState={activeGameState} />
    <ExploreUnknownNegaraBossMimicIntro
      visible={exploreUnknownNegaraMimicToast.visible}
      translationKey={exploreUnknownNegaraMimicToast.translationKey}
    />
    {showNegaraPeacefulOutro ? (
      <ExploreUnknownNegaraOutro
        onComplete={() => {
          const nav = pendingNegaraAfterBattleNavRef.current;
          pendingNegaraAfterBattleNavRef.current = null;
          setShowNegaraPeacefulOutro(false);
          negaraPeacefulOutroActiveRef.current = false;
          negaraPeacefulBattleEndHandledRef.current = false;
          isTransitioningRef.current = false;
          exitingRef.current = false;
          if (!nav || typeof nav !== 'object') {
            throw new Error('BattleUI: Negara peaceful outro missing after-battle navigation state');
          }
          navigate('/after-battle', { replace: true, state: nav });
        }}
      />
    ) : null}

    {(euDarknessMeterFlash ||
      euDarkKnowledgeMeterFlash ||
      euHeartMeterFlash ||
      euEpMeterFlash ||
      euRewriteMeterFlash) &&
      createPortal(
        (() => {
          const centerAnchor = getExploreUnknownBattleDarknessFlashAnchorLogicalPx(
            containerBaseWidth,
            containerBaseHeight
          );
          return (
            <div
              className="battle-eu-darkness-flash-viewport-shell"
              style={{
                position: 'fixed',
                left: viewportOffsetX,
                top: viewportOffsetY,
                width: containerBaseWidth * viewportScale,
                height: containerBaseHeight * viewportScale,
                zIndex: 15060,
                pointerEvents: 'none',
                overflow: 'visible',
              }}
            >
              <div
                className="battle-eu-darkness-flash-logical-stage"
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: containerBaseWidth,
                  height: containerBaseHeight,
                  transform: `scale(${viewportScale})`,
                  transformOrigin: '0 0',
                  pointerEvents: 'none',
                  overflow: 'visible',
                }}
              >
                {euDarknessMeterFlash ||
                euDarkKnowledgeMeterFlash ||
                euHeartMeterFlash ||
                euEpMeterFlash ||
                euRewriteMeterFlash ? (
                  <div
                    className="battle-eu-explore-meters-stack"
                    style={{ left: centerAnchor.x, top: centerAnchor.y }}
                  >
                    {euDarknessMeterFlash ? (
                      <ExploreUnknownBattleDarknessFlash
                        key={euDarknessMeterFlash.key}
                        fromPct={euDarknessMeterFlash.fromPct}
                        toPct={euDarknessMeterFlash.toPct}
                        regionLabel={t('exploreUnknownPage.darknessMeterRegion')}
                        tooltipTitle={t('exploreUnknownPage.darknessMeterTooltipTitle', {
                          percent: Math.round(euDarknessMeterFlash.toPct),
                        })}
                      />
                    ) : null}
                    {euDarkKnowledgeMeterFlash ||
                    euHeartMeterFlash ||
                    euEpMeterFlash ||
                    euRewriteMeterFlash ? (
                      <div className="battle-eu-collectible-meters">
                        {euDarkKnowledgeMeterFlash ? (
                        <ExploreUnknownBattleDarkKnowledgeFlash
                          key={`dk_${euDarkKnowledgeMeterFlash.key}`}
                          fromParts={euDarkKnowledgeMeterFlash.fromParts}
                          toParts={euDarkKnowledgeMeterFlash.toParts}
                          targetParts={EXPLORE_UNKNOWN_DARK_KNOWLEDGE_TARGET_PARTS}
                          regionLabel={t('exploreUnknownBattle.darkKnowledgeMeterRegion')}
                          tooltipTitle={t('exploreUnknownBattle.darkKnowledgeMeterTooltipTitle', {
                            collected: euDarkKnowledgeMeterFlash.toParts,
                            target: EXPLORE_UNKNOWN_DARK_KNOWLEDGE_TARGET_PARTS,
                          })}
                          tooltipBody={t('exploreUnknownBattle.darkKnowledgeMeterFlashBody', {
                            target: EXPLORE_UNKNOWN_DARK_KNOWLEDGE_TARGET_PARTS,
                          })}
                        />
                        ) : null}
                        {euHeartMeterFlash
                        ? (() => {
                            const heartMeter = EXPLORE_UNKNOWN_HEART_METERS.find(
                              (m) => m.id === euHeartMeterFlash.heartId
                            );
                            if (!heartMeter) {
                              throw new Error(
                                `Explore Unknown: unknown heart meter flash id "${String(euHeartMeterFlash.heartId)}"`
                              );
                            }
                            const i18nPrefix = `${heartMeter.i18nKey}Meter`;
                            return (
                              <ExploreUnknownHeartProgressBar
                                key={`heart_${euHeartMeterFlash.key}`}
                                variant={heartMeter.variant}
                                imageSrc={heartMeter.image}
                                collected={euHeartMeterFlash.toParts}
                                fromParts={euHeartMeterFlash.fromParts}
                                toParts={euHeartMeterFlash.toParts}
                                targetParts={EXPLORE_UNKNOWN_HEART_TARGET_PARTS}
                                regionLabel={t(`exploreUnknownBattle.${i18nPrefix}Region`)}
                                tooltipTitle={t(`exploreUnknownBattle.${i18nPrefix}TooltipTitle`, {
                                  collected: euHeartMeterFlash.toParts,
                                  target: EXPLORE_UNKNOWN_HEART_TARGET_PARTS,
                                })}
                                tooltipBody={t(`exploreUnknownBattle.${i18nPrefix}FlashBody`, {
                                  target: EXPLORE_UNKNOWN_HEART_TARGET_PARTS,
                                  healthBonus: heartMeter.healthBonus,
                                })}
                                mode="battle-flash"
                              />
                            );
                          })()
                          : null}
                        {euEpMeterFlash
                          ? (() => {
                              const epMeter = EXPLORE_UNKNOWN_EP_METERS.find(
                                (m) => m.id === euEpMeterFlash.epId
                              );
                              if (!epMeter) {
                                throw new Error(
                                  `Explore Unknown: unknown EP meter flash id "${String(euEpMeterFlash.epId)}"`
                                );
                              }
                              const i18nPrefix = `${epMeter.i18nKey}Meter`;
                              return (
                                <ExploreUnknownHeartProgressBar
                                  key={`ep_${euEpMeterFlash.key}`}
                                  variant={epMeter.variant}
                                  imageSrc={epMeter.image}
                                  collected={euEpMeterFlash.toParts}
                                  fromParts={euEpMeterFlash.fromParts}
                                  toParts={euEpMeterFlash.toParts}
                                  targetParts={EXPLORE_UNKNOWN_EP_TARGET_PARTS}
                                  regionLabel={t(`exploreUnknownBattle.${i18nPrefix}Region`)}
                                  tooltipTitle={t(`exploreUnknownBattle.${i18nPrefix}TooltipTitle`, {
                                    collected: euEpMeterFlash.toParts,
                                    target: EXPLORE_UNKNOWN_EP_TARGET_PARTS,
                                  })}
                                  tooltipBody={t(`exploreUnknownBattle.${i18nPrefix}FlashBody`, {
                                    target: EXPLORE_UNKNOWN_EP_TARGET_PARTS,
                                  })}
                                  mode="battle-flash"
                                />
                              );
                            })()
                          : null}
                        {euRewriteMeterFlash ? (
                          <ExploreUnknownHeartProgressBar
                            key={`rewrite_${euRewriteMeterFlash.key}`}
                            variant={EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_METER.variant}
                            imageSrc={EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_METER.image}
                            collected={euRewriteMeterFlash.toParts}
                            fromParts={euRewriteMeterFlash.fromParts}
                            toParts={euRewriteMeterFlash.toParts}
                            targetParts={EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_TARGET_PARTS}
                            regionLabel={t('exploreUnknownBattle.rewriteExperienceMeterRegion')}
                            tooltipTitle={t('exploreUnknownBattle.rewriteExperienceMeterTooltipTitle', {
                              collected: euRewriteMeterFlash.toParts,
                              target: EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_TARGET_PARTS,
                            })}
                            tooltipBody={t('exploreUnknownBattle.rewriteExperienceMeterFlashBody', {
                              target: EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_TARGET_PARTS,
                              darknessPercent: EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_LOSS_DARKNESS_INCREMENT_PERCENT,
                            })}
                            mode="battle-flash"
                          />
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })(),
        document.body
      )}

    {activeGameState?.exploreUnknownMissionIndex != null &&
      !activeGameState?.winner &&
      !isSpectating &&
      createPortal(
        (() => {
          const epUnified = activeGameState?.exploreUnknownLevelBonuses?.epUnified === true;
          const epChargeEntries = EXPLORE_UNKNOWN_EP_METERS.map((meter) => {
            const chargesRaw = activeGameState[meter.gameStateChargesKey];
            const charges = Number(chargesRaw);
            if (!Number.isInteger(charges) || charges < 0) {
              throw new Error(
                `Explore Unknown: ${meter.gameStateChargesKey} must be a non-negative integer (${String(chargesRaw)})`
              );
            }
            return { meter, charges };
          }).filter((entry) => entry.charges >= 1);

          const activateEpSpend = (meter) => {
            if (!yourTurn) {
              setError(t('battleUI.notYourTurn', "It's not your turn"));
              return;
            }
            setEuEpUnifiedSpendPickerOpen(false);
            if (meter.id === 'epGlobal') {
              sendExploreUnknownEpSpend(meter.spendActionType);
            } else if (meter.id === 'epZone') {
              setEuEpTargetMode(false);
              setEuEpZoneTargetMode(true);
              setError('');
            } else {
              setEuEpZoneTargetMode(false);
              setEuEpTargetMode(true);
              setError('');
            }
          };

          if (epChargeEntries.length === 0) return null;

          if (epUnified) {
            const totalCharges = epChargeEntries.reduce((sum, entry) => sum + entry.charges, 0);
            return (
              <>
                <div
                  className="battle-eu-ep-charge-stack"
                  aria-label={t('exploreUnknownBattle.epChargeStackRegion')}
                >
                  <button
                    type="button"
                    className="battle-eu-ep-charge-btn battle-eu-ep-charge-btn--global battle-eu-ep-charge-btn--unified"
                    onClick={() => {
                      if (!yourTurn) {
                        setError(t('battleUI.notYourTurn', "It's not your turn"));
                        return;
                      }
                      setEuEpUnifiedSpendPickerOpen(true);
                    }}
                    aria-label={t('exploreUnknownBattle.epUnifiedMeterChargeButton', {
                      count: totalCharges,
                    })}
                  >
                    <img
                      src="/images/ep_board.jpg"
                      alt=""
                      className="battle-eu-ep-charge-btn-image"
                    />
                    <span className="battle-eu-ep-charge-btn-count">{totalCharges}</span>
                    <div className="battle-eu-ep-charge-tooltip" role="tooltip" aria-hidden="true">
                      <p className="battle-eu-ep-charge-tooltip-title">
                        {t('exploreUnknownBattle.epUnifiedMeterChargeButton', {
                          count: totalCharges,
                        })}
                      </p>
                      <p className="battle-eu-ep-charge-tooltip-body">
                        {t('exploreUnknownBattle.epUnifiedSpendPickerTitle')}
                      </p>
                    </div>
                  </button>
                </div>
                {euEpUnifiedSpendPickerOpen ? (
                  <div
                    className="battle-eu-ep-unified-spend-backdrop"
                    role="presentation"
                    onClick={() => setEuEpUnifiedSpendPickerOpen(false)}
                  >
                    <div
                      className="battle-eu-ep-unified-spend-panel"
                      role="dialog"
                      aria-modal="true"
                      aria-label={t('exploreUnknownBattle.epUnifiedSpendPickerTitle')}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <p className="battle-eu-ep-unified-spend-title">
                        {t('exploreUnknownBattle.epUnifiedSpendPickerTitle')}
                      </p>
                      <div className="battle-eu-ep-unified-spend-options">
                        {epChargeEntries.map(({ meter, charges }) => {
                          const i18nPrefix = `${meter.i18nKey}Meter`;
                          return (
                            <button
                              key={meter.id}
                              type="button"
                              className={`battle-eu-ep-unified-spend-option battle-eu-ep-unified-spend-option--${meter.variant}`}
                              onClick={() => activateEpSpend(meter)}
                            >
                              <img src={meter.image} alt="" />
                              <span>{t(`exploreUnknownBattle.${i18nPrefix}SpendOption`)}</span>
                              <span className="battle-eu-ep-unified-spend-option-count">{charges}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : null}
              </>
            );
          }

          return (
            <div className="battle-eu-ep-charge-stack" aria-label={t('exploreUnknownBattle.epChargeStackRegion')}>
              {epChargeEntries.map(({ meter, charges }) => {
                const i18nPrefix = `${meter.i18nKey}Meter`;
                return (
                  <button
                    key={meter.id}
                    type="button"
                    className={`battle-eu-ep-charge-btn battle-eu-ep-charge-btn--${meter.variant}`}
                    onClick={() => activateEpSpend(meter)}
                    aria-label={t(`exploreUnknownBattle.${i18nPrefix}ChargeButton`, { count: charges })}
                  >
                    <img src={meter.image} alt="" className="battle-eu-ep-charge-btn-image" />
                    <span className="battle-eu-ep-charge-btn-count">{charges}</span>
                    <div className="battle-eu-ep-charge-tooltip" role="tooltip" aria-hidden="true">
                      <p className="battle-eu-ep-charge-tooltip-title">
                        {t(`exploreUnknownBattle.${i18nPrefix}ChargeButton`, { count: charges })}
                      </p>
                      <p className="battle-eu-ep-charge-tooltip-body">
                        {t(`exploreUnknownBattle.${i18nPrefix}FlashBody`, {
                          target: EXPLORE_UNKNOWN_EP_TARGET_PARTS,
                        })}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          );
        })(),
        document.body
      )}

    {euEpSetCompleteModal ? (
      <ExploreUnknownEpSetCompleteModal
        popupId={euEpSetCompleteModal.popupId}
        variant={euEpSetCompleteModal.variant}
        i18nKey={euEpSetCompleteModal.i18nKey}
        imageSrc={euEpSetCompleteModal.imageSrc}
        newCharges={euEpSetCompleteModal.newCharges}
        upgradedFromTarget={euEpSetCompleteModal.upgradedFromTarget === true}
        onDismiss={() => {
          if (euEpSetCompleteModal?.popupId) {
            euDismissedEpSetCompletePopupsRef.current.add(euEpSetCompleteModal.popupId);
          }
          setEuEpSetCompleteModal(null);
        }}
      />
    ) : null}

    {euRewriteSetCompleteModal ? (
      <ExploreUnknownRewriteSetCompleteModal
        popupId={euRewriteSetCompleteModal.popupId}
        newCharges={euRewriteSetCompleteModal.newCharges}
        onDismiss={() => {
          if (euRewriteSetCompleteModal?.popupId) {
            euDismissedRewriteSetCompletePopupsRef.current.add(euRewriteSetCompleteModal.popupId);
          }
          setEuRewriteSetCompleteModal(null);
        }}
      />
    ) : null}

    {isPlayer1 &&
      activeGameState?.exploreUnknownMissionIndex != null &&
      !activeGameState?.winner &&
      createPortal(
        <div
          className="explore-unknown-player1-dev-spawns"
          style={{
            position: 'fixed',
            top: 12,
            right: 12,
            zIndex: 15090,
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            maxWidth: 'min(420px, 96vw)',
            pointerEvents: 'auto',
          }}
        >
          <button
            type="button"
            onClick={() => sendExploreUnknownDevSpawn('devSpawnExploreUnknownMemory')}
            style={{
              padding: '6px 10px',
              fontSize: 12,
              fontWeight: 600,
              color: '#1a1a1a',
              background: 'rgba(255, 200, 120, 0.95)',
              border: '1px solid rgba(180, 90, 0, 0.9)',
              borderRadius: 6,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
            }}
          >
            spawn memory
          </button>
          <button
            type="button"
            onClick={() => sendExploreUnknownDevSpawn('devSpawnExploreUnknownDarkKnowledge')}
            style={{
              padding: '6px 10px',
              fontSize: 12,
              fontWeight: 600,
              color: '#f5f5f5',
              background: 'rgba(40, 20, 60, 0.95)',
              border: '1px solid rgba(200, 140, 255, 0.75)',
              borderRadius: 6,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.45)',
            }}
          >
            spawn dark knowledge
          </button>
          <button
            type="button"
            onClick={() => sendExploreUnknownDevSpawn('devSpawnExploreUnknownWeakHeart')}
            style={{
              padding: '6px 10px',
              fontSize: 12,
              fontWeight: 600,
              color: '#4a1020',
              background: 'rgba(255, 180, 200, 0.95)',
              border: '1px solid rgba(180, 60, 90, 0.85)',
              borderRadius: 6,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
            }}
          >
            spawn weak heart
          </button>
          <button
            type="button"
            onClick={() => sendExploreUnknownDevSpawn('devSpawnExploreUnknownHeart')}
            style={{
              padding: '6px 10px',
              fontSize: 12,
              fontWeight: 600,
              color: '#fff',
              background: 'rgba(200, 40, 70, 0.95)',
              border: '1px solid rgba(255, 120, 150, 0.85)',
              borderRadius: 6,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
            }}
          >
            spawn heart
          </button>
          <button
            type="button"
            onClick={() => sendExploreUnknownDevSpawn('devSpawnExploreUnknownStrongHeart')}
            style={{
              padding: '6px 10px',
              fontSize: 12,
              fontWeight: 600,
              color: '#fff8f0',
              background: 'rgba(120, 10, 35, 0.95)',
              border: '1px solid rgba(255, 80, 120, 0.9)',
              borderRadius: 6,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.45)',
            }}
          >
            spawn strong heart
          </button>
          <button
            type="button"
            onClick={() =>
              sendExploreUnknownDevSpawn(EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_METER.devSpawnActionType)
            }
            style={{
              padding: '6px 10px',
              fontSize: 12,
              fontWeight: 600,
              color: '#f0e8ff',
              background: 'rgba(55, 25, 95, 0.95)',
              border: '1px solid rgba(180, 140, 255, 0.85)',
              borderRadius: 6,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
            }}
          >
            spawn rewrite
          </button>
          {EXPLORE_UNKNOWN_EP_METERS.map((meter) => (
            <button
              key={meter.devSpawnActionType}
              type="button"
              onClick={() => sendExploreUnknownDevSpawn(meter.devSpawnActionType)}
              style={{
                padding: '6px 10px',
                fontSize: 12,
                fontWeight: 600,
                color: '#e8f4ff',
                background: 'rgba(20, 50, 90, 0.95)',
                border: '1px solid rgba(100, 180, 255, 0.85)',
                borderRadius: 6,
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
              }}
            >
              spawn {meter.i18nKey}
            </button>
          ))}
        </div>,
        document.body
      )}

    {euDarkKnowledgeRewardModal ? (
      <ExploreUnknownDarkKnowledgeRewardModal
        popupId={euDarkKnowledgeRewardModal.popupId}
        rewardType={euDarkKnowledgeRewardModal.rewardType}
        orderId={euDarkKnowledgeRewardModal.orderId}
        amount={euDarkKnowledgeRewardModal.amount}
        onDismiss={() => {
          if (euDarkKnowledgeRewardModal?.popupId) {
            euDismissedDarkKnowledgeRewardPopupsRef.current.add(euDarkKnowledgeRewardModal.popupId);
          }
          setEuDarkKnowledgeRewardModal(null);
        }}
      />
    ) : null}

    {euHeartSetCompleteModal ? (
      <ExploreUnknownHeartSetCompleteModal
        popupId={euHeartSetCompleteModal.popupId}
        variant={euHeartSetCompleteModal.variant}
        i18nKey={euHeartSetCompleteModal.i18nKey}
        imageSrc={euHeartSetCompleteModal.imageSrc}
        healthBonus={euHeartSetCompleteModal.healthBonus}
        darknessReductionPercent={euHeartSetCompleteModal.darknessReductionPercent}
        onDismiss={() => {
          if (euHeartSetCompleteModal?.popupId) {
            euDismissedHeartSetCompletePopupsRef.current.add(euHeartSetCompleteModal.popupId);
          }
          setEuHeartSetCompleteModal(null);
        }}
      />
    ) : null}
    
    <div 
      className={`battle-ui-background battle-ui-suppress-native-drag-select${isSpectating ? ' spectator-mode' : ''}`}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: `${containerBaseWidth}px`,
        height: `${containerBaseHeight}px`,
        /* Background image moved to fullscreen-background outside viewport-container */
        /* This div now just contains the game content overlays */
      }}
    >
    <div style={{ padding: 24 }}>
      {/* ✅ FIX: Disable Navigate component during transition - transition handles navigation */}
      {shouldExit && !battleEndTransition.isTransitioning && (
        <Navigate to={`/after-battle${resultForExit ? `?result=${resultForExit}` : ''}`} replace state={resultForExit ? { result: resultForExit, gameId, isWarEffort: !!(activeGameState?.warEffortMissionId || activeGameState?.isWarEffort), isCampaign: !!(activeGameState?.campaignMode && activeGameState?.missionId), missionId: activeGameState?.missionId, gameState: activeGameState, exploreUnknownRun: activeGameState?.exploreUnknownMissionIndex != null, trainPvE: isTrainPvEBattle(activeGameState) } : { gameId }} />
      )}

      {/* Spectator Stop Watching Button */}
      {isSpectating && (
        <button
          className="spectator-control-button"
          onClick={stopSpectating}
          style={{
            position: "fixed",
            top: "10px",
            left: "10px",
            background: "rgba(239, 68, 68, 0.9)",
            border: "1px solid rgba(255,255,255,0.4)",
            color: "white",
            padding: "8px 16px",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "500",
            pointerEvents: "auto",
            zIndex: 10001,
            boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
          }}
        >
          Stop Watching
        </button>
      )}

      {/* User Info (+ mobile battle log inline to the right of the player name) */}
      {user && !isSpectating && (
        <div 
          className={isMobile ? "user-info-mobile-hidden" : ""} 
          style={{ 
            position: "absolute", 
            top: isContainerMobile ? 5 : 10, 
            left: isContainerMobile ? 5 : 10, 
            zIndex: 1000, 
            display: "flex", 
            alignItems: "center",
            gap: isContainerMobile ? 4 : 0,
            maxWidth: isContainerMobile ? 'calc(100vw - 10px)' : undefined,
            transform: isContainerMobile ? "scale(0.8)" : "none",
            transformOrigin: "top left"
          }}
        >
          <FriendsIcon />
          <SettingsMenu
            onLogout={onLogout}
            onResign={isContainerMobile ? handleResign : null}
          />
          <span
            style={{
              color: "white",
              marginRight: isContainerMobile ? 0 : "10px",
              ...(isContainerMobile
                ? {
                    flex: '1 1 auto',
                    minWidth: 0,
                    maxWidth: 96,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }
                : {}),
            }}
          >
            {typeof user === 'string' ? user : (user?.username ?? '')}
          </span>
          {isContainerMobile && activeGameState ? (
            <BattleActionLog
              actionLog={actionLog}
              gameState={activeGameState}
              userId={activeUserId}
              opponentId={activeGameState.players?.find((id) => String(id) !== String(activeUserId))}
              currentUsername={typeof user === 'string' ? user : (user?.username ?? '')}
              opponentUsername={effectiveOpponentUsername ?? ''}
              isMobile
              inlineInUserRow
              fixedDesktopExpandedPosition={fixedDesktopExpandedPosition}
              orderDesignScale={viewportScale}
              unitRegistryRef={unitRegistryRef}
            />
          ) : null}
        </div>
        
      )}

      {/* Action log: desktop absolute slot; mobile when spectating / no user row */}
      {activeGameState && !(isContainerMobile && user && !isSpectating) && (
        <BattleActionLog
          actionLog={actionLog}
          gameState={activeGameState}
          userId={activeUserId}
          opponentId={activeGameState.players?.find((id) => String(id) !== String(activeUserId))}
          currentUsername={typeof user === 'string' ? user : (user?.username ?? '')}
          opponentUsername={effectiveOpponentUsername ?? ''}
          isMobile={isContainerMobile}
          fixedDesktopExpandedPosition={fixedDesktopExpandedPosition}
          orderDesignScale={viewportScale}
          unitRegistryRef={unitRegistryRef}
        />
      )}
      

      {/* State Synchronization Monitor - Development Tool */}
      {(process.env.NODE_ENV === 'development' && localStorage.getItem('enableStateSyncMonitor') === 'true') && (
        <>
          <StateSyncToggle onToggle={setStateSyncMonitorEnabled} />
          <StateSyncMonitor 
            gameState={gameState}
            userId={userId}
            ws={ws}
            isVisible={stateSyncMonitorEnabled}
          />
        </>
      )}


      {/* Error Messages - Lower Right Corner */}
      {/* Desktop: lower-right, Mobile: scaled and repositioned */}
      {(error || (immobilizeTargetMode && selectedImmobilizeWeaponId) || (confuseTargetMode && selectedConfuseWeaponId) || (specialTargetMode && selectedSpecialUnitId)) && (
        <div className="battle-notification" style={{
          position: "absolute",
          bottom: isContainerMobile ? 5 : 20,
          right: isContainerMobile ? 50 : 140,
          zIndex: 1000,
          transform: isContainerMobile ? "scale(0.6)" : "none",
          transformOrigin: "bottom right",
          backgroundColor: "rgba(0,0,0,0.8)",
          color: error ? "#ff4444" : "#4a90e2",
          padding: "8px 12px",
          borderRadius: "6px",
          fontSize: "14px",
          fontWeight: "bold",
          maxWidth: "300px",
          textAlign: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
          border: error ? "2px solid #ff4444" : "2px solid #4a90e2"
        }}>
          {error && <div>{error}</div>}
          {immobilizeTargetMode && selectedImmobilizeWeaponId && !error && (
            <div>
              {t('battleUI.immobilizeTargetingMode')}
              <button 
                onClick={() => {
                  setImmobilizeTargetMode(false);
                  setSelectedImmobilizeWeaponId(null);
                }}
                style={{
                  marginLeft: '10px',
                  padding: '5px 10px',
                  backgroundColor: '#ff4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: 'pointer'
                }}
              >
                {t('battleUI.cancelTargeting')}
              </button>
            </div>
          )}
          {confuseTargetMode && selectedConfuseWeaponId && !error && (
            <div>
              {t('battleUI.confuseTargetingMode')}
              <button 
                onClick={() => {
                  setConfuseTargetMode(false);
                  setSelectedConfuseWeaponId(null);
                }}
                style={{
                  marginLeft: '10px',
                  padding: '5px 10px',
                  backgroundColor: '#ff4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: 'pointer'
                }}
              >
                {t('battleUI.cancelTargeting')}
              </button>
            </div>
          )}
          {specialTargetMode && selectedSpecialUnitId && !error && (
            <div>
              {t('battleUI.specialTargetingMode')}
              <button 
                onClick={() => {
                  setSpecialTargetMode(false);
                  setSelectedSpecialUnitId(null);
                }}
                style={{
                  marginLeft: '10px',
                  padding: '5px 10px',
                  backgroundColor: '#ff4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: 'pointer'
                }}
              >
                {t('battleUI.cancelTargeting')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Desktop: Player strip (general + advisor docked above resign/end turn). Mobile: floating buttons. */}
      {!isContainerMobile ? (
        <div className="player-bottom-strip" style={{
          position: "absolute",
          bottom: 20,
          left: 20,
          right: 20,
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: 12,
          pointerEvents: "none"
        }}>
          <div className="player-buttons-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", pointerEvents: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <button
                onClick={handleResign}
                className="resign-button"
                style={{ margin: 0, position: "relative" }}
              >
                {t('battleUI.resign')}
              </button>
              {isPlayer1 && !isSpectating && !isAutobattler && !isAutobattlerSpectator && !gameState?.winner && showDevAutowinButton ? (
                <button
                  type="button"
                  onClick={handleDevAutowin}
                  style={{
                    margin: 0,
                    position: "relative",
                    padding: "8px 14px",
                    backgroundColor: "#1b5e20",
                    color: "#fff",
                    border: "1px solid #66bb6a",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "inherit",
                    fontFamily: "inherit",
                  }}
                >
                  {t('battleUI.autowin')}
                </button>
              ) : null}
            </div>
            <button
              onClick={endTurn}
              disabled={(() => {
          // ✅ AUTOBATTLER: Disable if in autobattler mode
          if (isAutobattler || isAutobattlerSpectator) {
            return true;
          }
          // ✅ SEQUENTIAL ATTACKS: Disable button if:
          // 1. Not player's turn
          // 2. It's the bot's turn
          // 3. There are pending attack animations
          const isBotTurn = gameState?.turn && (
            gameState.turn === 'bot' || 
            gameState.turn === 'campaign_bot' ||
            (typeof gameState.turn === 'string' && gameState.turn.startsWith('bot'))
          );
          // attackAnimations.pendingAttacks is an array of [attackId, attackData] tuples
          const hasPendingAnimations = attackAnimations && attackAnimations.pendingAttacks && attackAnimations.pendingAttacks.length > 0;
          const hasAttackInProgress = attackInProgressRef.current;
          const hasRedeemDeathSequence = redeemDeathSequence.isSequenceActive;
          
          const shouldDisable = !yourTurn || isBotTurn || hasPendingAnimations || hasAttackInProgress || hasRedeemDeathSequence || opponentHasCounterStrike;
          if (shouldDisable && (isBotTurn || hasPendingAnimations || hasAttackInProgress)) {
          }
          return shouldDisable;
        })()}
        className="end-turn-button"
        style={{
          position: "relative",
          margin: 0,
          opacity: (() => {
            const isBotTurn = gameState?.turn && (
              gameState.turn === 'bot' || gameState.turn === 'campaign_bot' ||
              (typeof gameState.turn === 'string' && gameState.turn.startsWith('bot'))
            );
            const hasPendingAnimations = attackAnimations?.pendingAttacks?.length > 0;
            const hasAttackInProgress = attackInProgressRef.current;
            const hasRedeemDeathSequence = redeemDeathSequence.isSequenceActive;
            const shouldBeEnabled = yourTurn && !isBotTurn && !hasPendingAnimations && !hasAttackInProgress && !hasRedeemDeathSequence && !opponentHasCounterStrike;
            return shouldBeEnabled ? 1 : 0.5;
          })(),
          pointerEvents: (() => {
            const isBotTurn = gameState?.turn && (
              gameState.turn === 'bot' || gameState.turn === 'campaign_bot' ||
              (typeof gameState.turn === 'string' && gameState.turn.startsWith('bot'))
            );
            const hasPendingAnimations = attackAnimations?.pendingAttacks?.length > 0;
            const hasAttackInProgress = attackInProgressRef.current;
            const hasRedeemDeathSequence = redeemDeathSequence.isSequenceActive;
            const shouldBeEnabled = yourTurn && !isBotTurn && !hasPendingAnimations && !hasAttackInProgress && !hasRedeemDeathSequence && !opponentHasCounterStrike;
            return shouldBeEnabled ? "auto" : "none";
          })(),
          border: (() => {
            const hasAttackableUnits = yourFront.some(unit => unit && unit.canAttackThisTurn === true) || yourFlank.some(unit => unit && unit.canAttackThisTurn === true);
            const shouldPass = !hasAffordablePlayableHandOrder && !hasAttackableUnits;
            return shouldPass ? "2px solid #ffd700" : "2px solid #555555";
          })(),
          boxShadow: (() => {
            const hasAttackableUnits = yourFront.some(unit => unit && unit.canAttackThisTurn === true) || yourFlank.some(unit => unit && unit.canAttackThisTurn === true);
            const shouldPass = !hasAffordablePlayableHandOrder && !hasAttackableUnits;
            return shouldPass ? "0 0 10px #ffd700, 0 0 20px #ffd700" : "0 2px 8px rgba(0,0,0,0.3)";
          })()
        }}
      >
        {t('battleUI.endTurn')}
      </button>
          </div>
        </div>
      ) : (
        <>
      <button
        onClick={endTurn}
        disabled={(() => {
          if (isAutobattler || isAutobattlerSpectator) return true;
          const isBotTurn = gameState?.turn && (gameState.turn === 'bot' || gameState.turn === 'campaign_bot' || (typeof gameState.turn === 'string' && gameState.turn.startsWith('bot')));
          const hasPendingAnimations = attackAnimations?.pendingAttacks?.length > 0;
          const hasAttackInProgress = attackInProgressRef.current;
          const hasRedeemDeathSequence = redeemDeathSequence.isSequenceActive;
          return !yourTurn || isBotTurn || hasPendingAnimations || hasAttackInProgress || hasRedeemDeathSequence || opponentHasCounterStrike;
        })()}
        className="end-turn-button"
        style={{
          position: "absolute",
          bottom: 5,
          right: 5,
          zIndex: 1000,
          margin: 0,
          opacity: yourTurn && !(gameState?.turn && (gameState.turn === 'bot' || gameState.turn === 'campaign_bot' || (typeof gameState.turn === 'string' && gameState.turn.startsWith('bot')))) && !(attackAnimations?.pendingAttacks?.length > 0) && !attackInProgressRef.current && !redeemDeathSequence.isSequenceActive && !opponentHasCounterStrike ? 1 : 0.5,
          pointerEvents: yourTurn && !(gameState?.turn && (gameState.turn === 'bot' || gameState.turn === 'campaign_bot' || (typeof gameState.turn === 'string' && gameState.turn.startsWith('bot')))) && !(attackAnimations?.pendingAttacks?.length > 0) && !attackInProgressRef.current && !redeemDeathSequence.isSequenceActive && !opponentHasCounterStrike ? "auto" : "none",
          padding: "6px 12px",
          fontSize: "10px",
          minWidth: "60px",
          minHeight: "28px"
        }}
      >
        {t('battleUI.endTurn')}
      </button>
        </>
      )}

      {/* Mobile Player HUD — desktop PlayerHUD scaled for bottom-left (outside battlefield) */}
      {isContainerMobile && you && (
        <BattlePlayerHudPanel {...playerHudPanelProps} mobileLayout />
      )}

      {isContainerMobile && currentEmoteBubble && (
        <BattlePlayerEmoteBubble bubble={currentEmoteBubble} t={t} />
      )}

      {showEmoteButtons && (
        <BattleEmotePicker
          isMobile={isContainerMobile}
          emotesLoading={emotesLoading}
          emotesError={emotesError}
          playerEmotes={playerEmotes}
          playerEmoteSelections={playerEmoteSelections}
          onRetryFetchEmotes={() => {
            setEmotesError(null);
            fetchEmotes();
          }}
          t={t}
          ws={ws}
          gameId={gameId}
          setShowEmoteButtons={setShowEmoteButtons}
          setCurrentEmoteBubble={setCurrentEmoteBubble}
          onPickerInteraction={() => {
            if (hoverTimeoutRef.current) {
              clearTimeout(hoverTimeoutRef.current);
              hoverTimeoutRef.current = null;
            }
            setHoveredIndex(null);
          }}
        />
      )}

      {/* Mobile Player Advisor - shown only for tutorial/campaign */}
      {isContainerMobile && showPlayerAdvisor && (
        <MobilePlayerAdvisor
          you={you}
          playerDamageFlash={playerDamageFlash}
          selectedAdvisor={selectedAdvisor}
          avatars={avatars}
          getAvatarImage={getAvatarImage}
          shouldMirrorAvatar={shouldMirrorAvatar}
          activeGameState={activeGameState}
          activeUserId={activeUserId}
          resolveBattleTitleDisplay={resolveBattleTitleDisplay}
        />
      )}

      {/* Mobile Opponent HUD — desktop OpponentHUD scaled for top-center (outside battlefield) */}
      {isContainerMobile && (
        <div
          className="opponent-avatars-container opponent-avatars-container--mobile"
          data-no-battle-flashlight="true"
          style={{ '--mobile-opponent-hud-top': `${MOBILE_OPPONENT_AVATARS_TOP_PX}px` }}
        >
          <BattleOpponentHudPanel {...opponentHudPanelProps} mobileLayout />
        </div>
      )}

      {/* Mobile Opponent Drones - Outside battlefield-and-carousel, top right */}
      {isContainerMobile && (
        <MobileOpponentDrones opponent={opponent} />
      )}

      {/* Mobile Opponent O/S/C — upper-right below drones (matches desktop opponent-stats-counters) */}
      {isContainerMobile && (
        <MobileOpponentStats opponent={opponent} />
      )}

      {/* Mobile Player Drones — mirrors desktop player-drone-zone (outside battlefield) */}
      {isContainerMobile && (
        <MobilePlayerDrones
          you={you}
          gameState={gameState}
          yourTurn={yourTurn}
          deployTargetMode={deployTargetMode}
          selectedDeployOrderId={selectedDeployOrderId}
          handleTargetDeploy={handleTargetDeploy}
          replaceDroneTargetMode={replaceDroneTargetMode}
          selectedReplaceDroneInstanceId={selectedReplaceDroneInstanceId}
          setClickedDroneForReplace={setClickedDroneForReplace}
          clickedDroneForReplace={clickedDroneForReplace}
          selectedOrderInstanceId={selectedOrderInstanceId}
          setReplaceDroneTargetMode={setReplaceDroneTargetMode}
          setSelectedReplaceDroneId={setSelectedReplaceDroneId}
          selectedOrderId={selectedOrderId}
          setSelectedReplaceDroneInstanceId={setSelectedReplaceDroneInstanceId}
          activateDrone={activateDrone}
          playDrone={playDrone}
          handleReplaceDrone={handleReplaceDrone}
          handleCancelReplaceDrone={handleCancelReplaceDrone}
        />
      )}

      {/* Resign Confirmation Dialog */}
      {/* Uses oversized background to cover full screen including areas beyond viewport-container */}
      {showResignDialog && (
        <div
          role="presentation"
          data-resign-dialog-overlay
          style={{
          position: "absolute",
          top: "-100%",
          left: "-100%",
          width: "300%",
          height: "300%",
          backgroundColor: "rgba(0,0,0,0.7)",
          zIndex: 2000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
        onClick={cancelResign}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="resign-dialog-title"
            style={{
            backgroundColor: "#333",
            padding: isContainerMobile ? "16px" : "24px",
            borderRadius: "12px",
            border: "2px solid #ff4444",
            textAlign: "center",
            maxWidth: isContainerMobile ? "300px" : "400px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.5)",
            transform: isContainerMobile ? "scale(0.9)" : "none"
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <h3 id="resign-dialog-title" style={{ color: "#ff4444", marginBottom: isContainerMobile ? "12px" : "16px", fontSize: isContainerMobile ? "16px" : "inherit" }}>{t('battleUI.resignConfirm')}</h3>
            <p style={{ color: "white", marginBottom: isContainerMobile ? "16px" : "24px", fontSize: isContainerMobile ? "14px" : "inherit" }}>
              {t('battleUI.resignMessage')}
            </p>
            <div style={{ display: "flex", gap: isContainerMobile ? "8px" : "12px", justifyContent: "center" }}>
              <button
                type="button"
                ref={resignDialogConfirmRef}
                tabIndex={0}
                onClick={confirmResign}
                style={{
                  backgroundColor: "#ff4444",
                  color: "white",
                  border: "none",
                  padding: isContainerMobile ? "8px 16px" : "10px 20px",
                  borderRadius: "6px",
                  fontSize: isContainerMobile ? "12px" : "14px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: resignDialogFocus === 'confirm' ? '0 0 0 3px rgba(255, 255, 255, 0.95)' : 'none',
                }}
                onMouseEnter={(e) => {
                  setResignDialogFocus('confirm');
                  e.target.style.backgroundColor = "#ff6666";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#ff4444";
                }}
              >
                {t('battleUI.yesResign')}
              </button>
              <button
                type="button"
                ref={resignDialogCancelRef}
                tabIndex={0}
                onClick={cancelResign}
                style={{
                  backgroundColor: "#666",
                  color: "white",
                  border: "none",
                  padding: isContainerMobile ? "8px 16px" : "10px 20px",
                  borderRadius: "6px",
                  fontSize: isContainerMobile ? "12px" : "14px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: resignDialogFocus === 'cancel' ? '0 0 0 3px rgba(255, 255, 255, 0.95)' : 'none',
                }}
                onMouseEnter={(e) => {
                  setResignDialogFocus('cancel');
                  e.target.style.backgroundColor = "#888";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#666";
                }}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

     
      {/* ✅ HIDDEN: Winner message removed - transition animation shows the result instead */}
      {/* {activeGameState?.winner && (
        <div style={{ marginTop: 24, fontWeight: "bold", color: "#0c0" }}>
          {t('battleUI.winner')} {activeGameState.winner === activeUserId ? t('battleUI.you') : t('battleUI.opponent')}
        </div>
      )} */}


      {/* Temporary Message Display (e.g., "feeling tired") */}
      {temporaryMessage === 'feelingTired' && (
        <div style={{
          position: "fixed",
          top: "80px",
          left: "50%",
          transform: "translateX(-50%)",
          backgroundColor: "rgba(255, 107, 53, 0.95)",
          color: "white",
          padding: "12px 24px",
          borderRadius: "8px",
          zIndex: 1500,
          textAlign: "center",
          boxShadow: "0 4px 16px rgba(0,0,0,0.5)",
          fontSize: "18px",
          fontWeight: "bold",
          animation: "fadeInOut 3s ease-in-out"
        }}>
          {t('battleUI.feelingTired')}
        </div>
      )}

      {/* Strategem Counter Strike Orders */}
      {you?.pendingStrategemCounterStrike?.length > 0 && (
        <div style={{ 
          position: "fixed", 
          top: "50%", 
          left: "50%", 
          transform: "translate(-50%, -50%)", 
          backgroundColor: "#333", 
          border: "3px solid #ff6b35", 
          borderRadius: "12px", 
          padding: "20px", 
          zIndex: 2000,
          textAlign: "center",
          boxShadow: "0 4px 16px rgba(0,0,0,0.5)"
        }}>
          <h3 style={{ color: "#ff6b35", marginBottom: "16px" }}>{t('battleUI.strategemCounterStrike')}</h3>
          <p style={{ color: "white", marginBottom: "20px" }}>
            {t('battleUI.strategemCounterStrikeMessage')}
          </p>
          <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
            {you.pendingStrategemCounterStrike.map((strategem, idx) => (
              <div key={strategem.id} style={{ marginBottom: "12px" }}>
                <div style={{ color: "white", marginBottom: "8px", fontWeight: "bold" }}>
                  {strategem.name}
                </div>
                <div style={{ color: "#ccc", marginBottom: "12px", fontSize: "12px" }}>
                  {strategem.description || strategem.effect || t('battleUI.strategemEffect')}
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => executeStrategemCounterStrike(idx)}
                    style={{
                      backgroundColor: "#ff6b35",
                      color: "white",
                      border: "none",
                      padding: "10px 20px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "bold",
                      transition: "background-color 0.2s"
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = "#e55a2b"}
                    onMouseLeave={(e) => e.target.style.backgroundColor = "#ff6b35"}
                  >
                    {t('battleUI.execute')}
                  </button>
                  <button
                    onClick={() => keepStrategemCounterStrike(idx)}
                    style={{
                      backgroundColor: "#4CAF50",
                      color: "white",
                      border: "none",
                      padding: "10px 20px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "bold",
                      transition: "background-color 0.2s"
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = "#45a049"}
                    onMouseLeave={(e) => e.target.style.backgroundColor = "#4CAF50"}
                  >
                    {t('battleUI.keep')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Battlefield Layout */}



      <div className="battlefield-and-carousel" style={{ 
        ...(isContainerMobile ? {
          position: 'absolute', // Match black background positioning
          top: '65px',
          bottom: '135px',
          left: '-24px', // Match black background left (extend beyond parent)
          width: 'calc(100% + 48px)', // Match black background width (extend beyond parent)
          marginTop: 0,
          marginBottom: 0,
          marginLeft: 0,
          marginRight: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: 'auto', // Will be determined by top/bottom positioning
          zIndex: 1 // Above black background
          // MOBILE FIX: Removed scale(0.48) - was restricting zones from filling width
        } : {
          marginTop: 100,
          marginBottom: 32,
          width: "100%"
        }),
        padding: "0",
        boxSizing: "border-box"
        // MOBILE FIX: No transform/scale on mobile - zones fill width naturally
      }}
      >
	  <div

  onClick={(e) => {
    
    // Check for blocking overlays
    const allBlockOverlays = [
      'front-block-overlay',
      'flank-block-overlay',
      'end-turn-block-overlay',
      'forsaken-sentinel-block-overlay',
      'counter-strike-player-front-block-overlay',
      'counter-strike-opponent-front-block-overlay',
      'counter-strike-keep-button-block-overlay',
      'isabela-block-overlay',
      'tranquilizer-block-overlay'
    ];
    const blockingOverlays = allBlockOverlays.filter(id => {
      const overlay = document.getElementById(id);
      return overlay && overlay.style.display !== 'none' && window.getComputedStyle(overlay).display !== 'none';
    });
    if (blockingOverlays.length > 0) {
      console.error('❌ BLOCKING OVERLAYS DETECTED ON GENERAL CLICK:', blockingOverlays);
    } else {
    }
    
    if (yourTurn && attackMode && selectedAttacker) {
      attackOrder(selectedAttacker.zone, selectedAttacker.idx, selectedAttacker.zone, null);
    } else {
    }
  }}
>

</div>
{/* Opponent HUD — upper middle (desktop: inside battlefield; mobile: duplicate render is outside) */}
{!isContainerMobile && (
<div
  className="opponent-avatars-container"
  data-no-battle-flashlight="true"
  style={{
    position: "absolute",
    top: "10px",
    left: "50%",
    transform: "translateX(-50%)",
    transformOrigin: "center top",
    display: "flex",
    gap: "10px",
    alignItems: "center",
    /* HUD chrome uses pointer-events:none on most layers; the wrapper must not eat clicks that should
       hit the battlefield (e.g. opponent flank). Clicks for the general go from onGeneralPortraitClick on the portrait only. */
    pointerEvents: "none",
    zIndex: 1000
  }}
>
  <BattleOpponentHudPanel {...opponentHudPanelProps} />
      </div>
)}

{/* Opponent Drones - Upper Right Corner - DESKTOP ONLY */}
{/* Mobile version is rendered outside battlefield-and-carousel */}
{!isContainerMobile && (
<div
  className="opponent-drone-zone"
  style={{
    position: "absolute",
    top: "10px",
    right: "20px",
    zIndex: 1002,
    display: "flex",
    gap: "4px",
    flexDirection: "row"
  }}
>
    {opponent.drones?.filter(drone => !drone.markedForRemoval).map((drone, index) => {
      // Check if this drone should be highlighted as a target
      const isDroneTarget = isTarget({
        strategemTargetMode: effectiveStrategemTargetMode,
        selectedStrategemId: effectiveSelectedStrategemId,
        strategemTargetInfo: effectiveStrategemTargetInfo,
        immobilizeTargetMode,
        confuseTargetMode,
        weaponTargetMode: effectiveWeaponTargetMode,
        selectedWeaponId,
        weaponDeployTargetMode,
        deployTargetMode,
        selectedDeployOrderId,
        selectedDeployInstanceId,
        selectedDeployZone,
        zone: "opponent_drones",
        targetingData,
        gameState,
        playerId: userId,
        unit: { ...drone, type: 'drone' },
        attackMode: effectiveAttackMode,
        selectedAttacker: effectiveSelectedAttacker,
        droneTargetMode: effectiveDroneTargetMode,
        selectedDroneId: effectiveSelectedDroneId
      });
      
      return (
        <AbilityTooltip
          as="div"
          key={`opponent-drone-${index}`}
          className={`drone-slot-bounds ${getTargetingBorderClassName(isDroneTarget)}`.trim()}
          tooltipText={buildDeployedDroneTooltipText(drone)}
          data-drone-index={index}
          data-drone-zone="opponent_drone"
          data-instance-id={drone.instanceId || ''}
          style={{
            width: '80px',
            height: '60px',
            backgroundColor: '#333',
            border: isDroneTarget ? undefined : '2px solid #a02c2c', // Border handled by className when targeting
            borderRadius: '4px',
            cursor: isDroneTarget ? 'pointer' : 'pointer',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'visible',
            ...(isDroneTarget ? getTargetingBorderStyles(true) : {})
          }}
        onClick={() => {
          // Handle attack mode targeting for drones
          if (attackMode && selectedAttacker) {
            // Attack drone - drones are attacked differently than units
            // We need to call attackOrder with special handling for drones
            // For now, we'll use a special zone identifier for drones
            attackOrder(selectedAttacker.zone, selectedAttacker.idx, 'opponent_drone', index);
            return;
          }
          
          // Handle strategem targeting for drones
          if (strategemTargetMode && selectedStrategemId) {
            targetStrategem(
              "drone", 
              "opponent_drone", 
              index, 
              "opponent", 
              drone.instanceId, 
              ws, 
              gameId, 
              setError, 
              setStrategemTargetMode, 
              setSelectedStrategemId, 
              setStrategemTargetInfo,
              // Animation config
              {
                registerAnimation: registerLocalStrategemTargetAnimation,
                strategemInstanceId: strategemTargetInfo?.strategemInstanceId,
                strategemId: selectedStrategemId,
                gameState,
                userId
              }
            );
            return;
          }
          
          // Allow deploy targeting to select opponent drones
          if (deployTargetMode && selectedDeployOrderId) {
            handleTargetDeploy('opponent_drone', index, 'opponent');
          }
        }}
      >
        {/* Drone image */}
        <img
          src={`/images/${drone.id}.jpg`}
          alt={drone.name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 1
          }}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        {/* Drone charge counter overlay */}
        {drone.charges !== undefined && drone.charges > 0 && (
          <div style={{
            color: 'white',
            fontSize: '16px',
            fontWeight: 'bold',
            textShadow: '1px 1px 1px rgba(0,0,0,0.8)',
            position: 'absolute',
            top: '2px',
            right: '2px',
            backgroundColor: 'rgba(0,0,0,0.7)',
            borderRadius: '50%',
            width: '20px',
            height: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2
          }}>
            {drone.charges}
          </div>
        )}
        </AbilityTooltip>
      );
    })}
    {/* Empty drone slots */}
    {Array.from({ length: 4 - (opponent.drones?.filter(drone => !drone.markedForRemoval).length || 0) }).map((_, index) => (
      <div
        key={`empty-opponent-drone-${index}`}
        style={{
          width: '80px',
          height: '60px',
          backgroundColor: '#333',
          border: '2px solid #555',
          borderRadius: '4px',
          opacity: 0.3
        }}
      />
    ))}
</div>
)}

{/* Spectator Indicator - Below Opponent Drone Zone, Left of O: S: C: */}
{/* Desktop: position right side, Mobile: scaled and hidden to save space */}
{spectators && spectators.length > 0 && !isContainerMobile && (
  <div className="spectator-indicator" style={{
    position: "absolute",
    right: "220px", // Position to the left of O: S: C: counters (which start at right: 20px)
    top: "70px", // Below drone zone (which is at top: 10px, height ~60px)
    zIndex: 1400,
    fontSize: '12px',
    color: '#a0a0a0',
    fontStyle: 'italic',
    whiteSpace: 'nowrap',
    maxWidth: '180px',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }}>
    {spectators.map((spectator, index) => (
      <div key={spectator.odId || index} style={{ marginBottom: index < spectators.length - 1 ? '2px' : '0' }}>
        {spectator.username} {t('battleUI.isSpectating')}
      </div>
    ))}
  </div>
)}

{/* Opponent Stats - Below Images in Upper Right - DESKTOP ONLY */}
{/* Mobile version is rendered outside battlefield-and-carousel */}
{!isContainerMobile && (
<div className="opponent-stats-counters" style={{ 
  position: "absolute", 
  right: "20px", 
  top: "80px", 
  zIndex: 1000,
  display: 'flex', 
  gap: "20px",
  fontSize: "14px",
  fontWeight: 'bold',
  color: 'white'
}}>
  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
    <span style={{ color: '#e24a4a', cursor: 'help' }} data-tooltip={t('battleUI.ordersReady')}>O:</span>
    <span>{opponent?.hand?.length ?? 0}</span>
  </div>
  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
    <span style={{ color: '#e24a4a', cursor: 'help' }} data-tooltip={t('battleUI.strategySize')}>S:</span>
    <span>{getStrategyHudTotal(opponent)}</span>
  </div>
  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
    <span style={{ color: '#e24a4a', cursor: 'help' }} data-tooltip={t('battleUI.completedOrders')}>C:</span>
    <span>{opponent.completedOrders?.length || 0}</span>
  </div>
</div>
)}



        {/* Battlefield: two rows of zones + zone cross overlay (always visible) */}
        <div className="battlefield-with-cross" style={{ position: 'relative', width: '100%', flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column', transform: 'translateY(30px)' }}>
        {/* Zone cross: white lines separating the four zones; above zones, no pointer events */}
        <div className="battlefield-zone-cross-overlay" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 5 }}>
          <div className="zone-cross-line zone-cross-vertical" style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 2, marginLeft: -1, backgroundColor: 'rgba(255,255,255,0.5)' }} />
          <div className="zone-cross-line zone-cross-horizontal" style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: 2, marginTop: -1, backgroundColor: 'rgba(255,255,255,0.5)' }} />
        </div>
        {/* Opponent Battlefield Zones */}
        <div 
          className="battlefield-container viewport-scaled" 
          style={{ 
            display: "flex", 
            flexDirection: "row", 
            justifyContent: "space-between", 
            // MOBILE FIX: Parent battlefield-and-carousel already extends -24px, so children just use 100%
            width: isContainerMobile ? "100%" : "calc(100% + 48px)",
            marginLeft: isContainerMobile ? "0" : "-24px",
            marginRight: isContainerMobile ? "0" : "-24px",
            padding: "0", 
            marginBottom: 0, 
            gap: "0px", 
            maxWidth: "none",
            boxSizing: "border-box",
            ...(isContainerMobile ? {
              height: 'calc(50% - 2px)',
              minHeight: 0
            } : {})
          }}
        >
          {/* Opponent Frontline */}
          <div
            data-zone-key="opponent_front"
            className={`battle-zone viewport-scaled opponent-front ${getTargetingBorderClassName(isBattlefieldZoneHighlightActive('opponent_front'))}`}
            style={{ 
              position: 'relative',
              zIndex: 1, // Keep constant to prevent position shifts
              ...getSlideInStyles(animationsStarted, 'left', 100),
              outline: "3px solid #ff0000",
              borderRight: '1px solid rgba(255,255,255,0.5)',
              ...(isContainerMobile ? { 
                width: 'auto', 
                flex: '1 1 0', 
                minWidth: 0,
                height: '100%', // Fill the battlefield-container height
                minHeight: 0
              } : {}),
              ...resolveBattlefieldZoneHighlightStyles('opponent_front')
            }}
            onClick={() => {
              if (!yourTurn) return;
              if (euEpZoneTargetMode) {
                sendExploreUnknownEpSpend('useExploreUnknownEpZone', { zone: 'front' });
                return;
              }
              const animationConfig = {
                registerAnimation: registerLocalStrategemTargetAnimation,
                strategemInstanceId: strategemTargetInfo?.strategemInstanceId,
                strategemId: selectedStrategemId,
                gameState,
                userId
              };
              handleZoneClick("opponent_front", strategemTargetMode, selectedStrategemId, strategemTargetInfo, targetStrategem, ws, gameId, setError, setStrategemTargetMode, setSelectedStrategemId, setStrategemTargetInfo, targetingData, animationConfig);
            }}
          >

            <div style={{ display: "flex", gap: 0, justifyContent: "center", alignItems: "center", width: "100%", height: "100%", minHeight: 0 }}>
{/* ========== DEBUGGING: Always render all 5 slots ========== */}
{/* TODO: Change back to opponentFront.slice(0, 5).map((order, idx) => { */}
{Array.from({length: 5}).map((_, idx) => {
  // Always render all 5 slots - get order from array if it exists
  const order = opponentFront[idx];
  // Use getVisibleUnit to handle delayed removals
  const visibleOrder = getVisibleUnit('opponent_front', idx, order);
  // COMMENTED OUT - slot number calculation no longer needed
  // const slotNumber = getGlobalSlotNumber('opponent_front', idx); // DEBUGGING: Slot number calculation
  
  if (!visibleOrder) {
    // Empty slot - render death animations if any
    // ========== DEBUGGING: Empty slot box with number and zone label ==========
    // COMMENTED OUT - no longer needed
    /*
    const isHighlighted = slotNumber === 4 || slotNumber === 18; // DEBUGGING: Highlight slots 4 and 18
    return (
      <React.Fragment key={`opponent_front_empty_${idx}`}>
        <SlotDeathAnimations zoneName="opponent_front" index={idx} leftOffset={0} mobileLayout={isContainerMobile} viewportScale={viewportScale} />
        <div
          style={{
            position: 'absolute',
            position: 'relative',
            flex: '0 0 auto',
            top: '0px',
                width: '200px',
                height: '100px',
            backgroundColor: isHighlighted ? 'rgba(255, 255, 0, 0.4)' : 'rgba(100, 100, 100, 0.3)',
            border: isHighlighted ? '4px solid #ffff00' : '1px dashed rgba(102, 102, 102, 0.35)',
            borderRadius: '8px',
            opacity: 0.8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '-1px',
            padding: '0px',
            boxSizing: 'border-box',
            boxShadow: isHighlighted ? '0 0 20px rgba(255, 255, 0, 0.8)' : 'none'
          }}
        >
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px'
          }}>
            <span style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#fff',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
            }}>
              {slotNumber}
            </span>
            <span style={{
              fontSize: '10px',
              fontWeight: 'bold',
              color: '#fff',
              textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
              textTransform: 'uppercase'
            }}>
              Opponent Front
            </span>
          </div>
        </div>
      </React.Fragment>
    );
    */
    // ========== END DEBUGGING ==========
    return (
      <React.Fragment key={`opponent_front_empty_${idx}`}>
        <SlotDeathAnimations zoneName="opponent_front" index={idx} leftOffset={0} mobileLayout={isContainerMobile} viewportScale={viewportScale} />
        <div
          className="empty-slot-container"
          data-slot-zone="opponent_front"
          data-slot-index={idx}
          style={{
            position: 'relative',
            // MOBILE FIX: Let CSS control flex, margins on mobile
            ...(isContainerMobile ? {} : { flex: '0 0 auto', marginLeft: idx > 0 ? '-15px' : '0' }),
          }}
        >
          <div
            style={{
              position: 'relative',
            }}
          >
            <div
              style={{
                ...ordersStyles.container,
                backgroundColor: 'transparent',
                width: '200px',
                height: '100px',
                margin: '-1px',
                padding: '0px',
                fontSize: '0.9em',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                border: '0px solid #ccc',
                borderRadius: '8px',
                boxSizing: 'border-box',
                overflow: 'visible',
                transform: 'none', // Removed to allow proper vertical centering
                pointerEvents: 'none',
              }}
            >
              {/* Border matching the image size (80% width, 140% height) */}
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '80%', // Match image width
                  height: '140%', // Match image height
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  borderRadius: '8px',
                  pointerEvents: 'none',
                }}
              />
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
  
  // OLD ATTACK ANIMATION CODE REMOVED - Now using isUnitAttacking from useAttackAnimations hook
  // The old activeAttack state is no longer used - all attack animations use the new attack-initiated flow
  const isAttacking = false; // OLD CODE DISABLED
  const isBeingAttacked = false; // OLD CODE DISABLED
  const attackerStillExists = true; // OLD CODE DISABLED - units are now hidden by getVisibleUnit using isUnitAttacking
  
  // Don't apply deployment animation if unit is attacking OR being attacked (attack animation takes precedence)
  const deploymentStyles = (isAttacking || isBeingAttacked) ? {} : getDeploymentAnimationStyles(opponentId, 'front', idx);
  // ========== DEBUGGING: Highlight slots 4 and 18 ==========
  // COMMENTED OUT - no longer needed
  // const isHighlighted = slotNumber === 4 || slotNumber === 18; // TODO: Remove highlighting
  // ========== END DEBUGGING ==========
  
  // Key by slot index only so the same DOM node stays mounted when slot content changes (unit dies/deploys).
  // Otherwise React would unmount the div and onMouseLeave never fires, leaving the card stuck expanded.
  return (
    <div
      key={`opponent_front_${idx}`}
      data-slot-zone="opponent_front"
      data-slot-index={idx}
      data-unit-key={visibleOrder?.instanceId ? `${opponentId}_${visibleOrder.instanceId}` : null}
      style={{
        position: 'relative',
        overflow: 'visible',
        // MOBILE FIX: Let CSS control flex, margins, transform on mobile
        ...(isContainerMobile ? {} : {
          flex: '0 0 auto',
          marginLeft: idx > 0 ? '-15px' : '0',
          transform: hoveredPlayed.zone === "opponent_front" && hoveredPlayed.idx === idx
            ? ((attackMode || (lastAttackedTarget && lastAttackedTarget.zone === "opponent_front" && lastAttackedTarget.idx === idx)) ? "scale(0.95)" : "scale(1.05)")
            : "scale(1)",
          transformOrigin: "center",
        }),
        marginTop: 0,
        marginRight: isContainerMobile ? 0 : 0,
        marginBottom: 0,
        cursor: (() => {
          const isCounterStrikeStrategemTargeting = strategemTargetMode && !yourTurn;
          const isCounterStrikeDeployTargeting = deployTargetMode && selectedDeployOrderId && !yourTurn;
          
          if (!yourTurn && !isCounterStrikeStrategemTargeting && !isCounterStrikeDeployTargeting) return "default";
          if (
            strategemTargetMode ||
            immobilizeTargetMode ||
            confuseTargetMode ||
            weaponTargetMode ||
            euEpTargetMode ||
            euEpZoneTargetMode ||
            (deployTargetMode && selectedDeployOrderId === 4 && selectedDeployZone === "front")
          ) {
            return "pointer";
          }
          if (attackMode && selectedAttacker) {
            if (visibleOrder?.isOOR) {
              return "not-allowed";
            }
            return "pointer";
          }
          if (canPinOpponentUnitInspect && visibleOrder) {
            return "pointer";
          }
          return "default";
        })(),
        zIndex: getOverlappingBattlefieldSlotZIndex(
          idx,
          (hoveredPlayed.zone === 'opponent_front' && hoveredPlayed.idx === idx) ||
            (opponentInspectSelection?.zone === 'opponent_front' && opponentInspectSelection?.idx === idx),
          OVERLAPPING_BATTLEFIELD_SLOT_COUNT,
          opponentFrontPreferLeftSlotStack
        ),
        transition: isContainerMobile ? 'none' : (deploymentStyles.animation ? "opacity 0.2s ease" : "transform 0.2s ease"),
        ...(isContainerMobile ? {} : deploymentStyles),
        opacity: 1,
      }}
  onMouseEnter={() => {
    playSoundEffect('/sounds/hoverover.mp3', 0.3);
    setHoveredPlayed({ zone: "opponent_front", idx });
    sendOrderHover(visibleOrder?.instanceId);
  }}
  onMouseLeave={() => {
    clearHoveredPlayedSlot('opponent_front', idx);
  }}
  onClick={(e) => {
    
    // Check for blocking overlays
    const allBlockOverlays = [
      'front-block-overlay',
      'flank-block-overlay',
      'end-turn-block-overlay',
      'forsaken-sentinel-block-overlay',
      'counter-strike-player-front-block-overlay',
      'counter-strike-opponent-front-block-overlay',
      'counter-strike-keep-button-block-overlay',
      'isabela-block-overlay',
      'tranquilizer-block-overlay'
    ];
    const blockingOverlays = allBlockOverlays.filter(id => {
      const overlay = document.getElementById(id);
      return overlay && overlay.style.display !== 'none' && window.getComputedStyle(overlay).display !== 'none';
    });
    if (blockingOverlays.length > 0) {
      console.error('❌ BLOCKING OVERLAYS DETECTED ON TARGET CLICK:', blockingOverlays);
      blockingOverlays.forEach(id => {
        const overlay = document.getElementById(id);
        if (overlay) {
          const rect = overlay.getBoundingClientRect();
          const clickX = e.clientX;
          const clickY = e.clientY;
          const isOverOverlay = clickX >= rect.left && clickX <= rect.right && clickY >= rect.top && clickY <= rect.bottom;
          console.error(`❌ Overlay ${id}:`, {
            display: overlay.style.display,
            computedDisplay: window.getComputedStyle(overlay).display,
            pointerEvents: overlay.style.pointerEvents,
            computedPointerEvents: window.getComputedStyle(overlay).pointerEvents,
            zIndex: overlay.style.zIndex,
            computedZIndex: window.getComputedStyle(overlay).zIndex,
            rect: { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom },
            clickPos: { x: clickX, y: clickY },
            isOverOverlay
          });
        }
      });
    } else {
    }
    
    e.stopPropagation(); // Prevent event from bubbling to zone handler
    // Handle drone targeting separately as it's not in the extracted function
    if (yourTurn && droneTargetMode && selectedDroneId) {
      targetDrone("opponent_front", idx, ws, setError, setDroneTargetMode, setSelectedDroneId, setSelectedDroneInstanceId, selectedDroneId, selectedDroneInstanceId, gameId, gameState, userId);
      return;
    }

    if (euEpZoneTargetMode) {
      sendExploreUnknownEpSpend('useExploreUnknownEpZone', { zone: 'front' });
      return;
    }

    if (euEpTargetMode && visibleOrder?.instanceId) {
      if (!isExploreUnknownEpTargetHighlight(visibleOrder, 'opponent_front')) {
        setError(t('exploreUnknownBattle.epTargetInvalidUnit'));
        return;
      }
      sendExploreUnknownEpSpend('useExploreUnknownEpTarget', {
        targetInstanceId: visibleOrder.instanceId,
      });
      return;
    }
    
    // Use extracted targeting click handler
    // Create animation config for strategem targeting
    const strategemAnimationConfig = {
      registerAnimation: registerLocalStrategemTargetAnimation,
      strategemInstanceId: strategemTargetInfo?.strategemInstanceId,
      strategemId: selectedStrategemId,
      gameState,
      userId
    };
    handleTargetingClick(
      "opponent_front",
      idx,
      yourTurn,
      strategemTargetMode,
      selectedStrategemId,
      strategemTargetInfo,
      immobilizeTargetMode,
      selectedImmobilizeWeaponId,
      confuseTargetMode,
      selectedConfuseWeaponId,
      weaponTargetMode,
      selectedWeaponId,
      selectedWeaponInstanceId,
      weaponDeployTargetMode,
      weaponDeployTargetInfo,
      deployTargetMode,
      selectedDeployOrderId,
      attackMode,
      selectedAttacker,
      targetStrategem,
      targetImmobilize,
      targetConfuse,
      handleTargetDeploy,
      weaponDeployTarget,
      attachWeapon,
      attackOrder,
      ws,
      gameId,
      setError,
      setStrategemTargetMode,
      setSelectedStrategemId,
      setStrategemTargetInfo,
      setImmobilizeTargetMode,
      setSelectedImmobilizeWeaponId,
      setConfuseTargetMode,
      setSelectedConfuseWeaponId,
      setWeaponTargetMode,
      setSelectedWeaponId,
      setWeaponDeployTargetMode,
      setWeaponDeployTargetInfo,
      // New validation parameters
      targetingData,
      gameState,
      userId,
      selectedDeployZone,
      // Replacement mode parameters
      replaceTargetMode,
      selectedReplaceUnitId,
      replaceTargetZone,
      setClickedUnitForReplace,
      // Animation config
      strategemAnimationConfig
    );
    if (
      canPinOpponentUnitInspect &&
      visibleOrder &&
      !euEpTargetMode &&
      !euEpZoneTargetMode
    ) {
      setOpponentInspectSelection(prev =>
        prev && prev.zone === 'opponent_front' && prev.idx === idx
          ? null
          : { zone: 'opponent_front', idx }
      );
    }
  }}
>
  {euMemoryFadeOverlays[`opponent_front_${idx}`] && (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 200,
        pointerEvents: 'none',
        backgroundImage: `url(${euMemoryFadeOverlays[`opponent_front_${idx}`].image})`,
        backgroundSize: 'contain',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        opacity: euMemoryFadeOverlays[`opponent_front_${idx}`].opacity,
        transition: 'opacity 0.45s ease-out',
      }}
    />
  )}
  {isNewlyDeployedLegendaryUnique(opponentId, 'front', idx) && (
    <LegendaryUniqueDeployRay />
  )}
  {(() => {
    const targetModeVal = getTargetingMode(strategemTargetMode, immobilizeTargetMode, confuseTargetMode, weaponTargetMode, deployTargetMode, selectedDeployOrderId, selectedDeployZone);
    // Always run isTarget validation first to prevent instanceId collisions
    const isTargetValid = isTarget({
      strategemTargetMode: effectiveStrategemTargetMode,
      selectedStrategemId: effectiveSelectedStrategemId,
      strategemTargetInfo: effectiveStrategemTargetInfo,
      immobilizeTargetMode,
      confuseTargetMode,
      weaponTargetMode: effectiveWeaponTargetMode,
      selectedWeaponId,
      weaponDeployTargetMode,
      deployTargetMode,
      selectedDeployOrderId,
      selectedDeployInstanceId,
      selectedDeployZone,
        zone: "opponent_front",
      targetingData,
      gameState,
        playerId: userId,
        unit: visibleOrder,
        attackMode: effectiveAttackMode,
        selectedAttacker: effectiveSelectedAttacker,
        droneTargetMode: effectiveDroneTargetMode,
        selectedDroneId: effectiveSelectedDroneId
      });
      
      // Tutorial overlay can force yellow border when strategemTargetAllowOnly includes this unit (e.g. Strike the Berserker → 377)
      const tutorialOverlayHighlight = strategemTargetMode && selectedStrategemId && isTutorialOverlayStrategemTarget(visibleOrder, selectedStrategemId);
      const isTargetVal =
        isTargetValid ||
        tutorialOverlayHighlight ||
        isExploreUnknownEpTargetHighlight(visibleOrder, 'opponent_front');
      const isOpponentInspectSelected =
        opponentInspectSelection?.zone === 'opponent_front' &&
        opponentInspectSelection?.idx === idx;
      const isHoveredThisOpponentSlot =
        hoveredPlayed.zone === 'opponent_front' && hoveredPlayed.idx === idx;
      const showOpponentExpandedPreview =
        isHoveredThisOpponentSlot ||
        (isOpponentInspectSelected && hoveredPlayed.zone == null);
      return (
        <>
          <div
            className={`deployed-unit-slot ${!isProtectUnit(visibleOrder) && !isNewlyDeployed(opponentId, 'front', idx) && !visibleOrder?._isRepositioning && !isImmobilizedUI(visibleOrder, "opponent_front") ? 'deployed-unit-slot-idle' : ''}`.trim()}
            data-slot-zone="opponent_front"
            data-slot-index={idx}
            style={{ position: 'relative' }}
          >
            {isNewlyDeployedLegendary(opponentId, 'front', idx) && (
              <LegendaryDeployGlow />
            )}
            <OrderDesign 
              order={visibleOrder} 
              count={0} 
              minimal={
                attackMode ||
                (lastAttackedTarget && lastAttackedTarget.zone === "opponent_front" && lastAttackedTarget.idx === idx) ||
                !showOpponentExpandedPreview
              }
              scale={viewportScale} 
              fixedDesktopExpandedPosition={fixedDesktopExpandedPosition}
              isOOR={visibleOrder.isOOR}
              isSelected={isOpponentInspectSelected || selectedOrderInstanceId === visibleOrder.instanceId}
              inspectSelectionBorder={!!isOpponentInspectSelected}
              isHighlighted={highlightedInstanceIds.some(id => String(id).trim() === String(visibleOrder.instanceId).trim())}
              isTarget={isTargetVal}
              forceTargetAppearance={tutorialOverlayHighlight}
              targetMode={targetModeVal}
              zoneName="opponent_front"
              slotIndex={idx}
              gameState={gameState}
              userId={activeUserId}
              isHiddenDuringAnimation={attackAnimations.isUnitHidden(visibleOrder?.instanceId)}
              isLethalDeathFading={isSlotLethalFading('opponent_front', idx, visibleOrder?.instanceId)}
              isRedeemMarkedForDeath={isRedeemMarkedForDeathForUnit(visibleOrder?.instanceId)}
              isRedeemDeathFinishing={isRedeemDeathFinishingForUnit(visibleOrder?.instanceId)}
              lethalDeathFadeDurationMs={getLethalFadeDurationMs('opponent_front', idx)}
              onLethalDeathFadeComplete={() => completeLethalAttackFade('opponent_front', idx)}
              isViewedByOpponent={!!(opponentLookingAtInstanceId && visibleOrder?.instanceId && String(opponentLookingAtInstanceId) === String(visibleOrder.instanceId))}
              onBattlefieldExpandedPointerLeave={
                isOpponentInspectSelected
                  ? undefined
                  : () => {
                      setHoveredPlayed({ zone: null, idx: null });
                      sendOrderHover(null);
                      if (lastAttackedTarget && lastAttackedTarget.zone === 'opponent_front' && lastAttackedTarget.idx === idx) {
                        setLastAttackedTarget(null);
                      }
                    }
              }
              passThroughExpandedPortalForTargeting={passThroughExpandedBattlefieldPortals}
            />
            <DemoralizingBrokenShieldOverlay
              visible={showDemoralizingBrokenShieldFor(visibleOrder?.instanceId)}
            />
          </div>
          {/* ========== DEBUGGING: Slot number and zone label overlay ========== */}
          {/* COMMENTED OUT - no longer needed */}
          {/*
          <div style={{
            position: 'absolute',
            top: '5px',
            left: '5px',
            fontSize: '12px',
            fontWeight: 'bold',
            color: '#fff',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            padding: '2px 6px',
            borderRadius: '4px',
            zIndex: 1000,
            pointerEvents: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: '2px'
          }}>
            <span>{slotNumber}</span>
            <span style={{ fontSize: '9px', textTransform: 'uppercase' }}>Opponent Front</span>
          </div>
          */}
          {/* ========== END DEBUGGING ========== */}
        {isImmobilizedUI(visibleOrder, "opponent_front") && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '84px',
              height: '84px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 6,
              animation: 'immobilizedPulse 2s ease-in-out infinite',
              pointerEvents: 'none',
            }}
          >
            <img
              src="/images/immobilize.png"
              alt="Immobilized"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
            />
          </div>
        )}
      </>
    );
  })()}
</div>
  );
})}

            </div>
          </div>
          {/* Opponent Flank */}

          <div
            data-zone-key="opponent_flank"
            className={`battle-zone viewport-scaled opponent-flank ${getTargetingBorderClassName(isBattlefieldZoneHighlightActive('opponent_flank'))}`}
            style={{ 
              position: 'relative',
              zIndex: 1, // Keep constant to prevent position shifts
              ...getSlideInStyles(animationsStarted, 'right', 200),
              outline: "3px solid #ff0000",
              borderLeft: '1px solid rgba(255,255,255,0.5)',
              ...(isContainerMobile ? { 
                width: 'auto', 
                flex: '1 1 0', 
                minWidth: 0,
                height: '100%', // Fill the battlefield-container height
                minHeight: 0
              } : {}),
              ...resolveBattlefieldZoneHighlightStyles('opponent_flank')
            }}
            onClick={() => {
              if (!yourTurn) return;
              if (euEpZoneTargetMode) {
                sendExploreUnknownEpSpend('useExploreUnknownEpZone', { zone: 'flank' });
                return;
              }
              const animationConfig = {
                registerAnimation: registerLocalStrategemTargetAnimation,
                strategemInstanceId: strategemTargetInfo?.strategemInstanceId,
                strategemId: selectedStrategemId,
                gameState,
                userId
              };
              handleZoneClick("opponent_flank", strategemTargetMode, selectedStrategemId, strategemTargetInfo, targetStrategem, ws, gameId, setError, setStrategemTargetMode, setSelectedStrategemId, setStrategemTargetInfo, targetingData, animationConfig);
            }}
          >

            <div style={{ display: "flex", gap: 0, justifyContent: "center", alignItems: "center", width: "100%", height: "100%", minHeight: 0 }}>
             {/* ========== DEBUGGING: Always render all 5 slots ========== */}
             {/* TODO: Change back to opponentFlank.slice(0, 5).map((order, idx) => { */}
             {Array.from({length: 5}).map((_, idx) => {
               // Always render all 5 slots - get order from array if it exists
               const order = opponentFlank[idx];
               const visibleOrder = getVisibleUnit('opponent_flank', idx, order);
               // COMMENTED OUT - slot number calculation no longer needed
               // const slotNumber = getGlobalSlotNumber('opponent_flank', idx); // DEBUGGING: Slot number calculation
               
               if (!visibleOrder) {
                 // ========== DEBUGGING: Empty slot box with number and zone label ==========
                 // COMMENTED OUT - no longer needed
                 /*
                 const isHighlighted = slotNumber === 4 || slotNumber === 18; // DEBUGGING: Highlight slots 4 and 18
                 return (
                   <React.Fragment key={`opponent_flank_empty_${idx}`}>
                     <SlotDeathAnimations zoneName="opponent_flank" index={idx} leftOffset={0} mobileLayout={isContainerMobile} viewportScale={viewportScale} />
                     <div
                       style={{
                         position: 'absolute',
                         position: 'relative',
            flex: '0 0 auto',
                         top: '0px',
                width: '200px',
                height: '100px',
                         backgroundColor: isHighlighted ? 'rgba(255, 255, 0, 0.4)' : 'rgba(100, 100, 100, 0.3)',
                         border: isHighlighted ? '4px solid #ffff00' : '1px dashed rgba(102, 102, 102, 0.35)',
                         borderRadius: '8px',
                         opacity: 0.8,
                         display: 'flex',
                         alignItems: 'center',
                         justifyContent: 'center',
                         margin: '-1px',
                         padding: '0px',
                         boxSizing: 'border-box',
                         boxShadow: isHighlighted ? '0 0 20px rgba(255, 255, 0, 0.8)' : 'none'
                       }}
                     >
                       <div style={{
                         display: 'flex',
                         flexDirection: 'column',
                         alignItems: 'center',
                         justifyContent: 'center',
                         gap: '4px'
                       }}>
                         <span style={{
                           fontSize: '24px',
                           fontWeight: 'bold',
                           color: '#fff',
                           textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
                         }}>
                           {slotNumber}
                         </span>
                         <span style={{
                           fontSize: '10px',
                           fontWeight: 'bold',
                           color: '#fff',
                           textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                           textTransform: 'uppercase'
                         }}>
                           Opponent Flank
                         </span>
                       </div>
                     </div>
                   </React.Fragment>
                 );
                 */
                 // ========== END DEBUGGING ==========
                 return (
                   <React.Fragment key={`opponent_flank_empty_${idx}`}>
                     <SlotDeathAnimations zoneName="opponent_flank" index={idx} leftOffset={0} mobileLayout={isContainerMobile} viewportScale={viewportScale} />
                     <div
                       className="empty-slot-container"
                       data-slot-zone="opponent_flank"
                       data-slot-index={idx}
                       style={{
                         position: 'relative',
                         // MOBILE FIX: Let CSS control flex, margins on mobile
                         ...(isContainerMobile ? {} : { flex: '0 0 auto', marginLeft: idx > 0 ? '-15px' : '0' }),
                       }}
                     >
                       <div
                         style={{
                           position: 'relative',
                         }}
                       >
                         <div
                           style={{
                             ...ordersStyles.container,
                             backgroundColor: 'transparent',
                width: '200px',
                height: '100px',
                             margin: '-1px',
                             padding: '0px',
                             fontSize: '0.9em',
                             display: 'flex',
                             flexDirection: 'column',
                             justifyContent: 'center',
                             alignItems: 'center',
                             border: '0px solid #ccc',
                             borderRadius: '8px',
                             boxSizing: 'border-box',
                             overflow: 'visible',
                             transform: 'none', // Removed to allow proper vertical centering
                             pointerEvents: 'none',
                           }}
                         >
                           {/* Border matching the image size (80% width, 140% height) */}
                           <div
                             style={{
                               position: 'absolute',
                               top: '50%',
                               left: '50%',
                               transform: 'translate(-50%, -50%)',
                               width: '80%', // Match image width
                               height: '140%', // Match image height
                               border: '1px solid rgba(255, 255, 255, 0.12)',
                               borderRadius: '8px',
                               pointerEvents: 'none',
                             }}
                           />
                         </div>
                       </div>
                     </div>
                   </React.Fragment>
                 );
               }
               
               // Check if this unit is currently animating an attack
               // OLD CODE DISABLED - using new attack-initiated flow
               const isAttackingFlank = false;
               const isBeingAttackedFlank = false;
               const attackerStillExistsFlank = true;
               const deploymentStylesFlank = getDeploymentAnimationStyles(opponentId, 'flank', idx);
               // ========== DEBUGGING: Highlight slots 4 and 18 ==========
               // COMMENTED OUT - no longer needed
               // const isHighlighted = slotNumber === 4 || slotNumber === 18; // TODO: Remove highlighting
               // ========== END DEBUGGING ==========
               
               // Key by slot index only so the same DOM node stays mounted when slot content changes (avoids stuck expand).
               return (
<div
  key={`opponent_flank_${idx}`}
  data-slot-zone="opponent_flank"
  data-slot-index={idx}
  data-unit-key={visibleOrder?.instanceId ? `${opponentId}_${visibleOrder.instanceId}` : null}
  style={{
    position: 'relative',
    // MOBILE FIX: Let CSS control flex, margins, transform on mobile
    ...(isContainerMobile ? {} : {
      flex: '0 0 auto',
      marginLeft: idx > 0 ? '-15px' : '0',
      transform: hoveredPlayed.zone === "opponent_flank" && hoveredPlayed.idx === idx
        ? ((attackMode || (lastAttackedTarget && lastAttackedTarget.zone === "opponent_flank" && lastAttackedTarget.idx === idx)) ? "scale(0.95)" : "scale(1.05)")
        : "scale(1)",
      transformOrigin: "center",
    }),
    marginTop: 0,
    marginRight: isContainerMobile ? 0 : 0,
    marginBottom: 0,
    cursor: (() => {
      const isCounterStrikeStrategemTargeting = strategemTargetMode && !yourTurn;
      const isCounterStrikeDeployTargeting = deployTargetMode && selectedDeployOrderId && !yourTurn;
      
      if (!yourTurn && !isCounterStrikeStrategemTargeting && !isCounterStrikeDeployTargeting) return "default";
      if (
        strategemTargetMode ||
        immobilizeTargetMode ||
        confuseTargetMode ||
        weaponTargetMode ||
        euEpTargetMode ||
        euEpZoneTargetMode ||
        (deployTargetMode && selectedDeployOrderId === 4 && selectedDeployZone === "flank")
      ) {
        return "pointer";
      }
      if (attackMode && selectedAttacker) {
        if (visibleOrder?.isOOR) {
          return "not-allowed";
        }
        return "pointer";
      }
      if (canPinOpponentUnitInspect && visibleOrder) {
        return "pointer";
      }
      return "default";
    })(),
    zIndex: getOverlappingBattlefieldSlotZIndex(
      idx,
      (hoveredPlayed.zone === 'opponent_flank' && hoveredPlayed.idx === idx) ||
        (opponentInspectSelection?.zone === 'opponent_flank' && opponentInspectSelection?.idx === idx),
      OVERLAPPING_BATTLEFIELD_SLOT_COUNT,
      opponentFlankPreferLeftSlotStack
    ),
    transition: isContainerMobile ? 'none' : (deploymentStylesFlank.animation ? "opacity 0.2s ease" : "transform 0.2s ease"),
    ...(isContainerMobile ? {} : deploymentStylesFlank),
    opacity: 1,
  }}
  onMouseEnter={() => {
    playSoundEffect('/sounds/hoverover.mp3', 0.3);
    setHoveredPlayed({ zone: "opponent_flank", idx });
    sendOrderHover(visibleOrder?.instanceId);
  }}
  onMouseLeave={() => {
    clearHoveredPlayedSlot('opponent_flank', idx);
  }}
  onClick={(e) => {
    e.stopPropagation(); // Prevent event from bubbling to zone handler
    // Handle drone targeting separately as it's not in the extracted function
    if (yourTurn && droneTargetMode && selectedDroneId) {
      targetDrone("opponent_flank", idx, ws, setError, setDroneTargetMode, setSelectedDroneId, setSelectedDroneInstanceId, selectedDroneId, selectedDroneInstanceId, gameId, gameState, userId);
      return;
    }

    if (euEpZoneTargetMode) {
      sendExploreUnknownEpSpend('useExploreUnknownEpZone', { zone: 'flank' });
      return;
    }

    if (euEpTargetMode && visibleOrder?.instanceId) {
      if (!isExploreUnknownEpTargetHighlight(visibleOrder, 'opponent_flank')) {
        setError(t('exploreUnknownBattle.epTargetInvalidUnit'));
        return;
      }
      sendExploreUnknownEpSpend('useExploreUnknownEpTarget', {
        targetInstanceId: visibleOrder.instanceId,
      });
      return;
    }
    
    // Use extracted targeting click handler
    // Create animation config for strategem targeting
    const strategemAnimationConfig = {
      registerAnimation: registerLocalStrategemTargetAnimation,
      strategemInstanceId: strategemTargetInfo?.strategemInstanceId,
      strategemId: selectedStrategemId,
      gameState,
      userId
    };
    handleTargetingClick(
      "opponent_flank",
      idx,
      yourTurn,
      strategemTargetMode,
      selectedStrategemId,
      strategemTargetInfo,
      immobilizeTargetMode,
      selectedImmobilizeWeaponId,
      confuseTargetMode,
      selectedConfuseWeaponId,
      weaponTargetMode,
      selectedWeaponId,
      selectedWeaponInstanceId,
      weaponDeployTargetMode,
      weaponDeployTargetInfo,
      deployTargetMode,
      selectedDeployOrderId,
      attackMode,
      selectedAttacker,
      targetStrategem,
      targetImmobilize,
      targetConfuse,
      handleTargetDeploy,
      weaponDeployTarget,
      attachWeapon,
      attackOrder,
      ws,
      gameId,
      setError,
      setStrategemTargetMode,
      setSelectedStrategemId,
      setStrategemTargetInfo,
      setImmobilizeTargetMode,
      setSelectedImmobilizeWeaponId,
      setConfuseTargetMode,
      setSelectedConfuseWeaponId,
      setWeaponTargetMode,
      setSelectedWeaponId,
      setWeaponDeployTargetMode,
      setWeaponDeployTargetInfo,
      // New validation parameters
      targetingData,
      gameState,
      userId,
      selectedDeployZone,
      // Replacement mode parameters
      replaceTargetMode,
      selectedReplaceUnitId,
      replaceTargetZone,
      setClickedUnitForReplace,
      // Animation config
      strategemAnimationConfig
    );
    if (
      canPinOpponentUnitInspect &&
      visibleOrder &&
      !euEpTargetMode &&
      !euEpZoneTargetMode
    ) {
      setOpponentInspectSelection(prev =>
        prev && prev.zone === 'opponent_flank' && prev.idx === idx
          ? null
          : { zone: 'opponent_flank', idx }
      );
    }
  }}
>
  {euMemoryFadeOverlays[`opponent_flank_${idx}`] && (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 200,
        pointerEvents: 'none',
        backgroundImage: `url(${euMemoryFadeOverlays[`opponent_flank_${idx}`].image})`,
        backgroundSize: 'contain',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        opacity: euMemoryFadeOverlays[`opponent_flank_${idx}`].opacity,
        transition: 'opacity 0.45s ease-out',
      }}
    />
  )}
  {isNewlyDeployedLegendaryUnique(opponentId, 'flank', idx) && (
    <LegendaryUniqueDeployRay />
  )}
  <>
    <div
      className={`deployed-unit-slot ${!isProtectUnit(visibleOrder) && !isNewlyDeployed(opponentId, 'flank', idx) && !visibleOrder?._isRepositioning && !isImmobilizedUI(visibleOrder, "opponent_flank") ? 'deployed-unit-slot-idle' : ''}`.trim()}
      data-slot-zone="opponent_flank"
      data-slot-index={idx}
      style={{ position: 'relative' }}
    >
      {isNewlyDeployedLegendary(opponentId, 'flank', idx) && (
        <LegendaryDeployGlow />
      )}
      <OrderDesign
        order={visibleOrder}
        count={0} 
        minimal={
          attackMode ||
          (lastAttackedTarget && lastAttackedTarget.zone === "opponent_flank" && lastAttackedTarget.idx === idx) ||
          !(
            (hoveredPlayed.zone === 'opponent_flank' && hoveredPlayed.idx === idx) ||
            (opponentInspectSelection?.zone === 'opponent_flank' &&
              opponentInspectSelection?.idx === idx &&
              hoveredPlayed.zone == null)
          )
        }
        scale={viewportScale}
        fixedDesktopExpandedPosition={fixedDesktopExpandedPosition}
        isOOR={visibleOrder.isOOR}
        isSelected={
          (opponentInspectSelection?.zone === 'opponent_flank' && opponentInspectSelection?.idx === idx) ||
          selectedOrderInstanceId === visibleOrder.instanceId
        }
        inspectSelectionBorder={
          !!(opponentInspectSelection?.zone === 'opponent_flank' && opponentInspectSelection?.idx === idx)
        }
        isHighlighted={highlightedInstanceIds.some(id => String(id).trim() === String(visibleOrder.instanceId).trim())}
        zoneName="opponent_flank"
        slotIndex={idx}
        gameState={gameState}
        userId={activeUserId}
        isHiddenDuringAnimation={attackAnimations.isUnitHidden(visibleOrder?.instanceId)}
        isLethalDeathFading={isSlotLethalFading('opponent_flank', idx, visibleOrder?.instanceId)}
        isRedeemMarkedForDeath={isRedeemMarkedForDeathForUnit(visibleOrder?.instanceId)}
        isRedeemDeathFinishing={isRedeemDeathFinishingForUnit(visibleOrder?.instanceId)}
        lethalDeathFadeDurationMs={getLethalFadeDurationMs('opponent_flank', idx)}
        onLethalDeathFadeComplete={() => completeLethalAttackFade('opponent_flank', idx)}
        isUnitTargetInPendingAttack={attackAnimations.isUnitTargetInPendingAttack}
        isViewedByOpponent={!!(opponentLookingAtInstanceId && visibleOrder?.instanceId && String(opponentLookingAtInstanceId) === String(visibleOrder.instanceId))}
      isTarget={isTarget({
        strategemTargetMode: effectiveStrategemTargetMode,
        selectedStrategemId: effectiveSelectedStrategemId,
        strategemTargetInfo: effectiveStrategemTargetInfo,
        immobilizeTargetMode,
        confuseTargetMode,
        weaponTargetMode: effectiveWeaponTargetMode,
        selectedWeaponId,
        weaponDeployTargetMode,
        deployTargetMode,
        selectedDeployOrderId,
        selectedDeployInstanceId,
        selectedDeployZone,
        zone: "opponent_flank",
        targetingData,
        gameState,
        playerId: userId,
        attackMode: effectiveAttackMode,
        selectedAttacker: effectiveSelectedAttacker,
        droneTargetMode: effectiveDroneTargetMode,
        selectedDroneId: effectiveSelectedDroneId,
        unit: visibleOrder
      }) ||
        (strategemTargetMode && selectedStrategemId && isTutorialOverlayStrategemTarget(visibleOrder, selectedStrategemId)) ||
        isExploreUnknownEpTargetHighlight(visibleOrder, 'opponent_flank')}
      forceTargetAppearance={strategemTargetMode && selectedStrategemId && isTutorialOverlayStrategemTarget(visibleOrder, selectedStrategemId)}
      targetMode={getTargetingMode(strategemTargetMode, immobilizeTargetMode, confuseTargetMode, weaponTargetMode, deployTargetMode, selectedDeployOrderId, selectedDeployZone)}
      onBattlefieldExpandedPointerLeave={
        opponentInspectSelection?.zone === 'opponent_flank' && opponentInspectSelection?.idx === idx
          ? undefined
          : () => {
              setHoveredPlayed({ zone: null, idx: null });
              sendOrderHover(null);
              if (lastAttackedTarget && lastAttackedTarget.zone === 'opponent_flank' && lastAttackedTarget.idx === idx) {
                setLastAttackedTarget(null);
              }
            }
      }
      passThroughExpandedPortalForTargeting={passThroughExpandedBattlefieldPortals}
    />
    <DemoralizingBrokenShieldOverlay
      visible={showDemoralizingBrokenShieldFor(visibleOrder?.instanceId)}
    />
    </div>
    {/* ========== DEBUGGING: Slot number and zone label overlay ========== */}
    {/* COMMENTED OUT - no longer needed */}
    {/*
    <div style={{
      position: 'absolute',
      top: '5px',
      left: '5px',
      fontSize: '12px',
      fontWeight: 'bold',
      color: '#fff',
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      padding: '2px 6px',
      borderRadius: '4px',
      zIndex: 1000,
      pointerEvents: 'none',
      display: 'flex',
      flexDirection: 'column',
      gap: '2px'
    }}>
      <span>{slotNumber}</span>
      <span style={{ fontSize: '9px', textTransform: 'uppercase' }}>Opponent Flank</span>
    </div>
    */}
    {/* ========== END DEBUGGING ========== */}
  </>
  </div>
               );
             })}

            </div>
          </div>
        </div>
        {/* Your Battlefield Zones */}
        <div 
          className="battlefield-container viewport-scaled" 
          style={{ 
            display: "flex", 
            flexDirection: "row", 
            justifyContent: "space-between", 
            // MOBILE FIX: Parent battlefield-and-carousel already extends -24px, so children just use 100%
            width: isContainerMobile ? "100%" : "calc(100% + 48px)",
            marginLeft: isContainerMobile ? "0" : "-24px",
            marginRight: isContainerMobile ? "0" : "-24px",
            padding: "0", 
            marginBottom: 4, 
            gap: "0px", 
            maxWidth: "none",
            boxSizing: "border-box",
            ...(isContainerMobile ? {
              height: 'calc(50% - 2px)',
              minHeight: 0
            } : {})
          }}
        >
          {/* Frontline */}

  <div
  data-zone-key="front"
  className={`battle-zone viewport-scaled front ${(() => {
    const selectedOrder = selectedOrderInstanceId ? you?.hand?.find(o => o.instanceId === selectedOrderInstanceId) : null;
    const selectedOrderType = (selectedOrder?.type || '').toLowerCase();
    const isUnitZoneDeploySelection = selectedOrderType !== 'strategem' && selectedOrderType !== 'stratagem' && selectedOrderType !== 'drone' && selectedOrderType !== 'weapon';
    const shouldHighlightForDeployment = (((selectedOrderInstanceId && yourTurn) || selectedCounterStrikeOrder) && isUnitZoneDeploySelection) || isDeployDragActive;
    const isStrategemZoneTargeting = effectiveStrategemTargetMode && effectiveStrategemTargetInfo?.type === 'zone';
    const targetingRules = effectiveStrategemTargetInfo?.targetingOptions?.[0];
    const playerRestriction = targetingRules?.player || 'opponent';
    const isFriendlyZoneTargeting = isStrategemZoneTargeting && (playerRestriction === 'friendly' || playerRestriction === 'both');
    
    return getTargetingBorderClassName(shouldHighlightForDeployment || isFriendlyZoneTargeting || euEpZoneTargetMode);
  })()}`}
  style={{
    position: 'relative',
    zIndex: 1, // Keep constant to prevent position shifts
    ...getSlideInStyles(animationsStarted, 'left', 300),
    borderRight: '1px solid rgba(255,255,255,0.5)',
    ...(isContainerMobile ? { width: 'auto', flex: '1 1 0', minWidth: 0, height: '100%', minHeight: 0 } : {}),
    ...(() => {
      // Check if we should highlight for unit deployment (carousel, counter strike, or drag)
      // ✅ FIX: Exclude strategems from deployment highlighting
      // ✅ FIX: Allow counterstrike highlighting even when it's not the player's turn (counterstrike happens during opponent's turn)
      const selectedOrder = selectedOrderInstanceId ? you?.hand?.find(o => o.instanceId === selectedOrderInstanceId) : null;
      const selectedOrderType = (selectedOrder?.type || '').toLowerCase();
      const isUnitZoneDeploySelection = selectedOrderType !== 'strategem' && selectedOrderType !== 'stratagem' && selectedOrderType !== 'drone' && selectedOrderType !== 'weapon';
      const shouldHighlightForDeployment = (((selectedOrderInstanceId && yourTurn) || selectedCounterStrikeOrder) && isUnitZoneDeploySelection) || isDeployDragActive;
      
      if (shouldHighlightForDeployment) {
        // Use unified targeting border style
        return getTargetingBorderStyles(true);
      }

      const epZoneStyles = getExploreUnknownEpZoneTargetingStyles(euEpZoneTargetMode);
      if (epZoneStyles) return epZoneStyles;
      
      // Check for strategem zone targeting mode (from Request button or drag)
      if (effectiveStrategemTargetMode && effectiveStrategemTargetInfo?.type === 'zone') {
        const targetingRules = effectiveStrategemTargetInfo?.targetingOptions?.[0];
        const playerRestriction = targetingRules?.player || 'opponent';
        // Highlight friendly zones if targeting allows friendly
        if (playerRestriction === 'friendly' || playerRestriction === 'both') {
          return getTargetingBorderStyles(true);
        }
      }
      
      return getZoneTargetingStyles(effectiveStrategemTargetMode, effectiveStrategemTargetInfo, 'zone', 'front');
    })()
  }}
  onDragOver={e => e.preventDefault()}
  onDrop={e => {
    if (!yourTurn || !draggedOrderId) return;
    playOrder(draggedOrderId, "front", null, draggedOrderInstanceId);
    setDraggedOrderId(null);
    setDraggedOrderInstanceId(null);
    setSelectedOrderId(null);
    setSelectedOrderInstanceId(null);
  }}
  onClick={() => {
    // ✅ FIX: Allow counterstrike zone clicks even when it's not the player's turn (counterstrike happens during opponent's turn)
    // Handle counter strike deployment FIRST (before turn check) - separate from carousel
    if (selectedCounterStrikeOrder) {
      const frontUnitsCount = you.playedOrders.front.filter(u => u !== null).length;
      if (frontUnitsCount >= 5) {
        enterCounterStrikeReplaceMode('front');
        return;
      }
      playCounterStrike(selectedCounterStrikeOrder.id, "front", selectedCounterStrikeOrder.instanceId);
      setSelectedCounterStrikeOrder(null);
      return;
    }
    
    // Allow zone clicks during counter strike deploy targeting (e.g. Bulwark needs to target a unit)
    const isCounterStrikeDeployTargetingFront = deployTargetMode && selectedDeployOrderId && !yourTurn;
    if (!yourTurn && !isCounterStrikeDeployTargetingFront) return;

    if (euEpZoneTargetMode) {
      sendExploreUnknownEpSpend('useExploreUnknownEpZone', { zone: 'front' });
      return;
    }
    
    // Handle strategem zone targeting (from Request button flow) - CHECK THIS FIRST
    const frontAnimationConfig = {
      registerAnimation: registerLocalStrategemTargetAnimation,
      strategemInstanceId: strategemTargetInfo?.strategemInstanceId,
      strategemId: selectedStrategemId,
      gameState,
      userId
    };
    if (handleZoneClick("front", strategemTargetMode, selectedStrategemId, strategemTargetInfo, targetStrategem, ws, gameId, setError, setStrategemTargetMode, setSelectedStrategemId, setStrategemTargetInfo, targetingData, frontAnimationConfig)) {
      return;
    }
    
    // ✅ FIX: Check if selected order is a strategem - prevent zone deployment
    // Only block if NOT in strategem targeting mode
    if (selectedOrderInstanceId && !strategemTargetMode) {
      const selectedOrder = you?.hand?.find(o => o.instanceId === selectedOrderInstanceId);
      if (selectedOrder?.type && (selectedOrder.type.toLowerCase() === 'strategem' || selectedOrder.type.toLowerCase() === 'stratagem')) {
        setError(t('battleUI.useRequestButton'));
        setTimeout(() => setError(''), 2000);
        return;
      }
      if (selectedOrder?.type === 'Drone') {
        setError(t('battleUI.clickDroneSlotToDeploy') || 'Click an empty drone slot to deploy this drone');
        setTimeout(() => setError(''), 2000);
        return;
      }
    }
    
    // ✅ FIX: Also block zone clicks when strategem is in unit targeting mode
    if (strategemTargetMode && strategemTargetInfo?.type === 'unit') {
      setError(t('battleUI.clickUnitNotZone'));
      setTimeout(() => setError(''), 2000);
      return;
    }
    
    // Handle deploy targeting: place the unit we're in targeting mode for (same instanceId backend will set)
    if (deployTargetMode && selectedDeployOrderId && selectedDeployInstanceId) {
      const frontUnitsCount = you.playedOrders.front.filter(u => u !== null).length;
      if (frontUnitsCount >= 5) {
        setError(t('battleUI.zoneFull'));
        setTimeout(() => setError(''), 2000);
        return;
      }
      playOrder(selectedDeployOrderId, "front", null, selectedDeployInstanceId);
      return;
    }
    
    // Handle normal unit deployment
    if (selectedOrderInstanceId) {
      // Check if selected order is a strategem
      const selectedOrder = you?.hand?.find(o => o.instanceId === selectedOrderInstanceId);
      if (selectedOrder?.type && (selectedOrder.type.toLowerCase() === 'strategem' || selectedOrder.type.toLowerCase() === 'stratagem')) {
        setError(t('battleUI.useRequestButton'));
        setTimeout(() => setError(''), 2000);
        return;
      }
      if (selectedOrder?.type === 'Drone') {
        setError(t('battleUI.clickDroneSlotToDeploy') || 'Click an empty drone slot to deploy this drone');
        setTimeout(() => setError(''), 2000);
        return;
      }
      
      // ✅ Check if zone is full - enter replacement mode
      const frontUnitsCount = you.playedOrders.front.filter(u => u !== null).length;
      if (frontUnitsCount >= 5) {
        setReplaceTargetMode(true);
        setSelectedReplaceUnitId(selectedOrderId);
        setSelectedReplaceInstanceId(selectedOrderInstanceId);
        setReplaceTargetZone('front');
        setError(t('battleUI.zoneFull'));
        return;
      }
      
      // ✅ FIX: Just deploy the unit - don't set targeting mode immediately
      // The backend will mark the unit with needsDeployTarget flag
      // The frontend will detect this from the updated game state and set up targeting
      // with the CORRECT instanceId that was assigned by the backend
      playOrder(selectedOrderId, "front", null, selectedOrderInstanceId);
      setSelectedOrderId(null);
      setSelectedOrderInstanceId(null);
    }
  }}
>

{/* ========== DEBUGGING: Always render all 5 slots ========== */}
{/* TODO: Change back to yourFront.slice(0, 5).map((order, idx) => { */}
<div style={{ display: "flex", gap: 0, justifyContent: "center", alignItems: "center", width: "100%", height: "100%", minHeight: 0 }}>
  {Array.from({length: 5}).map((_, idx) => {
    // Always render all 5 slots - get order from array if it exists
    const order = yourFront[idx];
    const visibleOrder = getVisibleUnit('your_front', idx, order);
    // COMMENTED OUT - slot number calculation no longer needed
    // const slotNumber = getGlobalSlotNumber('front', idx); // DEBUGGING: Slot number calculation
    
    if (!visibleOrder) {
      // ========== DEBUGGING: Empty slot box with number and zone label ==========
      // COMMENTED OUT - no longer needed
      /*
      const isHighlighted = slotNumber === 4 || slotNumber === 18; // DEBUGGING: Highlight slots 4 and 18
      return (
        <React.Fragment key={`your_front_empty_${idx}`}>
          <SlotDeathAnimations zoneName="your_front" index={idx} leftOffset={0} mobileLayout={isContainerMobile} viewportScale={viewportScale} />
          <div
            style={{
              position: 'relative',
              flex: '0 0 auto',
              marginLeft: idx > 0 ? (isContainerMobile ? '-5px' : '-15px') : '0',
              backgroundColor: isHighlighted ? 'rgba(255, 255, 0, 0.4)' : 'rgba(100, 100, 100, 0.3)',
              border: isHighlighted ? '4px solid #ffff00' : '1px dashed rgba(102, 102, 102, 0.35)',
              borderRadius: '8px',
              opacity: 0.8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '-1px',
              padding: '0px',
              boxSizing: 'border-box',
              boxShadow: isHighlighted ? '0 0 20px rgba(255, 255, 0, 0.8)' : 'none'
            }}
          >
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px'
            }}>
              <span style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#fff',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
              }}>
                {slotNumber}
              </span>
              <span style={{
                fontSize: '10px',
                fontWeight: 'bold',
                color: '#fff',
                textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                textTransform: 'uppercase'
              }}>
                Player Front
              </span>
            </div>
          </div>
        </React.Fragment>
      );
      */
      // ========== END DEBUGGING ==========
      return (
        <React.Fragment key={`your_front_empty_${idx}`}>
          <SlotDeathAnimations zoneName="your_front" index={idx} leftOffset={0} mobileLayout={isContainerMobile} viewportScale={viewportScale} />
          <div
            className="empty-slot-container"
            data-slot-zone="your_front"
            data-slot-index={idx}
            style={{
              position: 'relative',
              // MOBILE FIX: Let CSS control flex, margins on mobile
              ...(isContainerMobile ? {} : { flex: '0 0 auto', marginLeft: idx > 0 ? '-15px' : '0' }),
            }}
          >
            <div
              style={{
                position: 'relative',
              }}
            >
              <div
                style={{
                  ...ordersStyles.container,
                  backgroundColor: 'transparent',
                width: '200px',
                height: '100px',
                  margin: '-1px',
                  padding: '0px',
                  fontSize: '0.9em',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: '0px solid #ccc',
                  borderRadius: '8px',
                  boxSizing: 'border-box',
                  overflow: 'visible',
                  transform: 'none', // Removed to allow proper vertical centering
                  pointerEvents: 'none',
                }}
              >
                {/* Border matching the image size (80% width, 140% height) */}
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '80%', // Match image width
                    height: '140%', // Match image height
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    borderRadius: '8px',
                    pointerEvents: 'none',
                  }}
                />
              </div>
            </div>
          </div>
        </React.Fragment>
      );
    }
    
    const isHovered = hoveredPlayed.zone === "front" && hoveredPlayed.idx === idx;
    const isSelectedAttacker = selectedAttacker && selectedAttacker.zone === "front" && selectedAttacker.idx === idx;
    
    // Check if this unit is currently animating an attack
    // OLD CODE DISABLED - using new attack-initiated flow
    const isAttackingFront = false;
    const isBeingAttackedFront = false;
    const attackerStillExistsFront = true;
    const deploymentStylesFront = getDeploymentAnimationStyles(userId, 'front', idx);
    // ========== DEBUGGING: Highlight slots 4 and 18 ==========
    // COMMENTED OUT - no longer needed
    // const isHighlighted = slotNumber === 4 || slotNumber === 18; // TODO: Remove highlighting
    // ========== END DEBUGGING ==========
    
    return (
      <div
        key={`your_front_${visibleOrder?.id || order?.id || idx}_${idx}`}
        data-slot-zone="your_front"
        data-slot-index={idx}
        data-unit-key={visibleOrder?.instanceId ? `${userId}_${visibleOrder.instanceId}` : null}
          style={{
            position: 'relative',
            // MOBILE FIX: Let CSS control flex, margins, transform on mobile
            ...(isContainerMobile ? {} : {
              flex: '0 0 auto',
              marginLeft: idx > 0 ? '-15px' : '0',
              transform: isHovered ? "scale(1.05)" : "scale(1)",
              transformOrigin: "center",
            }),
            marginTop: 0,
            marginRight: isContainerMobile ? 0 : 0,
            marginBottom: 0,
          cursor: yourTurn ? "pointer" : "default",
          zIndex: getOverlappingBattlefieldSlotZIndex(
            idx,
            hoveredPlayed.zone === 'front' && hoveredPlayed.idx === idx,
            OVERLAPPING_BATTLEFIELD_SLOT_COUNT,
            yourFrontPreferLeftSlotStack
          ),
          transition: isContainerMobile ? 'none' : (deploymentStylesFront.animation ? "opacity 0.2s ease" : "transform 0.2s ease"),
          ...(isContainerMobile ? {} : deploymentStylesFront),
          opacity: (isAttackingFront && !attackerStillExistsFront) ? 0 : (isAttackingFront ? 0 : 1),
        }}
        onMouseEnter={() => {
          // Don't trigger hover if replacement buttons are showing for this unit
          const hasReplaceButtons = replaceTargetMode && clickedUnitForReplace && order && clickedUnitForReplace.instanceId === order.instanceId;
          if (!hasReplaceButtons) {
            playSoundEffect('/sounds/hoverover.mp3', 0.3);
            setHoveredPlayed({ zone: "front", idx });
          }
          sendOrderHover(visibleOrder?.instanceId);
        }}
        onMouseLeave={() => {
          clearHoveredPlayedSlot('front', idx);
        }}
        onMouseDown={(e) => {
          if (e.button !== 0) return;
          if (!yourTurn && !isContainerMobile) {
            e.preventDefault();
            return;
          }
          if (isContainerMobile || !visibleOrder) return;
          if (weaponDeployTargetMode && weaponDeployTargetInfo) return;
          if (baseBattleTargetingCursorActive) return;
          e.preventDefault();
          startAttackMouseDrag('front', idx, e.clientX, e.clientY);
        }}
        // Mobile touch handlers for attack dragging
        onTouchStart={(e) => {
          if (!isMobile || !yourTurn || !visibleOrder) return;
          if (weaponDeployTargetMode && weaponDeployTargetInfo) return;
          if (baseBattleTargetingCursorActive) return;
          handleAttackTouchStart(e, visibleOrder, 'front', idx);
        }}
        onTouchMove={(e) => {
          if (!isMobile || !yourTurn) return;
          handleAttackTouchMove(e);
        }}
        onTouchEnd={(e) => {
          if (!isMobile || !yourTurn) return;
          const { wasAttackDrag, attacker, target } = handleAttackTouchEnd(e);
          
          if (wasAttackDrag && attacker && target) {
            e.preventDefault();
            e.stopPropagation();
            
            if (target.type === 'general') {
              // Attack opponent general
              attackOrder(attacker.zone, attacker.index, 'general', null);
            } else if (target.type === 'unit') {
              // Attack opponent unit
              attackOrder(attacker.zone, attacker.index, target.zone, target.index);
            }
          } else if (!wasAttackDrag) {
            // Not a drag - let click handler process it on next event cycle
            // Do nothing here, onClick will handle it
          }
        }}
        onClick={(e) => {
          // On mobile, skip onClick to prevent double-firing
          if (isMobile) return;
          
          
          // Check for blocking overlays
          const allBlockOverlays = [
            'front-block-overlay',
            'flank-block-overlay',
            'end-turn-block-overlay',
            'forsaken-sentinel-block-overlay',
            'counter-strike-player-front-block-overlay',
            'counter-strike-opponent-front-block-overlay',
            'counter-strike-keep-button-block-overlay',
            'isabela-block-overlay',
            'tranquilizer-block-overlay'
          ];
          const blockingOverlays = allBlockOverlays.filter(id => {
            const overlay = document.getElementById(id);
            return overlay && overlay.style.display !== 'none' && window.getComputedStyle(overlay).display !== 'none';
          });
          if (blockingOverlays.length > 0) {
            console.warn('⚠️ BLOCKING OVERLAYS DETECTED:', blockingOverlays);
            blockingOverlays.forEach(id => {
              const overlay = document.getElementById(id);
              if (overlay) {
                const rect = overlay.getBoundingClientRect();
                const clickX = e.clientX;
                const clickY = e.clientY;
                const isOverOverlay = clickX >= rect.left && clickX <= rect.right && clickY >= rect.top && clickY <= rect.bottom;
                console.warn(`⚠️ Overlay ${id}:`, {
                  display: overlay.style.display,
                  computedDisplay: window.getComputedStyle(overlay).display,
                  pointerEvents: overlay.style.pointerEvents,
                  computedPointerEvents: window.getComputedStyle(overlay).pointerEvents,
                  zIndex: overlay.style.zIndex,
                  computedZIndex: window.getComputedStyle(overlay).zIndex,
                  rect: { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom },
                  clickPos: { x: clickX, y: clickY },
                  isOverOverlay
                });
              }
            });
          } else {
          }
          
          // Allow clicks during counter strike deploy targeting (e.g. Bulwark +2 target)
          const isCounterStrikeDeployTargeting = deployTargetMode && selectedDeployOrderId && !yourTurn;
          if (!yourTurn && !isCounterStrikeDeployTargeting) {
            return;
          }

          setOpponentInspectSelection(null);

          const isMission5CornClick =
            gameState?.missionId === 'chapter_1_mission_5' &&
            visibleOrder?.isMission5Corn === true &&
            hoveredPlayed?.zone === 'front' &&
            hoveredPlayed?.idx === idx &&
            !attackMode &&
            !replaceTargetMode &&
            !deployTargetMode &&
            !strategemTargetMode &&
            !weaponTargetMode &&
            !weaponDeployTargetMode &&
            !droneTargetMode &&
            !confuseTargetMode &&
            !specialTargetMode &&
            !selectedOrderInstanceId;
          if (isMission5CornClick) {
            return;
          }
          
          // Handle replacement mode (for full zones)
          if (replaceTargetMode && selectedReplaceUnitId && replaceTargetZone === "front") {
            if (!order) {
              return;
            }
            // Clear hover state to force minimal view
            setHoveredPlayed({ zone: null, idx: null });
            
            // Store clicked unit info to trigger button rendering
            setClickedUnitForReplace({
              unit: order,
              zone: "front",
              index: idx,
              instanceId: order.instanceId
            });
            return;
          }
          
          // Handle unit click when unit is selected in hand and zone is full (immediate replace mode)
          if (selectedOrderInstanceId && !replaceTargetMode) {
            const selectedOrder = you?.hand?.find(o => o.instanceId === selectedOrderInstanceId);
            // Skip if strategem is selected
            if (selectedOrder?.type && (selectedOrder.type.toLowerCase() === 'strategem' || selectedOrder.type.toLowerCase() === 'stratagem')) {
              // Let other handlers process this
            } else {
              // Check if front zone is full
              const frontUnitsCount = you.playedOrders.front.filter(u => u !== null).length;
              if (frontUnitsCount >= 5) {
                // Clear hover state to force minimal view
                setHoveredPlayed({ zone: null, idx: null });
                
                // Enter replace mode and immediately show buttons on this unit
                setReplaceTargetMode(true);
                setSelectedReplaceUnitId(selectedOrderId);
                setSelectedReplaceInstanceId(selectedOrderInstanceId);
                setReplaceTargetZone('front');
                if (!order) {
                  return;
                }
                setClickedUnitForReplace({
                  unit: order,
                  zone: "front",
                  index: idx,
                  instanceId: order.instanceId
                });
                return;
              }
            }
          }

          // Counter strike: click a unit in a full front zone to replace with the pending counter strike unit
          if (selectedCounterStrikeOrder && !replaceTargetMode) {
            const frontUnitsCount = you.playedOrders.front.filter(u => u !== null).length;
            if (frontUnitsCount >= 5) {
              if (!order) {
                return;
              }
              setHoveredPlayed({ zone: null, idx: null });
              enterCounterStrikeReplaceMode('front');
              setClickedUnitForReplace({
                unit: order,
                zone: 'front',
                index: idx,
                instanceId: order.instanceId
              });
              return;
            }
          }
          
          // Handle drone targeting
          if (droneTargetMode && selectedDroneId) {
            targetDrone("front", idx, ws, setError, setDroneTargetMode, setSelectedDroneId, setSelectedDroneInstanceId, selectedDroneId, selectedDroneInstanceId, gameId, gameState, userId);
            return;
          }
          
          // Handle weapon targeting
          if (weaponTargetMode && selectedWeaponId) {
            const targetUnit = {
              ...visibleOrder,
              zone: "front",
              index: idx
            };
            attachWeapon(targetUnit, ws, setError, setWeaponTargetMode, selectedWeaponId, setSelectedWeaponId, selectedWeaponInstanceId, gameId, userId, gameState, setWeaponDeployTargetMode, setWeaponDeployTargetInfo);
            return;
          }
          
          // Handle weapon deploy targeting
          if (weaponDeployTargetMode && weaponDeployTargetInfo) {
            const targetUnit = {
              ...visibleOrder,
              zone: "front",
              index: idx
            };
            weaponDeployTarget(targetUnit, ws, setError, weaponDeployTargetInfo, gameId);
            return;
          }
          
          // Handle confuse targeting
          if (confuseTargetMode && selectedConfuseWeaponId) {
            targetConfuse("front", idx, ws, setError, setConfuseTargetMode, setSelectedConfuseWeaponId);
            return;
          }
          
          if (euEpZoneTargetMode) {
            sendExploreUnknownEpSpend('useExploreUnknownEpZone', { zone: 'front' });
            return;
          }

          if (euEpTargetMode && visibleOrder?.instanceId) {
            if (!isExploreUnknownEpTargetHighlight(visibleOrder, 'front')) {
              setError(t('exploreUnknownBattle.epTargetInvalidUnit'));
              return;
            }
            sendExploreUnknownEpSpend('useExploreUnknownEpTarget', {
              targetInstanceId: visibleOrder.instanceId,
            });
            return;
          }

          // Handle strategem targeting
          if (strategemTargetMode && selectedStrategemId) {
            const animationConfig = {
              registerAnimation: registerLocalStrategemTargetAnimation,
              strategemInstanceId: strategemTargetInfo?.strategemInstanceId,
              strategemId: selectedStrategemId,
              gameState,
              userId
            };
            
            if (strategemTargetInfo?.type === 'zone') {
              // Target the zone itself
              targetStrategem("zone", "front", null, null, null, ws, gameId, setError, setStrategemTargetMode, setSelectedStrategemId, setStrategemTargetInfo, animationConfig);
            } else {
              // Target a unit in the zone
              const targetUnit = visibleOrder;
              targetStrategem("unit", "front", idx, "friendly", targetUnit?.instanceId, ws, gameId, setError, setStrategemTargetMode, setSelectedStrategemId, setStrategemTargetInfo, animationConfig);
            }
            return;
          }
          
          // Handle deploy targeting
          if (deployTargetMode && selectedDeployOrderId) {
            handleTargetDeploy("front", idx, "friendly");
            return;
          }
          
          // Handle attack selection
          if (
            selectedAttacker &&
            selectedAttacker.zone === "front" &&
            selectedAttacker.idx === idx
          ) {
            setSelectedAttacker(null);
            setAttackMode(false);
            setLastAttackedTarget(null); // Clear when canceling attack mode
          } else {
            // ✅ VALIDATION: Check if unit has 0 attack rating - cannot attack
            const attackRating = visibleOrder?.attack_rating ?? 0;
            if (attackRating === 0) {
              setError('This unit has 0 attack rating and cannot attack');
              return;
            }
            
            setSelectedAttacker({ zone: "front", idx, id: visibleOrder?.id || order?.id });
            setAttackMode(true);
            setLastAttackedTarget(null); // Clear when selecting new attacker
          }
        }}
      >
        {isNewlyDeployedLegendaryUnique(userId, 'front', idx) && (
          <LegendaryUniqueDeployRay />
        )}
        {(() => {
          const computedIsTarget = isTarget({
            strategemTargetMode: effectiveStrategemTargetMode,
            selectedStrategemId: effectiveSelectedStrategemId,
            strategemTargetInfo: effectiveStrategemTargetInfo,
            immobilizeTargetMode: false,
            confuseTargetMode,
            weaponTargetMode: effectiveWeaponTargetMode,
            selectedWeaponId,
            weaponDeployTargetMode,
            deployTargetMode,
            selectedDeployOrderId,
            selectedDeployInstanceId,
            selectedDeployZone,
            zone: "front",
            targetingData,
            gameState,
            playerId: userId,
            attackMode: effectiveAttackMode,
            selectedAttacker: effectiveSelectedAttacker,
            droneTargetMode: effectiveDroneTargetMode,
            selectedDroneId: effectiveSelectedDroneId,
            unit: visibleOrder
          });
          const guardedIsTarget =
            computedIsTarget || isExploreUnknownEpTargetHighlight(visibleOrder, 'front');
          // Check if replacement buttons are showing for this unit
          const hasReplaceButtons = replaceTargetMode && clickedUnitForReplace && order && clickedUnitForReplace.instanceId === order.instanceId;
          const isFriendlySlotSelected =
            (selectedAttacker && selectedAttacker.zone === "front" && selectedAttacker.idx === idx) ||
            selectedOrderInstanceId === visibleOrder.instanceId;
          const showFriendlyExpandedPreview =
            isHovered ||
            (isFriendlySlotSelected && hoveredPlayed.zone == null);
          return (
            <>
              <div
                className={`deployed-unit-slot ${!isProtectUnit(visibleOrder) && !isNewlyDeployed(userId, 'front', idx) && !visibleOrder?._isRepositioning && !isImmobilizedUI(visibleOrder, "your_front") ? 'deployed-unit-slot-idle' : ''}`.trim()}
                data-slot-zone="your_front"
                data-slot-index={idx}
                style={{ position: 'relative' }}
              >
                {isNewlyDeployedLegendary(userId, 'front', idx) && (
                  <LegendaryDeployGlow />
                )}
                <OrderDesign
                  order={visibleOrder}
                  count={0} 
                  minimal={!showFriendlyExpandedPreview || hasReplaceButtons}
                  scale={viewportScale}
                  fixedDesktopExpandedPosition={fixedDesktopExpandedPosition}
                  isOOR={visibleOrder.isOOR}
                  isSelected={isFriendlySlotSelected}
                  isHighlighted={highlightedInstanceIds.some(id => String(id).trim() === String(visibleOrder.instanceId).trim())}
                  isTarget={guardedIsTarget}
                  targetMode={getTargetingMode(strategemTargetMode, false, confuseTargetMode, false, deployTargetMode, selectedDeployOrderId, selectedDeployZone)}
                  zoneName="your_front"
                  slotIndex={idx}
                  gameState={gameState}
                  userId={activeUserId}
                  isHiddenDuringAnimation={attackAnimations.isUnitHidden(visibleOrder.instanceId)}
                  isLethalDeathFading={isSlotLethalFading('your_front', idx, visibleOrder?.instanceId)}
                  isRedeemMarkedForDeath={isRedeemMarkedForDeathForUnit(visibleOrder?.instanceId)}
                  isRedeemDeathFinishing={isRedeemDeathFinishingForUnit(visibleOrder?.instanceId)}
                  lethalDeathFadeDurationMs={getLethalFadeDurationMs('your_front', idx)}
                  onLethalDeathFadeComplete={() => completeLethalAttackFade('your_front', idx)}
                  isUnitTargetInPendingAttack={attackAnimations.isUnitTargetInPendingAttack}
                  isViewedByOpponent={!!(opponentLookingAtInstanceId && visibleOrder?.instanceId && String(opponentLookingAtInstanceId) === String(visibleOrder.instanceId))}
                  renderButtons={() => (
                    gameState?.missionId === 'chapter_1_mission_5' &&
                    visibleOrder?.isMission5Corn === true &&
                    isHovered ? (
                      <>
                        {(() => {
                          const groupInfo = getMission5CornGroupInfo('front', idx);
                          if (groupInfo.size <= 0) return null;
                          return (
                        <button
                          className="replace-button replace-button-confirm"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeMission5CornGroup('front', idx, visibleOrder);
                          }}
                        >
                          {`Cut corn for ${groupInfo.cost} intel`}
                        </button>
                          );
                        })()}
                      </>
                    ) : null
                  )}
                  onBattlefieldExpandedPointerLeave={() => {
                    setHoveredPlayed({ zone: null, idx: null });
                    sendOrderHover(null);
                  }}
                  passThroughExpandedPortalForTargeting={passThroughExpandedBattlefieldPortals}
                />
                <DemoralizingBrokenShieldOverlay
                  visible={showDemoralizingBrokenShieldFor(visibleOrder?.instanceId)}
                />
              </div>
              {/* ========== DEBUGGING: Slot number and zone label overlay ========== */}
              {/* COMMENTED OUT - no longer needed */}
              {/*
              <div style={{
                position: 'absolute',
                top: '5px',
                left: '5px',
                fontSize: '12px',
                fontWeight: 'bold',
                color: '#fff',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                padding: '2px 6px',
                borderRadius: '4px',
                zIndex: 1000,
                pointerEvents: 'none',
                display: 'flex',
                flexDirection: 'column',
                gap: '2px'
              }}>
                <span>{slotNumber}</span>
                <span style={{ fontSize: '9px', textTransform: 'uppercase' }}>Player Front</span>
              </div>
              */}
              {/* ========== END DEBUGGING ========== */}
              {/* Inline Replace/Cancel buttons */}
              {hasReplaceButtons && (
                <div className="replace-buttons-container">
                  <button
                    className="replace-button replace-button-confirm"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (counterStrikeReplaceMode) {
                        handleReplaceCounterStrike(clickedUnitForReplace);
                      } else {
                        handleReplaceUnit(clickedUnitForReplace);
                      }
                    }}
                  >
                    Replace
                  </button>
                  <button
                    className="replace-button replace-button-cancel"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancelReplace();
                    }}
                  >
                    Cancel
                  </button>
                </div>
              )}
              {/* Portal displacement (War Effort): one charge to switch zone (front → flank) */}
              {gameState?.defenderPortalActive && String(userId) === String(gameState.warEffortDefenderPlayerId) && visibleOrder?.portalDisplacementCharges > 0 && (
                <button
                  type="button"
                  className="portal-displacement-button"
                  data-tooltip="Switch zone (Portal)"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePortalDisplacement(visibleOrder, 'flank');
                  }}
                >
                  Portal (→flank)
                </button>
              )}
            </>
          );
        })()}
      </div>
    );
  })}
</div>

          </div>
          {/* Flank */}
<div
  data-zone-key="flank"
  className={`battle-zone viewport-scaled flank ${(() => {
    const selectedOrder = selectedOrderInstanceId ? you?.hand?.find(o => o.instanceId === selectedOrderInstanceId) : null;
    const selectedOrderType = (selectedOrder?.type || '').toLowerCase();
    const isUnitZoneDeploySelection = selectedOrderType !== 'strategem' && selectedOrderType !== 'stratagem' && selectedOrderType !== 'drone' && selectedOrderType !== 'weapon';
    const shouldHighlightForDeployment = (((selectedOrderInstanceId && yourTurn) || selectedCounterStrikeOrder) && isUnitZoneDeploySelection) || isDeployDragActive;
    const isStrategemZoneTargeting = effectiveStrategemTargetMode && effectiveStrategemTargetInfo?.type === 'zone';
    const targetingRules = effectiveStrategemTargetInfo?.targetingOptions?.[0];
    const playerRestriction = targetingRules?.player || 'opponent';
    const isFriendlyZoneTargeting = isStrategemZoneTargeting && (playerRestriction === 'friendly' || playerRestriction === 'both');

    return getTargetingBorderClassName(shouldHighlightForDeployment || isFriendlyZoneTargeting || euEpZoneTargetMode);
  })()}`}
  style={{
    position: 'relative',
    zIndex: 1, // Keep constant to prevent position shifts
    ...getSlideInStyles(animationsStarted, 'right', 400),
    borderLeft: '1px solid rgba(255,255,255,0.5)',
    ...(isContainerMobile ? { width: 'auto', flex: '1 1 0', minWidth: 0, height: '100%', minHeight: 0 } : {}),
    ...(() => {
      // Check if we should highlight for unit deployment (carousel, counter strike, or drag)
      // ✅ FIX: Exclude strategems from deployment highlighting
      // ✅ FIX: Allow counterstrike highlighting even when it's not the player's turn (counterstrike happens during opponent's turn)
      const selectedOrder = selectedOrderInstanceId ? you?.hand?.find(o => o.instanceId === selectedOrderInstanceId) : null;
      const selectedOrderType = (selectedOrder?.type || '').toLowerCase();
      const isUnitZoneDeploySelection = selectedOrderType !== 'strategem' && selectedOrderType !== 'stratagem' && selectedOrderType !== 'drone' && selectedOrderType !== 'weapon';
      const shouldHighlightForDeployment = (((selectedOrderInstanceId && yourTurn) || selectedCounterStrikeOrder) && isUnitZoneDeploySelection) || isDeployDragActive;

      if (shouldHighlightForDeployment) {
        // Use unified targeting border style
        return getTargetingBorderStyles(true);
      }

      const epZoneStyles = getExploreUnknownEpZoneTargetingStyles(euEpZoneTargetMode);
      if (epZoneStyles) return epZoneStyles;

      // Check for strategem zone targeting mode (from Request button or drag)
      if (effectiveStrategemTargetMode && effectiveStrategemTargetInfo?.type === 'zone') {
        const targetingRules = effectiveStrategemTargetInfo?.targetingOptions?.[0];
        const playerRestriction = targetingRules?.player || 'opponent';
        // Highlight friendly zones if targeting allows friendly
        if (playerRestriction === 'friendly' || playerRestriction === 'both') {
          return getTargetingBorderStyles(true);
        }
      }
      
      return getZoneTargetingStyles(effectiveStrategemTargetMode, effectiveStrategemTargetInfo, 'zone', 'flank');
    })()
  }}
  onDragOver={e => e.preventDefault()}
  onDrop={e => {
    if (!yourTurn || !draggedOrderId) return;
    playOrder(draggedOrderId, "flank", null, draggedOrderInstanceId);
    setDraggedOrderId(null);
    setDraggedOrderInstanceId(null);
    setSelectedOrderId(null);
    setSelectedOrderInstanceId(null);
  }}
  onClick={() => {
    // ✅ FIX: Allow counterstrike zone clicks even when it's not the player's turn (counterstrike happens during opponent's turn)
    // Handle counter strike deployment FIRST (before turn check) - separate from carousel
    if (selectedCounterStrikeOrder) {
      const flankUnitsCount = you.playedOrders.flank.filter(u => u !== null).length;
      if (flankUnitsCount >= 5) {
        enterCounterStrikeReplaceMode('flank');
        return;
      }
      playCounterStrike(selectedCounterStrikeOrder.id, "flank", selectedCounterStrikeOrder.instanceId);
      setSelectedCounterStrikeOrder(null);
      return;
    }
    
    // Allow zone clicks during counter strike deploy targeting (e.g. Bulwark needs to target a unit)
    const isCounterStrikeDeployTargetingFlank = deployTargetMode && selectedDeployOrderId && !yourTurn;
    if (!yourTurn && !isCounterStrikeDeployTargetingFlank) return;

    if (euEpZoneTargetMode) {
      sendExploreUnknownEpSpend('useExploreUnknownEpZone', { zone: 'flank' });
      return;
    }
    
    // Handle strategem zone targeting (from Request button flow) - CHECK THIS FIRST
    const flankAnimationConfig = {
      registerAnimation: registerLocalStrategemTargetAnimation,
      strategemInstanceId: strategemTargetInfo?.strategemInstanceId,
      strategemId: selectedStrategemId,
      gameState,
      userId
    };
    if (handleZoneClick("flank", strategemTargetMode, selectedStrategemId, strategemTargetInfo, targetStrategem, ws, gameId, setError, setStrategemTargetMode, setSelectedStrategemId, setStrategemTargetInfo, targetingData, flankAnimationConfig)) {
      return;
    }
    
    // ✅ FIX: Check if selected order is a strategem - prevent zone deployment
    // Only block if NOT in strategem targeting mode
    if (selectedOrderInstanceId && !strategemTargetMode) {
      const selectedOrder = you?.hand?.find(o => o.instanceId === selectedOrderInstanceId);
      if (selectedOrder?.type && (selectedOrder.type.toLowerCase() === 'strategem' || selectedOrder.type.toLowerCase() === 'stratagem')) {
        setError(t('battleUI.useRequestButton'));
        setTimeout(() => setError(''), 2000);
        return;
      }
      if (selectedOrder?.type === 'Drone') {
        setError(t('battleUI.clickDroneSlotToDeploy') || 'Click an empty drone slot to deploy this drone');
        setTimeout(() => setError(''), 2000);
        return;
      }
    }
    
    // ✅ FIX: Also block zone clicks when strategem is in unit targeting mode
    if (strategemTargetMode && strategemTargetInfo?.type === 'unit') {
      setError(t('battleUI.clickUnitNotZone'));
      setTimeout(() => setError(''), 2000);
      return;
    }
    
    // Handle deploy targeting: place the unit we're in targeting mode for (same instanceId backend will set)
    if (deployTargetMode && selectedDeployOrderId && selectedDeployInstanceId) {
      const flankUnitsCount = you.playedOrders.flank.filter(u => u !== null).length;
      if (flankUnitsCount >= 5) {
        setError(t('battleUI.zoneFull'));
        setTimeout(() => setError(''), 2000);
        return;
      }
      playOrder(selectedDeployOrderId, "flank", null, selectedDeployInstanceId);
      return;
    }
    
    // Handle normal unit deployment
    if (selectedOrderInstanceId) {
      // Check if selected order is a strategem
      const selectedOrder = you?.hand?.find(o => o.instanceId === selectedOrderInstanceId);
      if (selectedOrder?.type && (selectedOrder.type.toLowerCase() === 'strategem' || selectedOrder.type.toLowerCase() === 'stratagem')) {
        setError(t('battleUI.useRequestButton'));
        setTimeout(() => setError(''), 2000);
        return;
      }
      if (selectedOrder?.type === 'Drone') {
        setError(t('battleUI.clickDroneSlotToDeploy') || 'Click an empty drone slot to deploy this drone');
        setTimeout(() => setError(''), 2000);
        return;
      }
      
      // ✅ Check if zone is full - enter replacement mode
      const flankUnitsCount = you.playedOrders.flank.filter(u => u !== null).length;
      if (flankUnitsCount >= 5) {
        setReplaceTargetMode(true);
        setSelectedReplaceUnitId(selectedOrderId);
        setSelectedReplaceInstanceId(selectedOrderInstanceId);
        setReplaceTargetZone('flank');
        setError(t('battleUI.zoneFull'));
        return;
      }
      
      // ✅ FIX: Just deploy the unit - don't set targeting mode immediately
      // The backend will mark the unit with needsDeployTarget flag
      // The frontend will detect this from the updated game state and set up targeting
      // with the CORRECT instanceId that was assigned by the backend
      playOrder(selectedOrderId, "flank", null, selectedOrderInstanceId);
      setSelectedOrderId(null);
      setSelectedOrderInstanceId(null);
    }
  }}
>


{/* ========== DEBUGGING: Always render all 5 slots ========== */}
{/* TODO: Change back to yourFlank.slice(0, 5).map((order, idx) => { */}
<div style={{ display: "flex", gap: 0, justifyContent: "center", alignItems: "center", width: "100%", height: "100%", minHeight: 0 }}>
  {Array.from({length: 5}).map((_, idx) => {
    // Always render all 5 slots - get order from array if it exists
    const order = yourFlank[idx];
    const visibleOrder = getVisibleUnit('your_flank', idx, order);
    // COMMENTED OUT - slot number calculation no longer needed
    // const slotNumber = getGlobalSlotNumber('flank', idx); // DEBUGGING: Slot number calculation
    
    if (!visibleOrder) {
      // ========== DEBUGGING: Empty slot box with number and zone label ==========
      // COMMENTED OUT - no longer needed
      /*
      const isHighlighted = slotNumber === 4 || slotNumber === 18; // DEBUGGING: Highlight slots 4 and 18
      return (
        <React.Fragment key={`your_flank_empty_${idx}`}>
          <SlotDeathAnimations zoneName="your_flank" index={idx} leftOffset={0} mobileLayout={isContainerMobile} viewportScale={viewportScale} />
          <div
            style={{
              position: 'relative',
              flex: '0 0 auto',
              marginLeft: idx > 0 ? (isContainerMobile ? '-5px' : '-15px') : '0',
              backgroundColor: isHighlighted ? 'rgba(255, 255, 0, 0.4)' : 'rgba(100, 100, 100, 0.3)',
              border: isHighlighted ? '4px solid #ffff00' : '1px dashed rgba(102, 102, 102, 0.35)',
              borderRadius: '8px',
              opacity: 0.8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '-1px',
              padding: '0px',
              boxSizing: 'border-box',
              boxShadow: isHighlighted ? '0 0 20px rgba(255, 255, 0, 0.8)' : 'none'
            }}
          >
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px'
            }}>
              <span style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#fff',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
              }}>
                {slotNumber}
              </span>
              <span style={{
                fontSize: '10px',
                fontWeight: 'bold',
                color: '#fff',
                textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                textTransform: 'uppercase'
              }}>
                Player Flank
              </span>
            </div>
          </div>
        </React.Fragment>
      );
      */
      // ========== END DEBUGGING ==========
      return (
        <React.Fragment key={`your_flank_empty_${idx}`}>
          <SlotDeathAnimations zoneName="your_flank" index={idx} leftOffset={0} mobileLayout={isContainerMobile} viewportScale={viewportScale} />
          <div
            className="empty-slot-container"
            data-slot-zone="your_flank"
            data-slot-index={idx}
            style={{
              position: 'relative',
              // MOBILE FIX: Let CSS control flex, margins on mobile
              ...(isContainerMobile ? {} : { flex: '0 0 auto', marginLeft: idx > 0 ? '-15px' : '0' }),
            }}
          >
            <div
              style={{
                position: 'relative',
              }}
            >
              <div
                style={{
                  ...ordersStyles.container,
                  backgroundColor: 'transparent',
                width: '200px',
                height: '100px',
                  margin: '-1px',
                  padding: '0px',
                  fontSize: '0.9em',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: '0px solid #ccc',
                  borderRadius: '8px',
                  boxSizing: 'border-box',
                  overflow: 'visible',
                  transform: 'none', // Removed to allow proper vertical centering
                  pointerEvents: 'none',
                }}
              >
                {/* Border matching the image size (80% width, 140% height) */}
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '80%', // Match image width
                    height: '140%', // Match image height
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    borderRadius: '8px',
                    pointerEvents: 'none',
                  }}
                />
              </div>
            </div>
          </div>
        </React.Fragment>
      );
    }
    
    const isHovered = hoveredPlayed.zone === "flank" && hoveredPlayed.idx === idx;
    const isSelectedAttacker = selectedAttacker && selectedAttacker.zone === "flank" && selectedAttacker.idx === idx;
    
    // Check if this unit is currently animating an attack
    // OLD CODE DISABLED - using new attack-initiated flow
    const isAttackingFlankPlayer = false;
    const isBeingAttackedFlankPlayer = false;
    const attackerStillExistsFlankPlayer = true;
    const deploymentStylesFlankPlayer = getDeploymentAnimationStyles(userId, 'flank', idx);
    // ========== DEBUGGING: Highlight slots 4 and 18 ==========
    // COMMENTED OUT - no longer needed
    // const isHighlighted = slotNumber === 4 || slotNumber === 18; // TODO: Remove highlighting
    // ========== END DEBUGGING ==========
    
    return (
      <div key={`your_flank_${visibleOrder.id}_${idx}`} data-slot-zone="your_flank" data-slot-index={idx} data-unit-key={visibleOrder?.instanceId ? `${userId}_${visibleOrder.instanceId}` : null} style={{ 
        position: "relative", 
        // MOBILE FIX: Let CSS control flex, margins on mobile
        ...(isContainerMobile ? {} : { flex: '0 0 auto', marginLeft: idx > 0 ? '-15px' : '0' }),
        zIndex: getOverlappingBattlefieldSlotZIndex(
          idx,
          hoveredPlayed.zone === 'flank' && hoveredPlayed.idx === idx,
          OVERLAPPING_BATTLEFIELD_SLOT_COUNT,
          yourFlankPreferLeftSlotStack
        )
      }}>
        <div
          style={{
            position: 'relative',
            marginTop: 0,
            marginRight: isContainerMobile ? 0 : 0,
            marginBottom: 0,
            cursor: yourTurn ? "pointer" : "default",
            zIndex: getOverlappingBattlefieldSlotZIndex(
              idx,
              hoveredPlayed.zone === 'flank' && hoveredPlayed.idx === idx,
              OVERLAPPING_BATTLEFIELD_SLOT_COUNT,
              yourFlankPreferLeftSlotStack
            ),
            transition: isContainerMobile ? 'none' : (deploymentStylesFlankPlayer.animation ? "opacity 0.2s ease" : "transform 0.2s ease"),
            // MOBILE FIX: Let CSS control transform on mobile
            ...(isContainerMobile ? {} : {
              transform: isHovered ? "scale(1.05)" : "scale(1)",
              transformOrigin: "center",
            }),
          ...(isContainerMobile ? {} : deploymentStylesFlankPlayer),
            opacity: 1,
          }}
          onMouseEnter={() => {
            // Don't trigger hover if replacement buttons are showing for this unit
            const hasReplaceButtons = replaceTargetMode && clickedUnitForReplace && order && clickedUnitForReplace.instanceId === order.instanceId;
            if (!hasReplaceButtons) {
              playSoundEffect('/sounds/hoverover.mp3', 0.3);
              setHoveredPlayed({ zone: "flank", idx });
            }
            sendOrderHover(visibleOrder?.instanceId);
          }}
          onMouseLeave={() => {
            clearHoveredPlayedSlot('flank', idx);
          }}
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            if (!yourTurn && !isContainerMobile) {
              e.preventDefault();
              return;
            }
            if (isContainerMobile || !visibleOrder) return;
            if (weaponDeployTargetMode && weaponDeployTargetInfo) return;
            if (baseBattleTargetingCursorActive) return;
            e.preventDefault();
            startAttackMouseDrag('flank', idx, e.clientX, e.clientY);
          }}
          // Mobile touch handlers for attack dragging
          onTouchStart={(e) => {
            if (!isMobile || !yourTurn || !visibleOrder) return;
            if (weaponDeployTargetMode && weaponDeployTargetInfo) return;
            if (baseBattleTargetingCursorActive) return;
            handleAttackTouchStart(e, visibleOrder, 'flank', idx);
          }}
          onTouchMove={(e) => {
            if (!isMobile || !yourTurn) return;
            handleAttackTouchMove(e);
          }}
          onTouchEnd={(e) => {
            if (!isMobile || !yourTurn) return;
            const { wasAttackDrag, attacker, target } = handleAttackTouchEnd(e);
            
            if (wasAttackDrag && attacker && target) {
              e.preventDefault();
              e.stopPropagation();
              
              if (target.type === 'general') {
                // Attack opponent general
                attackOrder(attacker.zone, attacker.index, 'general', null);
              } else if (target.type === 'unit') {
                // Attack opponent unit
                attackOrder(attacker.zone, attacker.index, target.zone, target.index);
              }
            } else if (!wasAttackDrag) {
              // Not a drag - let click handler process it on next event cycle
              // Do nothing here, onClick will handle it
            }
          }}
          onClick={() => {
            // On mobile, skip onClick to prevent double-firing
            if (isMobile) return;
            
            // Allow clicks during counter strike deploy targeting (e.g. Bulwark +2 target)
            const isCounterStrikeDeployTargeting = deployTargetMode && selectedDeployOrderId && !yourTurn;
            if (!yourTurn && !isCounterStrikeDeployTargeting) return;

            setOpponentInspectSelection(null);

            const isMission5CornClick =
              gameState?.missionId === 'chapter_1_mission_5' &&
              visibleOrder?.isMission5Corn === true &&
              hoveredPlayed?.zone === 'flank' &&
              hoveredPlayed?.idx === idx &&
              !attackMode &&
              !replaceTargetMode &&
              !deployTargetMode &&
              !strategemTargetMode &&
              !weaponTargetMode &&
              !weaponDeployTargetMode &&
              !droneTargetMode &&
              !confuseTargetMode &&
              !specialTargetMode &&
              !selectedOrderInstanceId;
            if (isMission5CornClick) {
              return;
            }
            
            // Handle replacement mode (for full zones)
            if (replaceTargetMode && selectedReplaceUnitId && replaceTargetZone === "flank") {
              if (!order) {
                return;
              }
              // Clear hover state to force minimal view
              setHoveredPlayed({ zone: null, idx: null });
              
              // Store clicked unit info to trigger button rendering
              setClickedUnitForReplace({
                unit: order,
                zone: "flank",
                index: idx,
                instanceId: order.instanceId
              });
              return;
            }
            
            // Handle unit click when unit is selected in hand and zone is full (immediate replace mode)
            if (selectedOrderInstanceId && !replaceTargetMode) {
              const selectedOrder = you?.hand?.find(o => o.instanceId === selectedOrderInstanceId);
              // Skip if strategem is selected
              if (selectedOrder?.type && (selectedOrder.type.toLowerCase() === 'strategem' || selectedOrder.type.toLowerCase() === 'stratagem')) {
                // Let other handlers process this
              } else {
                // Check if flank zone is full
                const flankUnitsCount = you.playedOrders.flank.filter(u => u !== null).length;
                if (flankUnitsCount >= 5) {
                  // Clear hover state to force minimal view
                  setHoveredPlayed({ zone: null, idx: null });
                  
                  // Enter replace mode and immediately show buttons on this unit
                  setReplaceTargetMode(true);
                  setSelectedReplaceUnitId(selectedOrderId);
                  setSelectedReplaceInstanceId(selectedOrderInstanceId);
                  setReplaceTargetZone('flank');
                  if (!order) {
                    return;
                  }
                  setClickedUnitForReplace({
                    unit: order,
                    zone: "flank",
                    index: idx,
                    instanceId: order.instanceId
                  });
                  return;
                }
              }
            }

            // Counter strike: click a unit in a full flank zone to replace with the pending counter strike unit
            if (selectedCounterStrikeOrder && !replaceTargetMode) {
              const flankUnitsCount = you.playedOrders.flank.filter(u => u !== null).length;
              if (flankUnitsCount >= 5) {
                if (!order) {
                  return;
                }
                setHoveredPlayed({ zone: null, idx: null });
                enterCounterStrikeReplaceMode('flank');
                setClickedUnitForReplace({
                  unit: order,
                  zone: 'flank',
                  index: idx,
                  instanceId: order.instanceId
                });
                return;
              }
            }
            
            // Handle drone targeting
            if (droneTargetMode && selectedDroneId) {
              targetDrone("flank", idx, ws, setError, setDroneTargetMode, setSelectedDroneId, setSelectedDroneInstanceId, selectedDroneId, selectedDroneInstanceId, gameId, gameState, userId);
              return;
            }
            
            // Handle weapon targeting
            if (weaponTargetMode && selectedWeaponId) {
              const targetUnit = {
                ...visibleOrder,
                zone: "flank",
                index: idx
              };
              attachWeapon(targetUnit, ws, setError, setWeaponTargetMode, selectedWeaponId, setSelectedWeaponId, selectedWeaponInstanceId, gameId, userId, gameState, setWeaponDeployTargetMode, setWeaponDeployTargetInfo);
              return;
            }

            // Handle weapon deploy targeting (e.g. Toxic Shroud Grenade confuse step)
            if (weaponDeployTargetMode && weaponDeployTargetInfo) {
              const targetUnit = {
                ...visibleOrder,
                zone: "flank",
                index: idx
              };
              weaponDeployTarget(targetUnit, ws, setError, weaponDeployTargetInfo, gameId);
              return;
            }
            
            // Handle confuse targeting
            if (confuseTargetMode && selectedConfuseWeaponId) {
              targetConfuse("flank", idx, ws, setError, setConfuseTargetMode, setSelectedConfuseWeaponId);
              return;
            }

            if (euEpZoneTargetMode) {
              sendExploreUnknownEpSpend('useExploreUnknownEpZone', { zone: 'flank' });
              return;
            }

            if (euEpTargetMode && visibleOrder?.instanceId) {
              if (!isExploreUnknownEpTargetHighlight(visibleOrder, 'flank')) {
                setError(t('exploreUnknownBattle.epTargetInvalidUnit'));
                return;
              }
              sendExploreUnknownEpSpend('useExploreUnknownEpTarget', {
                targetInstanceId: visibleOrder.instanceId,
              });
              return;
            }
            
            // Handle strategem targeting
            if (strategemTargetMode && selectedStrategemId) {
              const animationConfig = {
                registerAnimation: registerLocalStrategemTargetAnimation,
                strategemInstanceId: strategemTargetInfo?.strategemInstanceId,
                strategemId: selectedStrategemId,
                gameState,
                userId
              };
              
              if (strategemTargetInfo?.type === 'zone') {
                // Target the zone itself
                targetStrategem("zone", "flank", null, null, null, ws, gameId, setError, setStrategemTargetMode, setSelectedStrategemId, setStrategemTargetInfo, animationConfig);
              } else {
                // Target a unit in the zone
                const targetUnit = visibleOrder;
                targetStrategem("unit", "flank", idx, "friendly", targetUnit?.instanceId, ws, gameId, setError, setStrategemTargetMode, setSelectedStrategemId, setStrategemTargetInfo, animationConfig);
              }
              return;
            }
            
            // Handle deploy targeting
            if (deployTargetMode && selectedDeployOrderId) {
              handleTargetDeploy("flank", idx, "friendly");
              return;
            }
            
            // Handle attack selection
            if (
              selectedAttacker &&
              selectedAttacker.zone === "flank" &&
              selectedAttacker.idx === idx
            ) {
              setSelectedAttacker(null);
              setAttackMode(false);
              setLastAttackedTarget(null); // Clear when canceling attack mode
            } else {
              // ✅ VALIDATION: Check if unit has 0 attack rating - cannot attack
              const attackRating = visibleOrder?.attack_rating ?? 0;
              if (attackRating === 0) {
                setError('This unit has 0 attack rating and cannot attack');
                return;
              }
              
              setSelectedAttacker({ zone: "flank", idx, id: visibleOrder?.id || order?.id });
              setAttackMode(true);
              setLastAttackedTarget(null); // Clear when selecting new attacker
            }
          }}
        >
          {isNewlyDeployedLegendaryUnique(userId, 'flank', idx) && (
            <LegendaryUniqueDeployRay />
          )}
          {(() => {
            // Check if replacement buttons are showing for this unit
            const hasReplaceButtons = replaceTargetMode && clickedUnitForReplace && order && clickedUnitForReplace.instanceId === order.instanceId;
            const isFriendlySlotSelected =
              (selectedAttacker && selectedAttacker.zone === "flank" && selectedAttacker.idx === idx) ||
              selectedOrderInstanceId === visibleOrder.instanceId;
            const showFriendlyExpandedPreview =
              isHovered ||
              (isFriendlySlotSelected && hoveredPlayed.zone == null);
            return (
              <>
                <div
                  className={`deployed-unit-slot ${!isProtectUnit(visibleOrder) && !isNewlyDeployed(userId, 'flank', idx) && !visibleOrder?._isRepositioning && !isImmobilizedUI(visibleOrder, "your_flank") ? 'deployed-unit-slot-idle' : ''}`.trim()}
                  data-slot-zone="your_flank"
                  data-slot-index={idx}
                  style={{ position: 'relative' }}
                >
                  {isNewlyDeployedLegendary(userId, 'flank', idx) && (
                    <LegendaryDeployGlow />
                  )}
                  <OrderDesign
                    order={visibleOrder}
                    count={0} 
                    minimal={!showFriendlyExpandedPreview || hasReplaceButtons}
                    scale={viewportScale}
                    fixedDesktopExpandedPosition={fixedDesktopExpandedPosition}
                    isOOR={visibleOrder.isOOR}
                    isSelected={isFriendlySlotSelected}
                    isHighlighted={highlightedInstanceIds.some(id => String(id).trim() === String(visibleOrder.instanceId).trim())}
                    zoneName="your_flank"
                    slotIndex={idx}
                    gameState={gameState}
                    userId={activeUserId}
                    isHiddenDuringAnimation={attackAnimations.isUnitHidden(visibleOrder.instanceId)}
                    isLethalDeathFading={isSlotLethalFading('your_flank', idx, visibleOrder?.instanceId)}
                    isRedeemMarkedForDeath={isRedeemMarkedForDeathForUnit(visibleOrder?.instanceId)}
                    isRedeemDeathFinishing={isRedeemDeathFinishingForUnit(visibleOrder?.instanceId)}
                    lethalDeathFadeDurationMs={getLethalFadeDurationMs('your_flank', idx)}
                    onLethalDeathFadeComplete={() => completeLethalAttackFade('your_flank', idx)}
                    isUnitTargetInPendingAttack={attackAnimations.isUnitTargetInPendingAttack}
                    isViewedByOpponent={!!(opponentLookingAtInstanceId && visibleOrder?.instanceId && String(opponentLookingAtInstanceId) === String(visibleOrder.instanceId))}
                  isTarget={
                    isTarget({
                      strategemTargetMode: effectiveStrategemTargetMode,
                      selectedStrategemId: effectiveSelectedStrategemId,
                      strategemTargetInfo: effectiveStrategemTargetInfo,
                      immobilizeTargetMode: false,
                      confuseTargetMode,
                      weaponTargetMode: effectiveWeaponTargetMode,
                      selectedWeaponId,
                      weaponDeployTargetMode,
                      deployTargetMode,
                      selectedDeployOrderId,
                      selectedDeployInstanceId,
                      selectedDeployZone,
                      zone: "flank",
                      targetingData,
                      gameState,
                      playerId: userId,
                      attackMode: effectiveAttackMode,
                      selectedAttacker: effectiveSelectedAttacker,
                      droneTargetMode: effectiveDroneTargetMode,
                      selectedDroneId: effectiveSelectedDroneId,
                      unit: visibleOrder
                    }) || isExploreUnknownEpTargetHighlight(visibleOrder, 'flank')
                  }
                  targetMode={getTargetingMode(strategemTargetMode, false, confuseTargetMode, weaponTargetMode, deployTargetMode, selectedDeployOrderId, selectedDeployZone)}
                  renderButtons={() => (
                    gameState?.missionId === 'chapter_1_mission_5' &&
                    visibleOrder?.isMission5Corn === true &&
                    isHovered ? (
                      <>
                        {(() => {
                          const groupInfo = getMission5CornGroupInfo('flank', idx);
                          if (groupInfo.size <= 0) return null;
                          return (
                        <button
                          className="replace-button replace-button-confirm"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeMission5CornGroup('flank', idx, visibleOrder);
                          }}
                        >
                          {`Cut corn for ${groupInfo.cost} intel`}
                        </button>
                          );
                        })()}
                      </>
                    ) : null
                  )}
                  onBattlefieldExpandedPointerLeave={() => {
                    setHoveredPlayed({ zone: null, idx: null });
                    sendOrderHover(null);
                  }}
                  passThroughExpandedPortalForTargeting={passThroughExpandedBattlefieldPortals}
                />
                <DemoralizingBrokenShieldOverlay
                  visible={showDemoralizingBrokenShieldFor(visibleOrder?.instanceId)}
                />
                </div>
                {/* ========== DEBUGGING: Slot number and zone label overlay ========== */}
                {/* COMMENTED OUT - no longer needed */}
                {/*
                <div style={{
                  position: 'absolute',
                  top: '5px',
                  left: '5px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: '#fff',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  zIndex: 1000,
                  pointerEvents: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px'
                }}>
                  <span>{slotNumber}</span>
                  <span style={{ fontSize: '9px', textTransform: 'uppercase' }}>Player Flank</span>
                </div>
                */}
                {/* ========== END DEBUGGING ========== */}
                {/* Inline Replace/Cancel buttons */}
                {hasReplaceButtons && (
            <div className="replace-buttons-container">
              <button
                className="replace-button replace-button-confirm"
                onClick={(e) => {
                  e.stopPropagation();
                  if (counterStrikeReplaceMode) {
                    handleReplaceCounterStrike(clickedUnitForReplace);
                  } else {
                    handleReplaceUnit(clickedUnitForReplace);
                  }
                }}
              >
                Replace
              </button>
              <button
                className="replace-button replace-button-cancel"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancelReplace();
                }}
              >
                Cancel
              </button>
            </div>
                )}
                {/* Portal displacement (War Effort): one charge to switch zone (flank → front) */}
                {gameState?.defenderPortalActive && String(userId) === String(gameState.warEffortDefenderPlayerId) && visibleOrder?.portalDisplacementCharges > 0 && (
                  <button
                    type="button"
                    className="portal-displacement-button"
                    data-tooltip="Switch zone (Portal)"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePortalDisplacement(visibleOrder, 'front');
                    }}
                  >
                    Portal (→front)
                  </button>
                )}
              </>
            );
          })()}
        </div>
      </div>
    );
  })}
</div>


          </div>
        </div>
        </div>
		<div

>

</div>
{/* Player General Section - Removed full GeneralStatus, now using compact mode on avatar */}

{/* Turn Countdown Timer and First Turn Info - Below Player Intelligence Orb */}
<div style={{
  position: "absolute",
  left: "50%",
  transform: "translateX(-50%)",
  top: "540px", // Below the GeneralStatus container (which is around 520px from top, + 20px gap)
  zIndex: 1000,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "8px"
}}>
  <TurnTimerDisplay turnTimeLeft={needsMulligan && mulliganTimeLeft !== null ? mulliganTimeLeft : turnTimeLeft} />
  {playerGoesFirst !== null && !needsMulligan && showFirstTurnMessage && (
    <div style={{
      backgroundColor: "rgba(0, 0, 0, 0.8)",
      color: "white",
      padding: "10px 20px",
      borderRadius: "8px",
      fontSize: "16px",
      fontWeight: "bold",
      textAlign: "center",
      border: "2px solid",
      borderColor: playerGoesFirst ? "#4CAF50" : "#FFA500",
      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.4)",
      animation: "fadeIn 0.5s ease-in"
    }}>
      {playerGoesFirst ? t('battleUI.shouldActNow') : t('battleUI.haveTimeToPrepare')}
    </div>
  )}
</div>

{/* Targeting Cancel Dialog - Center Screen */}
<TargetingCancelDialog
  deployMode={deployTargetMode && selectedDeployOrderId}
  strategemMode={strategemTargetMode && selectedStrategemId}
  weaponMode={weaponTargetMode && selectedWeaponId}
  weaponDeployMode={weaponDeployTargetMode && weaponDeployTargetInfo}
  weaponDeployTargetInfo={weaponDeployTargetInfo}
  droneMode={droneTargetMode && selectedDroneId}
  euEpZoneMode={euEpZoneTargetMode}
  euEpTargetMode={euEpTargetMode}
  strategemTargetInfo={strategemTargetInfo}
  selectedDeployOrderId={selectedDeployOrderId}
  deployTargetInfo={deployTargetInfo}
  deployOrderDisplayName={localizedTargetingOrderNames[Number(selectedDeployOrderId)]}
  strategemOrderDisplayName={localizedTargetingOrderNames[Number(selectedStrategemId)]}
  weaponOrderDisplayName={localizedTargetingOrderNames[Number(selectedWeaponId)]}
  droneOrderDisplayName={localizedTargetingOrderNames[Number(selectedDroneId)]}
  onCancelDeploy={createCancelDeployHandler(
    setDeployTargetMode,
    setSelectedDeployOrderId,
    setSelectedDeployInstanceId,
    setSelectedDeployZone,
    setDeployTargetInfo,
    selectedDeployOrderId,
    selectedDeployInstanceId,
    selectedDeployZone
  )}
  onCancelStrategem={createCancelStrategemHandler(
    setStrategemTargetMode,
    setSelectedStrategemId,
    setStrategemTargetInfo
  )}
  onCancelWeapon={createCancelWeaponHandler(
    setWeaponTargetMode,
    setSelectedWeaponId
  )}
  onCancelWeaponDeploy={createCancelWeaponDeployHandler(
    setWeaponDeployTargetMode,
    setWeaponDeployTargetInfo,
    weaponDeployTargetInfo
  )}
  onCancelDrone={createCancelDroneHandler(
    setDroneTargetMode,
    setSelectedDroneId,
    setSelectedDroneInstanceId
  )}
  onCancelEuEpZone={cancelEuEpZoneTargeting}
  onCancelEuEpTarget={cancelEuEpTargetTargeting}
  ws={ws}
  gameId={gameId}
/>

{/* Player dock: general + O/S/C left, advisor + drones right, eye-to-eye above buttons */}
{/* Mobile version is rendered outside battlefield-and-carousel for proper positioning */}
{!isContainerMobile && (
<div className="player-dock-row" style={{ position: "absolute", bottom: "148px", left: "20px", right: "20px", zIndex: 1200, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
  <div className="player-general-column" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8 }}>
    <div className="player-avatar-container" style={{}}>
  <div style={{ position: "relative", display: "inline-block" }}>
    <BattlePlayerHudPanel {...playerHudPanelProps} />
  </div>
  
  {/* Speaking Bubble - Show current emote */}
  {currentEmoteBubble && (
    isMission6DarknessMode ? createPortal(
      <div style={{
        position: "fixed",
        bottom: "210px",
        left: "70px",
        zIndex: 100005,
        pointerEvents: "none",
        maxWidth: "560px",
        minWidth: "260px"
      }}>
        <div style={{
          backgroundColor: "rgba(0, 0, 0, 0.85)", // Black with opacity
          border: "2px solid #40E0D0", // Turquoise border
          borderRadius: "12px", // Rounded borders
          padding: "12px 16px",
          boxShadow: "0 0 10px rgba(64, 224, 208, 0.6), 0 0 20px rgba(64, 224, 208, 0.4)", // Glowing turquoise effect
          color: "white", // White text
          fontSize: "18px",
          fontWeight: "bold",
          textAlign: "center",
          lineHeight: "1.4",
          wordWrap: "break-word",
          whiteSpace: "pre-wrap",
          display: "inline-block",
          width: "auto",
          minHeight: "40px"
        }}>
          {formatEmoteBubbleTwoWords(resolveBattleEmoteBubbleDisplay(currentEmoteBubble, t))}
        </div>
      </div>,
      document.body
    ) : <div style={{
      position: isMission6DarknessMode ? "fixed" : "absolute",
      bottom: isMission6DarknessMode ? "210px" : "calc(100% + 12px)",
      left: "70px",
      zIndex: isMission6DarknessMode ? 100005 : 10002,
      pointerEvents: "none",
      maxWidth: "560px",
      minWidth: "260px"
    }}>
      <div style={{
        backgroundColor: "rgba(0, 0, 0, 0.85)", // Black with opacity
        border: "2px solid #40E0D0", // Turquoise border
        borderRadius: "12px", // Rounded borders
        padding: "12px 16px",
        boxShadow: "0 0 10px rgba(64, 224, 208, 0.6), 0 0 20px rgba(64, 224, 208, 0.4)", // Glowing turquoise effect
        color: "white", // White text
        fontSize: "18px",
        fontWeight: "bold",
        textAlign: "center",
        lineHeight: "1.4",
        wordWrap: "break-word",
        whiteSpace: "pre-wrap",
        display: "inline-block",
        width: "auto",
        minHeight: "40px"
      }}>
        {formatEmoteBubbleTwoWords(resolveBattleEmoteBubbleDisplay(currentEmoteBubble, t))}
      </div>
      {/* Speech bubble tail pointing downward toward general */}
      <div style={{
        position: "absolute",
        bottom: "-8px", // Position below the bubble
        left: "20px", // Position on the left side (toward general)
        width: 0,
        height: 0,
        borderLeft: "10px solid transparent",
        borderRight: "10px solid transparent",
        borderTop: "10px solid rgba(0, 0, 0, 0.85)", // Match bubble background
        filter: "drop-shadow(0 0 5px rgba(64, 224, 208, 0.6))", // Glow effect
        zIndex: 10003
      }} />
      {/* Speech bubble tail border (turquoise outline) */}
      <div style={{
        position: "absolute",
        bottom: "-10px", // Slightly below the main tail
        left: "20px",
        width: 0,
        height: 0,
        borderLeft: "11px solid transparent",
        borderRight: "11px solid transparent",
        borderTop: "11px solid #40E0D0", // Turquoise border
        zIndex: 10002
      }} />
    </div>
  )}
</div>

  </div>
  {showPlayerAdvisor && <div className="player-advisor-column" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8 }}>
    <div className="player-advisor-container" style={{}}>
      <div style={{ position: "relative" }}>
        {/* Intel bar/orb removed: intel is now shown on the new PlayerHUD (bottom-left). */}
        <img
          src={getAvatarImage(selectedAdvisor)}
          alt="Player Advisor"
          style={{
            width: "180px",
            height: "180px",
            objectFit: "contain",
            transform: shouldMirrorAvatar(selectedAdvisor, 'right', avatars) ? 'scaleX(-1)' : 'none'
          }}
        />
        {advisorEmoteBubble && (
          isMission6DarknessMode ? createPortal(
            <div style={{
              position: "fixed",
              bottom: "230px",
              right: "120px",
              zIndex: 100005,
              pointerEvents: "none",
              maxWidth: "560px",
              minWidth: "260px"
            }}>
              <div style={{
                backgroundColor: "rgba(0, 0, 0, 0.85)",
                border: "2px solid #40E0D0",
                borderRadius: "12px",
                padding: "12px 16px",
                boxShadow: "0 0 10px rgba(64, 224, 208, 0.6), 0 0 20px rgba(64, 224, 208, 0.4)",
                color: "white",
                fontSize: "18px",
                fontWeight: "bold",
                textAlign: "center",
                lineHeight: "1.4",
                wordWrap: "break-word",
                whiteSpace: "pre-wrap",
                display: "inline-block",
                width: "auto",
                minHeight: "40px"
              }}>
                {advisorEmoteBubble.text}
              </div>
            </div>,
            document.body
          ) : <div style={{
            position: isMission6DarknessMode ? "fixed" : "absolute",
            bottom: isMission6DarknessMode ? "230px" : "calc(100% + 12px)",
            left: isMission6DarknessMode ? "auto" : "50%",
            right: isMission6DarknessMode ? "120px" : "auto",
            transform: isMission6DarknessMode ? "none" : "translateX(-50%)",
            zIndex: isMission6DarknessMode ? 100005 : 10002,
            pointerEvents: "none",
            maxWidth: "560px",
            minWidth: "260px"
          }}>
            <div style={{
              backgroundColor: "rgba(0, 0, 0, 0.85)",
              border: "2px solid #40E0D0",
              borderRadius: "12px",
              padding: "12px 16px",
              boxShadow: "0 0 10px rgba(64, 224, 208, 0.6), 0 0 20px rgba(64, 224, 208, 0.4)",
              color: "white",
              fontSize: "18px",
              fontWeight: "bold",
              textAlign: "center",
              lineHeight: "1.4",
              wordWrap: "break-word",
              whiteSpace: "pre-wrap",
              display: "inline-block",
              width: "auto",
              minHeight: "40px"
            }}>
              {advisorEmoteBubble.text}
            </div>
            <div style={{
              position: "absolute",
              bottom: "-8px",
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "10px solid transparent",
              borderRight: "10px solid transparent",
              borderTop: "10px solid rgba(0, 0, 0, 0.85)",
              filter: "drop-shadow(0 0 5px rgba(64, 224, 208, 0.6))",
              zIndex: 10003
            }} />
            <div style={{
              position: "absolute",
              bottom: "-10px",
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "11px solid transparent",
              borderRight: "11px solid transparent",
              borderTop: "11px solid #40E0D0",
              zIndex: 10002
            }} />
          </div>
        )}
        {/* Player title overlay removed: title is now shown on the new PlayerHUD (bottom-left). */}
      </div>
    </div>
  </div>}
  </div>
)}

{/* Player Drones - below advisor; aligned right so they sit under advisor in dock */}
{/* Desktop only - Mobile version rendered outside battlefield-and-carousel */}
{!isContainerMobile && (
<div
  className="player-drone-zone"
  data-player-drone-zone="true"
  style={{
  position: "absolute",
  right: "20px",
  bottom: "78px",
  zIndex: 1300,
  display: 'flex',
  gap: "8px",
  ...(isDroneDragActive ? { outline: '3px solid #ffff00', outlineOffset: '0', cursor: 'pointer' } : {})
}}>
  {you.drones?.filter(drone => !drone.markedForRemoval).map((drone, index) => {
    // ✅ Unit 502 (Disruptor): Check if any Disruptor is deployed (deactivates all drones)
    const allPlayerIds = gameState?.players || (gameState?.state ? Object.keys(gameState.state) : []);
    let disruptorDeployed = false;
    for (const pid of allPlayerIds) {
      const p = gameState?.state?.[pid];
      if (p && p.playedOrders) {
        for (const zone of ['front', 'flank']) {
          if (p.playedOrders[zone]) {
            for (const unit of p.playedOrders[zone]) {
              if (unit && unit.id === 502 && unit.health > 0) {
                disruptorDeployed = true;
                break;
              }
            }
            if (disruptorDeployed) break;
          }
        }
        if (disruptorDeployed) break;
      }
    }
    
    const canActivate = yourTurn && (drone.charges > 0 || drone.charges === undefined) && !drone.usedThisTurn && !disruptorDeployed;
    const selectedHandOrder = selectedOrderInstanceId ? you?.hand?.find(o => o.instanceId === selectedOrderInstanceId) : null;
    const isHandDroneTargeting = yourTurn && selectedHandOrder?.type === 'Drone' && !replaceDroneTargetMode;
    
    return (
    <AbilityTooltip
      as="div"
      key={`player-drone-${index}`}
      className="drone-slot-bounds"
      tooltipText={buildDeployedDroneTooltipText(drone, { disruptorDeployed, playerOwned: true })}
      data-drone-index={index}
      data-drone-zone="your_drone"
      data-instance-id={drone.instanceId || ''}
      style={{
        width: '80px',
        height: '60px',
        backgroundColor: '#333',
        border: isHandDroneTargeting ? '2px solid #ffd400' : (canActivate ? '2px solid #2c5aa0' : '2px solid #444'),
        borderRadius: '4px',
        cursor: canActivate ? 'pointer' : 'default',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: (canActivate || isHandDroneTargeting) ? 1 : 0.6,
        boxShadow: isHandDroneTargeting ? '0 0 10px rgba(255,212,0,0.9)' : 'none',
        overflow: 'visible'
      }}
      onMouseDown={(e) => {
        if (e.button !== 0) return;
        if (!canActivate && !isContainerMobile) {
          e.preventDefault();
          return;
        }
        if (isContainerMobile || !canActivate || isHandDroneTargeting) return;
        e.preventDefault();
        if (droneNeedsTargeting(drone.id, targetingData)) {
          startDroneTargetMouseDrag({ id: drone.id, instanceId: drone.instanceId }, e.clientX, e.clientY);
        }
      }}
      onClick={() => {
        // If deploy targeting is active, allow selecting friendly drones as targets
        if (deployTargetMode && selectedDeployOrderId) {
          handleTargetDeploy('drone', index, 'friendly');
          return;
        }
        
        // Handle replacement mode (when drone from hand selected and zone is full)
        if (replaceDroneTargetMode && selectedReplaceDroneInstanceId) {
          // Clicking on a drone in replace mode shows the buttons
          setClickedDroneForReplace({
            ...drone,
            index
          });
          return;
        }
        
        // Check if a drone is selected from hand (for replacement)
        if (selectedOrderInstanceId && !replaceDroneTargetMode) {
          const selectedOrder = you?.hand?.find(o => o.instanceId === selectedOrderInstanceId);
          // Only proceed if selected order is a drone
          if (selectedOrder?.type === 'Drone') {
            // Check if drone zone is full (4 slots)
            const dronesCount = you.drones?.filter(d => !d.markedForRemoval).length || 0;
            if (dronesCount >= 4) {
              // Enter replace mode and immediately show buttons on this drone
              setReplaceDroneTargetMode(true);
              setSelectedReplaceDroneId(selectedOrderId);
              setSelectedReplaceDroneInstanceId(selectedOrderInstanceId);
              setClickedDroneForReplace({
                ...drone,
                index
              });
              return;
            }
            // Zone has free slots: clicking any drone slot deploys the selected hand drone
            // to the next free drone slot (backend-owned slot assignment).
            playDrone(selectedOrder.id);
            setSelectedOrderId(null);
            setSelectedOrderInstanceId(null);
            return;
          }
        }
        
        // Normal drone activation (only if Disruptor is not deployed)
        if (canActivate) {
          activateDrone(drone.instanceId);
        }
      }}
    >
      {/* Drone image */}
      <img
        src={`/images/${drone.id}.jpg`}
        alt={drone.name}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 1
        }}
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
      {/* Drone charge counter overlay */}
      {drone.charges !== undefined && drone.charges > 0 && (
        <div style={{
          color: 'white',
          fontSize: '16px',
          fontWeight: 'bold',
          textShadow: '1px 1px 1px rgba(0,0,0,0.8)',
          position: 'absolute',
          top: '2px',
          right: '2px',
          backgroundColor: 'rgba(0,0,0,0.7)',
          borderRadius: '50%',
          width: '20px',
          height: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2
        }}>
          {drone.charges}
        </div>
      )}
      
      {/* Replace/Cancel icon buttons for drone replacement */}
      {replaceDroneTargetMode && clickedDroneForReplace && clickedDroneForReplace.instanceId === drone.instanceId && (
        <div style={{
          position: 'absolute',
          bottom: '3px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: '6px',
          zIndex: 3
        }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleReplaceDrone(clickedDroneForReplace);
            }}
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: '#4caf50',
              color: 'white',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.5)',
              padding: 0
            }}
            data-tooltip="Replace drone"
          >
            ✓
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleCancelReplaceDrone();
            }}
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: '#f44336',
              color: 'white',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.5)',
              padding: 0
            }}
            data-tooltip="Cancel replacement"
          >
            ✗
          </button>
        </div>
      )}
    </AbilityTooltip>
    );
  })}
  {/* Empty drone slots */}
  {Array.from({ length: 4 - (you.drones?.filter(drone => !drone.markedForRemoval).length || 0) }).map((_, index) => (
    <div
      key={`empty-player-drone-${index}`}
      data-drone-index={index + (you.drones?.filter(drone => !drone.markedForRemoval).length || 0)}
      data-drone-zone="your_drone"
      onClick={() => {
        if (!yourTurn) return;
        if (!selectedOrderInstanceId) return;
        const selectedOrder = you?.hand?.find(o => o.instanceId === selectedOrderInstanceId);
        if (selectedOrder?.type !== 'Drone') return;
        playDrone(selectedOrder.id);
      }}
      style={{
        width: '80px',
        height: '60px',
        backgroundColor: '#333',
        border: selectedOrderInstanceId && you?.hand?.some(o => o.instanceId === selectedOrderInstanceId && o.type === 'Drone')
          ? '2px solid #ffd400'
          : '2px solid #555',
        borderRadius: '4px',
        opacity: selectedOrderInstanceId && you?.hand?.some(o => o.instanceId === selectedOrderInstanceId && o.type === 'Drone') ? 0.85 : 0.3,
        boxShadow: selectedOrderInstanceId && you?.hand?.some(o => o.instanceId === selectedOrderInstanceId && o.type === 'Drone')
          ? '0 0 10px rgba(255,212,0,0.9)'
          : 'none',
        cursor: yourTurn ? 'pointer' : 'default'
      }}
    />
  ))}
</div>
)}

{/* Player Controls Section */}
<div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: 8 }}>
</div>

{/* End Turn Button moved outside battlefield-and-carousel for proper mobile positioning */}





        {/* Order Carousel - Hidden on mobile, mobile version rendered outside battlefield-and-carousel */}
        <div style={{ display: isContainerMobile ? "none" : "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="order-carousel">
{(() => {
  // ✅ CRITICAL: Filter out cards that are in completedOrders or deck (prevent desync display issues)
  // NOTE: This filtering is safe for effects that move cards from completedOrders to hand
  // (e.g., Unit 232 Ammit, Unit 339 Master Tactician, Unit 393 Essia Luhr) because:
  // 1. Backend removes card from completedOrders BEFORE adding to hand
  // 2. Frontend receives updated state where card is only in hand
  // 3. Filter checks current state - if card is not in completedOrders, it passes
  // 4. This prevents edge cases where desync might show card in both places
  const validHand = (you?.hand || []).filter(order => {
    // ✅ DEFENSIVE: Explicitly check for null/undefined first
    if (order === null || order === undefined) {
      console.warn(`🚨 Filtering out null/undefined order from hand`);
      return false;
    }
    
    if (!order.instanceId) {
      console.warn(`🚨 Filtering out order ${order.name || 'Unknown'} (ID: ${order.id || 'Unknown'}) - missing instanceId`);
      return false;
    }
    
    // Check if this order is in completedOrders - if so, exclude it from hand display
    const inCompleted = you.completedOrders?.some(co => co && co.instanceId === order.instanceId);
    if (inCompleted) {
      console.warn(`🚨 Filtered out card from hand display: ${order.name} (${order.instanceId}) is in completedOrders`);
      return false;
    }
    
    // ✅ CRITICAL: Check if this order is in deck - if so, exclude it from hand display
    // This prevents showing cards that haven't been drawn yet (desync issue)
    const inDeck = you.deck?.some(d => d && d.instanceId === order.instanceId);
    if (inDeck) {
      console.warn(`🚨 Filtered out card from hand display: ${order.name} (${order.instanceId}) is in deck (not drawn yet)`);
      return false;
    }
    
    return true;
  });
  
  // ✅ During mulligan, hide hand so player only sees cards in MulliganDialog (avoids seeing cards twice)
  const handToDisplay = needsMulligan ? [] : validHand;
  
  return handToDisplay.map((order, idx) => {
    // ✅ DEFENSIVE: Check for null/undefined order (shouldn't happen after filter, but safety first)
    if (!order) {
      console.error(`🚨 CRITICAL: Null order found in validHand at index ${idx}`);
      return null; // Return null to skip rendering this slot
    }
    
    const isHovered = idx === hoveredIndex;
    const isSelectedInHand = selectedOrderInstanceId === order.instanceId;
    const isExpandedInHand = isHovered || isSelectedInHand;
    const isBlockedByOrderRule = Array.isArray(blockedOrderIds) && blockedOrderIds.includes(order.id);
    const isBlocked = isBlockedByOrderRule || isDisorientationHandLocked;

    const hideForTacticalLoadoutFx =
      tacticalLoadoutHiddenHandInstanceIds &&
      tacticalLoadoutHiddenHandInstanceIds.has(String(order.instanceId));

    // ✅ FIXED: Validate instanceId instead of generating fallback
    if (!order.instanceId) {
      throw new Error(`Order ${order.name || 'Unknown'} (ID: ${order.id || 'Unknown'}) missing instanceId. This indicates a backend data integrity issue.`);
    }
    
    // Green glow: afford + your turn (same as historical behavior), plus no glow for weapons with no attach target.
    // Cost must match OrderDesign getDisplayCost (use order.cost || 0 when not using instanceCost).
    const orderCost =
      order.hasInstanceModifications && order.instanceCost !== undefined
        ? order.instanceCost
        : (order.cost || 0);
    const intelNum = Number(you.intelligence);
    const costNum = Number(orderCost);
    const isWeaponWithoutAttachTarget = order.type === 'Weapon' && !hasWeaponAttachTargetAvailable;
    const isPlayable =
      yourTurn &&
      you &&
      Number.isFinite(intelNum) &&
      Number.isFinite(costNum) &&
      intelNum >= costNum &&
      !isWeaponWithoutAttachTarget;
    
    // Get deployed unit info for tooltip (excludes Unit 134 - Beastcaller of the Rift)
    const deployedUnitInfo = getDeployedUnitInfo(order.id);
    
    // Use fetched order from DB (same method as deployment uses)
    let deployedUnitCard = null;
    if (deployedUnitInfo && gameState) {
      if (deployedUnitInfo.id) {
        const fetchedOrder = orderDBCache[deployedUnitInfo.id];
        if (fetchedOrder) {
          // Use order data directly from database - includes type, faction_name, description
          deployedUnitCard = {
            ...fetchedOrder,
            // Override with custom stats if specified (for Unit 137's Shadow Strike)
            attack_rating: deployedUnitInfo.customStats?.attack ?? fetchedOrder.attack_rating,
            health: deployedUnitInfo.customStats?.health ?? fetchedOrder.health,
            abilities: deployedUnitInfo.customStats?.abilities ?? fetchedOrder.abilities,
          };
        }
        // If not fetched yet, deployedUnitCard will be null and tooltip won't show until data loads
      } else if (deployedUnitInfo.isRandom) {
        // For random units, we can't show a specific card
        deployedUnitCard = {
          name: deployedUnitInfo.name,
          description: deployedUnitInfo.source ? `Random unit from ${deployedUnitInfo.source}` : 'Random unit'
        };
      }
    }
  

  return (
    <div
      key={`hand-${idx}-${order.id}`}
      data-instance-id={order.instanceId}
      data-order-id={order.id}
      className={`carousel-order-card${isExpandedInHand ? " expanded" : ""}${opponentLookingAtInstanceId && String(opponentLookingAtInstanceId) === String(order.instanceId) ? " order-viewed-by-opponent" : ""}`}
      style={{
        position: 'relative',
        background: "none",
        zIndex: isExpandedInHand ? 10000 : 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        margin: 0,
        border: "none",
        boxShadow: "none",
        padding: "8px 10px",
        cursor: isBlocked ? "not-allowed" : (yourTurn ? "pointer" : "default"),
        pointerEvents: hideForTacticalLoadoutFx || isBlocked ? "none" : undefined,
        opacity: hideForTacticalLoadoutFx ? 0 : isBlocked ? 0.6 : 1,
        visibility: hideForTacticalLoadoutFx ? "hidden" : "visible",
        transition: "transform 0.2s ease",
        transform: isExpandedInHand ? "translateY(-60px) scale(1.05)" : "translateY(-60px) scale(1)",
        transformOrigin: "center",
        touchAction: isMobile && yourTurn && !isBlocked ? 'none' : 'auto',
      }}
      onMouseEnter={() => {
        // Play hover sound when expanding order
        playSoundEffect('/sounds/hoverover.mp3', 0.3);
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
          hoverTimeoutRef.current = null;
        }
        setHoveredIndex(idx);
        sendOrderHover(order.instanceId);
      }}
      onMouseLeave={() => {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
          hoverTimeoutRef.current = null;
        }
        hoverTimeoutRef.current = setTimeout(() => {
          setHoveredIndex(prevIndex => {
            if (prevIndex !== idx) return prevIndex;
            return null;
          });
          hoverTimeoutRef.current = null;
        }, 100);
        sendOrderHover(null);
      }}
      onMouseDown={(e) => {
        if (e.button !== 0) return;
        if (!yourTurn && !isMobile) {
          e.preventDefault();
          return;
        }
        if (isBlocked || !yourTurn || isMobile) return;
        e.preventDefault();
        if (order.type === 'Weapon') {
          startWeaponMouseDrag(order, e.clientX, e.clientY);
        } else if (order.type === 'Strategem') {
          startStrategemMouseDrag(order, e.clientX, e.clientY);
        } else if (order.type === 'Drone') {
          startDroneMouseDrag(order, e.clientX, e.clientY);
        } else {
          startCarouselMouseDrag(order, e.clientX, e.clientY);
        }
      }}
      onClick={(e) => {
        if (isBlocked) return;
        if (isMobile) return;
        if (clickHandledByMouseupRef.current) {
          clickHandledByMouseupRef.current = false;
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        handleCardClick(order, e);
      }}
      // Mobile touch drag handlers for drag-to-deploy
      onTouchStart={(e) => {
        if (isBlocked || !isMobile || !yourTurn) return;
        handleTouchDragStart(e, order);
      }}
      onTouchMove={(e) => {
        if (!isMobile || !yourTurn) return;
        handleTouchDragMove(e);
      }}
      onTouchEnd={(e) => {
        if (isBlocked || !isMobile || !yourTurn) return;

        // Handle touch drag end - returns info about the drag
        const { wasDragging, order: dragOrder, zone } = handleTouchDragEnd(e);
        
        if (wasDragging) {
          // If it was a drag, handle deployment or cancellation
          e.preventDefault();
          e.stopPropagation();
          
          if (dragOrder && zone) {
            // Check if the order type is valid for zone deployment
            // Deployable units are anything that's NOT a Drone, Strategem, or Weapon
            const isDeployableToZone = dragOrder.type !== 'Drone' && 
                                        dragOrder.type !== 'Strategem' && 
                                        dragOrder.type !== 'Weapon';
            
            if (isDeployableToZone) {
              playOrder(dragOrder.id, zone, null, dragOrder.instanceId);
            } else {
              // For non-deployable types, fall through to click behavior
              handleCardTouch(dragOrder, e);
            }
          } else {
          }
        } else {
          // Not a drag, treat as tap (existing click-click behavior)
          e.preventDefault();
          e.stopPropagation();
          handleCardTouch(order, e);
        }
      }}
    >
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        width: '100%',
        height: '100%',
        justifyContent: 'space-between'
      }}>
        <div style={{ position: 'relative' }}>
          {showDuplicateHandOverlayFor(order) ? (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                zIndex: 10001,
                transform: DUPLICATE_HAND_OVERLAY_MATCH_ORDER_DESIGN_TRANSFORM,
              }}
            >
              <img
                src={DUPLICATE_TARGET_IMAGE_PATH}
                alt=""
                draggable={false}
                style={DUPLICATE_TARGET_CENTERED_IMAGE_STYLE}
              />
            </div>
          ) : null}
          <OrderDesign
            order={order}
            count={0}
            minimal={!isExpandedInHand}
            scale={viewportScale}
            fixedDesktopExpandedPosition={fixedDesktopExpandedPosition}
            isOOR={order.isOOR}
            isSelected={isSelectedInHand}
            isPlayable={isPlayable}
            gameState={gameState}
            userId={activeUserId}
            imageDraggable={false}
            isHiddenDuringAnimation={hideForTacticalLoadoutFx}
            debugInfo={{
              orderName: order.name,
              orderId: order.id,
              orderInstanceId: order.instanceId,
              selectedInstanceId: selectedOrderInstanceId,
              isSelected: isSelectedInHand
            }}
            renderButtons={null}
          />
        </div>
        
        {/* Deployed Unit Tooltip - Shows what unit will be deployed (excludes Unit 134) */}
        {isHovered && deployedUnitInfo && deployedUnitInfo.id && !deployedUnitInfo.isRandom && deployedUnitCard ? (
          <div
            className="deployed-unit-tooltip"
            style={{
              position: 'relative',
              width: 0,
              height: 0,
              overflow: 'visible',
              pointerEvents: 'none',
              zIndex: 10001,
              ...(fixedDesktopExpandedPosition && !isContainerMobile
                ? {}
                : {
                    position: 'absolute',
                    top: '0',
                    left: '410px',
                    width: 'auto',
                    height: 'auto',
                  }),
            }}
          >
            <OrderDesign
              order={deployedUnitCard}
              count={0}
              minimal={false}
              scale={viewportScale * 0.8} // Scale down by 20% (0.8 = 80% of original size)
              fixedDesktopExpandedPosition={
                fixedDesktopExpandedPosition && !isContainerMobile
                  ? getHandDeployedSpawnPreviewCenter(fixedDesktopExpandedPosition, viewportScale)
                  : undefined
              }
              isOOR={false}
              isSelected={false}
              isPlayable={false}
              gameState={gameState}
            />
          </div>
        ) : null}
        
        {/* Strategem selected indicator */}
        {yourTurn && selectedOrderInstanceId === order.instanceId && order.type === 'Strategem' && (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '4px',
            alignItems: 'center',
            margin: '8px 0'
          }}>
          </div>
        )}
        
        {/* Hand-card hover jump fix:
            A 28px-tall spacer used to render here only on hover. Because the
            carousel is anchored at `position: absolute; bottom: 0` with no
            fixed height, that spacer made the hovered card the tallest flex
            item, forced the carousel to grow upward, and visibly "jumped"
            every hand card on mouseover (deployed cards never had this
            spacer, which is why they don't jump). Removed entirely - it had
            no visible content and was a leftover compensation for
            instructional text that was deleted long ago. */}
      </div>
    </div>
  );
  });
})()}


          </div>
        </div>
      </div>
    </div>

    {/* Mobile Order Carousel - Outside battlefield-and-carousel for proper positioning */}
    {isContainerMobile && (
      <MobileOrderCarousel
        you={you}
        yourTurn={yourTurn}
        hoveredIndex={hoveredIndex}
        setHoveredIndex={setHoveredIndex}
        selectedOrderInstanceId={selectedOrderInstanceId}
        gameState={gameState}
        handleTouchDragStart={handleTouchDragStart}
        handleTouchDragMove={handleTouchDragMove}
        handleTouchDragEnd={handleTouchDragEnd}
        handleCardTouch={handleCardTouch}
        playOrder={playOrder}
        blockedOrderIds={blockedOrderIds}
        isDisorientationHandLocked={isDisorientationHandLocked}
        needsMulligan={needsMulligan}
        hasWeaponAttachTargetAvailable={hasWeaponAttachTargetAvailable}
        duplicateHandPulseUntil={duplicateHandPulseUntil}
        tacticalLoadoutHiddenHandInstanceIds={tacticalLoadoutHiddenHandInstanceIds}
      />
    )}

    {/* DEVELOPER CONSOLE - REMOVE BEFORE PRODUCTION (PLAYER 1 ONLY) */}
    {isDeveloperMode && showDevConsolePanel && (
      <DeveloperConsole
        ws={ws}
        gameId={gameId}
        devUnitSearch={devUnitSearch}
        setDevUnitSearch={setDevUnitSearch}
        showGridOverlay={showGridOverlay}
        setShowGridOverlay={setShowGridOverlay}
        showDebugOverlay={showDebugOverlay}
        setShowDebugOverlay={setShowDebugOverlay}
        showTargetedDebug={showTargetedDebug}
        setShowTargetedDebug={setShowTargetedDebug}
        showElementSelector={showElementSelector}
        setShowElementSelector={setShowElementSelector}
        showEditableOverlay={showEditableOverlay}
        setShowEditableOverlay={setShowEditableOverlay}
        gridSize={gridSize}
        setGridSize={setGridSize}
        targetElements={targetElements}
        setTargetElements={setTargetElements}
        handleElementSelected={handleElementSelected}
        handleElementMoved={handleElementMoved}
        handleElementResized={handleElementResized}
        handleSaveDesign={handleSaveDesign}
        savedDesign={savedDesign}
        setSavedDesign={setSavedDesign}
        actionLog={actionLog}
        setActionLog={setActionLog}
        userId={activeUserId}
        opponentId={
          activeGameState?.players?.find((id) => String(id) !== String(activeUserId)) ?? null
        }
        devRankMock={devRankMock}
        setDevRankMock={setDevRankMock}
        onHideDevElementsForScreenshot={() => setHideDevElementsForScreenshot(true)}
        hideDevElementsButtonLabel={t('battleUI.hideDevElementsForScreenshot')}
        onOpenSetup={() => setShowDevBattleSetupDialog(true)}
        devBetrayalPlayerDronePreview={{
          registerAnimation: registerLocalStrategemTargetAnimation,
          gameState: activeGameState,
          userId: activeUserId,
        }}
      />
    )}

    {isDeveloperMode ? (
      <DevBattleSetupDialog
        open={showDevBattleSetupDialog}
        onClose={() => setShowDevBattleSetupDialog(false)}
        gameState={activeGameState}
        userId={activeUserId}
        opponentUsername={opponentUsername}
        devBattleSetupUi={devBattleSetupUi}
        onApply={handleDevBattleSetupApply}
      />
    ) : null}

    {/* OLD DEVELOPER CONSOLE REMOVED */}


    {/* Grid Overlay - Only in developer mode */}
    {isDeveloperMode && (
      <GridOverlay 
        isVisible={showGridOverlay} 
        gridSize={gridSize} 
        showCoordinates={true} 
      />
    )}

    {/* Debug Overlay - Only in developer mode */}
    {isDeveloperMode && (
      <DebugOverlay 
        isVisible={showDebugOverlay} 
        showElementBoundaries={true} 
      />
    )}

    {/* Targeted Debug Overlay - Only in developer mode */}
    {isDeveloperMode && (
      <TargetedDebugOverlay 
        isVisible={showTargetedDebug} 
        targetElements={targetElements}
        showLabels={true}
      />
    )}

    {/* Simple Element Selector - Only in developer mode */}
    {isDeveloperMode && (
      <SimpleElementSelector 
        isVisible={showElementSelector} 
        onElementSelected={handleElementSelected}
      />
    )}

    {/* Editable Element Overlay - Only in developer mode */}
    {isDeveloperMode && (
      <EditableElementOverlay 
        isVisible={showEditableOverlay} 
        targetElements={targetElements}
        onElementMoved={handleElementMoved}
        onElementResized={handleElementResized}
        onSaveDesign={handleSaveDesign}
      />
    )}

    {/* Counter Strike Dialog */}
    {/* ✅ FIX: Add key based on orders to force reset when orders change (e.g., after playing one) */}
    <CounterStrikeDialog
      key={counterStrikeOrders.length > 0 ? counterStrikeOrders[0]?.instanceId || counterStrikeOrders.length : 'no-orders'}
      orders={counterStrikeOrders}
      onSelectZone={playCounterStrike}
      onKeepOrder={(orderId, instanceId) => keepCounterStrike(orderId, instanceId)}
      onStrikeModeActivated={handleCounterStrikeModeActivated}
      playerId={userId}
      isMobile={isContainerMobile}
      processingCounterStrikeId={processingCounterStrikeId}
      handSize={gameState?.state?.[userId]?.hand?.length || 0}
      gameState={gameState}
    />

    {/* Opponent Counter Strike Dialog - Blocks player when opponent has pending counterstrike */}
    <OpponentCounterStrikeDialog 
      show={opponentHasCounterStrike} 
      ws={ws}
      gameId={gameId}
      gameState={gameState}
      userId={userId}
      isMobile={isContainerMobile}
    />

    {showMulliganIntroOverlay && isExploreUnknownBossMulliganIntro && showMulliganIntroBossBar && (
      <div
        className={`battle-intro-overlay${isContainerMobile ? ' battle-intro-overlay--mobile' : ''}`}
        aria-live="polite"
        style={{ backgroundColor: 'transparent', backdropFilter: 'none', WebkitBackdropFilter: 'none' }}
      >
        <div
          className={`battle-intro-boss-bar-wrap${isMulliganIntroExiting ? ' battle-intro-boss-bar-wrap--exit' : ''}`}
          role="img"
          aria-label={t(exploreUnknownBossIntroTranslationKey)}
        >
          <div className="battle-intro-boss-bar">
            <div className="battle-intro-boss-bar-border battle-intro-boss-bar-border--top" aria-hidden="true" />
            <h1 className="battle-intro-boss-name">{t(exploreUnknownBossIntroTranslationKey)}</h1>
            {exploreUnknownBossRetryAttemptLabelKey ? (
              <p className="battle-intro-boss-retry-attempt">
                {t(exploreUnknownBossRetryAttemptLabelKey, {
                  n: exploreUnknownBossRetryAttemptNumber,
                })}
              </p>
            ) : null}
            <div className="battle-intro-boss-bar-border battle-intro-boss-bar-border--bottom" aria-hidden="true" />
          </div>
        </div>
      </div>
    )}

    {showMulliganIntroOverlay && !isExploreUnknownBossMulliganIntro && (
      <div
        className={`battle-intro-overlay${isContainerMobile ? ' battle-intro-overlay--mobile' : ''}`}
        aria-live="polite"
        style={{ backgroundColor: 'transparent', backdropFilter: 'none', WebkitBackdropFilter: 'none' }}
      >
        <div className={`battle-intro-stage${isContainerMobile ? ' battle-intro-stage--mobile' : ''}`}>
          {showMulliganIntroPlayerCircle && (
            <div
              className={`battle-intro-circle battle-intro-circle--player ${isMulliganIntroExiting ? 'battle-intro-circle--exit-left' : ''}`}
            >
              {introPlayerIsFirst !== null ? (
                <div className={`battle-intro-turn-sticker ${introPlayerIsFirst ? 'battle-intro-turn-sticker--first' : 'battle-intro-turn-sticker--second'}`}>
                  {introPlayerIsFirst ? t('battleUI.firstSticker') : t('battleUI.secondSticker')}
                </div>
              ) : null}
              <div className="battle-intro-circle-orb">
                <img
                  src={`${process.env.PUBLIC_URL}/images/faction_circle.png`}
                  alt=""
                  className="battle-intro-circle-frame"
                  draggable={false}
                  aria-hidden="true"
                />
                <div
                  className="battle-intro-faction-fill"
                  style={{ background: getFactionOrbBackground(playerFactionName) }}
                >
                  {playerFactionIconPath ? (
                    <img
                      src={playerFactionIconPath}
                      alt=""
                      className="battle-intro-circle-icon"
                      draggable={false}
                      aria-hidden="true"
                    />
                  ) : null}
                </div>
              </div>
              <div className="battle-intro-player-name">{user?.username}</div>
            </div>
          )}
          {showMulliganIntroVs ? (
            <img
              src={`${process.env.PUBLIC_URL}/images/vs_string.png`}
              alt="VS"
              className={`battle-intro-vs-image ${isMulliganIntroExiting ? 'battle-intro-vs-image--exit-up' : ''}`}
              draggable={false}
            />
          ) : null}
          {showMulliganIntroOpponentCircle && (
            <div
              className={`battle-intro-circle battle-intro-circle--opponent ${isMulliganIntroExiting ? 'battle-intro-circle--exit-right' : ''}`}
            >
              {introPlayerIsFirst !== null ? (
                <div className={`battle-intro-turn-sticker ${introPlayerIsFirst ? 'battle-intro-turn-sticker--second' : 'battle-intro-turn-sticker--first'}`}>
                  {introPlayerIsFirst ? t('battleUI.secondSticker') : t('battleUI.firstSticker')}
                </div>
              ) : null}
              <div className="battle-intro-circle-orb">
                <img
                  src={`${process.env.PUBLIC_URL}/images/faction_circle.png`}
                  alt=""
                  className="battle-intro-circle-frame"
                  draggable={false}
                  aria-hidden="true"
                />
                <div
                  className="battle-intro-faction-fill"
                  style={{ background: getFactionOrbBackground(opponentFactionName) }}
                >
                  {opponentFactionIconPath ? (
                    <img
                      src={opponentFactionIconPath}
                      alt=""
                      className="battle-intro-circle-icon"
                      draggable={false}
                      aria-hidden="true"
                    />
                  ) : null}
                </div>
              </div>
              <div className="battle-intro-player-name">{effectiveOpponentUsername}</div>
            </div>
          )}
        </div>
      </div>
    )}

    {/* Mulligan Dialog */}
    {!isDeveloperMode && needsMulligan && !showMulliganIntroOverlay && mulliganOrders.length > 0 && introPlayerIsFirst != null && (
      <MulliganDialog
        orders={mulliganOrders}
        onSubmitMulligan={submitMulligan}
        playerId={userId}
        isMobile={isContainerMobile}
        playerGoesFirst={introPlayerIsFirst}
      />
    )}

    {/* Touch Drag Overlay for mobile deployment */}
    <TouchDragOverlay
      isDragging={isTouchDragging}
      draggedOrder={touchDraggedOrder}
      dragPosition={touchDragPosition}
      targetZone={touchTargetZone}
      viewportScale={viewportScale}
      gameState={gameState}
      userId={userId}
    />
    
    {/* Attack Drag Overlay for mobile attacks */}
    <AttackDragOverlay
      isDragging={isAttackDragging}
      attackerInfo={attackDragAttacker}
      startPosition={attackDragStartPosition}
      dragPosition={attackDragPosition}
      hoveredTarget={attackDragTarget}
    />

    {/* Desktop drag-to-deploy / strategem drag: red line only (custom mouse drag); deploy by half-screen, strategem by drop-on-target. Portaled to body so laser is on top of expanded order cards (OrderDesign portal uses z-index 99999). */}
    {!isContainerMobile && (dragDeployState || strategemDragState || weaponDragState || droneDragState || attackDragState || droneTargetDragState) && createPortal(
      (() => {
        const lineState = dragDeployState || strategemDragState || weaponDragState || droneDragState || attackDragState || droneTargetDragState;
        if (!lineState) return null;
        return (
          <div
            className="deploy-drag-overlay"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 100002,
              pointerEvents: 'none',
            }}
          >
            <DeployDragLine
              originX={lineState.originX}
              originY={lineState.originY}
              initialX={lineState.currentX}
              initialY={lineState.currentY}
            />
          </div>
        );
      })(),
      document.body
    )}

    {/* Revealed Enemy Order Modal (Ghost Operative - Unit 295) */}
    {revealedEnemyOrder && (
      <RevealedOrderModal
        revealedOrder={revealedEnemyOrder.order}
        enemyId={revealedEnemyOrder.enemyId}
        onAcknowledge={acknowledgeGhostRevealedOrder}
      />
    )}

    {/* Completed Orders Modal */}
    <CompletedOrdersModal
      show={showCompletedOrders}
      isMobile={isContainerMobile}
      onClose={() => {
        // Only allow closing if Essia Luhr selection is complete or not active
        if (you?.essiaLuhrNeedsSelection && !you?.essiaLuhrSelected) {
          // Don't close if Essia Luhr selection is still needed
          return;
        }
        setShowCompletedOrders(false);
      }}
      completedOrders={you?.completedOrders || []}
      gameState={gameState}
      userId={activeUserId}
      hoveredOrder={hoveredCompletedOrder}
      onHoverOrder={setHoveredCompletedOrder}
      essiaLuhrActive={
        (you?.essiaLuhrNeedsSelection && !you?.essiaLuhrSelected) || false
      }
      onEssiaLuhrSelect={async (instanceId, orderId) => {
        if (!ws || ws.readyState !== 1) {
          setError(t('battleUI.websocketNotConnected') || 'WebSocket not connected');
          return;
        }
        
        try {
          ws.send(JSON.stringify({
            type: "play-turn",
            gameId,
            action: {
              type: "essia_luhr_select",
              instanceId: instanceId,
              orderId: orderId
            }
          }));
          
          setShowCompletedOrders(false);
        } catch (error) {
          console.error('❌ Error selecting Essia Luhr strategem:', error);
          setError(t('battleUI.failedToSelectOrder') || 'Failed to select strategem');
        }
      }}
    />

    {/* Unified mobile tooltip layer: portaled, consistent-size, viewport-clamped tooltips for all [data-tooltip] elements */}
    <MobileTooltipLayer active={isContainerMobile} />

    {/* Strategy Modal */}
    <StrategyModal
      show={showStrategy}
      onClose={() => setShowStrategy(false)}
      strategy={you?.strategy}
      gameState={gameState}
      userId={activeUserId}
      hoveredOrderIndex={hoveredStrategyOrder}
      onHoverOrder={setHoveredStrategyOrder}
      playerState={you}
      isMobile={isContainerMobile}
      commandDirectiveActive={
        exploreUnknownDiplomaticAccordsActive(gameState) &&
        playerAccordsInclude(gameState, userId, 'command_directive') &&
        !you?.commandDirectiveSelected
      }
      onCommandDirectiveSelect={async (orderId) => {
        if (!ws || ws.readyState !== 1) {
          setError(t('battleUI.websocketNotConnected') || 'WebSocket not connected');
          return;
        }
        
        try {
          ws.send(JSON.stringify({
            type: "play-turn",
            gameId,
            action: {
              type: "command_directive",
              orderId: orderId
            }
          }));
          
          setShowStrategy(false);
        } catch (error) {
          console.error('❌ Error selecting command directive order:', error);
          setError(t('battleUI.failedToSelectOrder') || 'Failed to select order');
        }
      }}
    />

    {/* Strategic Forecast Dialog (Strategem 323) */}
    {you?.needsStrategicForecastSelection?.orders && you.needsStrategicForecastSelection.orders.length > 0 && (
      <StrategicForecastDialog
        orders={you.needsStrategicForecastSelection.orders}
        onSelectOrder={(orderId) => {
          // Selection handled inside dialog component
        }}
        ws={ws}
        gameId={gameId}
      />
    )}


    {/* Repositioning Overlays - Rendered via portal to document.body for correct fixed positioning */}
    {activeRepositioningOverlays && activeRepositioningOverlays.size > 0 && createPortal(
      <>
        {Array.from(activeRepositioningOverlays.entries()).map(([key, data]) => (
          <RepositioningOverlay
            key={key}
            reposition={data.reposition}
            unit={data.unit}
            gameState={data.gameStateSnapshot || activeGameState}
            userId={activeUserId}
          />
        ))}
      </>,
      document.body
    )}

    {/* Decommission Overlays - Rendered via portal to document.body for correct fixed positioning */}
    {activeDecommissionOverlays && activeDecommissionOverlays.size > 0 && createPortal(
      <>
        {Array.from(activeDecommissionOverlays.entries()).map(([key, data]) => (
          <DecommissionOverlay
            key={key}
            decommission={data.decommission}
            unit={data.unit}
            gameState={activeGameState}
            userId={activeUserId}
          />
        ))}
      </>,
      document.body
    )}

    {/* Attack Animations - Rendered via portal to document.body for correct fixed positioning */}
    {createPortal(
      <>
        {attackAnimations.pendingAttacks.map(([attackId, pendingAttack]) => {
      const outcome = pendingAttack.outcome;
      if (!outcome) {
        console.warn(`⚠️ Attack animation: No outcome for attackId ${attackId}`);
        return null;
      }
      
      // ✅ NEW: Check if this is a general attack
      const isGeneralAttack = outcome.isGeneralAttack || outcome.targetZone === 'general';
      
      // ✅ SPECTATOR FIX: Use activeGameState instead of gameState for spectator support
      // Find attacker and target units in activeGameState
      const attackerPlayer = activeGameState?.state?.[outcome.attackerPlayerId];
      const targetPlayer = activeGameState?.state?.[outcome.targetPlayerId];
      
      if (!attackerPlayer) {
        console.warn(`⚠️ Attack animation: Attacker player not found`, {
          attackId,
          attackerPlayerId: outcome.attackerPlayerId,
          availablePlayers: activeGameState?.state ? Object.keys(activeGameState.state) : 'no state',
          isSpectating,
          hasActiveGameState: !!activeGameState
        });
        return null;
      }
      
      if (!targetPlayer) {
        console.warn(`⚠️ Attack animation: Target player not found`, {
          attackId,
          targetPlayerId: outcome.targetPlayerId,
          availablePlayers: activeGameState?.state ? Object.keys(activeGameState.state) : 'no state',
          isSpectating,
          hasActiveGameState: !!activeGameState
        });
        return null;
      }
      
      // ✅ FIX: Find attacker by instanceId first (more reliable than index)
      let attacker = null;
      if (attackerPlayer.playedOrders?.[outcome.attackerZone]) {
        if (outcome.attackerInstanceId) {
          attacker = attackerPlayer.playedOrders[outcome.attackerZone].find(
            u => u && u.instanceId === outcome.attackerInstanceId
          );
        }
        // Fallback to index if instanceId lookup fails
        if (!attacker && outcome.attackerIndex >= 0) {
          attacker = attackerPlayer.playedOrders[outcome.attackerZone][outcome.attackerIndex];
        }
      }
      
      // ✅ NEW: For general attacks, target is null (general is not in playedOrders)
      const target = isGeneralAttack ? null : (targetPlayer.playedOrders?.[outcome.targetZone]?.[outcome.targetIndex]);
      
      if (!attacker) {
        console.warn(`⚠️ Attack animation: Attacker unit not found`, {
          attackId,
          attackerPlayerId: outcome.attackerPlayerId,
          attackerZone: outcome.attackerZone,
          attackerIndex: outcome.attackerIndex,
          attackerInstanceId: outcome.attackerInstanceId,
          playedOrders: attackerPlayer.playedOrders?.[outcome.attackerZone]?.map((u, i) => ({
            index: i,
            name: u?.name,
            instanceId: u?.instanceId
          }))
        });
        return null;
      }
      
      
      // ✅ SPECTATOR FIX: Use activeUserId instead of userId for zone name determination
      // For spectators, activeUserId is the player they're watching, not their own userId
      const attackerPlayerId = outcome.attackerPlayerId === activeUserId ? 'your' : 'opponent';
      const targetPlayerId = outcome.targetPlayerId === activeUserId ? 'your' : 'opponent';
      
      return (
        <MemoizedAttackAnimation
          key={attackId}
          attackId={attackId}
          attacker={attacker}
          outcome={outcome}
          attackerPlayerId={attackerPlayerId}
          targetPlayerId={targetPlayerId}
          attackAnimations={attackAnimations}
          attackInProgressRef={attackInProgressRef}
          setDamageAnimationTrigger={queueDamageAnimationTrigger}
          damageAnimationTriggeredRef={damageAnimationTriggeredRef}
          onUnitLethalDeathFromAttack={triggerUnitLethalDeathSequence}
          onAttackHitOutcomePreview={applyAttackHitOutcomePreview}
          onQueueScatterAtHit={queueAttackScatterAtHit}
          animationSpeedMultiplier={speedControl.animationSpeedMultiplier}
          mobileLayout={isContainerMobile}
          resolveSlotDamageAnchor={resolveSlotDamageAnchor}
          validateDamageAnchor={validateDamageAnchor}
        />
      );
    })}

    {/* ✅ FIX: Render damage animations OUTSIDE of attack animation components */}
    {/* This ensures they persist even after attack components unmount */}
    {Array.from(damageAnimationTriggers.entries()).map(([attackId, trigger]) => {
      const isGeneralAnimation = resolveDamageTriggerIsGeneralAnimation(attackId, trigger);
      if (!validateDamageAnchor(trigger, { isGeneralAnimation })) {
        return null;
      }

      const damageContainerSizePx = isContainerMobile
        ? resolveMobileDamageAnimationContainerSizePx(trigger.rect, viewportScale)
        : 192;
      
      return (
      <div
        key={`damage-container-${attackId}-${trigger.timestamp}`}
        className={isContainerMobile ? 'damage-animation-portal damage-animation-portal--mobile' : undefined}
        style={{
          position: 'fixed',
          left: `${trigger.x}px`,
          top: `${trigger.y}px`,
          transform: 'translate(-50%, -50%)',
          zIndex: 10001,
          pointerEvents: 'none',
          width: `${damageContainerSizePx}px`,
          height: `${damageContainerSizePx}px`,
          overflow: 'visible',
        }}
      >
        <DamageAnimation
          key={`damage-${attackId}-${trigger.timestamp}`}
          damageAmount={trigger.damageAmount}
          shieldDeflected={trigger.shieldDeflected === true}
          sniperLethalKill={trigger.sniperLethalKill === true}
          mobileLayout={isContainerMobile}
          onComplete={() => {
            handleDamageAnimationComplete(attackId, trigger);
          }}
          animationKey={`attack-damage-${attackId}-${trigger.timestamp}`}
          animationSpeedMultiplier={speedControl.animationSpeedMultiplier}
        />
      </div>
    );
    })}

    {battlefieldHealthCountdown.flyoutPulses.length > 0 && createPortal(
      <>
        {battlefieldHealthCountdown.flyoutPulses.map((pulse) => (
          <HealthCountdownFlyoutFromInstance
            key={pulse.key}
            value={pulse.value}
            instanceId={pulse.instanceId}
            zoneName={pulse.zoneName}
            slotIndex={pulse.slotIndex}
            durationMs={pulse.durationMs}
            mobileLayout={isContainerMobile}
            onComplete={() => battlefieldHealthCountdown.dismissFlyoutPulse(pulse.key)}
          />
        ))}
      </>,
      document.body
    )}

    {/* Raid energy stream: raided general → raiding unit */}
    {raidAnimations.activeAnimations.map((animation) => (
      <RaidEnergyStreamEffect
        key={animation.key}
        fromSlot={animation.fromSlot}
        toSlot={animation.toSlot}
        animationSpeedMultiplier={speedControl.animationSpeedMultiplier}
        onComplete={() => raidAnimations.completeRaidAnimation(animation.key)}
      />
    ))}

    {/* Deploy Target Animations */}
    {deployTargetAnimations.activeAnimations.map((animation) => (
      <DeployTargetAnimation
        key={animation.key}
        fromSlot={animation.fromSlot}
        toSlot={animation.toSlot}
        animationType={animation.animationType}
        deployUnitId={animation.deployUnitId}
        deployUnitName={animation.deployUnitName}
        deployUnitAttackSoundType={animation.deployUnitAttackSoundType}
        isRedeemDamageAnimation={animation.isRedeemDamageAnimation === true}
        deployAnimationVariant={animation.deployAnimationVariant}
        onComplete={() => {
          if (animation.isRedeemDamageAnimation && animation.deployUnitInstanceId) {
            redeemDeathSequenceRef.current?.notifyRedeemDeployAnimationComplete(animation.deployUnitInstanceId);
          }
          if (animation.targetInstanceId) {
            releasePendingProjectileHealthHold(
              pendingProjectileHealthHoldRef.current,
              animation.targetInstanceId
            );
          }
          if (Array.isArray(animation.zoneDamageTargets)) {
            animation.zoneDamageTargets.forEach((target) => {
              releasePendingProjectileHealthHold(
                pendingProjectileHealthHoldRef.current,
                target?.targetInstanceId
              );
            });
          }
          deployTargetAnimations.completeAnimation(animation.key);
        }}
        onFireArcHealthCountdownComplete={
          animation.deployAnimationVariant === ENCRAZED_BOMBARDIER_FIRE_ARC_VARIANT
            ? () => startDeployZoneHealthCountdowns(animation.zoneDamageTargets)
            : undefined
        }
        onDamageAtTarget={(targetPosition) => {
          const isGeneralTarget = animation.targetType === 'general'
            || animation.isGeneralTarget
            || animation.toSlot?.zone === 'general'
            || targetPosition?.zone === 'general';

          if (isGeneralTarget) {
            const damageAmount = typeof animation.damageAmount === 'number' && animation.damageAmount > 0
              ? animation.damageAmount
              : (
                typeof animation.generalHealthBefore === 'number'
                && typeof animation.generalHealthAfter === 'number'
                && animation.generalHealthBefore > animation.generalHealthAfter
                  ? animation.generalHealthBefore - animation.generalHealthAfter
                  : undefined
              );
            if (damageAmount == null || damageAmount <= 0) {
              return;
            }

            const x = targetPosition?.x;
            const y = targetPosition?.y;
            if (typeof x !== 'number' || typeof y !== 'number' || Number.isNaN(x) || Number.isNaN(y)) {
              return;
            }

            const animationKey = `deploy-damage-${animation.key}-${Date.now()}`;
            queueDamageAnimationTrigger(animationKey, {
              attackId: animationKey,
              x,
              y,
              timestamp: Date.now(),
              isGeneralAnimation: true,
              targetZone: 'general',
              damageAmount,
            });
            return;
          }

          if (
            animation.targetInstanceId == null
            || typeof animation.healthBefore !== 'number'
          ) {
            return;
          }

          const targetZone = animation.toSlot?.zone ?? targetPosition?.zone;
          const targetIndex = animation.toSlot?.index ?? targetPosition?.index;
          if (targetZone == null || targetIndex == null) {
            return;
          }

          const anchor = resolveSlotDamageAnchor(targetZone, targetIndex);
          const x = anchor?.x ?? targetPosition?.x;
          const y = anchor?.y ?? targetPosition?.y;
          if (typeof x !== 'number' || typeof y !== 'number' || Number.isNaN(x) || Number.isNaN(y)) {
            return;
          }

          const damageAmount =
            typeof animation.damageAmount === 'number' && animation.damageAmount > 0
              ? animation.damageAmount
              : (
                typeof animation.healthAfter === 'number'
                  ? Math.max(0, animation.healthBefore - animation.healthAfter)
                  : undefined
              );

          const animationKey = `deploy-damage-${animation.key}-${Date.now()}`;
          queueDamageAnimationTrigger(animationKey, {
            attackId: animationKey,
            x,
            y,
            timestamp: Date.now(),
            targetZone,
            targetIndex,
            damageAmount,
            targetInstanceId: animation.targetInstanceId,
            healthBefore: animation.healthBefore,
            healthAfter: animation.healthAfter,
          });

          if (
            isBattlefieldUnitLethalHit({
              zoneName: targetZone,
              index: targetIndex,
              targetInstanceId: animation.targetInstanceId,
              damageAmount: damageAmount ?? undefined,
              healthAfter: animation.healthAfter,
              unitSnapshot: animation.ghostUnit,
              slotHasUnit: slotHasVisibleDeployedUnit,
              gameState: activeGameState,
              viewerUserId: activeUserId,
              targetOwnerPlayerId: animation.toSlot?.playerId,
              isDecommissionedUnitInHand: isDecommissionedUnitInHandForLethalFx,
            })
          ) {
            triggerUnitLethalDeathSequence({
              zoneName: targetZone,
              index: targetIndex,
              unitSnapshot: animation.ghostUnit,
              damageAmount: damageAmount ?? undefined,
              skipDamageQueue: true,
            });
          }
        }}
        animationSpeedMultiplier={speedControl.animationSpeedMultiplier}
      />
    ))}

    {/* Strategem Target Animations - Black balls for damage, yellow balls for positive effects */}
    {strategemTargetAnimations.activeAnimations.map((animation) => (
      <StrategemTargetAnimation
        key={animation.key}
        fromHand={animation.fromHand}
        toSlot={animation.toSlot}
        targetType={animation.targetType}
        effectType={animation.effectType || 'damage'} // Default to 'damage' (black), 'heal' (yellow) for positive effects
        strategemId={animation.strategemId}
        targetInstanceId={animation.targetInstanceId}
        viewerUserId={activeUserId}
        targetGeneralPlayerId={animation.targetGeneralPlayerId}
        strategemCasterPlayerId={animation.strategemCasterPlayerId}
        stormMissileSeq={animation.stormMissileSeq ?? 0}
        isOpponentStrategem={animation.isOpponentStrategem || false} // Pass isOpponentStrategem flag
        animationSpeedMultiplier={speedControl.animationSpeedMultiplier}
        onComplete={() => {
          // Dev console: Betrayal drone preview has no server two-phase flow — remove drone locally when animation ends.
          if (
            animation.isDevBetrayalDronePreview &&
            animation.strategemId === 341 &&
            animation.targetType === 'drone' &&
            animation.devBetrayalRemoveUserId &&
            animation.targetInstanceId
          ) {
            const removeUid = animation.devBetrayalRemoveUserId;
            const removeIid = animation.targetInstanceId;
            setGameState((prev) => {
              if (!prev?.state?.[removeUid]) return prev;
              const ps = prev.state[removeUid];
              if (!Array.isArray(ps.drones)) return prev;
              const idx = ps.drones.findIndex((d) => d && d.instanceId === removeIid);
              if (idx < 0) {
                console.warn('BattleUI: dev Betrayal preview — drone instance not in state', removeIid);
                return prev;
              }
              const drones = [...ps.drones];
              drones.splice(idx, 1);
              return {
                ...prev,
                state: {
                  ...prev.state,
                  [removeUid]: { ...ps, drones },
                },
              };
            });
          }

          // ✅ STRATEGEM 219: Check if zone should be marked complete BEFORE removing animation
          // When strategem 219 (Plague of Darkness) animation completes, allow immobilize indicators to show
          if (animation.strategemId === 219 && animation.toSlot?.zone) {
            const zone = animation.toSlot.zone.replace('your_', '').replace('opponent_', '');
            
            // For zone-targeting animations (targetType === 'zone'), mark complete immediately
            // For unit-targeting animations, check if all animations for this strategem instance and zone have completed
            if (animation.targetType === 'zone') {
              strategem219CompletedZonesRef.current.add(zone);
            } else {
              // Check remaining animations BEFORE calling completeAnimation (which removes this one)
              const strategemInstanceId = animation.strategemInstanceId;
              const remainingAnimations = strategemTargetAnimations.activeAnimations.filter(anim => 
                anim.strategemId === 219 && 
                anim.strategemInstanceId === strategemInstanceId &&
                anim.toSlot?.zone?.replace('your_', '').replace('opponent_', '') === zone &&
                anim.key !== animation.key // Exclude the current animation (which will be removed)
              );
              
              // If no remaining animations for this strategem instance and zone, mark zone as complete
              if (remainingAnimations.length === 0) {
                strategem219CompletedZonesRef.current.add(zone);
              } else {
              }
            }
          }
          
          // ✅ TWO-PHASE STRATEGEM SYSTEM: Send strategem-animation-complete message to backend
          // This tells the backend to apply the strategem effect now that animation is done
          // Only send if we have a pending animation ID for this strategem instance
          // Skip for spectators and autobattler spectators
          const strategemInstanceId = animation.strategemInstanceId;
          const strategemInstanceKey = normalizeStrategemInstanceKey(strategemInstanceId);
          const remainingAnimationsForStrategem = strategemTargetAnimations.activeAnimations.filter(
            (anim) =>
              normalizeStrategemInstanceKey(anim.strategemInstanceId) === strategemInstanceKey &&
              anim.key !== animation.key
          );

          if (strategemInstanceId != null && remainingAnimationsForStrategem.length === 0) {
            const pendingInstantHolds = pendingInstantExecuteHealthHoldsRef.current.get(
              String(strategemInstanceId)
            );
            if (
              pendingInstantHolds?.flushKind === INSTANT_EXECUTE_FLUSH_KIND.STRATEGEM_ANIMATION
              || pendingInstantHolds?.flushKind === INSTANT_EXECUTE_FLUSH_KIND.PER_UNIT_MISSILE
            ) {
              const bullyIds = new Set(
                pendingBullyFistPlaybackRef.current?.ghosts?.map((g) => g.instanceId) ?? []
              );
              if (bullyIds.size > 0 && Array.isArray(pendingInstantHolds.holdTargets)) {
                const filtered = filterHealthHoldTargetsExcludingBullyDamage(
                  pendingInstantHolds.holdTargets,
                  Array.from(bullyIds)
                );
                if (filtered.length === 0) {
                  pendingInstantExecuteHealthHoldsRef.current.delete(String(strategemInstanceId));
                } else if (filtered.length !== pendingInstantHolds.holdTargets.length) {
                  pendingInstantExecuteHealthHoldsRef.current.set(String(strategemInstanceId), {
                    ...pendingInstantHolds,
                    holdTargets: filtered,
                  });
                }
              }
              flushPendingInstantExecuteHealthHolds(strategemInstanceId);
            }
            flushPendingBullyFistPlayback(strategemInstanceId);
          }

          const completeResolution = resolveStrategemAnimationCompleteSend({
            pendingMap: pendingStrategemAnimationIdsRef.current,
            strategemInstanceId,
            wsReady: !!(ws && ws.readyState === 1),
            isSpectating,
            isAutobattlerSpectator,
            remainingAnimationsForStrategemCount: remainingAnimationsForStrategem.length,
          });

          if (completeResolution.send && ws && ws.readyState === 1) {
            ws.send(
              JSON.stringify({
                type: 'strategem-animation-complete',
                strategemAnimationId: completeResolution.strategemAnimationId,
                strategemInstanceId: completeResolution.strategemInstanceId,
                gameId: gameId,
              })
            );
            deletePendingStrategemAnimInfo(pendingStrategemAnimationIdsRef.current, strategemInstanceId);
            if (strategemInstanceKey) {
              strategemAnimationCompleteDeferredRef.current.delete(strategemInstanceKey);
            }
            clearOverlayStrategemWatchdog(
              overlayStrategemWatchdogTimeoutsRef.current,
              strategemInstanceId
            );
          } else if (
            shouldDeferStrategemAnimationComplete({
              pendingAnimInfo: completeResolution.pendingAnimInfo ?? getPendingStrategemAnimInfo(
                pendingStrategemAnimationIdsRef.current,
                strategemInstanceId
              ),
              strategemInstanceId,
              wsReady: !!(ws && ws.readyState === 1),
              isSpectating,
              isAutobattlerSpectator,
              remainingAnimationsForStrategemCount: remainingAnimationsForStrategem.length,
            }) &&
            strategemInstanceKey
          ) {
            strategemAnimationCompleteDeferredRef.current.add(strategemInstanceKey);
          }
          
          strategemTargetAnimations.completeAnimation(animation.key);
        }}
        onDamageAtTarget={(targetPosition) => {
          // Only trigger damage animation for damage effects, not heal effects
          // Check effectType first - if it's 'heal', don't trigger damage explosion
          if (animation.effectType === 'heal') {
            return;
          }

          if (isUnusableStrategemTargetPixel(targetPosition) || isInvalidStrategemTargetPixel(targetPosition)) {
            return;
          }

          const forceDamageAnimation = animation.forceDamageAnimation === true;
          const targetTypeForFx = animation.targetType || targetPosition?.targetType;
          if (!forceDamageAnimation && targetTypeForFx === 'unit') {
            const zone = animation.toSlot?.zone ?? targetPosition?.zone;
            const index = animation.toSlot?.index ?? targetPosition?.index;
            if (
              zone != null &&
              index != null &&
              index !== undefined &&
              !slotHasVisibleDeployedUnit(zone, index, animation.targetInstanceId)
            ) {
              console.log(
                `⏭️ Skipping strategem damage burst — no visible unit at ${zone}[${index}]`
              );
              return;
            }
          }
          
          // Trigger damage animation if the strategem deals damage, destroys units, or shrinks units
          const strategemId = animation.strategemId;
          if (!strategemId && !forceDamageAnimation) {
            return;
          }
          
          const dealsDamage = forceDamageAnimation
            ? true
            : doesStrategemDealDamageById(strategemId, gameState);
          const destroysUnits = forceDamageAnimation
            ? false
            : doesStrategemDestroyUnitsById(strategemId, gameState);
          const shrinksUnits = forceDamageAnimation
            ? false
            : doesStrategemShrinkUnitsById(strategemId, gameState);
          
          if (!dealsDamage && !destroysUnits && !shrinksUnits) {
            return;
          }
          
          // Repurposed Warhead (54): explosions are shown at each unit slot via slot death detection, not here
          if (strategemId === 54) {
            return;
          }

          // Neurotox Pulse (53): NeurotoxPulseEffect + neurotox.mp3 — no default Lottie
          if (strategemId === 53) {
            return;
          }

          // Venom Cloud (318): deal damage only to the general that took damage (authoritative in
          // gameState.generalInteractions on that player). useStrategemGeneralDamage places the burst on
          // the correct avatar via findGeneralElement — avoid onDamageAtTarget here (duplicate/wrong HUD).
          if (strategemId === 318) {
            return;
          }

          // Hive Surge (62): HiveSurgePulseEffect + hive_surge.mp3 — no default Lottie
          if (strategemId === 62) {
            return;
          }

          // Kinetron Blast (111): kinetron.mp3 + decommission fly-out — no damage burst / Lottie
          if (strategemId === 111) {
            return;
          }

          // Vein Sever (55): red slash at projectile hit (uses same anchor as damage Lottie below)
          if (
            strategemId === 55 &&
            Number.isFinite(targetPosition?.x) &&
            Number.isFinite(targetPosition?.y)
          ) {
            const pointKey = `vs55-pt-${animation.key}-${Date.now()}`;
            setVeinSeverSlashPoints((prev) => [
              ...prev,
              {
                key: pointKey,
                animationKey: pointKey,
                x: targetPosition.x,
                y: targetPosition.y,
              },
            ]);
            window.setTimeout(() => {
              setVeinSeverSlashPoints((prev) => prev.filter((p) => p.key !== pointKey));
            }, VEIN_SEVER_SLASH_DURATION_MS + 100);
          }

          // Trigger damage animation at the exact target position when projectile arrives
          // This works for damage-dealing strategems, unit-destroying strategems, and shrink strategems
          // Shrink strategems need damage animations for both lethal (kills unit) and non-lethal (reduces stats) cases
          const reason = dealsDamage ? 'deals damage' : (destroysUnits ? 'destroys units' : 'shrinks units');
          const animationKey = `strategem-damage-${animation.key}-${Date.now()}`;

          let damageAmount =
            typeof animation.damageAmount === 'number'
              ? animation.damageAmount
              : getStrategemDamageAmountById(strategemId, gameState);
          if (damageAmount == null && destroysUnits && animation.targetInstanceId) {
            damageAmount = getDeployedUnitHealthByInstanceId(gameState, animation.targetInstanceId);
          }
          
          // Check if this is a general animation (for proper validation)
          const isGeneralAnim = animation.targetType === 'general' || targetPosition.targetType === 'general';
          const targetZone = animation.toSlot?.zone ?? targetPosition.zone;
          const targetIndex = animation.toSlot?.index ?? targetPosition.index;

          const unitSnapshot =
            animation.ghostUnit ||
            strategemTargetAnimations.getGhostUnit(targetZone, targetIndex);
          const targetInstanceId = animation.targetInstanceId || unitSnapshot?.instanceId;
          const liveUnit = !isGeneralAnim
            ? getBattlefieldUnitAtZoneIndex(activeGameState, activeUserId, opponentId, targetZone, targetIndex)
            : null;

          let healthBefore =
            typeof animation.healthBefore === 'number'
              ? animation.healthBefore
              : (typeof unitSnapshot?.health === 'number' ? unitSnapshot.health : undefined);
          const liveHealth =
            liveUnit != null
            && targetInstanceId != null
            && liveUnit.instanceId === targetInstanceId
            && typeof liveUnit.health === 'number'
              ? liveUnit.health
              : undefined;

          const shieldDeflected = !isGeneralAnim && (
            animation.shieldDeflected === true
            || isStrategemShieldDeflectedHit({
              unitSnapshot,
              liveUnit,
              damageAmount,
              healthBefore,
            })
          );

          const healthAfter = shieldDeflected && typeof healthBefore === 'number'
            ? healthBefore
            : resolveSurvivorHealthAfter({
              healthBefore,
              healthAfter: typeof animation.healthAfter === 'number' ? animation.healthAfter : undefined,
              damageAmount,
              liveHealth,
            });

          if (
            !shieldDeflected
            && (damageAmount == null || damageAmount <= 0)
            && typeof healthBefore === 'number'
            && typeof healthAfter === 'number'
            && healthBefore > healthAfter
          ) {
            damageAmount = healthBefore - healthAfter;
          }

          const displayDamageAmount = shieldDeflected ? undefined : (damageAmount ?? undefined);

          queueDamageAnimationTrigger(animationKey, {
            attackId: animationKey,
            x: targetPosition.x,
            y: targetPosition.y,
            timestamp: Date.now(),
            isGeneralAnimation: isGeneralAnim,
            targetZone,
            targetIndex,
            damageAmount: displayDamageAmount,
            shieldDeflected,
            targetInstanceId: !isGeneralAnim ? targetInstanceId : undefined,
            healthBefore: !isGeneralAnim ? healthBefore : undefined,
            healthAfter: !isGeneralAnim ? healthAfter : undefined,
          });

          if (
            animation.strategemInstanceId != null
            && targetInstanceId != null
            && !isGeneralAnim
          ) {
            const pendingInstantHolds = pendingInstantExecuteHealthHoldsRef.current.get(
              String(animation.strategemInstanceId)
            );
            if (pendingInstantHolds?.flushKind === INSTANT_EXECUTE_FLUSH_KIND.PER_UNIT_MISSILE) {
              releaseInstantExecuteHoldForTarget(animation.strategemInstanceId, targetInstanceId);
            }
          }

          if (
            !isGeneralAnim
            && !shieldDeflected
            && isBattlefieldUnitLethalHit({
              zoneName: targetZone,
              index: targetIndex,
              targetInstanceId: animation.targetInstanceId,
              destroysUnits,
              damageAmount: displayDamageAmount,
              healthAfter: animation.healthAfter,
              unitSnapshot:
                animation.ghostUnit ||
                strategemTargetAnimations.getGhostUnit(targetZone, targetIndex),
              slotHasUnit: slotHasVisibleDeployedUnit,
              gameState: activeGameState,
              viewerUserId: activeUserId,
              targetOwnerPlayerId: animation.toSlot?.playerId,
              isDecommissionedUnitInHand: isDecommissionedUnitInHandForLethalFx,
            })
          ) {
            triggerUnitLethalDeathSequence({
              zoneName: targetZone,
              index: targetIndex,
              unitSnapshot:
                animation.ghostUnit ||
                strategemTargetAnimations.getGhostUnit(targetZone, targetIndex),
              damageAmount: displayDamageAmount,
              skipDamageQueue: true,
            });
          }
        }}
      />
    ))}

    {/* Strategem 113 (Demoralizing Warcry) & 325 (Oblivion Pulse) shockwave effects */}
    {strategemShockwave.activeShockwaves.map((shockwave) => (
      <ShockwaveEffect
        key={shockwave.key}
        x={shockwave.x}
        y={shockwave.y}
        animationKey={shockwave.key}
        variant={shockwave.variant || 'demoralizing'}
        onComplete={() => {
          strategemShockwave.completeShockwave(shockwave.key);
        }}
      />
    ))}

    {/* Strategem 110 / 51 / 57: PulseShockwaveEffect — 110 purple nums, 51 no nums, 57 red swarm_collapse.mp3 */}
    {pulseShockwave.activePulseShockwaves.map((p) => (
      <PulseShockwaveEffect
        key={p.key}
        direction={p.direction}
        viewerHits={p.viewerHits}
        animationKey={p.animationKey}
        variant={p.variant || 'pulse'}
      />
    ))}

    {/* Strategems 53 / 180 / 183: same downward-chevron shrink overlay (53 = neurotox.mp3; 180/183 = shrink.mp3) */}
    {neurotoxPulse.activeNeurotoxPulses.map((p) => (
      <NeurotoxPulseEffect
        key={p.key}
        viewerSlot={p.viewerSlot}
        animationKey={p.animationKey}
      />
    ))}

    {/* Strategems 62 / 182 / 224 / 227: same grow chevrons (62 = hive_surge.mp3; 182/224/227 = grow.mp3) */}
    {hiveSurgePulse.activeHiveSurgePulses.map((p) => (
      <HiveSurgePulseEffect
        key={p.key}
        viewerSlot={p.viewerSlot}
        animationKey={p.animationKey}
      />
    ))}

    {/* Strategem 187 (Quantum Surge): surge.mp3 + refresh ripples on target unit */}
    {quantumSurgeRefresh.activeQuantumSurgeRefreshes.map((p) => (
      <QuantumSurgeRefreshEffect
        key={p.key}
        viewerSlot={p.viewerSlot}
        animationKey={p.animationKey}
      />
    ))}

    {/* Weapon attached: brief equip_weapon.png on the unit slot (each new weaponInstanceId) */}
    {weaponEquipFlash.flashes.map((f) => (
      <WeaponEquipFlashOverlay
        key={f.key}
        viewerZone={f.viewerZone}
        slotIndex={f.index}
        flashKey={f.key}
        onComplete={() => weaponEquipFlash.dismissFlash(f.key)}
      />
    ))}

    {/* Strategem 56 / 185 / 188: heal.mp3 + green + on healed general; 185/188 also red × on damaged general */}
    {viralEchoHealFx.activeViralEchoHealFx.map((p) => (
      <ViralEchoHealPlusEffect
        key={p.key}
        generalDataZone={p.generalDataZone}
        animationKey={p.animationKey}
        variant={p.variant ?? 'heal'}
      />
    ))}

    {/* Strategem 55 (Vein Sever): red slash at projectile hit */}
    {veinSeverSlashPoints.map((p) => (
      <VeinSeverSlashEffect
        key={p.key}
        animationKey={p.animationKey}
        centerPoint={{ x: p.x, y: p.y }}
      />
    ))}

    {/* Inferno Drop (319) napalm effect – all 4 zones */}
    {infernoNapalm.activeNapalms.map((napalm) => (
      <InfernoNapalmEffect
        key={napalm.key}
        animationKey={napalm.key}
        onComplete={() => {
          const holdTargets = resolveInfernoNapalmHoldTargets(
            napalm,
            pendingBroadcastStrategemDamageRef.current
          );
          if (holdTargets.length > 0) {
            queueBroadcastStrategemDamageTriggers(holdTargets);
            if (napalm.strategemInstanceId != null) {
              pendingBroadcastStrategemDamageRef.current.delete(String(napalm.strategemInstanceId));
            }
          }
          infernoNapalm.completeNapalm(napalm.key);
        }}
      />
    ))}

    {/* Strategem 114, 115, 221 & 225 Explosion Effects */}
    {Array.from(explosionEffects.values()).map((explosion) => (
      <ExplosionEffect
        key={explosion.key}
        x={explosion.x}
        y={explosion.y}
        animationKey={explosion.key}
        color={explosion.color || 'orange'}
        onComplete={() => {
          setExplosionEffects(prev => {
            const next = new Map(prev);
            next.delete(explosion.key);
            return next;
          });
        }}
      />
    ))}

    <TacticalLoadoutFxOverlay session={tacticalLoadoutFxSession} onComplete={completeTacticalLoadoutFx} />

    {Array.from(battlebotPortalEffects.values()).map((p) => (
      <BattlebotPortalOverlay
        key={p.key}
        slots={p.slots}
        onComplete={() => {
          setBattlebotPortalEffects((prev) => {
            const next = new Map(prev);
            next.delete(p.key);
            return next;
          });
        }}
      />
    ))}

    {Array.from(fleshMimicPortalEffects.values()).map((p) => (
      <FleshMimicHandPortalOverlay
        key={p.key}
        copiedOrderInstanceId={p.copiedOrderInstanceId}
        onComplete={() => {
          setFleshMimicPortalEffects((prev) => {
            const next = new Map(prev);
            next.delete(p.key);
            return next;
          });
        }}
      />
    ))}

    {Array.from(plagueBloomPortalEffects.values()).map((p) => (
      <PlagueBloomSlotPortalsOverlay
        key={p.key}
        slots={p.slots}
        portalVariant={p.portalVariant ?? 'crimson'}
        onComplete={() => {
          setPlagueBloomPortalEffects((prev) => {
            const next = new Map(prev);
            next.delete(p.key);
            return next;
          });
        }}
      />
    ))}
      </>,
      document.body
    )}

    {/* DESYNC FIX: Loading overlay - only shows if action takes >300ms */}
    {showLoadingOverlay && (
      <div className="state-updating-overlay">
        <div className="state-updating-message">
          <div className="loading-spinner"></div>
          <p>Processing action...</p>
        </div>
      </div>
    )}

    
      </div>
    
    {/* War Effort fortification - outside battle-ui-background so visible (background has z-index -1). Shown on defender's side: opponent top-right (red box) or player bottom-left. Includes completion building bonuses (80 collected + 30 orders of faction). */}
    {(activeGameState?.warEffortBattleType === 'fortified_country' || activeGameState?.warEffortBattleType === 'other_country' || activeGameState?.warEffortBattleType === 'enemy_hq') && (() => {
      const fortUnitIds = {
        health_boost: 227, attack_boost: 77, intel_boost: 237, order_boost: 103,
        disorientation: 59, defend_twice: 187, instakill: 60, deploy_random: 279, ability_relay_drone: 399
      };
      const isOtherCountry = activeGameState?.warEffortBattleType === 'other_country';
      const fortTypes = isOtherCountry
        ? (activeGameState?.warEffortFortificationTypes || [])
        : (activeGameState?.warEffortFortificationType ? [activeGameState.warEffortFortificationType] : []);
      const completionBonuses = [
        { key: 'lavender', active: activeGameState?.defenderLavenderFarmActive, image: '/images/lavender.png', name: 'Lavender Farm', desc: 'Units costing <4 intel have Blitz (80 lavender, 30+ Exolot Dominion)' },
        { key: 'cattle', active: activeGameState?.defenderCattleFarmActive, image: '/images/cattle.png', name: 'Cattle Farm', desc: 'Remnants +1 health (80 stone, 30+ Remnants of Humanity)' },
        { key: 'quarry', active: activeGameState?.defenderQuarryActive, image: '/images/stone.png', name: 'Quarry', desc: 'Shield restore at turn start (80 stone, 30+ Ra\'s Children)' },
        { key: 'sewer', active: activeGameState?.defenderSewerActive, image: '/images/sewers.png', name: 'Sewer', desc: 'All units have Mend Wounds (80 biomass, 30+ Asrah)' },
        { key: 'portal', active: activeGameState?.defenderPortalActive, image: '/images/erray.png', name: 'Portal', desc: 'One zone-switch charge per unit (80 erray, 30+ League of Shadows)' }
      ].filter((b) => b.active);
      const hasFortTypes = fortTypes.length > 0;
      const hasCompletionBonuses = completionBonuses.length > 0;
      if (!hasFortTypes && !hasCompletionBonuses) return null;
      const defenderId = activeGameState.warEffortDefenderPlayerId != null
        ? activeGameState.warEffortDefenderPlayerId
        : (activeGameState.players && activeGameState.players[1]);
      const defenderState = defenderId && activeGameState.state ? activeGameState.state[defenderId] : null;
      const isDefender = userId === defenderId;
      const cellPx = 20;
      const cellsLeftPx = 4 * cellPx;
      const iconSize = isContainerMobile ? { width: '32px', height: '32px' } : { width: '36px', height: '36px' };
      const position = isContainerMobile
        ? (isDefender ? { left: `${12 - cellsLeftPx}px`, bottom: '72px' } : { right: `${12 + cellsLeftPx}px`, top: '72px' })
        : (isDefender ? { left: `${20 - cellsLeftPx}px`, top: '848px' } : { right: `${65 + cellsLeftPx}px`, top: '80px' });
      return (
        <div
          style={{
            position: 'absolute',
            ...position,
            zIndex: 1400,
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: '4px',
            alignItems: 'center',
            maxWidth: isContainerMobile ? '200px' : '320px'
          }}
        >
          {fortTypes.map((fortType, idx) => {
            const unitId = fortUnitIds[fortType];
            if (!unitId) return null;
            const oneTimeUsed = defenderState && (
              (fortType === 'instakill' && defenderState.fortificationInstakillUsed) ||
              (fortType === 'deploy_random' && defenderState.fortificationDeployRandomUsed)
            );
            const tooltipKey = oneTimeUsed ? 'battleUI.fortification.alreadyUsed' : `battleUI.fortification.${fortType}`;
            return (
              <div
                key={`${fortType}-${idx}`}
                className={`battle-fortification-indicator ${oneTimeUsed ? 'battle-fortification-used' : ''}`}
                style={{
                  ...iconSize,
                  borderRadius: '4px',
                  border: '1px solid rgba(78, 205, 196, 0.5)',
                  overflow: 'hidden',
                  background: 'rgba(30, 30, 30, 0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  ...(oneTimeUsed ? { filter: 'grayscale(1)', opacity: 0.6 } : {})
                }}
                data-tooltip={t(tooltipKey)}
                data-tooltip-position="top"
              >
                <img
                  src={`/images/${unitId}.jpg`}
                  alt=""
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    pointerEvents: 'none'
                  }}
                />
              </div>
            );
          })}
          {completionBonuses.map((bonus) => (
            <div
              key={`completion-${bonus.key}`}
              className="battle-fortification-indicator battle-completion-bonus-indicator"
              style={{
                ...iconSize,
                borderRadius: '4px',
                border: '1px solid rgba(78, 205, 196, 0.5)',
                overflow: 'hidden',
                background: 'rgba(30, 30, 30, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
              data-tooltip={`${bonus.name}: ${bonus.desc}`}
              data-tooltip-position="top"
            >
              <img
                src={bonus.image}
                alt=""
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  pointerEvents: 'none'
                }}
              />
            </div>
          ))}
        </div>
      );
    })()}
    
      </div>{/* Close viewport-container */}
    </div>{/* Close viewport-wrapper */}
    {/* Speed control for autobattler spectators (outside viewport wrapper so flashlight pointer capture cannot intercept) */}
    {isAutobattlerSpectator && (
      <SpectateSpeedControl
        speed={speedControl.speed}
        onSpeedChange={speedControl.updateSpeed}
        isPaused={speedControl.isPaused}
        onTogglePause={speedControl.togglePause}
      />
    )}
    {isMission6DarknessMode && !mission6LightsRestored && (
      <div
        ref={mission6DarknessOverlayRef}
        aria-hidden="true"
        data-no-battle-flashlight="true"
        style={{
          position: 'fixed',
          inset: 0,
          background: buildMission6DarknessBackground(null, null),
          pointerEvents: 'none',
          zIndex: 10000
        }}
      />
    )}
    {isMission6DarknessMode && !mission6LightsRestored && (
      <button
        type="button"
        onClick={() => setMission6LightsRestored(true)}
        data-no-battle-flashlight="true"
        aria-label="Hidden light switch"
        data-tooltip="Hidden light switch"
        style={{
          position: 'fixed',
          top: '220px',
          right: '160px',
          width: '18px',
          height: '32px',
          borderRadius: '4px',
          border: '1px solid rgba(190, 190, 190, 0.35)',
          background: 'linear-gradient(to bottom, rgba(120, 120, 120, 0.7) 0 50%, rgba(245, 245, 245, 0.85) 50% 100%)',
          boxShadow: '0 0 8px rgba(255, 255, 220, 0.08), inset 0 0 2px rgba(0, 0, 0, 0.35)',
          cursor: 'pointer',
          zIndex: 9999,
          opacity: 0.28,
          pointerEvents: 'auto'
        }}
      />
    )}
    {/* Sibling of wrapper so containerRef.current is set before this mounts (child-in-wrapper ran layout before ref attached). */}
    <BattleDragFlashlight
      containerRef={viewportWrapperRef}
      onBeamSample={onLocalBeamSample}
      colorKey={battleFlashlightColor}
      disabled={battleFlashlightDisabled}
    />
    <OpponentBattleFlashlight
      normalized={opponentBattleFlashlight}
      wrapperRef={viewportWrapperRef}
    />

    {/* Battle End Transition Overlay - Rendered via portal to cover full screen */}
    {battleEndTransition.isTransitioning && battleEndTransition.type && createPortal(
      <div 
        className={`battle-end-overlay ${battleEndTransition.type}`}
        data-block-global-esc-back
        style={battleEndTransition.type === 'win' ? {
          '--victory-white': victoryImageWhite
        } : battleEndTransition.type === 'loss' ? {
          '--defeat-gray': defeatImageGray
        } : {}}
      >
        {battleEndTransition.type === 'win' && showVictoryImage && (
          <div className="victory-image-container">
            {battleEndOverlayVisuals.winIsVideo ? (
              <video
                src={battleEndOverlayVisuals.winSrc}
                className="victory-image"
                autoPlay
                muted
                playsInline
                aria-label="Victory"
              />
            ) : (
              <img
                src={battleEndOverlayVisuals.winSrc}
                alt=""
                className="victory-image"
              />
            )}
          </div>
        )}
        {battleEndTransition.type === 'loss' && showDefeatImage && (
          <div className="defeat-image-container">
            {battleEndOverlayVisuals.lossIsVideo ? (
              <video
                src={battleEndOverlayVisuals.lossSrc}
                className="defeat-image"
                autoPlay
                muted
                playsInline
                aria-label="Defeat"
              />
            ) : (
              <img
                src={battleEndOverlayVisuals.lossSrc}
                alt=""
                className="defeat-image"
              />
            )}
          </div>
        )}
      </div>,
      document.body
    )}
  </>
  );
}

// Memoized BattleUI to prevent re-renders when tutorial changes
// Note: We can't use deep comparison for gameState, so we rely on reference equality
// BattleUI will re-render when gameState reference changes (which is expected)
const MemoizedBattleUI = React.memo(BattleUI);

// Wrapper component to provide damage animation context and repositioning context
const BattleUIWrapper = React.memo(function BattleUIWrapper(props) {
  // We need to get isUnitRepositioning from the hook, but hooks can't be called conditionally
  // So we'll create a nested component that uses the hook
  function BattleUIWithRepositioning(innerProps) {
    const { isUnitRepositioning } = useUnitRepositionAnimations(innerProps.gameState || null, innerProps.userId || null);
    
    return (
      <RepositioningProvider isUnitRepositioning={isUnitRepositioning}>
        <MemoizedBattleUI {...innerProps} />
      </RepositioningProvider>
    );
  }
  
  return (
    <DamageAnimationProvider>
      <BattleUIWithRepositioning {...props} />
    </DamageAnimationProvider>
  );
});

export default BattleUIWrapper;
